/**
 * Parametri sismici di sito dei comuni italiani — NTC2018 §3.2, All. A e B.
 *
 * FILE GENERATO — non modificare a mano: `node scripts/genera-comuni.mjs`.
 *
 * Per ogni comune di `COMUNI` (stesso ordine, chiave = `Comune.indice`) sono
 * riportati ag, F0 e TC* per i 9 periodi di ritorno del reticolo di
 * riferimento, ottenuti dalla media pesata con 1/d sui 4 nodi più vicini
 * (10751 nodi dell'Allegato B, file `dati/spettri2008.csv`).
 *
 * Impacchettamento: una riga per comune, 9 valori di ag + 9 di F0 + 9 di TC*,
 * ogni gruppo per differenze successive, in base 36 con segno a zigzag.
 * Quantizzazione: ag in millesimi di g, F0 in millesimi, TC* in millesimi di
 * secondo — la stessa precisione con cui i parametri sono pubblicati.
 */

/** Periodi di ritorno tabellati nel reticolo, in anni. */
export const TR_RETICOLO = [30, 50, 72, 101, 140, 201, 475, 975, 2475] as const;

export interface ParametriSito {
  /** ag/g per ciascun TR di TR_RETICOLO. */
  ag: number[];
  /** F0 per ciascun TR di TR_RETICOLO. */
  F0: number[];
  /** TC* in secondi per ciascun TR di TR_RETICOLO. */
  TCstar: number[];
}

const DATI = `
2q,m,k,m,m,s,2c,2g,3y,3se,26,i,s,c,i,17,u,24,fw,1k,u,m,i,i,1q,i,4
2k,k,i,i,k,o,1w,20,38,3t0,2o,1g,m,12,q,1v,1k,2u,g2,1m,w,q,k,m,2u,s,6
2m,m,k,k,m,s,2a,2g,3w,3sm,1i,c,8,8,1,0,s,1o,fu,1g,w,i,i,g,10,a,8
26,i,e,c,g,i,1e,1g,26,3wm,1l,2s,2c,1,1o,q,24,32,fo,1w,w,u,u,o,2w,u,g
2g,i,e,g,g,k,1k,1k,2k,3u6,2a,30,i,1k,d,1o,1o,18,g4,1s,y,u,w,1c,2o,1o,1a
2q,m,i,i,m,o,22,24,3e,3so,36,c,1s,a,16,1p,1u,2k,g0,1k,w,o,m,m,2s,u,m
38,s,o,o,s,w,2w,30,4o,3rm,n,1a,y,14,1e,1x,29,q,fw,1g,o,k,i,i,1c,20,q
2s,q,m,o,q,w,2m,2q,4i,3s6,m,2,6,3,5,i,14,20,fm,1i,s,g,g,g,k,a,e
2i,m,g,k,k,q,22,28,3i,3ta,10,1a,1,w,4,9,s,1q,fu,1k,w,m,i,k,1c,c,8
2e,i,e,e,g,i,1i,1o,2i,3ui,2c,3i,10,1d,26,18,27,2k,g6,20,y,10,2c,w,1y,2u,12
2m,k,g,i,i,q,22,1u,30,3sc,42,1e,1k,f,1t,5,4g,24,gc,1y,y,w,1k,w,1g,w,24
2w,q,m,o,s,w,2q,2s,4k,3s6,4,k,1,0,3,k,14,28,fo,1i,q,e,g,g,k,e,e
2e,i,e,e,g,i,1e,1k,2o,3uq,22,3e,12,z,1o,38,1d,17,g4,20,w,y,22,14,24,30,14
22,g,c,c,c,e,14,10,1q,3w6,m,2o,2w,13,1q,5o,42,2w,fw,28,16,y,28,18,26,3c,1e
2y,s,o,o,s,y,2s,2u,4m,3sc,r,10,b,2,0,q,1i,2g,fm,1e,q,e,g,g,m,g,g
2w,q,m,o,o,y,2k,2s,4i,3s8,q,m,g,2,8,i,o,1u,fs,1k,q,g,i,g,s,e,g
2i,m,i,i,m,q,22,28,3m,3sy,1q,16,4,10,5,15,y,1m,fy,1m,u,o,i,k,1w,c,2
2y,q,m,q,u,10,2o,2w,4o,3r6,14,y,u,r,12,1u,j,t,g6,1w,w,k,6,a,1a,12,16
2u,o,k,i,o,u,2e,2e,3s,3sa,2q,s,24,6,33,11,2y,2g,g4,1w,y,w,k,1c,1s,u,s
2q,m,k,k,q,u,26,2a,3q,3qu,3q,a,1i,t,1,2s,k,6,gi,1w,s,q,c,8,1w,1c,s
2i,k,g,g,i,o,1s,1u,2w,3ti,2a,26,8,1m,c,z,1q,30,g2,1q,u,s,m,o,30,s,e
36,s,m,q,q,w,2q,2w,4k,3rq,f,1i,m,14,1o,z,1z,y,fw,1g,o,k,k,i,1c,1w,m
36,s,o,q,s,y,2u,2y,4q,3s0,15,1g,6,e,s,1k,1c,1m,fs,1e,m,g,g,i,q,i,q
3g,w,s,s,u,14,34,38,56,3qe,15,e,y,1a,12,1g,i,1g,fq,16,o,g,e,e,y,m,y
2s,m,i,i,m,o,20,28,3k,3sq,2u,u,26,c,o,j,x,x,g2,1k,w,q,m,y,2s,w,1e
2e,k,g,i,i,m,1u,1y,32,3ue,2,26,0,1a,g,l,18,1y,fu,1o,w,o,m,k,22,g,6
24,g,e,a,e,g,1a,1g,1y,3vs,10,38,2u,1f,26,s,11,5y,g2,2g,10,y,2m,10,1s,2k,1i
2g,k,e,g,i,q,1q,1o,2m,3su,30,2u,k,1l,1x,42,3o,1o,gg,24,y,s,1w,g,18,12,30
30,q,m,m,o,u,2i,2o,48,3s8,e,1i,14,q,1i,1f,25,i,fy,1i,s,k,k,m,1u,1q,q
2w,q,o,o,q,y,2q,2u,4k,3s6,1,q,5,0,1,k,16,2a,fo,1g,q,g,g,g,k,e,g
36,u,o,q,s,10,2s,30,4q,3s0,17,18,c,e,q,1m,1e,1q,fs,1a,o,g,g,g,s,k,o
2s,q,m,m,q,w,2m,2q,4i,3s2,y,2,a,2,9,g,s,20,fq,1i,s,g,g,g,k,c,c
24,g,e,c,e,g,18,1a,1u,3wi,r,2u,2q,e,n,44,2e,4o,fs,24,10,w,y,24,1y,28,w
2q,k,i,g,k,q,26,20,36,3sk,3w,w,28,4,25,25,3k,30,g8,1w,y,y,14,1m,1g,w,1e
2c,k,g,g,i,m,1q,1s,2w,3vc,17,2c,6,12,10,d,18,2a,fs,1k,w,q,k,m,22,s,8
2a,k,e,e,e,m,1o,1i,2a,3uc,1s,3i,1c,1l,p,1g,4c,3c,gc,28,10,u,2c,o,14,16,36
2c,k,e,i,g,m,1q,1s,2u,3uq,3,2q,4,1e,0,2,1g,26,fw,1q,w,s,m,u,28,m,4
2a,i,e,e,e,i,1k,1k,26,3v2,1i,3m,1q,1p,2e,15,w,5c,g8,26,10,y,2k,s,1o,1w,22
3q,12,y,10,12,1a,3q,3m,5u,3oo,11,l,r,1,k,e,1u,2i,fm,s,o,m,e,c,16,s,u
30,s,m,o,q,y,2m,2s,4k,3s8,7,18,i,i,i,16,e,1s,fu,1i,o,g,i,i,w,s,g
2c,i,e,e,g,g,1i,1o,2e,3uw,1w,3i,1e,1l,2g,18,2x,4c,g6,22,y,10,2g,w,1w,2o,18
2k,m,i,k,m,s,26,2c,3s,3su,1c,o,2,k,3,7,u,1o,fu,1i,u,k,k,g,16,a,8
2y,q,o,o,s,y,2q,2w,4m,3s4,9,w,1,6,2,g,18,2a,fo,1i,o,g,g,g,m,e,i
2i,i,e,i,g,k,1k,1u,2s,3to,36,2y,s,a,3,20,2h,w,g6,1u,y,y,1m,1e,22,2e,1e
3c,w,q,s,u,10,2w,34,4w,3qy,v,c,u,w,1c,2g,12,1m,fq,14,q,g,g,e,w,m,w
2c,k,g,g,i,m,1q,1s,2w,3um,2,2u,3,1o,b,d,1k,26,fw,1s,w,s,m,y,2e,m,2
28,i,e,e,e,k,1m,1e,24,3uy,18,3o,1w,1n,6,5,4q,44,ga,2c,y,u,2g,s,16,16,36
3q,10,w,w,10,1a,3i,3k,5o,3om,h,p,k,10,6,16,q,1u,fm,s,s,g,e,c,12,q,y
2g,k,e,g,g,m,1u,1q,2i,3te,3a,2y,u,t,g,1t,3e,3q,gc,20,y,y,2a,q,1i,16,2m
2g,k,i,i,k,o,1y,22,3c,3tw,6,1u,b,18,2,l,16,1w,fu,1i,w,m,k,k,1u,e,4
2q,k,g,i,i,o,20,24,38,3ss,3m,18,1y,12,15,1z,r,1g,g4,1s,w,y,w,1s,1y,14,1k
2y,o,k,m,o,s,2a,2g,3y,3sg,18,18,14,i,16,g,f,1e,fy,1i,s,m,k,k,1w,1e,i
2s,m,i,i,k,o,22,2a,3i,3sq,2w,12,24,u,9,1b,1x,9,g2,1m,y,s,m,1e,2g,12,1e
2y,q,k,m,m,s,2e,2k,40,3se,12,1a,1g,i,1e,z,1n,6,fy,1k,s,m,k,m,26,1g,u
3a,w,o,q,s,10,2w,30,4s,3rc,17,10,q,16,1q,y,7,1k,fu,1c,m,i,g,g,10,18,o
22,g,c,c,c,g,14,1c,20,3vw,16,2u,2w,11,1y,4s,2z,34,g0,2c,16,y,2i,16,28,2y,10
28,i,c,e,g,g,1e,1e,26,3w6,r,34,1s,e,l,34,2a,3o,fw,20,w,w,w,22,1u,1m,q
2q,o,m,m,o,u,2i,2o,4a,3s2,1o,3,k,9,6,8,k,1q,fs,1i,u,i,g,g,m,a,e
24,i,c,e,e,g,1c,1a,20,3ws,1b,2q,2m,g,c,2a,2w,2q,fo,20,10,s,w,1e,2g,u,1m
26,i,c,e,e,g,1a,1a,1w,3w8,b,34,2g,5,5,44,2a,4u,fy,22,y,w,14,22,1y,2s,12
3s,12,w,10,10,1a,3m,3o,5u,3oc,l,z,7,16,6,18,w,20,fk,s,m,m,e,c,12,s,y
2g,k,e,g,g,o,1u,1m,2k,3t6,34,2y,q,z,17,16,4c,2q,ge,22,10,u,28,m,16,y,36
30,s,m,q,s,y,2q,2w,4o,3s6,j,1g,3,4,g,q,16,20,fs,1g,m,g,i,g,q,g,i
2w,o,m,k,o,s,2a,2g,3y,3sg,1e,14,14,i,14,8,b,1m,fy,1i,s,m,k,k,1w,1e,g
32,u,m,q,s,y,2s,2y,4q,3s4,z,1c,4,4,g,w,1g,24,fq,1g,m,e,i,g,o,i,m
2i,k,i,i,i,o,1u,1y,32,3ta,2e,1u,e,1c,i,1h,1m,2w,g2,1o,w,q,k,o,2w,q,a
2s,m,i,i,m,o,20,2a,3k,3sq,2u,y,28,i,8,x,1j,n,g2,1m,w,q,o,14,2o,y,1e
3k,y,s,w,y,14,3e,3e,5g,3po,p,9,6,u,1a,2,k,1a,fq,w,u,i,e,e,14,y,u
2i,k,i,i,k,q,20,26,3g,3tc,14,1g,0,w,a,d,s,1o,fw,1m,u,o,k,i,1k,e,6
22,g,c,c,c,e,14,12,1s,3w6,o,2o,2w,17,1s,5o,40,28,fw,28,16,y,28,1a,24,3c,1e
34,u,o,q,u,y,2u,2y,4o,3s8,19,16,4,2,g,16,1q,2a,fo,18,q,g,g,i,o,k,m
38,s,o,q,q,y,2w,30,4o,3rm,t,1c,u,14,1m,1p,29,u,fw,1e,q,i,i,i,1a,20,o
30,q,o,q,q,y,2s,2w,4m,3s6,l,16,7,4,4,o,1c,2c,fo,1i,m,g,g,g,m,g,i
2k,m,k,k,m,s,28,2c,3u,3ss,16,c,a,0,e,8,u,1q,fs,1g,u,k,k,e,y,c,a
24,i,c,c,g,g,1a,18,1y,3wi,v,2w,2m,c,p,40,2e,4c,fs,24,y,w,w,26,1w,1w,u
32,u,o,q,s,y,2s,2y,4o,3sc,17,16,1,0,c,y,1o,2g,fo,1a,o,g,g,i,m,i,m
2u,o,k,m,o,s,2e,2i,42,3sg,1m,o,y,g,k,h,k,28,fw,1k,s,k,k,i,1k,u,8
2m,k,g,g,i,m,1w,1y,2w,3sy,3w,1u,1i,c,h,29,w,2q,g8,1s,y,10,1k,1e,1s,1c,1s
2w,o,i,k,m,q,2e,2g,3u,3sk,20,18,1y,u,h,4h,t,1k,g0,1o,w,s,m,18,2e,12,10
32,q,m,m,o,w,2q,2q,4e,3s6,8,1k,18,18,a,4d,1p,s,fy,1k,s,m,k,m,28,1i,y
2e,i,e,e,g,i,1s,1k,2a,3u8,2e,3k,10,1d,28,3v,54,3o,ga,24,10,y,2i,o,1o,o,34
2u,o,m,m,s,w,2g,2m,46,3qq,2k,e,16,n,1a,2i,1h,7,ge,1u,u,m,8,8,18,1s,q
2k,o,k,k,m,s,2a,2e,3y,3sm,10,8,6,4,0,s,c,1s,fq,1g,u,i,i,g,q,m,8
2y,q,m,q,q,y,2q,2u,4m,3s4,3,q,1,8,5,k,16,2a,fo,1i,q,e,g,g,m,e,g
24,g,c,c,e,g,1i,1a,1w,3v8,1c,38,2w,17,18,1t,40,5q,g4,2g,12,w,2g,10,1e,1k,2q
24,g,c,c,e,g,18,18,1u,3wm,p,2m,34,a,x,4i,2s,3w,fo,26,10,u,y,24,1w,1m,18
2m,m,k,k,o,s,2a,2g,3y,3sg,28,a,m,8,2,v,q,1q,fw,1k,w,k,i,g,1k,a,6
26,k,c,e,g,g,1e,1e,24,3w8,t,32,1w,c,j,34,2a,3q,fw,20,w,w,w,22,1u,1m,q
26,g,c,c,e,e,16,16,20,3wa,c,36,2u,1r,1s,56,2q,k,fy,28,10,10,24,1a,22,3c,1a
30,q,m,o,s,12,2q,2w,4q,3rs,w,1a,w,p,13,18,w,b,g4,1u,y,o,c,c,1q,u,1e
3k,y,s,u,w,14,38,3c,58,3pm,h,7,10,y,y,1q,s,1o,fo,w,w,e,e,c,10,o,y
2a,k,g,g,i,k,1o,1s,2q,3va,v,2q,8,1e,g,r,1q,2a,fs,1o,y,q,m,o,2m,i,6
22,g,e,a,e,g,14,14,1m,3wa,0,2o,2y,0,6,52,36,5w,fu,26,14,y,1c,1y,22,38,18
2k,k,g,i,i,m,1s,1s,30,3t4,36,1u,w,1q,15,u,24,2,g4,1m,y,s,k,1g,2k,w,1o
2s,m,k,k,o,w,28,2a,3q,3ri,3e,c,1s,t,1l,2s,1a,10,gc,1y,w,s,g,m,1o,18,s
2k,o,i,m,m,s,2a,2e,3y,3sk,16,a,6,2,8,m,e,1q,fs,1g,u,i,i,g,q,g,a
34,u,o,o,s,w,2q,2u,4k,3ry,t,1c,m,u,y,1s,4,1q,fu,1i,k,i,g,i,y,14,i
28,i,e,e,g,i,1i,1g,2c,3w0,x,30,1q,c,f,2a,24,2y,fu,1w,y,u,s,1m,22,s,i
2i,k,g,i,k,s,1s,1s,2w,3s4,3g,24,q,1p,1n,4m,2q,w,gi,22,u,s,1i,c,1g,14,2i
2q,m,m,m,q,u,2g,2o,4a,3s8,1i,9,g,d,4,e,q,1u,fq,1i,s,i,i,e,m,a,c
20,e,c,c,a,e,10,1a,1y,3vo,20,24,2q,a,1e,58,27,1m,fw,2a,1g,w,2c,1e,3i,1u,12
2y,o,m,k,o,s,2e,2k,40,3se,14,1a,1i,k,1c,1n,1v,0,fy,1k,s,m,m,m,28,1g,w
22,g,c,c,c,g,14,12,1o,3w8,c,2q,2w,z,1e,5e,3q,56,fw,28,14,y,20,1c,24,3c,1c
2m,o,k,k,o,u,2e,2i,44,3s8,1u,b,m,l,a,8,s,1m,fs,1g,u,i,i,e,q,8,c
4e,1c,12,14,16,1i,3u,42,6k,3oc,2x,r,x,0,y,2w,1y,38,f8,i,g,e,s,g,1i,y,12
4c,1e,12,12,18,1i,3u,44,6k,3p2,3d,r,t,5,m,2s,24,38,f6,i,g,e,q,g,1m,y,12
4c,1e,12,14,18,1k,46,44,6k,3nk,1h,z,v,0,8,m,2e,3u,f8,g,i,i,s,k,1i,10,14
4e,1e,12,14,16,1i,3w,42,6k,3ok,2z,t,t,4,m,2u,20,36,f8,i,g,g,q,i,1k,y,12
3w,16,y,12,16,1e,40,3u,66,3o8,1n,j,17,b,6,b,22,3i,fk,o,m,o,e,e,1a,s,u
42,1c,y,10,14,1c,3s,3u,6g,3po,3z,z,t,3,12,2g,1y,1s,f6,i,e,e,s,c,18,y,y
40,16,10,10,12,1a,3o,3q,60,3pk,35,1d,x,2,16,1s,1s,2k,f6,m,c,e,s,e,18,y,w
4c,1e,12,14,16,1i,3w,42,6m,3pi,3v,r,r,b,q,2q,22,3a,f4,g,g,e,m,k,1m,10,16
4e,1c,12,14,16,1i,3u,42,6k,3oi,37,r,v,3,12,2y,20,38,f8,g,g,g,q,g,1i,y,12
4e,1c,12,14,18,1k,42,42,6i,3ns,1t,x,r,0,c,18,2g,3o,f8,g,i,g,s,k,1k,10,12
4e,1e,12,14,16,1i,3w,44,6k,3ou,35,t,t,2,k,2q,24,36,f6,i,g,g,q,i,1m,y,12
4e,1e,10,14,18,1i,3u,42,6k,3o6,2p,t,v,2,o,30,22,36,f8,i,g,g,q,i,1i,y,10
4c,1e,10,14,18,1g,3y,42,6k,3pk,3x,r,p,9,i,2s,24,3a,f2,g,g,e,g,q,1m,10,16
48,1a,12,14,16,1g,3s,3y,6e,3ny,2d,13,1b,b,18,38,28,3a,fa,k,c,g,s,e,1c,y,w
42,18,12,12,16,1g,3q,3u,68,3ng,19,1d,t,h,6,2y,2g,38,fe,o,c,i,q,e,16,w,w
4c,1e,10,14,16,1i,3u,42,6k,3om,3h,p,x,d,18,30,22,3c,f6,e,g,e,s,e,1i,10,12
3m,u,u,w,u,12,32,2y,4e,3sk,d,29,2p,12,e,8,1s,26,f8,o,i,u,c,c,14,w,w
48,1a,12,14,16,1i,3s,3y,6e,3ne,1z,z,13,b,m,3e,2e,3g,fc,i,e,e,w,e,1c,w,w
46,18,12,14,16,1e,3q,3w,6c,3nu,1p,1b,1f,n,12,3g,26,38,fc,m,a,g,u,e,18,w,w
3s,10,w,y,w,16,3e,3a,54,3ri,1l,2l,1t,16,c,6,1w,2c,f6,q,e,o,i,c,14,w,w
4e,1e,10,14,18,1g,3w,44,6k,3p8,3t,r,p,d,y,2s,22,3a,f4,g,g,e,q,g,1k,10,16
4c,1e,12,12,18,1g,3w,40,6k,3oa,2z,r,x,1,12,2y,20,36,f8,i,g,e,s,g,1g,y,12
3g,u,o,u,s,12,2o,2o,3y,3tu,p,v,2r,i,v,1k,1o,1y,f8,o,k,s,c,e,10,w,s
4c,1c,10,14,18,1g,3u,40,6i,3o2,2v,r,z,9,16,36,24,36,f8,i,e,g,s,e,1g,y,y
38,s,m,s,s,w,2g,2c,3m,3v4,17,6,31,p,0,20,1w,1c,f4,o,i,q,c,e,u,s,w
42,16,10,12,16,1e,3q,3s,66,3o8,17,1h,v,l,g,2m,22,2y,fe,o,a,m,m,e,14,y,w
4e,1e,10,14,18,1g,3w,42,6k,3oi,31,r,v,0,u,2w,1y,36,f8,i,g,e,s,g,1k,y,12
4a,1c,12,14,18,1i,48,44,6m,3nc,1j,l,1j,g,8,a,28,3u,fc,g,e,12,a,i,1g,10,10
3m,y,u,w,u,14,34,30,4o,3se,r,2f,2d,1c,f,m,1y,1y,f6,q,i,q,e,c,14,w,u
4a,1c,12,14,16,1g,3u,40,6i,3o2,2t,v,z,9,16,36,24,38,fa,g,e,g,u,c,1g,y,y
4e,1e,10,14,18,1g,3w,42,6k,3oi,31,r,v,2,s,2w,1y,36,f8,i,g,e,s,g,1k,y,12
4c,1e,10,14,16,1i,3w,42,6k,3p4,3f,p,x,7,o,2u,24,38,f6,g,i,e,q,g,1k,y,12
4c,1e,12,12,18,1i,3u,44,6k,3p2,3d,r,t,5,m,2s,24,38,f6,i,g,e,q,g,1m,y,12
3s,10,y,y,y,14,3e,3c,56,3r2,1f,2v,1p,10,1m,0,1m,2a,f6,q,e,s,e,c,16,w,y
4e,1e,10,14,18,1i,42,40,6k,3o0,23,v,r,1,g,1q,2e,3k,f6,i,i,g,s,k,1k,y,12
3q,y,w,w,w,14,3a,36,4w,3rq,v,2l,2b,14,w,0,1s,28,f6,q,g,q,g,c,16,u,w
4e,1e,12,14,16,1i,3w,42,6k,3om,31,t,t,2,o,2u,20,36,f8,i,g,e,s,i,1k,y,12
4e,1e,10,14,18,1g,3w,44,6k,3p0,3b,r,t,1,m,2q,22,36,f6,i,g,g,q,g,1m,y,12
44,1a,10,12,14,1c,3q,3u,6e,3pc,3h,x,11,5,12,30,1y,26,f6,i,g,e,s,c,1a,10,y
48,1c,12,12,18,1g,3s,3y,6g,3nu,2d,15,z,b,14,38,26,3c,fa,i,e,e,w,c,1e,w,y
4a,1c,12,14,16,1i,3s,40,6i,3nu,2j,t,z,9,10,36,26,3c,fa,g,g,e,u,e,1g,y,w
4e,1e,10,14,18,1g,3w,42,6k,3oq,3b,p,v,0,w,2s,1y,38,f6,i,g,g,q,g,1k,y,14
4e,1e,10,14,18,1g,3w,42,6k,3oq,39,r,t,0,u,2s,1y,36,f6,i,g,g,q,i,1i,y,14
4e,1e,12,12,18,1i,3w,42,6k,3p6,3n,r,r,1,s,2q,20,36,f6,i,g,e,q,i,1k,10,14
4e,1e,10,14,18,1i,3u,44,6k,3oo,35,r,v,2,s,2s,20,36,f8,i,g,e,q,i,1k,y,12
4e,1c,12,14,16,1i,3w,42,6k,3oy,37,r,x,1,m,2s,24,36,f6,i,g,e,s,g,1m,y,10
4e,1e,10,14,18,1g,3w,42,6k,3og,2z,r,v,0,u,2w,20,36,f8,i,g,e,s,g,1k,y,12
4c,1e,10,14,18,1i,3u,40,6k,3nu,2f,t,x,1,o,36,22,3c,fa,g,g,g,s,g,1i,y,y
4e,1e,10,14,18,1i,3w,42,6k,3oy,3b,r,t,1,o,2s,20,36,f6,i,g,g,q,g,1m,y,12
4c,1e,12,12,18,1i,3w,42,6k,3oy,37,r,x,3,o,2s,24,36,f6,i,g,e,s,g,1k,10,10
3y,18,y,10,12,18,3o,3o,60,3q8,37,1p,15,i,12,14,1w,28,f6,m,e,g,o,e,16,y,w
4c,1e,10,14,16,1i,3w,42,6k,3pc,3p,p,v,b,q,2s,26,3a,f4,i,e,g,q,e,1m,y,14
40,1a,y,10,14,1a,3s,3u,68,3py,3t,1b,r,1,14,1k,22,24,f4,k,e,e,q,c,18,y,y
46,1a,10,12,16,1c,3u,3w,6k,3pg,3r,v,z,b,12,2o,2a,22,f4,i,g,c,s,c,1c,10,y
4e,1c,12,14,16,1i,3u,42,6k,3oc,2x,r,x,0,w,2y,1y,38,f8,i,g,e,s,g,1i,y,12
4c,1g,10,14,16,1i,3w,44,6k,3pg,3x,r,p,7,m,2s,22,3a,f4,e,g,e,i,o,1m,10,16
3o,w,u,y,u,14,34,30,4o,3rw,l,2d,2j,14,w,e,1m,28,f6,s,g,u,c,e,14,w,w
4a,1c,12,14,16,1g,3u,3y,6i,3o0,2p,v,z,9,14,36,24,3a,fa,i,e,e,u,e,1e,y,y
4e,1e,12,12,18,1i,3w,42,6k,3p8,3n,r,r,3,s,2q,20,36,f6,i,g,e,q,i,1k,y,16
42,16,10,14,14,1e,3o,3u,66,3o4,z,1h,2f,4,14,34,1w,2u,fe,o,8,s,i,e,14,y,u
4e,1c,12,14,16,1i,3y,42,6k,3oe,2h,t,v,3,k,2a,2a,3c,f6,g,i,g,q,k,1k,10,10
36,q,k,q,o,y,2a,2a,3c,3vc,t,o,3l,8,17,2g,1e,1u,f0,q,g,g,o,8,s,o,u
4e,1e,12,12,18,1i,3w,42,6k,3ow,37,t,t,2,k,2s,22,34,f6,i,i,e,q,i,1m,y,12
4c,1e,10,14,16,1i,3w,40,6k,3oy,37,r,x,5,m,2u,26,38,f6,i,g,e,s,e,1k,10,10
4c,1e,10,14,16,1i,3w,42,6k,3p4,3f,r,t,9,o,2s,26,36,f4,i,g,e,s,g,1i,10,12
44,1a,10,14,16,1g,3q,3w,6a,3nk,1l,z,1f,p,i,3o,2a,38,fe,k,c,i,s,e,18,y,u
38,s,m,q,s,y,2e,2c,3k,3v4,17,8,3b,1,b,24,1s,1a,f4,q,g,o,g,c,u,s,u
4c,1e,12,14,16,1i,3y,40,6k,3ok,2p,t,t,5,m,2e,28,3a,f6,g,i,e,s,i,1m,y,12
4e,1c,12,14,16,1i,3w,42,6k,3p0,3b,r,t,3,m,2s,22,38,f6,i,g,e,s,g,1m,y,12
4a,1c,10,14,18,1i,3w,40,6i,3n6,1p,r,13,3,k,2g,2i,3o,fc,g,e,g,u,g,1g,y,u
4c,1c,12,14,16,1i,3w,40,6i,3nm,29,r,z,1,m,34,26,3e,fa,g,g,g,s,g,1g,y,y
4c,1g,10,14,16,1i,3w,44,6k,3pi,3v,r,p,f,u,2q,20,3a,f4,g,g,e,q,g,1m,10,16
4e,1e,12,12,18,1g,3w,44,6k,3ou,3f,r,t,1,y,2s,1y,38,f6,i,g,g,o,i,1k,y,14
4e,1e,10,14,18,1g,3w,42,6k,3om,39,p,v,1,10,2u,1y,36,f6,i,g,g,q,g,1k,y,12
4a,1c,12,14,16,1g,3u,40,6i,3ns,2h,t,11,7,y,38,24,3e,fa,i,e,e,u,e,1g,w,y
4c,1e,12,12,18,1i,3u,42,6i,3o2,2p,r,x,0,s,32,22,38,f8,i,g,g,q,g,1i,y,10
4c,1e,12,12,18,1g,3w,42,6i,3o4,2r,r,x,1,w,32,20,38,f8,i,g,e,s,g,1i,y,10
4a,1c,12,14,18,1i,3y,42,6g,3na,1l,v,13,2,g,22,2g,3q,fa,g,g,k,q,i,1g,y,w
34,q,m,q,o,w,2a,2a,3a,3ve,v,s,3n,g,1b,2g,18,20,ey,s,g,g,o,8,q,o,s
4e,1e,10,14,18,1i,3w,42,6k,3pa,3n,r,r,3,q,2o,20,38,f6,i,g,e,q,i,1k,10,12
4e,1e,10,14,18,1g,3w,44,6k,3p6,3j,r,r,3,o,2q,22,36,f6,i,g,e,q,i,1k,y,14
4e,1c,12,14,16,1i,3w,42,6i,3no,27,t,x,2,k,2w,28,3c,fa,g,g,g,s,i,1i,y,y
48,1a,12,12,16,1g,3s,3y,6e,3nq,21,15,1b,b,w,3e,2a,3a,fc,k,c,g,u,c,1c,w,w
4a,1c,12,14,18,1i,40,40,6i,3na,1l,t,17,8,e,1u,2e,3s,fa,i,e,o,m,i,1g,y,y
4c,1c,12,12,18,1i,3u,42,6k,3p2,3d,p,x,7,o,2u,26,36,f6,i,g,e,q,g,1k,y,10
4c,1c,12,12,18,1g,3u,40,6k,3o0,2t,p,z,5,14,32,22,3a,fa,g,g,e,u,e,1g,y,y
4e,1e,12,12,18,1i,3u,44,6k,3ou,3d,t,t,0,w,2s,20,36,f6,i,g,g,q,g,1k,y,14
4c,1c,12,14,16,1g,3u,42,6i,3o4,2v,t,x,7,16,32,24,36,f8,i,e,g,s,e,1g,y,10
3s,12,y,y,y,16,3e,3c,5a,3qm,1t,21,1v,m,1k,s,1k,2k,f6,q,e,m,k,c,16,w,y
4e,1e,12,12,18,1i,3w,42,6k,3oy,3h,r,t,0,u,2s,1y,36,f6,i,g,g,o,i,1k,y,14
3i,w,s,u,u,12,2y,2u,4c,3ta,j,1v,2p,k,1,e,22,1y,f6,o,i,o,g,c,12,y,u
4a,1c,12,12,16,1g,3u,3y,6i,3o2,2n,13,z,b,18,36,26,3a,fa,i,c,g,u,c,1e,y,y
4e,1e,12,14,18,1i,3w,42,6m,3oi,2t,v,v,6,k,2o,26,36,f8,i,g,g,q,k,1k,y,12
4c,1e,12,14,18,1i,48,44,6k,3nk,1j,v,11,4,8,m,2c,3s,f8,g,i,m,o,k,1i,10,12
4c,1c,12,12,18,1g,3w,42,6m,3pg,3t,p,v,b,k,2w,28,36,f2,g,g,e,k,m,1k,10,14
3u,14,y,y,y,18,3k,3i,5k,3r0,2f,2p,x,q,e,i,1s,2g,f4,o,e,g,o,c,16,w,y
4e,1e,10,14,18,1g,3w,42,6m,3oq,37,r,v,2,s,2s,20,36,f8,g,i,e,q,i,1k,y,12
4a,1c,12,12,18,1g,3u,3y,6i,3no,2d,t,11,9,u,3c,28,3c,fa,i,e,g,u,e,1e,y,w
3m,u,u,w,u,14,32,2y,4k,3sq,j,2p,23,w,3,e,1u,24,f6,o,i,o,g,c,14,w,u
4e,1e,10,14,18,1g,3w,42,6k,3om,35,r,v,0,w,2s,1y,38,f8,g,i,e,q,i,1i,y,14
4a,1c,10,12,16,1i,3u,40,6m,3pg,3t,r,z,7,i,30,2c,2w,f2,g,g,e,e,q,1g,12,12
46,1a,10,12,14,1e,3s,3w,6i,3pe,3n,v,z,7,12,2o,28,24,f6,i,g,c,s,e,1a,10,y
4e,1e,12,12,18,1i,3w,42,6k,3p2,3j,r,r,1,s,2q,20,36,f6,i,g,g,o,i,1m,y,14
40,14,10,12,14,1c,3o,3s,62,3oc,r,19,1p,4,u,2e,1k,2g,fe,q,a,s,g,e,14,u,y
4e,1e,12,14,18,1i,3w,42,6k,3ok,2x,t,t,4,k,2q,24,36,f8,i,g,g,q,k,1k,y,12
48,1c,10,12,16,1e,3s,40,6k,3p0,37,t,11,d,u,36,2a,2e,f4,i,g,e,u,c,1e,10,y
4e,1e,10,14,18,1i,42,40,6i,3o0,23,v,r,0,g,1o,2e,3k,f6,i,i,g,s,k,1k,y,12
4c,1c,12,12,18,1g,3u,42,6i,3ny,2r,n,z,7,12,34,22,3c,fa,g,g,e,u,e,1g,y,y
3k,y,q,u,u,14,32,36,52,3qi,l,d,12,14,u,2o,1c,1k,fm,u,u,g,e,g,w,o,10
3g,w,s,s,u,10,2y,32,4u,3rc,r,b,10,u,y,2w,1q,1k,fm,u,w,g,e,i,u,o,y
3q,y,u,u,y,16,38,3e,5c,3pm,h,f,10,s,k,2e,10,1o,fk,s,s,i,e,e,y,q,10
3s,10,s,y,y,16,3a,3g,5i,3pi,f,b,1,s,1a,2e,u,1c,fi,s,m,m,e,e,10,q,10
46,18,12,12,16,1e,3s,3w,6a,3nu,1r,1b,v,l,q,36,26,36,fc,m,c,g,s,e,18,y,w
2s,q,m,o,q,w,2m,2s,4g,3s2,2,7,5,0,b,w,1e,22,fk,1a,w,e,e,g,m,6,g
3s,10,w,w,10,16,3e,3k,5k,3oy,d,n,1,14,y,24,w,1m,fi,u,o,k,e,c,10,s,y
3q,10,u,w,y,14,3c,3g,5g,3pm,f,b,6,y,1c,22,q,16,fi,s,m,m,g,c,10,s,y
3w,12,w,y,16,1e,3m,3o,5u,3oi,j,b,e,1d,15,2c,1m,28,fg,s,g,o,e,g,10,u,y
3c,w,q,s,u,10,2u,30,4s,3s8,17,5,10,g,o,2c,20,1q,fm,u,w,g,g,i,u,o,u
32,s,m,s,s,y,2s,2w,4m,3sg,z,s,b,1,4,y,1s,2k,fk,12,y,g,g,g,m,i,i
2u,q,o,o,s,w,2q,2s,4k,3rw,5,f,b,3,9,10,1i,2q,fi,18,w,e,e,e,o,6,g
3k,y,s,u,u,12,30,38,50,3qm,d,n,y,16,1c,2y,1c,1a,fk,u,s,i,g,e,w,q,10
3k,w,s,s,w,12,30,36,50,3r0,l,h,y,14,12,2k,1e,18,fk,u,s,i,g,g,w,q,y
30,u,m,s,s,y,2s,2w,4m,3si,15,m,l,5,4,16,24,2i,fi,12,y,c,g,i,o,c,k
3w,12,w,y,14,18,3g,3k,5q,3oq,l,j,h,b,y,3a,1g,28,fg,s,i,k,i,e,10,u,y
3i,y,s,s,u,12,2y,34,4y,3qy,l,h,10,10,16,34,1k,1i,fk,u,w,g,e,g,w,q,y
30,u,o,q,s,10,2q,2w,4m,3sg,19,g,n,7,4,1c,26,2k,fi,10,w,e,g,i,o,a,m
3g,y,q,s,u,10,2w,32,4u,3ro,n,l,10,w,s,2y,1q,1i,fk,u,u,g,g,i,u,q,w
3e,w,q,s,s,12,2w,30,4u,3rk,z,1,10,s,q,2m,1u,1m,fo,u,w,g,g,g,u,o,u
34,u,o,q,s,10,2s,2y,4k,3sq,1f,o,3,7,6,1c,24,2k,fk,10,w,e,g,k,o,g,m
38,w,o,s,s,10,2u,30,4q,3s6,1b,k,o,8,g,1u,1y,22,fo,10,u,g,e,i,q,o,q
3g,w,q,s,s,12,2w,30,4s,3ry,x,d,10,m,q,2q,1w,1o,fk,u,w,g,g,i,u,o,w
2m,m,m,k,o,u,2e,2i,44,3s8,q,d,2,5,4,m,w,24,fq,16,y,g,i,e,o,a,8
2y,s,o,q,s,y,2q,2w,4m,3sc,t,m,l,0,1,y,1u,2i,fi,14,y,e,g,i,k,e,i
3a,w,q,q,u,10,2u,30,4q,3se,1b,2,w,a,i,22,20,1y,fm,w,w,g,g,i,s,o,s
36,u,o,s,s,10,2s,2y,4m,3sw,1j,g,a,9,8,1m,24,2i,fm,w,w,e,g,k,q,i,o
3i,w,s,s,u,10,2y,32,4y,3ra,r,d,10,w,10,30,1o,1i,fm,u,u,g,g,g,u,q,y
3w,14,w,10,12,1c,3i,3o,5s,3ok,l,l,d,n,a,30,1k,2c,fg,s,g,m,i,c,12,u,y
30,s,o,q,s,y,2s,2w,4m,3sg,z,q,h,1,0,10,1u,2k,fk,12,y,e,g,i,k,g,i
32,s,o,q,s,10,2s,2w,4k,3sk,19,m,j,5,4,18,26,2i,fi,12,w,e,g,i,o,c,m
3q,y,u,u,y,14,36,3c,5a,3pm,f,d,u,q,1a,3a,16,1m,fi,u,s,g,g,c,y,q,12
48,1c,12,12,18,1e,3u,3y,6g,3nu,2j,v,17,9,12,3c,26,3e,fc,i,c,i,s,e,1c,y,w
3k,y,s,s,w,12,32,38,52,3qg,j,f,12,14,w,2o,1a,1k,fm,s,w,g,e,g,w,o,10
36,u,o,q,s,10,2s,2y,4o,3sk,1f,w,6,1,a,1a,1y,2g,fm,12,u,g,g,i,o,k,m
3u,12,w,y,10,1a,3o,3o,5s,3oo,j,r,e,1a,f,k,g,28,fi,s,q,g,c,e,10,u,y
3m,y,s,s,w,14,32,38,54,3q8,h,h,12,18,10,2s,16,1i,fk,u,u,g,g,e,w,q,y
3s,10,u,y,y,16,3c,3k,5m,3ow,h,7,3,w,1m,2a,o,10,fk,s,q,i,e,c,10,s,y
3g,w,q,s,u,12,2w,32,4u,3rc,t,7,10,s,w,2w,1q,1k,fm,u,w,g,g,g,u,o,w
36,u,o,s,s,10,2u,2y,4o,3s8,1b,w,c,4,g,1g,1u,2a,fo,14,s,g,g,g,q,k,o
2q,o,k,o,o,w,2i,2m,4c,3s0,u,h,c,f,1,q,18,1u,fo,1c,u,e,i,c,o,6,e
3w,12,w,10,12,1a,3k,3o,5s,3ok,h,z,2,0,3,2c,1c,2a,fg,s,i,o,e,e,10,u,y
3q,10,s,w,y,16,38,3c,5c,3pk,f,f,s,c,o,36,1c,1o,fi,u,q,i,e,e,y,s,10
3e,w,s,q,u,10,2y,30,4u,3rk,z,3,12,m,u,2o,1s,1o,fm,w,u,g,g,i,s,o,w
3k,w,s,s,u,12,30,34,50,3qw,d,n,y,14,16,2w,1i,1a,fk,u,u,g,g,g,w,q,y
3k,y,s,u,u,12,32,36,52,3qq,j,h,y,16,16,2o,1c,1a,fk,s,s,i,g,g,w,q,10
2s,q,o,q,s,10,32,3a,5c,3rs,12,17,j,14,f,9,14,h,fa,10,m,s,a,8,e,8,k
32,u,o,q,s,10,2q,2w,4i,3sc,1h,c,n,b,1,1m,26,38,fc,y,i,u,e,g,q,a,k
3e,w,s,q,u,10,2w,30,4s,3sc,x,j,y,i,o,2o,1y,1m,fk,u,u,g,g,i,u,q,u
2y,s,o,q,s,10,2q,2w,4k,3rw,z,0,n,1,3,16,20,36,fe,14,w,g,e,e,q,8,i
3c,w,o,s,s,10,2u,2y,4o,3t6,1d,b,w,1,e,26,24,20,fm,s,u,g,g,k,s,o,s
32,u,o,q,s,10,2q,2w,4i,3sa,1f,1,f,9,6,1m,28,32,fe,y,s,k,e,g,o,c,k
3c,w,q,q,u,10,2w,2y,4q,3sq,17,b,w,8,i,2a,24,1u,fm,s,u,g,g,k,u,o,s
38,u,o,q,u,10,2q,2y,4k,3ti,1l,0,c,l,6,1s,2a,2g,fk,u,u,g,e,k,s,g,o
36,u,o,q,s,10,2s,2w,4k,3t2,1n,6,0,f,4,1s,2c,2k,fk,w,u,g,e,i,s,c,o
3i,w,s,s,s,12,2w,32,4u,3rw,p,p,10,q,w,30,1u,1i,fk,s,s,g,i,i,u,q,y
34,s,o,s,s,10,2q,2w,4i,3so,1l,2,9,b,4,1q,2c,2o,fg,y,u,i,e,i,q,a,m
3q,10,u,u,y,14,38,3e,5g,3q2,f,d,6,w,1g,2i,s,w,fg,s,k,o,g,c,10,s,10
36,u,o,q,u,y,2s,2w,4m,3sy,1l,e,2,d,8,1m,28,2k,fk,w,w,e,g,k,q,g,o
34,u,o,q,s,10,2s,2w,4k,3ss,1j,i,9,b,6,1k,28,2m,fk,y,u,e,g,k,q,c,o
36,u,o,q,u,y,2s,2w,4m,3t2,1n,a,2,f,8,1o,2c,2i,fk,w,u,g,g,i,s,e,o
38,u,o,s,s,10,2s,2w,4m,3ta,1n,4,e,f,a,1s,28,2g,fk,w,u,g,e,k,s,i,o
38,u,o,q,u,y,2s,2w,4k,3ti,1l,1,c,n,6,1o,2c,2i,fi,u,u,i,e,i,s,e,q
3o,y,s,u,w,14,34,38,56,3qq,f,p,u,16,u,2i,18,1e,fg,u,o,i,g,g,y,s,10
2u,s,o,q,s,10,32,36,50,3s8,6,l,t,s,h,9,1q,1m,f8,12,g,u,c,8,k,a,k
2y,u,m,s,s,10,2u,30,4k,3s6,n,e,13,3,f,o,1k,44,f8,12,a,10,e,c,o,g,i
2y,s,o,q,u,10,2u,2w,4o,3ry,j,i,15,3,d,q,2a,3a,f8,12,a,10,e,c,q,e,g
3y,14,y,12,14,1c,3i,3q,5y,3p2,t,t,1r,13,u,3k,1o,2g,fc,q,c,g,o,e,12,w,10
44,18,14,12,16,1e,3q,3w,6a,3oc,1h,2j,13,x,1a,3g,2a,38,fc,k,a,c,w,c,18,y,w
44,16,12,12,16,1e,3q,3u,68,3oe,1b,1v,17,x,o,3c,24,32,fc,m,a,g,s,c,16,y,y
2s,q,o,o,s,10,2y,36,50,3r0,1g,15,0,a,b,t,1c,20,fe,y,y,g,e,8,m,6,g
3u,10,w,y,10,18,3e,3k,5o,3pk,j,n,l,s,w,22,10,1o,fg,q,g,k,k,e,10,u,10
2q,q,o,q,s,12,36,38,5e,3s0,10,1r,0,1q,11,m,1y,1l,fa,10,u,i,8,6,6,8,k
36,u,o,q,s,10,2s,2w,4m,3sy,1l,e,0,d,6,1o,28,2k,fk,w,w,e,g,k,q,g,m
3o,y,s,u,w,14,32,38,54,3qq,h,p,u,14,1a,3a,18,1e,fg,s,s,g,g,e,y,q,12
2y,u,m,s,s,y,2s,2w,4k,3rq,x,0,j,1,7,18,20,3c,fe,12,u,i,c,g,m,e,g
2y,s,o,q,u,y,2u,2y,4k,3rm,l,4,n,3,9,s,1k,40,fa,12,o,o,c,e,q,c,g
30,u,m,s,s,10,2s,2u,4k,3s0,z,i,z,7,7,14,2c,3q,fa,10,e,w,e,g,o,e,i
30,u,o,q,s,10,2q,2w,4k,3s6,1d,4,j,5,1,1k,24,36,fe,12,u,i,e,e,q,a,k
3a,w,o,q,u,10,2u,2w,4o,3ta,1j,3,s,9,c,20,26,26,fm,s,u,g,g,k,s,m,q
44,16,12,12,16,1e,3q,3u,68,3oc,1b,21,19,d,y,2w,22,34,fc,m,8,g,u,c,18,w,y
2o,q,m,o,q,w,2o,2u,4s,3r6,w,19,o,5,h,a,y,1c,fm,y,12,g,g,a,k,a,8
3o,y,s,u,w,12,38,3a,5a,3r2,f,r,m,1a,w,22,s,10,fg,s,k,m,e,g,w,u,10
2s,q,o,o,q,10,2s,32,50,3qq,18,z,8,4,n,3,o,1i,fi,y,y,i,e,a,m,a,a
34,u,o,q,s,10,2q,2u,4k,3sq,1n,0,9,d,4,1o,2c,2s,fe,y,o,m,e,i,q,a,m
30,u,o,q,s,10,2s,2u,4i,3s0,15,g,v,7,3,1c,2a,3m,fa,12,g,u,e,g,o,c,i
2m,m,m,m,o,u,2e,2m,48,3s2,o,p,4,1,5,o,y,1o,fo,18,y,e,k,a,o,a,6
3a,u,q,q,u,y,2u,2w,4o,3ti,1l,3,m,d,c,1w,28,28,fm,s,u,g,e,m,s,k,q
32,s,o,q,u,y,2s,2u,4k,3s2,19,g,t,9,3,1g,26,3i,fa,12,e,w,e,e,q,e,i
3e,w,q,q,u,10,2u,30,4q,3sw,11,l,w,8,i,2i,1y,1s,fk,s,u,e,g,k,u,o,w
2u,q,o,q,q,10,30,3c,54,3rm,y,t,l,o,h,d,m,10,fa,10,k,u,c,8,i,a,i
3q,y,u,u,w,16,3c,3e,5a,3qg,j,l,u,16,4,1m,10,1q,fg,s,m,k,g,e,10,s,10
3m,y,s,u,u,14,30,38,52,3re,d,r,i,12,16,36,16,1i,fg,s,i,o,e,g,w,s,12
2m,o,m,m,o,w,2c,2a,3m,3n0,42,1w,22,6,j,8,2m,r,g4,1g,q,k,k,m,1c,o,1q
2c,m,e,i,i,o,1w,1w,30,3pc,26,4k,i,1q,t,w,2w,3a,fs,1y,w,y,u,1c,2q,12,24
20,i,e,e,g,i,1i,1a,1y,3qq,18,20,3i,s,18,z,52,50,fk,20,18,k,o,k,2e,s,i
2m,u,q,s,u,10,2q,2i,3w,3pa,4y,h,1f,h,i,q,32,14,g8,10,m,g,c,c,i,g,m
2g,o,i,i,i,q,1u,1w,36,3n0,2o,2e,1q,1i,y,26,2s,1o,fw,1s,w,u,q,s,2q,1e,1k
22,i,e,c,g,i,1i,1g,20,3r8,s,2m,3w,b,2,2q,20,6e,fk,28,1c,y,y,26,1y,2q,1o
24,k,e,e,g,i,1c,1c,20,3r4,1,2u,22,u,s,4u,38,3m,fm,24,14,12,o,1s,1q,16,1i
28,q,k,m,o,u,2c,28,3a,3si,c,3w,c,7,1b,0,o,34,g4,1m,a,k,c,e,e,a,a
2k,s,o,o,s,y,2o,2e,3s,3oy,4u,k,10,1l,j,e,2k,w,g8,16,i,i,g,e,m,e,s
2k,m,i,k,k,q,22,26,3k,3ms,38,20,24,12,y,12,2g,16,fw,1o,y,s,q,q,2w,1a,1g
2g,s,q,s,s,y,2o,2g,3q,3ry,32,1d,2p,18,s,u,22,1u,g4,18,m,e,c,6,g,c,i
2e,s,q,s,s,y,2o,2k,3s,3ry,2o,d,1z,6,q,k,s,2a,g4,16,i,c,8,4,e,a,e
28,m,k,o,i,o,1s,1q,2o,3vy,11,a,31,2e,26,3i,2k,4,fg,28,u,o,o,w,2a,1i,3g
26,m,i,i,k,o,1w,1q,2m,3s4,1,3m,1q,k,8,1r,3s,3w,fy,1w,k,m,i,m,1q,g,g
2a,s,o,q,s,y,2o,2k,3s,3sa,1y,q,b,1d,8,c,s,1e,g4,14,i,a,6,0,e,8,a
1y,g,e,c,a,g,12,10,1e,3ps,1q,16,40,36,4,6i,6a,7m,fa,28,1k,10,s,24,26,1i,2y
26,s,m,o,u,w,2o,2k,3u,3rm,3w,6,1,25,s,l,w,m,g0,6,18,a,4,2,8,8,6
1y,g,e,c,e,i,1a,16,1s,3q0,2a,12,3u,1c,n,4m,34,7e,fi,2a,1k,y,w,26,20,38,1g
2m,s,o,q,u,y,2o,2g,3u,3o6,5a,m,y,1t,0,2,30,8,g8,14,k,i,g,e,o,e,y
20,k,e,e,g,g,1c,18,1y,3r4,q,24,2s,o,16,2w,48,30,fk,20,1c,u,q,10,28,10,12
1u,e,e,c,a,e,10,y,1c,3og,3w,3,3u,3k,0,72,56,8c,fa,2c,1m,12,u,2a,24,36,1q
28,q,k,m,q,w,2e,2g,3g,3s8,1a,2w,i,19,p,6,2,28,g2,1e,e,e,8,8,a,8,8
1y,g,e,c,e,i,1a,18,1q,3q4,24,16,3y,1a,l,4m,32,7c,fi,2a,1i,y,w,28,20,36,1g
2g,q,o,o,s,y,2k,2g,3m,3qm,3c,w,12,1n,x,o,1e,26,g6,1a,i,g,c,a,i,c,g
24,k,e,g,i,m,1s,1q,2i,3s0,d,3u,1w,h,d,y,32,5m,fo,28,10,16,w,1w,20,1g,2o
2g,q,k,m,m,w,2a,26,3c,3pa,2y,2w,10,e,21,a,2y,1g,g2,1o,g,q,i,i,12,e,s
1u,e,e,a,c,c,y,w,18,3os,3q,d,3m,42,i,7u,5y,92,f6,2c,1o,12,s,2a,26,2q,20
2e,m,g,i,i,o,1o,1s,2q,3oa,1q,38,u,1q,u,2i,22,2e,fu,1w,w,w,q,s,2o,16,1c
2m,u,q,u,s,10,2q,2e,3y,3po,4u,15,2b,a,o,12,44,12,g6,14,m,i,e,a,k,i,m
20,i,c,e,e,g,1c,18,1s,3qc,1k,26,42,10,9,4i,3a,68,fi,28,1g,y,u,28,24,2c,22
22,g,e,e,g,i,1g,1e,1y,3r2,12,28,40,b,3,34,24,6q,fk,28,1e,10,w,24,20,30,1g
3a,12,y,10,14,1e,4c,4u,7k,3m4,13,l,g,4,q,16,d,2k,fe,y,14,i,g,g,1g,12,26
2g,m,i,m,o,o,22,20,32,3sa,16,4w,t,f,f,4s,1s,2g,fo,28,u,s,w,2u,1g,38,14
2u,u,s,s,y,12,2w,2o,4a,3ms,44,28,f,19,c,k,3e,h,g4,16,i,i,e,i,s,k,1a
32,y,s,u,y,16,3a,3e,5m,3lg,f,20,w,q,14,2u,g,i,fu,1c,k,k,e,e,10,22,16
2q,u,o,s,u,14,3g,40,66,3ow,3,o,s,5,1,2d,1t,2u,fk,1c,10,o,m,m,1w,1e,14
2q,s,o,o,u,10,34,3g,5u,3oi,40,30,2s,j,b,2j,e,l,fq,2a,g,o,s,14,32,m,w
2y,u,q,s,u,14,3c,3o,5s,3ly,12,1s,w,1i,u,1h,l,1a,fu,20,s,o,k,i,2k,18,10
36,16,10,12,18,1m,4y,5a,7q,3pc,2f,1h,b,9,f,v,1w,78,fa,y,18,i,i,i,1i,12,2c
2a,o,g,m,k,o,26,26,3q,3v8,q,54,27,1y,2f,3s,r,0,fm,20,12,w,q,3e,1a,38,0
38,14,y,10,16,1k,4q,54,7m,3ny,29,h,6,5,7,r,2,6m,fe,18,1a,g,k,e,1e,1c,1q
2q,o,o,w,u,10,3c,3q,6m,3q6,6o,1i,3p,1,6,v,9,2l,fs,1s,q,s,q,1e,2e,e,10
36,14,w,10,16,1i,4q,54,7u,3n4,25,k,e,0,1,3n,14,56,fm,1s,u,g,k,e,2m,o,w
36,14,y,12,16,1m,4u,54,7m,3pc,1t,1t,9,d,f,17,2m,6w,fc,u,12,m,i,k,1c,1c,2a
2y,w,s,u,y,14,32,2y,4m,3mc,2g,2o,e,19,a,12,20,n,g0,1c,i,g,e,i,y,u,22
38,12,y,y,14,1g,4c,4w,7g,3me,t,r,e,b,c,w,7,3m,fe,y,14,i,i,g,1e,14,26
2q,u,q,u,w,16,3m,40,6a,3pg,1,n,q,2,d,2f,3,36,fg,1c,10,o,o,k,26,16,18
2s,u,q,s,w,10,2w,2k,46,3n0,4y,1m,d,1b,c,c,3o,6,g6,14,i,i,e,i,s,g,o
2m,q,m,s,s,u,2e,28,3e,3pi,2w,10,2d,2,10,2g,3y,3c,fs,20,w,m,o,1a,1y,y,1y
2w,10,w,y,12,1e,4c,4k,74,3qw,1z,1v,1,9,7,1l,14,5y,fc,10,10,q,g,m,1i,1k,1q
3a,14,12,12,18,1m,54,5e,84,3nu,2d,r,k,1,h,1v,i,5w,fg,1k,10,g,i,g,1s,1a,1e
36,16,12,14,1a,1o,54,56,7s,3qa,1f,31,p,h,p,j,38,6y,fc,o,k,14,g,m,1m,1i,28
36,16,12,14,1a,1o,54,56,7s,3qa,1f,31,p,j,p,j,3a,6y,fc,o,k,14,g,m,1m,1i,28
2u,u,q,q,u,12,2w,2s,4e,3lm,3i,2s,w,6,3,6,12,c,g4,18,m,g,g,g,w,14,1e
32,14,10,12,18,1m,4u,4y,7m,3qm,1b,33,j,l,j,p,2w,6k,fc,s,o,y,g,k,1m,1e,28
2i,o,k,m,o,w,2o,2y,4y,3pq,1q,1c,14,6,x,1x,o,9,fm,1o,y,w,u,w,2m,w,u
2a,m,g,k,k,q,26,26,3a,3rq,7,44,7,q,21,o,38,4k,fo,20,10,12,w,1o,24,10,12
2k,o,m,o,q,y,2w,3a,58,3p8,18,y,12,4,d,27,6,k,fm,1k,10,s,s,q,2s,u,w
2s,s,o,q,s,12,38,3o,62,3n0,1c,s,1e,o,3,2b,17,c,fq,1c,y,q,k,m,2e,16,u
30,12,y,12,14,1i,4o,4s,7e,3qs,1v,2l,5,d,j,x,2g,68,fa,y,w,s,g,m,1i,1e,22
2q,s,q,q,w,14,3e,3y,62,3ow,2,4,q,a,9,2v,5,2m,fi,1e,10,m,q,m,2k,u,16
2o,q,m,m,q,y,2q,2y,4s,3oi,3m,24,40,k,1h,a,10,a,fq,2e,m,m,u,1u,2c,s,s
2g,o,i,k,q,s,2c,2e,3y,3ru,2q,4q,q,17,1j,3k,5,1o,fs,22,u,u,u,36,18,34,1n
2o,s,o,q,u,12,3a,3q,5w,3os,g,e,s,8,7,2f,d,1u,fk,1g,10,o,q,o,2g,12,12
2k,m,k,m,o,w,2m,30,4y,3p6,2c,1c,1e,e,j,2b,k,l,fo,1o,y,s,s,s,2w,s,u
2a,m,g,m,k,o,20,20,3c,3vc,1,5k,21,20,21,3w,7,12,fk,2a,u,w,q,3a,1i,3s,2
2m,q,m,s,s,10,32,3e,60,3qe,6c,1i,1z,l,1n,1o,3,2b,fu,1s,s,u,q,2e,1g,i,s
36,14,12,12,1a,1m,52,54,7s,3qa,15,37,n,l,p,r,38,6y,fc,o,q,y,e,k,1m,1g,2a
2m,o,k,m,o,u,2k,2w,4u,3ne,38,16,24,e,8,1j,4,t,fs,1k,10,q,q,q,2y,w,u
2w,10,y,y,14,1g,4g,4o,78,3qk,1l,25,3,h,d,17,1q,5w,fe,w,12,m,g,m,1g,1g,1y
34,16,10,14,1a,1m,50,52,7q,3qi,1b,35,r,j,l,j,34,6u,fc,o,m,12,g,m,1m,1g,28
2y,u,q,s,u,12,34,3k,5w,3lo,1,22,q,10,1c,1g,29,n,fs,1c,u,k,i,i,1c,2a,y
30,12,10,12,16,1k,4q,4w,7k,3qc,19,2x,9,l,h,n,2m,6g,fc,u,y,q,e,m,1i,1e,26
2k,s,w,s,u,14,3q,4c,82,3tk,4o,7p,y,y,1m,3,55,47,g0,i,1s,i,m,s,1k,1c,18
2k,s,o,q,s,12,36,3m,5w,3ow,4,c,y,0,6,2d,b,1a,fk,1i,10,o,q,m,2i,10,y
38,14,12,12,18,1m,50,58,7s,3ow,p,2p,9,l,n,f,28,76,fc,q,s,w,g,k,1g,1e,2c
38,14,10,12,18,1m,4y,58,7o,3pc,r,2v,d,l,l,n,2g,74,fc,q,q,y,g,k,1g,1g,2a
2i,o,k,q,q,u,2q,2y,5a,3s4,4o,26,1r,1,2d,3k,p,21,fu,1u,w,u,q,32,u,14,2
2q,q,s,w,w,12,3o,42,7g,3r6,6e,1h,3v,10,1y,2d,1f,3b,fs,1o,s,q,m,m,2w,i,16
2i,m,k,m,m,u,2k,2s,4o,3pg,28,1u,14,o,l,1z,w,5,fo,1q,y,u,s,u,2w,u,s
36,16,12,14,18,1q,54,56,7s,3qc,15,35,v,l,t,l,3a,70,fc,m,m,12,e,m,1m,1i,2a
2e,s,w,q,s,14,3m,4c,80,3we,o,6z,1g,w,12,1a,6x,47,fq,i,1y,k,m,s,q,1w,18
2y,y,w,y,12,1e,4c,4k,74,3q4,1h,1h,3,b,d,1p,18,5y,fe,10,10,o,g,m,1c,1g,1w
38,14,10,12,18,1k,50,5c,7w,3nw,29,p,e,0,d,1t,i,6q,fg,1i,10,i,i,g,1s,16,1g
2y,12,y,10,16,1i,4k,4s,7g,3qi,1d,2l,7,l,h,x,2g,62,fe,u,y,q,g,k,1i,1c,24
2a,m,g,k,k,q,26,26,3e,3rm,1,3y,3,o,1d,0,32,42,fo,20,y,14,u,1c,2g,12,1e
2g,o,k,q,m,q,20,1y,2y,3sg,1c,2e,3j,18,c,5e,1c,34,fo,26,u,o,u,24,1i,3g,10
2c,o,k,q,q,u,2s,36,5s,3vm,3w,17,2h,14,2l,3e,27,4b,fq,q,24,q,m,36,e,o,i
36,16,10,14,18,1m,52,56,7s,3po,15,2n,j,l,r,n,2w,70,fc,q,m,10,i,i,1i,1g,2c
3a,16,10,14,1a,1o,54,5g,8e,3ns,33,8,8,3,j,1j,6,52,fk,1o,s,g,i,g,1k,20,12
38,14,y,12,16,1i,4q,54,7o,3nk,21,p,6,1,9,7,6,66,fc,12,1c,g,k,e,1g,1a,1u
2m,q,m,q,q,u,2c,2a,3e,3pg,2s,1q,v,13,g,3g,3i,2c,fq,24,w,m,q,1o,1q,10,2e
2s,s,o,q,s,10,2s,2m,46,3lw,48,26,1i,1,3,1,u,g,g6,16,o,g,i,i,u,18,14
38,14,w,10,12,1c,42,4i,7c,3lm,1h,6,e,m,1w,1u,l,a,fi,14,10,g,i,c,1c,1c,1s
38,12,w,10,12,1e,40,4i,7a,3lq,1b,8,g,e,16,20,5,u,fi,12,10,g,g,e,1e,12,24
2o,q,q,w,u,12,3i,3w,70,3qw,6s,3,4r,10,1c,1l,15,37,fs,1o,u,q,o,w,2o,i,12
34,10,w,10,14,1e,4g,52,7w,3nk,1f,u,e,i,s,21,15,2m,fs,20,o,i,g,g,26,1o,16
2q,o,o,q,u,10,36,3g,60,3p8,5w,36,j,1t,17,7,s,1n,fs,1y,k,u,s,1y,24,e,w
2m,o,m,q,s,y,2y,36,5i,3q0,5s,2i,h,19,29,28,k,1p,fu,1w,q,u,s,2q,1a,i,o
2s,y,u,y,10,1c,44,4g,6w,3qc,1d,1n,6,7,7,1v,w,56,fg,10,12,m,k,k,1q,18,1q
2e,o,i,k,m,u,2e,2m,48,3qi,1g,2o,i,q,1n,11,1g,w,fo,1s,y,w,w,1a,2e,w,18
38,14,12,14,1a,1o,52,56,7s,3qg,1x,2v,j,d,r,d,34,6y,fc,q,o,10,g,m,1m,1i,26
30,y,s,w,y,18,3u,48,6u,3m6,b,1a,s,16,g,2z,b,1k,fq,1w,w,i,m,c,2i,18,o
30,y,u,w,10,1a,40,4o,7g,3nu,7,k,1u,18,u,23,1h,w,fu,24,m,k,e,i,2g,1i,1e
2k,s,q,u,q,w,2i,26,3g,3r0,3q,2d,35,14,1i,28,52,2k,fy,1i,q,m,i,c,14,w,o
2u,y,u,w,12,1a,44,4i,6u,3qc,1l,17,4,7,7,1j,2,5g,fc,16,10,o,i,k,1i,1k,1o
2e,o,i,m,m,u,2g,2k,48,3q2,12,2e,q,a,11,d,1u,1c,fo,1u,10,y,u,14,2k,w,u
32,10,u,y,14,1e,4a,4s,7i,3nm,1b,1a,q,x,4,2h,e,3c,fs,22,o,k,i,m,2g,1i,s
2u,w,s,u,y,1a,3y,4a,6o,3q4,13,11,g,1,b,29,0,4s,fe,16,12,o,k,k,1o,1i,1e
2e,m,i,k,m,u,2c,2g,3w,3qm,m,30,e,g,1j,0,2e,2k,fo,1w,10,10,u,1e,2c,y,u
2y,12,w,y,14,1g,4e,4o,78,3qi,2b,1n,1,0,f,15,1k,5w,fc,12,10,o,g,m,1i,1i,1w
34,14,y,12,16,1k,4u,50,7m,3pe,2b,19,f,7,d,1d,2k,6w,fc,w,14,k,i,k,1c,1a,2a
2a,k,e,i,i,o,1y,1y,2y,3rc,q,4q,c,12,1x,m,2s,44,fq,22,u,12,y,1u,26,w,2o
36,16,10,14,1a,1o,54,56,7s,3qc,15,35,t,n,r,l,38,70,fc,m,m,12,e,m,1m,1i,2a
38,16,10,12,18,1m,4w,5a,7q,3og,v,2j,0,f,j,d,1u,78,fc,s,12,k,i,k,1e,1e,2a
38,14,10,12,18,1m,4w,58,7q,3oi,1j,1v,5,b,f,h,1s,78,fa,u,14,k,i,k,1e,1a,2c
34,16,y,12,16,1k,4w,52,7o,3pg,2f,1b,f,7,d,19,2i,6y,fc,w,12,m,g,m,1c,1a,2a
2a,m,e,k,k,q,24,24,3a,3s6,3,48,3,q,21,4,32,4o,fq,20,u,12,y,1k,26,12,16
32,12,y,12,16,1k,4q,4w,7k,3qe,19,2t,f,j,j,11,2s,6i,fa,w,y,o,g,k,1g,1e,28
2q,u,s,s,y,16,3o,42,6c,3pe,b,j,m,6,b,2l,0,3g,fg,1a,12,m,o,m,26,14,1a
2m,o,o,o,q,y,2y,3a,5c,3o4,1q,y,18,8,d,27,e,16,fo,1m,10,s,s,w,2i,w,y
3a,14,y,12,16,1i,4o,54,7q,3my,1d,17,a,5,a,4,a,5e,fc,y,18,g,k,e,1i,12,28
2m,q,s,u,q,w,2i,26,3g,3q8,3o,1j,3p,1c,1g,1o,5g,32,fy,1m,o,q,i,e,1q,m,12
36,16,10,14,18,1o,54,56,7s,3q6,1b,2x,p,l,r,l,32,72,fc,o,m,12,e,k,1m,1g,2c
2q,y,u,u,12,18,40,4c,6q,3q6,1l,r,4,d,1,1t,k,4s,fg,18,y,m,k,k,1m,1i,1i
2w,y,q,u,y,14,30,2y,4k,3m2,2w,2q,i,t,4,k,2g,13,g2,18,i,i,c,i,w,o,26
2u,u,o,y,w,12,2u,2m,4a,3nw,2s,24,2r,11,1a,22,20,18,g0,1g,m,o,i,i,w,1o,10
2i,q,o,u,q,10,36,3m,6o,3su,5u,2n,21,w,23,2o,27,45,fw,1g,16,m,o,2q,q,k,s
34,14,y,12,16,1k,4u,50,7o,3pg,2f,17,h,5,b,1f,2g,6w,fc,y,12,m,g,k,1e,18,2a
36,14,12,12,18,1o,4y,52,7s,3qk,1z,2z,9,9,t,d,2y,6u,fc,s,u,u,g,k,1m,1i,24
34,14,10,12,16,1k,4w,50,7m,3pq,v,2v,h,h,h,13,2s,6u,fc,s,w,s,g,i,1g,1g,28
3q,16,12,14,18,1i,4c,4o,7q,3ko,1f,i,m,1e,1i,3o,2a,22,fm,16,u,g,i,k,1e,16,2i
3m,16,y,14,16,1i,4a,4k,7c,3kq,1d,u,k,1e,18,3e,2a,2i,fm,1e,o,g,g,k,1g,18,2i
3y,1a,12,18,1a,1k,4o,52,8a,3ju,19,m,1a,1s,1e,3i,2o,20,fk,10,10,e,g,m,1k,1c,2q
36,10,u,y,y,1a,3o,3q,5s,3n0,1p,e,w,m,6,1y,28,3w,fi,1k,o,k,i,m,1a,12,24
3c,10,y,y,12,1c,3w,46,6i,3me,1p,o,m,o,y,2w,1o,2q,fk,1i,o,m,g,k,1e,14,2c
2q,u,o,s,u,10,32,3a,52,3mo,10,g,i,a,m,12,a,3c,fq,1g,w,q,m,k,1i,1o,14
34,y,u,w,10,18,3o,3m,5m,3mu,1f,k,o,k,0,1m,2m,46,fg,1m,o,m,g,m,18,12,24
3g,16,y,12,14,1e,44,4e,74,3li,1l,q,k,10,18,3a,20,2g,fm,1g,m,i,i,k,1c,16,2e
2u,u,s,u,w,16,3g,3g,5c,3lo,a,18,0,1,7,12,36,3i,fk,1g,u,k,g,k,10,w,24
3q,1a,10,16,18,1k,4i,4u,7y,3kc,1h,q,m,1o,1e,3o,2c,28,fm,12,y,e,g,k,1g,1a,2k
3a,12,w,10,12,1c,3y,44,6k,3ma,1n,q,k,m,y,2y,1q,2s,fi,1k,o,k,i,k,1c,14,2e
40,1c,12,18,1a,1m,4q,52,88,3jg,r,o,1s,1k,1c,3e,2c,2i,fk,12,10,c,i,o,1k,1o,2g
3q,1a,10,16,18,1k,4i,4s,7w,3k8,1d,s,o,1o,1a,3m,2g,2c,fm,12,y,e,g,m,1g,1a,2m
3i,14,y,12,14,1g,44,4e,70,3li,1n,s,k,14,14,36,26,2m,fk,1i,m,i,i,k,1e,16,2i
3g,14,y,10,14,1e,42,4c,6w,3lq,1n,s,k,12,14,34,20,2m,fk,1i,m,k,g,k,1g,16,2g
38,10,w,y,10,1a,3q,3u,60,3n2,1v,8,12,o,a,2i,1u,3e,fi,1k,o,k,i,m,1a,14,24
3e,12,w,12,12,1e,40,48,6o,3m2,1n,o,k,u,10,30,1w,2s,fk,1i,m,m,g,k,1e,16,2e
3y,1c,12,16,1a,1m,4o,52,8a,3jo,z,i,1k,1q,1e,3g,2o,20,fk,10,10,e,g,o,1k,1c,2q
3y,1a,12,16,1a,1m,4q,52,8a,3je,v,18,1m,1i,14,38,1i,3g,fo,18,u,c,g,o,1k,2a,1u
3s,18,12,14,1a,1k,4i,4u,7y,3k6,1d,s,q,1q,18,3m,2i,2c,fm,12,y,e,g,m,1g,1a,2m
32,y,u,w,10,18,3m,3o,5m,3mk,15,q,g,g,2,1m,2g,44,fg,1k,q,m,g,m,16,12,22
3u,1a,12,16,1a,1m,4m,50,86,3ju,19,q,14,1s,18,3k,2o,26,fk,10,10,e,g,m,1k,1a,2q
32,y,s,w,y,18,3k,3u,5w,3mm,t,12,k,2,i,2c,q,30,fk,1k,s,m,i,m,16,12,28
40,1c,12,18,1a,1m,4s,54,8a,3je,n,y,1u,1c,1a,38,1o,3g,fm,10,10,c,k,o,1k,2c,1u
3g,14,y,10,14,1e,44,4e,7a,3lm,15,u,10,1a,10,2y,10,4a,fu,1k,m,i,e,m,1g,2c,1c
40,1a,14,16,1a,1o,4q,54,8a,3ji,x,o,1q,1m,1c,3c,2g,2g,fk,10,10,c,i,o,1k,1k,2m
3s,18,12,16,18,1i,4i,4s,7y,3kg,1j,k,m,1k,1m,3q,2a,20,fk,14,y,e,g,k,1g,18,2k
36,10,w,w,12,1a,3q,3y,6a,3mm,1d,q,o,c,s,2m,18,2u,fi,1m,o,m,i,m,1a,14,2a
3a,10,w,y,12,1a,3s,42,6e,3mk,1j,m,o,i,10,2s,1e,2o,fk,1k,o,k,k,k,1a,16,2a
3u,1a,12,16,18,1k,4k,4y,84,3k8,1h,k,s,1s,1i,3q,2g,1w,fk,10,10,e,g,m,1g,1a,2m
3q,18,12,14,1a,1i,4i,4u,7s,3k8,1d,s,q,1m,1a,3m,2g,2c,fm,14,w,e,g,m,1g,1a,2m
3q,18,10,12,18,1k,4i,4w,80,3k2,x,w,1g,1m,u,36,1e,3g,fs,18,s,e,g,m,1g,1y,1y
2m,s,o,q,s,y,2u,32,4q,3n0,1c,0,u,g,u,o,2,3c,fs,1e,y,q,o,k,1o,1w,s
2u,w,q,u,u,14,3a,3i,5e,3mi,e,w,a,6,k,1q,8,3a,fo,1g,w,o,k,m,12,1o,1k
3u,1a,12,16,1a,1m,4m,4y,84,3jy,1b,s,y,1s,1a,3k,2o,26,fk,10,10,e,g,m,1i,1c,2o
3y,1a,12,16,1a,1m,4q,54,8a,3jc,p,w,1m,1m,16,38,2c,2k,fm,10,10,e,g,o,1k,1k,2k
3o,16,10,14,16,1i,4a,4k,7i,3ku,1f,k,m,18,1e,3o,2a,22,fm,1a,s,g,g,k,1e,16,2g
3w,1a,12,16,1a,1m,4o,50,86,3jq,15,s,16,1s,18,3i,2q,26,fk,10,10,e,g,o,1i,1c,2q
30,y,u,w,y,18,3m,3g,5g,3mk,z,o,c,g,1,1a,3m,3y,fg,1k,q,m,g,m,14,12,24
3u,1c,12,16,18,1m,4m,4y,88,3k4,1h,o,u,1u,1g,3q,2g,20,fk,10,10,e,g,m,1g,1c,2o
3u,18,12,14,18,1m,4m,50,86,3jm,11,16,1k,1k,12,36,1m,36,fq,1g,m,e,e,o,1g,20,20
3s,18,12,14,18,1m,4k,50,86,3jq,13,16,1i,1k,10,36,1q,32,fq,1g,o,c,e,o,1g,1u,24
3y,1a,12,18,1a,1k,4o,52,8a,3ju,19,m,18,1u,1e,3k,2m,1y,fk,10,10,e,g,m,1k,1a,2q
3a,10,w,y,10,1c,3s,42,6e,3mi,1h,m,q,i,y,2s,1c,2q,fk,1k,o,k,k,k,1a,14,2c
38,12,u,y,12,1a,3s,42,6c,3mg,1f,q,o,i,u,2s,1a,2o,fk,1k,o,m,i,k,1a,16,2a
3y,1a,12,16,1a,1m,4s,52,8a,3jc,r,16,1o,1g,14,38,1k,3g,fo,16,w,c,g,o,1k,2c,1s
3g,14,y,10,14,1e,44,4g,7c,3lm,13,s,y,1a,10,2y,18,3u,fu,1m,k,i,g,m,1c,22,1o
3i,14,y,12,14,1g,44,4e,70,3lg,1l,s,k,16,12,38,26,2k,fk,1i,m,i,i,k,1e,16,2i
3g,14,w,12,14,1e,42,4a,6u,3lo,1j,s,k,12,12,34,22,2k,fk,1i,m,k,i,k,1e,16,2g
3q,18,12,16,18,1i,4g,4s,7w,3kk,1l,k,k,1i,1m,3q,28,22,fm,12,y,e,g,k,1e,18,2k
3a,10,w,y,12,1c,3u,42,6e,3mg,1j,o,o,k,w,2s,1i,2o,fk,1k,o,k,i,k,1c,16,2a
36,10,w,y,10,1a,3o,3s,5s,3n6,1x,c,14,m,6,24,1y,3y,fi,1m,o,i,i,m,1a,14,24
40,1a,14,16,1a,1o,4q,54,8c,3jg,v,q,1q,1k,1a,3c,2i,2e,fk,10,10,c,i,o,1k,1i,2o
3y,1c,12,16,1a,1o,4q,54,8a,3jc,p,10,1q,1g,16,3a,1o,3c,fm,10,10,c,i,o,1k,28,1y
3k,16,y,14,16,1g,48,4k,7c,3l4,1l,s,i,18,18,3g,24,2e,fm,1e,o,g,i,i,1e,16,2i
3m,18,10,12,16,1i,4g,4s,7w,3kg,15,y,1c,1i,y,34,1s,2y,fu,1g,m,g,e,m,1e,1i,2a
3u,1a,12,16,1a,1m,4m,50,86,3ju,17,q,12,1s,18,3k,2o,28,fk,10,10,e,g,m,1k,1a,2q
3y,1c,12,18,1a,1m,4o,52,8a,3jm,z,k,1k,1s,1c,3g,2o,22,fk,10,10,e,g,o,1k,1c,2q
30,y,s,u,10,16,3i,3s,5u,3mk,l,14,g,2,i,2a,k,36,fk,1k,s,o,i,m,14,18,22
3i,14,y,12,14,1g,44,4e,70,3li,1n,s,k,14,14,36,26,2m,fk,1i,m,i,i,k,1e,16,2g
2w,u,q,u,w,14,3a,3i,5g,3mi,a,10,a,6,i,1s,2,3g,fo,1g,w,o,k,k,14,1s,1g
2m,s,o,q,s,y,2u,30,4q,3n4,16,4,u,e,w,i,8,3e,fq,1g,y,q,o,k,1w,1q,s
36,y,w,w,10,1a,3o,3y,64,3mm,19,u,o,a,o,2k,12,2w,fi,1m,q,m,i,k,1a,14,28
3k,14,10,12,16,1g,46,4i,7a,3la,1l,k,m,10,14,3m,28,24,fm,1e,o,g,i,k,1c,14,2e
32,10,u,w,10,18,3m,3u,5u,3mi,17,s,i,e,a,28,1e,3q,fi,1k,q,k,g,m,16,12,24
40,1c,12,18,1a,1m,4s,54,88,3jg,r,q,1s,1k,1a,3c,26,2s,fk,12,y,e,i,o,1k,1u,2c
38,12,w,y,10,1a,3q,3w,64,3n2,1v,6,12,o,c,2o,1u,34,fi,1k,o,k,i,m,1a,12,28
3q,1a,10,16,18,1k,4i,4u,80,3kg,1n,q,i,1m,1g,3s,2c,22,fk,14,w,g,g,k,1e,1a,2k
3c,12,w,10,14,1c,3w,48,6q,3mg,1x,i,k,o,o,3c,1o,2g,fk,1i,o,i,i,k,18,12,2a
3u,1a,12,16,18,1k,4k,4w,82,3k8,1f,k,s,1s,1k,3o,2c,20,fk,12,10,e,g,m,1e,1a,2m
30,w,s,u,y,18,3g,3o,5q,3mo,h,12,c,4,k,2a,g,2y,fk,1k,s,o,i,m,14,1a,20
40,1c,12,18,1a,1o,4q,54,8a,3je,n,12,1s,1a,1a,3a,1o,3g,fm,10,10,e,i,o,1k,2c,1w
3c,14,w,10,14,1c,3y,46,6q,3mc,1t,e,q,q,m,3g,1s,2a,fk,1i,m,k,i,k,18,14,2a
3c,10,y,y,12,1c,3w,44,6k,3ma,1h,k,o,q,12,2w,1m,2k,fk,1k,m,m,i,k,1c,14,2c
3a,10,w,y,10,1c,3s,40,6e,3mi,1h,m,q,k,w,2u,1c,2m,fk,1k,o,k,k,k,1a,14,2a
3q,18,10,16,16,1k,4c,4q,7s,3km,1h,k,m,1g,1i,3q,28,22,fm,16,u,g,g,k,1e,18,2k
3u,1c,12,16,1a,1k,4m,50,88,3k6,1j,m,s,1w,1g,3u,2g,1u,fk,10,10,e,g,m,1g,1a,2o
3g,12,y,12,14,1e,44,4c,6w,3lq,1p,q,i,10,14,34,24,2o,fk,1i,m,i,i,k,1e,16,2g
3i,14,y,10,16,1e,44,4c,6y,3lg,1h,s,k,14,16,34,24,2k,fk,1i,m,k,g,k,1e,16,2i
3g,14,y,12,14,1e,40,48,6w,3ly,1p,c,w,u,y,3g,24,24,fk,1i,m,i,k,k,1c,12,2c
3e,16,y,12,16,1g,4e,4w,7y,3lm,1v,0,1e,1y,1q,1q,1s,3a,fi,y,12,m,e,k,1o,1e,22
2o,q,o,o,q,w,2k,2q,48,3n8,2s,0,10,g,s,2i,2e,4m,g6,1q,12,u,q,w,26,18,1a
3y,1c,12,18,1a,1m,4s,54,8a,3j4,n,u,1o,1u,18,36,2k,2e,fg,12,16,g,i,m,1e,1i,2o
3i,16,y,12,16,1g,4a,4m,7q,3la,1b,q,14,1c,14,2y,1c,3a,fu,1m,k,g,g,k,1c,1q,20
2q,u,s,s,w,16,3o,44,6c,3ou,n,0,g,6,4,23,f,3k,fk,1c,y,o,m,m,1w,1c,1a
2a,o,g,k,k,q,22,22,32,3qy,1,3c,9,12,1j,24,3e,54,fu,22,10,12,w,1s,1w,12,1c
2e,m,i,m,m,s,2a,2e,3s,3qc,w,2o,g,i,19,s,24,2k,fs,1w,10,10,w,1e,2a,y,12
3u,1a,10,16,18,1m,4o,50,88,3ji,11,18,1m,1i,12,38,1o,34,fq,1g,m,e,e,o,1g,20,22
3i,18,10,14,16,1i,4m,50,88,3ki,1b,0,1c,26,1s,2g,1y,2q,fi,w,14,k,g,i,1o,18,2a
34,y,s,u,y,14,3g,3m,5w,3mu,7,14,o,q,1e,10,2g,4q,fy,1s,q,m,i,k,2g,1g,12
22,k,e,g,g,k,1m,1i,24,3qi,2,2i,2m,j,k,2u,3m,6o,fs,2a,1c,10,w,1y,20,1q,24
42,1a,14,16,1a,1o,4s,54,88,3ja,l,1c,1q,18,16,3a,1q,3i,fk,18,y,c,k,m,1k,2c,1w
34,y,u,u,y,18,3g,3q,64,3mo,9,12,o,q,1a,12,2e,4o,fy,1s,q,m,g,k,2e,1g,12
3y,1a,14,16,1a,1m,4s,54,88,3ja,t,1e,1o,1e,12,3a,1m,3g,fm,1e,q,e,g,o,1i,2c,1u
2e,o,i,k,o,s,26,28,3a,3ps,1m,22,1,g,1f,2u,3i,5e,g6,1y,12,y,w,1q,1s,y,1c
42,1a,14,16,1c,1m,4s,52,88,3jc,j,w,1u,1c,1c,3a,1q,3c,fm,12,y,e,i,o,1m,2a,1w
3y,1c,12,16,1c,1m,4u,54,8a,3j8,v,y,1w,1u,12,34,2m,2g,fi,10,16,e,k,o,1e,1c,2u
36,10,w,w,10,1a,3m,3q,5o,3n2,1j,6,y,q,8,1w,24,44,fk,1q,m,i,i,k,1a,12,26
2k,o,m,m,o,u,2g,2i,3s,3o8,2k,o,s,2,9,2o,32,5c,g4,1u,14,u,u,1c,1w,12,14
2i,o,k,k,q,u,2e,2i,40,3ou,2i,1a,10,7,15,1s,2w,38,g0,1u,12,w,w,1c,24,y,y
2u,w,s,u,10,16,3i,3g,5e,3li,a,1g,5,1,3,12,3g,3m,fo,1o,q,i,c,i,10,y,24
32,y,u,w,10,18,3o,3m,5m,3mg,v,o,a,m,4,1k,2i,46,fk,1s,o,i,e,i,16,12,24
32,y,u,w,y,18,3k,3g,5e,3mm,x,e,i,m,0,1c,3m,3y,fi,1q,o,i,g,m,16,10,24
20,i,e,e,g,i,1i,1e,20,3qu,14,1w,3o,n,2,34,2m,70,fm,28,1i,10,w,20,22,2y,1e
2o,u,s,s,y,16,3a,3c,5g,3lg,q,k,e,5,j,1w,3i,30,fo,1k,q,g,c,i,y,u,1c
3u,1a,12,14,1a,1m,4o,52,88,3jc,v,16,1o,1i,12,36,22,2s,fo,1c,u,c,g,m,1g,1o,2e
40,1a,12,18,1a,1m,4s,54,88,3jc,p,1a,1o,1c,14,3a,1m,3i,fm,18,w,c,i,o,1i,2c,1u
42,1a,14,16,1a,1o,4o,52,88,3jc,r,y,1u,1g,1c,3c,1q,38,fk,1e,s,e,g,o,1i,2c,1u
38,12,u,y,12,1a,3y,4k,78,3m2,1h,m,1a,1o,1o,14,6,3m,fi,1a,y,o,e,m,1k,1u,1k
40,1a,12,16,1c,1m,4s,54,8a,3ja,l,18,1o,1e,12,38,1m,3k,fk,12,14,e,i,m,1i,2c,1u
3s,1a,10,14,1a,1k,4o,50,88,3jg,11,16,1o,1m,10,34,2a,2i,fo,1i,q,c,g,k,1e,1g,2k
24,m,e,i,i,m,1s,1o,2i,3rk,v,3q,16,0,7,1u,3g,60,fq,2a,12,16,u,1w,22,1a,2i
3a,16,10,12,14,1i,4s,54,7s,3ow,2r,1v,12,1k,12,1f,1q,68,fe,w,12,m,g,k,1m,1i,24
42,1a,14,16,1c,1m,4s,54,88,3ja,h,18,1q,18,16,3c,1q,3i,fk,14,12,e,i,m,1k,2c,1w
42,1a,14,16,1c,1m,4s,54,88,3ja,p,1e,1s,16,18,3c,1q,3g,fm,1c,s,c,i,o,1k,2c,1w
2o,q,m,o,s,y,2u,36,54,3nm,24,q,1e,8,6,13,u,1c,fq,1o,14,s,s,w,2e,y,12
3o,16,12,12,18,1i,4i,4w,84,3k6,1b,12,1g,1o,w,32,20,2k,fs,1k,m,e,e,k,1c,1a,2m
3k,16,12,12,18,1i,4i,4u,84,3kg,1r,e,1g,1y,1k,30,1y,2a,fo,1a,w,g,g,k,1g,14,2g
3i,16,10,14,16,1g,4g,4u,80,3kq,1x,k,1c,1y,1i,2w,1u,2e,fo,1g,s,g,i,i,1e,12,2i
2o,q,o,o,q,w,2k,2q,4a,3n6,2w,0,y,e,q,2c,2m,4q,g4,1q,14,u,q,w,26,16,1c
32,y,s,w,y,16,3w,4a,6s,3mk,11,12,10,1a,1e,1j,1,42,fi,1i,w,q,g,m,1q,1q,1c
3g,16,y,10,14,1g,46,4i,7i,3lm,19,q,10,1a,12,2y,1c,3g,fw,1k,m,g,g,k,1c,1u,1u
42,1a,14,16,1c,1m,4s,52,88,3jc,j,w,1w,1a,1c,3c,1o,3e,fm,12,y,e,i,o,1m,2a,1w
38,10,w,y,12,1c,3u,44,6q,3m4,1n,18,m,o,u,36,18,28,fk,1s,o,m,i,k,12,10,26
3u,1a,12,14,1a,1m,4q,52,88,3j4,n,a,1u,1w,1i,34,2m,2e,fi,y,18,e,i,o,1i,16,2m
34,y,u,y,10,18,3o,3s,5s,3mg,11,o,e,k,8,22,1s,40,fk,1s,o,i,g,i,16,12,24
2y,w,s,w,y,18,3k,3k,5i,3lu,3,1e,3,4,4,1c,2i,3y,fm,1q,s,i,e,g,12,y,24
2a,m,g,k,k,s,20,22,30,3qw,k,3g,h,g,15,20,3m,5w,g4,24,10,12,10,1o,1y,16,12
3w,1a,12,14,1a,1m,4q,52,8a,3ja,r,16,1o,1g,12,38,1s,34,fo,1a,u,e,g,m,1g,20,24
3u,18,12,14,1a,1k,4o,50,88,3jg,z,16,1m,1i,14,38,1y,2s,fq,1e,q,e,e,m,1g,1m,2e
36,12,y,y,12,1c,3w,4a,72,3ma,1v,q,u,1c,1a,2w,1q,38,fo,1o,q,o,g,k,1c,1i,1o
3a,10,w,y,10,1c,3u,46,6w,3lw,v,14,w,12,y,2q,16,3s,fw,1q,q,i,g,i,1a,1w,1m
42,1a,14,16,1c,1m,4s,54,88,3ja,n,1c,1s,18,16,3c,1q,3g,fm,1c,s,c,i,o,1k,2c,1w
3k,18,10,12,18,1i,4o,54,88,3k4,13,4,1w,26,1s,1o,1i,3e,fg,u,16,i,g,k,1q,1c,2c
32,y,s,u,y,16,3e,3o,5y,3me,6,12,s,q,14,1u,1u,4g,fy,1s,s,o,g,k,1y,1o,18
2u,s,q,q,u,12,32,3k,5o,3mw,10,14,14,o,u,5,a,1m,fo,1o,10,q,m,q,2a,18,16
36,12,y,y,12,1c,4e,4s,7e,3ni,23,9,w,1a,1q,1n,q,5m,fg,16,y,o,g,k,1i,1s,1o
30,y,s,u,w,16,3c,3m,5y,3m6,8,18,g,w,1e,1g,24,4c,fw,1s,u,o,k,m,1y,1e,18
3u,18,12,14,1a,1k,4o,50,88,3jk,11,18,1k,1i,12,36,1s,30,fq,1g,o,c,e,o,1g,1w,24
2i,o,k,m,o,u,2i,2o,4a,3ou,2a,1e,w,8,13,m,2a,2e,fy,1s,14,u,w,1a,28,u,y
2u,u,o,s,u,10,2y,34,52,3mi,1g,10,i,i,14,1w,2g,4i,g0,1q,10,s,k,q,26,1a,14
3i,14,10,10,16,1g,4a,4o,7q,3li,1f,o,12,1c,14,2w,10,3i,fu,1m,m,g,e,k,1c,1w,1s
36,14,12,14,18,1o,50,54,7s,3qe,1p,2x,j,f,n,f,32,6u,fc,q,m,12,g,m,1m,1g,28
36,14,12,14,18,1o,4y,54,7s,3qg,1z,2v,d,3,x,b,2y,6u,fc,s,q,y,g,k,1m,1i,26
40,1a,14,16,1c,1m,4s,54,88,3ja,j,18,1q,1a,14,38,1q,3i,fk,12,14,e,i,m,1i,2c,1w
3m,18,10,14,16,1k,4i,4y,84,3ju,1j,y,1o,1u,1a,2w,20,2g,fq,1g,o,g,i,k,18,18,2m
34,w,s,u,y,16,3e,3k,5y,3mm,2,12,o,i,1a,y,2o,4m,fy,1u,q,m,i,k,2i,1a,10
3k,14,10,12,14,1g,44,4e,76,3l6,1b,k,m,14,16,3g,26,28,fo,1m,m,g,g,i,1c,12,2g
3g,18,10,12,14,1i,4o,50,80,3le,1x,1,1s,1w,1s,y,1q,40,fg,u,14,k,g,k,1q,1c,2a
3y,1c,12,18,1a,1o,4s,54,8a,3j8,p,16,1s,1k,12,36,1q,3e,fi,12,14,g,i,o,1e,28,20
2g,o,k,m,o,u,2g,2m,44,3p0,26,1o,q,8,17,u,2k,2u,fy,1u,12,w,w,1e,24,u,y
3w,1a,12,14,1a,1m,4o,52,8a,3je,11,1c,1m,1i,14,36,1m,3a,fo,1g,o,c,g,o,1g,26,1y
38,12,w,y,12,1c,40,48,74,3m8,1x,s,s,1e,1a,2u,1s,3c,fo,1o,q,m,g,k,1e,1g,1q
30,w,u,w,y,18,3k,3g,5e,3m8,l,s,0,k,1,1e,3o,3u,fi,1q,o,k,e,k,14,10,24
40,1c,12,18,1a,1o,4q,54,88,3ja,r,1g,1q,18,16,3c,1o,3i,fm,1e,q,c,i,o,1k,2c,1u
3o,18,10,14,18,1k,4k,4y,84,3ju,19,16,1k,1o,y,30,24,2k,fq,1k,o,e,e,k,1c,1a,2m
3o,18,10,12,18,1k,4i,4y,82,3k2,19,12,1i,1m,y,32,22,2k,fs,1i,o,e,e,k,1c,1a,2m
40,1a,12,16,1c,1m,4s,52,8a,3ja,r,1e,1o,1c,14,38,1o,3g,fm,1c,s,c,i,o,1i,2c,1u
3q,1a,10,14,1a,1k,4m,4y,88,3jo,13,14,1k,1m,10,36,24,2k,fq,1g,q,e,e,k,1e,1g,2k
3i,16,10,12,16,1i,4e,4u,7y,3kw,21,i,1c,1y,1g,2y,1u,2e,fo,1g,s,g,i,i,1e,14,2g
3o,18,10,14,16,1k,4k,4y,86,3jq,1b,y,1q,1s,12,2y,22,2i,fq,1e,s,e,g,k,18,1c,2m
20,i,e,e,g,k,1i,1e,20,3qg,12,1w,3e,l,a,36,2u,7e,fo,28,1i,10,w,22,1y,2w,1e
3a,16,12,12,16,1m,4y,56,7u,3py,33,29,m,1g,p,p,2a,6q,fe,s,12,m,i,k,1m,1i,26
34,16,10,12,16,1m,4s,52,7o,3qk,2r,29,7,i,t,j,2m,6k,fc,w,y,q,e,m,1k,1g,24
38,14,y,10,14,1e,46,4i,7g,3m4,1z,e,s,1a,1o,2i,22,3c,fm,1c,w,o,g,i,1g,1e,1s
22,g,g,c,i,i,1k,1g,22,3r0,s,2a,3i,r,6,2o,2o,6s,fm,28,1g,10,w,20,20,2o,1o
24,i,g,e,k,k,1q,1m,2e,3rk,b,3e,28,n,2,1m,32,66,fo,28,16,16,u,1y,22,1o,2e
2u,u,q,s,w,14,3m,40,66,3ms,k,u,m,12,o,3j,0,3q,fo,1i,y,m,m,m,2c,16,16
36,12,u,y,12,1a,3q,3y,66,3mk,1f,k,g,o,e,2w,1g,34,fk,1s,o,i,g,i,16,12,26
40,1a,14,16,1a,1m,4q,52,88,3jg,p,k,1q,1m,1e,3e,2g,2c,fk,12,10,c,i,o,1k,1k,2k
30,y,s,u,w,14,3c,3m,5u,3m8,8,16,q,10,18,1e,1q,4o,fy,1s,s,q,k,k,1w,1o,12
2w,y,u,w,10,1a,44,4e,6u,3p0,1r,0,1,g,6,23,s,54,fg,1c,w,o,i,m,1m,1k,1i
42,1a,14,18,1a,1m,4s,52,88,3jc,l,y,1w,1a,1c,3c,1q,3c,fk,16,y,c,k,o,1k,2c,1u
3y,1a,14,16,1a,1o,4q,54,88,3j8,p,1c,1m,1g,12,38,1m,3i,fm,18,w,e,g,o,1i,2c,1u
3y,1a,14,16,1a,1m,4s,52,8a,3jc,p,18,1m,1e,16,38,1m,3g,fm,18,w,c,g,q,1i,2c,1u
40,1c,12,18,1a,1o,4q,54,88,3j8,j,1a,1q,18,16,3c,1o,3i,fm,16,y,e,g,o,1k,2c,1u
42,1a,14,16,1c,1m,4s,54,88,3ja,j,1a,1q,18,16,3c,1q,3g,fk,16,10,c,k,m,1k,2c,1w
30,y,s,w,10,18,3k,3i,5e,3m8,l,u,2,k,0,18,3k,3w,fk,1q,o,i,e,k,14,10,26
2g,m,k,m,m,s,2a,2e,3s,3pm,1k,1q,q,e,1h,24,2w,2s,fs,1y,10,10,w,1o,1u,10,w
2i,q,m,o,s,y,2w,3a,5e,3oo,w,y,12,4,5,1h,3,c,fo,1m,10,q,s,q,2m,y,w
3q,18,10,14,18,1k,4m,4y,86,3jq,17,14,1o,1m,y,32,28,2g,fq,1i,o,g,e,k,1c,1a,2o
1y,e,e,e,c,i,1a,16,1q,3pk,2s,e,3o,1m,r,4s,3o,7k,fk,2a,1k,12,w,22,22,38,1g
3o,1a,12,14,18,1m,4q,52,8a,3jg,z,4,1g,24,1s,30,2c,2q,fk,w,16,g,i,k,1m,14,2i
40,1a,14,16,1c,1m,4s,54,88,3ja,r,1g,1o,1a,16,3a,1o,3i,fm,1c,s,c,i,o,1k,2c,1u
40,1a,12,18,1a,1o,4s,54,88,3ja,n,18,1s,1e,12,36,1m,3k,fk,12,14,e,i,o,1e,2c,1w
20,i,e,e,g,k,1g,1e,1y,3pw,1c,1i,3e,f,c,3k,3g,7e,fq,2a,1i,10,u,22,1y,2m,1i
3g,18,y,12,16,1g,4e,4q,7w,3ks,1t,q,1e,1s,1c,2u,1s,2i,fq,1k,o,i,g,k,18,14,2i
40,1c,12,18,1a,1o,4s,54,88,3ja,h,18,1o,1a,16,3a,1q,3i,fk,14,12,e,i,m,1k,2c,1w
3c,16,12,12,14,1k,4y,56,7w,3p6,2z,2b,1e,1q,8,11,1y,6g,fe,u,12,m,g,k,1m,1k,26
3i,14,10,12,14,1i,4c,4o,7u,3ko,1l,w,1e,1q,14,2u,1i,2w,fs,1k,o,g,i,i,16,1g,2a
3q,1a,12,14,1a,1k,4m,4y,86,3jg,z,i,1e,1s,1k,3e,2a,24,fk,12,14,g,g,o,1a,16,2o
3u,1a,14,14,1a,1m,4q,52,8a,3j6,r,g,1q,1y,1e,36,2m,26,fi,10,16,g,g,q,1e,18,2o
3a,12,y,10,14,1c,48,4o,7k,3m4,1x,8,14,1m,1u,1e,1g,3q,fi,18,w,o,e,k,1m,1m,1q
3k,16,y,12,16,1i,4e,4s,7w,3kg,1d,12,1g,1m,u,2w,1s,2u,fs,1k,o,g,g,i,18,1g,2c
3q,18,12,14,1a,1k,4k,4w,84,3jm,17,o,1a,1s,1g,3g,26,26,fk,18,y,g,i,m,18,16,2m
3e,14,w,10,12,1c,3u,40,6c,3mw,1v,8,1c,m,w,2u,1s,2o,fo,1k,m,i,k,k,1g,12,2a
2s,u,q,q,u,12,3g,3q,60,3my,12,w,12,m,s,3l,g,2o,fm,1m,y,q,o,m,2g,12,16
3m,1a,10,14,18,1i,4o,50,88,3ju,z,6,1c,2a,1s,2u,1y,2o,fi,y,16,i,g,k,1m,16,2e
32,y,s,u,w,16,3g,3m,60,3me,3,18,u,u,12,22,18,4k,fy,1s,s,o,i,i,1m,22,12
3i,18,10,14,16,1k,4g,4w,82,3ko,1v,a,1a,20,1m,30,22,2e,fm,18,y,i,g,k,1g,14,2e
3g,16,10,12,16,1g,4e,4q,7w,3kw,1x,o,1e,1u,1c,2u,1s,2g,fq,1k,o,i,g,k,18,14,2i
2i,o,k,m,q,w,2o,30,4w,3ow,1g,1g,u,g,r,15,o,w,fs,1q,10,u,s,y,2k,w,u
36,10,w,w,10,18,3o,3w,6i,3mu,r,u,o,w,1a,1q,1m,4i,fy,1q,o,k,i,k,1y,1s,14
40,1a,14,16,1c,1m,4s,54,88,3ja,j,18,1q,1a,14,3a,1o,3i,fk,12,14,e,i,m,1i,2c,1w
34,10,w,y,10,1a,3w,46,6w,3mc,1h,o,q,w,1o,2a,1s,3q,fm,1m,q,q,g,k,1g,1q,1c
3o,18,10,14,16,1k,4k,4y,84,3ju,1b,12,1m,1q,y,30,22,2k,fq,1g,q,g,e,k,1a,1a,2m
2k,q,m,m,q,u,2i,2i,3y,3o0,2m,m,u,0,b,2m,34,5a,g4,1s,14,u,s,1a,20,10,14
2w,w,q,s,w,14,3a,3g,5o,3mg,o,u,u,i,q,1w,2q,3s,fs,1q,w,s,k,q,1u,1e,16
38,14,w,10,12,1e,42,4c,7a,3m8,25,o,u,1i,1c,2u,1u,34,fo,1m,q,m,g,k,1e,1c,1u
2o,s,o,q,s,12,38,3k,5s,3nm,1c,m,12,c,0,2l,0,1o,fq,1k,y,q,q,m,2i,12,10
2g,m,k,m,m,u,2g,2i,44,3pi,1m,24,i,g,1l,w,2i,2e,fy,1u,12,w,w,1m,1y,w,u
3w,1c,12,16,1c,1m,4s,54,8a,3j4,r,m,1u,20,18,34,2m,2a,fg,12,16,e,i,q,1e,1a,2s
40,1c,12,16,1c,1m,4s,54,88,3ja,t,1i,1q,18,16,3a,1q,3g,fm,1e,q,c,i,o,1k,2c,1u
3g,18,10,10,16,1i,4s,54,7y,3mk,29,t,1y,1q,1a,1,1o,56,fg,s,14,k,i,k,1o,1g,2a
2w,w,s,w,y,16,3g,3e,5c,3lo,3,16,9,6,9,1w,3o,3k,fk,1m,q,g,g,k,12,y,20
2g,o,k,m,o,u,2k,2s,4k,3pg,16,22,i,i,17,j,1i,1c,fs,1s,12,u,u,18,2e,u,u
3y,1c,12,16,1a,1m,4s,54,88,3jc,p,18,1o,1c,16,38,1m,3g,fm,18,w,c,i,o,1i,2c,1u
3a,12,w,y,12,1c,3u,46,6w,3mk,13,s,q,10,16,2g,1a,42,fw,1q,m,i,i,k,1k,20,1c
3m,18,12,12,18,1k,4m,50,86,3jw,z,4,1e,28,1s,2u,1y,2o,fi,y,16,i,g,k,1m,16,2c
42,1a,14,16,1c,1m,4s,54,88,3ja,h,18,1q,18,16,3c,1q,3i,fk,14,12,e,i,m,1k,2c,1w
3s,1a,12,14,1a,1m,4s,54,8a,3j8,p,4,1s,22,1m,2y,2e,2u,fi,w,18,g,g,o,1m,14,2k
3m,18,12,14,18,1k,4k,50,86,3jw,11,4,1e,20,1q,30,22,2s,fk,w,18,g,i,k,1k,14,2e
2q,w,q,u,y,16,3a,3c,5e,3le,i,y,1,1,h,24,3i,34,fm,1k,q,g,e,i,10,w,1i
3m,1a,10,14,18,1k,4k,50,88,3jm,1p,y,1s,1w,1g,32,20,2a,fo,1e,q,g,g,m,1c,14,2o
2o,w,q,s,y,14,3m,42,6c,3ow,f,7,m,4,4,2b,3,38,fk,1c,10,m,o,k,24,14,1c
2c,m,i,k,k,q,20,20,2w,3qm,7,2y,7,18,19,2u,30,62,fu,26,10,10,w,1o,1w,14,18
3q,1a,12,14,1a,1k,4k,4y,86,3ji,13,m,1c,1s,1k,3c,2a,24,fk,14,12,g,g,o,1a,16,2m
2q,s,o,q,s,12,36,3k,5m,3mw,20,i,18,m,g,2l,c,20,fs,1m,10,o,q,o,2e,14,14
2i,o,m,m,o,s,2e,2i,3u,3ou,1y,w,12,8,l,20,28,48,fu,1u,14,w,u,1a,26,10,y
40,1a,14,16,1a,1o,4s,54,88,3ja,j,18,1o,1c,12,3a,1o,3i,fk,12,16,c,i,m,1i,2c,1w
30,w,u,w,y,18,3g,3e,5c,3mo,11,k,i,8,3,1k,40,3q,fi,1k,q,m,g,k,16,y,22
36,10,w,w,10,1a,3m,3o,5k,3nk,25,a,1c,k,8,1q,2e,44,fk,1m,o,k,i,m,1a,14,24
2u,u,s,u,y,16,3c,3c,5c,3lm,2,12,1,4,b,22,3q,2w,fk,1i,s,i,g,k,10,w,1w
2u,w,s,u,y,16,3c,3c,5c,3lq,3,12,2,2,b,20,3s,32,fk,1g,s,k,g,k,10,y,1w
38,10,w,y,10,1a,3o,3o,5q,3nk,25,8,1e,k,c,20,22,3u,fm,1m,m,k,k,k,1c,12,26
36,10,w,w,10,1a,3m,3m,5k,3ni,21,a,1a,k,6,1q,2i,44,fk,1o,m,k,i,m,1a,14,24
2i,s,o,s,u,10,32,32,56,3n6,i,7,q,5,h,1g,32,2w,fk,1q,o,i,g,g,y,q,w
28,q,k,o,o,w,2i,2k,46,3pa,1l,20,2,0,5,14,1o,42,fc,26,k,o,k,i,y,18,q
38,10,w,w,10,18,3o,3o,5o,3ng,23,c,1a,i,a,1u,2a,42,fk,1k,o,k,k,k,1c,12,26
30,y,s,w,y,18,3i,3e,5a,3mo,11,k,k,8,3,1i,40,3q,fi,1k,q,m,g,k,16,10,22
2m,u,q,u,u,16,36,38,5c,3lw,o,a,g,1,h,1w,3g,2w,fm,1k,q,g,g,i,y,s,10
2q,u,q,u,w,16,3a,3e,5e,3lc,g,w,c,0,j,1s,3g,3a,fk,1g,s,i,g,i,10,u,16
28,q,k,m,q,w,2q,2u,4q,3os,2b,2c,k,c,b,2,22,34,fa,24,m,q,i,g,s,k,w
2o,s,s,s,w,14,36,38,5c,3m0,s,6,a,4,h,1y,3c,2o,fm,1i,s,g,g,i,10,s,1a
34,10,u,y,y,1a,3m,3m,5k,3n4,1p,e,y,k,2,1o,2m,48,fi,1k,q,k,i,m,18,12,24
2y,y,s,w,y,18,3g,3c,5c,3mg,p,k,c,8,5,1o,3y,3o,fi,1m,q,k,g,k,14,y,22
34,10,w,w,y,1a,3m,3m,5i,3na,1t,c,12,i,4,1m,2q,46,fi,1m,o,k,i,m,1a,12,24
2o,u,s,u,u,16,38,3a,5c,3lq,k,k,8,6,h,1y,3g,2q,fk,1i,s,i,g,i,y,w,1c
30,y,u,u,10,16,3k,3e,5c,3mq,13,k,i,a,3,1i,3y,3s,fg,1m,q,k,i,k,16,10,22
2w,w,q,w,y,16,3e,3e,5c,3lu,9,12,2,2,d,1w,3s,3e,fi,1i,s,k,e,m,12,y,1y
2s,u,s,u,y,14,3a,3c,5c,3lm,8,10,0,2,d,22,3m,2s,fk,1i,s,i,g,k,10,u,1s
2w,w,s,w,y,16,3e,3e,5a,3m0,d,w,6,2,9,1u,3w,3g,fi,1i,s,k,g,k,12,10,1y
38,10,w,w,10,1a,3o,3o,5o,3ni,21,8,1c,k,a,1w,28,3y,fm,1o,m,i,k,k,1c,12,24
2s,w,q,w,w,16,3c,3c,5c,3lg,4,18,2,2,h,1y,3o,30,fi,1i,s,i,g,k,10,w,1u
2i,s,q,s,u,12,30,34,54,3mw,i,1,o,5,h,1o,34,2u,fk,1m,q,k,g,i,w,s,w
2u,w,s,u,y,16,3e,3c,5c,3lu,3,y,1,2,9,22,3o,3c,fk,1k,q,i,g,k,10,y,1u
30,y,s,w,y,18,3i,3e,5a,3mm,v,g,i,e,5,1e,42,3s,fi,1m,q,k,g,m,14,10,22
2o,u,q,u,w,16,3e,3q,5i,3mm,8,8,g,2,d,1g,q,3u,fg,1a,y,k,k,k,10,12,2a
32,y,s,y,10,18,3o,3w,64,3mi,1d,12,c,e,8,2o,14,2u,fe,1i,s,m,i,m,16,16,2a
3q,1a,12,16,1a,1m,4m,50,86,3k2,1n,4,y,1s,1g,3w,2m,28,fg,w,12,c,g,m,1i,1e,2q
38,12,w,10,14,1c,3y,46,6o,3mc,1z,i,c,s,u,3a,1s,2s,fc,1e,s,k,g,m,1c,18,2e
2w,y,s,u,10,18,3k,3s,5u,3me,t,14,2,8,4,2e,10,3a,fe,1g,u,m,i,k,16,16,28
3g,1a,10,14,1a,1k,4m,50,84,3l6,25,r,10,1a,1e,3w,2g,2k,f6,w,s,o,e,k,1i,1g,2q
3m,1a,10,14,1a,1k,4m,4y,84,3ke,1b,5,1g,1g,16,3m,2g,2m,fg,w,10,e,g,m,1k,1e,2q
32,y,u,y,10,1a,3q,40,64,3mg,1f,s,6,g,e,2s,1a,36,fa,1e,w,m,g,k,1a,18,2a
2q,w,q,u,y,16,3g,3q,5k,3mc,4,o,4,4,7,1s,u,3s,fg,1c,w,k,k,k,12,14,2a
2q,s,q,s,u,10,34,3a,54,3mq,12,e,e,6,i,18,a,3a,fq,1e,y,o,m,k,1e,1o,18
30,y,u,y,12,1a,3w,44,6o,3m8,15,3,c,k,w,2s,1y,2m,f6,16,10,k,g,i,1c,18,2g
2q,u,q,s,w,14,3a,3i,5o,3n8,1,f,e,m,o,2a,s,3i,fc,18,y,m,k,m,1a,1y,1o
2u,w,s,u,y,18,3i,3s,5o,3mc,h,14,1,4,2,26,w,3m,fe,1g,u,m,i,k,14,16,2a
2g,s,q,q,u,12,38,3k,5e,3oe,r,0,10,1,f,q,u,2c,fe,1i,s,o,k,i,12,u,2a
2o,u,s,s,y,16,3i,3s,5q,3mi,3,c,k,6,d,1g,o,3o,fe,1a,w,m,i,k,12,12,2a
3q,1a,12,16,1a,1m,4s,54,88,3ju,11,f,1i,1e,1e,3o,2u,2i,fc,u,10,c,g,m,1o,1g,2w
2i,q,o,o,o,w,2m,2u,4c,3ny,s,6,y,k,16,1,q,32,fq,1i,w,u,o,m,2e,1a,s
3k,16,12,14,18,1k,4m,50,86,3l8,1p,h,1i,18,12,3e,2e,2o,fg,u,10,e,e,k,1i,1g,2s
3s,1a,12,16,1a,1m,4q,52,88,3k0,1p,8,16,1s,1e,3s,2q,24,fg,w,12,c,g,m,1i,1e,2s
36,10,w,y,10,1c,3u,42,6c,3mi,1x,w,c,m,m,32,1g,2s,fe,1i,q,m,g,k,1c,16,2c
2y,y,s,w,12,18,3q,3y,62,3m6,r,o,2,a,6,2g,16,3g,fa,16,12,m,g,k,16,18,2c
3q,1a,12,16,1a,1m,4u,54,8a,3jm,z,9,10,1e,1k,3u,2y,2g,f6,w,k,s,g,m,1o,1i,2w
32,10,u,w,12,1a,3s,40,66,3mg,1f,m,6,i,g,2w,1c,36,fa,1c,w,m,g,m,1a,18,2a
2y,y,u,w,10,1a,3q,3y,64,3m8,v,m,4,a,8,2g,18,3c,fa,16,12,m,g,k,18,18,2a
2o,s,o,q,u,10,30,36,50,3n2,1a,3,m,a,g,1c,7,3c,fq,1e,y,o,m,m,14,24,12
36,10,w,y,12,1c,3u,44,6e,3mi,1t,i,a,m,q,34,1m,2w,fa,1e,u,m,g,k,1c,18,2c
3o,1a,10,16,18,1k,4m,4u,80,3kc,1r,8,s,1m,1e,3w,2i,2c,fg,10,y,e,g,k,1i,1c,2o
3m,18,10,14,1a,1i,4i,4s,7s,3kk,1j,0,m,1i,1c,3w,2e,2g,fg,y,10,g,e,k,1i,1a,2m
36,14,y,12,14,1i,48,4k,7i,3mc,27,t,s,s,1a,3a,2c,2q,f4,12,10,k,e,i,1e,1a,2k
3q,1a,14,16,1a,1k,4q,50,8a,3ju,1d,9,16,1q,1i,40,2q,24,fc,w,12,c,g,m,1m,1e,2s
3q,1a,12,16,1a,1m,4o,50,86,3k0,1h,b,10,1q,1i,44,2o,28,fc,w,10,e,g,k,1m,1e,2s
3s,1a,12,16,1a,1m,4q,52,88,3jy,1n,6,16,1u,1e,3s,2o,26,fg,w,12,c,g,m,1i,1e,2s
2s,w,s,u,y,18,3m,3u,5u,3m8,5,i,8,6,5,1u,u,3s,fc,18,10,k,i,k,14,14,2c
3m,18,12,14,1a,1k,4o,50,86,3km,1f,b,1i,1a,16,3i,2m,2o,fe,w,10,c,g,k,1m,1g,2s
3q,1a,12,16,1a,1k,4m,50,84,3k6,1p,6,w,1s,1g,3u,2m,28,fg,y,10,e,e,m,1i,1c,2q
32,y,s,y,10,18,3q,3w,62,3mi,1d,12,6,c,e,2q,14,32,fe,1i,s,m,g,m,18,16,2a
3m,18,10,14,1a,1i,4i,4q,7s,3km,1r,g,k,1i,18,3u,2e,2g,fg,12,y,g,e,m,1g,1a,2k
3m,18,12,14,18,1k,4k,50,88,3k4,1j,a,1g,1m,18,3i,26,2e,fk,12,u,e,e,m,1g,1e,2o
2s,u,s,u,w,14,3a,3k,5g,3mk,g,q,4,2,8,1y,c,3e,fk,1g,w,m,k,m,12,1m,1o
32,10,u,w,10,1a,3o,3y,64,3mi,1f,12,c,c,g,2u,12,2y,fg,1i,q,o,g,m,18,14,2a
3q,1a,12,16,1a,1o,4q,54,8a,3jq,z,f,1i,1e,1g,3o,2u,2i,fa,w,10,c,g,m,1o,1i,2u
3s,1a,12,16,1a,1m,4q,52,88,3jy,1n,6,16,1u,1e,3s,2o,26,fg,w,12,c,g,m,1i,1e,2s
2s,s,q,u,w,14,3a,3g,5c,3mm,q,a,8,0,c,1q,0,3y,fk,1e,w,m,m,k,14,1w,1e
3a,14,w,10,14,1c,40,48,6q,3m8,27,w,e,s,w,3a,1w,2o,fe,1k,m,k,g,m,1c,18,2e
2o,s,o,s,u,10,32,38,52,3n4,18,7,k,6,e,1e,3,3g,fm,1g,w,o,m,m,14,22,16
3q,1a,10,16,1a,1k,4m,4y,84,3k6,1n,6,u,1q,1g,3u,2k,2a,fg,y,10,e,e,m,1i,1c,2q
2y,y,q,w,y,18,3k,3s,5s,3mg,n,16,6,4,a,2g,m,38,fi,1g,u,o,g,m,14,18,24
3q,1a,12,16,1a,1m,4o,52,8c,3jq,17,5,1c,1o,1g,3u,2m,28,fe,w,10,e,e,m,1m,1g,2s
3q,1c,12,16,1a,1k,4o,50,88,3k0,1l,2,12,1s,1g,3w,2m,26,fg,w,12,c,g,m,1i,1e,2q
2y,10,u,w,12,1a,3u,42,6q,3n0,1h,b,6,o,1a,2u,22,3g,f8,14,10,k,e,k,1e,1g,2a
3m,1a,12,14,1a,1k,4k,4u,80,3kc,1l,5,q,1m,1i,42,2e,2e,fe,y,10,g,e,k,1i,1c,2o
2c,q,k,k,m,q,28,2c,3m,3ow,d,1k,u,1k,1m,g,q,32,fo,1o,u,y,s,o,2q,18,s
3e,16,12,12,18,1k,4k,4w,86,3lu,2b,x,w,1a,1e,3m,2a,2o,f4,y,q,q,e,i,1i,1g,2q
3i,1a,12,14,1a,1k,4q,54,8c,3kw,1t,j,12,18,1e,3o,2k,2k,f4,w,k,u,e,k,1k,1i,2w
3o,1a,12,16,1a,1k,4m,4y,84,3k6,1j,7,u,1q,1i,42,2k,2a,fc,y,10,g,e,m,1i,1e,2q
2s,w,q,w,y,16,3k,3u,64,3me,1,2,8,c,4,28,1c,24,fc,14,12,k,i,k,16,16,2e
3m,18,10,14,1a,1k,4k,4y,86,3kc,1f,0,1g,1k,16,3k,26,2k,fi,y,y,e,g,k,1i,1e,2q
2q,s,q,s,s,12,32,3a,54,3mq,14,c,g,6,k,14,a,3a,fq,1e,y,o,m,m,1e,1o,16
2k,s,m,q,q,y,2s,2y,4m,3nk,12,0,s,i,w,g,8,38,fq,1g,y,q,o,m,1w,1o,s
3e,14,10,12,16,1g,48,4g,76,3ls,1p,7,c,18,10,3o,2c,2m,fc,10,10,k,e,m,1g,18,2k
3o,1a,12,16,18,1m,4k,4y,82,3k8,1n,2,u,1o,1g,3y,2i,2a,fg,w,12,e,e,m,1i,1c,2o
2y,y,s,w,y,18,3m,3s,5w,3mg,z,18,0,6,a,2i,10,38,fe,1g,u,m,i,m,14,16,2a
3q,18,14,14,1c,1m,4u,58,8a,3k2,z,9,12,1a,1e,3k,2w,2k,f8,u,k,s,g,k,1o,1k,2y
2k,q,o,o,q,w,2o,2w,4i,3ns,w,2,w,i,14,6,e,34,fq,1i,w,s,o,k,26,1i,s
3s,1a,12,16,1a,1m,4q,54,8c,3jm,1b,2,1c,1u,1e,3q,2k,26,fg,w,10,e,e,m,1k,1g,2s
3c,16,10,14,18,1i,4e,4q,7o,3lo,23,r,s,12,18,3q,2c,2o,f6,y,y,m,e,i,1g,1c,2m
2i,s,m,q,q,w,2q,2y,4i,3o0,q,1,u,g,10,y,d,3a,fo,1i,w,q,q,k,1m,20,u
30,y,w,y,10,1c,3u,46,6u,3n2,1l,f,8,s,18,2u,22,3e,f6,16,10,k,c,k,1e,1g,2c
3q,1a,12,16,18,1m,4q,54,8c,3jk,1b,6,1k,1q,1e,3i,2g,2a,fi,w,10,c,g,m,1i,1e,2s
30,y,s,y,10,1a,3s,40,68,3m6,v,g,4,e,a,2m,1a,3a,f8,16,12,k,g,m,18,18,2c
3o,1a,10,14,1a,1k,4o,52,8c,3jk,17,6,1m,1o,1a,3i,28,2c,fk,u,12,c,g,k,1g,1e,2s
3k,18,10,14,18,1i,4g,4o,7o,3ku,1v,c,k,1g,1a,3s,2c,2i,fg,14,w,g,e,k,1i,1a,2k
32,y,u,w,10,1a,3m,3y,62,3mi,1b,14,c,a,e,2q,10,30,fg,1i,s,m,i,k,18,14,2a
3c,18,10,12,18,1k,4i,4u,7y,3m8,2p,13,12,14,18,3o,2c,2o,f4,y,10,k,c,i,1g,1e,2m
34,12,u,10,12,1c,3w,44,6g,3me,1f,2,6,o,q,32,1q,30,f8,16,10,m,e,m,1c,1a,2e
3q,1c,10,16,1a,1m,4o,50,84,3k6,1r,a,y,1u,1c,3u,2m,28,fi,w,10,e,g,k,1i,1e,2q
3q,1a,12,16,1a,1m,4q,52,8a,3jq,17,7,1c,1o,1g,3w,2m,28,fe,u,12,c,g,m,1m,1g,2s
3o,18,12,14,1a,1m,4o,52,86,3ka,17,d,1i,1c,1a,3k,2q,2m,fe,u,10,e,g,k,1m,1i,2s
2y,y,s,y,10,1a,3q,3y,60,3m6,r,o,0,c,6,2g,18,3g,fa,16,12,k,i,k,16,18,2c
3q,1a,12,16,1c,1m,4s,54,8a,3jo,z,d,1g,1g,1e,3q,2w,2g,fa,w,10,c,g,m,1o,1i,2u
3q,1a,12,16,1a,1m,4q,52,8a,3jq,13,9,1c,1m,1g,3u,2q,28,fc,w,12,c,g,m,1m,1g,2s
2y,y,s,w,y,18,3m,3s,5w,3mg,z,18,1,8,8,2k,10,38,fe,1g,u,m,i,k,16,16,2a
3o,1a,12,16,1a,1k,4o,4y,84,3k6,1l,b,w,1q,1k,44,2k,2a,fc,w,12,e,e,m,1k,1e,2q
3s,1a,12,16,1c,1m,4s,54,8c,3jk,z,f,1e,1k,1g,3u,2w,2e,fa,u,12,c,g,m,1o,1i,2u
3q,1a,12,16,1a,1m,4m,50,86,3k2,1j,b,10,1q,1i,44,2o,28,fc,w,10,e,g,m,1k,1e,2s
3i,18,y,14,18,1i,4i,4s,7w,3li,1p,3,1a,1a,w,3e,2g,2w,fg,y,10,e,e,m,1k,1e,2q
3o,1a,12,14,1a,1m,4m,52,8a,3ju,17,3,1g,1m,1e,3o,2g,2a,fg,w,10,e,e,m,1k,1e,2s
3s,1a,12,16,1a,1m,4o,52,88,3k2,1r,8,16,1s,1e,3s,2o,26,fg,y,10,c,g,m,1i,1e,2s
2o,u,o,s,w,12,34,3c,54,3my,w,8,a,4,e,1o,5,3m,fm,1e,w,o,m,m,12,1y,1c
3q,1a,12,16,1a,1m,4q,52,8a,3jq,17,7,1c,1o,1g,3w,2o,26,fc,w,12,c,g,m,1m,1g,2s
2s,w,s,u,y,18,3m,3w,62,3m8,5,c,8,8,0,22,y,2o,fc,14,12,k,i,k,14,16,2e
2k,q,o,o,q,y,2o,2y,4i,3nq,y,2,u,i,12,a,a,38,fq,1g,y,s,o,k,24,1k,s
2o,s,q,s,s,10,32,38,50,3mu,16,8,i,8,m,y,a,3a,fq,1e,y,q,m,k,1g,1s,12
3q,1a,12,16,1a,1k,4o,50,86,3k0,1l,1,10,1s,1i,3y,2m,28,fe,w,12,e,e,m,1k,1c,2q
3q,1a,12,16,1a,1k,4m,50,86,3k2,1j,3,w,1s,1i,40,2k,28,fe,w,12,e,e,m,1k,1c,2q
3q,1a,12,16,1a,1m,4o,50,86,3k0,1j,3,y,1u,1i,40,2m,24,fe,w,12,c,g,m,1k,1e,2q
3i,18,10,12,1a,1k,4k,4y,86,3lo,1x,h,1c,1a,10,3a,2c,2o,fg,w,y,e,e,m,1g,1g,2q
3q,1a,10,16,1a,1k,4m,4y,82,3ka,1p,c,s,1q,1c,3u,2k,2a,fi,y,10,e,e,m,1i,1c,2o
3o,1a,12,14,1a,1k,4k,4w,7y,3ke,1p,e,q,1o,1a,3s,2i,2c,fi,y,12,e,e,m,1g,1a,2o
3g,16,10,12,16,1g,4e,4u,7y,3ku,21,y,1a,1m,10,30,1u,2k,fq,1g,k,g,e,i,1c,1a,2k
3c,14,w,10,14,1e,40,48,6q,3m2,1t,s,i,s,y,36,1y,2o,fi,1k,m,k,g,k,1e,16,2e
3q,1a,12,16,18,1k,4m,4w,80,3k8,1n,m,o,1q,1c,3q,2i,2c,fk,10,10,c,g,m,1g,1c,2m
3m,18,10,14,18,1k,4m,50,88,3k2,1v,q,1i,1q,18,38,20,2e,fo,16,q,e,e,k,1e,1c,2o
3q,1a,10,16,1a,1k,4k,4y,80,3kc,1p,c,s,1q,1c,3s,2i,2c,fi,y,10,e,g,k,1i,1c,2m
3q,1a,12,16,1a,1m,4m,50,86,3k6,1r,a,y,1s,1e,3u,2k,2a,fi,w,10,e,g,k,1i,1e,2q
36,12,w,y,10,1c,3y,4c,7a,3m4,1f,q,m,16,14,2e,1e,36,fu,1i,k,k,e,i,1a,1g,22
38,12,w,10,12,1c,3w,44,6i,3ma,1v,w,i,m,s,34,1m,2o,fg,1k,o,k,i,k,1c,14,2c
3u,1a,12,16,1a,1m,4o,52,88,3jw,1f,m,14,1w,16,3o,2o,22,fk,y,12,c,g,m,1k,1c,2q
3o,1a,12,14,1a,1k,4q,52,8c,3jq,1l,g,1m,1q,1a,3e,2a,2a,fk,y,10,c,e,m,1g,1e,2q
3u,1a,12,16,1a,1m,4o,52,88,3k2,1p,i,14,1u,1a,3q,2o,24,fi,y,12,c,g,m,1i,1c,2s
3u,1a,12,16,1a,1m,4o,52,8a,3ju,1f,m,16,1w,16,3o,2m,24,fk,y,12,c,g,m,1k,1c,2q
3s,1a,12,16,1a,1m,4q,56,8c,3js,1l,c,1i,1s,1c,3i,2k,28,fi,w,12,c,e,o,1i,1e,2s
3q,18,12,16,18,1k,4m,4w,80,3kc,1p,e,q,1o,1c,3u,2g,2c,fi,y,12,e,e,m,1g,1c,2m
3q,1c,12,14,1a,1m,4q,54,8e,3jq,1l,e,1k,1s,1c,3g,2g,28,fk,w,10,c,g,m,1g,1e,2u
3a,14,y,y,14,1e,46,4k,7k,3lq,1z,u,10,1c,10,2u,1g,32,fq,1i,i,i,e,i,1c,1g,2a
3o,18,12,14,18,1m,4o,52,8c,3jo,1h,e,1m,1o,1c,3e,26,2c,fm,w,y,e,e,k,1g,1e,2s
3u,1a,12,18,1a,1k,4s,52,8c,3jw,1n,i,1c,1w,18,3k,2o,24,fi,y,12,c,e,o,1i,1e,2s
3s,1a,12,16,1a,1k,4s,54,8c,3jq,1l,e,1i,1s,1c,3g,2k,28,fi,w,12,c,g,m,1g,1g,2s
38,12,u,10,12,1a,3w,42,6g,3me,1r,u,i,k,s,2y,1k,2s,fg,1k,o,m,g,m,1a,16,2c
3s,1a,12,16,1a,1k,4o,4y,84,3k4,1l,k,w,1s,1a,3q,2k,28,fk,y,10,e,g,m,1g,1c,2q
34,y,u,w,10,1a,3o,3y,64,3mg,1b,14,e,a,i,2q,10,2y,fg,1k,q,o,g,m,16,16,28
3k,16,12,12,18,1i,4k,50,86,3kc,1z,m,1g,1o,16,38,1w,2i,fm,18,o,g,e,i,1e,1c,2o
38,14,w,y,14,1c,44,4g,7g,3lq,1n,u,w,1a,10,2k,1a,36,fs,1i,k,i,g,i,18,1k,22
3s,1c,10,16,1a,1m,4q,54,8c,3jq,1h,i,1i,1s,1a,3i,2g,26,fk,w,12,e,e,m,1i,1e,2s
3o,18,12,14,1a,1i,4i,4u,7u,3kg,1t,q,m,1m,18,3s,2e,2e,fk,14,u,g,e,m,1g,1a,2m
3s,1a,12,16,1a,1k,4m,4y,84,3k4,1l,m,w,1u,18,3q,2k,28,fk,10,10,c,g,m,1i,1a,2o
34,y,u,w,10,1a,3q,42,6q,3ma,t,10,i,s,12,2k,w,3w,fu,1i,o,k,e,k,1a,1y,1k
3o,1a,12,14,1a,1k,4q,54,8c,3jm,1f,c,1m,1q,1a,3g,2a,2a,fk,w,10,e,e,k,1g,1e,2s
3q,1a,12,14,1a,1k,4q,56,8c,3jm,1f,a,1m,1q,1c,3g,2e,2a,fk,u,12,c,g,k,1i,1e,2s
3s,1a,12,16,18,1m,4s,54,8c,3jq,1l,e,1k,1q,1e,3g,2i,26,fk,u,12,c,g,m,1g,1g,2s
3s,1c,12,14,1a,1m,4q,56,8c,3ju,1n,e,1i,1s,1a,3i,2m,26,fi,w,12,c,g,m,1i,1e,2s
3q,18,12,16,18,1k,4k,4w,7y,3ka,1t,s,o,1o,1a,3s,2g,2c,fk,14,w,e,e,m,1g,1a,2m
3s,1c,12,16,1a,1k,4s,54,8c,3ju,1n,i,1e,1u,1a,3i,2m,26,fk,w,12,c,e,o,1i,1c,2u
3c,14,w,10,14,1e,40,4a,6s,3m0,1v,w,g,s,y,36,1y,2o,fi,1i,o,k,g,k,1c,16,2e
3g,14,y,12,16,1e,46,4e,70,3lm,1x,w,i,10,12,3a,26,2m,fi,1i,m,k,g,k,1e,16,2g
3o,1a,10,16,18,1k,4k,4u,7w,3ke,1n,g,o,1k,1c,3s,2g,2e,fi,10,10,e,g,k,1g,1a,2m
3q,18,12,16,18,1k,4m,4w,80,3kc,1p,e,q,1o,1c,3u,2g,2c,fi,y,12,e,e,m,1g,1c,2m
3g,14,y,10,16,1e,44,4e,70,3lo,1v,w,g,10,10,3a,24,2m,fi,1i,m,k,g,k,1e,16,2g
36,12,w,y,12,1a,40,4a,78,3m6,1h,s,o,14,14,2k,1a,3c,fs,1i,m,i,g,i,1a,1k,20
3s,1c,12,16,1a,1k,4s,54,8c,3js,1l,g,1g,1u,1a,3i,2m,24,fi,y,10,e,e,m,1k,1c,2u
32,w,u,u,y,18,3k,3w,6g,3m8,9,10,g,q,14,20,1e,3w,fy,1i,o,k,i,i,1k,1o,1k
3o,18,10,14,18,1k,4g,4s,7q,3ki,1z,12,i,1i,18,3o,2e,2i,fk,1c,o,g,e,m,1e,1a,2k
3o,1a,10,16,18,1k,4k,4u,7w,3ke,1r,o,o,1m,18,3s,2g,2e,fk,12,w,g,e,m,1g,1a,2m
3s,1a,12,16,1a,1m,4q,56,8c,3js,1l,c,1k,1q,1c,3i,2k,26,fi,w,12,c,g,m,1i,1e,2s
3a,12,y,y,14,1e,44,4k,7k,3lu,1x,q,u,1e,14,2i,1k,2u,fs,1i,k,i,e,g,1a,1c,2a
3e,16,y,10,16,1g,4c,4q,7y,3la,27,u,14,1o,12,2q,1k,2u,fq,1i,i,i,c,i,1c,1c,2e
3i,18,10,12,18,1i,4k,4y,88,3k4,25,12,1g,1s,12,36,1u,2g,fo,1e,i,g,e,i,1c,1c,2m
2u,w,q,s,w,14,32,34,4c,3m0,3,s,k,i,4,y,1s,3w,fs,1k,w,i,i,i,y,u,1e
38,14,w,y,10,1c,3s,3q,5o,3mi,2f,5,k,m,c,1s,3o,4s,fk,10,16,k,g,i,14,u,1s
3c,16,y,16,1a,1m,54,5o,8k,3ne,2n,u,j,i,e,j,1l,4o,fq,1s,m,g,e,g,1e,2u,18
30,w,s,w,12,1a,48,4s,8a,3og,1u,4m,k,1n,y,5r,r,15,fo,22,6,o,k,m,30,18,1a
3a,12,w,14,16,1i,4s,5k,90,3o6,1z,1c,l,3,14,i,3b,1s,fu,1s,m,i,e,g,12,2w,1o
30,w,s,w,w,16,3g,3a,4s,3mc,19,q,k,m,8,k,2m,4a,fo,1k,u,k,g,i,10,w,1i
2y,w,s,s,y,14,30,2s,3y,3l2,l,18,e,1,3,24,46,62,fs,1k,s,e,e,e,q,i,m
3g,16,10,14,1c,1m,5a,5g,8k,3na,25,d,4,9,f,1d,8,4o,fg,10,1e,i,g,k,1k,1w,16
2y,w,q,u,w,18,3q,4a,6k,3ni,n,18,g,k,b,2l,z,2y,fi,1k,10,o,m,m,2k,u,q
2y,w,s,u,w,16,30,2s,40,3l0,n,16,i,3,3,26,4a,60,fs,1k,s,e,e,e,q,i,m
36,10,w,y,14,1e,4m,5g,8w,3nw,b,2m,2o,3,e,2v,45,3,fs,1w,e,e,g,i,1s,2i,1q
3i,16,10,14,1a,1o,58,5i,8g,3ng,1r,p,3,7,f,1d,2,56,fi,y,1a,i,i,k,1m,1m,1g
3a,16,y,16,18,1m,52,5i,8i,3ni,2v,16,t,e,a,p,15,4k,fq,1s,m,g,g,g,1e,2m,18
34,12,y,y,16,1i,4o,52,7e,3o2,1n,11,o,2,b,1r,l,7g,fe,14,1a,k,m,g,1i,1k,1c
36,12,y,12,16,1i,4o,5c,8i,3nm,1r,14,d,1e,w,a,3d,2e,fs,1y,k,g,g,e,12,2y,1o
34,10,w,w,10,1a,3s,3q,5m,3mu,27,a,k,o,2,e,36,50,fk,1e,w,m,g,i,16,w,1m
36,14,10,10,16,1k,4u,58,7m,3o8,1n,1l,q,1,9,1r,f,7k,fc,12,1e,k,k,i,1g,1s,16
34,10,u,w,10,18,3k,3c,4y,3m6,23,i,k,k,a,16,3y,4a,fm,1a,10,k,e,i,10,o,1i
3c,16,10,16,1a,1m,54,5q,8y,3nm,2f,s,z,c,u,3,1p,30,fq,1o,o,g,e,i,1a,32,1c
36,10,w,y,14,1i,4e,52,8c,3pi,1p,10,0,17,v,1z,i,1c,fu,1u,s,i,k,i,2m,12,14
30,y,s,u,y,18,3s,48,6g,3ni,19,u,s,k,1,1n,1l,3o,fi,1i,y,o,k,m,1q,1m,w
3c,16,10,16,18,1o,54,5o,8w,3nk,29,u,13,m,g,5,1l,30,fq,1m,q,g,e,i,1a,2w,1g
3a,14,10,10,16,1i,4s,50,7i,3oc,29,1b,o,2,d,19,5,6w,fe,10,18,k,k,i,1m,1q,18
38,12,y,y,14,1g,4e,4m,6y,3ny,2d,p,s,c,7,1d,k,6c,fg,12,14,m,i,k,1g,1m,1a
30,y,s,u,w,16,3a,34,4k,3mg,1n,y,s,8,i,y,34,38,fo,1k,s,k,e,i,y,o,1m
2y,w,q,u,w,16,38,38,4m,3m8,n,s,i,m,6,o,20,4a,fo,1m,u,k,i,i,10,y,1g
3c,16,w,10,14,1e,44,4a,6m,3ni,2z,h,s,c,a,1c,2s,4u,fi,w,16,i,i,k,18,12,1y
36,12,w,w,12,1c,42,40,66,3n8,29,3,m,q,1,d,2e,5g,fi,18,10,m,g,k,1a,14,1k
2q,u,o,q,s,10,2s,2q,3w,3m0,i,m,y,e,a,14,1o,46,fu,1o,w,i,k,g,14,u,12
3c,16,10,14,1a,1o,58,5i,8i,3ns,3d,g,8,9,h,1j,1,4q,fi,1k,y,g,g,i,1i,20,12
2s,u,q,s,w,12,2s,2m,3q,3ks,m,q,8,3,5,2c,3o,6y,fw,1k,q,e,e,a,k,i,a
3e,14,y,18,18,1o,54,5q,8y,3no,2d,14,1j,4,6,4,1n,3e,fq,1o,o,g,g,i,1c,2w,1a
3g,16,10,14,1a,1m,52,5c,84,3ns,1v,1f,e,7,d,p,3,6e,fe,y,18,k,k,i,1o,1m,1g
2q,u,o,q,u,10,2q,2q,3s,3ly,6,s,q,6,6,1m,1u,5e,fu,1o,w,g,g,g,s,u,k
3g,16,10,14,1c,1m,5a,5g,8i,3nc,25,f,4,9,d,1d,4,4s,fg,10,1c,k,g,i,1m,1w,16
3g,16,10,14,1a,1m,54,5e,8a,3nq,23,z,a,7,d,13,3,5q,fe,y,1c,k,i,i,1m,1s,1a
2w,u,o,s,w,12,3m,46,7c,3p2,18,2y,38,b,o,61,1b,n,fy,28,e,k,i,m,38,u,w
3g,18,10,14,1a,1o,5a,5g,8i,3n4,1t,h,0,9,f,1f,8,4u,fi,y,1c,i,i,i,1m,1w,16
34,12,u,w,10,18,3m,3e,52,3m8,27,e,k,m,a,16,40,4i,fm,18,12,i,g,i,10,q,1k
3e,18,10,14,1a,1o,5a,5k,8m,3na,2t,e,1,b,5,1d,0,4e,fk,1g,y,g,g,i,1k,28,12
30,u,s,y,12,18,4a,4q,8e,3po,32,40,2d,15,1c,6v,w,1t,fo,1w,8,s,m,k,3e,o,1c
3c,14,10,10,14,1i,4k,4q,76,3o6,2h,1l,10,w,l,v,1w,5k,fg,y,14,k,i,k,1i,12,20
2s,u,q,s,w,12,2u,2m,3s,3ks,i,w,a,1,5,24,3g,74,fw,1k,s,c,g,c,k,i,c
3a,14,10,12,18,1m,52,5c,7y,3oa,1z,1h,k,1,f,1l,3,6o,fc,12,1e,k,k,g,1k,1s,16
3e,14,12,10,18,1k,4u,54,7k,3o8,23,1v,u,4,h,r,g,6q,fe,y,14,k,k,k,1m,1g,1m
34,12,u,w,10,1c,3o,3m,5g,3mm,27,8,k,o,6,u,3k,4q,fk,1e,w,k,g,k,12,s,1q
2u,w,q,s,w,14,2u,2m,3u,3kg,c,12,4,3,7,2m,3w,72,fw,1i,q,c,e,c,m,g,e
3g,16,10,14,1a,1o,58,5k,8k,3na,1v,0,j,9,j,1f,3,4w,fm,16,14,i,g,i,1m,22,14
2y,y,s,u,y,16,38,30,4c,3lu,1h,16,o,4,0,1c,42,4g,fq,1m,s,g,e,g,w,k,w
3g,18,10,12,1a,1o,52,5g,84,3no,1t,19,c,b,b,r,4,60,fg,w,16,m,i,k,1o,1g,1m
2u,s,o,s,w,10,3m,3y,78,3ow,32,4k,1,13,18,63,s,27,fw,26,c,q,m,m,3c,c,w
38,12,w,12,16,1i,4k,58,8q,3oq,1v,12,j,h,1,2,17,1s,fu,1s,q,g,i,g,1k,26,1c
38,12,y,y,12,1e,3y,40,66,3n2,2f,f,m,q,3,16,32,4w,fi,14,12,m,g,k,18,u,1u
2u,y,q,s,y,14,30,2u,42,3lg,p,1a,k,0,1,1m,3k,5m,fu,1k,u,e,g,e,s,i,o
36,12,u,y,10,1a,3q,3m,5c,3me,2b,4,i,q,8,1a,3s,4o,fk,16,12,k,g,i,14,q,1q
3e,16,10,10,14,1k,4g,4q,78,3nq,2t,t,s,k,d,8,22,5g,fi,u,14,k,i,k,1e,12,24
2w,w,q,s,u,16,3i,3y,66,3ng,d,10,q,q,4,27,z,1s,fk,1k,10,o,m,o,2e,10,s
34,10,u,w,12,1c,42,4c,6k,3nm,1z,8,q,c,3,1f,1,5a,fg,1a,12,o,g,m,1m,1k,12
3e,14,10,10,14,1k,4i,4q,76,3no,2j,13,y,k,f,5,1y,5k,fg,y,14,i,k,k,1g,10,22
2w,u,q,10,y,16,42,4g,7y,3pk,40,3i,2r,11,1m,5j,a,1z,fo,1w,c,s,o,k,3a,o,18
2o,s,o,o,q,y,2i,2m,3o,3m4,16,8,1c,k,m,s,1a,40,fu,1q,w,m,k,g,1m,y,y
3e,16,10,16,1a,1o,5a,5k,8m,3nc,2z,k,1,d,3,1d,5,4g,fk,1i,w,i,g,i,1i,28,12
2q,u,q,q,u,12,2s,2m,3q,3lc,g,s,g,0,3,1y,2u,6q,fw,1m,s,e,g,c,o,g,e
2y,y,s,u,y,14,34,2s,44,3l6,x,16,k,0,1,1y,4c,5m,fs,1k,q,g,e,e,s,i,q
36,10,w,y,14,1e,4g,4s,78,3ny,21,7,q,4,5,1l,11,66,fe,18,14,m,m,k,1i,1u,10
30,10,s,u,y,18,3a,34,4i,3lw,1r,12,o,8,2,1c,46,46,fo,1k,u,g,e,g,w,m,14
2u,u,s,s,w,14,2u,2m,3u,3kg,e,12,6,5,5,2i,3s,76,fw,1i,q,e,e,c,k,i,c
34,y,w,w,y,1a,3s,3m,5e,3mu,25,g,i,q,2,0,3o,5c,fk,1i,u,m,g,i,16,s,1m
3c,16,10,12,16,1m,4u,56,7q,3oa,23,1n,m,1,f,v,7,70,fe,y,18,k,k,i,1m,1s,1a
3c,14,y,y,14,1e,40,46,6e,3n2,2r,h,s,g,6,1s,30,4u,fi,y,14,k,i,k,16,10,1w
3g,16,y,12,14,1k,4m,4w,7c,3nq,2j,15,w,e,b,7,1q,5u,fg,w,14,i,k,k,1g,12,26
36,10,u,w,10,1a,3k,3c,52,3m6,27,i,k,k,a,1g,3y,4c,fm,18,10,k,e,i,10,q,1k
3a,14,w,10,12,1e,40,40,66,3mu,2n,d,m,s,2,1e,38,50,fi,10,14,k,i,i,18,w,1w
3a,14,y,y,14,1e,40,46,6c,3my,2n,h,o,q,0,1g,30,4u,fi,y,16,k,i,i,18,y,1w
2y,y,s,u,y,16,32,2u,44,3l8,z,16,k,0,1,1w,4e,5g,fs,1k,q,g,e,e,s,k,o
2q,s,q,q,u,12,2q,2k,3q,3li,k,m,g,2,3,1w,2s,6o,fw,1m,s,g,e,c,o,g,e
3e,18,10,14,1a,1o,58,5g,8g,3no,2l,9,6,9,f,1d,2,4y,fg,16,18,i,i,i,1k,1w,16
36,12,u,w,10,1a,3k,3g,54,3mc,2d,g,k,k,a,1g,3y,4o,fm,1a,y,k,e,i,12,q,1k
30,y,q,u,w,14,38,32,4k,3mk,1f,12,o,8,g,y,2y,3c,fo,1k,u,i,g,i,y,u,1g
32,10,u,w,y,1a,3q,3m,5e,3ms,1v,k,g,q,2,2,2u,5m,fk,1i,u,m,g,k,16,10,1g
3a,12,y,10,14,1g,4i,4o,70,3o2,2b,15,u,k,d,17,w,68,fg,10,16,k,i,k,1g,1i,1i
34,y,u,u,y,18,3e,38,4q,3ma,23,y,m,e,a,16,3m,46,fo,1k,q,i,g,i,y,o,1g
2u,u,q,s,u,12,34,32,4e,3m4,8,y,k,m,c,k,w,5m,fq,1m,w,m,k,i,1c,1a,10
3c,14,10,16,18,1m,52,5o,90,3no,27,10,17,14,m,2,27,2k,fs,1m,o,i,e,e,1a,2y,1m
32,10,u,w,y,1a,3o,3i,5a,3ms,21,m,g,q,2,8,3g,5e,fk,1k,s,m,g,k,14,u,1k
3a,14,y,14,18,1k,4w,5q,94,3nu,1z,1c,t,1u,q,e,3r,1i,fu,1s,k,g,c,e,14,32,1w
3c,16,10,14,18,1o,56,5g,8e,3ns,31,6,8,5,j,1h,4,50,fk,1k,w,g,i,g,1i,22,12
34,12,u,w,y,1a,3i,3a,4y,3m2,23,k,k,i,c,1g,3w,40,fm,16,14,i,g,g,10,o,1k
3e,16,10,12,18,1m,4u,56,7q,3o2,1z,1r,q,0,f,p,e,6m,fe,y,14,m,i,k,1m,1g,1o
3e,16,10,14,1a,1o,58,5g,8g,3ns,2l,f,a,9,f,1d,0,56,fe,16,1a,i,i,i,1k,1u,18
2u,u,o,s,u,12,30,34,4e,3m4,c,1e,e,k,c,k,1,5u,fq,1k,10,k,k,m,1c,1m,o
3c,14,y,10,12,1g,44,4a,6o,3n4,2r,j,s,q,0,16,2q,4s,fi,y,14,k,i,k,18,10,1y
2y,w,q,u,w,16,3a,38,4o,3ma,n,10,g,k,8,k,1i,56,fo,1m,u,k,i,k,12,18,16
2y,u,o,s,w,14,3q,4c,7m,3ow,10,38,36,f,m,5x,1t,l,fw,28,e,k,i,k,36,y,w
34,y,s,y,12,1c,4g,56,8q,3oi,w,3y,1y,1f,m,5z,1t,z,fs,1y,8,k,i,k,2m,1o,1g
2u,w,q,u,y,18,3q,4c,6i,3ni,2,o,m,4,d,2l,13,3k,fk,1i,12,o,k,m,2i,q,w
2s,s,o,q,s,y,2s,2u,40,3m0,12,q,10,m,k,i,m,5k,fs,1m,10,m,k,k,1o,1a,w
2y,y,s,u,y,16,32,2u,44,3la,11,16,k,0,0,1u,4e,5g,fs,1k,q,g,e,e,s,k,q
2q,q,o,o,q,y,2k,2o,3q,3m0,1g,e,1e,q,q,a,16,54,fs,1q,y,o,k,k,20,10,10
32,y,u,u,10,18,3o,3i,58,3mq,1r,q,g,o,4,1,2q,5k,fk,1k,u,m,g,k,14,12,1c
2y,w,q,u,y,14,30,2q,3y,3l0,j,18,e,3,3,26,46,66,fs,1k,s,e,e,e,q,i,k
3g,16,10,16,1a,1o,58,5g,8k,3na,25,d,4,9,f,1d,8,4o,fg,10,1e,i,g,i,1m,1w,16
32,y,u,u,y,18,3c,32,4k,3lu,1r,y,o,8,6,1e,48,48,fo,1g,w,i,e,g,w,m,14
2s,u,q,u,u,12,2u,2o,3q,3ku,g,w,c,1,7,26,3e,72,fw,1k,s,c,g,c,m,g,e
3e,16,10,12,18,1k,4s,56,7m,3o2,21,1t,y,1,f,n,q,6i,fg,w,14,k,k,k,1m,18,1u
3a,16,10,12,1a,1m,54,5e,82,3oc,25,1h,k,3,h,1f,3,6m,fc,12,1g,i,k,g,1i,1s,18
3c,16,10,14,18,1m,52,5c,84,3o6,27,17,e,3,f,19,5,6a,fe,10,1c,i,k,i,1k,1s,1a
3e,16,y,10,16,1i,4k,4s,78,3nq,2j,15,10,i,f,b,1y,5m,fg,w,14,k,i,m,1g,10,24
3c,16,y,y,14,1g,42,4c,6o,3n6,2v,j,u,g,8,1w,2i,4i,fi,w,16,i,i,k,1a,10,1y
3a,14,y,14,18,1k,4w,5o,94,3nu,1v,14,2,1i,s,3,3r,1e,fu,1s,i,g,c,g,14,34,1u
3c,14,w,10,12,1g,40,46,6g,3my,2p,h,q,o,6,1k,2w,4o,fi,y,16,k,g,k,18,y,1w
3a,14,y,14,16,1k,4w,5q,92,3ns,1r,y,c,1k,q,3,3x,1g,fu,1s,i,g,c,e,16,34,1w
3a,12,y,12,16,1k,4s,5o,96,3nu,1j,18,w,1i,m,d,4h,u,fu,1s,k,e,c,g,14,32,1w
3g,16,10,12,18,1m,4u,56,7o,3ny,27,1f,o,0,d,j,14,6a,fg,w,12,k,k,k,1m,10,26
3e,16,10,14,1a,1m,54,5c,88,3ny,25,11,c,7,d,15,5,62,fe,y,1c,k,k,i,1k,1s,1a
3a,12,w,14,16,1i,4q,5m,96,3nw,1r,1i,v,1q,1a,10,4p,o,fw,1s,k,g,e,c,y,34,20
2y,w,s,u,w,14,3e,3i,5a,3mo,x,1c,a,k,8,9,12,4m,fm,1i,y,o,i,m,1o,16,u
36,12,u,10,12,1g,4g,5m,94,3ny,11,1w,2g,1a,g,p,6j,n,fw,1w,g,e,c,g,1c,2u,1s
2y,w,q,s,u,12,30,2y,4g,3ms,t,1c,g,g,k,y,1u,4m,fq,1i,u,m,i,k,14,1i,12
2y,s,q,q,w,14,3i,3w,64,3pw,2,18,g,6,v,2x,1h,2w,fo,1o,s,m,k,q,1m,1k,12
3i,16,10,14,1a,1m,54,5i,82,3o6,21,19,0,7,d,r,e,66,fg,w,14,m,i,k,1o,16,20
3a,14,w,y,14,1e,42,4c,6s,3o4,2f,l,o,6,6,y,24,4k,fk,w,14,k,i,k,1a,12,20
2w,w,o,s,u,12,2w,2y,46,3mm,j,12,e,e,o,u,2a,30,fs,1k,u,i,i,i,10,s,1o
3a,12,w,y,10,1c,3s,3o,5o,3mm,2f,9,m,m,c,1m,42,5a,fk,10,16,k,g,i,14,u,1q
2u,q,o,q,q,w,2m,2u,4s,3qg,2u,b,k,2k,1g,10,a,2,g4,28,y,q,e,12,2g,y,10
3g,16,10,14,18,1o,52,5e,7q,3r2,3p,19,b,h,l,r,1e,7a,fk,u,12,i,k,m,1i,14,2c
32,y,q,u,w,16,3c,38,56,3na,1n,12,u,4,a,y,24,4w,fm,1i,u,m,g,k,18,1q,w
30,u,o,s,u,10,32,30,4m,3n2,t,1g,g,g,o,q,10,4y,fo,1k,u,m,i,m,18,1u,u
3a,12,w,12,16,1k,4q,56,8c,3p6,25,k,7,x,13,2r,14,3k,fq,1m,w,g,k,i,2m,y,y
3g,16,10,12,18,1m,4s,54,7m,3o6,2f,19,i,4,l,d,18,6m,fg,w,12,k,i,m,1i,12,28
3g,16,10,12,1a,1m,54,5e,7s,3qu,3j,x,b,h,l,v,g,7i,fm,u,16,i,k,k,1k,14,24
3a,10,w,y,10,1c,48,4g,6y,3p4,29,j,i,e,e,1t,16,54,fk,10,12,k,i,k,1g,1e,1s
3g,16,10,14,1a,1m,54,5e,7q,3r4,3r,19,b,h,l,p,1a,7c,fk,u,12,i,k,m,1k,12,2c
2w,s,q,s,s,12,3a,3s,6m,3pu,1e,u,o,2k,q,1v,1f,1j,g0,26,o,q,i,12,2c,y,12
38,y,s,w,y,1c,3y,4a,72,3q2,1h,16,g,s,d,35,5,18,fu,1m,u,k,m,i,2m,16,u
3c,14,y,12,14,1i,4m,4s,7c,3p8,2d,17,g,2,f,v,20,60,fi,w,12,k,i,k,1e,18,26
38,12,w,y,14,1i,4k,4q,7c,3qi,29,z,e,v,f,23,1q,6k,fm,12,y,i,k,k,1c,1c,20
2y,q,q,q,q,y,2o,2u,4o,3q0,28,1,1o,2w,1s,l,4,8,g6,26,s,k,8,m,2u,y,10
3a,10,w,y,12,1e,4a,4g,70,3pa,29,p,i,8,a,1t,1c,5a,fk,10,12,k,i,k,1e,1e,1u
3c,14,w,10,12,1e,42,44,6e,3nc,2v,h,s,c,e,1e,36,50,fi,y,14,k,i,i,18,10,1w
3e,14,10,12,18,1m,50,56,7m,3re,2j,2d,9,f,p,17,22,74,fk,w,10,i,k,k,1g,18,2a
3a,12,u,y,14,1g,4g,4u,7m,3q0,23,g,g,0,p,2v,r,3c,fs,1g,w,i,k,i,24,1g,q
36,y,u,u,10,1c,40,4i,78,3q6,1f,16,i,6,11,33,h,1i,fu,1o,u,k,o,i,2m,14,k
3g,16,10,12,1a,1o,52,5e,7q,3qi,29,1r,d,j,p,r,18,7a,fk,u,10,k,i,m,1i,18,2a
2s,q,m,o,o,w,2m,2u,4s,3oa,1g,o,1e,o,i,t,16,k,fs,1o,w,o,o,o,2k,16,y
2q,q,m,m,q,u,2c,2e,3e,3m6,1k,c,1i,s,16,m,1q,4s,fu,1m,w,o,m,i,1w,w,1g
3a,10,y,y,16,1i,4o,4u,7e,3qk,29,x,7,l,f,21,1o,6w,fm,12,w,i,k,k,1e,1c,20
3a,14,w,y,14,1g,4g,4o,78,3pm,2h,11,g,2,3,1h,1s,5s,fk,y,10,k,i,m,1e,1a,20
2w,s,o,s,s,10,30,3e,5u,3qg,1q,1e,1f,1i,1g,8,b,17,fy,26,y,s,i,w,2g,10,16
2w,u,q,q,u,12,2w,2u,44,3me,b,14,c,e,m,10,2a,3g,fs,1k,u,i,i,i,y,s,1i
3g,16,10,12,1a,1m,54,5g,7s,3qm,3p,p,d,b,n,z,g,7a,fm,u,16,i,k,k,1m,14,22
3g,16,10,14,18,1o,52,5g,7s,3qo,3p,v,b,f,l,v,o,7c,fk,w,14,i,k,k,1m,12,26
30,u,o,s,u,14,3a,3q,5w,3oi,n,1c,k,w,b,1d,13,2i,fo,1m,s,m,k,o,1o,1m,12
2y,u,q,u,u,12,3g,42,72,3pu,o,1g,f,1m,1g,1f,13,1z,fy,24,s,o,k,q,2e,10,1a
3c,14,10,12,18,1m,4w,54,7m,3re,2v,21,5,j,l,1b,26,70,fk,w,10,i,k,m,1e,18,2a
2o,m,m,k,m,s,2a,2e,3w,3os,2k,g,1w,g,w,t,2g,2c,fw,1o,10,q,o,s,2q,14,12
2y,u,o,s,s,14,38,3o,5u,3om,d,1e,i,w,b,1h,15,24,fo,1o,s,k,m,o,1q,1m,10
2y,s,o,s,s,y,2u,2w,48,3ms,9,1k,e,o,q,u,w,5s,fq,1k,u,m,k,m,1e,1w,s
38,y,u,w,14,1e,4a,4o,7m,3pw,1l,12,2,z,z,23,3,2k,fs,1q,w,i,o,i,2c,18,q
2y,q,o,u,s,10,38,3k,6a,3q6,1g,1q,1p,1y,1q,p,h,1z,fy,26,u,s,i,q,2m,y,16
34,y,u,u,y,18,3e,3e,5a,3n0,1x,q,s,8,8,1e,2i,46,fm,1g,u,k,g,k,16,16,1c
3i,16,y,14,1a,1m,52,5g,7u,3qi,2t,15,f,f,f,t,2,6y,fm,u,14,k,i,k,1m,16,22
3i,16,10,14,1a,1m,56,5k,84,3oy,2n,x,7,9,h,13,k,5w,fk,w,14,m,i,k,1m,10,24
3e,14,y,14,18,1k,50,5e,82,3pw,35,3,h,b,l,1n,h,5u,fm,14,16,i,i,k,1k,1m,1a
36,10,u,w,10,1a,3k,3c,52,3ma,2b,k,k,k,a,1e,3w,4i,fm,1c,w,k,g,g,12,q,1i
30,u,q,u,u,12,34,30,4i,3ms,13,16,k,c,k,u,2a,3q,fq,1k,s,k,i,i,12,14,1e
3c,14,10,10,18,1k,4s,50,7k,3qq,2v,1p,6,l,h,17,26,6u,fk,w,10,i,k,m,1e,18,28
3g,16,10,14,18,1o,54,5i,7u,3qc,3j,v,9,d,l,x,i,74,fk,w,14,k,k,k,1m,10,26
3i,14,12,12,1a,1o,54,5g,7q,3qk,2f,1r,d,l,l,n,10,7a,fk,u,10,k,i,m,1k,16,2a
3g,16,10,14,1a,1m,56,5i,80,3po,35,r,b,b,j,13,g,6i,fk,w,16,i,k,k,1m,12,22
3e,16,10,14,18,1m,50,58,7q,3qo,39,1f,5,l,h,t,1u,74,fk,u,10,i,k,m,1g,18,2a
3g,16,10,14,1a,1o,52,5g,7q,3r4,3l,1b,b,j,l,p,14,7e,fk,w,10,k,k,k,1k,12,2c
2y,u,o,s,w,16,3k,40,68,3q2,5,18,a,2,13,2n,1l,3g,fo,1o,s,k,m,o,1k,1k,14
3e,14,y,12,16,1k,4y,58,7w,3q6,33,3,9,h,j,1l,n,68,fo,16,12,i,k,i,1k,1o,18
2u,o,q,o,o,u,2i,2o,4c,3qa,3i,1b,2k,26,2,2c,i,u,g8,2c,q,o,g,1m,20,y,u
3g,16,y,12,16,1k,4k,4w,7c,3ny,2p,z,q,a,j,6,1q,5y,fg,w,12,k,i,m,1g,12,26
3a,12,w,10,16,1i,4o,52,7y,3pm,2d,i,1,t,x,2j,i,4e,fq,1k,y,g,k,i,28,1a,s
3c,16,w,10,14,1e,48,4e,6w,3o8,3b,l,u,0,12,i,2e,4q,fi,w,14,k,i,i,1a,14,20
3c,14,10,12,16,1m,4w,54,7m,3r8,2f,27,7,h,n,1d,24,72,fk,w,10,i,k,k,1g,18,28
3e,16,10,12,1a,1m,50,58,7o,3ri,3b,1x,9,j,l,x,1w,76,fk,u,10,k,k,k,1i,16,2c
3e,16,10,14,18,1o,50,5a,7o,3rg,3b,1v,b,j,l,v,1u,76,fk,u,10,k,k,k,1i,16,2c
3a,10,u,y,14,1i,4g,4w,7o,3q0,25,m,c,l,11,2l,j,3i,fq,1k,y,i,m,i,26,1c,m
3e,18,y,10,16,1k,4i,4u,7a,3o0,2x,t,s,e,n,8,20,5q,fi,u,14,i,i,m,1e,14,24
3e,16,10,10,16,1k,4i,4s,7c,3nu,2r,x,s,e,f,6,1w,5q,fg,w,14,i,k,k,1e,14,24
3g,16,y,12,14,1m,4k,4u,7e,3o8,2z,r,q,8,v,6,22,5y,fi,u,12,k,i,m,1e,14,26
36,y,s,w,12,1c,4a,4i,6y,3q2,1r,c,a,v,f,2f,a,60,fm,1g,q,i,k,m,1c,1k,1k
2w,q,o,o,q,y,2m,2s,48,3ms,c,18,w,y,u,a,10,4i,fs,1k,w,m,o,k,26,1e,u
3e,16,w,10,16,1g,4c,4i,70,3ny,35,j,u,c,b,o,2k,5a,fi,w,14,i,i,k,1c,14,20
3g,16,10,14,1a,1o,56,5k,8e,3oe,2d,f,f,9,h,1l,3,5e,fk,y,18,k,i,i,1m,1i,1k
3e,12,w,10,14,1i,4k,4u,7u,3pk,1z,2,k,a,n,27,p,38,fs,18,w,i,i,i,1w,1q,w
3i,14,10,14,18,1m,50,5e,7y,3q8,2l,z,f,d,b,z,9,6k,fm,w,14,i,i,k,1o,1c,1s
36,y,s,w,y,1e,44,4e,6u,3pm,1n,c,k,6,15,2b,8,5g,fm,1e,s,k,i,m,1e,1i,1k
34,y,u,w,10,1c,46,4g,6u,3pw,1l,a,g,d,r,2f,8,5m,fm,1e,s,k,k,k,1e,1i,1k
36,10,u,w,y,1a,3y,42,6i,3oi,21,4,k,k,s,1t,w,4o,fk,14,12,m,g,k,1g,1i,1g
3e,14,y,12,18,1k,4s,50,7m,3qa,2r,1h,4,l,h,11,24,6w,fk,w,y,k,k,k,1g,18,26
3a,12,w,10,12,1g,4g,4m,76,3pq,2f,11,i,3,3,1l,1s,5q,fk,y,12,k,i,k,1e,1a,20
38,w,u,u,y,1c,3w,46,6o,3rm,1l,4,i,s,z,2n,k,3o,fm,14,y,i,i,k,1c,16,1w
32,w,s,u,10,1a,40,4e,6q,3ra,19,i,0,j,15,2r,7,4u,fm,1c,y,k,i,m,1k,18,1k
2q,o,m,m,q,w,2w,38,5a,3qw,2a,m,1i,6,l,3d,t,d,fu,1m,10,o,o,u,2g,10,u
2u,s,o,q,s,y,2o,2q,3s,3m2,i,s,o,k,s,w,1o,42,fu,1k,u,k,i,g,14,o,1k
2c,q,i,m,m,q,22,22,2w,3o6,1f,1o,m,12,q,1i,u,5a,fu,1u,m,o,e,g,o,18,g
2u,q,m,o,s,10,36,3k,5q,3ry,14,o,10,4,15,3n,1n,18,fq,1m,w,m,o,s,2c,w,y
2g,k,e,i,i,m,1s,1q,2o,3r6,16,3i,c,2a,1l,2c,2k,4g,fw,1y,w,y,q,1w,1y,14,32
2i,k,g,i,k,o,20,24,36,3sy,2o,2w,12,18,2d,8,1i,3c,fs,1s,y,w,q,1u,28,y,2i
2o,m,k,k,o,s,2e,2m,4e,3q0,2w,o,1y,c,i,1t,q,1,fw,1o,10,s,o,u,2q,10,y
2c,i,e,g,i,k,1o,1q,2i,3tm,q,4e,w,1i,1b,16,s,4q,fs,22,u,12,u,28,20,2g,24
2e,k,e,i,i,m,1q,1u,2o,3s6,1a,3y,a,26,21,24,1m,46,fw,1y,w,10,q,26,1s,1e,2y
24,i,e,c,e,i,16,14,1m,3qa,11,2s,34,u,m,5c,5i,76,fk,26,12,w,y,1o,22,1k,3a
2i,m,k,i,k,o,1w,1u,2o,3na,i,1a,1a,1q,16,q,2q,4k,fu,1u,u,q,k,o,2g,w,12
3k,14,y,10,16,1i,48,4g,70,3pq,1d,7,9,r,j,j,1,2o,fi,s,k,o,i,i,18,16,1u
2c,k,e,i,g,k,1i,1g,22,3qa,r,3q,m,1u,g,36,46,5g,fu,22,s,10,q,1c,2e,18,3a
2e,o,i,k,m,o,20,20,2s,3o4,11,1k,q,1i,10,1a,d,5a,fu,1u,q,o,e,i,s,1m,k
26,g,e,c,g,g,1c,1c,1w,3u0,l,3o,3k,e,5,3q,1w,64,fo,28,y,y,10,2a,24,3c,1g
2i,m,i,k,k,o,1u,1q,2k,3na,k,1i,16,1s,18,y,3e,5q,fu,1u,u,u,k,o,2m,10,14
28,m,g,g,i,k,1m,1i,22,3oq,25,3i,w,1i,1i,1g,2k,62,fq,22,o,u,m,k,24,16,y
2c,m,i,i,k,m,1s,1s,2g,3oc,1d,2m,m,1w,w,1o,u,5e,fs,1y,o,s,i,m,1i,1e,s
28,m,g,i,i,m,1o,1m,28,3os,29,3e,o,1q,16,1k,1k,5q,fq,22,m,s,m,i,1q,1c,u
2g,m,i,i,i,o,1q,1m,2c,3nm,6,22,u,24,14,1g,42,6i,fu,1w,u,u,m,q,2q,12,1e
2c,i,e,g,g,k,1m,1o,2g,3tq,e,4c,18,1c,x,1c,q,4y,fs,22,u,12,w,26,22,2q,1u
2w,s,o,q,u,12,3c,3q,5y,3pw,i,16,m,e,p,3b,1d,1y,fo,1o,w,k,m,q,1s,1i,10
2k,k,g,k,k,o,22,28,3g,3r8,2k,24,1a,1e,1r,s,16,34,fw,1s,10,w,q,1s,20,y,1g
2k,m,k,i,m,o,1y,1y,2y,3oe,20,12,1i,16,14,k,3a,5w,fw,1s,y,s,m,u,2s,18,1e
20,i,e,c,e,g,16,12,1i,3ou,3,1g,38,1e,1g,4c,54,7a,fe,26,1a,o,w,12,2k,1i,34
3e,12,w,12,16,1k,4u,50,7k,3q4,1t,v,d,l,n,1r,1u,6w,fm,w,y,k,i,k,1g,18,26
22,g,c,a,c,g,18,16,1o,3ui,a,2s,3u,1s,1b,3m,32,6q,fe,28,16,w,10,2i,32,2y,1e
2o,q,m,m,o,u,2a,2e,3c,3ma,1k,4,1k,q,16,14,1a,2s,fu,1o,w,m,k,g,1e,s,1q
2g,o,m,m,o,s,2a,26,36,3nk,n,u,w,g,k,1m,1k,5k,fu,1s,o,m,a,e,o,s,c
3g,12,u,10,12,1g,42,4c,6w,3qo,1l,j,k,8,p,n,i,2w,fk,u,u,i,g,i,18,16,20
2o,m,i,k,k,q,22,28,3e,3rw,24,1q,1u,1e,6,b,18,2g,fo,1m,y,s,q,u,2u,10,y
2g,m,g,i,i,o,1o,1m,2c,3nu,5,2c,q,26,10,1m,3s,60,fu,1y,s,u,m,q,2o,10,1i
2s,q,m,o,s,10,32,3i,5o,3re,1k,s,u,6,17,3b,1n,u,fq,1o,y,m,o,s,2c,y,w
22,g,c,c,e,g,16,16,1o,3ti,8,2o,3u,1g,x,4y,2w,76,fi,2a,14,w,y,2g,26,3k,1g
3k,12,w,10,16,1g,4c,4i,74,3py,17,j,m,3,x,j,1c,3w,fi,u,u,i,g,k,1c,16,28
2i,k,g,i,k,q,20,26,3e,3s0,2g,2q,u,1k,2b,o,12,34,fw,1s,10,w,s,1w,1y,y,1q
28,i,e,e,e,i,1i,1i,24,3ue,h,46,2m,k,1,1y,12,5e,fq,26,u,12,w,28,24,3c,1g
2a,o,g,k,i,o,1q,1s,2e,3og,1l,2u,k,1w,w,1q,u,5g,fs,1y,o,s,k,k,1i,1e,s
3m,14,w,12,18,1k,4k,4s,7g,3pa,x,h,i,z,x,r,1w,52,fi,u,u,g,k,k,1g,18,2a
2m,k,g,i,i,o,1y,1y,32,3s6,28,2i,1k,1y,9,7,1i,36,fo,1o,y,u,q,14,2s,16,w
2e,k,e,i,i,m,1u,1w,2s,3so,1k,3y,g,1w,27,1c,18,46,fu,1y,y,y,s,28,1u,1i,2e
3a,12,w,10,14,1i,4q,4w,7e,3qk,29,r,5,p,l,1x,1e,72,fm,14,u,i,k,k,1e,1e,1y
26,k,e,e,g,i,1c,18,1q,3qm,1t,3k,20,y,16,4c,4q,6w,fq,26,u,y,w,1g,24,1m,2u
2e,o,k,k,m,q,22,22,2w,3nw,v,1g,u,12,w,1e,s,58,fu,1u,o,o,e,i,w,16,i
3k,12,y,10,18,1k,4o,4u,7k,3pi,x,t,i,t,v,19,22,5u,fk,s,y,g,k,k,1i,16,2a
2m,m,i,k,m,u,2e,2o,4g,3ro,36,10,1s,0,h,1r,z,1,fw,1o,12,s,q,12,2m,w,w
2c,q,i,m,m,q,24,20,2y,3o8,1n,1u,k,10,m,1m,16,58,fs,1w,m,o,c,g,o,14,g
2g,m,g,i,i,o,1o,1o,2c,3nu,5,2a,q,26,y,1m,3i,5m,fu,1y,s,u,k,q,2m,10,1i
2m,o,m,k,m,q,24,26,32,3mk,1i,c,1q,y,1e,k,1w,3u,fu,1q,w,o,k,k,22,w,1a
2i,m,k,i,k,q,1w,1w,2q,3ng,6,1a,18,1m,12,s,2e,40,fu,1u,s,q,k,m,26,s,y
2k,k,g,i,k,o,20,22,36,3sm,2o,2g,1i,14,13,h,1u,3o,fs,1o,10,w,q,1c,2m,10,1w
30,s,m,o,s,y,2q,2y,4q,3rc,1,m,1e,6,a,3,r,r,fk,1a,10,m,k,k,1y,y,1e
20,g,c,c,c,e,14,10,1g,3ru,y,1i,3m,2i,v,64,4c,8a,fe,2a,1a,s,y,2c,28,3i,1m
2g,q,m,m,o,u,2a,28,3a,3ne,f,o,w,8,o,1k,20,5g,fu,1q,q,k,c,e,m,k,c
2c,o,k,k,m,s,24,20,2y,3oe,1x,1w,g,y,m,1o,1m,54,fs,1w,m,m,e,e,o,y,e
2e,k,e,i,g,m,1s,1s,2o,3s8,16,3y,e,20,1t,22,1m,4c,fw,1y,w,10,s,24,1u,1k,2m
2k,m,i,i,m,o,22,26,3g,3py,2i,1g,1k,14,1,8,1y,30,fw,1s,10,u,m,16,2k,12,1g
3c,12,w,10,16,1i,4s,4y,7g,3qc,1x,r,7,t,j,1z,1u,6o,fm,w,10,k,i,k,1e,18,26
2k,k,i,i,k,s,26,2e,3u,3sm,2w,22,18,o,23,h,a,1y,fw,1o,10,u,s,1s,24,w,1e
2q,o,k,m,o,u,2m,2w,4s,3si,28,m,1m,2,r,1h,x,g,fq,1m,10,o,q,10,2i,u,u
2e,i,e,i,g,m,1s,1s,2o,3su,16,42,m,1u,1r,1k,10,4k,fu,20,w,10,u,26,1w,22,24
2i,k,g,i,k,o,22,24,3a,3ru,2c,2q,u,1m,25,w,14,38,fw,1u,y,y,q,1w,1y,y,1w
2g,m,g,i,k,m,1q,1o,2c,3nu,7,28,s,24,10,1i,3c,5a,fu,1w,s,u,m,o,2k,10,1g
3e,12,u,y,14,1g,48,4i,72,3qo,1d,v,y,i,13,1d,1k,4c,fk,w,y,g,i,k,1c,14,28
38,y,s,w,y,1a,3w,44,6i,3rk,1j,2,i,10,t,27,c,3i,fm,14,y,g,i,k,1c,18,1s
2y,q,m,q,q,y,2u,32,4u,3ry,e,14,u,k,c,z,1l,1i,fm,1k,w,m,k,o,1q,1g,y
36,y,s,u,y,1c,40,4a,6s,3rq,1l,2,i,g,19,37,w,3u,fm,12,12,i,k,i,1k,y,1y
2i,m,k,i,k,o,1w,1u,2m,3n8,k,1e,18,1q,18,s,2y,50,fu,1u,u,s,k,o,2g,10,12
2g,k,g,i,i,o,1y,22,34,3t6,2c,3c,o,1g,2r,k,12,3o,fu,1s,10,w,s,26,1y,1a,22
2q,m,k,i,m,q,22,26,3k,3ri,1s,1e,22,14,16,7,1a,c,fk,1k,y,q,o,y,2e,1a,u
2g,i,e,g,i,k,1s,1q,2m,3te,1i,3s,1c,1s,11,4,20,3s,fo,1w,w,10,u,1o,2g,1a,2m
2o,k,k,k,o,s,2i,2u,4m,3s8,32,s,1u,5,11,23,13,1,fu,1m,12,q,s,14,2k,u,u
2g,k,e,i,i,m,1y,1y,2y,3t2,1y,3w,c,1s,2n,s,14,3w,fu,1w,y,y,s,28,1w,18,2g
2u,o,m,o,s,10,30,3g,5i,3qc,1k,y,y,g,v,31,15,o,fs,1m,10,m,o,q,26,18,y
28,m,g,i,g,m,1m,1m,26,3oq,25,3e,s,1m,1c,1g,22,5u,fq,22,m,u,m,i,1w,1a,w
2s,s,q,o,s,10,2o,2o,3s,3ly,k,q,o,i,o,18,1o,3w,fw,1i,u,k,i,e,w,o,1g
3g,12,y,12,16,1k,4w,54,7m,3ps,1h,v,l,h,p,1j,1q,6y,fk,w,y,k,i,m,1g,18,28
2a,o,i,k,i,q,1u,1u,2k,3om,1z,2m,i,1k,w,1o,s,5e,fs,1y,m,s,g,i,12,1e,m
2k,o,k,k,m,q,22,22,2y,3n0,u,m,1k,12,1a,c,26,3s,fu,1s,u,o,i,k,24,u,w
2i,o,i,k,k,q,1w,1w,2q,3n4,s,14,1e,1i,1c,k,2s,4q,fu,1u,u,q,k,o,2g,y,12
38,w,q,s,w,16,3a,3e,5e,3qo,1d,c,c,8,9,1b,17,2,fg,10,y,m,g,e,1a,12,1k
3c,12,w,12,16,1i,4s,50,7i,3qa,1z,t,9,p,l,1v,1w,6s,fm,w,10,i,k,k,1e,18,26
2g,m,g,k,i,m,1q,1m,2a,3o0,4,28,s,26,y,1k,44,6e,fu,1y,u,u,m,s,2q,12,1o
2e,k,g,i,i,k,1m,1i,24,3oo,j,36,c,24,w,2g,42,5w,fu,20,s,w,o,w,2m,16,2i
34,w,q,w,y,1a,3y,4a,6q,3rs,1d,c,8,6,1d,35,1,4c,fm,1a,w,k,k,k,1o,14,1m
2g,q,m,m,o,u,2c,26,3a,3ne,h,o,u,a,i,1o,20,5m,fu,1q,q,k,a,e,m,m,c
2s,s,m,q,q,w,2m,2k,3o,3m0,w,m,w,m,u,12,1g,40,fw,1k,u,k,k,e,16,q,1i
2e,m,i,i,i,m,1o,1m,28,3o0,b,2i,o,24,12,1s,3s,5s,fu,1y,s,u,m,q,2o,12,1q
28,k,e,e,g,i,1e,1a,1u,3r4,1n,3m,1o,18,16,3u,4e,6g,fq,28,s,10,u,1e,2c,1k,32
26,i,c,c,g,g,1e,1e,20,3uy,b,3y,3k,g,l,28,1k,5o,fk,28,w,10,y,2a,28,36,1q
28,k,c,e,g,i,1i,1i,26,3u2,l,48,22,w,6,1y,16,5e,fs,24,u,12,y,26,22,3a,1e
2g,q,m,m,o,u,2a,26,38,3ng,h,q,w,a,m,1m,1u,5i,fu,1q,q,k,c,e,m,o,c
2c,k,e,g,g,k,1q,1s,2k,3u0,10,4a,16,1g,1p,k,u,4e,fq,20,w,10,u,26,24,2c,28
2c,o,g,k,k,m,1u,1u,2i,3oa,1b,2e,m,1u,w,1m,k,5c,fs,1y,o,s,g,k,1e,1g,q
2m,m,g,k,i,q,22,24,36,3s0,2u,1u,1s,1a,0,17,1w,3y,fq,1m,10,u,q,u,30,10,y
2c,o,i,m,k,s,20,20,2u,3oe,1t,22,i,16,m,1q,y,58,fs,1w,m,o,e,g,q,18,g
2u,o,k,o,o,w,2q,2y,4s,3sc,1m,q,1g,4,3,1j,x,14,fo,1o,w,o,o,q,2a,14,y
3e,10,w,y,14,1i,4g,4o,7c,3qm,1d,t,m,d,13,1x,1u,5c,fk,w,10,i,i,k,1g,14,28
2o,o,m,k,m,s,24,26,30,3ma,2a,4,22,m,1k,g,2e,62,fw,1m,y,o,o,k,2k,12,1a
2a,o,g,i,i,o,1q,1q,2e,3ok,1r,30,i,1w,w,1u,u,5g,fs,20,m,s,k,k,1i,1g,s
26,k,e,g,i,i,1g,1e,1w,3og,1t,38,1q,y,22,18,3y,62,fm,24,s,s,q,k,2i,10,1e
2e,k,e,i,i,m,1q,1q,2m,3ru,12,3u,e,24,1r,2e,20,4g,fw,1y,w,10,q,24,1s,1g,2u
2o,s,o,q,s,10,2o,2g,3m,3lw,k,2,i,0,1,22,3e,6e,fw,1k,q,e,e,a,k,c,8
2k,s,q,q,s,12,2m,2g,3o,3lw,i,b,g,5,b,2o,3y,6i,fw,1k,m,e,a,8,e,8,4
20,i,e,c,g,g,18,12,1i,3mg,20,1m,38,w,1q,48,52,7u,fi,2c,14,o,u,u,2c,16,1c
1q,g,e,c,c,g,12,10,1e,3k8,3e,1m,1w,2q,1o,4u,4m,68,eq,2g,1e,s,i,q,1s,1a,14
2i,s,o,q,s,10,2k,2g,3m,3mg,0,7,i,9,9,2m,3o,6e,fu,1m,m,e,a,8,e,6,2
28,u,q,q,w,12,2s,2m,3q,3o8,47,c,6,n,x,22,3u,7s,fm,1o,a,c,2,2,4,4,6
1w,o,k,m,q,y,2s,2m,3s,3l2,8,i,6,d,z,15,26,76,f6,22,o,a,6,1,3,1,1
2e,q,m,m,q,u,2e,28,3c,3ne,r,12,u,3,a,1g,38,56,fu,1u,o,k,8,c,g,8,a
2c,q,m,m,q,u,2a,26,3a,3nk,15,1g,k,8,a,1k,38,4w,fs,1w,m,k,a,c,g,6,e
2q,s,q,s,w,10,2q,2k,3o,3l2,k,c,6,5,b,2u,48,6m,fw,1i,o,c,e,8,i,a,a
2k,q,o,o,q,y,2g,2c,3i,3mq,8,2,u,0,c,1q,2w,5u,fw,1m,q,i,c,c,k,c,8
2e,s,m,o,s,y,2i,2e,3k,3ne,1b,e,g,b,5,2e,3i,60,fs,1o,k,i,6,a,c,4,4
2u,w,q,s,w,14,2u,2o,3s,3ku,0,18,4,1,5,2e,3q,6s,fw,1g,s,c,g,c,m,i,g
1s,e,e,c,e,e,14,y,1e,3ka,3i,1i,20,2o,1k,4s,4w,68,es,2e,1g,q,k,q,1y,16,16
2q,u,o,s,u,12,2s,2i,3q,3l2,u,g,8,3,7,2a,3o,6u,fy,1i,s,c,e,a,k,e,a
2k,q,o,o,s,y,2i,2e,3i,3mi,e,1,q,1,2,20,36,60,fw,1m,q,g,c,a,i,a,8
2m,s,q,q,u,12,2m,2i,3o,3lk,m,5,e,5,9,2m,44,6k,fw,1i,o,e,c,8,e,a,6
2u,w,q,u,w,14,2w,2m,3s,3kc,8,18,2,3,7,2s,3u,76,fw,1g,q,e,e,c,m,i,c
1y,o,m,o,q,10,2u,2o,3s,3lq,x,16,n,5,17,x,2q,7o,f8,24,i,a,6,1,5,0,0
2g,q,m,o,q,u,2e,2a,3e,3n6,d,i,u,0,8,1u,2o,5q,fu,1q,o,i,c,c,i,g,8
2i,s,q,o,u,10,2m,2g,3o,3ma,0,d,g,9,9,2u,3s,6k,fu,1k,m,e,8,8,c,6,4
24,m,g,g,i,k,1m,1i,24,3o2,11,32,1m,1c,1o,1k,1w,4y,fo,28,q,q,m,k,1o,12,12
2k,s,q,q,u,10,2m,2i,3o,3lw,e,f,e,5,b,2w,3y,6m,fu,1k,m,e,a,8,e,6,4
24,m,g,g,i,k,1k,1i,22,3nu,z,2w,1o,16,1u,12,2i,4y,fm,28,q,q,m,i,1o,w,12
2o,u,q,s,u,10,2q,2i,3q,3l8,i,4,8,7,9,2w,4c,6m,fw,1i,o,c,c,8,g,a,8
2k,u,q,s,u,12,2o,2i,3q,3lm,6,d,8,9,b,3g,46,6u,fu,1k,k,c,a,6,c,8,4
2u,w,q,u,u,14,2w,2o,3u,3l0,5,1a,6,1,5,28,3q,6m,fw,1g,s,e,e,e,m,i,i
2u,w,q,s,w,14,2u,2m,3u,3ko,4,18,4,3,7,2k,3q,6y,fw,1g,s,c,e,e,m,g,e
2w,w,s,s,y,14,2y,2o,3y,3kw,d,1c,8,3,3,2a,40,6i,fu,1i,s,c,g,c,q,i,i
2k,q,o,o,q,w,2i,2c,3i,3mo,a,2,s,1,a,1u,2y,5u,fw,1m,q,i,a,c,k,a,a
2g,u,o,q,u,12,2m,2i,3q,3me,n,7,c,d,h,36,3i,6w,fs,1m,i,e,6,6,a,4,2
1w,k,i,k,o,u,2i,2i,3k,3kc,2g,p,1a,d,d,15,1c,5q,f2,26,w,a,a,2,3,1,3
2i,q,m,o,q,w,2g,2a,3g,3mw,1,a,s,0,4,1y,2w,5s,fu,1o,q,i,a,c,i,e,8
2g,q,m,o,o,w,2c,28,3c,3n8,b,i,w,4,g,1q,2c,5m,fu,1q,q,i,c,c,m,i,a
2e,q,m,o,q,w,2c,2a,3g,3nk,15,q,m,1,6,1w,34,5m,fu,1q,m,i,8,c,g,c,6
24,m,g,g,i,k,1k,1k,22,3o2,11,32,1m,1a,1o,1i,20,4y,fo,28,q,q,m,k,1o,10,12
2g,s,m,m,q,w,2e,2a,3e,3n2,9,g,u,0,8,1u,2q,5q,fu,1q,o,i,a,e,i,g,8
20,q,k,s,s,12,2w,2o,3u,3me,2d,1y,1f,b,1b,b,3e,7y,fa,22,e,a,2,1,3,0,4
1w,m,m,m,o,y,2q,2k,3q,3l2,m,c,2,3,x,15,24,6w,f6,24,o,a,8,1,3,1,0
26,m,g,g,i,k,1m,1m,24,3o8,1b,36,1g,1g,1i,1m,1i,4y,fo,28,o,s,m,i,1i,14,10
2u,u,s,s,w,14,2u,2m,3s,3kg,c,12,4,5,7,2q,3w,72,fw,1i,q,c,e,c,k,i,c
2e,s,o,o,s,y,2i,2e,3m,3nc,1b,c,g,d,5,2g,3g,66,fs,1o,k,g,8,8,c,4,2
2q,u,q,q,w,10,2q,2k,3o,3l4,g,g,8,5,7,2o,42,6o,fw,1i,q,c,c,a,i,e,8
2q,u,q,u,u,12,2s,2i,3q,3ks,g,m,2,7,9,32,48,6q,fw,1g,q,c,c,a,i,c,a
2k,u,q,s,u,12,2o,2i,3q,3lo,6,d,6,9,9,3e,44,6w,fu,1k,k,c,a,6,c,8,4
1y,i,e,e,e,i,1a,18,1o,3lm,20,16,38,16,1e,2e,48,5o,fa,28,16,i,o,o,1w,14,10
2g,s,o,o,s,y,2k,2e,3m,3ms,d,0,o,d,7,2g,3o,66,fu,1m,m,g,8,a,e,6,2
20,i,e,e,g,i,1c,18,1o,3mc,1a,1k,34,w,1k,28,4a,5a,fe,2a,12,k,o,o,22,12,10
28,s,q,q,u,12,2u,2k,3s,3o6,47,m,4,p,x,1q,3q,7s,fk,1s,8,e,0,4,2,4,4
2e,s,m,q,q,y,2i,2e,3k,3nc,19,e,i,b,5,2e,3i,62,fs,1o,m,g,8,8,c,6,2
2i,s,q,s,u,12,2o,2i,3q,3ma,n,l,4,7,f,38,3q,78,fs,1k,i,c,6,6,8,6,2
2s,w,q,u,w,12,2u,2k,3s,3kg,e,10,0,5,9,30,40,70,fw,1g,q,c,e,c,i,g,c
2o,s,q,s,u,10,2q,2i,3o,3li,o,0,4,3,9,2m,48,6k,fw,1k,o,c,e,8,g,a,6
2q,u,q,s,u,12,2s,2i,3q,3ky,o,i,4,5,9,2m,42,6s,fw,1k,o,e,c,a,i,e,a
1y,i,g,e,e,i,1a,18,1m,3m6,1k,1g,3a,w,1g,2c,4i,52,fc,2c,14,i,q,o,26,10,12
22,i,g,e,g,i,1e,1a,1q,3n4,i,2e,2m,u,20,24,3o,64,fk,2a,y,o,q,o,28,y,14
2i,q,o,o,q,y,2g,2c,3g,3ms,6,a,m,0,a,1o,34,5o,fw,1q,q,g,c,c,g,a,8
2m,s,q,s,u,12,2o,2i,3q,3lm,8,d,6,9,9,3e,46,6u,fu,1k,k,c,a,6,e,6,4
2o,s,q,s,u,12,2o,2i,3q,3lc,g,0,8,7,9,30,4a,6m,fw,1i,o,a,e,6,g,a,6
2a,q,i,m,m,q,22,1y,2w,3o8,1n,2a,k,16,q,1g,1s,4c,fs,20,o,o,c,g,m,m,s
2i,s,q,q,u,12,2m,2k,3q,3m8,f,f,a,b,d,38,3m,70,fs,1m,i,e,6,6,8,6,2
2a,u,q,q,w,12,2q,2k,3s,3ns,37,0,6,h,t,2g,3o,7m,fo,1m,c,e,2,4,4,4,4
26,s,o,s,u,14,2u,2m,3q,3ni,3z,w,5,n,13,1e,3u,80,fi,1s,a,c,0,2,0,4,4
2c,q,k,o,o,w,2c,28,3c,3oa,2b,1c,8,2,e,1o,3i,54,fs,1s,k,k,8,c,e,6,6
2c,s,o,o,s,10,2k,2g,3o,3no,25,i,a,h,f,2i,3a,6k,fq,1q,g,g,6,8,8,4,0
2w,y,q,s,y,14,2y,2q,3y,3l6,j,1c,a,1,3,20,42,64,fu,1i,s,e,e,e,q,i,m
2o,u,q,s,u,10,2q,2i,3q,3la,q,6,4,3,9,2k,46,6o,fw,1k,o,e,c,a,g,a,a
2i,u,q,q,u,12,2o,2i,3q,3ly,5,h,a,b,b,3c,3u,6y,fs,1k,k,c,8,6,a,6,4
2k,s,q,q,u,10,2m,2g,3o,3m0,m,3,a,3,5,26,42,6e,fw,1m,o,e,c,8,g,8,6
2u,w,q,u,w,12,2u,2m,3s,3ke,a,14,2,5,7,2u,3y,74,fw,1g,q,e,e,a,m,g,e
2k,u,q,s,u,12,2o,2i,3o,3lo,6,f,8,9,b,3g,44,6u,fu,1k,k,c,a,6,c,6,4
2s,u,q,u,w,12,2s,2k,3q,3ki,g,w,2,5,9,2y,44,6u,fw,1g,q,c,e,a,k,e,a
1y,i,g,e,e,i,1c,16,1o,3m4,1k,1g,34,12,1g,2k,4e,5i,fc,2c,12,k,o,o,22,12,10
2m,u,q,s,u,12,2o,2i,3q,3li,6,5,6,b,9,3e,4c,6q,fu,1k,m,a,c,6,e,8,4
2g,q,m,m,q,u,2c,2a,3c,3nc,l,m,w,2,a,1s,2i,5o,fu,1q,o,k,a,c,k,i,8
26,o,g,i,k,m,1o,1q,2c,3ou,23,3k,s,1w,12,1s,e,4s,fq,26,m,u,i,k,14,1e,u
26,m,g,i,i,m,1q,1m,2a,3oe,1n,38,10,1o,1c,1i,1g,4i,fo,28,o,q,k,i,1a,10,12
2s,u,q,u,w,12,2s,2k,3q,3kk,g,u,0,7,9,32,46,6u,fw,1g,q,c,e,a,i,e,c
2s,u,q,u,u,14,2s,2k,3q,3km,e,u,2,7,9,30,46,6u,fw,1g,q,c,e,a,i,e,c
1w,m,m,m,q,y,2q,2m,3s,3kw,k,c,a,b,z,19,22,76,f4,24,q,8,8,1,5,1,1
2o,s,q,s,w,12,2o,2i,3o,3le,c,0,4,9,9,38,4e,6o,fw,1i,o,a,c,8,e,8,8
1y,g,g,c,g,g,1a,14,1m,3lm,2a,14,38,1a,1a,2u,4w,5w,fa,2a,18,i,o,q,26,y,14
2i,s,m,o,q,y,2g,2c,3e,3ms,g,8,k,a,e,1m,22,5y,fw,1q,s,i,e,c,k,k,c
2i,s,o,o,s,y,2k,2c,3k,3mk,a,4,i,1,2,1u,3k,60,fw,1o,q,g,a,a,g,8,6
2a,q,k,m,o,s,28,22,36,3nw,1l,1w,e,o,i,1i,2y,4g,fs,1y,k,m,c,c,g,a,k
2g,s,o,q,u,10,2k,2i,3o,3mk,j,2,i,h,f,2y,3i,6i,fs,1o,k,e,8,6,a,6,0
2c,q,m,m,o,w,2a,26,3a,3nm,1b,1i,i,8,8,1s,36,4y,fs,1w,m,k,8,c,g,6,c
2w,y,q,u,w,14,2y,2q,3y,3ky,f,1a,a,1,5,2a,40,6e,fu,1i,s,c,g,c,q,i,k
22,i,g,g,g,i,1g,1c,1w,3n0,8,26,2i,y,1s,1u,34,5m,fg,2c,w,m,m,m,1q,16,y
2e,s,m,o,q,y,2g,2c,3i,3nk,1f,k,i,9,1,26,3i,5u,fs,1q,k,i,8,a,c,6,2
2u,w,q,u,w,14,2w,2m,3u,3kg,6,16,4,3,5,2m,3u,74,fw,1i,q,c,e,c,m,i,e
2m,s,q,s,u,12,2o,2i,3q,3lk,8,d,8,7,b,3e,48,6s,fu,1k,k,c,a,6,e,8,4
2i,o,k,k,o,u,2g,2q,4g,3pi,1g,1c,1c,o,1p,14,16,7,fq,1y,12,u,s,1s,24,o,14
1o,e,c,c,c,g,12,10,1g,3oi,4a,1m,9,1y,2g,6c,5g,8e,fe,2g,22,1e,1u,1a,2a,2m,1e
1y,i,e,e,g,k,1k,1g,26,3re,1s,y,2w,1n,1g,30,2q,6g,fm,2k,1e,12,24,16,1o,2e,1g
20,k,e,g,i,m,1s,1q,2g,3s0,g,26,2a,1n,8,1e,34,5o,fm,2g,16,12,1e,1g,1w,1i,2i
22,k,e,g,i,k,1s,1o,2i,3sa,9,2y,2c,2d,1q,1e,38,52,fm,2m,12,14,20,12,1q,16,2o
20,g,e,e,e,g,16,12,1e,3me,2e,1k,36,1a,q,6a,5o,80,fi,2c,18,q,w,1i,1u,18,2o
2a,o,i,i,k,q,1u,1u,2m,3ok,1p,2w,q,1i,12,1m,m,4g,fs,24,o,q,i,i,14,18,o
22,k,e,g,k,m,1s,1s,2k,3sq,b,2w,1u,1v,s,14,36,58,fm,2k,10,14,1s,1a,1o,1a,2m
1o,e,a,e,c,e,14,12,1g,3p4,4a,22,19,1m,2a,4w,5y,8k,fi,2g,20,1k,1u,14,3e,1o,1a
2w,10,u,w,10,1e,48,4k,6y,3pc,23,2,0,4,7,27,4,5y,fg,12,14,o,g,m,1e,1m,1k
20,i,e,c,e,i,16,12,1g,3mi,26,1q,36,10,u,62,5k,82,fi,2c,18,q,w,1g,1w,16,2o
2u,w,s,u,y,1a,3y,4c,6o,3o0,r,k,6,c,3,2f,j,4y,fi,1e,y,o,i,m,1k,1e,1e
2c,m,g,i,k,m,1q,1o,2a,3o4,11,3a,16,1w,u,20,30,66,fq,28,q,y,q,y,2a,12,1k
22,i,e,e,i,k,1i,1g,26,3qy,6,2o,32,1h,1s,34,3i,5q,fm,2k,16,12,1s,1a,1s,1s,2c
22,k,e,e,g,k,1i,1g,24,3qq,3,2s,34,j,10,3e,42,5g,fm,2k,14,12,18,1s,1u,1e,2q
26,k,g,i,k,o,20,1y,30,3sy,15,3w,k,b,11,s,3g,46,fm,2g,w,12,1c,1m,1o,y,30
2i,q,m,o,o,u,2a,2c,3a,3n0,i,m,12,s,m,1e,y,4i,fu,1u,w,m,i,e,1a,y,q
2y,10,u,y,12,1e,4e,4q,72,3o2,19,0,9,8,9,2j,g,6u,fg,16,12,m,g,m,1q,18,1i
2q,w,s,s,y,18,3s,48,6i,3pm,x,d,k,1,5,2f,7,44,fi,18,10,q,i,k,1w,1e,18
30,12,w,10,14,1g,4m,4u,7c,3p8,29,h,j,2,9,1x,1y,6i,fe,12,12,m,g,k,1c,1a,22
2g,o,i,k,k,o,1y,1w,2m,3n6,2,22,1i,1q,10,1e,2e,6e,fq,22,w,w,o,s,2g,12,1c
1s,e,e,e,e,i,1c,1a,1s,3pi,52,1b,1a,1a,4,44,42,7o,fm,28,22,18,1s,14,2a,2o,16
2c,m,g,k,k,s,26,2c,3m,3ro,b,3e,8,14,27,1c,28,20,fq,26,10,10,u,22,1u,o,28
1y,g,g,e,g,k,1m,1i,26,3s6,2a,e,2m,21,12,2a,30,6o,fm,2g,1k,10,20,14,1q,28,1o
1w,e,g,e,g,i,1i,1e,20,3ri,3u,v,24,n,g,3a,34,78,fm,2e,1u,10,1u,18,1u,2q,16
1u,g,e,c,e,i,1a,18,1s,3po,4i,t,2a,10,q,52,3w,7c,fi,2i,1u,12,20,1c,1u,2m,18
1m,e,a,e,c,e,12,10,1e,3pa,40,2s,1h,1k,2u,52,62,8q,fe,2k,1y,1m,1w,14,3g,1m,1c
2u,y,w,w,12,1c,48,4k,6y,3qa,1t,15,3,3,b,1j,g,5o,fe,10,12,o,i,k,1g,1k,1q
2g,q,m,o,q,y,2u,3a,5a,3ps,1,14,u,k,b,29,4,m,fk,1k,12,s,s,o,2s,w,u
2a,o,i,k,o,s,2e,2m,46,3r4,l,32,c,o,1h,j,20,1w,fk,22,u,10,s,16,2g,10,w
2g,q,k,k,m,s,24,24,30,3ne,1,1i,y,1a,y,s,1i,3u,fu,1y,s,q,i,i,1s,u,u
20,g,g,e,i,k,1o,1m,2a,3sc,12,1e,2o,2d,1i,1u,30,64,fm,2k,1a,12,22,14,1o,1y,1w
24,m,g,g,k,o,1y,20,2w,3sy,13,3q,q,b,v,14,38,3y,fo,2g,w,14,18,1q,1q,w,2y
1m,c,a,c,c,e,y,w,1a,3oc,38,3k,v,1e,3w,5i,78,9g,f6,2q,1u,1q,1y,12,40,1c,1i
1q,e,c,e,c,i,18,14,1m,3pi,58,6,3,1s,w,54,4o,86,fm,2c,24,1a,1q,1a,20,2s,18
3a,16,12,12,1a,1o,56,5g,86,3oa,29,1f,k,3,l,19,0,6g,fe,14,1c,i,i,i,1i,1o,1c
2a,m,g,i,i,m,1o,1k,2a,3ow,1d,3o,u,1s,1,3g,48,6c,fq,2a,s,12,u,1m,1y,14,2s
26,k,g,e,i,k,1g,1c,1u,3o8,r,3k,1y,10,1u,2s,40,7a,fo,2e,s,w,u,y,2a,14,1m
1w,g,g,e,g,k,1k,1i,24,3r0,30,b,2i,11,i,2y,2u,6q,fm,2c,1o,12,1m,1a,1y,2i,1i
1s,e,c,e,e,g,1a,18,1o,3p2,54,l,i,1q,g,4i,4k,82,fo,26,24,1c,1s,12,2g,2k,16
1s,e,e,e,c,i,1c,18,1q,3q0,5c,13,w,1c,8,4i,44,7u,fo,2a,22,14,1s,1a,1y,2s,16
22,i,e,e,e,i,18,14,1k,3n4,16,2c,30,10,1k,52,5q,8a,fk,2g,14,u,y,1g,1y,1a,2k
1s,e,e,e,e,g,1a,18,1q,3pu,58,z,1a,1g,i,4u,46,7q,fk,2e,22,14,1u,1e,1u,2o,18
32,12,y,10,14,1i,4q,50,7e,3o8,1h,15,8,4,b,1r,8,7e,fc,14,18,k,k,i,1i,18,1s
1y,g,e,c,e,g,14,10,1c,3ly,34,14,34,1w,y,6i,5y,8c,fg,2c,1c,o,w,1c,20,18,2g
24,k,g,i,i,o,1w,1w,2u,3sw,z,3m,12,p,d,14,3a,46,fo,2i,w,16,1a,1o,1q,w,2y
2m,q,o,q,s,10,36,3k,5s,3oa,1c,k,16,4,x,25,d,o,fo,1q,10,q,o,10,2i,i,u
28,m,g,i,i,m,1o,1o,2c,3ok,1h,3a,14,1k,1a,1o,1k,48,fs,26,o,u,k,k,1q,16,w
2c,m,i,k,m,u,2c,2g,40,3ro,b,32,a,u,23,6,2e,1c,fo,22,y,10,u,1o,1u,y,1o
1s,g,e,e,e,i,1c,1c,1s,3q8,58,1h,1c,u,4,48,3q,7o,fo,2a,20,12,1s,1a,1y,2s,16
26,k,g,g,i,m,1u,1u,2o,3s6,11,3w,16,7,1,1s,3a,4c,fo,2i,w,16,1a,1o,1u,w,30
1y,i,e,g,g,m,1m,1k,28,3se,1i,10,2o,2b,1c,22,3a,60,fm,2i,1e,12,22,14,1o,1o,26
2g,m,k,i,k,q,1w,1w,2m,3n6,2,24,1g,1u,w,1g,26,5w,fs,20,w,u,o,s,2e,10,1c
1y,i,e,c,e,g,16,10,1e,3m2,2q,1a,38,1a,1e,5c,5o,84,fg,2c,1a,m,w,12,26,18,1s
1u,e,g,e,e,i,1g,1c,1w,3qi,4s,1d,1o,e,4,3w,3g,7i,fo,2a,1y,12,1s,18,1y,2s,14
28,m,g,i,i,m,1o,1m,2e,3py,1b,3y,g,1s,p,3s,44,5m,fo,2e,u,12,w,1u,1u,14,32
2a,m,g,k,i,o,1s,1s,2o,3q2,t,3u,4,22,1j,3e,3w,56,fq,28,w,14,s,1w,1w,10,32
1u,e,g,e,e,i,1e,1c,1w,3r0,4o,17,1s,c,8,3u,3i,7g,fm,2e,1w,14,1u,1a,1u,2o,16
1u,e,e,e,e,i,1c,1a,1s,3q2,4q,13,20,y,g,4o,3s,7e,fk,2g,1w,14,1y,1c,1s,2m,18
26,m,g,i,k,q,24,26,3c,3rs,17,3w,g,e,1h,m,30,3g,fi,2a,w,14,u,1m,26,y,1s
1o,e,c,c,c,e,14,10,1g,3os,4c,1u,n,1w,2g,64,5g,8g,fe,2i,20,1g,1w,18,2e,2i,1e
22,k,g,g,i,o,1u,1u,2m,3su,h,32,1u,1z,g,s,3g,56,fm,2i,10,14,1o,18,1s,10,2y
1y,i,e,g,g,m,1m,1m,2a,3rq,1m,w,2m,1x,s,22,2u,6c,fm,2g,1e,10,1o,1a,1w,26,1u
38,14,12,12,18,1m,52,5e,7s,3oi,1v,1t,g,1,h,1b,g,76,fc,10,1e,i,m,g,1i,18,1u
1o,e,c,c,c,g,14,10,1i,3p0,4i,1m,n,1u,26,60,58,8c,fg,2g,22,1e,1u,1a,28,2o,1a
1w,g,g,e,g,k,1i,1g,24,3ru,2y,5,2i,1b,w,2u,36,6q,fm,2g,1m,12,20,16,1q,28,1m
22,i,e,e,e,i,18,14,1k,3mw,1g,28,30,u,1m,4s,58,84,fk,2e,14,q,w,18,22,18,28
2e,o,k,k,k,o,1y,1y,2o,3ng,5,22,1a,1s,w,1e,1u,4o,fs,22,s,u,m,m,26,12,10
22,i,e,e,e,i,18,16,1i,3mw,1g,28,30,u,1k,4o,54,7y,fk,2c,14,s,w,16,22,18,26
1u,g,e,e,g,k,1g,1e,1y,3rc,4a,17,22,b,a,3g,3a,7a,fm,2e,1u,12,1s,1a,1u,2q,14
1w,g,e,e,g,k,1i,1g,22,3r4,3k,p,28,v,k,38,2u,74,fm,2e,1q,10,1s,18,1w,2s,16
2m,s,o,q,u,12,3a,3q,5y,3ow,a,k,12,3,7,27,t,1g,fk,1k,w,s,o,m,28,18,y
1k,e,a,c,c,c,10,w,18,3os,34,3w,13,16,3s,5g,76,9g,f6,2s,1s,1u,1w,14,40,1c,1g
26,k,e,g,i,k,1m,1k,2a,3qy,15,3q,1o,s,2,34,46,58,fo,2g,y,14,y,1y,1u,14,32
2a,k,g,i,k,o,1s,1u,2q,3qw,x,3u,i,1k,19,2u,3u,4k,fq,2a,y,14,u,1y,1w,w,34
1w,g,e,e,g,k,1g,1e,20,3re,3i,j,2e,n,o,3g,32,6y,fk,2i,1q,10,20,1a,1q,2g,1c
2y,12,y,10,14,1i,4m,4s,7e,3qg,1v,1z,5,l,d,19,24,6e,fc,y,10,o,e,k,1g,1i,20
28,o,g,k,m,s,2a,2e,3u,3re,x,3i,a,m,17,9,26,2e,fk,24,w,10,u,1a,2e,10,1c
2a,o,i,i,k,m,1u,1s,2i,3og,1b,2y,u,1k,1a,1a,1g,46,fs,24,q,u,k,k,1u,12,u
28,o,g,i,i,m,1o,1m,2a,3oo,1j,3m,w,1q,1c,1i,2e,4u,fs,26,q,u,o,o,28,y,12
1w,e,g,e,e,k,1g,1e,1y,3rc,46,13,20,b,a,3g,38,7a,fm,2e,1u,10,1u,1a,1u,2q,14
1q,e,c,e,c,g,14,14,1i,3os,4s,o,8,1y,1m,62,4w,84,fg,2g,22,1a,1u,1e,1w,2s,1c
28,k,g,i,i,o,1u,1w,2u,3ry,15,46,k,q,x,26,3g,3w,fq,2e,w,16,12,1s,1w,u,32
2s,u,q,u,w,16,3o,48,6e,3ni,i,e,s,8,j,2n,v,38,fm,1i,10,o,k,o,2i,m,10
1o,e,c,c,c,e,14,12,1g,3p8,4i,1u,x,1q,2a,5k,5e,8g,fg,2g,22,1g,1u,18,2k,2e,1a
1s,e,e,e,c,i,1c,18,1o,3q2,5a,x,12,1e,e,4m,46,7s,fm,2c,22,16,1s,1e,1u,2o,18
28,k,g,k,k,q,24,24,3a,3sq,11,3w,0,k,1x,y,3a,2q,fm,2e,u,14,10,1w,1m,w,30
30,12,y,10,16,1i,4q,4s,7i,3qg,25,1v,5,l,l,15,2m,6c,fc,w,10,o,g,i,1g,1e,26
2a,m,i,i,k,o,1q,1s,2g,3om,1n,34,u,1m,18,1k,y,48,fs,26,o,s,k,k,1k,18,s
2u,u,s,u,w,18,3u,4c,6k,3na,4,m,e,8,5,35,r,4q,fk,1i,y,o,i,o,2c,o,14
28,m,g,i,g,m,1m,1i,20,3oi,1d,3u,18,1m,16,2g,3m,6u,fq,2a,q,y,u,y,2c,12,1o
1m,e,a,c,c,e,12,y,1c,3ow,3s,30,19,1m,3c,5c,6e,8y,fa,2m,1y,1m,1w,14,3k,1m,1e
1s,e,e,c,e,i,1a,16,1o,3pu,5a,n,u,1k,i,4s,4a,7w,fk,2e,24,16,1q,1e,1w,2o,18
2u,w,s,w,y,1a,40,4e,6o,3o8,x,e,6,a,1,2h,9,50,fi,1c,10,m,k,m,1i,1e,1e
2m,q,o,q,s,12,36,3o,5s,3oa,16,e,16,2,6,2r,p,18,fo,1m,y,s,o,o,2c,10,u
24,k,g,i,i,o,20,1w,2u,3sy,z,3o,10,r,r,s,3e,4y,fm,2g,w,14,1e,1i,1q,10,30
1o,e,c,e,c,g,16,14,1i,3oy,4k,16,r,1q,1m,4q,5k,8g,fk,2c,22,1i,1u,12,36,1w,18
2y,10,u,y,12,1e,4c,4o,72,3p2,25,2,9,8,7,27,q,68,fe,16,10,o,i,k,1c,1g,1s
1k,e,a,c,c,e,10,y,1c,3p8,3g,3o,1j,1a,3e,52,6o,92,fa,2q,1u,1s,1w,12,3y,1c,1c
22,k,e,e,g,k,1g,1c,20,3q0,9,2u,2y,g,m,48,4m,5s,fm,2i,14,10,y,1y,1w,18,30
1y,i,e,e,e,i,18,14,1i,3ma,20,1g,38,10,1o,3e,54,6u,fg,2c,14,m,s,q,2e,12,1c
2a,m,i,k,k,s,28,2e,3q,3sa,v,3g,2,y,1r,4,2c,1m,fo,28,y,10,u,1o,22,q,2e
1y,g,e,c,e,g,14,10,1c,3lq,38,12,30,1s,1c,5s,5s,7w,fe,2c,1c,m,u,12,28,18,22
1q,e,c,e,c,g,16,12,1k,3oy,4w,k,6,1w,1g,5y,4u,84,fi,2e,24,1a,1s,1e,1y,2q,1a
20,i,e,e,e,i,18,12,1i,3mo,1u,1y,32,y,1a,58,58,7y,fk,2c,16,o,w,1a,20,16,2a
36,16,10,12,18,1m,50,5a,7q,3pa,2d,1j,9,9,f,v,1s,78,fa,y,18,i,k,i,1i,10,2e
1y,i,e,e,i,k,1k,1k,26,3ry,1m,w,2y,25,1i,2c,2k,6m,fm,2k,1e,12,22,14,1o,2k,1c
1y,g,g,e,i,k,1m,1k,28,3r8,24,e,2w,1d,e,2a,32,68,fk,2e,1i,12,1g,1i,1w,20,20
2c,o,g,k,k,m,1u,1s,2i,3o6,z,2w,y,1o,1a,10,2a,4i,fs,24,s,u,k,m,2a,u,10
22,i,g,g,i,m,1s,1s,2k,3s6,2,2i,24,1j,2,18,38,5g,fm,2g,14,12,1c,1i,1w,1a,2q
32,14,y,10,16,1k,4s,4y,7k,3p4,25,x,l,1,9,1n,1w,76,fc,10,14,k,i,k,1e,1c,20
26,m,g,i,k,q,22,22,34,3ss,13,3u,g,1,1f,s,3c,40,fm,2e,u,14,18,1o,1o,y,2m
1q,e,c,c,e,e,16,14,1i,3p4,4u,u,5,1w,1i,5w,4u,86,fi,2e,24,1c,1s,1c,1y,2s,1a
26,m,g,k,k,q,22,24,36,3sw,19,40,6,c,1v,q,3e,42,fm,2c,u,14,14,1q,1o,10,2o
2y,10,u,10,12,1e,4g,4m,76,3q2,2p,9,9,0,h,23,14,6k,fe,12,10,o,i,i,1g,1m,1o
1q,e,e,c,e,g,1a,14,1o,3po,5a,5,8,1q,q,50,4i,84,fm,2c,24,18,1q,1c,1y,2q,18
26,k,g,i,i,q,1y,20,2y,3sy,13,3s,o,f,x,u,3e,48,fm,2g,w,14,1c,1k,1q,y,2y
30,10,w,10,12,1g,4k,4q,7a,3ow,21,7,j,6,b,21,1i,6k,fe,14,12,m,g,k,1c,1e,1w
2a,m,e,i,i,m,1o,1k,2a,3pe,1l,42,c,1w,n,44,48,5y,fq,2a,u,12,u,1u,1s,14,34
1y,g,g,e,g,k,1m,1k,28,3r2,28,c,2k,1f,o,2m,2k,6m,fk,2c,1k,14,1q,18,1w,2k,1i
32,14,y,10,16,1i,4s,50,7i,3oy,21,x,h,3,7,1n,1m,7a,fc,10,14,m,g,k,1i,18,20
26,m,g,g,g,k,1k,1g,22,3p2,1h,3u,18,1i,a,46,4m,6g,fo,2e,u,12,w,1s,1s,16,32
2c,o,k,k,m,q,20,20,2s,3o6,15,1y,u,10,1a,1k,8,4s,fu,20,q,o,g,g,y,16,m
1m,e,a,c,c,e,12,y,1c,3p8,3q,38,1h,1g,3a,56,6c,8w,fc,2m,1w,1o,1w,14,3m,1k,1c
1s,e,e,e,c,i,1c,18,1q,3pq,58,11,w,1e,6,4k,42,7w,fo,28,22,18,1s,16,20,2u,16
2q,s,o,s,u,14,3e,3y,64,3o4,m,e,12,6,3,2n,13,2a,fk,1g,10,q,o,o,22,18,y
2k,q,m,o,s,10,2y,3e,5i,3ou,14,w,1c,4,x,1l,9,8,fm,1o,y,u,q,w,28,14,u
2g,q,m,o,o,u,2c,28,38,3na,3,o,o,i,w,1k,s,5q,fw,1u,s,k,c,e,o,w,e
2a,o,g,k,m,s,2a,2e,3u,3s2,n,3g,3,12,2b,g,2o,1e,fo,26,u,12,u,1s,1s,y,2c
2s,w,s,s,y,18,3s,4a,6i,3o6,b,a,i,8,6,2x,j,46,fi,1c,10,q,k,m,1u,1a,16
28,m,i,i,i,m,1o,1o,2c,3om,1l,3c,12,1m,18,1m,1k,40,fs,26,o,s,m,k,1q,14,w
26,k,g,k,k,q,22,24,3a,3rs,15,3u,o,8,1b,m,2y,3m,fk,2a,u,16,u,1m,26,y,24
3a,14,12,12,18,1o,52,5g,80,3og,1x,1t,i,3,h,19,6,70,fc,10,1g,i,k,i,1i,1e,1m
2g,o,k,o,o,w,2q,32,4y,3q2,e,1k,s,g,n,1x,w,g,fk,1u,w,w,q,q,2q,10,u
2e,o,i,m,o,w,2k,2w,4s,3q2,7,28,q,g,t,13,w,5,fk,1s,y,y,q,10,2m,y,s
2i,q,m,o,q,u,2e,2a,3a,3n2,a,k,o,k,m,1e,16,5m,fw,1s,u,k,e,e,w,s,g
2o,s,o,s,u,14,3e,3w,60,3p0,b,q,s,4,9,23,15,2c,fk,1i,w,s,m,m,20,1e,10
2a,q,i,k,m,q,22,1y,2u,3oa,1l,2c,m,16,s,1k,1c,4o,fs,20,o,o,e,g,o,s,o
22,i,e,e,e,i,1a,16,1k,3mw,1c,26,30,s,1m,4a,4i,7e,fk,2c,12,q,u,12,22,16,1u
1m,e,a,c,c,e,12,y,1c,3p2,3q,32,19,1i,3a,5c,6c,8w,fc,2m,1w,1o,1w,12,3i,1o,1e
28,o,g,k,k,q,26,2a,3i,3si,z,3s,3,o,1x,10,2m,26,fq,28,y,12,12,1s,1s,o,2o
1o,e,c,c,c,e,14,12,1g,3p8,4g,1u,x,1s,2a,5q,5c,8e,fg,2g,22,1g,1u,18,2e,2k,1a
2i,q,k,m,o,s,28,28,36,3n8,8,10,10,y,s,12,1a,4a,fu,1w,u,o,g,i,1i,u,q
2k,q,o,o,s,10,32,3k,5o,3ok,18,i,18,0,0,2v,9,m,fo,1q,y,s,o,q,2o,o,u
1o,e,a,e,c,e,14,12,1g,3pa,4g,1y,11,1q,2a,5k,5e,8g,fg,2g,22,1g,1u,18,2m,2c,1a
1w,g,g,e,g,k,1i,1g,22,3rw,36,b,2e,19,q,2w,32,6w,fm,2g,1o,10,20,16,1q,2i,1e
2q,u,q,s,w,14,3m,40,6a,3oo,3,c,q,1,2,2l,r,30,fk,1e,y,q,m,m,1y,1e,12
1q,e,c,c,e,e,16,12,1i,3ou,4s,y,3,1y,1o,64,4y,86,fi,2e,24,1a,1u,1c,1y,2s,1c
2i,o,m,o,q,10,2u,3c,5g,3p8,e,u,1a,2,d,1z,0,7,fk,1k,10,s,s,o,2s,u,u
22,k,g,g,i,o,1u,1w,2q,3ry,b,2w,1y,r,t,12,3g,58,fk,2g,10,14,w,1w,1w,10,2o
2g,o,m,m,q,w,2s,34,54,3py,3,1i,q,k,d,1z,c,e,fk,1o,10,u,q,q,2s,w,u
26,k,g,g,g,k,1g,1e,1y,3o4,n,3e,1y,12,1y,24,3e,62,fq,2a,s,u,s,m,2c,12,14
1u,e,g,c,e,i,1c,1a,1s,3q2,4o,11,22,u,i,4m,3q,7e,fk,2g,1w,12,1y,1e,1s,2m,16
24,k,g,g,k,o,1y,1w,2s,3sw,v,3k,16,11,h,s,3g,4y,fm,2g,w,16,1g,1e,1s,10,30
1u,e,e,e,e,i,1e,1c,1s,3qo,54,1f,1i,q,0,42,3q,7k,fm,2e,1y,12,1s,1c,1w,2o,16
30,12,w,y,12,1g,4i,4w,76,3o0,19,l,8,4,9,21,5,6y,fe,18,14,m,k,i,1o,14,1m
2a,m,i,k,m,u,2c,2i,42,3r6,z,3g,2,u,1b,7,22,1u,fk,20,w,12,q,18,2i,10,14
24,k,e,e,g,i,1e,18,1o,3nq,0,34,2m,s,1m,46,54,7w,fm,2g,y,w,y,1e,1y,18,2g
3e,10,s,u,10,10,2y,30,4m,3se,p,n,l,1r,4,6,o,i,e4,c,a,4,c,a,g,8,1g
2u,o,m,m,s,10,2k,2q,48,3u2,12,v,5,1n,21,x,m,a,ea,u,a,c,8,2,w,8,i
2o,m,m,q,u,w,2m,2u,4u,3tg,22,2p,a,18,30,1g,2r,4d,ea,u,e,5,1,1,4,e,q
2m,o,m,q,s,y,2m,2w,4w,3t8,1e,1v,2a,1w,1i,o,39,43,ec,u,e,f,2,2,4,e,q
2o,m,m,q,u,w,2m,2w,4u,3tc,1o,1r,k,12,2o,1a,2v,49,ea,w,c,7,0,1,4,e,q
30,q,m,m,s,10,2k,2i,3u,3to,0,8,l,n,29,17,1o,1y,ec,q,e,e,4,8,12,a,m
3u,12,u,w,w,14,2w,2w,4c,3pi,f,3,a,12,1c,1w,2w,3y,ee,g,a,c,4,a,u,o,s
2y,q,m,q,u,12,2s,30,4q,3sw,4,1,n,19,15,1s,13,d,ee,s,a,c,6,2,k,8,w
2u,m,m,m,u,10,2o,2u,4i,3tw,1c,15,e,25,1l,o,6,1r,ec,s,c,a,4,4,c,4,o
3c,w,q,q,s,10,2o,2i,3u,3tk,11,d,c,5,7,11,1i,3e,e6,k,c,8,8,6,o,m,u
30,q,k,o,q,y,2i,2i,3o,3u0,4,6,p,n,23,1x,1i,2g,ea,s,c,e,4,6,14,e,k
3u,12,u,w,w,14,2w,2w,4e,3pc,h,3,i,16,1a,1s,2y,3w,ee,g,a,c,2,a,w,o,q
36,s,m,o,o,s,2a,2a,3e,3uq,n,q,j,8,g,20,g,2a,e4,o,e,a,a,8,e,k,s
3u,10,u,u,y,12,2w,2w,4c,3q8,f,1,1,k,1c,1y,2i,48,ec,g,c,c,6,a,s,s,o
3e,w,q,s,w,12,2s,2m,44,3t6,17,d,8,z,19,9,1u,2o,e6,i,a,6,8,a,m,c,14
3c,y,s,u,w,14,30,2y,4a,3rk,19,p,y,f,1b,6,2k,4y,ei,i,c,6,8,a,q,q,w
2s,o,m,o,s,10,2q,2w,4q,3tk,1i,15,v,15,o,10,l,35,ec,u,c,8,0,4,0,6,k
3i,y,u,s,y,12,2y,2u,46,3r4,x,r,10,a,5,o,2e,52,eg,g,e,4,4,a,o,u,s
2k,m,o,q,q,y,2k,2w,4w,3ty,1c,2p,2m,2q,1g,e,3d,4p,e8,s,g,f,0,2,6,g,q
32,s,m,o,u,12,2q,2q,4c,3t2,h,8,b,15,27,3,1m,10,ee,q,c,c,6,a,w,c,k
3g,w,u,q,u,12,2u,2s,4c,3ss,t,r,0,1,0,17,c,1o,e8,i,8,8,c,6,k,i,y
2g,o,m,q,q,y,2m,2w,4w,3uu,d,1r,40,2g,g,g,3f,53,e4,u,g,l,2,4,6,e,u
3o,12,u,u,y,12,30,2u,4a,3pu,p,9,u,14,o,16,2q,4k,eg,g,c,6,2,a,u,q,s
3u,12,u,u,y,12,2y,2u,4e,3pq,f,1,4,y,1c,20,2q,40,ee,g,a,c,4,a,u,o,s
3c,w,q,q,u,y,2k,2m,40,3ti,19,d,2,d,6,18,0,2e,e2,m,8,6,e,6,m,k,w
2c,q,m,q,q,w,2o,2w,4w,3vy,2j,q,3a,32,t,a,2x,5j,ec,m,g,j,5,c,4,e,u
2w,q,k,q,s,10,2s,2s,4o,3tc,o,h,r,1b,v,c,10,2b,ee,q,e,a,6,6,c,4,s
3a,s,m,o,q,s,2a,2a,3g,3ug,r,e,1,2,i,2o,1e,1y,e8,o,c,a,a,a,g,i,u
3o,12,u,u,w,14,30,2w,4a,3po,n,b,y,16,a,w,30,4m,eg,g,e,6,2,a,u,s,q
3e,y,s,u,u,12,2u,2y,4c,3sq,13,r,4,v,d,2,3,1o,e2,i,6,6,e,8,i,c,18
3e,w,o,o,s,u,2c,2g,3i,3te,j,7,e,1,s,3k,1q,32,ec,m,c,8,e,6,i,k,u
2e,q,k,q,q,y,2m,2y,4w,3v4,1l,m,38,2y,v,g,33,59,ee,m,g,j,3,a,4,e,s
36,s,k,o,o,s,2c,28,3c,3um,j,w,l,c,u,6,w,2q,ea,o,e,a,a,6,e,k,o
34,w,o,q,w,12,2y,2y,4k,3sg,13,0,e,1l,1d,b,1y,34,ee,q,a,c,6,8,y,e,s
2o,m,m,s,s,y,2m,2w,4u,3t6,1o,1d,k,1k,22,12,2v,43,ec,u,c,7,0,0,2,e,s
2o,m,o,s,s,w,2m,2y,4w,3ss,2c,3j,1o,2q,24,w,3d,43,ee,u,c,b,0,0,4,g,s
3i,w,o,q,s,y,2g,2g,3o,3su,9,l,8,6,m,32,2c,3o,ec,i,e,a,a,8,k,o,s
30,q,m,m,q,w,2g,2c,3e,3ui,0,e,x,2,1b,1b,0,3c,e8,q,c,c,6,4,e,s,m
3g,w,q,q,s,y,2k,2i,3q,3si,l,h,a,g,1c,26,1w,3q,ee,k,c,8,a,4,k,o,s
3c,s,o,o,o,u,2a,2c,3e,3u4,p,8,4,2,q,2s,1i,2c,ec,m,e,8,c,6,i,k,q
3e,y,s,s,u,16,2y,2w,4g,3r8,z,l,o,u,1j,4,2q,44,ei,i,e,8,6,c,w,k,u
38,w,o,q,w,12,2q,2o,3w,3sq,1b,1,m,p,17,x,24,3w,ee,m,c,8,6,6,w,m,w
38,u,m,o,q,w,2g,2e,3e,3to,v,g,7,m,8,9,1e,34,ee,m,c,c,8,2,i,o,s
2g,o,m,q,s,w,2m,2y,4w,3us,j,l,4e,28,d,4,33,5d,ea,q,g,n,0,8,6,e,u
2o,m,m,q,u,w,2m,2w,4s,3tk,2a,2r,d,q,2q,1u,1z,4h,ea,u,c,0,1,3,2,c,q
3e,u,o,q,q,u,2c,2c,3m,3to,n,5,a,0,e,2y,26,2k,ea,m,c,8,e,6,k,m,s
2m,o,m,q,u,w,2m,2u,4u,3tg,1w,2n,q,1e,2u,1a,2x,4b,ea,u,e,7,0,3,6,e,q
2o,m,m,o,u,y,2m,2w,4s,3to,22,1d,13,g,1k,2k,23,49,e8,u,c,0,0,1,2,a,o
34,s,m,o,s,10,2m,2i,3q,3tc,l,i,f,l,1j,1v,1u,2q,ec,q,c,e,4,6,12,g,q
2i,o,m,q,s,w,2m,2w,4w,3ua,2,17,3o,2g,a,a,39,4z,ea,s,e,j,0,6,6,e,s
2k,o,k,s,s,w,2m,2u,4u,3ts,1o,1h,n,22,2e,1y,39,4n,e6,u,c,3,5,2,2,g,o
34,s,k,o,o,w,2e,2a,3e,3ua,f,u,r,m,n,r,k,30,ea,q,c,c,8,4,e,o,o
30,q,k,o,o,y,2e,2c,3e,3um,2,6,x,3,17,13,1,3g,e6,q,c,c,4,6,c,q,o
36,s,m,m,o,u,28,2c,3c,3us,l,s,j,a,k,1m,o,26,e6,q,c,a,a,a,c,k,q
2y,q,k,o,q,y,2i,2g,3m,3u6,c,3,r,r,27,1t,1m,2u,ea,q,c,e,4,8,10,i,g
2k,o,m,q,s,y,2m,2w,4w,3t4,18,1x,2u,1u,1s,g,3h,47,eg,u,e,j,2,0,6,e,s
20,i,k,i,m,u,2a,2k,4a,3y8,25,27,1m,30,12,m,2d,4h,du,1c,a,8,d,3,0,c,m
2g,o,m,q,q,y,2m,2w,4y,3vc,1d,9,4g,26,d,2,31,5h,e8,q,g,l,1,a,6,c,u
1q,c,8,a,a,c,18,1a,1u,3w8,22,6,0,2k,2e,1x,x,1o,cg,2m,12,o,k,i,q,4,8
1w,e,e,c,e,o,1o,1m,2q,3we,14,h,14,i,19,22,18,1h,dw,1m,m,g,g,1,f,2,q
1s,c,c,c,c,k,1g,1e,28,3x4,1u,1d,4,14,z,12,1c,g,d2,28,u,g,i,c,2,2,s
24,k,i,m,o,w,2e,2q,4k,3x8,2v,1,k,3g,1a,k,2t,4z,e8,14,g,3,h,3,4,c,k
1u,e,c,c,e,m,1o,1k,2o,3ws,1g,19,i,10,f,k,1o,r,dk,1w,o,g,g,1,7,2,k
1k,a,8,8,a,8,q,o,1a,3ua,1m,1a,c,1e,3u,7o,3m,2n,c2,2k,12,y,w,i,1e,w,12
1q,c,8,a,c,c,1a,1a,1u,3wk,1y,0,1,24,1i,1b,7,1m,cg,2m,12,m,m,i,o,2,e
1u,e,a,c,e,k,1m,1k,2g,3wm,1o,17,18,1a,z,i,1a,d,dq,1q,q,g,i,6,b,0,q
22,i,i,k,o,s,2a,2k,4a,3x8,29,t,s,28,28,14,2f,4j,e4,1a,c,6,h,7,2,c,k
1i,c,8,6,a,a,o,o,14,3uk,10,1e,s,s,3q,7i,3s,i,by,2m,12,w,10,m,1e,10,14
22,i,i,k,o,s,2a,2k,4e,3x2,1z,9,6,y,3e,y,2b,4l,e6,1a,e,6,h,9,0,a,k
2c,o,k,s,o,y,2o,2w,4w,3wc,37,1g,2e,3q,1b,i,2v,5j,ec,m,g,h,7,c,4,e,s
2a,o,i,q,q,y,2k,2w,4u,3w8,2n,u,2i,2m,7,k,31,55,eg,q,k,n,7,8,2,e,q
1o,a,a,8,a,c,10,16,1q,3w8,1w,s,t,2e,2u,o,15,7,ca,2k,14,u,o,i,16,e,c
28,o,k,q,q,w,2o,2w,4w,3xk,4t,24,1o,4c,1b,c,2t,5l,e6,s,e,d,d,e,4,e,s
1w,g,c,g,i,o,1s,1u,36,3x0,g,19,4,4,1m,14,14,2f,du,1m,i,e,4,9,3,4,k
24,k,i,o,o,u,2i,2s,4o,3xw,47,o,1m,3s,l,q,2n,55,e0,16,c,3,h,6,0,e,o
26,m,i,q,o,w,2k,2s,4s,3x4,3b,q,1a,3y,j,u,2z,55,ea,y,g,d,d,6,2,c,o
3u,12,s,w,y,14,2w,2u,4g,3pu,b,2,q,z,f,1q,32,4s,eo,g,8,a,8,c,k,q,m
3m,10,s,w,y,16,30,2w,4a,3pm,j,3,10,h,r,q,38,54,eo,i,c,6,4,e,w,o,s
34,u,o,s,u,12,30,34,54,3se,17,5,b,1,7,1,1i,y,eu,w,a,i,a,c,q,e,q
3w,12,u,w,w,14,2y,2u,4c,3oo,7,2,w,18,1c,1q,3e,3k,ek,i,a,a,4,8,y,m,o
3c,w,s,u,10,16,2y,30,4s,3rg,1h,4,x,1x,1l,38,2k,3i,eq,m,e,c,8,o,k,e,o
2s,u,o,u,y,y,2y,34,5e,3ro,1c,2u,2j,w,1u,c,2,2l,ey,m,0,8,1,0,g,o,u
40,12,u,u,y,12,2y,2w,4c,3p2,3,2,c,q,1g,1y,3e,3u,ek,i,a,c,8,8,y,k,o
40,12,w,u,w,14,2y,2u,4c,3os,3,4,e,14,1g,28,3i,3i,ei,i,a,c,8,8,w,k,q
3o,10,s,w,w,14,30,2w,48,3pi,f,7,18,i,j,o,34,4w,em,g,e,6,4,c,w,o,s
3i,y,u,u,y,14,32,2u,4c,3q4,n,9,12,15,1b,16,3c,4m,eo,i,e,6,6,g,u,m,s
40,12,u,u,y,12,2y,2w,4c,3p4,3,2,e,m,1g,1u,3e,3y,ek,g,c,c,8,8,y,m,m
32,u,q,u,w,12,34,3a,5c,3s0,x,a,9,2,6,10,12,1j,es,10,4,g,0,8,k,g,s
2y,s,s,q,y,12,34,36,5g,3s0,b,1m,12,z,o,12,i,31,eu,10,3,a,4,4,g,o,s
36,u,q,u,w,12,36,32,50,3s2,1b,5,3,13,f,g,2g,1k,es,u,c,c,e,g,g,g,s
3u,12,u,w,w,14,2y,2u,4c,3oq,b,4,10,18,u,1i,3g,42,em,i,a,8,4,a,y,m,o
3k,y,s,s,w,10,2u,2w,4e,3r0,l,p,1,a,m,1m,2s,48,eu,i,e,c,8,a,w,i,i
40,12,w,u,w,14,2y,2u,4c,3oy,5,4,c,y,1i,28,3g,3k,eg,i,a,c,8,a,w,k,q
38,u,q,u,y,12,36,32,4w,3ry,1d,1,d,2h,0,k,2s,2e,es,s,e,a,m,a,g,k,u
3y,12,u,u,w,14,2w,2y,4e,3pm,5,2,a,g,u,16,2m,4o,eg,i,a,c,8,a,u,o,m
3y,12,u,w,w,14,2y,2u,4c,3om,5,4,m,18,1g,20,3g,3i,ek,i,a,c,4,8,y,m,o
3y,12,u,u,w,14,2y,2u,4e,3pm,5,2,e,g,8,16,32,50,ei,g,c,c,6,c,u,m,k
40,12,w,u,w,14,2y,2u,4e,3p4,1,2,c,o,1i,20,3g,3q,ei,g,c,c,a,8,w,m,m
36,w,o,s,w,12,32,30,4w,3s8,1d,9,8,h,v,5,2a,2q,eu,q,e,e,a,k,k,g,s
2y,s,q,s,y,12,32,36,5i,3s0,9,2i,8,13,10,i,w,2z,ew,w,3,c,2,4,g,q,q
3y,12,u,w,w,12,2y,2w,4e,3pa,3,2,c,e,18,1i,38,4g,ek,i,a,c,8,a,w,m,k
3o,10,s,u,u,12,2w,2w,4c,3qi,b,f,3,6,o,1w,34,48,es,i,e,a,8,a,w,k,i
2y,u,q,s,w,12,34,36,5g,3s2,d,1i,10,t,e,s,y,2t,eu,10,3,a,4,a,c,m,u
3c,w,q,s,s,12,2w,2w,4g,3s0,19,j,4,6,5,2,2a,4w,ew,k,e,c,8,i,u,i,m
40,12,u,w,w,14,2y,2u,4e,3pa,3,2,c,i,1i,1w,3c,3w,eg,i,a,c,a,8,w,o,m
3s,12,u,w,y,12,30,2y,4k,3q2,b,7,d,h,w,6,2e,4k,eo,g,8,a,8,a,i,o,q
2i,m,m,q,s,w,2m,2w,4w,3uc,16,1n,x,30,2g,1e,3d,4p,e2,u,e,5,7,2,6,e,q
2g,o,m,o,s,y,2m,2w,4w,3um,q,1x,16,34,1e,u,3j,4r,e2,u,e,9,5,4,6,e,s
2m,o,k,m,u,10,2q,2u,4o,3ti,1k,1f,g,2l,13,2a,5,2p,e0,w,c,4,6,2,4,8,o
2a,q,k,q,q,y,2m,2y,4w,3xe,4f,1k,2s,3o,1f,e,2x,5n,e4,q,i,l,5,c,4,e,w
2c,m,k,o,s,w,2o,2w,4w,3w2,n,p,23,2k,3w,g,33,4t,du,w,i,2,f,0,6,e,q
2s,m,m,m,u,10,2m,2s,4g,3ty,1k,1j,5,21,23,k,o,11,e8,u,a,a,4,4,i,6,i
2w,o,m,o,s,y,2i,2e,3i,3u2,o,11,f,1t,1x,25,1m,48,e4,q,c,a,4,6,u,s,m
2y,q,k,o,q,y,2i,2c,3e,3uc,c,f,v,p,1h,1h,6,3m,e4,q,a,c,4,4,g,q,s
26,o,k,o,q,y,2m,2w,4w,3ym,3x,f,24,4o,1h,a,2x,5h,dw,10,e,b,d,a,4,e,u
20,g,i,i,m,s,2a,2i,46,3zk,1n,25,t,12,3u,5,1t,4l,da,1m,g,c,5,f,6,8,m
3c,w,q,s,s,y,2s,2o,42,3t8,19,7,0,8,k,1l,2,2a,dy,m,8,a,a,6,o,i,10
26,o,i,q,o,y,2m,2w,4u,3ye,4x,1c,1w,4m,1t,c,2t,5f,e0,w,g,b,f,e,2,e,s
2w,q,m,o,q,y,2g,2c,3g,3u2,g,r,p,p,1f,1d,6,3m,e0,q,c,a,2,6,i,q,q
3g,y,u,u,w,14,2y,2y,4i,3rm,3,j,8,1h,t,b,o,1k,dw,g,a,8,a,c,i,a,1e
2u,o,m,o,s,10,2i,2e,3m,3ts,u,19,9,1x,25,1l,16,3y,e2,s,a,8,6,6,o,y,m
3a,w,q,q,q,y,2g,2i,3w,3te,15,1,4,7,c,28,5,28,du,m,c,8,c,a,k,q,u
30,q,m,m,o,w,2e,2a,3a,3uq,2,a,x,2,n,j,a,3a,e2,q,c,e,4,6,c,g,u
36,s,m,o,o,u,28,2c,3e,3ui,p,o,l,6,i,2e,g,2c,dw,q,c,c,a,a,g,i,w
2w,q,m,o,q,y,2i,2c,3g,3u4,g,r,n,13,1h,1r,m,3m,e2,q,a,c,2,6,i,s,s
30,q,k,o,o,y,2e,2c,3c,3uk,6,1,11,0,1d,v,8,3c,e4,q,a,e,2,6,e,i,s
26,m,k,q,o,y,2o,2w,4u,3yo,45,5,24,4u,1t,c,2x,5h,dw,10,e,b,d,c,2,e,u
22,k,k,m,o,u,2k,2q,4m,3z6,3d,1f,1a,3a,10,3,2p,59,dq,1a,a,4,j,2,2,e,q
2w,o,m,o,s,y,2i,2e,3g,3u4,m,z,h,1p,1v,23,1e,44,e4,q,c,a,4,6,q,u,m
3c,u,q,q,s,y,2i,2g,3q,3tg,17,5,a,9,6,s,1e,36,dy,k,c,8,c,8,o,m,w
36,s,m,o,o,u,28,2c,3e,3uc,p,o,j,8,g,2k,g,28,du,q,c,c,a,c,e,i,y
36,s,m,o,o,u,2a,2a,3g,3um,p,o,j,6,g,2c,g,2c,e2,o,e,a,a,8,g,i,u
2m,m,k,q,u,y,2m,2u,4u,3to,1w,1j,15,w,2i,22,25,4p,e4,u,c,0,1,1,2,c,q
24,i,i,k,o,u,2k,2q,4o,3za,2d,17,1r,1w,42,j,2l,4r,dk,1a,i,a,d,d,4,c,o
38,s,m,o,q,u,2a,2a,3i,3u6,r,g,d,4,e,2m,y,2e,du,q,c,c,a,a,i,k,w
34,q,m,m,o,s,2c,2a,3c,3uw,f,w,v,c,k,i,g,2q,dy,s,c,c,8,a,c,k,o
3g,y,u,s,w,12,2w,30,4i,3rq,d,d,8,z,r,3,2,1a,dw,i,8,8,c,c,i,a,1c
36,s,k,o,o,u,28,2c,3c,3uk,j,s,p,8,g,1w,g,2g,dw,q,e,a,a,a,e,k,s
32,q,k,o,m,u,2c,2a,3a,3um,0,g,p,6,8,7,k,2y,du,s,c,e,6,a,a,g,s
2i,m,o,o,s,w,2m,2y,4u,3ua,14,2x,2c,2w,1k,o,3l,4p,e4,u,e,d,1,2,8,e,q
3e,w,s,q,u,10,2o,2o,46,3sq,13,b,e,h,3,e,6,1w,dw,k,a,8,c,a,m,g,12
2u,o,m,m,s,10,2i,2k,3y,3u2,12,1b,5,23,2z,h,28,1m,e8,s,c,a,6,6,12,a,i
26,o,k,o,o,y,2o,2w,4u,3yo,51,y,26,50,2b,e,2t,5j,dy,y,e,d,d,g,0,e,u
24,i,k,k,q,s,2k,2s,4o,3za,2z,1z,a,36,2o,r,2n,55,do,18,c,8,h,5,6,c,q
2c,m,m,q,q,y,2m,2y,4w,3ws,17,25,1w,3w,8,k,3b,59,dy,w,g,d,7,6,6,e,u
2y,o,m,o,q,y,2g,2c,3e,3u2,e,n,r,j,1d,19,8,3i,e0,q,a,c,2,6,g,o,s
2y,q,m,m,q,y,2i,2c,3g,3ue,c,d,v,p,1j,1v,c,3o,e6,q,a,c,4,6,g,w,m
32,q,k,m,o,u,2e,28,3a,3us,1,g,t,8,3,9,i,32,dy,q,e,c,6,8,a,i,q
3a,w,o,q,s,w,2k,2g,3u,3to,17,3,0,8,m,4,o,2i,dy,m,c,8,c,6,o,k,w
2i,m,k,q,s,y,2m,2w,4w,3u8,10,17,1j,1e,2y,24,2n,4p,e0,u,e,0,5,1,4,e,o
2w,o,m,m,s,10,2i,2g,3m,3u4,q,z,f,1r,29,1t,1m,3e,e6,s,a,c,4,6,u,q,k
2y,q,m,m,q,y,2i,2c,3e,3ue,a,b,v,p,1j,1r,c,3m,e6,q,a,c,4,4,i,u,o
34,q,k,o,m,u,2c,2a,3a,3uy,b,w,t,e,a,0,c,30,e4,q,c,c,8,8,c,k,m
34,10,w,10,u,10,2y,36,4q,3q0,e,25,3b,24,1w,24,0,19,d4,i,a,8,c,a,i,a,1s
2w,u,o,s,s,y,2q,2o,3y,3q4,a,0,6,a,l,7,a,24,cw,10,a,8,8,c,m,k,s
2y,y,u,10,w,y,2w,34,4o,3p8,1m,2p,39,1m,26,34,9,c,d0,k,8,a,8,a,i,a,s
3a,y,s,q,u,10,2u,2s,42,3ri,z,a,a,5,8,z,5,30,de,q,a,a,c,8,q,i,u
2q,s,m,m,q,s,28,28,3a,3r2,1i,t,8,5,c,38,1s,2a,cs,12,e,c,a,c,g,k,w
38,14,u,10,y,10,32,38,4s,3qi,9,17,2t,u,1q,w,3,d,d6,g,c,a,a,c,k,a,1g
22,k,e,g,k,m,1s,1s,2w,3x2,21,s,6,z,2,7,1y,1k,c2,1q,i,o,c,8,k,6,i
32,s,m,o,o,u,2a,2a,3e,3sy,0,q,l,e,o,1y,k,2a,dc,w,c,c,a,a,i,i,w
2q,q,m,m,o,u,2e,2a,3a,3s8,1e,z,1,r,o,6,s,20,dc,w,c,8,8,6,c,g,q
22,g,e,c,e,g,18,1a,22,3ya,p,10,14,a,6,22,1o,16,ce,1s,m,o,i,c,y,k,s
32,12,w,10,w,10,2y,38,4s,3pq,a,2p,2p,28,22,1w,9,6,d4,g,a,8,a,a,k,a,10
3c,y,q,s,w,12,2s,2o,46,3rs,x,2,e,h,n,7,m,2k,dg,q,a,8,c,8,s,i,12
2u,q,m,m,q,u,2c,2a,3a,3sm,12,j,9,b,g,c,s,26,de,u,c,a,8,8,c,g,s
2i,o,m,m,o,u,2e,2a,3a,3sy,0,z,5,l,o,6,w,24,cw,14,e,8,a,2,c,g,q
2c,o,i,m,k,s,28,26,36,3v6,19,4,z,2,h,a,e,1e,cw,14,m,a,8,6,8,e,o
2g,o,m,k,o,s,2c,28,38,3tm,4,n,9,p,4,6,k,1s,d0,12,i,8,a,4,a,e,o
2w,s,m,m,o,u,2c,2a,3a,3se,s,0,3,8,c,q,u,2a,dc,u,c,a,8,a,g,g,s
2y,s,k,o,m,u,2c,2a,3c,3tg,i,6,f,8,6,g,q,2g,dg,u,c,c,8,a,e,g,s
2w,q,m,o,o,u,2c,2a,3a,3ta,q,7,b,5,a,8,o,2e,dg,u,e,a,8,8,e,g,q
2s,q,m,m,q,u,2c,2a,3c,3ru,1a,r,1,h,q,e,u,22,da,w,a,a,8,6,e,g,s
3g,y,u,u,y,14,2w,30,4m,3qw,c,9,3,19,x,1,w,w,dk,k,a,8,c,c,m,8,1g
2s,o,m,m,o,u,2e,2c,3a,3t8,1c,13,7,t,a,x,6,2c,di,w,e,a,8,4,a,i,u
30,s,k,o,m,u,2c,2a,3a,3tq,8,e,h,a,a,m,o,2i,dg,w,c,c,8,c,c,i,s
2m,q,m,m,o,u,2e,2a,3a,3s6,1c,17,6,11,q,6,q,20,d6,10,c,8,a,4,c,g,o
3c,w,q,s,s,y,2q,2s,44,3si,15,5,6,2,16,11,1j,2c,dm,m,c,a,c,6,q,i,12
2i,o,i,k,o,q,2a,26,38,3ty,y,j,7,r,5,h,4,1i,dc,y,g,c,8,6,8,e,s
2u,q,m,o,o,s,2c,2a,3c,3ra,18,h,8,8,w,16,10,20,cw,14,c,a,a,8,g,i,u
20,g,e,c,g,i,1g,1g,2c,3xi,j,2,10,5,3,2,1q,1c,bw,1w,k,q,i,a,s,g,m
1y,e,e,c,c,g,18,18,1w,3y0,8,7,16,o,h,1i,1e,1q,by,1w,m,q,k,e,w,m,w
2u,q,m,m,o,u,2e,2a,3a,3si,14,l,7,d,i,c,s,24,de,u,c,a,8,6,e,g,q
2m,q,m,o,o,u,2e,28,3c,3ri,1a,15,g,n,u,o,12,1y,cy,12,c,8,c,2,e,i,s
26,m,e,i,i,m,1s,1w,2u,3x0,1x,10,9,b,2,6,m,s,cm,1e,o,i,a,a,g,c,m
22,g,c,e,e,g,1a,1c,24,3y0,r,s,12,2,6,1e,1u,1o,c8,1s,m,q,g,c,y,i,q
34,u,o,o,q,u,2e,2c,3k,3ro,9,k,9,c,y,2q,o,2a,d8,u,c,c,a,a,k,o,y
28,o,g,k,m,q,22,26,34,3w8,29,m,13,0,h,k,8,14,cm,1a,o,c,8,8,a,a,o
22,g,e,c,c,g,18,18,24,3yi,p,18,16,8,c,2y,1c,1j,cg,1w,k,q,g,c,12,o,s
32,s,m,o,o,u,2a,2c,3c,3so,4,o,j,g,o,20,k,2c,da,w,c,c,a,c,g,k,w
36,s,m,q,o,u,2a,2c,3g,3t8,h,m,f,8,k,2q,q,2g,de,w,c,c,a,c,i,m,w
2y,y,w,y,w,10,2u,34,4o,3p8,1k,2v,31,1o,24,36,9,a,d0,k,8,a,8,a,i,a,u
24,i,e,c,g,i,1e,1g,2c,3y0,17,16,u,7,i,10,1k,k,ck,1k,o,m,g,c,w,g,m
2w,q,k,o,o,u,2e,2a,3a,3te,s,h,f,9,c,9,k,2i,dk,u,c,c,6,8,a,i,s
36,w,q,q,u,y,2m,2k,3u,3qo,p,a,c,0,e,u,y,2o,d6,u,a,8,a,a,s,k,12
2w,s,m,m,o,u,2c,2a,3c,3ru,u,0,0,e,o,q,y,2a,d6,y,a,c,8,a,g,i,u
2y,u,o,o,q,u,2c,2c,3i,3qw,a,6,9,e,s,3g,w,24,d0,y,c,c,8,c,i,o,y
1s,g,e,e,e,k,1k,1o,2k,3vw,2a,2f,w,s,1t,v,1b,a,be,12,18,g,q,e,k,a,c
2c,o,k,k,o,q,28,24,38,3uo,1f,1,v,1,0,1i,1k,1w,cm,16,m,a,8,6,c,c,o
1q,e,e,e,e,k,1m,1o,2m,3uq,2m,27,2,u,1p,v,b,2,b8,y,18,m,o,e,i,8,e
22,i,g,e,i,m,1q,1q,2o,3wu,1r,s,m,19,e,v,1s,32,bw,1q,k,q,c,a,m,8,g
26,m,e,g,i,m,1o,1q,2o,3vo,z,1k,4,5,6,z,l,10,by,1a,u,i,e,a,w,i,k
1w,i,e,e,e,k,1k,1o,2m,3vg,w,j,1a,1,l,1j,1p,k,bi,14,y,o,k,a,s,c,c
1o,e,c,g,e,k,1k,1o,2m,3ve,2e,1t,n,q,1h,1h,1,0,b6,10,1e,i,m,i,g,8,a
20,g,e,e,g,k,1i,1m,2i,3wq,h,8,10,d,0,l,9,1e,bs,1m,k,q,g,c,s,g,g
1o,e,c,c,e,g,1g,1k,2i,3wc,2c,1b,r,10,x,1z,11,4,b8,10,1g,i,i,o,k,c,4
1u,e,e,c,c,i,1a,1e,28,3x6,1m,1h,16,y,x,b,f,a,bm,1g,w,i,o,i,q,i,k
24,k,e,g,i,k,1o,1o,2m,3wc,1j,1c,g,l,c,j,g,28,bw,1i,o,m,e,c,q,g,g
1m,e,c,e,e,k,1k,1o,2o,3vk,22,t,1v,q,13,1z,c,1,b2,12,1a,q,g,k,g,8,a
2e,m,k,k,k,q,20,20,30,3ua,j,a,l,2,3,1m,1y,2k,ck,14,o,a,a,a,i,c,o
1u,c,c,c,c,e,14,16,1s,3xk,1y,1p,1a,14,d,6,y,s,bm,1e,14,g,q,m,s,s,w
1y,i,e,e,i,i,1m,1o,2m,3v6,6,c,1a,h,2,23,1f,e,bk,1a,o,s,i,a,s,e,g
20,i,e,e,g,k,1k,1o,2m,3vk,3,m,1a,l,4,21,1f,a,bm,1a,q,q,i,a,s,e,g
1y,g,e,e,g,k,1k,1o,2m,3v6,s,b,1c,7,f,1n,1l,i,bi,18,s,q,i,c,q,e,c
20,g,g,e,g,k,1k,1m,2i,3wy,r,4,w,l,0,l,u,2c,bu,1o,k,q,g,a,q,c,i
2a,o,g,i,k,o,1w,1w,2w,3ug,h,10,f,4,b,n,t,10,c4,18,s,e,c,c,s,g,o
2a,o,g,k,k,q,1y,1y,30,3u2,p,w,l,4,b,1f,b,q,c0,1a,s,e,c,a,s,e,s
26,m,g,i,k,o,1u,1w,2u,3w8,25,10,n,7,b,q,1y,2s,c2,1i,s,e,a,a,i,c,i
1u,e,e,e,e,k,1k,1m,2m,3w4,26,2b,y,u,1r,v,1j,c,bg,16,14,g,o,e,m,c,a
1u,e,c,e,e,k,1i,1m,2k,3wg,2a,2d,w,u,1p,11,1h,8,bg,16,16,g,o,g,m,a,a
1s,e,e,c,e,k,1i,1o,2m,3w6,2g,2l,o,u,1t,15,13,6,be,10,1c,e,q,g,k,a,8
28,k,e,i,i,m,1o,1q,2m,3vs,11,1k,5,1,2,7,8,2c,by,1e,u,g,e,c,q,k,i
2e,o,i,k,m,q,20,1y,2y,3tq,6,6,f,4,3,1k,2g,2u,cm,12,o,a,a,a,k,e,m
1u,c,c,c,c,e,16,16,1w,3xg,22,1p,18,12,b,5,k,i,bm,18,18,e,q,m,s,o,u
2k,m,k,m,m,q,22,22,34,3sc,18,d,0,7,4,10,12,28,cm,10,m,c,a,a,q,k,q
1y,i,e,e,g,k,1m,1q,2m,3uw,c,4,1e,j,1,1z,1d,e,bi,16,u,q,i,a,s,e,e
24,i,e,g,g,k,1m,1m,2k,3w6,z,1g,y,p,m,15,p,w,bs,1c,u,o,e,c,u,g,i
1q,e,e,e,e,m,1k,1o,2m,3v8,2k,2h,e,u,1t,x,l,4,ba,y,1a,i,q,e,i,a,c
1u,e,e,e,e,k,1k,1o,2k,3vu,2c,2h,w,s,1r,v,1b,a,be,12,18,g,q,c,m,a,a
1y,g,e,c,g,k,1i,1m,2i,3wk,k,h,16,2,l,p,1j,e,bo,1e,q,q,i,c,s,e,e
22,i,g,e,g,k,1k,1m,2k,3w8,t,1a,14,r,k,17,11,q,bs,1c,s,o,g,a,w,g,i
1w,g,e,c,e,k,1g,1k,2g,3wu,y,z,14,i,v,h,11,a,bo,1g,s,m,m,c,q,g,g
1y,g,e,c,g,k,1i,1o,2k,3w8,q,j,18,2,r,v,1v,g,bm,1c,s,o,k,a,s,c,c
1o,e,c,e,e,i,1i,1o,2k,3w2,2g,1v,f,w,1h,1n,n,2,b8,10,1i,e,m,k,i,a,8
1y,g,e,e,g,i,1k,1m,2i,3wk,6,3,18,5,d,n,1b,k,bo,1g,q,q,i,c,s,e,g
1y,g,e,e,e,i,1i,1i,2c,3x4,2,f,10,4,l,3,m,1s,bs,1q,k,o,k,c,q,e,m
1o,e,c,e,e,i,1k,1o,2m,3vq,2c,1f,13,s,1b,1r,5,0,b6,10,1g,i,k,k,g,8,a
1y,g,e,e,g,i,1k,1o,2k,3w2,e,1,1a,7,f,15,1v,i,bm,1c,q,q,i,c,s,c,c
2i,o,k,m,m,q,22,24,32,3sm,14,f,2,9,8,1y,24,2c,cs,10,i,c,a,a,i,e,q
20,g,e,e,g,i,1k,1m,2k,3we,7,g,1a,h,0,v,1d,k,bq,1c,q,s,g,c,s,g,e
20,g,e,e,g,i,1k,1k,2g,3x6,j,1,u,9,d,5,16,26,bu,1q,m,o,i,a,q,c,k
1w,g,e,e,g,k,1k,1o,2m,3us,1o,1b,1c,6,v,1b,1l,m,be,w,16,o,k,c,q,c,e
1o,e,e,e,e,k,1k,1q,2m,3v6,2e,1t,n,q,1j,1b,2,0,b6,10,1a,m,m,g,g,8,c
2m,o,m,q,s,y,2m,2w,4u,3se,20,1h,1,14,3a,16,2z,41,eg,w,c,1,5,1,2,e,w
32,s,o,o,u,12,30,32,4w,3s4,b,c,9,3,x,n,1,7,ei,q,e,e,4,4,k,a,u
3c,y,q,s,u,14,30,2y,4g,3re,x,h,g,10,1l,3,20,3s,ei,k,e,a,6,a,w,i,u
3y,12,w,u,y,12,2y,2w,4c,3ou,7,0,g,18,1g,20,3g,3k,eg,g,a,e,4,a,w,m,q
3y,12,u,u,y,14,2w,2w,4c,3pa,b,1,a,y,1e,20,36,3w,ec,i,a,e,6,a,u,m,s
3q,10,u,u,w,14,30,2u,4a,3pi,j,5,u,16,m,k,36,4m,ei,g,c,8,2,c,w,o,q
2s,q,s,s,s,10,2u,34,5a,3ro,16,2,6,1o,2g,s,1r,3v,eu,w,3,6,2,3,a,e,12
2w,q,m,q,u,12,2u,30,4y,3sg,m,d,t,t,16,14,1f,2z,eg,s,c,a,2,1,8,8,1a
38,u,q,q,s,14,2y,30,4q,3ry,11,2,2,m,1n,c,s,2a,ei,o,e,e,6,8,u,e,o
3q,12,u,u,w,14,30,2u,4a,3pa,f,1,w,16,o,q,3a,4a,ei,i,c,8,2,a,y,m,q
2u,q,m,q,u,10,2s,30,4y,3sa,10,l,x,5,1i,1g,1t,3j,ei,s,e,6,2,1,2,a,18
36,u,o,q,s,14,30,32,4q,3s2,x,8,7,o,13,t,0,1w,ei,o,e,e,6,6,s,g,o
34,u,o,q,s,14,2y,32,4s,3s8,r,8,9,1,19,5,o,y,eg,q,c,e,6,6,o,c,u
34,u,m,q,u,14,2y,32,4s,3rw,l,8,b,1,19,2,a,u,ek,q,c,e,6,8,i,a,w
3u,12,w,u,y,12,2y,2w,4c,3p0,f,0,o,16,1c,1o,3a,3s,eg,g,a,a,4,a,y,m,q
34,s,o,q,s,14,30,32,4u,3s0,l,c,b,0,v,z,2,m,ei,s,c,e,8,0,o,a,u
34,u,m,q,u,12,2y,32,4u,3s8,p,8,d,8,1d,c,0,s,eg,q,e,c,8,4,m,c,u
3i,10,s,u,u,14,30,2y,48,3qk,p,l,s,14,x,d,2q,4s,ei,i,e,8,4,c,w,o,s
2u,q,m,m,q,w,2i,2c,3g,3ti,u,z,j,1,1h,1f,2,2w,ds,s,e,a,4,4,e,i,y
2g,m,i,k,m,w,2q,2q,4i,3v6,8,3,x,1,1x,21,1g,1l,ds,w,i,a,8,4,2,4,g
30,q,m,m,o,u,2e,28,3c,3ua,6,8,p,4,2,9,i,2y,ds,s,c,e,4,a,a,i,s
2u,o,m,m,o,w,2g,2a,3e,3tc,14,z,f,b,d,v,4,2i,dm,u,e,a,6,4,c,i,u
20,e,c,c,c,e,16,1c,26,3yy,5,m,18,12,5,2e,1z,1v,cc,24,g,q,k,e,18,m,m
22,e,e,a,c,g,14,1a,2a,3ys,d,10,16,q,1,3o,p,4n,ce,20,i,q,k,c,14,s,u
2g,m,g,i,m,o,2c,2g,3u,3ve,c,a,t,3,b,3x,1j,1k,dm,y,k,c,a,a,c,q,c
28,k,g,k,o,w,2g,2q,4k,3xu,1x,1k,29,25,s,4e,1f,4l,dm,10,q,8,0,5,5,6,k
2e,k,g,i,i,o,24,24,3c,3w4,3,u,x,e,n,3h,1h,1u,dg,12,m,c,a,e,g,q,e
30,q,m,m,o,u,2c,2a,3a,3ty,8,c,j,c,6,6,m,2o,dm,s,e,c,6,c,a,i,s
32,s,k,o,m,u,2c,2a,3a,3uc,5,o,r,c,i,i,k,2o,do,u,c,e,6,c,c,i,s
2u,q,m,o,q,y,2k,2c,3g,3to,q,13,j,v,1n,21,k,3m,dy,s,a,a,4,4,k,s,u
30,q,m,m,o,u,2c,2a,3a,3u6,4,e,n,8,8,1,k,2s,do,u,c,e,6,a,a,i,q
2y,q,m,m,q,w,2e,2c,3c,3u6,a,9,v,4,15,j,c,38,dw,s,c,c,2,8,c,i,u
2a,i,e,g,i,k,1w,22,38,3xg,x,18,7,0,3,2f,27,u,dc,16,o,g,c,e,k,m,4
36,s,m,o,q,u,2a,2a,3g,3tw,p,m,f,6,i,2m,k,2g,dm,s,e,c,a,a,i,k,w
2o,o,m,k,o,w,2g,2e,3i,3te,1o,1d,1,x,n,2p,n,2q,do,w,e,a,8,2,e,i,y
2e,m,i,m,s,y,2m,2w,4s,3vi,f,8,1t,z,q,3y,11,4n,du,u,k,6,0,3,1,6,o
26,i,g,k,o,u,2i,2o,4m,3yk,1p,m,2f,r,2w,1m,25,4f,di,16,o,a,3,b,1,8,m
2e,m,g,i,i,o,20,24,32,3vs,4,o,p,a,n,1n,1r,o,de,10,m,c,c,a,c,m,o
20,e,e,a,c,g,1i,1k,2c,3yw,h,o,1k,12,x,29,6,1c,cg,22,i,o,k,e,y,0,8
20,e,c,c,c,g,1i,1k,2e,3yw,f,o,1o,14,13,33,1k,u,ci,20,i,o,k,e,y,5,a
1y,c,c,a,a,g,1g,1g,26,3ys,w,6,1u,12,15,41,2q,1g,ca,28,o,i,o,k,s,7,k
2q,o,m,m,o,u,2e,2c,3c,3tc,1g,15,7,t,1,1d,3,28,dk,w,e,a,8,4,a,g,w
28,k,e,i,m,u,2c,2k,4a,3y0,23,1k,t,23,23,4k,4,45,di,12,q,e,4,0,b,2,i
26,i,e,c,g,i,1g,1s,2s,3y4,11,1a,u,d,m,u,39,1l,d4,1c,o,k,e,e,w,k,i
2u,q,m,m,o,w,2g,2a,3c,3tg,u,p,j,5,b,n,a,2m,do,s,e,a,6,6,a,i,u
2u,q,m,m,q,y,2g,2c,3g,3ti,u,x,l,0,1f,19,2,2w,ds,s,c,c,2,6,e,i,w
22,g,c,g,m,q,26,2e,40,3z2,t,m,17,2n,26,28,1p,3z,d2,1o,i,k,4,7,b,6,i
2a,o,e,q,q,w,2m,2u,4u,3wo,15,1c,4b,j,46,2g,25,4p,dq,w,o,4,3,7,0,c,m
24,i,g,k,o,u,2i,2q,4k,3z0,1t,5,2j,8,40,c,2f,4h,dg,1a,m,a,5,d,0,8,o
2q,o,m,m,q,10,2m,2o,44,3tc,1i,1n,7,19,2j,f,a,1c,e0,s,c,8,8,4,g,s,m
2i,o,k,k,s,10,2q,2w,4q,3u4,s,x,1,1d,15,2q,0,3p,dy,u,e,6,4,0,0,6,m
2w,q,m,m,o,w,2e,2a,3a,3ti,o,d,j,5,6,d,i,2m,dm,s,e,a,8,6,a,i,u
2i,o,i,k,k,s,28,28,3a,3ue,12,b,b,l,3,27,z,1g,dg,10,g,c,a,6,a,i,s
2u,q,m,m,s,y,2i,2e,3g,3tk,s,15,l,n,1n,1z,8,3i,dy,q,c,a,2,6,g,o,y
36,s,m,o,o,u,2a,2a,3e,3ua,n,o,l,6,i,2a,k,2g,ds,q,e,c,a,a,g,i,w
3g,y,s,s,y,14,2y,2w,4c,3rk,d,3,6,r,1h,x,y,28,do,m,8,8,c,a,o,a,1c
34,s,m,o,o,u,2a,2c,3e,3ts,f,q,l,a,k,28,i,2e,dk,u,c,c,a,c,g,i,y
2w,q,k,o,o,y,2e,2c,3c,3tm,o,l,n,6,x,n,8,2u,dq,s,e,a,4,6,c,i,u
2y,q,m,m,o,w,2g,2a,3a,3u0,c,5,t,8,r,b,c,30,ds,s,e,c,2,8,c,g,u
3a,u,o,o,s,w,2e,2e,3o,3tk,11,6,1,1,m,24,i,2g,dq,o,c,c,c,8,m,m,w
2q,w,m,y,w,10,32,32,5e,3rm,2w,2c,3r,1a,14,z,1q,21,f0,c,8,6,1,6,i,s,o
3m,10,s,u,u,14,2u,2w,4k,3r0,b,n,0,4,j,1k,40,4c,es,k,c,a,8,c,y,e,m
2o,s,s,10,u,14,30,32,5e,3qo,7w,2f,4t,28,h,q,2e,1x,fc,2,8,4,2,4,k,s,m
2u,u,m,w,w,14,32,38,5i,3rg,24,34,37,f,o,e,q,25,f4,i,2,g,1,a,c,s,q
2y,s,o,s,s,14,30,3a,54,3s0,l,1c,1f,18,1n,1g,b,u,ew,12,2,o,4,i,i,e,u
3a,u,o,q,u,y,2o,2u,4i,3t6,1b,9,3,17,5,k,28,40,ey,m,e,e,k,a,s,c,i
2q,u,o,10,w,12,32,32,5g,3qs,6k,5,5n,1u,k,4,24,2j,fa,6,6,8,0,4,i,u,o
2w,u,m,s,u,16,34,3a,5a,3re,0,2m,z,e,1z,1u,d,f,f4,u,1,k,2,k,2,s,w
30,s,o,s,s,10,2w,30,4u,3sg,x,g,27,w,f,g,1a,2y,eu,10,a,q,8,a,s,g,m
2w,u,m,s,u,14,34,3a,5c,3ro,3,28,1b,q,1n,1m,h,b,f0,w,0,m,2,k,8,m,u
2w,s,o,s,w,14,34,3c,5a,3rc,a,2q,z,4,1x,1u,7,r,f4,s,0,i,2,k,0,u,w
2y,s,o,s,s,14,30,3a,58,3s4,h,16,15,16,1z,1s,j,i,ew,12,2,m,4,k,g,c,u
2w,s,o,s,w,14,34,3a,5e,3ri,u,2u,1n,5,13,1g,7,13,f4,o,0,i,0,i,6,q,u
3c,u,q,q,u,10,2q,2u,4i,3sq,15,f,1,r,b,q,2e,46,ew,m,g,a,g,g,q,e,g
3e,w,o,s,s,10,2q,2u,4i,3ss,x,l,0,n,9,14,2y,3g,ey,k,g,c,e,e,q,e,m
32,u,o,q,u,12,2y,34,56,3si,z,8,x,8,d,o,m,i,eu,y,a,k,a,c,k,i,q
2q,u,o,10,w,10,34,32,5g,3r4,5k,w,5v,1o,12,7,20,2n,f8,8,6,8,0,4,i,u,o
2u,s,o,w,w,14,34,36,5i,3rc,2k,2s,3b,9,i,i,o,25,f4,i,2,g,1,a,c,s,q
2w,s,o,s,u,16,34,3c,5a,3rg,3,2u,v,i,2j,28,n,9,f2,u,0,i,2,o,1,s,w
2u,u,o,s,w,16,34,38,5g,3re,1u,2o,25,0,17,1g,4,1f,f4,m,0,e,0,i,6,q,s
2s,s,o,10,w,12,32,34,5i,3qs,5w,w,5x,18,1g,5,1w,2x,fa,8,6,a,1,4,g,w,o
38,u,o,q,s,y,2q,2u,4i,3t2,1d,1,f,z,3,i,1y,48,ew,o,g,c,k,c,s,c,g
3i,w,s,s,u,10,2s,2w,4i,3ru,h,t,1,3,d,w,36,46,eu,k,g,a,a,g,s,g,k
2w,u,q,q,y,12,34,36,5g,3s2,c,28,0,17,6,y,i,2b,ew,w,3,e,2,a,a,s,q
30,u,m,s,s,10,2u,30,4s,3si,x,e,25,q,l,g,1i,3c,eu,10,a,o,c,8,s,i,m
3c,w,o,s,s,10,2q,2u,4i,3so,15,h,1,l,d,q,2g,48,ew,m,g,a,g,g,q,c,i
32,s,o,s,s,12,2y,38,58,3sg,v,e,1h,q,7,q,a,1,eu,10,8,m,8,c,k,g,q
2w,y,s,w,w,16,3a,3q,6g,3rm,12,y,1b,11,9,e,12,15,d4,14,g,m,m,k,1g,u,1e
2w,w,s,u,w,14,3c,3q,6i,3s2,a,10,p,11,f,e,g,17,d8,14,i,k,m,q,1a,u,1c
2g,s,m,m,s,10,2k,2q,4i,3ty,r,q,k,1l,2d,24,2w,2k,cy,1c,q,k,i,o,1e,o,y
38,14,w,10,12,1c,3y,4a,7i,3t0,10,17,1b,p,t,b,y,f,d6,w,m,q,i,m,1g,12,1i
2q,u,q,s,w,12,2y,3a,5o,3rm,y,14,l,1n,n,14,24,7,d2,1a,g,k,m,i,1i,q,18
2g,s,m,m,q,10,2i,2q,4k,3u0,r,m,e,t,2h,24,2q,26,cy,1c,u,k,i,q,1c,o,y
30,y,w,w,10,16,3c,3k,60,3qc,4a,29,19,t,v,10,36,k,d4,q,k,m,o,g,1i,s,1g
2k,u,m,q,u,y,2q,30,56,3ss,6,g,m,1r,17,1m,28,k,d0,1e,m,k,i,m,1g,q,12
1m,e,a,c,c,e,16,1c,1w,3w2,14,g,x,26,3w,2c,25,1w,bi,1u,1m,12,12,16,22,1k,26
2o,s,o,s,s,10,2w,38,5m,3sq,b,o,c,1b,t,1m,14,j,d4,1c,m,k,k,q,1a,s,12
2o,s,o,q,u,10,2u,38,5m,3ss,b,4,i,v,r,1o,y,p,d6,1c,o,k,i,y,14,q,14
26,q,k,k,k,u,28,2a,3m,3vw,d,f,8,8,2f,10,34,3o,ci,1k,14,m,i,12,1a,o,s
2u,w,q,u,w,12,34,3k,66,3ru,8,1q,11,1d,b,y,10,17,d4,18,g,k,m,k,1g,s,1c
2w,y,s,u,y,14,38,3k,68,3r2,1w,m,1d,15,d,k,24,t,d2,12,g,m,o,i,1i,s,1c
2k,s,o,o,s,y,2q,30,5a,3tk,t,4,q,x,17,20,18,0,d4,1c,q,k,k,u,16,s,10
2a,q,k,k,o,w,2c,2e,42,3vo,1n,e,6,8,2p,1u,2w,36,cu,1e,10,m,i,10,16,o,u
2i,s,o,m,u,y,2m,2w,4y,3tc,5,2,y,1h,1j,1u,2c,y,d0,1e,q,k,i,o,1e,o,10
22,o,i,i,k,o,22,24,3c,3xy,1h,0,17,8,m,19,3i,3w,ci,1m,16,s,o,12,1e,o,o
34,10,w,w,10,18,3k,3y,6w,3rm,2k,1b,1b,j,b,1,1g,v,d6,s,k,q,k,i,1i,w,1i
2s,w,q,u,w,12,32,3e,5w,3re,16,16,z,1h,h,y,22,l,d2,18,g,k,m,i,1i,s,18
2c,q,k,m,m,w,2e,2k,4a,3ve,19,9,c,8,1p,1c,2e,2e,d0,1c,y,m,o,u,18,o,u
2u,u,s,w,w,14,34,3a,5o,3qc,2o,w,1z,19,v,y,38,s,d0,14,g,k,m,i,1i,q,1a
26,o,k,k,k,s,28,28,3k,3wa,p,9,5,g,1r,k,34,3s,ci,1k,14,o,i,14,1a,o,q
24,o,i,k,i,q,26,24,3i,3xq,1n,5,v,c,8,x,3a,3s,cm,1k,16,q,q,10,1c,o,o
2c,s,k,m,o,w,2e,2m,4g,3v8,17,f,i,1,1b,1c,24,1y,d0,1e,w,m,o,u,16,q,u
3m,1c,10,14,16,1i,4a,4y,82,3ug,2h,t,15,l,r,1k,f,i,dc,w,q,o,k,y,16,14,1i
3m,18,10,12,16,1g,48,4u,7w,3u2,1h,19,v,l,n,12,9,1,dg,w,q,o,k,10,14,10,1i
3i,18,10,10,16,1g,44,4q,7u,3u0,1d,z,13,n,p,14,b,7,de,w,o,q,k,y,14,12,1i
2g,q,m,m,o,u,2i,2i,48,3vc,1t,17,f,18,j,1a,4c,1u,de,1e,u,s,y,s,16,o,y
3m,1c,10,14,16,1i,4a,4y,82,3ui,2l,r,15,l,t,1m,f,i,dc,w,q,o,i,10,16,12,1k
2y,w,s,u,w,16,3e,3k,6c,3sg,3,7,5,13,2,i,2g,1n,d8,1c,i,m,m,12,10,u,1c
3i,1a,y,14,14,1g,46,4u,7w,3u4,1p,t,15,n,v,1c,f,2,dc,w,o,q,k,y,16,10,1k
3a,16,y,10,12,1e,40,4i,7k,3tw,r,11,19,l,p,u,1,t,d8,y,o,q,k,y,14,10,1k
2m,s,m,o,q,w,2o,2o,4m,3tq,7,1n,c,8,b,1a,4w,c,dc,1i,u,q,12,k,16,o,10
3q,1a,10,14,16,1i,4c,4y,82,3uo,2d,1d,r,j,n,14,b,e,dg,w,q,o,k,y,16,12,1i
3q,1a,10,14,18,1i,4a,50,82,3uk,2f,17,v,l,n,1e,f,i,de,y,q,m,k,10,16,12,1i
28,q,k,k,k,q,2a,26,3k,3z6,4p,t,1b,1s,s,d,44,40,dg,1c,y,s,10,u,1e,q,s
32,10,u,w,y,18,3m,3y,6w,3sy,9,7,r,t,9,a,1a,1n,d6,16,k,o,o,y,14,u,1g
3c,16,y,10,14,1e,40,4k,7o,3u4,v,13,1f,l,n,w,5,n,d8,10,m,s,k,y,14,10,1i
3q,1c,10,14,18,1g,4e,4y,84,3uw,2p,1d,p,j,n,16,d,i,de,y,q,o,k,y,16,12,1i
34,y,u,u,y,16,3i,3s,6o,3ss,f,1,l,x,e,i,1s,1v,dc,1c,i,m,o,12,10,s,1c
22,m,i,i,k,m,1u,1y,2s,414,5p,10,11,b,1o,a,1w,5w,d0,1w,12,w,u,10,1s,u,w
2y,y,u,u,y,16,3i,3s,6m,3sq,7,3,h,x,9,c,1s,1j,d4,1a,k,m,m,10,14,u,1e
3o,1c,10,14,16,1i,4a,50,82,3ui,2d,17,v,j,p,1e,f,i,dg,w,q,m,k,10,16,10,1k
2m,q,o,o,q,y,2q,2o,4s,3u4,x,1j,c,8,6,y,4e,8,dc,1g,u,o,u,s,16,m,12
36,10,u,y,y,1a,3o,40,72,3t0,9,h,v,n,1,e,14,1t,da,16,k,o,m,10,12,u,1g
3q,1a,10,14,18,1i,4a,50,84,3um,2l,13,z,j,p,1i,f,k,de,w,q,o,k,y,16,12,1k
3o,1a,12,12,18,1i,4a,4y,82,3ug,2b,15,x,j,p,1g,h,i,de,w,s,m,k,y,16,12,1i
3q,1a,12,14,16,1i,4c,4y,84,3uo,2l,17,v,j,n,1e,f,i,de,y,q,m,k,10,16,12,1i
3o,1c,10,14,16,1i,4a,4y,84,3ug,2f,11,z,j,r,1i,f,i,de,w,q,o,i,10,16,12,1i
22,m,g,i,k,m,1s,1w,2o,410,4n,4,9,p,18,c,22,5w,d0,1w,12,w,w,10,1u,w,y
2e,q,m,m,m,u,2i,2g,48,3wq,37,15,j,12,y,6,3w,28,dc,1e,u,s,10,o,18,o,y
3m,1a,y,12,16,1g,48,4s,7y,3ua,1h,1h,p,l,j,m,6,h,di,w,q,o,k,y,14,10,1i
3i,18,10,12,16,1g,44,4u,7u,3u6,1p,t,17,n,v,1a,d,0,da,y,o,o,k,y,16,12,1k
32,w,u,w,w,16,3i,3q,6m,3sm,9,0,l,z,8,e,20,1t,da,1a,k,m,m,12,10,u,1c
2y,w,s,u,w,14,3a,3i,66,3so,d,7,5,11,g,i,2s,1p,dc,1c,k,m,m,12,10,s,1a
2s,u,o,s,u,12,32,34,5o,3sm,4,11,e,v,6,s,3m,13,d8,1g,o,k,s,w,12,q,16
24,q,i,k,k,o,24,24,38,40o,5x,5,1j,1a,1k,r,3g,4w,da,1i,y,u,y,w,1i,s,q
3a,14,w,10,10,1c,3w,4c,7e,3ti,d,x,13,l,9,g,g,1f,da,12,m,q,m,y,12,y,1i
36,12,w,y,10,1a,3s,48,78,3t8,7,r,z,n,9,g,o,1h,da,12,k,q,m,y,12,y,1i
34,y,u,u,y,16,3g,3q,6i,3t4,p,1,n,11,o,s,22,21,de,1c,k,m,w,s,10,s,1c
2y,w,q,s,w,12,38,3a,5w,3t8,x,d,6,19,s,y,3c,1r,de,1g,m,k,y,q,12,q,18
3a,12,y,y,10,1a,3u,4a,7a,3te,f,p,11,l,3,g,k,1l,dc,14,k,s,k,10,12,w,1g
2e,q,k,k,m,s,2c,2a,3q,3wi,1v,1d,r,1q,7,c,4q,34,dg,1e,w,u,12,q,1c,o,w
3m,1a,10,12,16,1g,48,4w,7y,3u6,1n,1b,t,l,l,10,7,0,dg,w,q,o,k,10,14,10,1i
24,q,g,k,k,m,22,20,34,40s,67,10,1z,10,1u,p,36,58,d8,1m,10,u,y,w,1o,s,q
3e,14,w,10,12,1c,3w,4c,7g,3te,1,z,19,j,1,i,m,1r,dg,10,m,s,m,y,12,w,1i
3k,1a,10,14,16,1g,48,4u,80,3ue,25,p,19,n,t,1g,f,a,dc,w,q,o,k,y,18,10,1i
3m,1c,10,14,16,1i,48,4y,82,3uk,2h,t,15,l,r,1g,f,g,dc,y,o,o,k,10,16,12,1i
3o,1c,10,12,18,1g,4c,4w,82,3um,29,1d,r,l,l,12,9,a,dg,w,q,o,k,10,14,12,1i
3m,1c,10,12,16,1i,48,4y,80,3ua,23,13,x,l,p,1e,f,c,dg,u,q,o,k,10,14,12,1i
2w,u,q,q,u,12,32,34,5k,3ti,z,v,i,1d,y,14,3y,1n,dc,1i,o,m,12,k,14,o,16
3q,1a,10,14,16,1i,4c,4w,82,3us,2d,1h,n,j,n,y,7,8,dg,w,s,m,k,10,14,12,1i
3c,14,y,10,12,1c,3y,4e,7i,3tk,b,x,17,l,5,a,i,1l,de,12,k,s,m,y,12,w,1i
2u,u,q,s,u,12,32,34,5o,3sy,h,n,6,v,i,w,3o,1d,dc,1i,m,k,u,u,12,q,16
26,q,k,m,q,w,28,2a,3o,3v0,2,s,c,2f,3f,2o,2y,2g,ce,1c,y,k,k,k,1k,q,w
24,s,k,o,s,y,2a,2a,3u,3tw,m,1i,6,49,35,2c,4o,18,c0,1c,w,g,k,o,1c,o,w
1m,i,c,e,g,i,1m,1e,2c,3y6,y,3c,0,f,1j,3l,40,1k,bi,w,16,s,s,q,2m,w,w
1q,m,i,k,m,u,20,1u,38,3ya,t,y,f,19,3j,19,4g,16,bm,16,s,q,g,m,1u,o,w
26,q,k,o,s,y,28,28,3u,3u0,y,12,2,39,3n,2o,4a,y,c2,1g,w,i,k,m,1e,q,w
1u,m,i,k,m,s,1y,1u,36,3xk,m,6,d,1t,2t,1,48,14,bo,18,10,o,i,q,1w,k,y
2a,q,k,k,m,s,2c,2g,42,3wa,21,b,f,i,4,q,1s,2o,d0,1i,y,o,w,u,16,o,s
3m,1a,10,14,16,1i,48,4y,80,3v0,2z,l,19,n,v,1g,d,i,da,y,o,o,k,y,18,12,1k
34,10,w,y,10,18,3q,46,74,3te,p,0,11,v,f,i,c,19,d6,16,i,o,o,y,14,y,1e
1u,i,g,e,g,m,1m,1o,2m,3zw,25,k,1a,17,c,n,1w,3s,c4,1w,16,10,16,s,1y,10,u
32,12,u,y,10,1a,3q,46,74,3tg,n,0,15,t,n,k,0,z,d2,16,k,o,m,10,14,y,1e
3q,1c,12,14,16,1i,4g,4y,86,3vm,39,1j,j,j,p,q,1,6,dc,10,q,m,k,10,16,12,1k
3o,1c,10,14,18,1i,4e,4y,84,3vs,3b,1f,p,j,v,u,0,0,da,10,q,m,k,10,16,12,1k
28,q,k,k,k,q,28,2a,3q,3xo,2f,11,t,12,u,9,2u,3m,d6,1e,10,s,y,u,1a,o,s
2s,s,q,o,s,10,2w,2s,52,3tq,5,13,0,j,3,22,4u,13,da,1k,q,o,16,g,14,o,1a
2e,q,k,k,m,q,2c,28,3i,3w8,1z,l,z,1y,14,d,4m,3e,dc,1g,w,u,12,s,1c,q,10
2i,q,m,m,o,w,2i,2m,4g,3v0,v,19,3,i,9,u,3u,1e,da,1e,w,q,w,q,16,o,y
3q,1c,12,14,18,1i,4e,50,86,3vg,3h,11,x,j,r,1a,f,o,da,10,q,m,k,10,16,14,1i
3o,1a,10,12,16,1g,4a,4s,80,3uu,1z,1n,j,j,l,m,8,v,de,10,q,o,k,y,14,12,1i
2y,y,s,u,w,14,3g,3m,6g,3t2,7,0,b,13,3,a,1q,1l,d8,1c,k,k,o,y,12,s,1c
2c,o,k,k,k,q,2a,24,3e,3xc,2h,n,1j,1u,18,x,4s,3w,dg,1e,y,s,12,u,1g,o,y
2u,s,q,q,s,12,2y,2y,5a,3tk,9,t,2,z,a,1m,4k,1f,dc,1g,q,m,10,m,14,o,1a
2a,q,k,m,m,s,2e,2i,48,3w4,1r,j,3,c,7,s,1u,2a,d0,1i,w,o,s,w,16,o,s
1w,g,g,e,g,k,1k,1o,2e,3zy,n,2r,1m,1a,2,9,10,42,cc,24,18,16,w,w,22,16,16
3k,16,10,12,14,1e,46,4k,7u,3ue,11,1j,r,l,f,o,g,1n,de,10,o,q,k,10,12,10,1i
2a,o,k,k,i,q,28,24,3e,3xg,1v,n,1p,1g,16,x,4e,40,dc,1g,w,s,12,u,1g,q,u
3k,1c,y,14,16,1i,48,4w,80,3us,2r,h,1d,n,v,1g,b,g,da,y,o,o,k,y,18,12,1i
2o,s,s,q,u,12,34,3a,5u,3t0,6,1f,c,b,t,14,20,v,cu,1g,s,k,i,16,14,q,14
20,k,i,i,i,m,1w,1y,30,3zs,31,c,n,r,y,7,22,4k,ce,20,16,s,y,w,1q,s,q
2k,s,o,o,s,y,2q,30,5a,3tg,h,p,q,j,j,1m,1c,1,d6,1e,q,m,k,10,12,q,y
3e,18,y,14,14,1g,44,4q,7s,3v4,21,t,1r,n,v,10,9,0,d2,10,o,q,k,s,1e,12,1i
3g,18,y,12,16,1g,44,4q,7w,3ug,19,11,1d,t,11,u,6,6,d8,w,m,q,k,o,1g,12,1k
2k,q,m,m,o,u,2i,2g,46,3uw,t,1l,7,16,a,o,4y,1g,dc,1i,w,q,14,m,18,o,12
3k,16,y,10,14,1e,44,4k,7q,3u6,p,1h,v,l,b,q,i,1v,dg,y,o,s,k,y,12,10,1i
2o,s,q,q,u,10,32,38,5o,3te,d,13,1,7,p,1i,1w,f,cw,1g,s,k,m,12,14,q,14
32,10,w,w,10,18,3o,42,74,3so,k,5,v,v,n,9,c,z,d8,10,k,m,m,q,1a,y,1g
3a,10,w,w,10,18,3o,3y,6y,3tc,2,n,1d,l,e,y,1i,2d,de,16,m,s,q,u,10,u,1g
38,10,w,w,10,18,3m,3y,6w,3t8,1,l,19,n,g,q,1i,23,de,18,k,s,q,u,10,u,1g
34,14,y,y,12,1c,3u,4e,7c,3u8,f,x,1p,l,n,e,1,p,d0,12,m,o,m,o,1i,y,1i
3c,18,y,12,14,1g,46,4k,7u,3uw,1j,r,1l,v,z,4,w,0,d0,w,o,q,g,o,1k,12,1m
38,14,y,y,12,1c,3y,4e,7g,3u0,p,r,1l,l,n,q,3,n,d6,12,m,q,k,y,16,y,1i
3k,1a,y,14,14,1i,48,4s,80,3uw,21,17,x,p,z,y,0,d,dc,w,o,o,m,y,16,10,1k
3a,12,w,w,10,1a,3o,40,70,3te,8,t,1d,n,e,12,1e,2d,de,16,m,s,s,s,10,u,1i
1y,k,g,g,k,m,1u,1s,2w,40q,31,16,1d,1z,26,29,3u,44,c8,1s,1a,10,s,w,1s,q,o
32,y,s,u,y,14,3e,3i,6a,3ta,j,9,t,z,q,18,2s,29,dc,1e,m,o,w,q,10,s,1c
3q,1a,10,14,16,1i,4a,4y,80,3us,2d,1h,n,j,l,w,7,6,dg,y,q,m,k,10,14,12,1i
20,q,k,i,m,o,26,28,3i,3z8,5j,9,z,9,20,g,26,4u,cm,1q,10,s,12,u,1g,q,q
2q,u,q,s,s,12,34,38,5q,3su,4,11,6,j,9,q,34,x,d0,1i,o,m,k,14,12,q,16
2y,y,s,w,y,14,3i,3q,6k,3ss,b,2,j,v,7,e,1m,1h,d4,1a,m,m,m,10,14,u,1c
3s,1c,10,14,18,1i,4g,52,86,3vo,3j,1b,p,j,p,10,b,k,da,10,s,k,k,10,18,12,1k
2a,q,k,k,k,s,28,2a,3q,3x4,1l,1b,11,18,m,b,3q,3k,da,1e,y,q,12,s,1c,o,u
3g,1a,y,14,16,1g,48,4q,80,3v8,29,t,1h,v,11,i,w,8,d0,y,o,o,i,o,1k,14,1k
1u,c,e,g,e,i,1i,1g,28,3z4,28,4d,v,4e,1e,2n,28,3o,ca,28,1c,1e,u,u,24,20,s
3a,14,y,10,12,1e,3y,4g,7i,3u4,r,v,1p,l,r,u,5,f,d6,10,o,q,k,y,16,10,1g
2i,q,m,m,m,u,2i,2g,40,3uq,d,1b,d,10,g,g,4q,1u,da,1g,y,q,12,o,18,o,12
1m,c,a,c,c,e,14,16,20,3wa,18,i,35,2e,38,2a,1y,l,bi,22,1q,1m,14,14,2a,2k,14
3s,1c,12,14,18,1k,4g,54,88,3w4,41,15,t,l,v,14,d,o,d8,12,q,k,k,10,18,14,1k
3q,1c,10,14,16,1i,4e,4y,84,3vk,33,1j,j,j,p,o,4,5,dc,10,q,m,k,10,16,12,1k
3m,18,y,12,14,1g,46,4o,7w,3ua,15,1l,l,n,h,o,e,1f,dg,y,q,q,k,y,12,10,1k
2q,w,s,u,w,16,3c,3o,6g,3ti,n,2,f,v,r,8,s,t,cs,18,o,i,o,o,1k,w,1a
30,y,s,w,y,16,3i,3w,6s,3su,1,8,j,13,f,8,g,19,d8,18,i,k,o,w,14,w,1c
1s,e,e,c,e,i,1g,1g,26,3ye,1y,3t,0,3g,10,c,t,4i,c2,26,1e,1e,y,u,20,1w,10
38,10,u,w,10,16,3m,3u,6s,3tc,a,p,1d,p,g,16,1w,2h,de,16,m,s,u,q,10,u,1g
30,w,s,s,w,14,38,3a,5y,3ti,p,9,f,1b,s,1m,3m,2d,dc,1g,m,o,12,k,12,q,1c
3e,1a,y,12,16,1e,44,4s,7s,3uy,1x,t,1p,n,v,14,b,2,d6,y,o,q,k,y,18,10,1k
3g,16,y,12,14,1e,44,4m,7s,3ua,x,17,17,r,v,s,0,h,da,y,m,q,k,u,18,12,1i
3s,1a,12,14,18,1i,4g,4y,86,3vq,3f,1h,l,j,p,s,3,a,da,12,q,m,k,10,16,12,1k
2i,q,m,k,o,u,2i,2g,42,3us,j,17,d,w,g,g,4o,1u,da,1g,w,s,12,m,1a,m,12
3c,12,y,y,12,1a,3s,46,7a,3tg,c,13,1b,l,8,10,w,29,de,14,m,s,o,u,12,w,1i
1w,i,g,e,i,m,1k,1o,2g,40q,3f,2,1q,1b,c,y,y,4k,ce,2a,18,16,q,w,24,10,18
3o,1a,12,12,18,1g,4e,4u,84,3vi,2v,1j,l,l,p,k,a,f,dc,10,q,m,k,10,14,12,1k
3q,1c,12,14,16,1i,4e,50,84,3vg,37,1f,l,l,l,u,5,a,dc,10,q,m,k,10,16,12,1k
2u,u,s,s,w,14,3a,3k,68,3su,l,8,b,v,f,10,18,v,d0,1c,m,m,o,10,14,u,16
3c,14,w,y,12,1a,3u,46,7a,3tg,e,13,1b,l,4,u,10,29,de,12,m,s,o,w,10,y,1i
1u,g,g,e,g,k,1k,1k,2c,400,v,29,16,1c,o,1b,22,42,ce,28,18,1a,s,w,24,1e,14
2e,q,m,m,m,w,2g,2m,4e,3vg,19,19,1,k,1,y,2i,1u,d8,1e,w,q,u,s,16,o,u
2s,u,q,u,w,12,38,3i,66,3so,2,l,2,n,p,y,1i,11,cu,1g,o,k,m,12,14,u,16
32,w,s,u,w,14,3a,3g,64,3te,f,3,p,17,i,1c,3c,2f,dc,1c,m,o,u,s,10,s,1e
2q,u,q,s,u,12,36,3e,5y,3ss,0,b,5,t,r,16,1y,x,cs,1g,q,k,m,12,16,q,18
2w,w,s,s,w,14,3a,3i,66,3ta,j,1,1,15,0,c,2e,1l,d8,1e,k,k,m,10,12,s,1a
1y,i,g,g,i,m,1m,1o,2i,40o,3h,5,1o,19,4,w,1g,4s,ck,26,16,12,u,y,20,10,18
2u,w,s,w,w,16,3e,3q,6i,3t4,3,2,r,t,r,g,1c,19,cu,1a,m,m,o,q,1g,u,1c
22,o,g,i,k,m,1y,1y,32,402,3z,q,1b,j,1u,9,28,4y,cu,1s,14,w,u,w,1q,s,q
3g,14,y,10,14,1e,40,4i,7o,3ty,d,1b,11,n,l,m,e,1j,dc,y,o,q,k,y,14,y,1k
2q,q,o,o,s,y,2s,2q,4w,3ts,8,1z,k,r,g,14,4w,d,dc,1g,u,m,12,i,16,m,18
32,y,s,w,y,16,3i,3q,6m,3t2,4,b,h,11,1,a,1w,1v,da,1a,i,o,m,10,10,u,1e
2i,s,o,q,q,10,2u,30,56,3tk,b,1f,b,c,n,1y,28,a,cy,1e,u,o,k,14,14,o,12
1u,e,e,e,e,i,1k,1g,28,3z6,14,3f,5,3i,1a,27,1w,3u,ca,26,1e,1c,u,u,26,1q,10
3a,12,y,10,12,1c,3w,4e,7g,3te,c,13,1b,n,p,a,6,r,d8,10,m,q,k,q,1a,10,1i
3a,14,y,10,12,1c,3y,4g,7i,3u4,p,v,1l,n,p,s,5,j,d6,12,m,q,m,w,16,10,1g
3a,14,y,y,14,1c,40,4c,7k,3t8,s,1b,1b,p,v,9,10,d,d6,w,m,q,i,m,1i,10,1k
3m,1c,12,14,18,1i,4c,50,88,3vu,3p,t,1b,n,x,1a,3,q,d6,y,o,m,i,q,1k,14,1m
3k,18,10,12,14,1g,46,4o,7w,3u6,15,1h,r,l,f,i,e,11,di,w,q,q,k,y,12,10,1i
2o,u,q,s,u,10,34,3a,5w,3ti,b,1f,6,b,p,18,1w,p,cw,1e,s,m,i,14,14,s,14
34,10,w,w,10,1a,3q,44,74,3ti,f,h,19,n,j,o,8,11,d4,16,k,q,m,y,14,y,1e
3i,18,y,12,14,1e,44,4m,7s,3ua,v,1d,v,n,j,m,i,1n,de,y,o,q,m,y,12,10,1k
3c,12,y,10,12,1c,3y,4e,7g,3tq,1,17,15,n,h,g,a,1f,dc,y,m,s,k,y,12,y,1k
3m,1c,10,16,18,1i,4c,50,84,3vw,3z,j,1b,n,z,1g,d,o,d4,10,q,m,k,y,1a,14,1k
3g,1a,y,14,16,1g,46,4s,7w,3v4,2h,l,1n,n,x,18,b,6,d6,y,o,q,k,w,1a,12,1i
2c,q,m,o,o,w,2k,2s,4m,3vq,23,1d,v,i,e,1e,26,1o,cs,1i,y,q,q,10,14,q,w
3c,12,w,y,10,1a,3s,44,76,3te,4,t,1d,j,a,w,10,29,de,16,k,u,o,u,12,u,1i
20,m,g,i,i,m,1s,1s,2m,40y,4d,6,6,t,k,i,2a,5o,d0,1w,14,u,w,10,1w,y,10
1w,g,e,g,g,k,1k,1k,2c,406,z,2d,18,10,o,x,1w,4c,ce,2a,1a,18,s,w,24,1i,10
26,o,i,k,k,q,26,2a,3o,3xi,1f,t,r,a,12,15,2q,3i,cs,1m,12,o,w,u,1c,o,o
3i,16,y,10,14,1c,42,4i,7o,3ts,f,1b,z,j,5,k,i,1p,dg,10,o,q,m,y,12,y,1i
1y,m,e,g,m,k,1o,1u,2q,410,4x,1w,9,23,1y,w,g,58,ci,22,18,10,q,y,20,u,s
2w,u,s,s,w,14,3a,3o,6e,3so,7,e,2,19,f,k,g,17,d8,1a,i,k,m,y,12,u,1a
3g,1a,10,12,16,1i,46,4q,80,3uy,1v,11,1b,x,11,m,w,8,d2,y,o,o,i,o,1i,14,1m
3i,1c,y,14,16,1i,4a,4s,84,3v6,2h,z,1b,t,11,y,i,g,d6,w,o,o,i,q,1i,12,1m
2i,q,m,m,q,w,2m,2u,4u,3uc,19,h,g,1,l,1q,1c,u,d6,1e,s,o,o,w,12,q,w
38,y,u,w,y,18,3i,3s,6o,3tc,0,f,1d,r,k,18,20,2h,de,18,m,s,w,o,y,u,1g
2i,s,m,m,o,y,2m,2o,4q,3um,j,1j,6,6,h,1a,3e,u,d8,1e,w,q,s,s,14,o,y
1y,o,k,k,i,o,22,24,3a,402,4x,z,19,h,2k,7,2i,5a,cm,1s,y,s,16,s,1m,q,q
2q,s,q,s,u,10,34,38,5q,3t4,5,11,2,d,f,12,2k,p,cy,1i,q,k,m,12,14,q,14
3k,1a,10,14,18,1i,4a,4u,86,3vk,37,l,1j,r,z,w,k,i,d0,10,o,o,i,o,1k,14,1m
2u,u,o,q,u,10,2y,2y,5c,3tm,l,11,4,p,c,1o,4g,1d,dc,1i,q,m,16,i,14,m,18
38,16,y,10,12,1c,3y,4g,7g,3u4,l,z,1l,n,n,s,7,l,d8,10,m,q,m,w,16,10,1g
3e,16,10,12,14,1g,44,4k,7w,3uo,x,17,1d,t,z,1,14,1,d2,u,q,o,i,m,1k,12,1m
2s,w,s,s,w,14,3a,3k,6a,3su,9,9,3,n,r,w,1a,13,cw,1e,o,k,m,12,14,u,18
3s,1c,12,14,18,1i,4i,52,88,3w8,45,15,v,l,x,18,d,o,d8,10,q,k,k,10,18,14,1m
2e,o,k,k,k,q,2c,26,3i,3wa,1p,f,19,1q,1c,n,4i,3i,dc,1g,w,s,14,s,1e,o,10
3q,1a,10,14,16,1i,4e,4u,84,3vi,2v,1l,j,l,p,m,8,f,dc,10,q,m,k,10,16,12,1i
2g,s,o,s,s,10,2y,3c,5o,3u4,1b,3,f,9,t,w,c,8,ci,1e,u,i,m,m,1o,s,14
3g,14,y,y,14,1c,3y,4e,7k,3tq,0,19,13,n,7,q,o,21,de,10,o,q,m,y,12,y,1i
1y,k,e,g,k,m,1q,1u,2u,408,3j,1c,i,29,g,o,1i,4c,c8,20,16,y,10,u,1u,u,q
3a,16,y,10,14,1e,40,4i,7m,3ui,j,1f,1t,n,r,u,9,7,d6,y,o,q,m,w,16,10,1i
3k,1a,10,12,18,1g,48,4w,82,3v6,2j,z,19,r,13,1a,3,g,d8,w,o,q,i,u,1c,14,1i
1y,m,g,i,i,m,1s,1w,2u,40q,3t,m,x,r,14,5,1w,4u,ce,22,1a,u,s,10,1u,s,s
3q,1c,12,14,18,1i,4e,52,84,3ve,3b,19,r,j,n,14,d,k,dc,10,q,k,m,y,18,12,1k
3o,18,10,12,16,1g,4c,4q,82,3uy,21,1n,j,j,n,m,c,11,de,10,o,q,i,10,14,10,1k
2m,s,q,s,s,12,30,38,5q,3sm,c,v,0,h,p,1g,22,n,cs,1g,s,k,m,14,14,q,14
3k,16,10,10,16,1e,46,4m,7u,3ue,11,1h,t,l,j,o,g,1l,de,y,o,q,m,y,12,10,1k
2o,s,o,o,s,10,2u,32,5g,3tc,0,1d,s,p,9,1a,22,h,d6,1g,q,m,k,y,12,q,10
34,y,u,w,y,16,3k,3q,6o,3t8,2,b,z,x,8,q,26,29,dc,18,k,q,o,y,10,s,1g
24,o,i,k,i,m,20,1w,2u,40i,47,6,2z,1i,1u,1t,4i,5k,dg,1g,10,q,14,w,1q,s,u
3q,1c,10,14,18,1i,4e,50,84,3vi,3j,x,z,l,t,1c,d,m,da,y,q,m,k,10,18,12,1k
3g,1a,y,14,16,1g,44,4s,7w,3uc,23,9,1j,r,x,1e,b,a,d8,y,o,q,k,y,18,10,1i
3a,16,y,10,12,1e,3y,4i,7k,3u4,v,r,1p,n,r,w,7,d,d6,10,o,q,k,y,16,10,1g
2e,q,k,m,m,u,2e,2m,4e,3vg,1z,h,1,m,7,1e,1k,20,d6,1e,w,o,w,s,14,q,s
34,12,u,y,12,18,3s,48,76,3tm,n,5,1b,r,n,k,0,v,d2,16,k,o,m,10,14,y,1g
40,1a,12,12,18,1g,48,42,6e,3mw,n,v,19,r,k,5,32,4a,f6,g,e,g,10,e,1c,12,y
30,q,k,o,m,y,28,26,38,3um,3,a,29,c,1r,2e,y,20,ew,u,g,m,k,6,o,k,q
32,s,k,s,o,y,2c,2a,3e,3tw,v,8,3x,14,l,2w,1s,1q,f2,u,g,w,c,a,q,o,s
48,1a,12,14,16,1i,3w,42,6k,3ng,1j,z,z,7,2,2u,2o,34,f2,g,g,c,g,u,1g,12,y
28,k,c,g,g,m,1q,1q,2q,3wq,1d,1u,1c,w,e,1n,1b,2,f0,18,14,q,k,i,12,16,o
2w,o,k,k,o,u,26,24,36,3uc,m,d,7,p,11,10,w,1k,eu,u,g,i,o,a,k,i,o
32,u,o,o,u,12,2w,30,4q,3r4,f,1,a,j,h,0,3,i,fa,10,y,i,i,c,18,10,1c
3a,y,u,w,w,14,38,32,4w,3r2,1r,1v,1z,5,g,g,2c,24,f6,s,e,o,g,e,w,u,w
2s,q,k,q,q,w,2a,2a,3e,3t4,i,17,1z,15,1,2o,1q,20,f4,w,k,u,c,8,m,k,q
3q,16,y,y,12,1a,3m,3m,5w,3p6,21,1z,p,4,f,16,2c,2a,f4,k,e,c,e,q,12,y,y
44,1a,12,14,16,1i,3y,40,6m,3my,x,13,11,j,a,2e,2y,36,f2,g,e,e,o,m,1e,12,y
2c,k,c,e,g,i,1g,1k,2a,3u2,a,40,28,20,k,14,1k,3g,fg,1u,u,y,s,1q,2i,1a,2m
40,1a,12,12,16,1g,40,3y,6k,3my,x,z,15,r,i,1y,34,38,f2,i,e,e,u,g,1c,10,y
38,u,m,w,s,10,2m,2k,3w,3sk,1b,1,3t,k,d,2q,20,1o,f6,s,i,s,a,e,w,u,s
40,1a,10,12,14,1e,3s,3w,6i,3o8,2d,11,x,1,0,2q,2a,26,f2,i,e,e,c,u,18,10,w
46,1c,10,12,16,1g,3u,40,6m,3o4,29,z,11,f,g,36,2i,2i,f2,i,e,e,q,i,1c,12,w
40,1a,10,12,16,1g,3u,3y,6q,3nc,1f,13,15,7,5,32,2w,1y,f2,g,e,e,c,u,18,12,y
40,1a,10,14,14,1g,3u,3y,6q,3ng,1l,13,15,3,7,3a,2u,1q,f2,g,e,e,a,w,18,12,y
3e,y,u,10,y,18,3i,3i,5g,3qa,1n,1b,1j,2,6,8,w,3g,fc,u,u,c,g,g,12,10,w
3m,10,y,w,y,16,3e,38,58,3qa,19,2t,17,c,0,u,26,1q,f6,o,e,e,o,g,10,w,y
2i,i,g,g,g,k,1m,1q,2m,3ss,1i,3a,1g,2u,u,2,18,3s,fi,1o,w,u,q,10,2y,1a,1c
2i,m,i,k,m,s,28,22,36,3ty,y,f,k,r,v,r,1q,1e,f8,y,10,m,e,e,q,i,m
3e,y,y,w,10,1a,3g,3i,5w,3qe,1p,1z,z,11,a,1e,1w,1m,fa,q,c,e,u,a,y,10,10
2m,m,k,m,o,u,28,26,38,3tk,14,17,d,1f,v,1k,1c,1y,f4,w,q,q,c,c,k,g,m
2y,s,m,q,u,10,2m,2k,3y,3s4,b,p,v,1p,j,16,1y,1s,f6,w,s,o,c,a,w,s,q
3g,12,w,y,10,1a,3o,3m,5m,3pc,x,25,3,1,c,h,12,3u,fe,q,i,q,i,g,18,12,u
38,y,s,w,y,14,38,38,54,3r4,1z,z,25,l,8,e,1s,2g,f8,s,g,o,g,c,w,w,u
3i,12,y,y,12,1a,3k,3o,64,3pq,21,1n,19,r,4,1y,2a,1q,f8,m,c,e,o,g,10,y,y
38,s,m,s,u,w,2g,2e,3m,3u0,17,4,2z,f,a,2g,1w,1e,f6,s,i,u,a,e,w,s,u
2e,k,e,e,g,i,1k,1m,2g,3ti,s,3q,20,24,8,u,1u,3c,fi,1u,u,y,s,1q,2e,1a,2o
2e,k,c,g,g,i,1i,1m,2g,3ts,o,3q,1m,2a,1w,4,18,2i,fe,1o,w,w,s,y,32,18,1w
2s,o,k,m,o,u,2g,2a,3o,3sa,10,h,u,r,k,23,1a,0,f8,10,y,o,e,e,10,s,1k
2w,o,m,o,m,y,28,26,38,3u4,e,p,2f,s,1l,2e,14,24,ey,w,g,u,c,8,m,k,q
3c,u,o,s,u,y,2m,2i,3q,3u8,11,d,2j,j,a,1i,1u,1q,f8,q,k,s,a,e,10,w,s
32,q,k,q,m,y,28,28,38,3ui,b,g,3d,y,1f,2k,14,26,ey,u,g,s,e,8,o,m,s
3a,w,u,u,w,12,2y,2w,4k,3rg,15,25,2f,e,2,1g,2e,1m,f6,s,g,m,g,e,w,u,u
40,1a,10,12,14,1e,3u,3w,6m,3nw,21,13,z,3,7,2s,2m,22,f2,g,e,e,c,u,18,10,y
3i,10,y,w,10,16,3e,3c,5a,3q6,1n,2p,13,4,2,q,2i,1y,f6,m,e,g,k,i,y,w,y
30,q,m,q,o,w,2a,28,3c,3u4,j,3,3l,1e,11,2o,1e,26,ey,w,g,w,c,8,o,m,s
48,1c,10,14,18,1i,3y,40,6m,3n6,17,z,x,d,6,2i,2q,3e,f2,g,g,e,m,o,1g,12,10
2g,k,g,i,k,q,20,20,2y,3v0,c,4,4,q,1n,n,u,1a,f0,10,y,o,g,e,o,e,i
36,s,m,s,q,w,2e,2a,3i,3ue,15,a,3h,i,b,2m,1s,1g,f4,s,i,u,c,c,s,s,u
3o,12,y,y,y,18,3i,3e,5e,3pw,1h,2t,v,4,8,s,26,1s,f6,m,e,c,o,i,10,w,10
3e,w,s,w,u,14,2w,2w,4g,3rk,17,1l,2p,q,d,1u,24,1q,f6,q,i,k,g,g,y,w,s
2o,m,i,i,m,q,24,22,30,3ue,1q,r,c,f,p,f,y,12,ey,w,k,s,e,e,k,i,k
30,q,k,m,m,u,28,22,36,3tm,1,c,b,6,1d,m,1g,18,em,s,g,g,c,g,o,i,m
2i,m,g,i,k,o,22,22,34,3tg,18,e,16,1e,y,2v,b,z,fa,12,12,o,k,i,12,14,1s
2m,k,k,i,m,s,24,22,34,3u2,1g,p,7,d,x,4,12,1g,f0,w,k,w,e,c,k,i,k
44,1c,10,12,16,1e,3s,3y,6k,3oe,2j,z,11,9,i,36,2c,26,f4,i,e,e,o,i,1c,10,y
4a,1c,12,14,18,1i,40,42,6k,3n4,11,z,t,j,e,1y,2q,3m,f4,g,g,e,u,i,1i,12,10
2i,k,g,i,g,m,1s,1q,2w,3t8,1a,1i,1c,24,16,3,12,1,fc,14,14,s,m,o,24,1a,1k
38,y,q,u,10,14,3a,3a,58,3qk,1l,1,r,13,0,g,e,2c,fa,w,w,e,g,e,12,10,10
32,o,k,o,o,w,28,26,38,3uu,9,g,1v,5,1n,2a,y,1y,ew,u,e,i,o,6,o,k,q
30,q,m,o,q,w,2m,2s,4g,3r6,8,6,12,i,16,s,2v,k,fe,12,16,i,i,m,14,1w,q
2s,o,k,o,s,w,2a,2a,3g,3t0,u,1f,v,2b,4,2g,1w,1q,f6,w,o,q,c,a,m,m,o
2s,o,k,k,m,u,2a,2e,3u,3rm,16,i,1g,14,3,n,6,l,fc,12,16,m,i,k,1u,10,1g
44,1c,10,12,16,1g,3u,40,6o,3no,1v,z,11,b,4,36,2q,28,f2,g,e,e,g,s,1c,10,y
30,s,m,s,s,y,2e,2e,3m,3sw,n,7,3p,7,8,32,26,1g,f2,w,i,u,c,a,s,o,s
3g,10,w,y,y,16,3g,3a,5c,3qe,27,1x,1l,4,8,e,2c,2c,f6,o,e,i,i,g,y,w,w
3g,12,y,y,10,1a,3k,3m,60,3py,25,1l,1j,h,4,1g,1y,20,f8,m,e,e,m,g,y,y,y
48,1c,12,14,18,1i,44,42,6i,3mq,l,z,z,h,c,14,2w,40,f4,g,g,e,w,i,1i,12,10
4a,1e,10,14,18,1i,3y,42,6k,3nq,1r,v,v,d,e,2g,2i,3e,f4,g,g,e,q,k,1k,10,12
2o,m,g,i,k,o,1w,24,3a,3rw,1m,1w,1u,22,14,e,z,1w,fi,1e,12,q,o,q,2a,1o,w
2s,o,k,m,o,s,2i,2c,3s,3s0,16,f,14,5,u,33,16,n,fa,10,y,o,g,g,10,w,1q
3c,w,u,w,w,16,3g,3g,5e,3q0,11,r,4,c,4,11,9,26,fg,w,o,q,g,g,1a,14,14
2q,k,k,i,k,o,22,22,3e,3rk,1w,1o,1w,1k,12,f,1k,16,fk,1k,y,q,o,y,2i,1a,w
2k,k,e,i,i,k,1s,1s,2u,3sa,1w,2q,1s,20,a,8,28,2m,fk,1q,w,u,q,1e,2i,1a,1i
2s,m,k,i,m,q,24,2a,3k,3re,1i,1e,1s,1q,1a,f,0,1a,fi,1e,12,o,m,q,28,1k,u
3y,18,10,12,16,1g,40,3w,6e,3n8,1b,l,19,v,k,18,38,3q,f6,g,e,e,y,e,1a,10,w
3q,16,y,10,14,1c,3s,3s,68,3os,2v,r,z,7,f,16,2c,2g,f6,g,e,c,g,o,12,y,10
32,o,k,m,o,w,26,26,36,3uc,5,i,r,p,1l,1y,y,1q,es,s,g,g,m,a,m,i,o
4a,1c,12,14,18,1i,40,42,6k,3na,19,x,t,j,g,22,2m,3m,f4,g,g,e,u,i,1k,10,10
2q,k,i,i,k,q,22,20,2y,3ui,1u,p,g,6,1,z,i,u,ew,w,i,o,i,e,m,i,o
3w,18,y,10,12,1c,3o,3q,64,3p0,2d,1l,v,6,4,20,22,1w,f4,k,e,c,g,q,14,10,y
34,s,o,u,u,10,2m,2m,3y,3s8,11,9,3j,8,j,2i,20,1s,f4,w,g,u,a,e,u,s,s
2m,m,i,k,m,s,24,24,32,3u2,1i,v,3,p,r,8,12,1g,f0,w,k,u,e,e,i,i,k
3s,18,10,10,16,1e,3u,3w,6e,3nu,23,d,11,11,a,20,34,2m,f6,i,c,e,s,g,18,y,y
34,q,k,s,o,w,2c,28,3c,3ue,t,e,3x,1g,15,2q,1k,1y,f0,w,g,s,e,8,s,m,u
2y,q,m,s,q,y,2c,2a,3k,3t0,9,f,3h,9,6,34,20,1o,f2,w,i,w,a,a,o,m,s
36,s,k,s,o,y,2c,2a,3e,3uq,13,i,3l,u,r,2i,1m,1k,f2,u,g,s,e,a,s,q,u
32,q,k,s,m,y,2a,28,3a,3uc,l,8,3r,1i,1b,2o,1e,22,f0,u,g,u,e,8,q,k,u
2a,k,e,e,i,k,1q,1u,2o,3wc,15,1w,1e,y,y,11,33,c,f4,16,16,q,m,i,16,1m,u
2i,i,g,g,g,k,1o,1o,2m,3ss,1g,36,1u,2a,e,c,1y,3a,fk,1q,u,w,s,1e,2m,18,20
40,1a,12,14,16,1g,44,40,6e,3mw,r,v,17,p,i,k,36,48,f6,g,e,e,10,e,1e,10,y
36,u,m,s,u,y,2g,2g,3o,3t8,19,6,35,p,i,2q,22,1g,f4,u,i,s,c,e,u,s,u
44,1c,10,12,16,1i,3u,40,6o,3ne,1j,11,13,9,0,38,2u,28,f2,g,e,e,c,w,1c,12,y
36,s,k,s,q,y,2c,2c,3g,3u8,13,a,3d,a,7,2o,1u,18,f4,u,g,u,c,c,s,q,u
46,1c,12,14,16,1i,40,40,6k,3mu,r,11,z,l,e,20,2w,3m,f2,g,g,e,w,g,1g,12,y
3g,12,u,y,12,18,3k,3i,5s,3pm,29,5,13,11,a,10,1m,2s,fa,q,m,e,m,e,12,10,w
2e,m,g,i,k,q,20,20,30,3v8,1,c,a,c,13,17,10,1a,f2,10,10,o,g,e,q,e,i
3w,18,10,14,16,1g,46,44,6c,3ng,11,r,19,n,e,j,28,4c,f8,k,c,k,u,g,1c,10,y
2g,w,s,s,q,u,26,2i,3m,3xc,5l,33,4,2e,2w,5q,27,47,e6,q,a,1,3,1,4,8,q
28,e,c,c,a,e,10,y,1g,3xw,m,1u,24,2q,30,5g,18,2y,ee,14,q,s,q,k,1i,22,22
2a,i,c,e,e,i,1c,1e,24,3vm,n,30,2k,1g,26,1c,1c,1m,f8,1c,18,u,s,s,2u,1i,30
28,i,c,c,g,g,1e,1e,22,3uy,h,46,3e,u,h,22,14,5c,fg,24,w,10,w,2c,24,36,1s
2g,o,i,k,i,o,1o,1u,2q,3wk,1z,x,y,26,1g,3q,z,1b,ee,q,i,a,4,4,e,q,m
2o,o,k,i,k,o,1u,1w,2s,3uw,1,1f,12,1o,1g,1y,3,0,ei,q,i,c,4,8,k,m,q
22,e,c,a,c,g,12,12,1o,3wm,f,2e,3i,2a,q,2m,2k,3y,ew,1o,1e,s,w,16,32,2s,2q
1y,e,a,c,a,c,10,12,1g,3v8,w,1s,3e,32,15,5g,2s,6g,ew,1w,1o,10,y,2k,2a,4a,1i
1u,c,a,c,a,c,w,w,1c,3us,1m,1i,2i,2y,e,5u,3g,7i,eq,1w,1s,18,y,2e,2g,4i,1o
20,c,c,c,a,e,10,10,1k,3vs,c,28,3k,2y,1j,5c,2i,66,ew,1u,1k,y,y,2k,26,46,1o
1y,c,a,a,8,a,s,o,10,3yk,1a,g,2i,3s,3w,76,3k,7e,du,1i,u,w,s,s,1s,2o,3c
26,i,e,c,g,g,1c,1g,28,3wc,15,2s,2g,1e,1s,18,r,g,f4,1e,16,u,q,q,2c,1q,2o
28,g,e,c,e,i,1e,1g,2a,3x2,p,1e,2g,14,1y,y,19,2r,es,16,y,q,o,m,18,1a,1m
22,g,c,a,c,g,16,16,1q,3v4,3,32,3s,22,1f,3w,26,66,f6,26,18,y,y,2k,2a,3y,1a
1y,c,c,a,a,e,y,y,1i,3x2,1a,u,36,2w,1i,2c,2g,4k,em,1q,1e,10,w,u,3e,3k,26
2c,i,c,e,e,e,14,12,1i,3vk,u,1a,16,26,2w,50,36,42,ec,y,q,k,k,i,1a,12,1i
1u,c,a,8,a,a,q,o,y,3xm,1w,h,2w,3q,3o,4k,5y,7m,dk,1w,10,12,u,w,30,28,56
1e,a,8,6,6,a,m,k,s,3y6,1g,w,2o,2m,18,52,58,bo,by,2u,1e,12,1k,1i,42,5u,2a
22,g,c,a,c,g,14,14,1u,3x4,j,1c,3g,2a,1m,28,k,t,ek,1g,12,s,s,m,20,26,20
2a,k,e,g,i,m,1s,1s,2q,3w8,p,y,12,w,4,1r,d,m,ey,12,12,o,k,g,y,s,k
2q,q,k,m,k,q,20,22,2y,3v8,l,1r,u,1s,1k,1q,t,5,ei,q,g,c,2,6,k,g,q
28,g,c,e,e,g,18,1e,26,3xg,l,1g,2m,16,28,2c,13,3t,eq,16,u,u,o,k,1c,1a,20
26,g,c,c,e,i,1c,1e,2a,3x2,11,1g,32,10,1q,7,1p,13,eq,1c,12,o,q,m,1g,1y,14
1k,c,8,6,8,a,m,m,u,3xg,1w,s,1u,24,34,4u,5s,ag,ci,2i,18,12,1c,14,3s,40,3s
1i,a,8,6,8,a,m,m,u,3yg,1i,s,3a,1c,24,56,4w,ay,cc,2s,1c,14,1m,18,3w,5k,2a
2c,o,g,i,i,k,1k,1q,2i,3x4,1x,0,16,1s,16,2w,h,15,e6,s,i,8,4,6,i,e,w
28,g,a,c,c,e,y,w,1a,3x6,i,1w,1q,2y,38,5y,2u,4i,ea,12,o,q,o,k,1i,1i,22
22,e,c,a,a,c,w,u,18,3ya,e,1o,2q,38,2m,6a,1m,5k,ea,1c,s,u,s,o,1q,2q,1y
20,e,c,a,c,e,12,14,1s,3x8,8,w,3a,2m,14,1u,1k,0,ek,1k,1a,s,u,q,2q,1q,2k
1u,a,a,c,8,c,w,u,1a,3vy,1q,1m,2c,2s,6,5s,3o,7s,ei,1w,1k,1c,10,2a,2k,4c,1s
26,i,c,c,e,g,1a,1c,1y,3v6,l,40,3g,1c,j,2q,1u,4k,fc,22,y,y,y,2g,24,2i,2i
1o,c,8,a,8,a,s,q,12,3xe,1w,26,1o,22,2w,3c,5c,8y,dc,2k,16,1m,12,y,44,4g,1w
1q,g,e,e,g,m,1m,1m,2i,3jg,3w,o,10,20,6,1s,26,30,e8,2m,1c,q,c,i,i,4,e
4a,1e,12,12,18,1e,3w,42,6k,3os,3v,n,v,7,12,32,22,3c,f6,e,e,c,g,q,1e,12,14
4c,1e,12,12,18,1g,3w,42,6k,3oy,3t,p,t,5,y,2w,22,3c,f6,e,e,e,e,s,1i,10,16
42,1a,10,10,14,1c,3s,3w,6g,3q4,49,15,t,6,2,2k,2a,26,f0,g,e,c,c,s,18,12,12
38,u,m,q,s,y,2g,2e,3m,3v2,1b,2,2v,x,1,20,1w,1e,f4,o,g,e,o,c,w,s,w
3a,s,o,q,u,y,2k,2i,3q,3uy,19,5,2l,1l,5,20,1y,1m,f2,o,g,e,m,c,y,u,w
4a,1e,10,14,16,1i,3w,42,6m,3pm,43,n,t,5,6,2y,28,3c,f2,e,g,c,c,u,1i,12,16
40,1a,10,10,12,1c,3s,3u,6c,3qa,49,1b,p,c,3,28,26,2a,f0,i,e,c,a,s,18,12,10
40,18,y,10,12,1c,3q,3s,66,3qc,3v,1p,p,g,6,1i,20,2i,f2,k,e,e,k,i,18,10,y
3o,10,y,u,y,18,3c,3c,56,3sa,1j,3l,r,u,1x,w,28,26,f0,k,e,e,a,q,12,w,10
2u,o,k,o,q,w,28,28,36,3ve,c,13,1l,2n,4,o,1a,2k,eq,q,e,c,8,4,w,e,q
30,q,k,q,o,y,28,2a,38,3vc,d,4,2t,17,11,26,18,2k,eu,q,e,e,e,e,o,m,s
2y,o,k,o,s,u,2a,28,38,3ve,0,f,23,21,l,1g,18,2k,es,q,c,e,8,8,u,i,s
38,s,m,s,s,w,2g,2e,3m,3v2,1b,6,37,j,9,24,1y,1a,f2,o,i,e,m,e,u,s,w
3u,16,y,y,10,1c,3m,3m,5u,3r6,3h,2f,j,y,15,18,20,2q,f0,k,e,c,8,s,16,10,10
2y,o,k,o,q,u,2a,28,36,3ve,0,l,1v,27,b,1e,18,2g,eq,s,c,e,8,8,w,i,q
3m,w,w,u,u,1a,32,32,4s,3sw,j,41,15,s,29,1w,24,1w,f2,m,e,e,8,s,12,w,y
4c,1e,12,14,16,1g,3y,42,6m,3pg,45,n,n,3,k,2q,22,3a,f2,e,g,e,a,w,1k,10,18
3a,s,m,s,s,y,2g,2g,3m,3v0,19,0,2z,l,7,20,1w,1i,f4,o,i,k,i,c,w,u,u
36,q,m,s,o,y,2c,2c,3g,3v4,13,g,3n,0,t,2g,1s,1i,f0,q,e,g,m,c,q,q,w
3a,s,o,q,u,y,2i,2g,3o,3uy,19,3,2r,v,5,1y,1w,1g,f4,o,i,i,k,c,y,u,u
2q,m,k,m,q,u,28,28,36,3vm,14,1p,13,35,1,5,14,2m,eq,q,c,8,a,2,s,e,k
3c,u,o,s,u,10,2o,2m,3y,3ug,v,j,2b,19,p,1e,20,1m,f2,k,i,c,i,e,w,u,w
3i,u,s,u,u,14,2y,2u,4c,3to,t,1x,2d,5,l,y,20,1y,f4,m,g,e,m,e,10,y,w
30,o,k,q,q,w,28,2a,38,3vg,1,1,2j,1n,x,1k,18,2i,es,q,c,e,8,c,q,k,s
36,s,k,s,s,w,2g,2c,3k,3v8,15,e,3d,x,n,2a,1u,1i,f0,o,e,e,m,8,u,q,w
32,q,k,q,q,w,2c,28,3c,3va,f,e,3b,p,1h,2c,1e,2a,eu,q,e,c,e,g,o,m,u
2w,m,m,m,q,u,28,28,38,3vc,e,z,1j,2h,3,u,18,2g,eq,q,e,c,a,4,w,g,o
3s,14,y,w,10,18,3i,3g,5i,3rg,2d,2v,t,o,9,i,1y,2g,f2,m,e,e,g,m,14,y,10
2q,o,k,m,q,u,28,26,38,3vi,w,1j,19,2v,1,0,16,2k,eq,q,c,a,8,4,u,c,m
30,q,k,q,o,w,2a,28,3a,3ve,b,2,2r,19,11,26,16,2o,eu,q,e,e,c,g,o,m,s
36,s,k,s,q,y,2e,2e,3k,3v8,z,c,3f,r,t,2a,1u,1g,ey,o,g,e,i,c,s,q,w
3i,u,q,u,u,16,2u,2w,4c,3tg,d,1t,23,3,1j,18,22,1u,f2,k,g,c,c,m,y,w,w
44,1c,10,12,16,1c,3u,3y,6o,3ps,4l,t,t,6,c,2y,2e,1w,ey,g,e,a,c,s,1a,12,14
3c,u,m,s,w,y,2m,2i,3u,3uq,17,f,2l,1h,c,1g,20,1s,f4,m,i,e,m,e,y,w,w
2o,m,k,k,o,w,26,26,36,3vy,18,1b,11,2h,1d,4,g,2g,em,s,c,a,a,2,k,i,i
3e,u,q,s,u,10,2q,2o,3y,3ua,z,13,2h,j,5,1c,1y,1u,f6,m,i,g,k,e,10,y,u
44,1c,10,12,14,1e,3s,3y,6m,3pw,4d,v,x,2,a,2u,2e,26,f0,g,e,c,c,s,1a,12,12
2y,o,k,o,q,w,28,28,38,3ve,1,f,23,1z,l,1m,18,2i,es,q,e,e,8,a,u,i,q
30,o,m,o,q,w,2a,28,3a,3vc,3,0,2n,1d,1d,22,18,2o,eu,o,e,c,a,i,m,k,s
32,q,k,s,o,y,2a,2a,3c,3v6,j,c,3j,b,1b,2e,1e,2a,ew,q,e,e,g,e,o,m,w
2u,o,k,o,q,u,28,28,38,3vk,g,15,1l,2r,6,c,1a,2i,eq,q,c,c,6,4,u,g,o
34,q,k,q,q,w,2c,2a,3c,3v6,r,i,3h,d,13,2i,1i,24,ey,q,e,e,k,c,q,m,w
32,q,m,q,o,y,2a,2a,3c,3v0,n,o,3j,h,15,2i,1g,28,ew,q,e,e,o,8,o,o,w
2w,o,k,o,q,u,28,28,38,3vc,a,x,1r,2d,3,y,1a,2i,es,q,c,c,a,6,u,g,q
3k,y,s,w,u,18,32,32,4q,3sw,t,2f,1n,i,23,10,22,26,f2,k,e,c,a,q,10,w,y
3a,u,m,s,s,10,2i,2g,3q,3uu,17,5,2t,r,5,1u,1w,1m,f4,o,i,m,g,e,w,w,u
34,q,k,q,o,y,2a,28,3a,3vc,j,m,3f,f,15,2g,18,28,ew,s,e,e,o,a,o,m,u
38,u,k,s,s,y,2g,2e,3m,3v2,1b,4,2v,z,1,20,1y,1c,f4,o,g,e,o,c,w,s,w
3u,14,y,y,y,1a,3i,3i,5m,3r8,2l,2p,v,o,5,s,22,2a,f2,m,g,c,m,g,16,y,y
3q,y,y,w,w,18,3e,3a,56,3s2,1f,3f,15,s,l,6,22,2a,f2,m,e,e,e,o,14,w,10
3s,12,y,y,y,1a,3i,3i,5k,3rm,25,3d,f,u,1t,12,24,26,f0,k,c,c,8,s,14,y,10
2y,o,k,o,q,w,28,28,38,3vc,2,j,25,1z,h,1e,1a,2i,es,q,e,c,8,a,u,i,q
2w,o,k,o,q,w,28,28,36,3ve,4,t,1t,2b,5,12,1a,2i,es,q,c,c,a,6,u,i,q
32,q,m,q,o,y,2a,28,3c,3v8,n,i,3f,f,15,2i,1a,2a,ew,s,e,e,m,a,o,m,w
2y,q,k,o,q,w,28,28,38,3ve,3,9,29,1r,p,1s,16,2k,es,q,e,e,a,e,q,k,q
34,q,k,q,q,y,2a,2a,3c,3v6,t,i,3h,d,13,2i,1i,24,ey,q,e,e,k,c,o,o,w
4a,1e,10,14,16,1g,3w,42,6m,3pm,45,n,t,3,4,2y,2a,3c,f0,g,e,c,c,u,1i,12,16
36,q,m,q,q,y,2c,2a,3g,3v6,11,i,3l,0,t,2e,1m,1q,f0,q,g,e,o,a,s,q,w
3i,w,s,u,w,16,2y,2y,4i,3ti,z,2l,1l,0,1p,1a,24,22,f2,k,i,c,g,k,10,y,w
3m,y,u,w,w,18,38,34,4y,3so,15,2r,17,g,23,s,20,2c,f2,k,e,c,a,o,12,w,y
38,s,m,s,s,y,2e,2g,3k,3v4,1b,4,2z,19,0,26,1y,1i,f2,o,g,e,m,c,w,s,w
2y,o,k,q,q,u,2a,28,38,3ve,1,d,27,1v,p,1k,18,2k,es,q,e,c,a,a,s,i,s
2w,q,k,o,q,w,28,28,38,3ve,2,n,1x,25,f,1e,18,2k,es,q,c,e,8,a,u,i,q
30,o,m,o,q,u,2a,28,38,3vg,7,0,2d,1l,t,1y,14,2g,es,s,e,e,c,e,q,i,s
2u,o,k,o,q,u,28,28,38,3ve,e,15,1l,2p,8,i,1a,2k,eq,q,e,a,8,4,w,e,q
2q,m,k,m,q,u,26,28,36,3vm,y,1h,19,2r,b,1,12,2k,eq,q,c,a,8,4,s,c,k
2u,m,m,m,s,u,28,26,38,3vi,k,17,1j,2t,6,a,1a,2i,eq,q,c,a,8,4,u,e,o
2y,o,k,o,q,w,28,28,38,3ve,1,f,1z,21,h,1i,16,2i,es,q,e,e,8,a,u,i,q
2w,o,m,o,q,u,2a,28,38,3vg,4,n,1z,29,b,14,18,2k,eq,q,e,c,8,8,s,i,q
34,q,m,q,q,y,2a,2c,3e,3v4,x,i,3l,7,11,2k,1o,1u,ey,q,e,e,m,a,s,o,w
36,s,k,s,q,w,2e,2a,3g,3v8,15,e,3l,a,v,2g,1s,1i,f2,q,e,g,o,c,s,q,u
2y,q,k,o,q,w,2a,28,38,3vg,0,7,2d,1t,v,1k,18,2k,es,q,c,e,8,c,q,i,s
3e,u,o,s,u,10,2o,2k,3w,3uk,17,n,2j,x,0,1a,1w,20,f6,k,k,c,o,e,y,y,u
32,q,k,m,o,w,28,26,38,3v0,9,k,27,6,1p,26,u,1w,eu,u,e,g,o,6,o,k,q
32,q,k,o,o,y,28,26,38,3ve,b,m,2h,p,19,28,w,24,ew,q,g,g,m,8,m,m,s
30,12,o,q,o,u,28,2i,3e,3oo,25,2u,2q,2w,2w,2o,39,2h,ea,g,2,2,1,4,a,e,q
1o,c,a,c,c,e,16,16,1s,404,1m,j,13,a,b,0,1i,24,bk,2a,w,i,e,m,s,g,g
1u,c,8,8,8,a,o,m,w,42a,1m,8,1m,1k,3c,5c,54,4w,cg,1y,u,o,q,q,1g,1c,2u
32,q,k,o,o,w,28,26,38,3ve,9,k,2f,p,1b,24,u,24,eu,q,g,g,k,8,o,k,q
1s,e,c,a,c,e,10,10,1g,3ty,3o,7,18,1k,2c,40,2c,24,ca,2e,u,g,i,k,14,s,w
32,q,k,o,m,y,26,28,36,3uy,b,k,25,0,1l,28,w,1y,ew,s,e,i,o,6,o,k,q
26,s,k,m,k,o,1s,1w,30,3ye,5p,1f,12,2k,28,4m,j,3h,de,1g,e,2,0,2,6,a,o
32,10,q,o,o,u,28,2i,3e,3om,1x,2s,2o,2w,2w,2m,39,2h,ea,g,4,0,1,4,a,e,q
34,q,k,o,o,y,2a,28,38,3vi,l,o,37,7,1d,2e,14,28,ew,s,g,e,q,6,q,m,s
30,w,q,o,q,s,2g,2i,36,3vy,41,t,o,26,26,4,23,1u,ee,s,c,4,2,2,8,g,w
32,q,k,k,o,w,26,24,36,3u6,1,o,j,v,1j,1s,w,1q,eo,s,e,g,e,i,m,i,m
1s,a,8,8,8,a,q,m,y,42q,1e,a,w,1g,2m,5u,4k,42,c2,24,y,m,m,o,1g,16,2e
36,s,k,s,o,y,2c,2a,3e,3v8,13,k,3p,m,x,2c,1k,1k,f2,q,g,m,k,a,s,o,w
22,e,a,a,a,c,y,y,1i,41c,m,1g,1e,1o,26,38,1m,d,dm,18,m,s,m,m,1g,y,1i
1s,c,a,a,a,c,y,y,1g,41m,1k,l,i,w,10,2w,2k,30,c4,26,s,g,o,k,12,u,u
32,q,k,o,o,w,28,26,38,3v8,9,m,2h,3,1j,24,u,20,eu,s,e,i,m,6,o,k,q
2e,i,e,g,g,m,1u,1s,2q,3y4,q,14,n,a,1h,2j,3,8,ec,u,m,i,c,k,o,e,i
34,q,k,m,o,u,24,24,34,3u2,1j,1c,1,e,1b,10,i,1i,ei,u,e,a,8,c,u,i,m
2e,i,e,c,e,g,1a,1a,20,3wa,1c,1a,12,28,2q,3c,o,1n,em,w,q,q,m,i,18,12,1s
34,s,k,m,o,u,26,26,32,3u2,1z,1g,8,u,11,2,i,1q,eg,u,c,8,8,a,s,g,o
32,12,o,o,q,s,2a,2g,3e,3oi,1j,2u,2q,2s,2u,2a,39,29,ea,g,4,0,0,2,c,e,o
32,o,k,m,m,w,24,24,36,3u0,7,i,d,h,21,1g,u,1q,ei,s,e,e,a,i,k,i,k
34,u,m,o,o,s,2c,28,32,3um,35,1g,u,16,6,11,a,2m,ei,s,c,6,6,6,m,g,u
2a,g,c,c,e,i,1e,1k,2a,3zc,a,1i,w,e,1c,2,37,t,e6,w,o,k,i,i,10,o,o
34,u,m,o,q,s,2e,2a,32,3vc,3t,u,s,1e,y,x,b,2q,eg,u,c,4,6,4,k,i,s
32,q,k,m,o,u,26,26,38,3us,3,o,1l,h,1n,1w,s,1w,eq,s,e,g,g,e,k,k,m
34,q,k,q,o,w,2a,28,3a,3vg,n,s,3b,2,1d,2g,12,22,ey,s,e,g,q,6,q,m,u
1k,a,8,6,8,8,k,k,q,42o,1a,m,m,u,20,7s,4o,5k,bg,24,18,u,m,o,1u,12,38
2w,10,q,q,o,u,28,2i,3g,3r8,3l,1k,2k,2o,2s,36,35,2p,ea,k,2,0,1,2,a,c,q
32,q,k,k,o,u,26,26,36,3uc,7,o,x,d,1x,1o,u,1s,em,s,e,e,a,i,m,i,m
32,q,k,o,o,y,28,28,38,3vg,d,k,2r,x,11,28,10,28,eu,s,e,g,o,6,o,k,s
1e,a,8,6,8,8,m,m,u,40a,3g,m,i,a,10,5y,4u,6q,aq,1e,1w,w,q,k,1k,u,14
2k,k,i,k,m,s,24,24,34,3wi,10,f,x,1n,2b,j,g,20,em,s,g,a,c,8,o,a,i
2w,o,m,k,o,q,26,22,2u,3u2,4,p,f,o,y,s,1a,32,ei,u,g,e,6,8,o,g,o
36,10,o,o,q,u,2c,2i,38,3s0,2z,1q,2s,20,2m,6,31,c,ec,m,6,1,4,2,c,e,q
2k,k,g,i,k,s,22,22,30,3wo,16,3,r,13,2d,13,e,1k,ei,u,g,e,c,c,k,e,g
2o,m,k,k,q,u,26,26,36,3vs,10,1d,z,2r,v,0,w,2i,eo,s,e,8,a,4,s,a,k
1q,c,a,a,a,c,u,s,16,3wu,30,a,k,1e,12,4s,44,3i,by,28,u,i,k,m,16,w,14
2o,k,i,i,k,m,1s,1q,2i,3tq,24,z,y,o,4,3,1a,28,eg,q,i,a,8,a,u,m,m
22,c,a,a,8,c,u,u,16,40u,y,1q,1m,20,2s,5a,2o,1s,dk,18,m,q,o,o,1g,10,2c
34,10,o,o,q,s,2a,2i,3c,3q0,2f,2o,2q,2e,2q,1q,31,1r,ec,i,2,0,2,2,c,e,o
34,y,m,o,o,u,2i,2c,32,3um,3x,1a,1m,1k,1g,1v,n,2q,ee,s,a,2,4,4,e,e,y
2s,m,k,i,k,o,1y,1u,2o,3tm,1k,n,8,6,18,o,1c,22,em,u,i,e,i,e,s,i,o
32,s,m,m,o,u,26,24,34,3uk,25,10,1,w,r,k,c,1w,ei,u,e,a,6,a,s,i,o
32,10,q,o,q,s,28,2i,3e,3p4,2b,2u,2s,2o,2u,2g,37,2b,ec,g,4,0,0,2,c,c,q
32,q,k,m,o,u,26,26,38,3uo,3,o,1f,l,1j,1w,u,1u,eq,s,e,g,i,c,m,i,o
2m,k,g,i,g,m,1s,1u,2o,3uo,20,4,c,1g,q,d,l,0,eu,u,k,o,k,g,u,m,o
34,10,o,o,q,s,2a,2i,3c,3pi,1v,2m,2s,2e,2q,1u,33,1v,ec,i,2,0,0,4,a,e,q
2u,o,k,m,m,s,28,22,2y,3uc,g,11,o,e,6,1n,12,2k,ee,u,e,a,2,8,g,o,k
34,q,k,q,o,w,2a,28,38,3v2,n,o,39,m,1f,2i,14,22,f0,s,g,k,k,8,q,m,s
34,y,o,q,o,u,2i,2g,34,3v8,4p,a,1u,20,26,x,1x,2g,ee,s,a,0,4,2,a,g,w
1g,a,8,6,6,8,k,i,o,420,1u,m,s,c,1k,7u,5a,5m,b2,1u,1m,y,q,m,20,16,3e
2y,10,q,q,o,u,28,2i,3g,3q4,3z,2g,2u,2y,2y,38,37,2p,ec,i,2,0,3,4,a,c,q
32,10,o,q,o,u,2a,2i,3c,3t2,47,18,2g,28,2m,1m,2v,z,ee,m,8,1,2,2,a,e,q
30,q,k,o,o,w,28,26,38,3vc,3,8,27,15,15,20,s,2a,es,q,e,g,e,e,m,k,o
26,g,c,a,c,g,1a,1c,24,40m,5,1i,16,o,1y,4,1b,2d,e0,10,o,m,o,g,16,q,s
32,o,k,m,o,w,26,28,36,3v2,1,e,1v,r,1h,1w,q,22,eq,q,e,g,g,e,k,i,o
1s,a,a,8,a,a,u,q,14,426,1i,3,q,16,1y,4q,3y,3q,c2,26,u,k,m,m,18,10,1c
32,q,k,o,o,w,28,28,38,3ve,b,m,2h,n,1b,26,w,24,eu,s,e,g,m,8,o,k,s
2y,q,i,m,m,w,24,24,36,3u8,a,3,1,z,1z,14,s,1u,eg,u,e,e,8,c,q,i,i
34,10,o,o,q,s,2a,2i,3c,3om,1d,2s,2s,2m,2s,24,37,25,ea,i,2,0,0,4,a,e,q
24,g,c,a,c,e,14,1c,1y,410,1,1g,1e,s,1q,1u,25,15,dw,12,o,o,k,i,18,s,w
2m,k,i,g,i,o,1s,1w,2q,3uq,22,p,q,a,f,j,d,e,eg,q,i,c,e,c,u,i,k
36,10,o,o,q,s,2c,2i,3a,3qk,27,2e,2s,24,2o,u,31,p,ec,k,4,0,2,2,c,e,q
36,10,o,o,q,s,2c,2g,3c,3qi,29,2e,2u,26,2m,y,31,t,ec,k,4,1,4,2,c,e,o
2y,q,k,m,q,u,28,28,36,3va,2,3,1z,1j,r,1k,y,28,eq,q,e,g,a,a,s,i,o
2m,k,e,i,g,k,1i,1g,26,3u8,24,1,e,1g,1w,2s,18,28,em,u,m,i,k,g,10,s,w
2u,m,k,m,q,u,28,26,36,3va,u,11,11,2d,n,m,10,2a,eo,s,c,e,8,6,u,e,m
2m,k,g,i,g,o,1s,1u,2q,3uy,20,d,i,g,v,d,b,a,eg,q,i,e,c,e,u,i,k
2y,o,k,o,o,w,28,26,38,3va,6,d,1t,1v,j,1e,12,28,eq,q,e,e,a,6,w,g,q
2w,o,k,m,o,w,26,26,38,3v4,k,l,13,21,v,12,y,24,eo,q,e,e,a,6,s,g,o
2a,i,e,e,g,i,1o,1q,2k,3z8,2,1a,4,1,c,2n,1l,4,ea,u,o,i,i,g,u,i,i
2o,m,i,m,o,u,26,26,38,3vs,14,1f,r,2t,13,2,w,2g,eo,s,e,8,c,2,s,c,i
32,12,o,o,q,s,2a,2g,3e,3p2,1z,2s,2q,2m,2u,2a,37,27,ec,i,2,0,0,4,a,e,q
2y,o,k,m,q,w,28,26,38,3vc,6,j,1p,23,f,18,14,2e,eq,q,e,e,a,8,u,g,q
1w,e,c,c,c,g,14,14,1q,3ua,2y,b,18,1g,20,3s,1u,1g,cm,28,q,g,g,k,y,m,s
32,q,k,m,m,w,26,24,36,3u2,3,k,n,9,1r,1m,10,1m,eo,s,g,e,g,g,m,i,o
34,w,m,o,o,u,2g,2a,32,3us,3t,1e,1c,1e,12,1x,2,32,ei,s,a,4,4,4,i,e,y
2w,o,k,k,o,u,26,24,36,3uo,q,j,0,1l,1t,10,m,1y,ei,s,e,e,a,a,q,g,k
32,o,k,q,o,w,28,28,38,3vg,9,a,2h,19,11,26,y,2e,eu,q,e,g,g,e,m,k,s
32,q,k,m,m,w,24,26,34,3ty,f,u,j,5,1t,1i,y,1m,em,s,e,e,c,g,o,i,m
34,s,k,o,m,u,26,26,32,3ua,23,1u,8,i,p,8,2,1s,ei,u,a,8,6,a,u,g,o
2c,w,q,m,o,s,22,28,3i,3x0,63,25,1q,2i,2g,54,1j,4n,dw,w,a,0,1,0,4,a,m
2o,k,k,k,o,w,24,26,38,3vw,10,17,11,2j,19,0,u,2e,eo,s,e,8,a,4,s,a,k
2a,g,c,c,e,g,1e,1g,28,3z4,g,1k,w,k,1k,s,2r,15,e6,w,o,k,i,i,12,o,q
2c,k,e,g,i,o,1w,1w,2s,3yo,2,w,r,b,1b,3b,6,y,ee,u,m,e,e,g,m,c,g
34,o,k,q,o,y,28,2a,38,3vg,j,o,35,h,17,2e,12,2a,ew,s,e,g,o,8,o,m,s
32,q,k,o,o,w,28,26,38,3va,b,o,2j,9,1f,26,u,20,eu,s,g,g,m,8,m,k,s
34,s,k,o,m,u,26,26,32,3ug,2d,1c,6,u,r,g,e,1s,ei,u,c,a,6,a,s,g,q
36,10,o,o,o,u,2a,2g,3e,3ow,z,2o,2q,2e,2q,1k,35,1p,ec,i,2,0,2,4,a,g,o
36,10,o,o,q,s,2c,2i,3a,3qi,1z,2e,2q,24,2o,q,31,p,ec,k,4,0,4,2,c,e,q
32,q,k,m,o,w,26,26,36,3us,5,o,1p,b,1l,20,u,1u,es,s,e,g,k,c,k,k,o
30,q,k,o,o,u,28,28,36,3va,3,a,29,t,1b,20,o,28,eq,s,e,g,e,e,k,k,o
32,q,k,m,m,w,26,26,36,3ue,5,k,13,7,1p,1s,y,1o,eq,s,g,g,i,e,k,k,m
2c,o,g,i,g,m,1k,1o,2g,3x4,1x,2,18,1q,16,2u,f,z,e6,s,i,8,4,6,i,g,w
32,q,k,o,o,w,28,28,38,3vg,b,k,2j,p,1b,26,w,26,eu,s,e,g,m,8,m,m,q
24,g,a,a,c,c,12,14,1q,40g,c,1q,1k,1a,2a,3m,n,19,dw,10,o,o,m,k,1c,w,1c
32,o,k,m,o,w,26,26,36,3uu,1,k,1p,d,1t,1y,o,1y,eo,s,e,g,c,i,k,i,m
36,10,o,o,q,s,2g,2i,36,3sk,3d,1o,2m,1y,2i,5,2x,w,ec,o,8,1,4,0,e,e,u
34,s,k,m,o,u,26,26,32,3ua,27,1e,c,u,z,0,c,1s,eg,u,c,8,6,a,o,k,o
30,o,k,m,o,u,26,24,36,3u6,2,6,f,n,1z,1c,s,1s,ei,s,e,e,a,e,o,i,k
34,q,k,m,m,u,24,24,36,3ts,x,10,7,c,1p,16,w,1g,ek,s,e,e,8,g,s,g,m
2s,m,k,i,m,q,24,22,32,3ug,1k,1f,w,v,r,13,m,1c,eg,s,g,c,a,a,s,e,i
2i,k,g,i,m,s,22,20,32,3x8,o,0,13,19,2t,t,g,1q,ek,s,g,c,c,a,m,c,g
1e,a,6,6,8,8,k,k,s,40i,3o,m,m,8,q,6s,50,6q,as,1g,1u,10,s,m,1s,10,1a
32,o,k,o,o,u,28,26,36,3v2,3,i,1v,n,1j,1y,q,20,eq,s,e,g,g,c,m,k,m
2s,m,i,i,m,m,1s,1q,2i,3tk,1w,19,a,6,1k,1y,22,2i,ek,u,i,g,a,g,u,i,q
34,o,k,o,o,y,28,26,38,3v8,f,q,2n,3,1h,2a,10,22,ew,s,g,i,m,6,o,m,q
30,s,k,m,o,u,28,26,34,3uk,1r,g,8,y,t,h,e,1o,ee,u,c,a,4,a,i,m,o
1o,c,a,c,c,e,18,18,1u,406,1q,p,13,c,l,7,14,1u,bk,2c,w,i,e,k,q,g,g
2q,m,k,k,q,u,28,26,38,3vk,14,1n,t,31,f,0,12,2k,eq,q,e,8,a,4,s,c,k
1w,c,8,a,8,a,s,o,10,42a,1o,i,1m,1c,34,4k,4c,4m,cm,1w,q,m,q,m,1c,18,20
38,s,m,q,q,y,2c,2c,3i,3uy,15,8,35,6,d,2a,1s,1e,f4,s,g,s,e,c,s,s,u
34,q,k,s,o,w,2a,2a,3a,3vc,r,q,3j,0,17,2g,1c,22,ey,s,e,g,o,8,q,o,u
32,w,o,o,q,u,2g,2g,34,3vw,47,h,u,22,24,p,1l,2k,ee,u,a,6,2,2,a,i,u
30,10,o,q,o,u,28,2i,3e,3rc,3j,22,2g,2e,2q,2k,31,27,ee,k,4,0,0,2,a,e,o
30,q,k,m,o,w,26,26,36,3uw,0,e,1r,j,1p,1w,o,20,eo,q,g,e,e,g,k,i,m
34,s,k,q,o,y,2a,2a,3c,3va,t,q,3p,4,15,2i,1e,20,ey,s,e,g,o,a,q,m,w
36,y,o,o,q,s,2i,2g,34,3u2,45,1c,26,1s,24,1d,1t,2c,ee,q,a,1,4,2,c,g,w
2s,m,m,i,o,q,20,1y,2s,3v4,1q,1d,i,z,0,8,1k,2q,dw,k,c,8,8,6,e,a,i
2e,o,k,m,m,s,22,1y,2u,3u6,4,f,h,t,1,o,1s,3i,de,o,g,6,6,4,i,c,e
2s,o,k,k,o,q,22,1y,2w,3v8,1g,13,c,13,h,g,1q,2m,e2,o,a,a,6,8,c,c,i
22,i,g,i,k,o,1y,1u,2s,3yw,31,2,x,1f,n,n,1c,2o,da,1c,c,i,8,2,a,8,e
2m,m,i,i,k,o,20,20,32,3wa,1q,n,4,r,x,21,9,g,ea,o,g,c,c,8,e,i,g
1y,e,a,a,a,e,w,w,1a,414,u,k,18,1a,1e,4w,32,3u,d4,1g,k,o,m,i,12,w,14
22,m,g,i,m,q,20,1y,2u,3ym,3t,e,1f,n,13,7,1e,36,dg,16,a,g,4,4,a,8,c
2s,m,m,k,m,q,20,1y,2s,3uq,1o,17,k,x,2,8,1o,2s,dy,m,a,8,8,6,g,a,g
2o,k,k,i,k,q,20,22,30,3w2,1m,17,a,r,9,1h,7,12,e6,o,g,c,a,6,g,e,i
28,i,c,c,e,i,1i,1m,2e,40c,f,1c,q,1,14,1b,3d,d,e6,w,o,k,k,g,y,i,m
2g,k,e,g,i,k,1k,1m,2g,3xc,10,q,v,k,b,y,u,1g,e2,o,k,g,c,c,o,i,k
28,o,i,m,k,s,20,1y,2u,3we,2h,w,1l,1,r,i,1e,3a,cy,12,g,a,4,6,g,a,c
2o,m,m,k,m,s,1y,1y,2u,3t6,28,1p,y,z,e,q,1m,2w,du,i,c,6,8,6,i,a,g
22,e,e,a,e,e,14,14,1o,408,l,14,12,c,m,34,2q,3c,di,16,i,m,i,c,10,o,u
26,i,c,c,e,e,16,14,1q,406,h,1i,u,8,1a,3s,2s,1s,du,w,m,k,g,e,10,o,w
28,i,c,c,e,e,16,14,1u,402,5,1g,w,6,1i,44,2o,7,dy,u,m,k,g,e,10,q,w
26,i,c,a,e,g,1a,1g,24,40o,5,1e,12,g,1q,0,1p,1n,e0,y,q,k,i,i,12,o,q
2u,m,k,i,o,s,26,26,36,3w8,16,19,e,1b,1p,15,b,4,e8,q,e,a,8,6,6,8,s
2s,m,m,k,m,s,22,1w,2s,3us,1e,19,i,z,9,c,28,38,e2,m,c,a,6,8,e,a,k
2e,o,k,m,m,s,20,20,2u,3u8,4,j,j,l,1,o,1o,3g,dc,o,g,6,6,6,i,c,c
2c,i,e,e,g,m,1s,1q,2o,3z8,6,14,b,0,5,3t,0,7,ea,u,m,g,c,k,o,e,k
2s,o,k,k,m,s,22,24,32,3vq,18,19,a,11,l,t,i,y,e4,q,c,a,6,8,c,8,o
2e,i,e,g,g,k,1s,1s,2o,3ym,s,14,d,8,f,2b,25,o,e8,s,m,g,c,g,o,i,i
2c,g,e,c,g,g,1c,1g,26,3ze,e,1c,a,g,14,18,7,b,e2,s,o,i,c,i,u,o,q
28,i,e,c,e,k,1k,1o,2i,402,b,1a,c,0,i,1x,2b,9,e6,y,o,i,i,g,w,i,k
2g,o,k,k,o,q,22,1y,2u,3u2,c,p,9,r,4,m,1q,3e,da,o,i,6,4,6,i,c,e
2k,k,g,i,k,q,22,20,30,3x2,1e,8,p,1d,23,1x,4,1c,ee,s,g,c,e,8,k,e,g
2o,o,k,k,o,q,20,1w,2u,3tw,2a,1t,w,13,a,m,1i,2s,ds,i,e,6,8,6,g,c,g
2s,m,m,i,o,q,24,24,36,3vs,16,17,4,15,3,19,k,g,e6,o,e,a,6,8,c,6,q
2q,o,k,k,m,q,20,1y,2s,3ug,22,1n,q,11,a,e,1i,2o,dw,i,c,6,a,4,g,c,g
2i,o,k,k,o,q,22,1y,2u,3tk,y,11,0,t,a,m,1s,3c,de,m,g,6,6,6,i,c,e
2o,o,k,k,o,q,20,1w,2u,3ts,28,1t,w,11,a,o,1i,2s,ds,i,c,6,8,6,i,a,g
2u,m,k,k,o,q,22,20,30,3vi,1g,15,8,15,d,a,w,1y,e2,q,a,a,6,8,c,a,k
2o,o,k,k,o,q,20,1w,2u,3t4,28,1p,10,z,e,q,1m,2w,dw,g,e,4,a,6,g,c,g
1q,c,8,c,a,a,w,u,18,3z6,1u,6,6,1c,16,2q,3a,4e,bg,1e,1e,i,c,k,u,m,q
1y,i,i,g,k,o,1w,1u,2q,3xw,2f,z,7,17,f,d,18,2y,ce,22,8,e,a,2,c,8,e
2q,o,k,k,o,q,20,1w,2u,3tu,1u,1d,q,v,a,g,1q,2w,e0,k,c,8,8,6,e,c,i
20,e,a,a,8,c,w,u,1a,41k,w,1i,1i,1k,2a,3y,2i,36,de,1c,m,q,m,m,1c,12,1c
24,g,a,a,c,e,12,16,1s,412,4,1m,1e,16,1o,2y,f,1h,du,12,o,m,k,i,18,w,y
2q,m,m,k,m,q,20,1y,2s,3uc,26,1t,u,13,a,g,1i,2o,du,i,c,6,8,6,g,c,g
1u,c,a,a,a,c,y,w,1c,41k,1o,l,y,w,1e,3q,2y,3g,ca,24,o,k,o,i,14,u,12
2o,m,i,k,m,s,26,22,36,3w8,1o,z,1,1p,2d,l,9,1k,ee,q,e,c,c,6,8,o,g
2i,o,m,k,o,q,20,20,2s,3t0,1o,1f,e,x,c,s,1s,3a,dm,g,i,4,6,6,k,a,g
2q,o,k,k,m,q,20,1y,2s,3uw,1w,1j,m,11,8,a,1g,2o,du,k,c,6,a,4,g,a,g
24,g,c,c,e,e,16,12,1o,406,r,1k,10,c,w,3o,2u,34,do,12,k,m,g,e,y,o,w
2s,m,k,k,m,s,20,1w,2s,3us,1s,1f,m,z,8,8,1k,2q,dw,k,c,8,8,6,e,a,i
2g,m,e,i,i,m,1o,1q,2k,3ww,18,6,j,e,f,e,q,1c,e4,m,k,e,c,a,m,i,i
22,e,a,c,a,c,y,w,1c,40w,6,1i,1c,1c,1m,58,30,3k,di,16,k,q,i,i,12,w,16
1q,c,c,c,a,e,12,10,1i,3yq,1m,v,1,s,k,1q,2q,3g,bm,1y,10,e,e,i,q,k,o
2e,o,k,m,m,s,20,20,2u,3u6,6,h,j,t,2,o,1q,3g,da,q,i,6,4,6,i,c,e
2e,q,i,m,o,s,22,1y,2u,3tw,i,j,d,11,5,s,1y,3k,dm,i,g,6,4,6,i,c,e
2m,o,k,k,o,q,20,1y,2u,3sw,24,1p,s,x,e,s,1o,32,dq,e,g,4,8,6,i,c,g
2e,i,e,g,e,i,1e,1i,2a,3y4,s,18,j,q,8,2g,g,9,e2,q,m,g,c,e,u,k,o
2k,k,g,i,i,q,20,1y,2y,3wu,1k,8,9,d,27,2r,a,u,ec,q,g,c,e,a,k,g,g
1y,e,e,c,e,i,1e,1c,22,404,5,n,m,n,9,a,1i,2e,cs,1s,i,i,i,a,q,g,o
2a,o,i,k,m,q,22,1y,2w,3xg,37,4,1t,0,n,m,1k,3e,dw,m,i,a,2,6,c,8,c
2e,k,c,g,g,i,1g,1i,2e,3xw,y,1a,p,u,2,1q,0,9,e4,o,m,g,c,e,s,m,m
26,m,i,k,m,q,20,1y,2w,3y6,43,u,23,0,15,g,1g,3a,dq,u,e,e,2,4,c,8,a
2m,o,m,k,m,s,20,1w,2u,3ss,2c,1r,u,x,g,u,1q,2y,dw,i,c,6,8,6,g,c,g
2m,m,k,k,m,q,20,1w,2s,3va,1g,1l,m,z,3,f,1q,3i,e2,o,g,6,c,4,g,a,k
14,c,8,8,6,a,s,o,12,3z2,z,11,10,w,t,1y,44,6k,8u,1w,10,s,1g,1a,18,16,y
1m,e,a,c,e,e,1c,1g,2c,3vc,28,4,1l,1e,k,1f,1n,7,b0,10,16,y,c,o,k,k,8
1k,e,a,e,c,g,1e,1k,2g,3v0,24,o,2p,1e,q,2b,1l,e,aw,12,12,16,8,m,k,i,6
1g,c,a,a,c,e,12,18,22,3v6,20,y,x,r,1k,d,19,15,as,12,o,1o,g,c,w,i,g
24,k,g,i,i,o,1y,20,30,3uq,1j,18,2,f,a,25,2n,0,bq,1c,s,m,e,8,q,e,o
1g,g,a,e,e,i,1i,1o,2k,3to,20,18,3b,s,a,2b,6,8,ay,w,y,18,a,m,g,c,a
1k,e,8,c,c,e,16,1c,26,3vm,22,q,1x,14,1a,h,1p,n,ay,12,12,16,c,i,q,k,c
16,c,a,a,8,e,12,14,1w,3xq,1r,c,w,p,1j,w,1,z,9u,1c,w,10,1c,o,s,c,g
1c,e,a,a,e,g,1a,1g,2a,3vw,3,14,j,23,1m,x,1f,0,ac,1a,s,1e,s,8,q,e,8
1w,g,e,e,g,i,1u,1u,2w,3v6,14,v,18,2,d,4p,2f,13,bg,1c,w,m,m,c,m,c,g
1y,i,e,e,g,k,20,1y,30,3vk,4,0,16,9,b,71,5,b,bm,1k,m,o,k,e,g,4,g
1i,e,a,e,e,i,1i,1m,2k,3to,2k,w,35,12,e,2f,d,a,b0,w,y,18,8,o,i,c,8
24,i,g,g,i,m,22,20,34,3vi,1b,1c,k,l,1f,53,0,3,bq,1m,m,q,e,c,g,8,g
1c,e,a,a,e,e,1c,1e,2c,3w0,7,14,h,27,1m,v,1d,1,ac,1a,q,1e,u,8,q,e,8
1u,e,e,e,e,k,1o,1u,2u,3va,1u,1d,14,k,l,4h,2v,13,be,16,16,g,q,e,m,a,c
1q,g,c,e,e,i,1g,1k,2o,3uw,2k,1l,s,10,z,v,t,37,ba,w,1c,g,o,k,m,c,g
12,c,8,8,8,8,q,m,y,3z8,13,r,w,w,1,20,5s,7o,8u,1s,y,u,18,1k,1e,1a,10
1y,i,g,e,i,k,20,1y,30,3v6,1,0,10,f,3,5x,19,1,bk,1i,q,o,i,a,m,a,e
1s,e,e,e,c,k,1g,1q,2o,3v0,2e,1l,w,10,v,v,2t,3d,bc,14,14,i,m,i,o,a,k
1k,e,c,e,g,i,1k,1o,2m,3tq,2e,e,2n,14,l,1x,a,2,ay,10,14,y,c,m,g,a,c
1m,e,a,e,e,g,1g,1k,2k,3uq,2e,4,23,1e,2,2b,17,e,ay,10,16,y,c,o,i,g,8
1u,g,e,e,e,m,1k,1o,2m,3uk,24,1t,1c,g,15,15,1n,o,bc,y,16,k,o,c,o,c,e
1u,g,e,e,e,i,1k,1o,2m,3us,1w,1d,16,k,x,t,17,1f,be,12,14,i,m,g,o,c,i
1y,i,e,g,g,k,1o,1o,2o,3uq,8,4,18,j,2,1v,15,g,bi,1a,q,q,i,a,u,c,k
1c,c,a,8,a,e,10,12,1u,3ws,9,10,k,21,s,8,9,p,aa,1a,s,1a,14,a,y,k,k
1w,g,g,e,g,k,1k,1q,2k,3us,12,p,1e,3,j,1j,1j,k,bg,y,12,q,k,a,s,c,e
1i,e,c,e,g,i,1m,1q,2m,3t6,22,16,3f,u,d,1v,w,6,b0,u,14,y,c,m,g,8,e
1u,g,e,e,e,k,1s,1u,2w,3vc,1m,17,16,e,n,5f,2d,7,bg,1a,10,i,q,e,k,8,a
1u,g,e,e,e,k,1o,1w,2s,3v6,1u,1j,1a,k,t,3b,3p,11,bg,1a,y,i,o,g,k,a,i
1w,g,g,e,e,k,1k,1q,2q,3us,14,t,18,4,j,v,1b,1n,bg,18,w,m,k,c,s,a,m
1m,e,c,e,e,g,1g,1k,2i,3us,2k,j,13,1a,d,1z,17,c,b0,10,16,u,g,o,i,g,8
16,c,a,8,a,c,y,10,1o,3y0,1n,6,u,0,1x,1m,a,h,9w,18,w,u,1m,o,u,i,o
16,c,a,8,8,c,w,w,1i,3ya,1n,4,u,a,1v,24,k,1c,9g,1m,y,u,1o,o,y,q,s
22,m,g,g,i,o,22,20,32,3us,1d,14,4,f,4,3v,21,d,bq,1e,s,m,c,a,m,g,o
1e,e,c,c,e,g,1g,1k,2i,3uk,14,18,29,d,16,1z,v,g,aq,10,q,1g,g,i,k,e,6
1q,e,c,e,e,i,1g,1k,2k,3uu,2o,1f,8,1a,11,1b,v,n,b8,u,1c,k,m,k,m,e,a
1y,i,e,e,e,k,1y,1w,2y,3vu,c,1,16,0,n,6z,l,l,bm,1k,o,o,k,e,i,6,i
1u,g,e,e,g,k,1k,1o,2m,3um,1w,1j,1c,e,z,19,1l,o,be,12,12,k,m,c,q,c,e
1k,e,a,e,e,i,1i,1m,2k,3tw,2o,i,2r,1a,2,2d,j,a,b0,w,12,14,a,m,k,c,8
24,k,g,i,i,o,24,24,36,3us,1h,16,2,f,1,4z,v,p,bq,1k,o,m,c,a,m,c,q
1q,e,e,e,c,k,1i,1k,2k,3uo,2q,1p,k,18,1b,19,11,7,b8,w,1a,k,o,i,m,c,c
1w,g,e,e,e,k,1k,1s,2q,3uu,1i,15,1a,a,n,h,33,2p,bg,18,y,i,m,e,q,a,s
1i,e,a,a,c,e,16,1c,26,3v2,2c,u,1p,g,1c,p,1r,p,ay,10,s,1i,a,i,q,k,e
1m,c,a,c,c,e,16,1c,2c,3vs,24,g,1f,18,18,d,2r,4l,b0,12,1a,w,e,m,q,k,o
1q,g,e,e,e,k,1k,1q,2m,3ua,2s,29,w,w,1j,11,1b,k,b8,w,1a,i,q,e,m,a,e
1o,e,c,c,e,e,1c,1k,2m,3v8,2m,r,b,1c,0,11,25,51,b6,y,1e,k,i,o,o,c,g
1k,e,a,c,c,e,18,1c,28,3vg,26,k,1r,14,14,t,1r,r,b0,10,14,12,c,k,q,k,a
24,m,g,g,k,m,1u,1w,2u,3uq,1h,1e,1,b,1,1j,1b,y,bq,1c,q,m,c,a,s,g,m
1g,g,c,e,e,k,1k,1q,2m,3ta,22,16,3f,u,7,21,s,6,b0,u,14,10,a,m,g,a,c
1s,g,e,e,e,m,1k,1o,2m,3ui,2k,29,18,u,1h,z,1p,o,ba,y,18,i,q,c,o,c,c
14,e,a,8,a,e,12,16,20,3xw,1t,4,w,l,1t,s,7,t,9e,1q,w,y,1a,u,q,a,e
1w,g,g,e,g,k,1m,1o,2m,3uo,14,p,1a,3,h,1j,1h,k,bg,12,y,q,k,a,s,c,g
1w,i,e,g,g,k,1o,1y,2s,3uw,o,j,1a,7,5,1z,3t,1h,bi,1c,u,m,k,c,o,c,q
1e,e,a,c,e,e,1c,1g,2c,3ui,1w,10,1p,n,1m,1f,1r,6,aw,w,o,1m,e,e,q,g,6
1m,e,a,c,c,e,18,1e,2e,3vk,2a,4,17,18,y,n,27,3p,b0,12,18,w,e,m,q,g,k
2a,o,i,m,m,q,24,26,3a,3tk,11,k,7,3,f,15,11,10,c6,14,s,c,a,a,o,g,i
1i,e,a,e,g,g,1k,1o,2k,3ti,2c,18,3h,12,4,2d,8,6,b0,u,12,14,a,m,i,a,a
1g,e,a,c,e,g,1g,1k,2g,3u6,1y,12,2f,0,16,1x,17,e,aw,w,q,1k,c,g,m,e,8
1w,g,e,e,e,k,1u,1y,2y,3vk,16,x,18,a,l,63,23,k,bi,1i,s,k,m,g,i,6,8
1i,e,a,a,e,c,18,1a,26,3v4,2c,s,1p,i,1c,r,1p,n,ay,10,u,1g,a,i,q,k,e
12,e,8,8,8,c,u,w,1e,3ys,19,v,w,s,1v,1u,u,1q,8w,20,w,s,1a,1c,y,u,s
1q,e,e,e,e,k,1i,1o,2m,3uc,2u,1x,i,10,1j,17,15,g,b6,w,18,m,o,i,k,c,a
1o,g,c,c,e,i,1e,1k,2k,3uw,2m,15,1,1a,r,1f,r,13,b6,w,1c,k,m,k,m,e,a
1m,e,c,c,e,g,1c,1i,2e,3v6,2e,7,19,1g,4,1p,1d,5,b2,y,18,u,e,o,k,i,8
1m,g,c,e,e,k,1k,1o,2o,3ua,2e,11,x,12,17,1h,b,8,ay,12,16,s,i,k,i,a,c
1k,g,c,e,g,i,1m,1o,2m,3tw,28,5,1z,10,x,1l,a,4,aw,12,16,u,e,m,g,a,c
1q,e,c,e,c,g,1g,1o,2o,3vc,2k,19,8,1a,9,1v,3n,3j,b8,12,1c,g,o,k,m,c,i
1k,m,m,o,u,y,2m,2o,4o,3xw,3y,27,2f,37,2n,m,26,s,aq,s,12,14,q,o,14,u,12
1m,q,q,s,u,14,2s,2w,52,404,1a,25,3v,2j,2j,16,2c,16,ac,1o,m,16,o,k,1a,u,12
1k,k,k,m,q,y,2g,2m,4e,3uw,5m,1z,5,3t,2h,0,22,u,aq,s,16,y,s,m,14,u,12
1o,k,m,m,q,w,2g,2k,4e,3wq,42,23,15,3f,2j,12,1u,s,aq,w,10,14,q,o,18,s,10
1o,q,q,u,w,14,2u,2y,54,3zy,m,1f,49,2p,2h,1q,2c,1a,aa,1y,i,12,o,k,1a,u,12
1o,q,s,u,w,16,2u,30,56,40s,9,1l,4b,2v,2b,20,2c,1a,a8,20,m,y,o,k,1a,w,10
1o,s,q,u,w,14,2u,30,54,40y,f,1b,4d,2t,2b,1u,2c,18,a8,1y,m,12,o,i,1c,u,10
1e,m,k,m,q,y,2g,2i,4e,3ws,3q,h,1x,3n,29,5,28,q,ac,10,16,y,w,m,16,u,12
1m,m,m,o,s,10,2o,2q,4q,3w4,5s,2n,1z,35,2z,o,22,10,au,s,10,12,q,m,16,u,12
1m,q,q,s,w,14,2w,2y,52,3zg,24,2f,3v,2n,2n,18,2c,1c,a8,1s,o,12,o,k,1a,u,12
1g,m,m,o,q,y,2k,2m,4i,3ws,4g,1l,1v,3l,2f,8,26,s,ao,q,16,10,u,m,14,u,12
1m,o,q,u,w,12,2w,2y,52,3za,2i,2l,3t,2l,2n,12,2c,1c,a8,1q,q,12,o,k,1a,u,12
1q,q,q,u,w,14,2u,30,56,40w,r,r,4j,2v,27,1w,28,18,aa,1y,o,y,o,k,1a,u,12
1k,o,q,u,u,14,2u,2w,52,40g,20,3r,3d,2d,2n,w,2c,18,a2,1o,w,12,o,m,18,u,14
1m,q,q,u,w,14,2u,2y,52,40u,s,2r,3t,2n,2h,1g,2e,16,a4,1u,q,12,q,k,18,w,12
1e,o,k,q,s,12,2s,2s,4s,3vo,4g,p,z,3p,33,t,26,1e,ag,10,18,u,o,m,14,u,18
1m,o,o,s,u,14,2u,2w,4y,3ye,3w,2z,3b,2p,2t,s,2a,18,ac,1g,w,12,m,m,18,u,14
1m,o,q,s,w,14,2w,2y,52,3z6,30,31,3n,2l,2r,y,2a,1e,a6,1o,u,10,o,k,1a,u,14
1m,o,q,s,w,14,2w,2y,54,3z8,2w,2z,3p,2j,2r,y,2c,1c,a6,1o,u,12,m,k,1a,u,14
1i,o,o,s,u,12,2u,2u,4w,3zi,2q,3f,2n,2j,2v,g,28,18,a0,1k,12,10,o,m,18,u,14
1i,o,q,s,u,14,2u,2w,4y,3yw,38,39,2v,2l,2x,m,2a,1a,a8,1e,10,10,o,m,18,u,14
1q,o,m,q,q,10,2i,2q,4m,3y2,1i,j,39,27,2b,1g,1q,12,as,10,y,16,m,o,18,s,10
1i,o,o,s,u,12,2u,2u,4y,3zo,2q,3l,2n,2f,2z,e,2a,16,9y,1m,10,12,m,o,16,w,14
1i,o,o,s,u,14,2w,2w,50,3wk,5k,2v,2j,2v,33,4,26,1g,am,10,10,10,m,m,16,w,16
1g,o,m,q,u,12,2u,2u,4u,3vo,5i,1r,1r,3b,37,9,24,1g,aq,s,14,y,m,o,14,w,16
1g,o,m,q,u,12,2s,2u,4u,3vg,5q,1p,1p,3h,37,5,24,1g,as,q,14,y,m,o,14,w,16
1g,o,m,q,u,12,2u,2u,4w,3vo,5i,1r,1t,39,35,b,24,1g,ao,u,14,y,m,o,14,w,16
1k,o,o,q,s,12,2o,2u,4s,3ya,3o,2j,2t,2x,2r,s,28,y,aq,w,y,16,o,m,18,s,14
1k,q,q,s,w,14,2w,2y,54,3zw,22,35,3l,2f,2r,12,2c,1c,a2,1o,w,12,o,k,1a,u,14
1o,s,q,u,y,14,2u,30,58,40i,f,11,4d,2z,29,22,2c,1a,aa,1y,q,u,q,i,1c,u,12
1m,o,o,s,u,12,2q,2w,4w,3zc,2c,2b,3f,2n,2n,y,2a,12,ai,1a,u,14,q,k,18,u,12
1m,o,q,u,w,14,2u,30,52,3ze,2e,2n,3t,2l,2p,14,2c,1c,a6,1s,q,12,o,k,18,w,12
1s,o,m,o,s,y,2k,2q,4m,3y4,1a,d,3d,23,29,1g,1m,14,as,12,w,16,m,o,18,s,10
1s,q,q,u,w,12,2u,30,52,40e,t,9,4n,2v,1v,22,20,1a,ag,1s,s,u,q,k,18,w,10
1o,s,q,u,y,14,2w,30,56,40m,h,15,4b,31,29,24,2c,18,a8,20,q,u,q,i,1c,u,12
1q,q,m,o,s,10,2k,2q,4o,3yc,14,d,3j,23,29,1g,1q,12,aq,14,w,16,m,o,18,s,10
1k,m,m,m,s,y,2k,2o,4k,3xi,4c,27,25,3d,2j,k,24,q,au,m,14,14,q,o,16,s,12
1g,o,m,o,u,y,2o,2o,4o,3xe,4c,2d,1r,3n,2n,c,26,10,ao,s,14,10,s,m,16,u,12
1m,o,q,s,u,14,2s,2w,4y,3zi,2e,2r,3j,2l,2l,12,2a,16,ag,1g,q,14,q,k,18,u,12
1k,q,q,s,w,14,2w,2y,52,40a,1o,35,3n,2h,2n,16,2c,1a,a2,1q,u,12,o,m,18,w,12
1o,o,m,q,u,10,2q,2s,4s,3yi,2k,1j,3j,2d,2t,14,26,y,ag,1a,y,14,o,m,18,s,12
1q,s,o,u,w,14,2u,30,56,40u,x,f,4l,2v,27,1w,28,18,ac,1w,q,w,q,i,1a,u,12
1i,q,o,s,w,12,2u,2w,4y,3zs,2i,3j,2x,2l,2r,s,28,18,a6,1i,y,12,m,m,18,w,14
1i,o,q,s,u,14,2w,2y,50,3xk,4q,35,2t,2p,31,c,26,1g,ae,18,10,10,m,m,18,w,14
1o,o,m,o,s,y,2k,2o,4m,3xy,2e,17,2t,2n,2h,18,1y,y,am,12,10,16,m,o,18,s,12
1k,q,q,u,w,14,2w,30,52,3zs,22,2x,3r,2j,2p,16,2c,1e,a4,1q,s,12,o,k,1a,w,12
1k,m,m,o,s,10,2m,2q,4o,3vo,5y,2j,19,3l,2v,c,22,12,au,q,14,10,o,o,14,u,14
1q,o,o,s,w,12,2q,2w,4y,3zi,q,v,47,2h,2f,1k,28,14,ae,1m,o,16,o,k,1a,s,12
1i,o,q,q,w,12,2s,2w,4w,3zi,2y,3p,2p,2n,2r,m,28,18,a6,1e,10,12,o,m,18,u,14
1m,o,q,q,u,12,2s,2u,4w,402,1i,21,3p,2h,2j,u,2a,10,aa,1g,w,16,o,k,18,u,12
1g,m,m,o,u,12,2s,2u,4q,3vg,4y,z,13,3n,35,p,26,1e,ak,w,18,u,o,m,14,u,18
1g,o,k,o,s,y,2k,2o,4k,3ws,4o,1v,1z,3h,2h,a,26,u,ao,q,16,10,u,m,14,u,12
1g,o,o,s,u,12,2s,2u,4u,3y8,3k,2t,2d,2v,2z,g,28,18,aa,1a,12,y,o,o,16,w,14
1i,o,o,q,s,12,2q,2s,4u,3yi,3q,35,27,39,2r,m,26,14,ai,10,12,12,q,m,16,u,14
1g,o,o,s,u,12,2q,2u,4u,3yq,3a,33,2b,2z,2r,g,26,18,a8,1c,10,12,o,m,18,u,14
1i,o,m,q,s,10,2o,2s,4q,3xq,4g,2x,27,37,2n,e,28,10,am,w,12,12,q,m,16,u,14
1i,m,m,q,s,y,2m,2q,4m,3ws,52,2f,25,37,2l,c,26,w,au,m,14,12,q,o,14,u,14
1k,q,s,s,w,14,2u,2y,52,40o,1a,37,3p,2j,2j,1a,2c,1a,a4,1s,q,14,o,k,1a,u,12
1i,o,q,s,u,14,2s,2w,4y,3zm,2m,3f,2v,2n,2t,s,28,18,a6,1i,y,10,o,m,18,w,12
1s,s,o,u,w,12,2u,2y,54,40e,t,9,4n,2v,1t,20,20,1a,ag,1s,s,w,o,k,18,w,10
1g,m,m,o,s,y,2k,2m,4k,3wq,4i,1n,1t,3n,2f,6,26,w,am,s,16,10,s,m,16,u,12
1e,m,k,m,q,w,2g,2i,4c,3wu,3m,d,23,3f,29,5,26,q,ac,y,18,w,y,m,16,u,12
1k,q,q,u,w,14,2w,2y,54,3zo,26,2x,3p,2j,2p,14,2c,1c,a4,1q,s,12,o,k,1a,w,12
1e,m,i,m,o,u,2e,2e,48,3wq,36,2,1p,3n,1v,9,24,k,aa,10,18,w,10,k,16,u,12
1m,o,o,q,s,12,2q,2u,4u,3xo,44,2d,31,2p,2x,w,26,12,ak,16,y,12,o,m,18,s,14
1g,o,m,q,u,12,2w,2w,4w,3uo,6k,1v,1b,3d,39,p,24,1k,as,s,14,w,m,o,12,w,16
2a,m,e,i,i,m,24,22,32,3wg,f,14,f,6,15,5v,1r,o,cm,12,u,g,c,e,i,g,u
2c,m,g,i,i,o,24,24,34,3vi,2,w,j,a,1j,4p,1z,u,cm,12,s,i,c,c,k,g,u
2g,m,i,i,k,s,24,28,38,3ty,o,c,5,6,2h,2l,1z,q,co,10,q,g,c,a,o,g,w
2q,o,m,m,o,w,2c,2e,3k,3s6,1a,f,2,l,1j,1j,1d,18,d0,10,g,c,a,a,q,e,12
20,g,e,e,e,k,1w,1u,2u,3wi,2,g,18,0,7,7v,19,h,bo,1m,m,q,k,c,m,a,m
2e,m,i,i,k,q,24,26,38,3u2,e,q,l,m,2j,1z,31,o,cm,10,s,e,e,c,k,g,y
2g,m,i,k,k,s,24,28,38,3tm,o,c,7,4,2b,25,27,q,cm,12,q,e,c,c,m,g,w
2s,q,m,o,q,y,2e,2i,3q,3rg,12,1,7,h,1b,1d,n,16,d2,y,e,c,8,c,o,c,12
2a,m,g,i,k,q,24,24,36,3um,r,14,b,8,2h,2p,1n,i,c6,1a,s,i,c,a,m,e,m
2a,m,g,i,i,q,22,24,36,3v0,l,14,9,8,29,3d,1r,c,c8,1a,q,k,c,c,k,e,u
2m,o,i,k,m,u,28,2a,3e,3ta,1c,f,1,9,1p,25,23,10,d0,10,k,e,a,a,o,e,w
1y,i,e,e,e,k,1y,1u,2y,3w2,4,a,18,3,9,7x,7,17,bm,1m,m,q,k,c,i,8,q
2e,m,i,i,i,s,24,24,36,3um,g,m,d,a,27,3d,25,w,co,10,s,g,c,c,k,g,w
20,g,e,e,g,i,1y,1u,2w,3wa,2,e,16,1,d,79,17,l,bo,1k,o,o,k,e,k,8,m
2e,m,i,k,i,s,24,28,38,3ty,8,k,9,6,29,2f,23,m,ci,14,q,g,c,a,o,e,w
26,k,e,g,i,k,22,1y,30,3xa,x,1c,2,5,b,7f,1j,i,ce,18,q,m,e,c,k,g,o
20,g,e,e,e,k,1w,1u,2u,3wm,1,i,1c,3,3,7t,1d,h,bq,1k,m,q,k,e,k,c,m
2g,m,i,i,k,s,24,26,38,3um,u,6,d,6,23,2r,2p,q,cu,10,q,e,c,c,m,e,y
2c,o,i,k,m,s,28,28,3c,3tc,l,i,h,c,1t,21,1h,q,c8,1a,q,e,a,8,o,g,m
20,g,e,c,e,i,1u,1u,2s,3ww,6,c,1c,4,3,7b,2n,3,bq,1k,o,q,k,c,o,c,m
28,m,e,g,i,o,24,20,32,3wc,13,1e,7,2,1l,5j,13,c,ce,14,s,k,c,e,i,g,s
22,g,e,c,g,i,1s,1w,2s,3xw,f,q,14,3,1,77,39,c,c0,1k,k,q,i,c,q,e,k
28,k,g,g,g,o,22,20,32,3wc,t,1c,2,3,11,65,1f,e,cc,18,q,k,e,c,k,e,s
22,g,e,e,e,k,1u,1w,2u,3xg,h,w,16,b,0,71,35,a,bw,1m,k,q,i,c,o,e,m
2m,o,k,m,m,u,2a,2c,3g,3s6,1c,d,4,1,1r,23,1f,14,cu,10,k,c,c,a,q,e,s
2m,m,k,k,m,u,26,2a,3c,3t4,1c,b,1,l,19,2j,1p,18,cw,10,m,e,a,a,q,e,y
2i,m,i,i,k,s,26,26,3a,3u6,10,2,b,1,1p,33,25,y,cu,10,o,g,c,a,o,e,y
2m,o,k,m,o,u,2a,2e,3i,3rq,18,h,k,l,11,1x,1j,y,cq,12,k,c,a,a,q,e,u
2m,q,m,o,s,w,2g,2i,3s,3qm,y,0,3,n,19,d,11,14,cq,12,e,a,6,c,q,e,o
22,i,e,e,e,k,1y,1u,2w,3wy,h,y,14,9,5,7p,1t,0,bw,1i,m,q,i,c,o,a,o
2i,m,i,i,m,q,26,28,38,3u2,u,a,f,2,1z,2z,1z,16,cs,10,q,e,c,a,o,e,y
2s,s,o,o,q,y,2i,2k,3u,3qu,10,5,e,13,h,1h,b,10,cy,10,e,8,a,c,o,c,w
1e,k,k,m,s,y,2s,2s,4m,3u6,4a,y,2,3j,2x,2h,22,1o,a0,1e,1c,k,w,k,10,u,18
1c,k,e,k,o,u,2m,2m,4c,3te,2q,3g,15,j,2t,4b,1i,24,9u,1e,1a,m,10,m,y,w,12
12,a,8,8,8,8,q,o,12,3z6,1n,c,m,10,s,1o,5o,54,8s,1m,10,w,14,1m,1w,16,18
12,a,8,8,8,8,s,o,12,3z4,1l,a,o,12,m,1m,5a,4w,8s,1m,10,w,18,1k,1u,16,18
12,c,8,8,8,a,s,u,1a,3zg,21,e,1a,y,l,1w,2m,4g,8s,1u,y,w,1a,14,26,14,14
1e,m,k,o,u,10,2s,2s,4q,3vm,3u,1,f,3t,33,1d,24,1i,a6,1a,18,q,q,m,14,s,1a
1g,k,i,m,o,y,2i,2m,4e,3t6,5q,h,m,3d,2j,1j,1y,14,aa,14,1c,o,w,m,12,s,16
1c,k,c,i,i,q,26,26,3s,3ts,20,2q,23,14,2l,27,1a,y,9u,1a,14,10,u,u,12,u,10
1a,i,c,c,g,k,1m,1q,30,3wg,1,1y,1r,e,1,t,m,3,9i,1e,12,18,m,18,1c,u,10
12,c,8,a,8,a,y,10,1o,3z4,23,6,1o,18,1p,28,1e,s,8s,1q,14,w,1c,1e,1q,y,y
1a,i,c,g,i,m,20,24,3k,3x2,1n,2i,25,14,11,2x,12,16,9i,1e,12,1a,o,10,12,s,10
1a,k,c,i,k,q,2a,2e,3w,3vs,p,36,1v,1k,2d,3x,14,1w,9q,1a,14,14,s,u,w,w,10
14,i,c,c,g,i,1q,1u,36,40u,57,24,7,23,1c,23,a,w,8s,24,u,18,u,12,18,s,w
14,e,8,a,a,c,12,14,1w,41y,45,1s,1i,6,19,8,o,o,8m,2e,m,w,1a,12,1w,14,10
12,c,8,8,6,a,s,q,12,3zo,1t,g,12,12,4,16,3s,5w,8u,1k,16,u,10,18,2e,16,16
14,g,a,c,c,i,1c,1k,2o,41i,4l,1u,q,27,q,l,c,f,8m,2c,q,10,14,10,1k,w,w
1a,g,a,c,e,g,1c,1i,2g,3xa,a,1u,h,n,14,r,2,3,9c,1i,12,14,s,18,1k,10,10
12,c,8,8,8,a,u,u,1a,3zs,27,i,1g,y,x,1s,2e,4g,8s,1u,10,u,1a,14,24,16,14
1k,m,m,q,s,12,2s,2u,4s,3ve,6e,2h,1f,3j,33,1,22,1c,au,q,14,y,o,m,16,u,14
1i,i,i,k,m,u,2c,2e,44,3t0,5i,l,i,2n,2b,z,1w,o,a8,14,1a,s,y,o,12,u,12
1g,i,e,k,i,s,26,28,3u,3se,4k,14,t,d,2p,11,1k,i,a0,18,18,s,10,s,12,u,12
12,a,8,8,8,8,q,m,10,3zc,1p,6,k,12,s,1y,6c,60,8s,1k,12,w,14,1q,1q,18,16
12,c,8,8,8,a,s,u,14,3zy,1z,s,1a,y,t,1g,32,5w,8s,1q,12,u,12,1a,28,18,16
1a,k,e,i,k,s,2e,2e,42,3us,g,3g,1p,12,2l,43,1a,1w,9s,1a,16,y,w,q,y,u,12
1e,k,g,k,m,w,2i,2i,48,3sk,4m,1o,d,1h,2r,2t,1q,1e,a0,1a,1c,m,y,o,10,u,14
12,c,8,8,8,a,u,u,18,3zw,23,o,1c,y,11,1o,2c,5c,8s,1o,14,w,18,12,28,18,14
12,a,8,8,8,8,q,m,y,3zg,1l,o,o,y,s,1g,5c,68,8u,1k,10,w,s,1i,2c,16,16
1g,i,e,i,k,q,24,28,3s,3sk,4a,14,x,7,2p,r,1m,c,a0,18,16,w,y,s,14,u,10
12,g,c,a,e,i,1g,1o,2u,41c,4x,1y,w,2t,10,x,3,a,8o,2c,o,y,18,y,1g,u,s
1i,k,m,m,u,10,2q,2u,4q,3u8,64,1f,4,41,31,11,24,1e,ak,y,16,w,o,m,12,u,16
16,i,c,c,i,i,1q,1s,36,402,41,1s,15,v,w,23,10,c,8u,1w,14,1c,k,16,18,s,10
12,a,8,8,6,8,q,k,w,3zm,1j,g,a,w,s,1y,6m,6k,8s,1k,y,w,s,1m,28,14,14
1e,m,i,o,s,10,2s,2s,4o,3uq,44,k,0,3r,31,21,22,1m,a0,1e,1c,m,s,m,12,s,1a
18,i,c,g,i,m,22,24,3m,3yo,3n,28,1l,y,f,3l,u,1m,9c,1i,12,1e,m,y,10,u,y
16,g,c,c,g,i,1i,1q,2w,40c,3z,1y,h,1j,14,17,m,7,8q,22,10,16,s,16,1c,w,y
12,e,8,a,8,c,y,12,1o,40k,31,y,1q,y,1x,1a,16,1c,8q,28,o,u,1c,16,1u,14,10
18,e,a,a,a,e,14,16,1u,400,1j,2a,14,1b,i,l,8,2e,94,1s,w,y,12,12,1s,1c,y
12,e,8,8,8,a,u,w,1a,40k,2r,1a,1e,s,1j,1m,2c,52,8q,20,u,w,16,18,22,18,14
10,c,8,6,8,8,o,m,w,404,1n,i,4,s,o,1e,70,6y,8s,1k,w,w,s,1k,26,16,y
14,e,a,8,8,e,y,12,1m,42e,4p,2a,1g,a,1h,e,1c,2e,8m,2e,m,w,1a,12,1y,14,14
1i,k,g,k,m,s,28,2a,40,3to,4s,z,i,2d,27,d,1u,i,aa,14,18,u,y,o,14,u,12
12,c,a,a,8,e,14,18,24,3zm,2h,i,1u,w,1z,q,s,d,8s,1y,y,u,1c,1a,1k,u,u
1k,k,k,m,s,y,2k,2m,4k,3uk,60,1v,2,41,2r,7,20,10,as,q,16,y,q,m,14,u,14
12,e,c,8,c,g,16,1c,2c,41k,3l,1a,1a,x,n,4,a,t,8o,2a,o,w,1c,u,1u,y,u
1i,k,k,o,s,10,2q,2s,4o,3u4,60,19,c,45,2x,15,24,1c,ak,y,18,u,o,m,12,u,16
14,e,a,8,a,e,10,16,1u,420,4f,24,1g,9,v,a,q,q,8m,2e,o,u,1a,12,1w,14,10
12,c,a,8,8,a,u,y,1c,3zy,2h,q,1k,y,1j,24,1y,3k,8s,20,u,u,1a,16,22,14,14
12,a,8,6,8,8,o,k,w,3zk,1b,u,a,w,q,1w,6a,6g,8u,1k,w,w,u,1i,2a,16,14
16,g,c,e,g,k,1s,1w,3a,40k,51,22,p,1d,16,2n,m,12,8u,20,y,1a,q,12,16,s,w
12,c,8,a,6,c,u,u,1c,3zw,2b,o,1g,y,17,1w,24,4c,8s,1u,10,u,1a,14,24,16,14
12,a,8,8,6,8,q,k,w,3zo,1h,a,a,u,u,1w,6u,6m,8s,1k,y,w,s,1m,28,14,12
12,c,8,8,8,a,u,w,1g,3z8,1z,6,1c,14,z,28,2a,2o,8s,1m,16,w,1c,18,20,12,10
14,g,a,a,c,g,1a,1e,2g,41s,4t,24,18,29,s,1,4,t,8m,2e,q,u,18,10,1o,10,w
14,e,8,8,8,c,w,y,1g,41o,3z,24,1i,g,1v,1a,1q,46,8o,2c,m,u,1c,14,1y,16,14
12,a,8,8,8,8,q,m,y,3zu,1n,2,a,u,m,y,62,7a,8s,1k,12,u,u,1u,1o,16,w
14,g,a,a,c,g,18,1e,2e,41o,4h,1y,18,1v,g,3,4,l,8m,2c,q,w,18,10,1o,10,y
12,c,a,8,a,c,12,16,1y,3zg,2b,e,1u,14,23,18,y,5,8s,1y,y,u,1c,1a,1o,u,w
12,a,8,8,8,8,o,m,y,3zm,1n,2,e,y,w,1u,6q,6o,8s,1k,10,w,s,1w,1w,16,14
1i,m,o,q,u,14,2u,2w,4y,3v2,6q,2d,1n,3b,37,d,24,1g,au,q,14,y,m,m,14,w,16
1k,m,q,q,w,12,2w,2w,50,3wo,5q,31,2l,2v,31,2,28,1e,ak,12,10,10,o,m,16,w,14
1c,i,c,i,i,q,28,28,3s,3us,m,30,27,1i,2f,2z,16,1e,9s,1a,12,14,s,w,10,s,12
16,g,c,c,g,k,1m,1s,34,40u,4r,1w,j,1r,16,1z,u,e,8o,22,12,1a,q,14,1a,s,w
12,c,a,8,8,a,s,q,16,3z0,1f,p,y,u,11,1q,2k,54,8u,22,s,u,1a,1e,14,14,u
12,c,8,8,8,c,w,10,1k,3z6,23,6,1k,18,1j,28,1k,1g,8s,1o,16,w,1c,1e,1q,10,y
12,a,8,8,8,8,q,m,y,3zg,1t,4,k,12,s,20,6k,6m,8s,1k,12,w,16,1o,1o,1a,14
1i,k,i,m,q,w,2k,2m,4g,3tm,5w,z,i,3p,2n,11,1y,12,ai,y,18,u,s,m,12,u,14
12,e,8,a,a,c,12,18,20,404,2p,q,1s,q,1t,q,u,7,8q,24,u,u,1c,14,1s,w,y
1e,m,i,m,q,y,2q,2q,4k,3t8,56,i,k,3h,2n,2l,20,1k,a2,1c,1c,m,w,k,10,s,18
1g,i,g,k,k,u,2c,2e,42,3sa,52,10,9,1b,2l,1t,1o,u,a2,18,1c,m,10,o,12,u,12
20,o,i,k,m,q,22,20,36,3r6,1x,14,j,2,5,2c,26,24,be,1e,g,q,e,c,k,i,u
20,m,k,k,k,s,20,20,34,3r8,1t,12,h,1,3,28,26,24,be,1e,e,s,e,c,m,i,s
1u,k,k,i,m,q,22,22,34,3qm,c,1h,g,r,c,18,2w,2c,b6,1e,i,s,e,a,i,c,s
1q,i,i,g,k,o,1u,1y,2w,3rg,24,2p,y,l,7,0,28,2i,b0,10,10,m,o,c,m,c,m
1i,g,a,e,e,i,1g,1i,2g,3sq,30,q,1x,m,0,t,d,1q,au,s,q,s,q,q,w,k,e
1i,g,c,e,g,i,1k,1o,2m,3sq,20,g,25,o,d,t,4,2i,aq,y,o,w,m,q,q,e,e
1g,g,a,e,g,i,1g,1m,2i,3su,22,10,27,e,4,x,f,28,ak,12,o,y,i,u,s,g,c
1g,g,c,e,e,i,1i,1m,2i,3su,22,y,27,e,2,v,d,28,am,10,o,y,i,u,s,g,c
1o,i,g,i,i,m,1w,1w,2w,3rm,2a,2p,k,b,f,9,26,2o,ay,10,12,g,s,c,m,a,m
1g,e,c,c,g,g,1g,1m,2g,3sw,1y,1i,2j,a,k,1b,l,28,ac,18,o,10,c,10,s,i,a
1w,m,i,k,k,q,22,20,32,3rg,t,b,4,l,6,1k,2a,28,be,1c,g,u,c,a,k,e,s
1o,g,i,g,i,o,1w,1w,2w,3rq,28,2x,e,7,f,d,2e,2q,ay,12,y,g,u,c,i,a,m
1i,c,a,a,a,e,y,12,1m,3vk,38,1c,h,4,1a,c,h,1g,ae,10,o,y,g,10,1a,s,u
1i,g,c,e,e,i,1g,1k,2i,3ta,22,i,1x,y,0,p,l,1u,b0,u,m,w,q,q,o,i,c
18,a,8,8,6,a,q,q,14,40c,1r,2u,y,s,j,26,s,3o,96,1o,k,q,s,y,1w,w,10
1o,g,e,g,g,k,1o,1q,2q,3s2,2y,1v,3,g,p,h,w,2g,ay,w,y,i,w,i,q,g,g
1q,i,i,i,k,o,20,1y,30,3qw,24,31,u,r,3,a,2w,2i,b2,10,10,i,m,c,i,a,o
1k,c,8,a,a,c,w,w,1i,3vo,3e,1c,f,i,1g,10,16,1u,ag,12,m,14,g,y,1e,w,10
1w,m,k,k,m,s,24,22,36,3qi,v,9,3,b,8,1w,2q,2c,ba,1g,e,q,e,a,i,e,s
1w,k,i,k,k,q,20,20,32,3rk,f,n,c,t,a,18,2c,26,bc,1e,e,w,e,8,k,e,q
1s,k,i,i,m,o,20,20,30,3r8,w,1z,w,x,8,i,2o,2c,b8,12,u,q,g,a,k,a,q
1g,g,a,e,e,i,1i,1m,2i,3ss,22,16,2b,e,8,11,j,2a,ag,16,m,10,g,w,s,g,c
1c,e,a,c,c,g,16,1c,26,3ug,1a,1m,d,19,s,7,r,6,a8,14,q,u,o,o,1a,k,g
1c,g,8,c,c,g,16,1c,24,3u4,1s,1m,b,1b,u,7,n,0,a8,14,q,u,m,s,18,m,g
1o,i,g,g,i,o,1u,1u,2w,3ro,2e,2l,g,5,l,7,1y,2o,aw,12,10,g,u,e,m,a,k
1y,m,i,k,k,q,22,20,34,3r0,z,6,1,f,6,1m,2g,28,ba,1c,i,s,g,a,m,e,s
1u,i,i,g,k,m,1u,1w,2w,3ro,1a,1r,16,x,a,2,1y,2c,b4,12,u,u,i,c,s,e,m
1c,c,a,a,a,e,12,16,1w,3vg,1c,1k,k,1j,i,a,0,3,a2,12,s,q,u,m,1i,o,k
1y,m,k,k,o,q,24,24,36,3qg,1f,c,b,1,4,26,2k,2c,bc,1g,c,s,c,a,i,e,u
1m,g,e,i,g,m,1u,1u,2s,3s8,1u,1l,t,i,v,h,1q,2w,ay,y,s,m,u,g,i,a,k
1i,g,c,e,g,i,1m,1o,2m,3si,1y,m,2f,o,7,13,e,2q,aq,y,q,w,k,q,o,e,e
1i,g,c,g,e,k,1o,1q,2m,3sg,1w,k,2n,y,j,13,o,2y,aw,u,o,w,o,o,k,c,g
1y,m,i,k,k,q,22,20,34,3r0,x,4,1,f,6,1m,2g,28,ba,1c,i,s,g,a,m,e,s
1u,g,g,e,g,k,1m,1o,2k,3sm,2g,2b,1e,0,n,0,o,34,b2,y,y,q,o,g,q,m,m
1g,e,c,e,e,i,1g,1m,2i,3tg,18,12,1z,6,e,v,n,26,ag,16,o,y,g,w,q,g,c
1i,g,a,e,e,g,1g,1i,2g,3sm,34,y,27,o,6,x,h,1o,au,s,o,u,o,u,u,k,e
1c,c,8,8,8,a,s,s,18,3xy,22,1i,y,1,8,1w,s,3a,9y,u,w,o,12,g,20,s,y
1i,g,c,g,e,k,1m,1q,2m,3sy,1i,6,1z,u,h,v,c,2q,au,y,m,w,m,q,k,e,e
1i,g,c,g,e,k,1m,1q,2m,3sc,26,a,29,q,9,13,g,2u,ao,12,o,w,i,u,m,e,e
1q,i,i,i,k,o,1y,1y,30,3ra,1w,2z,y,p,3,4,2q,2k,b2,12,y,m,m,a,i,a,o
18,c,8,a,8,c,w,y,1i,3ya,x,1i,14,5,x,1i,q,18,9i,1a,w,o,w,o,1q,o,q
1u,k,k,i,m,q,24,20,34,3qk,e,1j,g,r,c,1a,2w,2c,b6,1c,k,q,g,a,i,c,s
1y,o,k,k,m,s,26,22,38,3qg,1p,k,h,4,0,2g,2i,2a,be,1e,c,s,c,a,i,e,u
20,m,k,k,m,s,22,22,36,3r4,1z,w,j,0,1,2i,22,28,bg,1e,i,o,c,a,i,i,s
1a,e,a,a,e,e,14,1a,24,3vs,b,1m,6,1f,s,8,t,9,a2,1a,q,q,q,q,1a,k,g
1m,g,e,g,g,k,1o,1s,2q,3sm,28,1h,l,m,p,n,u,2o,b0,y,s,o,w,g,k,e,g
1c,c,a,8,c,c,10,10,1q,3w2,20,1e,k,17,c,g,0,18,a2,10,s,o,y,m,1k,q,o
1i,g,c,e,g,k,1k,1q,2m,3t0,1k,6,1x,u,d,x,6,2m,au,y,m,w,m,s,k,c,g
1e,e,a,c,e,e,1a,1e,2a,3tq,1w,1m,z,x,w,d,13,k,ac,12,q,y,i,u,12,k,g
1g,g,c,g,e,i,1m,1o,2k,3t4,1g,i,21,o,3,z,1,2i,aq,10,m,y,i,u,m,e,e
1a,c,a,a,a,e,12,14,1w,3wk,p,1m,y,1f,c,s,3,r,a0,16,s,m,w,i,1m,m,k
1q,i,g,g,i,o,1w,1w,2w,3ru,22,2r,w,d,f,7,24,2k,b2,10,10,k,q,c,i,c,m
1y,m,k,k,m,s,24,22,36,3qi,1b,a,b,3,6,20,2m,2c,bc,1g,c,s,c,c,g,g,s
1g,c,a,a,c,e,12,12,1o,3y6,16,1i,b,13,o,a,o,20,a2,16,s,10,g,y,12,k,o
1c,e,a,a,e,e,18,1c,26,3v6,6,1k,5,1d,u,3,t,4,a4,1a,o,s,o,s,18,i,g
1w,k,i,i,m,o,1y,1y,30,3rc,5,j,g,p,8,12,2a,26,b8,18,m,u,g,c,o,e,q
1i,g,c,e,e,i,1g,1m,2i,3t6,22,g,1t,w,1,p,h,1w,b0,u,k,y,q,q,o,g,e
1q,g,g,e,g,m,1o,1o,2q,3ru,32,27,o,c,r,1,q,2c,b0,y,y,i,u,g,s,i,k
18,a,8,8,8,a,q,q,18,406,2b,32,w,q,p,20,c,34,90,20,g,q,s,y,1u,u,y
1m,i,g,g,i,m,1u,1w,2u,3ry,2a,2h,0,4,l,h,1y,2s,b0,y,w,m,s,e,i,a,k
22,o,k,m,m,s,26,24,38,3qq,2n,1m,v,e,2,2u,24,26,bk,1a,k,k,c,c,i,k,u
1m,e,a,c,e,e,16,1a,1y,3ua,3k,c,t,12,k,p,j,1e,aw,q,s,q,q,u,10,q,s
1w,i,g,g,i,m,1q,1s,2q,3sg,w,13,12,p,1,0,1o,28,b8,y,s,w,k,e,s,i,o
1w,m,k,k,m,s,24,22,36,3qg,r,f,1,d,4,22,2q,2c,ba,1e,g,q,e,a,g,e,s
1q,g,g,g,g,k,1o,1q,2q,3rw,2y,29,s,a,r,1,u,2c,b0,y,y,i,w,e,s,i,k
1u,m,i,i,m,q,22,20,32,3qu,4,13,e,r,c,14,2q,2c,b6,18,o,s,e,c,k,c,q
1w,k,k,i,m,q,22,20,34,3qs,5,v,a,n,c,1a,2q,2c,b8,1a,k,s,e,c,k,c,s
1q,i,g,g,i,m,1u,1u,2u,3rk,2s,2x,10,b,d,5,1o,2k,b0,10,10,k,q,e,o,e,k
1q,g,c,e,e,k,1g,1i,2c,3t2,3c,1n,i,o,j,h,2,2q,ay,w,w,m,u,k,u,o,k
1g,g,a,e,g,i,1i,1m,2k,3t8,1e,10,27,c,4,t,d,2c,am,12,m,y,i,u,q,e,e
20,o,i,m,m,s,24,24,36,3qk,21,10,l,6,2,2e,2g,2a,be,1g,c,q,e,a,i,g,u
18,e,a,a,a,e,10,16,1u,3wm,r,1m,y,1b,8,u,1,p,a0,14,u,m,w,i,1m,m,k
1m,g,g,g,i,o,1u,1u,2u,3s2,20,25,d,a,p,f,1w,2w,ay,y,u,m,u,e,i,a,m
20,o,i,m,m,s,24,24,38,3qo,25,12,n,8,0,2i,2c,28,bg,1e,e,q,c,a,g,i,u
1m,e,a,c,c,g,18,18,20,3tw,3s,k,1b,u,i,j,d,1o,aw,o,m,y,m,w,10,q,o
1k,c,8,a,c,c,y,y,1k,3vi,3e,1c,n,o,1e,q,k,1q,ak,y,o,10,i,y,1c,u,y
1u,k,i,k,m,q,22,22,32,3qo,i,1t,k,t,8,1a,30,2a,b6,12,w,o,g,8,i,c,s
1q,g,e,g,g,m,1m,1q,2o,3rs,36,25,m,e,t,3,o,2e,b2,u,10,i,u,g,s,i,k
1o,k,g,i,k,o,20,1y,30,3r4,20,33,s,n,5,6,2w,2k,b0,10,12,g,o,c,i,a,o
1a,e,a,8,c,e,10,14,1w,3vs,s,1k,w,1j,e,e,6,f,a2,12,s,o,w,m,1i,o,i
1i,e,a,e,e,g,1a,1e,2a,3t8,3a,10,1t,6,i,f,l,12,am,w,o,u,m,y,y,m,i
1y,m,k,k,m,q,24,22,36,3qu,1d,8,9,7,6,1y,2i,2c,be,1c,g,s,c,a,g,g,u
1w,o,i,k,m,s,24,22,36,3qi,z,3,7,9,8,1y,2o,2c,ba,1g,e,q,e,a,i,e,u
1i,g,a,e,e,g,1e,1g,2e,3t0,34,s,1v,i,8,n,h,1e,aq,u,o,u,o,u,w,m,g
1a,e,8,a,a,e,10,14,1w,3we,3,1m,s,15,6,q,2,h,a0,12,u,o,y,g,1o,m,i
1u,i,g,g,g,m,1o,1q,2m,3so,1g,1j,16,f,d,1,16,2g,b6,y,u,u,m,e,q,k,o
1w,k,i,k,k,q,22,20,32,3ra,9,v,e,r,a,16,2i,2a,bc,18,k,u,e,a,i,e,q
22,q,i,m,m,s,24,22,38,3r0,2n,1m,x,e,0,2u,20,26,bk,1a,k,m,c,c,i,k,w
1k,g,e,g,g,k,1q,1s,2q,3s2,26,p,1j,m,n,v,1c,2w,aw,w,s,q,o,m,k,e,g
16,a,8,8,6,a,o,o,12,414,2t,34,y,u,4,1k,18,3w,8s,1y,k,s,o,14,1u,y,10
1k,i,e,g,i,m,1u,1u,2s,3rw,24,1j,x,e,p,n,1w,2y,b0,u,w,k,s,i,i,c,i
1e,e,a,a,e,e,14,18,20,3u2,3g,1e,r,13,w,2,b,6,aa,10,q,o,q,y,16,q,k
1u,i,g,g,i,m,1q,1s,2s,3s2,1k,1n,1a,n,0,5,1q,24,b4,y,w,s,m,e,s,g,o
1y,m,i,k,m,q,22,20,34,3qw,11,6,3,b,4,1q,2i,28,ba,1c,i,s,e,c,k,e,s
1i,g,c,e,e,k,1k,1o,2k,3t0,1s,c,1x,u,7,x,5,2g,ay,u,m,w,m,u,k,e,e
1u,k,i,i,m,o,20,20,32,3re,c,1h,q,v,a,s,2g,2c,ba,16,o,s,g,a,k,c,q
1i,g,e,g,g,k,1o,1s,2o,3sk,1q,7,1v,q,j,v,u,2w,au,y,o,u,o,o,k,c,g
1m,e,c,c,c,g,18,1a,20,3tu,3u,8,v,10,c,r,b,1k,ay,o,o,u,q,u,y,q,q
1q,g,g,g,g,m,1q,1q,2q,3rq,32,2j,u,2,l,5,12,2g,b0,y,10,k,s,e,s,g,k
1w,m,i,i,m,q,20,20,32,3rs,x,7,6,r,6,1m,26,20,be,1e,e,w,c,a,k,e,s
1y,k,i,i,k,q,1w,1w,2y,3ru,r,8,c,p,6,y,2c,26,bc,14,o,u,g,c,q,g,u
1a,c,8,6,8,c,o,s,12,3z8,y,1u,12,q,d,2i,y,4c,9w,u,s,q,u,o,22,u,y
20,o,i,m,k,s,22,22,36,3qw,1z,16,l,4,5,2g,26,2a,bg,1c,e,s,e,c,i,i,u
1q,i,i,g,k,m,1w,1y,2w,3rq,1u,2n,y,j,9,1,28,2i,b4,10,y,m,o,a,k,c,m
18,c,a,8,a,c,w,10,1k,3xu,j,1i,14,h,p,18,u,q,9q,14,w,o,y,k,1q,o,o
1g,c,8,8,a,c,u,w,1c,3x0,2m,1e,w,t,1a,16,10,2m,a4,14,q,s,s,u,1k,s,w
1q,g,g,g,g,m,1m,1q,2q,3ry,2u,29,u,a,r,0,u,2c,b0,y,y,k,u,e,s,i,k
20,o,k,m,m,s,24,22,38,3qo,27,1a,p,8,0,2i,2a,2a,bg,1e,e,q,c,c,i,i,u
1u,k,i,g,k,o,1y,1w,2y,3re,w,1j,u,r,4,k,26,2c,b6,14,q,u,i,c,o,c,o
1q,g,g,g,i,m,1q,1u,2s,3ro,2y,2p,w,5,h,9,1c,2k,b0,y,10,k,s,e,q,e,k
1w,m,i,k,m,s,24,22,36,3qi,j,p,4,h,6,1w,2s,2c,ba,1a,k,q,e,a,g,e,s
1y,m,i,k,m,s,24,20,36,3qm,13,4,7,9,8,1u,2m,2c,ba,1e,g,s,e,a,i,e,u
1q,k,i,i,k,o,20,1y,30,3r0,1u,2x,y,t,2,a,2u,2i,b0,16,w,k,m,a,k,a,o
1m,e,c,e,e,g,1e,1g,2a,3t4,3i,6,1n,u,4,b,b,1m,ay,q,s,o,s,s,u,o,k
1s,m,i,k,k,s,22,20,34,3qi,w,23,q,v,6,1a,2w,2e,b4,18,q,q,e,a,i,c,s
1i,c,8,a,a,c,y,10,1k,3vw,36,1a,0,9,1c,k,1,1q,ac,10,o,w,k,y,1e,s,u
1y,k,k,i,m,q,1y,20,34,3r6,v,4,0,j,6,1k,2g,24,ba,1e,e,u,g,c,m,e,s
20,o,k,m,m,s,24,24,38,3qm,2b,1e,r,a,0,2k,2c,28,bg,1e,e,q,c,c,g,i,u
1c,c,8,8,8,c,s,u,1c,3xe,2a,1a,y,d,a,1m,g,2i,a0,u,w,o,12,e,20,q,w
1w,k,i,i,k,q,1y,20,32,3rm,1,11,k,v,a,y,2c,28,bc,1a,i,w,e,8,m,c,q
1m,i,g,g,i,o,1w,1u,2w,3rc,2i,2j,1,1,l,f,2i,2s,b2,u,12,e,s,g,i,a,k
1q,i,g,e,i,m,1o,1q,2s,3ru,2q,2b,y,0,j,3,12,2e,b0,y,10,k,s,e,s,g,k
1a,c,8,8,8,c,u,w,1e,3xy,s,1m,y,5,b,14,a,2g,9s,14,s,q,y,m,1s,s,s
1o,g,i,g,k,m,1y,1w,2y,3rk,28,33,g,b,b,d,2o,2s,aw,12,10,e,u,c,i,a,m
18,e,8,a,a,c,y,12,1o,3x4,5,1k,14,11,7,y,k,8,9u,14,u,o,y,k,1o,o,k
1u,i,g,g,i,m,1o,1q,2o,3sk,1a,1f,16,j,7,0,1a,2g,b6,10,s,u,m,e,q,k,o
1o,i,i,g,k,o,1y,1y,2y,3rg,26,35,q,h,7,5,2m,2o,b0,10,10,g,s,c,g,c,m
1y,m,i,k,k,q,22,20,34,3qy,x,2,0,f,6,1m,2i,28,ba,1c,i,s,e,c,k,e,s
1g,g,c,e,g,i,1k,1o,2k,3t2,1g,s,29,k,5,v,1,2k,ao,10,o,w,m,q,o,e,e
1e,g,a,e,e,i,1g,1k,2g,3t8,1m,1g,27,2,i,13,l,20,ac,18,o,y,g,y,s,g,e
1w,o,i,m,m,q,24,24,36,3qk,17,0,7,7,4,24,2m,2c,bc,1e,e,q,e,a,g,g,s
1e,e,a,c,e,g,1c,1i,2c,3ua,o,1i,17,r,10,d,1l,18,a8,1a,o,10,c,y,y,i,c
18,a,8,8,8,8,q,q,12,40a,1b,2q,y,s,h,2c,10,44,9g,1e,k,q,q,10,1w,w,10
1o,g,e,g,e,m,1o,1q,2q,3sm,2e,1x,3,m,t,h,s,2k,b0,10,y,i,y,e,k,e,g
1c,c,8,8,a,c,u,w,1e,3x4,22,1a,10,l,4,10,6,2e,a0,w,w,o,10,i,1s,s,s
1w,k,i,g,k,o,1w,1w,2y,3rk,a,t,s,t,a,k,26,24,b8,16,m,w,g,c,q,g,o
1w,o,i,k,m,s,24,22,36,3qi,11,1,7,7,8,1w,2o,2e,ba,1g,e,q,e,a,i,e,u
1u,k,i,i,m,o,22,20,32,3r2,o,1t,q,x,8,s,2u,2c,b8,12,u,q,g,8,k,a,s
1y,o,k,m,m,s,24,24,38,3qg,1v,s,j,6,0,2g,2i,2a,be,1g,a,s,c,c,g,g,u
1s,g,i,g,k,m,1w,1w,2w,3rw,1u,2n,14,l,5,3,20,2e,b6,10,y,m,o,a,k,c,m
1k,g,c,g,g,m,1o,1s,2q,3s4,26,j,1n,o,n,v,14,2w,au,y,s,q,o,o,k,c,g
1q,g,i,g,i,o,1w,1w,2w,3ri,2a,2p,o,f,b,7,28,2m,ay,12,10,i,q,c,m,a,m
18,c,8,8,8,c,s,u,1a,3yu,7,20,y,e,r,1s,6,2o,9k,1c,q,o,y,o,1w,q,w
1g,e,a,c,c,e,16,18,20,3u4,3i,1g,1h,j,16,0,n,o,ac,12,o,u,i,10,14,s,k
1k,e,a,c,c,e,14,18,1w,3ue,3m,w,19,q,s,9,n,1c,as,q,o,y,i,10,10,s,q
1k,e,c,c,e,g,1c,1e,28,3te,3g,w,29,k,q,f,j,18,au,q,q,u,m,w,w,o,k
1m,e,c,c,c,g,18,18,20,3u0,3q,6,r,10,e,t,d,1i,aw,q,q,s,q,u,10,q,o
1q,i,i,i,m,o,20,1y,32,3qs,28,3b,14,z,2,g,2w,2g,b0,12,10,m,i,a,k,a,q
16,a,8,8,6,8,q,m,12,418,2r,30,w,w,c,1e,1c,3y,8s,1w,k,s,o,14,1u,10,10
1k,i,e,g,i,m,1s,1u,2s,3s2,20,1d,13,i,r,n,1s,2w,ay,w,u,m,s,i,i,c,i
1m,g,e,i,g,o,1s,1u,2u,3s6,1w,1r,p,g,t,h,1s,2w,ay,y,s,o,s,g,i,a,k
1q,k,i,g,m,o,1y,20,2y,3r8,1o,2r,y,r,1,8,2q,2i,b4,10,y,m,m,a,i,a,o
1i,g,c,e,g,i,1k,1q,2k,3sq,1y,g,23,o,f,r,4,2i,aq,y,o,w,m,q,o,g,e
1c,g,a,a,e,e,1a,1e,28,3ue,10,1m,p,13,w,d,z,g,a6,18,q,w,i,u,14,k,g
20,o,k,m,m,s,24,24,38,3qg,25,14,p,c,0,2k,2g,28,bg,1e,c,q,c,c,g,i,u
1m,e,a,e,e,e,1c,1c,24,3ts,3e,i,1p,s,e,b,b,1o,au,s,o,u,q,s,y,q,k
20,m,i,k,k,q,20,1y,30,3ru,1t,10,b,9,1,1u,28,2a,be,1a,m,q,e,c,o,k,s
1s,i,i,i,m,o,20,20,30,3qw,1m,2p,w,v,6,g,2u,2i,b2,16,u,m,k,a,k,a,q
1s,g,g,g,g,m,1q,1q,2s,3ru,2m,29,y,3,f,1,12,2e,b2,y,y,m,q,e,s,g,m
1u,i,g,g,g,m,1q,1q,2o,3sc,1k,1l,1a,j,5,0,1e,2o,b4,y,w,u,k,g,s,i,o
1k,e,a,e,c,e,18,18,1y,3u6,3k,s,1f,s,m,d,h,1k,as,s,m,y,k,y,10,q,o
1a,c,8,8,8,c,u,u,1e,3y4,m,1o,y,0,h,1c,8,2i,9s,14,s,o,10,m,1s,s,u
1a,a,8,8,8,a,s,q,18,3z4,6,1y,w,o,r,2c,c,30,9q,14,q,q,w,o,1y,u,y
1a,a,8,8,8,c,q,u,18,3yy,a,1s,y,m,v,28,6,2u,9q,12,s,q,y,o,1w,s,y
1o,i,i,g,k,o,1y,1y,2y,3rg,20,2z,m,f,7,3,2m,2o,b0,10,10,g,s,a,i,a,o
1m,g,a,e,e,i,1c,1g,2c,3t6,3g,3,1b,u,1,b,5,1s,ay,q,s,o,u,q,u,o,k
1i,g,e,g,e,m,1o,1q,2o,3s8,24,3,21,s,j,z,w,2w,as,y,s,s,m,q,k,e,e
1k,g,e,g,g,m,1q,1s,2q,3sa,26,11,19,q,x,n,16,2w,ay,u,u,o,u,i,k,c,i
1c,e,a,c,c,g,16,1e,26,3uw,k,1k,7,1j,10,b,p,3,a4,1a,o,s,q,q,16,k,i
1k,g,e,g,g,k,1q,1s,2s,3so,1q,t,1h,12,15,n,10,2y,b0,u,o,s,s,k,i,c,i
1s,i,g,i,k,o,1w,1w,2y,3rc,1y,2p,12,r,1,2,2e,2g,b0,12,y,o,m,c,m,a,o
24,o,k,m,m,s,24,22,3a,3ro,31,1k,z,g,4,2s,1s,1q,bm,18,q,i,a,c,m,m,u
1m,e,c,e,e,g,1e,1g,2a,3t6,3g,2,1h,u,2,b,7,1q,ay,q,s,o,s,s,u,o,k
1i,g,c,g,g,k,1o,1q,2o,3s8,26,4,29,u,h,13,s,2w,as,y,q,u,m,q,k,e,e
20,o,i,m,k,s,22,22,36,3rg,29,16,p,0,1,2q,1s,1y,bi,1e,k,m,c,a,i,k,u
1o,i,g,i,i,o,1w,1y,2w,3ri,2a,2z,o,f,d,7,2g,2q,aw,12,12,g,s,c,k,a,m
1s,i,g,g,i,m,1q,1s,2q,3s0,1w,1x,18,f,5,1,1e,2c,b2,10,w,q,o,e,s,g,m
1y,k,i,g,k,o,1w,1w,2u,3ry,h,0,m,x,a,k,2g,26,ba,12,q,y,e,c,s,g,s
1s,g,e,e,g,i,1i,1k,2i,3te,2k,1r,1a,e,n,n,2,1e,b0,w,10,k,u,i,u,m,o
1w,k,i,k,m,o,22,20,34,3r6,7,x,e,p,a,16,2i,2c,bc,18,k,u,e,8,k,c,s
1c,c,8,8,a,c,w,w,1g,3ws,2e,1a,10,v,i,u,a,2c,a2,w,u,o,10,i,1s,s,s
1e,e,a,c,e,g,1a,1g,2a,3to,1q,1m,15,t,w,d,1d,u,aa,16,o,10,e,y,10,k,e
18,e,8,8,a,c,y,10,1m,3xk,j,1k,14,n,h,18,m,e,9q,16,u,o,y,k,1q,o,m
1m,e,c,c,e,e,1a,1c,22,3tm,3w,6,11,y,8,v,7,1s,ay,o,o,u,q,u,y,q,m
1c,e,a,8,c,e,12,14,1w,3v4,2e,1e,6,1f,k,g,b,g,a4,12,q,q,u,q,1e,o,m
1u,m,i,k,m,q,22,20,34,3qq,1,z,a,n,c,18,2s,2c,b8,1a,k,s,e,c,i,c,s
1g,e,a,a,e,e,1a,1c,26,3tg,30,1k,1b,l,u,f,p,k,ag,y,o,w,m,u,14,m,i
1a,e,a,8,c,e,12,16,1y,3vo,k,1m,s,1p,m,c,5,j,a2,14,s,o,w,k,1i,m,k
18,a,8,8,6,a,q,q,14,40e,23,30,y,s,j,26,o,3k,92,1u,i,s,o,10,1w,w,y
22,q,i,m,m,s,24,22,38,3r6,2r,1m,x,g,1,2w,1y,26,bk,1a,m,k,c,c,k,k,w
1q,k,i,i,k,o,20,1y,32,3r0,1q,2t,y,t,2,c,2s,2i,b0,16,w,m,k,a,k,a,q
1i,c,8,8,c,a,u,u,1c,3w8,3e,18,a,3,1k,1g,2e,26,ag,10,o,10,m,w,1i,w,16
1g,g,a,e,g,g,1i,1k,2i,3sk,2m,1a,2l,k,4,z,j,24,as,u,o,y,k,s,s,i,e
1o,g,g,g,g,m,1q,1u,2s,3s0,2k,25,2,8,t,d,1e,2o,aw,10,10,g,w,e,o,e,i
1q,k,i,i,k,q,20,20,30,3qu,1u,2z,12,z,4,k,2u,2g,b0,16,w,m,k,a,i,a,q
1i,e,a,c,e,e,1a,1c,26,3tg,3e,18,1p,5,q,7,l,m,ai,y,o,u,k,10,10,o,i
1y,k,g,i,k,o,1w,1u,2w,3ry,d,3,o,x,a,i,2e,24,ba,12,q,w,g,c,s,g,s
1c,g,a,c,e,e,1c,1g,2a,3um,g,1i,v,z,y,7,1d,w,a6,1a,q,w,i,u,12,g,e
1o,e,c,c,e,g,1c,1c,24,3te,3u,b,l,w,0,t,3,20,ay,q,q,s,s,s,w,o,o
1k,g,e,g,g,m,1q,1s,2q,3s8,2a,z,1b,o,t,n,14,2w,ay,u,w,m,s,k,m,c,g
1e,e,a,c,e,g,1c,1i,2c,3u6,u,1i,13,v,s,l,r,w,aa,18,o,u,m,u,y,i,e
1u,g,g,g,g,m,1o,1q,2m,3se,1y,1v,1a,b,b,0,14,2s,b4,w,y,s,m,e,u,i,o
1w,i,i,i,k,o,1u,1w,2y,3rm,g,z,w,v,a,g,24,26,b8,14,o,w,g,c,q,g,o
1q,k,i,i,k,q,20,1y,32,3r0,1k,2t,u,t,2,i,2w,2i,b2,12,y,k,m,a,i,a,q
1o,i,i,i,k,o,1y,1y,30,3r0,26,31,q,l,9,6,2u,2m,b0,10,12,g,o,c,i,a,o
1e,e,c,c,c,g,1c,1g,2c,3ti,1w,1k,1f,j,q,l,11,16,ae,12,o,10,g,w,10,i,e
1e,g,c,c,e,i,1g,1k,2g,3to,12,18,1r,3,i,n,z,1y,ae,18,m,10,e,w,u,g,c
18,c,a,8,8,c,w,y,1g,3yc,7,1g,10,9,t,1g,a,1q,9m,16,w,o,y,m,1s,o,s
22,o,i,k,m,s,20,20,36,3rs,2j,1i,t,4,3,2g,20,24,bi,1a,m,m,e,c,k,m,u
20,o,k,m,m,s,26,22,38,3qg,2b,1c,r,g,1,2k,2e,28,bg,1e,c,s,c,a,g,i,w
1e,g,a,e,e,i,1g,1k,2g,3ti,1a,1c,1v,5,g,v,j,1u,ag,14,o,w,k,u,u,g,e
1o,i,g,i,i,o,1y,1w,2y,3ri,28,31,o,f,d,7,2k,2o,aw,12,10,i,s,c,k,a,m
1a,c,a,a,a,e,10,14,1w,3wm,j,1m,10,1h,c,o,1,v,a0,14,s,o,y,c,1s,m,k
1u,k,i,k,k,q,22,20,34,3qo,q,1t,o,v,c,10,2u,2c,b6,18,o,q,g,a,k,a,s
20,m,i,k,m,q,22,20,38,3ry,21,u,h,9,2,2c,1s,18,bi,1e,i,s,a,a,i,k,u
1w,o,i,k,m,q,24,20,36,3qq,z,0,3,b,6,1q,2m,2c,ba,1c,i,s,e,a,k,e,s
1m,g,e,e,g,m,1o,1s,2q,3sm,22,13,13,u,v,p,w,2u,b0,w,q,s,u,g,k,c,g
1m,g,e,i,g,o,1s,1w,2u,3ro,2c,1t,p,c,r,n,24,2w,b0,u,y,i,u,g,i,c,i
1g,e,a,c,e,g,1e,1g,2e,3t4,2c,1k,1v,5,i,p,t,1c,ak,y,o,y,k,u,y,i,e
1s,i,k,i,k,q,22,1y,32,3qu,1a,2j,s,v,4,s,2y,2g,b2,12,y,m,k,8,i,c,q
1e,e,a,a,e,e,18,1c,24,3to,2g,1k,n,11,u,7,t,8,aa,12,q,w,k,u,16,m,g
1g,c,8,8,a,c,u,u,1c,3xo,28,1i,12,x,1a,18,1i,32,a4,14,q,s,s,u,1k,u,w
1i,i,c,g,g,k,1o,1q,2q,3sc,24,9,1v,s,n,v,u,2u,au,w,q,u,o,o,k,e,g
1g,c,a,8,c,c,w,y,1i,3w6,30,1c,e,l,1c,o,8,1w,a8,12,q,s,q,u,1i,s,u
1a,c,8,6,8,c,o,s,14,3z0,18,1q,y,s,h,2i,w,46,9w,s,u,q,y,m,20,u,10
1c,c,8,8,a,c,u,w,1g,3x4,20,1a,10,l,4,y,8,2c,a0,w,w,o,10,i,1s,s,s
14,c,6,8,6,8,q,m,12,41c,2v,2o,y,w,q,s,1i,3u,8u,1o,o,u,o,16,1s,10,y
16,c,8,8,8,c,u,u,1c,3zg,1f,1k,12,e,z,1m,i,24,9a,1e,w,q,u,s,1q,s,s
1m,i,g,g,i,m,1w,1w,2u,3rw,22,2f,3,2,j,f,26,2s,ay,10,w,i,u,e,i,a,k
1m,i,g,g,k,m,1w,1w,2w,3ru,22,2n,4,1,j,d,2a,2u,ay,10,w,i,u,e,g,c,k
1q,g,g,e,i,k,1o,1q,2o,3rs,34,29,o,a,p,5,s,2g,b2,w,y,i,u,g,s,i,i
22,o,i,m,m,s,22,22,36,3r8,2h,1i,t,c,5,2s,1y,24,bi,1c,i,o,c,c,k,k,w
1w,m,i,i,m,q,1y,20,32,3r6,h,9,8,l,8,1a,2e,26,ba,1a,i,u,g,c,m,e,q
22,m,k,k,m,q,20,20,36,3ro,2d,1g,p,0,3,2e,22,24,bg,1c,k,o,e,c,m,k,u
1a,e,a,a,c,e,14,1a,20,3vw,b,1m,i,1n,q,a,j,l,a2,18,q,q,u,k,1g,k,g
1g,g,c,e,g,i,1i,1o,2i,3sq,24,u,2b,m,5,v,7,2e,aq,w,o,y,k,s,q,g,e
1k,e,a,c,c,g,16,18,1w,3ua,3k,u,1d,s,o,b,l,1i,as,s,m,y,k,y,10,q,q
1q,k,i,g,k,q,1y,1y,30,3r2,1w,2x,y,t,2,6,2u,2i,b0,14,y,k,m,a,k,a,q
22,o,k,m,m,s,22,24,36,3r0,2j,1i,t,c,3,2u,20,28,bi,1c,i,o,c,c,i,k,u
1i,c,8,8,c,a,u,u,1c,3wg,34,1c,a,5,1i,1a,1u,2u,a8,16,o,y,k,y,1i,w,12
1y,m,i,m,k,s,22,22,36,3qo,15,6,7,7,6,1u,2k,2c,ba,1e,g,s,e,a,k,e,s
1q,i,i,i,m,o,20,20,30,3qu,1s,2v,u,r,3,k,2y,2g,b2,10,10,k,k,a,k,8,q
1i,g,a,e,g,g,1i,1m,2i,3sg,2s,s,23,m,3,t,d,20,au,s,s,u,m,s,s,i,e
20,k,i,k,o,q,28,2a,3q,3v4,q,e,1j,2,1k,1r,13,y,c0,1s,k,e,0,1,c,m,a
24,q,k,m,o,s,24,24,3a,3r8,31,1e,v,g,i,2y,1w,20,bm,18,q,g,c,a,m,m,w
28,q,o,o,q,s,2k,2k,44,3w6,1v,l,27,h,1c,z,a,h,bs,16,10,c,6,a,g,c,o
1w,g,e,e,g,k,1m,1u,36,3vy,o,1t,14,d,0,v,l,1j,bo,1m,q,m,k,a,k,4,4
22,o,m,m,q,s,2i,2k,40,3xg,r,2j,1l,c,i,21,4,g,bq,1e,u,g,2,4,c,g,g
1q,e,c,e,c,i,1c,1e,2a,3tu,3a,z,m,12,d,l,j,m,ay,u,10,g,y,m,y,o,q
1s,g,g,e,e,k,1k,1k,2k,3tg,2e,1x,1g,a,l,r,4,16,b2,w,y,m,u,g,u,m,q
26,q,k,m,o,u,2c,2a,3q,3so,2x,1s,11,c,r,3,28,6,bs,16,w,e,8,c,m,g,o
26,o,k,m,o,u,2e,2g,42,3ta,1v,22,x,n,13,a,10,r,bs,1c,w,e,6,c,c,a,k
1y,k,i,i,k,s,22,2e,3m,3vm,c,1,t,p,t,f,1x,8,bk,1e,o,q,c,6,e,8,g
22,o,i,m,m,s,26,26,3e,3sw,2t,1c,n,c,f,d,1c,1c,bo,1c,u,g,a,8,m,i,q
26,s,o,m,q,w,2k,2k,48,3xi,13,3j,15,i,0,1h,m,5,bq,18,y,g,4,6,e,e,k
1y,k,g,i,i,q,1w,26,3g,3us,1d,3,s,t,5,d,t,k,bo,1k,m,u,c,4,i,6,8
2a,u,o,o,s,y,2m,2m,48,40k,5j,21,4,b,4,m,f,q,c6,16,o,8,0,4,i,m,g
26,q,m,m,o,s,2a,28,3g,3s0,35,q,b,w,i,6,18,22,bq,16,u,e,8,a,q,o,s
1g,a,8,8,8,a,o,q,1a,3ww,3k,16,w,d,1g,3g,3a,v,aa,10,s,14,q,w,1o,12,1a
1o,e,c,c,c,e,18,1e,26,3uy,2k,d,1,1m,e,9,1j,j,au,10,12,k,u,s,y,o,s
1o,e,c,c,c,g,18,1c,28,3uw,2k,f,0,1m,e,d,1b,p,au,10,14,i,u,s,y,o,s
26,s,k,q,o,u,2e,2g,44,3v2,1r,g,2l,b,i,b,k,11,bq,16,10,g,6,a,i,e,o
1y,i,i,g,k,o,1w,1y,34,3tw,p,h,k,17,e,0,6,q,bk,1e,o,u,c,8,k,g,m
28,q,k,q,q,u,2i,2m,48,3u6,x,1a,2b,11,4,1,6,l,bu,1c,s,e,6,8,e,a,m
1m,c,a,a,c,c,10,14,1u,3uo,3m,14,13,1a,1i,e,9,15,ay,q,q,y,o,w,14,w,y
2a,q,q,o,q,w,2m,2o,48,3w6,6,35,17,b,m,15,g,0,bu,18,w,c,6,6,e,c,m
20,k,i,i,k,q,20,20,34,3u2,17,c,7,v,h,t,s,1w,be,1c,g,12,e,8,o,e,s
28,s,m,o,q,u,2c,2g,3y,3ts,1r,8,1h,t,2,q,12,13,bs,12,12,e,8,a,k,g,u
24,q,k,m,o,s,2a,28,3e,3s8,31,1c,p,s,0,f,1i,20,bq,18,u,e,8,a,q,k,s
1k,c,a,a,a,c,y,10,1m,3um,3o,14,p,u,1m,q,k,1y,aw,s,u,10,s,o,1e,w,u
1m,e,8,c,a,e,10,16,1u,3us,38,q,t,1c,1i,4,b,8,au,w,y,s,q,u,16,u,u
22,m,k,m,m,s,2a,2i,3u,3vs,h,4,2d,4,2,x,15,g,bo,1c,s,k,8,6,e,a,i
26,s,k,o,o,s,28,2a,3e,3sw,37,4,b,m,c,c,12,2k,bq,14,u,g,a,a,s,m,u
28,s,m,q,o,w,2i,2i,48,3vc,19,11,1p,l,g,7,i,t,bs,16,10,c,8,8,i,c,q
28,s,o,o,s,y,2m,2k,46,402,4j,23,d,9,k,2,d,16,bu,1i,q,a,1,0,k,m,g
2a,u,o,q,s,y,2k,2o,48,40e,5t,1r,a,2,9,u,b,c,ca,14,m,6,2,2,m,k,i
22,m,i,m,o,s,2e,2e,3s,3uq,16,i,2v,6,s,l,v,1o,bw,1i,s,i,0,1,6,o,8
1i,c,8,a,a,a,u,w,1e,3v0,3u,18,8,7,1o,1k,1i,2s,am,y,s,14,u,m,1k,12,u
24,q,o,o,o,u,2k,2k,40,3zi,4p,21,1b,1m,o,r,9,10,bs,1i,q,c,1,4,e,m,e
26,q,k,m,o,u,2c,2a,3s,3sq,2v,1u,13,8,r,2,22,1,bs,16,w,e,a,a,k,i,o
22,q,k,m,m,s,24,24,38,3qy,2v,1o,x,e,6,2y,20,24,bk,1a,m,k,a,c,k,k,w
24,o,k,m,q,u,2e,2g,3y,3w4,9,b,1z,14,h,f,7,a,c0,1s,g,a,1,2,i,i,g
28,s,k,o,o,u,2a,28,3i,3sm,37,c,3,g,4,a,1m,22,bs,14,s,g,8,c,s,k,u
28,q,m,m,q,u,2c,2c,3s,3sy,2p,16,v,b,n,m,1y,b,bu,14,u,g,8,a,o,g,q
24,q,i,o,m,s,24,26,3e,3rg,2x,1g,11,m,k,2e,1c,10,bo,18,q,i,8,a,m,m,w
28,q,m,q,q,w,2m,2o,4a,3vs,b,1,3b,9,k,n,0,0,bu,1c,s,e,4,4,a,g,k
26,q,k,o,o,u,2e,2e,42,3uw,1r,w,2t,1,2,d,m,17,bq,16,10,g,6,a,i,e,o
24,q,k,m,o,s,24,26,3i,3ri,2z,18,x,q,q,28,10,w,bo,18,q,i,8,a,m,o,u
26,s,k,m,o,s,26,28,3g,3rw,3b,m,f,u,y,1q,q,1u,bq,16,q,i,8,a,o,q,u
1g,c,8,6,a,a,q,q,1a,3w0,3u,18,w,l,1i,30,2k,14,ac,12,s,16,s,s,1k,16,14
28,s,m,o,o,w,2a,2c,3q,3t2,29,a,f,h,h,q,1w,0,bu,10,y,g,8,a,o,i,u
1k,c,8,a,a,c,w,y,1k,3uk,3y,18,n,q,1k,14,k,2c,ay,q,o,16,q,o,1g,y,w
1k,c,8,a,a,a,w,y,1i,3us,3q,18,d,e,1m,16,18,2e,as,u,s,14,s,o,1i,y,u
1g,c,8,6,a,a,q,q,18,3vy,3w,16,w,l,1i,34,2g,1k,ae,10,s,16,s,s,1m,14,14
24,q,k,m,m,s,26,24,3a,3r2,2v,1i,x,e,k,30,1o,1k,bo,18,o,i,a,a,k,m,w
1y,k,g,g,k,o,20,2a,3k,3v0,1h,c,14,17,g,5,21,4,bq,1q,i,q,e,4,6,4,e
1i,c,8,a,a,c,u,w,1g,3uy,3q,18,0,1,1o,1e,1m,2m,am,10,s,14,u,m,1i,10,u
1o,c,a,c,c,e,14,18,20,3v0,2s,e,n,1i,14,1,15,5,as,10,10,m,s,u,12,s,u
24,s,k,m,o,s,24,24,3g,3re,33,14,r,k,w,2k,1q,1a,bo,18,q,g,a,a,m,o,u
28,s,m,o,q,u,2c,2c,3w,3tc,1x,a,x,j,j,12,18,t,bu,10,10,g,8,a,k,i,u
1y,k,i,i,m,o,22,24,3a,3um,n,c,5,x,f,1d,l,0,be,1g,i,w,e,8,m,e,s
24,q,k,m,o,s,24,24,3a,3r2,2z,1g,v,g,k,30,1s,20,bm,1a,o,g,a,c,k,m,w
22,q,i,m,m,s,24,26,3e,3rs,2t,1i,13,k,a,22,u,14,bm,1a,q,k,8,a,k,o,u
28,s,m,o,q,u,2c,2c,3w,3tm,1z,e,17,r,9,y,18,x,bs,12,10,g,8,a,k,i,u
24,q,k,o,m,s,24,24,3a,3rk,35,18,r,g,k,2u,1q,1q,bo,16,q,i,a,c,m,m,w
1g,c,8,8,a,c,u,u,1a,3ys,1c,1k,10,p,14,1y,1w,36,a2,14,s,w,o,w,1i,s,y
24,o,i,m,m,s,26,2a,3c,3s8,2x,1i,13,u,6,u,b,2a,bo,1a,s,i,8,8,m,m,u
1q,g,c,c,e,i,1c,1g,2e,3ts,38,19,y,16,l,n,h,p,ay,w,10,e,10,o,u,o,u
24,o,i,o,m,s,2a,26,3k,3sq,2z,1q,t,g,p,f,22,o,bq,1a,u,g,8,a,m,g,q
2a,u,o,o,u,y,2k,2m,4k,3ye,3z,6,g,1f,19,4,20,4b,cs,u,c,4,6,a,s,6,q
1e,c,8,6,8,a,q,o,14,3z2,1y,1i,12,4,16,3c,3e,36,a2,12,s,o,12,y,1q,y,1c
24,q,k,o,m,s,26,22,3c,3ra,33,1a,r,g,o,2u,1w,1w,bo,16,q,g,c,a,m,m,w
1o,e,c,c,c,e,18,1a,26,3uo,2w,3,7,1m,e,5,1f,f,aw,w,10,k,u,u,w,s,u
1w,e,e,e,e,i,1m,1u,2s,3ws,8,2j,2e,i,0,28,3d,8,bu,1u,s,i,k,6,0,8,e
28,q,k,o,q,u,2c,2g,42,3t8,1x,16,z,v,j,s,y,r,bu,18,u,g,6,a,k,c,m
1w,g,g,e,g,k,1s,20,30,3wc,d,1f,26,11,i,22,3j,w,bs,1u,o,k,k,2,1,a,a
2e,s,o,q,s,y,2k,2m,4e,3wg,2d,z,q,2,p,m,q,27,cq,s,i,0,6,8,s,a,o
24,q,k,o,m,u,26,26,3e,3ta,2x,o,15,c,2,c,1s,24,bo,16,u,i,8,c,q,i,u
2c,s,q,o,s,y,2k,2k,4m,3va,1p,k,0,v,n,8,20,4p,cu,q,a,8,8,a,s,4,q
2a,u,o,q,q,y,2o,2m,48,3zw,4h,2z,8,1,i,l,4,10,c8,12,q,6,2,4,i,k,i
26,o,i,q,o,u,2k,2m,42,3u6,1,3e,51,7,4,9,l,y,bw,1g,m,i,4,4,4,g,i
1s,g,e,e,e,k,1i,1k,2i,3ti,2m,1r,1c,i,n,r,1,o,b0,w,10,i,w,k,s,o,q
1y,k,g,e,k,o,22,28,3e,3vi,19,s,1g,27,q,7,1h,w,bu,1q,i,q,g,1,1,a,c
28,s,m,m,o,u,2c,28,3i,3sy,2z,6,3,6,3,5,2c,1s,bs,12,w,e,a,a,s,k,u
24,q,m,o,q,s,2m,2k,42,3xw,19,2t,1j,b,1k,2p,i,e,bq,1e,u,g,0,6,c,g,i
1e,c,8,8,8,a,q,s,14,3za,1c,1k,14,1,16,2y,2u,44,a0,12,u,q,w,y,1o,u,18
20,o,i,k,m,q,24,24,3e,3ua,1p,y,l,v,t,t,y,m,bg,1e,m,u,a,a,k,g,u
24,q,k,m,m,s,24,24,3c,3ru,37,1e,x,g,e,2m,1o,1a,bm,18,q,i,c,a,m,o,u
20,k,g,k,m,q,2a,28,3k,3v2,v,22,n,1t,y,13,3,1a,bw,1o,k,o,6,3,2,i,a
24,q,m,o,o,s,2k,2k,40,3wy,x,1p,25,8,u,23,6,o,bq,1a,w,g,4,6,g,a,k
22,m,i,k,o,s,2a,2e,40,3ty,2d,2g,4,19,x,9,0,h,bs,1k,o,k,6,8,a,4,k
20,s,k,m,o,u,2g,2e,46,3zg,7h,1w,6,q,1n,15,1q,3h,c4,1m,e,6,6,a,o,4,m
24,q,i,o,m,u,2a,28,3q,3uc,27,1c,1n,7,13,3,18,b,bm,1a,w,i,8,a,k,g,s
26,q,k,o,m,u,2a,28,3g,3s8,35,u,9,u,4,t,20,2e,bs,14,w,c,8,c,q,m,s
28,u,o,o,u,y,2i,2m,4c,410,6f,t,i,j,j,6,u,1p,ce,16,i,4,0,8,q,c,o
24,q,i,m,o,s,2a,26,3g,3si,31,1i,n,m,f,v,2c,1e,bq,18,w,e,8,a,q,g,s
1m,c,8,c,a,c,y,12,1q,3ui,3q,10,v,14,1k,k,1,1i,ay,s,s,y,s,q,1c,u,w
1k,c,a,8,c,a,y,y,1m,3uw,3s,16,p,w,1i,10,w,7,b0,m,q,12,q,q,1c,y,10
28,q,m,o,q,u,2c,2e,3y,3u4,1p,8,z,1t,2,m,14,1b,bs,12,14,c,8,a,k,i,y
26,o,k,o,m,s,2a,28,3g,3s6,31,14,l,u,6,d,1e,26,bq,16,w,e,8,a,o,m,s
22,q,m,o,o,u,2g,2i,3y,3zq,5x,v,19,2i,0,5,n,16,bw,1k,k,a,1,6,i,k,e
24,q,k,m,o,s,2c,26,3k,3sg,2z,1m,r,k,f,p,28,1c,bq,18,w,e,8,a,o,i,q
1m,e,8,c,c,c,12,16,1u,3v0,30,s,x,1e,1i,6,v,6,as,y,y,q,s,u,14,u,u
1e,c,8,6,a,a,o,o,14,3y4,2w,1a,y,1,18,3e,3s,y,a6,10,s,u,y,y,1q,y,1e
2e,s,q,q,s,w,2m,2m,4m,3vi,z,h,q,17,d,5,20,4v,cu,q,e,0,a,a,u,4,s
20,m,i,i,m,s,24,28,3o,3u0,2j,1a,2,7,19,8,a,1,bo,1i,q,o,a,6,g,8,e
24,q,i,m,o,s,28,26,3c,3to,2r,10,1b,a,l,2,1w,1k,bm,18,u,k,8,c,m,i,u
22,m,i,m,m,s,24,26,3e,3ua,1x,14,p,p,11,p,14,i,bi,1c,q,o,c,a,k,g,u
22,o,i,m,k,s,20,22,38,3sc,2p,1a,t,4,5,24,1e,1i,bi,1a,q,k,e,c,k,o,q
2a,q,q,o,q,y,2m,2q,48,3xe,l,3h,x,f,u,11,1,k,bw,18,u,c,4,4,c,i,i
1w,i,e,e,i,k,1q,22,3a,3vw,0,1f,1e,d,0,s,2n,t,bq,1q,m,m,k,8,8,0,a
2c,u,o,q,s,y,2k,2m,4i,3x2,2h,r,u,t,r,4,1k,3p,cq,s,i,0,8,8,s,8,q
1e,c,8,8,8,c,q,q,14,3za,1c,1k,14,3,16,30,2q,42,a0,14,s,q,w,y,1o,u,16
22,o,i,m,m,q,22,22,38,3sa,2t,1c,v,6,1,26,1e,1s,bi,1a,q,k,c,e,k,o,s
26,s,k,o,o,s,28,28,3g,3sg,3b,a,9,q,o,s,14,1y,bq,14,u,g,a,8,s,o,s
26,q,o,o,q,w,2k,2k,42,3xu,1p,1p,1l,a,i,0,j,16,bu,1k,q,a,1,0,g,o,e
26,q,k,o,q,u,2g,2k,46,3tq,t,18,19,1b,f,6,k,f,bu,1c,w,a,6,a,e,8,k
26,o,m,m,o,u,2e,2g,40,3v4,1j,c,2b,3,d,f,o,1b,bo,18,10,g,6,a,i,e,q
2a,u,q,o,s,y,2m,2m,48,3zu,4x,2h,g,a,5,a,5,q,ca,12,q,4,2,4,k,k,i
2a,q,m,q,q,u,2i,2k,48,3um,13,h,2b,h,o,3,m,13,bs,16,12,c,6,a,i,c,m
1m,c,8,c,a,c,y,12,1q,3um,3i,10,v,14,1m,e,k,w,aw,s,w,y,q,q,1c,w,s
2a,s,q,o,q,y,2m,2q,48,3yk,2b,3l,f,1,c,r,2,g,by,18,s,c,4,2,e,k,i
2a,s,q,m,s,y,2m,2q,48,3yc,1j,41,9,b,g,l,7,m,by,16,u,c,4,2,e,k,g
28,s,o,o,q,u,2m,2o,48,3xs,1d,3j,r,f,12,1r,g,1,bu,18,w,c,6,6,e,e,k
22,o,i,k,m,u,28,2a,3s,3tg,2v,24,p,a,1t,g,12,p,bq,1e,s,k,8,a,g,c,g
26,s,m,m,o,s,28,2a,3e,3sm,3b,4,7,s,k,g,14,2e,bq,14,u,g,a,8,u,m,u
1k,c,8,8,c,a,w,w,1k,3uw,3s,18,b,e,1m,18,14,1s,as,u,q,14,s,o,1g,10,w
1y,m,g,k,k,q,1y,1y,3a,3ss,1d,6,0,t,4,1i,1k,0,bi,1e,k,u,c,8,i,k,q
20,o,i,k,k,s,22,24,3c,3so,2d,10,p,1,8,1a,a,12,bm,1c,o,o,a,8,i,m,s
28,s,k,o,o,s,2a,2a,3g,3sg,3b,c,1,o,e,g,s,2i,bs,14,s,g,8,a,s,m,u
1m,c,a,a,c,c,10,14,1u,3uy,3a,12,11,1c,1g,e,f,p,aw,s,u,u,q,u,16,u,y
2c,s,o,q,s,y,2k,2m,4e,3yu,45,15,o,9,v,k,u,29,ck,w,m,2,2,8,s,a,o
26,s,m,m,o,s,28,2a,3i,3sa,3f,e,9,w,w,10,0,2c,bs,12,u,g,8,a,s,m,u
1g,c,8,6,a,a,q,q,1a,3ws,3i,16,y,j,1k,2s,3a,6,aa,12,q,12,s,u,1o,10,18
2a,u,o,o,s,w,2o,2o,48,3z4,3b,35,d,3,e,h,1,o,c0,18,q,c,2,4,e,k,g
22,q,i,o,m,s,24,24,3a,3qo,2n,1k,x,g,6,2u,24,26,bk,1a,i,m,c,c,i,i,w
22,q,i,m,m,s,24,24,36,3r4,2r,1m,x,g,1,2y,1y,26,bk,1a,m,k,c,c,i,m,u
1i,c,8,a,a,a,u,w,1g,3uy,3s,18,4,5,1o,1g,1q,2w,am,y,u,14,u,m,1k,10,u
26,s,k,o,o,s,26,2a,3g,3s6,3d,i,d,s,y,1a,i,1y,bq,14,s,i,8,a,q,o,u
1y,k,g,g,k,m,20,2a,3k,3v6,1f,6,1c,19,q,3,2p,8,bq,1q,k,q,e,4,4,2,e
24,o,i,m,o,s,2c,2a,3u,3us,1x,1q,19,1r,j,r,y,21,bk,1e,u,k,6,c,i,g,y
22,o,i,m,m,s,2a,2c,3s,3v2,1b,1c,1x,l,7,z,9,h,bm,1c,s,m,8,8,i,c,s
20,m,i,k,k,s,20,22,34,3tq,1t,m,p,f,f,0,w,22,bg,1c,i,w,c,a,o,i,q
1s,g,g,e,e,k,1k,1m,2i,3te,2e,1x,1g,8,n,n,6,1a,b2,w,y,m,u,g,u,m,o
1o,e,c,c,c,g,16,1c,28,3ue,34,7,1,1k,6,7,15,t,ay,u,y,k,w,s,y,q,u
1w,i,g,g,i,m,1s,1q,2s,3sm,o,z,y,p,1,4,1g,1y,b8,12,q,w,i,e,q,k,o
26,q,k,m,o,s,26,28,3c,3si,39,o,r,q,i,1c,m,28,bo,16,u,g,a,a,q,o,s
2c,u,o,q,q,10,2k,2m,4a,3yi,3t,1x,10,e,t,k,c,x,ck,u,q,0,2,6,q,e,o
22,o,i,m,m,s,24,2a,3a,3sc,2t,1g,15,q,a,10,r,2i,bo,1a,q,k,8,a,k,m,u
26,s,k,m,q,u,2e,2g,40,3t6,1v,1e,13,r,l,o,10,r,bu,18,w,e,6,a,i,e,k
1c,a,8,8,6,a,o,m,y,3z6,1w,1g,14,q,i,3k,4g,54,a0,w,w,q,14,14,1q,10,1k
1w,g,e,e,g,m,1m,1u,32,3vs,s,1t,14,f,7,z,1h,1,bm,1i,s,o,m,8,m,8,6
20,q,k,m,m,s,24,24,38,3qm,2h,1e,t,e,2,2s,26,28,bi,1c,g,o,c,a,i,i,w
28,s,m,q,s,w,2m,2k,4i,3yk,4r,2g,1h,2d,1t,a,2o,53,cy,w,4,a,8,c,k,4,o
1w,g,g,e,g,m,1o,24,38,3vy,0,1f,1e,9,9,1c,33,n,bq,1q,o,k,k,8,8,1,c
28,q,o,o,o,w,2k,2k,48,3wy,z,2v,1d,0,i,1b,e,f,bs,16,y,g,4,8,e,e,k
2a,u,o,o,s,y,2o,2m,48,40c,5d,2b,3,1,8,a,d,y,c4,18,o,8,2,2,g,o,e
26,q,k,m,o,s,24,26,3g,3s0,3d,10,r,m,s,28,18,12,bo,16,s,g,c,8,o,q,s
28,q,q,m,q,w,2m,2m,46,3ys,2h,3r,l,i,4,1h,k,c,bu,18,w,c,4,4,e,g,k
2c,u,o,q,s,y,2k,2m,4c,3xq,39,1n,10,4,r,o,g,1h,cm,u,m,0,4,6,s,c,o
1w,g,g,e,i,m,1q,1u,2u,3uw,m,1l,14,t,9,j,j,10,bk,1a,u,q,i,a,m,e,k
1y,k,g,g,k,o,24,24,3e,3vm,13,1a,k,1j,1w,1t,r,1c,bw,1u,m,k,6,3,4,k,8
2a,q,o,o,s,u,2m,2o,4a,3vy,7,1n,21,l,16,1b,e,2,bu,1a,u,c,6,6,c,e,k
20,o,i,m,m,q,22,22,3a,3rs,2f,18,r,0,4,2i,1q,10,bk,1c,m,m,a,a,k,k,u
28,q,m,m,o,u,28,28,3g,3sk,3b,6,1,m,k,c,1c,2c,bs,12,u,g,a,8,u,k,u
2a,w,o,o,s,y,2k,2o,48,3zq,5f,25,q,a,d,o,5,a,cc,12,o,4,2,4,k,k,i
28,s,m,m,q,u,2c,2c,3s,3t0,2f,q,n,l,l,w,22,9,bu,14,u,g,8,a,o,g,s
26,q,k,o,m,u,24,26,3g,3rm,37,u,l,o,10,2a,1c,1e,bq,16,q,g,a,a,o,o,u
22,o,i,m,m,s,24,22,3a,3ra,2l,1g,v,8,2,2w,1s,1q,bk,1c,m,k,a,a,i,m,w
28,s,o,o,q,w,2m,2o,48,3ye,1r,45,7,d,o,1h,c,7,bu,18,w,e,2,6,e,g,k
22,o,k,k,m,s,22,22,3a,3se,2t,18,x,8,1,24,10,1k,bi,1a,s,k,c,c,k,o,s
2c,u,o,o,s,y,2k,2o,48,3yo,4r,1l,w,g,r,12,3,d,ci,y,m,2,2,6,o,g,k
2e,s,o,q,s,y,2k,2m,4g,3wc,25,13,10,9,j,k,u,2p,cq,s,i,0,6,8,u,8,q
1y,i,g,g,i,o,1u,26,3e,3v6,t,j,14,11,i,f,1p,c,bo,1o,k,s,g,4,c,4,8
20,k,i,i,m,q,22,22,3a,3to,1z,i,1,j,4,n,g,1k,bm,1e,q,q,a,6,m,g,m
28,s,k,o,q,w,2e,2m,46,3to,11,a,r,1r,4,i,1,l,bu,1a,w,e,6,6,k,a,k
26,q,m,m,o,u,28,28,3e,3t8,31,k,v,6,6,g,1o,1u,bq,14,u,i,8,c,q,i,w
1g,a,8,8,8,a,q,q,18,3vs,40,16,w,j,1e,3g,22,2w,ae,10,s,16,u,s,1m,16,12
1g,c,8,8,8,c,q,s,18,3vq,3w,18,y,r,1k,2o,26,24,ae,12,q,16,u,q,1k,16,10
1w,e,e,e,e,i,1i,1q,30,3wi,10,29,16,c,p,k,11,1x,bo,1q,s,g,o,c,k,0,6
2a,s,q,m,s,w,2o,2o,4a,3xy,1j,3t,h,0,8,x,8,c,by,18,s,c,4,4,e,g,k
2c,s,q,o,u,y,2m,2m,4q,3wc,1j,y,z,2d,1f,e,2o,5l,cy,q,6,8,c,a,o,4,q
2c,u,o,q,s,y,2k,2m,4g,3xm,2x,15,u,f,r,8,18,2v,co,u,i,2,4,8,u,8,q
1e,a,8,8,8,a,o,o,14,3yq,2g,1e,y,6,14,3e,3s,2e,a4,10,s,q,12,10,1o,y,1g
24,o,i,m,m,s,22,24,3a,3se,2z,18,z,c,4,22,s,1y,bk,18,s,k,a,c,m,o,s
22,m,i,k,m,s,22,24,36,3tq,23,u,v,5,p,0,16,1u,bg,1e,m,s,a,a,m,i,s
1c,a,8,8,8,a,m,o,y,3za,1u,1k,14,o,m,3e,44,4s,a0,y,u,q,14,10,1u,w,1k
2a,s,o,o,q,y,2k,2k,4i,3w2,3f,1q,h,x,t,0,2e,4t,ci,14,8,a,a,a,o,4,q
28,q,m,m,o,u,2c,28,3k,3sm,35,u,d,c,9,0,20,1e,bs,14,w,e,8,a,q,k,s
2e,s,o,q,s,y,2k,2m,4i,3va,19,9,e,f,h,2,1o,3z,cu,o,e,4,8,8,u,6,q
28,q,m,m,o,u,2a,2a,3i,3sg,37,m,5,k,0,4,1c,24,bs,14,u,e,8,c,q,m,u
26,s,m,o,q,w,2i,2i,4e,3xq,5t,28,f,2,19,9,20,3x,c8,1g,a,8,6,c,o,4,q
24,q,k,m,o,s,26,28,3g,3ru,2z,1a,x,y,g,10,a,1s,bq,16,s,i,6,a,o,o,s
26,q,m,o,q,u,2k,2m,42,3y0,1n,2p,1p,a,10,2f,m,m,bq,1c,w,e,2,6,e,e,k
22,o,k,k,o,s,22,24,38,3r4,2l,1g,v,c,0,2w,1u,1w,bk,1c,m,k,a,a,i,k,w
2a,q,q,o,q,w,2m,2o,48,3xw,1n,3h,p,b,y,1n,k,6,bw,18,u,c,4,6,e,g,k
24,q,m,o,o,u,2i,2i,3y,3zu,63,x,15,2a,2,7,f,12,bw,1k,k,a,1,6,i,k,e
1y,k,g,i,k,o,1y,22,3c,3ui,1d,1,g,p,3,15,2,m,bm,1g,o,w,a,8,k,8,e
1y,i,g,g,i,o,1w,1y,36,3uq,l,n,m,r,1,15,d,y,bm,1e,q,u,e,8,k,c,e
26,o,m,o,o,u,2g,2i,42,3w4,11,17,1z,g,b,z,a,5,bq,16,10,g,6,8,g,a,o
1e,a,8,8,a,a,q,q,12,3zk,10,1k,18,8,14,34,2w,4g,9y,14,s,q,y,y,1o,w,1a
28,q,m,o,o,u,2a,2a,3m,3tg,2p,k,r,n,0,q,1s,c,bs,12,w,g,a,c,m,k,u
1w,i,g,g,k,m,1u,1u,32,3tu,6,19,w,z,5,g,10,3,bi,1a,s,s,g,a,i,g,o
2a,u,o,o,s,w,2o,2o,48,400,4h,3f,g,3,g,l,0,s,c6,14,q,8,2,4,g,k,i
1c,c,8,6,8,a,o,o,10,3ze,1k,1m,16,m,q,3c,3q,4o,9y,10,u,q,12,10,1u,w,1g
22,k,i,k,m,s,2a,2a,3o,3v0,m,q,1n,9,1e,1b,z,1c,by,1q,m,g,0,1,a,m,8
2a,u,o,o,q,y,2m,2o,46,414,7b,1p,8,o,3,c,9,y,c8,16,o,6,0,4,g,o,e
20,k,i,i,m,o,1y,1w,34,3sg,1j,k,0,l,0,18,1u,10,be,18,m,u,e,c,o,k,q
y,8,8,6,6,8,i,k,s,3zo,13,2e,0,10,10,6,28,4w,8q,1e,e,s,s,s,2m,18,y
y,8,8,6,6,6,k,i,q,3zm,19,1g,1c,w,w,2,2k,4w,8q,12,s,o,s,s,2q,16,12
u,8,4,8,6,6,g,g,m,402,1f,1r,4a,a,8,k,1g,6q,8o,i,1k,e,w,w,2q,1a,1a
10,8,8,6,6,8,k,k,u,3ze,17,2g,b,16,14,g,1y,4m,8s,1g,e,u,s,u,2c,18,w
u,8,6,6,6,6,g,g,m,416,1l,1l,3u,8,a,m,1g,6y,8o,i,1e,e,u,y,2s,18,1c
y,8,8,6,6,8,i,k,s,3zg,1f,1u,y,10,10,a,2a,4u,8s,16,m,s,q,s,2o,14,12
y,a,6,8,6,8,k,i,u,3za,1f,2c,6,16,14,g,22,4o,8s,1e,g,u,q,s,2i,16,y
u,8,6,6,6,8,e,i,k,3zc,1b,1r,4k,g,m,s,1q,6a,8s,g,1i,g,u,u,2u,18,1c
w,6,8,6,6,6,g,i,m,40u,11,19,3w,c,a,8,1u,6m,8o,i,1e,e,u,w,2w,16,1a
10,8,8,8,6,8,k,k,w,3ys,1d,28,0,1e,18,i,1w,4g,8s,1k,g,u,q,12,22,14,y
10,8,8,6,6,8,k,k,u,408,r,1s,5,14,12,m,1y,4m,8q,1c,i,u,s,u,2c,18,y
y,8,8,6,6,8,i,i,s,40q,v,4,26,y,w,8,2s,4u,8s,i,1a,m,u,q,2o,14,16
y,8,6,6,6,8,g,i,o,41i,p,p,3e,o,i,0,2w,5q,8s,k,1a,e,u,u,2u,14,18
w,6,8,6,6,6,g,g,m,41s,15,15,3u,a,a,a,1m,74,8q,g,1e,a,w,w,2u,1a,1a
u,8,6,6,6,6,g,g,m,3zs,1h,1t,4i,a,e,m,1m,6i,8q,i,1i,g,u,w,2s,18,1c
u,8,4,8,6,6,g,g,k,400,1l,1v,4i,a,c,i,1i,6q,8q,i,1k,e,u,w,2s,18,1c
u,8,4,8,6,6,g,g,m,400,1b,1r,48,a,a,i,1g,6q,8o,i,1i,g,w,w,2q,18,1a
u,8,6,6,6,8,g,g,m,410,1b,1f,3w,a,a,e,1i,6u,8o,i,1e,c,w,w,2u,18,1c
w,8,6,6,6,8,g,g,o,3zy,11,17,40,g,e,4,2a,60,8o,i,1g,g,s,w,2u,16,1c
w,8,6,8,6,6,g,i,o,3yw,15,11,42,12,w,8,2s,5i,8s,o,1a,i,s,s,30,12,18
y,a,6,8,6,6,k,k,s,3z2,1h,28,i,18,14,g,28,4m,8s,1c,k,s,q,u,2i,14,10
w,8,6,6,6,6,g,i,m,40i,z,19,3w,e,c,8,20,6c,8o,i,1e,e,u,w,2w,14,1c
w,8,6,6,6,8,g,i,m,3zw,13,15,40,i,e,4,2e,5u,8q,g,1g,g,s,w,2w,14,1a
w,6,6,8,6,6,g,g,m,3za,19,1n,4i,m,o,o,1w,64,8s,g,1i,g,s,w,2w,16,1a
10,8,8,6,6,8,k,k,u,3yi,1f,2i,2,1g,1c,m,2a,4a,8u,1k,g,s,o,y,2a,12,10
w,8,6,6,6,6,g,i,m,3z6,17,1j,4g,s,q,i,26,5y,8s,g,1i,g,u,s,30,14,1a
y,8,8,6,6,8,i,i,s,40c,t,o,1k,w,u,8,2k,4w,8q,u,10,o,s,q,2o,16,14
u,6,6,8,6,6,g,g,m,40u,1v,1v,3u,i,k,14,1m,6y,8m,k,1g,e,w,y,2q,18,1a
y,8,6,8,6,6,i,i,q,3z8,1h,k,2q,w,y,0,2u,4y,8s,u,12,m,q,s,2u,12,16
10,a,6,8,6,8,m,k,u,400,r,1k,3,14,12,m,1o,4g,8s,1e,i,w,q,y,24,18,w
y,8,6,6,6,8,i,i,o,3zg,1d,9,3i,q,s,3,2y,50,8s,k,1c,k,s,s,2u,12,16
w,6,8,6,6,6,g,i,m,406,11,1b,40,e,c,a,1u,6g,8o,i,1e,g,u,w,2u,16,1c
u,8,6,6,6,6,g,g,m,3yu,1b,1t,4m,k,q,18,1s,64,8s,i,1i,i,u,s,2y,16,1c
y,8,6,8,6,6,i,i,q,3yu,1j,1o,1q,12,16,6,2u,4y,8s,18,o,o,q,q,2u,12,16
w,6,6,6,6,8,g,g,k,41y,1b,17,3s,a,8,c,1i,78,8q,g,1e,a,u,y,2u,1a,1a
10,8,8,8,6,6,m,k,u,3yq,1f,2g,5,1g,18,m,20,48,8u,1i,g,u,q,14,20,14,10
y,a,6,8,6,6,k,k,s,3zw,t,1i,i,10,10,a,2a,4o,8q,18,m,s,s,u,2g,16,12
w,8,6,6,6,8,g,i,m,3zk,19,19,4c,o,k,6,2i,5o,8s,e,1i,g,u,s,2y,14,18
w,6,8,6,6,6,g,i,m,3ze,17,1j,4g,o,k,i,22,62,8s,g,1i,e,u,u,2y,14,1a
y,a,6,8,6,6,k,k,s,3zw,v,1m,e,10,10,c,28,4m,8q,18,m,s,s,u,2g,16,12
u,8,6,6,6,6,g,g,m,3zs,1h,1v,4i,c,e,k,1m,6k,8q,i,1k,e,u,w,2s,18,1c
u,8,6,6,6,6,g,g,m,428,1h,1b,3s,8,6,e,1k,7e,8q,i,1c,c,s,y,2s,1a,1a
u,8,6,6,6,8,e,i,m,40q,1d,1j,3y,a,a,g,1i,6w,8o,i,1e,e,u,y,2s,18,1e
u,8,6,6,6,8,g,g,m,40s,1b,1h,3y,a,a,g,1i,6u,8o,i,1e,e,u,w,2u,18,1c
w,8,8,6,6,8,g,i,o,3yq,19,8,2w,18,10,8,2w,58,8u,10,w,k,s,q,2y,12,16
w,8,6,6,6,6,g,i,m,3zw,13,19,42,g,e,a,22,64,8o,i,1g,g,s,w,2u,18,1a
w,6,8,6,6,6,g,g,m,3yy,17,1l,4i,o,w,u,20,60,8s,g,1k,g,s,u,2y,14,1c
u,8,6,6,6,6,g,g,m,41i,1h,1h,3s,a,6,g,1i,70,8o,i,1e,c,u,y,2s,1a,1c
w,8,6,6,6,6,g,i,m,414,z,17,3u,c,c,4,1w,6o,8q,g,1e,c,u,w,2y,16,1a
u,8,6,6,6,6,g,i,k,3zw,1b,1p,4a,c,c,o,1k,6i,8q,g,1i,e,w,w,2s,18,1c
w,8,6,6,6,6,i,g,m,40u,z,19,3w,e,a,4,20,6i,8q,g,1e,e,u,w,2w,16,1a
u,8,6,6,6,8,e,i,k,41w,1b,1b,3u,8,8,c,1i,78,8q,g,1e,a,w,w,2u,1a,1a
w,8,6,8,6,6,g,g,o,42a,p,z,3s,c,a,4,20,6y,8s,g,1e,a,w,u,2y,16,16
w,8,8,6,6,6,i,i,o,3z6,1d,b,3m,u,w,0,2u,5a,8s,o,18,k,s,s,2w,12,18
u,8,6,6,6,6,g,g,m,428,1d,19,3s,8,8,c,1k,7c,8q,i,1c,a,u,y,2s,1a,1a
u,8,6,6,6,8,g,g,m,40s,19,1h,3y,a,a,g,1i,6u,8o,i,1e,e,u,w,2u,18,1c
y,8,6,8,6,6,g,i,o,42c,l,n,3e,g,c,6,26,66,8s,c,1g,i,u,s,2q,18,14
w,8,6,8,6,6,g,g,m,42g,r,z,3o,c,a,4,20,6q,8s,e,1e,e,u,u,2w,16,16
w,8,8,6,6,6,g,i,m,42g,r,x,3g,c,c,6,22,6g,8q,g,1e,g,u,s,2u,14,16
10,8,8,6,8,6,m,k,u,3zm,v,20,7,14,12,e,20,4g,8s,1g,e,u,s,y,28,16,y
10,a,6,8,6,8,m,k,w,3zm,p,1m,5,16,14,g,1s,4a,8s,1e,i,w,q,14,20,16,w
y,8,6,6,6,8,i,i,o,404,z,7,30,m,o,7,2y,56,8q,k,1a,k,s,s,2u,14,18
u,8,6,6,6,6,g,g,m,402,1f,1r,4a,a,a,m,1k,6m,8q,g,1i,g,u,w,2s,1a,1a
w,6,6,8,6,6,g,g,m,3yy,17,1n,4i,o,w,w,1w,62,8s,g,1k,g,s,u,2y,16,1a
y,8,8,6,6,6,k,i,q,3yu,1l,2e,10,14,16,6,2s,4s,8s,1g,e,q,q,s,2s,10,16
w,8,6,6,6,8,g,g,o,3zc,17,1h,4g,s,m,c,2a,5w,8s,g,1i,g,s,u,2y,14,1a
w,8,6,6,6,8,g,g,m,42g,r,11,3o,c,a,6,1y,6u,8s,g,1c,e,u,u,2w,16,16
10,8,6,6,6,8,i,i,o,42c,h,f,34,i,e,m,22,5q,8s,e,1c,o,u,q,2k,1a,12
w,8,6,6,6,8,g,i,m,3zm,17,1b,4a,m,k,8,2e,5q,8q,g,1i,g,s,u,2y,14,1a
w,8,6,6,6,6,g,g,m,420,z,15,3u,a,a,8,1q,76,8s,g,1c,a,u,y,2w,18,1a
w,6,6,6,6,8,e,i,k,42i,17,11,3o,a,a,e,1s,76,8q,g,1e,c,u,w,2s,18,18
w,8,6,6,8,6,g,i,m,41i,r,11,3u,e,c,1,2a,6o,8s,e,1e,e,u,u,2y,16,18
w,6,8,6,6,6,g,g,o,3z6,17,1n,4i,o,q,o,20,62,8s,g,1i,g,u,u,2w,16,1a
10,a,8,6,6,8,m,m,w,3yy,1b,1w,0,1c,18,g,1s,4i,8s,1i,k,u,q,10,20,16,w
w,8,6,6,8,6,g,i,o,40a,x,11,3s,g,g,1,2k,5u,8o,g,1g,g,u,u,2w,14,1a
u,8,6,6,6,6,g,g,m,418,1j,1j,3u,8,a,k,1g,6y,8o,i,1e,c,w,w,2u,18,1c
10,a,8,6,8,6,o,k,y,3z4,15,1i,4,1a,16,e,1u,4c,8s,1i,k,w,o,12,1y,16,u
10,8,8,6,6,8,k,k,s,40g,p,14,k,12,12,i,26,4m,8s,14,o,u,q,u,2e,18,10
w,8,8,6,6,6,i,g,o,410,v,x,3m,i,g,3,2k,60,8q,g,1e,g,s,u,2y,14,18
u,8,6,8,6,6,g,g,m,3yw,17,1p,4i,o,w,10,1u,64,8s,g,1k,g,u,s,30,14,1c
w,8,8,6,6,6,i,i,o,3zs,17,f,3i,k,m,7,2y,58,8q,k,1c,i,s,u,2w,12,18
y,8,6,6,6,8,g,g,o,42e,p,r,3g,c,c,4,28,66,8q,e,1g,i,u,q,2s,16,16
u,8,6,6,6,6,g,g,m,3yu,1b,1t,4m,k,m,18,1s,64,8s,i,1k,g,u,u,2w,16,1c
w,8,6,6,6,8,g,g,o,3yy,15,1h,4g,y,u,e,2k,5q,8s,m,1c,i,s,s,32,10,1a
10,8,8,6,6,8,k,k,u,3zq,p,1k,6,12,12,e,22,4e,8q,1c,k,u,q,10,28,16,10
w,6,8,6,6,6,g,g,o,3zm,19,1n,4g,k,i,k,1u,68,8s,g,1g,g,u,u,2w,16,1c
10,a,6,8,6,8,m,k,w,3z0,1d,26,3,1c,16,i,1u,4i,8s,1i,i,u,q,y,24,16,w
u,8,6,6,8,6,g,g,m,3z0,19,1p,4k,k,u,y,1s,66,8s,g,1k,g,s,u,2y,16,1a
u,8,6,6,6,8,g,g,m,3zm,1b,1p,4g,e,i,q,1o,6c,8q,i,1i,e,u,w,2u,18,1a
y,8,8,6,6,6,k,i,q,40s,t,7,2k,w,s,4,2w,4y,8s,g,1c,m,s,s,2q,14,16
y,a,8,6,6,8,k,k,u,3ym,1h,2k,0,1g,1a,k,28,4c,8u,1i,g,s,q,10,28,12,10
w,6,6,6,6,8,g,g,m,3zu,13,1f,44,e,c,i,1m,6k,8o,i,1g,e,w,u,2u,18,1c
w,8,6,8,6,6,g,i,m,41s,n,13,3u,e,c,0,28,6s,8s,e,1e,c,w,u,2y,16,18
10,8,8,8,6,8,k,k,w,3ze,11,1y,5,18,14,e,1y,4c,8s,1g,g,w,q,10,24,16,w
w,8,6,6,6,6,i,g,o,400,z,15,3y,g,e,2,28,64,8o,i,1g,e,u,w,2u,16,1c
10,8,8,6,8,6,m,k,u,3ze,13,22,5,16,14,e,20,4e,8s,1g,g,u,q,10,26,16,y
w,6,6,8,6,6,g,g,m,3zs,1b,1n,4e,g,g,o,1o,6c,8q,i,1g,g,u,u,2u,18,1c
10,8,6,6,8,6,i,i,o,42c,f,f,34,g,e,q,24,5o,8q,m,18,m,u,q,2i,1c,10
y,8,6,8,6,6,i,k,q,3zk,19,q,28,s,u,1,2s,4w,8q,w,10,m,s,s,2s,12,16
u,8,6,6,6,8,e,g,m,3ys,1b,1r,4m,m,s,1a,1s,64,8s,i,1i,i,u,s,2y,16,1c
w,8,6,8,6,6,g,g,m,42g,r,z,3o,c,a,4,20,6s,8s,g,1c,e,u,u,2w,16,16
w,8,6,6,6,8,g,g,o,40q,x,17,3w,e,c,1,2c,68,8q,g,1e,e,u,w,2w,14,1c
y,8,8,6,6,8,i,i,q,3yq,1j,2c,y,16,18,a,2o,4u,8s,1g,g,q,q,s,2q,12,14
u,8,6,6,6,6,g,g,m,3zw,1h,1t,4g,a,c,m,1k,6k,8q,i,1i,e,w,w,2s,18,1c
u,8,6,6,6,8,e,g,m,3zi,1b,1r,4i,c,k,q,1m,6e,8s,g,1i,e,w,u,2u,18,1c
y,8,8,6,6,8,i,k,q,3zi,1d,1o,14,y,10,6,2e,4w,8s,14,o,q,s,q,2q,14,12
y,8,6,6,6,6,i,g,m,42e,p,t,3i,c,c,0,28,6c,8s,e,1e,g,u,s,2u,16,16
w,8,6,8,6,6,g,g,o,42c,p,z,3q,c,a,4,20,6w,8s,g,1e,a,w,u,2y,16,16
w,6,6,8,6,6,g,g,m,412,17,1d,3w,c,8,e,1k,6u,8o,i,1e,c,u,y,2u,18,1c
w,8,6,6,6,6,g,i,m,420,t,15,3w,a,a,6,1u,76,8s,g,1c,c,u,u,30,16,18
w,8,6,8,6,6,i,g,o,40a,v,z,3q,i,g,3,2q,5m,8q,e,1g,g,u,u,2w,12,1c
u,8,6,6,6,8,e,i,k,41g,1f,1d,3s,a,8,g,1g,70,8o,i,1e,c,u,y,2s,1a,1c
y,a,6,8,6,6,k,k,s,3zu,v,1o,e,10,10,a,2a,4m,8q,18,m,s,s,u,2g,16,12
u,8,6,6,6,8,e,i,k,41u,1d,1b,3s,8,8,c,1i,78,8q,g,1e,a,w,w,2u,1a,1a
y,a,6,8,6,8,k,i,u,3zu,t,1m,a,12,10,c,26,4k,8q,1a,k,u,q,y,2c,16,10
10,a,8,6,8,6,o,k,w,3zk,t,1c,4,14,14,e,1q,4a,8s,1g,k,u,q,16,1w,16,w
y,a,6,8,6,6,k,k,s,408,t,1g,e,12,10,i,26,4q,8q,16,o,s,s,s,2g,18,10
u,6,6,6,8,6,g,g,k,402,1d,1r,48,a,a,i,1g,6q,8o,i,1k,g,u,w,2s,16,1a
w,8,6,8,6,6,g,i,m,3yo,15,1d,4e,14,10,g,2s,5k,8u,y,10,i,s,s,30,12,1a
w,8,8,6,6,6,g,i,o,3yi,15,r,3y,16,12,g,30,5g,8u,16,s,i,s,s,30,12,18
w,8,6,6,6,6,i,g,m,416,z,17,3u,e,a,2,1w,6s,8q,g,1e,c,u,w,2y,16,1a
u,8,6,6,6,6,g,g,m,428,1d,19,3s,8,8,c,1m,7c,8q,i,1c,a,u,y,2s,1a,1a
w,6,8,6,6,6,g,i,m,3zy,19,1j,48,g,g,k,1q,6a,8q,g,1g,g,u,w,2u,16,1c
u,8,6,6,6,6,g,g,m,428,1f,1b,3s,8,8,c,1m,7c,8q,i,1c,a,u,y,2s,1a,1a
10,a,6,8,6,6,k,k,q,3xu,15,2u,2,1a,1o,1w,2g,5w,8w,1m,m,q,o,10,24,14,10
w,6,6,6,6,8,g,g,m,40o,19,1f,3y,a,c,g,1i,6s,8o,i,1e,e,u,w,2u,18,1c
w,8,8,6,6,8,g,i,q,3ze,1d,j,3w,q,s,7,34,50,8s,k,1c,i,s,s,2y,10,18
u,6,6,6,6,8,g,g,k,406,1l,1x,4i,8,a,g,1g,6u,8q,i,1k,e,w,w,2q,18,1a
10,8,6,6,8,6,i,i,o,42a,d,f,34,g,g,q,22,5o,8q,i,1c,m,u,q,2i,1c,10
y,8,8,6,6,8,i,k,q,40e,v,o,1k,w,u,a,2i,4y,8q,u,10,o,s,q,2o,16,14
w,8,6,8,6,6,g,g,m,42g,r,z,3o,c,a,4,20,6q,8s,e,1e,e,u,u,2w,16,16
y,8,6,8,6,6,i,i,q,3zo,19,o,2a,q,s,3,2u,4y,8q,u,10,o,s,q,2u,14,14
w,8,6,6,6,8,g,i,m,400,v,15,3y,g,e,1,2c,62,8o,i,1g,e,u,u,2w,16,1a
w,8,6,6,6,8,g,g,o,41g,x,11,3u,c,c,2,22,6q,8q,g,1e,c,u,w,2y,16,18
y,8,8,6,6,6,i,i,o,42c,j,j,36,g,e,g,26,5s,8q,i,1a,m,u,q,2m,1a,12
w,8,6,6,6,6,g,g,o,3yi,13,1j,4i,w,14,14,28,5w,8u,m,1c,i,s,u,30,12,1c
u,8,6,6,6,6,g,g,m,400,1b,1r,48,a,a,i,1i,6q,8o,i,1i,g,w,w,2q,18,1a
u,8,6,6,6,6,g,g,m,422,1l,1f,3q,8,8,g,1i,7c,8q,g,1e,c,u,y,2q,1a,1a
10,8,8,6,8,6,m,i,w,40g,l,10,g,14,10,k,24,4e,8s,16,o,u,q,w,2a,18,y
u,8,4,8,6,6,g,g,k,402,1d,1t,4a,a,a,i,1g,6q,8o,i,1k,g,u,w,2q,18,1a
u,8,6,6,6,6,g,g,m,41o,1r,1n,3q,8,8,w,1g,70,8o,i,1e,e,u,y,2q,18,1a
w,8,8,6,6,6,i,g,o,40k,p,13,3u,g,e,f,34,5k,8q,e,1g,g,s,u,30,10,1a
u,8,6,6,6,8,g,g,m,3z2,19,1p,4k,k,q,w,1u,64,8s,g,1k,g,s,u,2y,16,1a
y,8,6,8,6,6,i,g,o,42c,l,n,3e,g,c,6,26,66,8s,c,1g,i,u,s,2q,18,14
w,8,8,6,6,6,i,i,o,3z4,1b,d,3k,y,u,4,2q,5e,8s,o,18,k,s,s,2y,10,1a
w,6,6,8,6,6,g,g,m,3zo,19,1n,4e,i,g,m,1s,6a,8q,i,1g,g,u,u,2w,16,1c
w,8,8,6,6,6,i,i,o,40k,r,11,3q,i,g,b,30,5k,8q,e,1g,g,u,u,2w,12,1a
1q,e,a,c,c,c,12,14,1s,3x6,22,15,k,18,o,5,s,1,bg,w,1i,g,m,q,s,w,u
1i,e,c,c,e,e,1c,1e,2a,3tq,28,12,1v,s,o,f,j,q,b0,u,m,y,q,s,q,k,e
1q,e,c,c,c,g,18,1a,22,3w8,2c,1h,k,12,5,j,0,s,be,u,1i,e,q,m,q,o,o
1u,g,e,e,g,k,1o,1o,2o,3uu,1m,27,12,1,r,b,c,m,bg,12,14,o,m,c,m,e,k
1k,e,a,c,c,g,16,18,20,3uw,24,w,1j,10,y,4,b,1e,ay,10,q,12,m,q,u,o,e
1u,c,c,c,a,e,14,12,1q,3xs,1q,1p,1a,14,b,k,1m,g,bo,1k,12,g,o,m,s,w,y
1w,g,e,e,i,k,1o,1s,2u,3v2,14,1x,12,f,l,f,n,o,bk,18,w,o,m,a,m,g,i
1u,c,c,a,a,c,16,18,22,3xu,12,27,2o,1o,1d,1k,q,27,c0,1s,10,e,o,u,i,e,i
1u,i,g,i,i,o,1w,1w,2y,3s4,18,25,14,t,0,c,1w,24,ba,14,q,u,i,a,m,c,o
1o,g,c,g,e,i,1k,1k,2i,3tk,2e,1j,1,w,n,b,6,1y,b2,10,12,o,s,e,o,i,g
1k,e,a,c,e,e,16,18,1y,3v6,20,w,1l,14,14,2,h,1k,ay,12,o,16,k,q,s,q,e
1q,e,a,e,c,e,18,1a,22,3w0,2e,17,8,16,4,j,9,1c,bc,w,1g,g,o,m,q,o,m
1u,c,c,c,a,e,12,16,24,3xy,18,29,12,1i,w,2,1w,2p,bq,1o,14,c,i,o,1e,8,k
1o,g,e,e,g,k,1k,1o,2m,3t2,2e,1n,5,q,p,b,e,22,b0,10,10,k,w,e,m,g,g
1s,g,c,e,c,i,1e,1i,2g,3w2,24,2b,16,q,1d,7,j,l,bi,y,1e,e,q,k,o,i,m
1s,g,g,g,i,m,1s,1s,2u,3si,26,2r,14,b,f,1,1e,26,b8,10,w,s,k,c,o,c,k
1k,e,8,c,c,c,14,14,1s,3vu,1w,16,1n,12,1a,g,d,1c,aw,14,k,1e,e,s,u,q,i
1u,i,i,g,k,m,1w,1u,2y,3sk,u,1t,16,11,2,g,1u,1u,bc,18,m,y,e,a,o,c,o
1m,e,c,e,e,g,1e,1g,2e,3tw,28,3,17,16,0,d,b,1k,b0,y,w,s,s,m,o,k,e
1y,c,c,a,c,c,10,y,1g,3z0,s,j,1a,14,1,3s,2s,2w,c4,22,m,i,o,i,18,y,10
1s,g,e,e,e,k,1k,1k,2k,3v4,20,2b,10,e,z,h,8,12,bg,y,18,k,q,e,m,g,k
1s,i,i,g,k,o,1w,1w,2y,3ru,1m,2h,14,t,4,1,24,2c,b8,10,w,q,k,a,m,a,o
1u,c,a,a,a,c,w,w,1m,3ym,14,1v,1o,1s,1m,3g,20,z,c0,1w,y,a,i,w,1i,m,u
1o,e,c,e,c,g,1a,1c,26,3v4,2i,v,5,12,6,f,f,20,b8,w,18,o,o,m,q,k,e
1s,e,a,c,c,e,16,14,20,3x4,20,1r,10,14,d,c,14,15,bk,16,1a,e,q,o,q,s,u
1u,g,g,e,i,k,1o,1s,2s,3uy,16,1z,14,d,n,d,5,e,bi,18,y,o,k,c,k,g,k
1u,c,c,c,a,c,10,y,1k,3ye,1a,1p,18,12,6,2u,26,2q,bu,1s,y,e,k,m,18,w,u
1k,e,a,c,c,e,16,18,1y,3v4,22,y,1j,10,12,6,b,1g,ay,10,q,14,k,q,u,q,e
1u,c,c,c,c,c,16,16,26,3xq,1e,2b,14,1a,c,2,10,11,bq,1m,14,c,k,m,1a,c,g
1u,c,c,c,a,c,y,y,1g,3yk,18,1n,16,16,m,3i,2a,2m,bw,1w,w,c,i,o,1e,u,u
1u,e,e,e,e,i,1i,1k,2k,3vu,1w,2b,14,g,1b,9,x,3,bi,14,16,i,q,g,m,i,k
1u,e,c,e,c,i,1a,1g,2m,3wu,1s,2j,18,w,1b,g,11,25,bm,1i,10,g,o,i,q,k,8
1k,e,a,a,c,e,12,14,1u,3vw,1y,14,1p,14,1a,g,h,1a,aw,14,m,1e,e,q,u,q,k
1o,e,c,e,c,i,1c,1g,2a,3uc,2e,p,j,16,3,d,5,1u,b2,10,10,u,o,k,q,k,e
1u,c,e,c,c,g,1a,1c,2c,3ws,20,2b,18,12,1h,m,h,1n,bk,18,18,e,q,m,o,o,k
1m,e,c,e,c,g,1e,1g,2a,3u2,28,0,19,16,6,h,9,1g,b0,y,y,q,s,m,q,k,e
1q,e,a,c,a,e,12,14,1u,3x6,22,x,8,1a,s,7,0,4,be,w,1i,i,k,q,q,w,u
1u,g,g,e,g,m,1o,1q,2o,3tu,1q,2b,16,9,l,b,1c,1u,be,10,10,s,k,c,m,e,m
1u,c,c,a,c,c,y,10,1o,3yc,16,1x,14,1c,12,2s,18,1a,bu,1s,10,c,g,q,1g,o,m
1i,c,a,a,c,c,12,14,1q,3uo,2m,1e,z,8,1a,q,1,1o,b0,u,m,14,o,o,10,q,i
1q,g,e,e,e,i,1i,1k,2g,3u6,2i,23,m,u,z,d,c,2e,ba,w,14,q,q,g,o,g,g
1o,e,a,a,c,c,14,14,1u,3wi,2c,3,t,1c,14,7,h,8,b8,y,1e,o,g,q,q,q,u
1u,e,e,e,c,i,1c,1m,2s,3wy,16,2j,16,18,15,8,h,1t,bo,1s,u,e,m,i,q,3,c
1u,c,c,c,a,g,12,16,20,3xm,1k,1z,14,10,b,1g,6,n,bo,1i,14,g,k,m,y,w,i
1u,g,e,g,g,k,1o,1q,2o,3uc,1s,2f,16,7,p,f,14,1c,bg,10,14,o,m,c,m,c,m
1u,c,c,c,c,e,14,14,1s,3xq,1s,1p,1c,14,h,i,1a,k,bo,1m,10,e,q,m,s,u,w
1o,c,a,c,c,e,14,18,1w,3vs,2e,a,11,16,16,3,l,12,b4,10,14,u,i,q,s,o,k
1u,e,c,c,c,g,18,1g,2e,3x8,1i,2h,16,y,7,f,11,j,bo,1m,10,e,m,i,12,c,6
1q,c,c,c,a,e,14,14,1u,3wq,28,v,4,18,s,d,8,s,bc,w,1i,i,k,q,q,u,u
1w,c,c,c,a,c,10,y,1e,3yu,10,z,1a,12,4,3c,2e,3g,c2,1y,s,e,m,m,16,y,10
1u,c,e,c,a,e,1a,1g,2c,3xm,14,2z,20,28,f,1a,1d,1r,bw,1u,u,c,o,m,i,2,q
1q,g,e,e,e,i,1i,1k,2i,3ty,2i,21,k,s,t,d,g,26,b8,w,14,q,q,g,o,g,g
1o,e,e,c,e,i,1c,1g,2a,3ue,2i,13,3,14,d,d,5,2a,b6,y,12,s,o,k,q,i,e
1u,c,c,c,a,e,12,10,1u,3xs,1m,1r,14,14,9,1e,1u,d,bo,1i,16,g,k,m,w,10,s
1w,c,c,a,c,c,y,y,1e,3yy,10,11,1c,10,a,3o,2o,3s,c2,20,q,g,m,k,1a,10,y
1i,e,a,c,c,e,14,18,1y,3uk,2c,1e,1r,u,16,g,b,18,b2,u,k,14,o,s,w,o,e
1m,e,a,c,c,e,16,18,1y,3vg,2c,e,15,16,14,1,n,1i,b2,10,y,10,i,q,s,o,g
1m,e,a,c,c,e,16,16,1y,3vi,28,k,1b,18,12,0,l,1i,b2,10,u,14,g,s,s,o,g
1u,e,c,a,a,e,y,10,1i,3yk,18,1h,1a,12,4,30,22,2c,bw,1w,w,e,k,m,16,w,10
1q,g,e,e,g,k,1k,1m,2k,3ti,2k,2d,s,k,x,7,q,1y,b8,w,14,q,q,e,m,g,i
1u,c,e,e,c,i,1a,1g,2e,3wg,22,2d,18,y,1p,8,13,l,bk,10,1c,g,q,m,m,k,m
1u,g,e,c,e,g,1i,1q,2s,3wy,s,2l,1k,12,t,1g,1d,1t,bs,1s,u,c,o,g,a,1,k
1s,e,c,c,c,g,1a,1a,28,3wc,2a,1x,y,10,p,h,e,13,bg,w,1i,c,q,o,q,m,o
1i,e,a,c,c,e,16,16,20,3ua,2e,1e,1l,m,16,6,9,10,b2,s,m,12,q,q,w,o,e
1i,e,a,c,c,e,16,18,22,3u6,2e,1e,1l,k,14,0,9,u,b2,s,m,10,s,q,u,o,e
1s,e,c,c,c,e,14,16,28,3xa,1s,21,14,12,v,14,12,3n,bm,1g,14,g,m,m,q,y,k
1u,e,c,e,c,g,1c,1i,2k,3x6,1a,2j,14,18,l,l,j,x,bo,1q,y,c,m,i,y,2,a
1u,e,c,c,c,g,1a,1e,2k,3xg,16,2p,18,1u,9,1j,2q,3z,bq,1s,y,c,m,k,w,5,q
1u,c,c,a,a,c,14,14,20,3y4,12,2d,1u,22,1g,17,2k,2x,by,1u,w,a,m,s,16,a,k
1o,g,e,e,g,k,1k,1m,2k,3te,2g,1x,a,q,r,9,e,20,b4,y,12,q,q,e,o,g,g
1m,e,e,e,e,i,1i,1k,2i,3tg,26,d,19,14,9,l,3,1o,b0,y,u,o,w,k,m,i,g
1u,c,c,c,a,e,12,12,1q,3xy,1m,1r,1c,12,b,16,1o,o,bq,1o,10,g,m,m,w,w,w
1u,c,c,a,c,c,10,10,1s,3y4,1e,1v,14,14,a,2k,1o,g,bq,1o,14,e,i,m,1a,u,m
1o,e,a,c,a,c,14,14,1u,3wq,28,7,n,1a,16,b,f,8,ba,y,1e,m,g,s,o,u,u
1u,c,e,c,c,g,18,1a,2c,3ww,1y,29,16,12,1b,o,5,21,bk,1a,16,g,q,k,o,s,k
1m,g,c,e,e,i,1i,1k,2g,3tk,28,n,v,12,9,j,3,1o,b0,y,y,o,w,i,m,i,g
1s,g,g,e,i,m,1o,1s,2q,3sy,28,2n,14,3,n,1,18,1y,b8,10,y,s,m,c,o,c,k
1o,e,c,c,e,e,1a,1e,24,3v0,2k,p,b,14,a,f,h,22,b6,y,16,q,m,o,q,m,c
1m,e,c,e,c,g,1a,1c,26,3uq,2a,3,x,16,g,9,f,1w,b0,12,w,y,m,o,q,m,c
1m,e,a,e,c,e,16,1a,20,3va,2c,8,13,16,w,5,j,1q,b2,10,y,10,i,q,s,o,e
1u,c,c,a,a,a,u,w,1k,3yy,14,1l,1q,1s,1e,4e,32,2n,c4,20,s,c,k,u,1k,o,12
1g,e,a,c,e,e,1a,1e,28,3tg,2e,1e,1l,8,10,1,13,4,b0,q,m,12,i,y,s,k,g
1u,g,e,e,e,k,1i,1o,2s,3w0,1g,27,16,8,19,9,1z,4,bm,1a,10,k,o,e,m,g,c
1s,g,g,g,g,m,1o,1q,2q,3te,26,2r,18,5,p,b,1o,1q,bc,w,10,u,m,c,m,e,k
1q,g,e,e,g,i,1k,1m,2i,3ty,2g,29,q,m,v,d,o,26,ba,w,12,s,q,e,o,e,i
1q,c,c,c,c,e,18,18,20,3w2,2e,x,0,18,e,h,b,14,ba,w,1g,i,m,o,q,o,o
1w,c,c,c,a,e,12,12,1o,3y8,18,1b,1a,14,j,1q,1u,24,bu,1u,u,i,m,k,y,u,y
1u,c,a,c,8,c,w,y,1o,3yk,14,1v,1e,1u,1o,2m,2u,2j,by,1w,y,a,i,u,1k,m,q
1u,c,c,a,a,c,w,w,1i,3yo,16,1r,18,1c,1e,3e,2w,y,by,1w,w,c,i,o,1k,s,u
1u,e,a,c,a,c,y,y,1e,3yq,18,1j,1a,12,g,3m,2g,2o,by,1w,w,c,k,o,1a,y,10
1u,e,e,e,c,i,1e,1m,2s,3ws,1e,2j,18,o,z,i,1f,1h,bo,1m,y,c,q,e,q,6,4
1q,g,g,g,g,m,1q,1q,2s,3so,2i,2r,y,2,p,3,12,28,b6,y,10,q,o,c,m,e,i
1s,c,c,c,a,e,14,14,1w,3x2,22,1f,s,16,8,5,1a,1j,bg,w,1k,e,o,q,s,u,s
1u,c,a,c,a,a,y,w,1m,3ym,16,1t,18,1o,1k,2q,3k,1f,by,1w,y,a,i,q,1m,o,s
1y,k,g,i,m,o,1y,1w,34,3su,v,9,e,11,8,14,1q,q,bi,1e,i,w,a,a,k,g,s
1s,e,c,e,c,i,1e,1g,2c,3ve,2e,1x,o,u,n,n,0,1u,be,u,1e,i,q,i,o,i,i
1u,c,c,c,a,e,10,12,1o,3y0,1k,1r,1a,12,9,1k,1w,u,bq,1m,12,g,m,m,y,y,u
1s,c,c,a,c,c,14,14,1s,3xa,22,1b,s,16,i,1,s,9,bi,u,1m,e,o,o,u,u,w
1u,c,a,a,8,c,w,10,1s,3yk,12,19,2a,1o,1g,2e,w,55,c2,1y,y,c,o,10,14,m,q
1e,e,a,8,c,c,10,12,1q,3uo,2a,1g,1,p,1e,u,e,18,aq,y,m,y,w,o,14,m,i
1w,c,c,c,c,e,16,16,1u,3xu,1g,1j,1e,10,n,q,18,q,bq,1s,u,i,o,k,s,s,w
1u,g,g,e,i,m,1o,1q,2q,3tq,1m,29,16,d,j,9,1g,1u,be,10,10,s,k,c,m,e,m
1k,e,c,e,c,g,1e,1g,2c,3tw,24,o,1x,16,g,l,d,12,b2,u,q,w,s,o,q,k,e
1u,c,a,a,a,c,10,14,1w,3y8,10,25,2e,1w,e,20,c,37,c0,1s,10,a,o,10,w,g,o
1u,c,c,c,a,c,12,18,20,3y2,14,2b,16,20,1a,1e,19,t,bu,1u,y,a,k,o,1c,8,k
1w,e,c,c,a,e,12,12,1k,3yk,s,p,1a,10,d,2m,20,1y,c0,1w,q,k,m,i,10,u,12
1m,e,c,c,e,g,1e,1g,2a,3u0,28,2,1b,16,6,f,9,1e,b0,y,w,s,s,m,q,k,e
1s,e,e,e,e,k,1i,1m,2g,3ug,2c,2b,w,m,z,d,g,28,bc,w,16,o,q,g,m,g,g
1k,e,8,e,c,e,16,18,20,3um,28,18,1t,10,12,a,d,18,b2,u,o,10,o,u,s,q,e
1m,g,c,e,g,i,1k,1m,2k,3t6,28,n,13,10,f,l,0,22,b0,y,s,o,y,i,m,g,g
1u,c,c,c,c,e,16,1a,26,3xi,1i,2b,14,16,7,e,13,i,bo,1k,14,e,k,m,16,g,a
1u,e,e,c,c,i,1g,1m,2m,3x6,u,2x,22,18,d,1k,1l,1p,bu,1u,s,e,o,g,6,2,m
1u,c,a,c,a,a,12,12,1u,3ya,14,25,1e,1y,1o,u,24,29,bw,1w,w,a,k,q,1i,e,m
10,a,6,8,6,8,k,k,w,40o,n,10,g,14,10,o,20,4i,8s,1a,m,u,q,y,26,1a,w
18,c,a,8,a,e,w,12,1o,3xa,19,1m,12,b,j,1s,6,3,9y,12,u,m,u,m,1q,m,m
12,a,8,8,8,8,o,o,12,3zg,1l,w,10,y,q,k,y,4m,8s,1k,q,u,q,16,1m,16,q
14,a,8,6,8,8,q,o,14,40k,23,1i,y,y,g,q,y,3g,8u,1k,q,u,q,16,1o,12,s
12,c,8,8,6,a,q,o,14,3ys,1t,12,10,16,6,1g,1c,3y,8u,1m,s,u,o,16,1k,10,s
12,c,8,8,6,a,q,o,14,3yq,1l,u,y,16,4,1m,1a,3w,8u,1m,u,s,o,16,1k,10,s
12,c,6,8,6,8,o,m,10,416,25,1y,u,y,y,o,1m,3o,8u,1k,m,w,o,16,1s,12,y
y,8,6,6,6,6,i,g,o,41y,n,t,3o,i,c,2,2g,6e,8u,c,1g,c,w,s,2y,14,18
12,a,6,8,8,6,o,m,10,3zc,19,18,i,14,w,k,1i,48,8s,1i,o,u,q,16,1q,14,u
18,e,a,8,a,e,10,12,1u,3wy,11,1m,10,x,1,16,2,h,a0,14,s,m,w,k,1o,m,k
12,a,8,8,8,8,o,o,12,3yu,1j,10,s,16,k,10,1k,42,8u,1k,q,u,o,16,1m,14,s
18,c,a,8,a,c,y,10,1q,3xg,1d,1i,14,f,f,1m,e,7,9u,16,u,m,y,i,1q,o,i
16,c,a,8,8,c,w,w,1i,3yc,1h,1a,16,6,x,24,m,14,9i,1a,w,o,u,q,1q,o,q
16,e,8,a,8,c,w,y,1k,3y2,1d,1g,16,3,t,1w,m,e,9q,14,w,o,w,k,1s,o,o
10,8,6,6,6,8,i,i,o,41w,j,n,36,w,k,c,2y,52,8u,o,14,m,s,s,2o,12,1a
14,a,a,8,6,a,s,q,16,3ye,1b,k,10,12,7,28,w,3m,92,1k,u,s,m,14,1k,w,s
12,a,a,6,8,8,q,o,12,3ys,1h,u,u,16,g,16,1g,42,8u,1k,s,u,o,16,1m,10,s
14,a,8,8,6,a,q,o,12,40y,2n,24,10,u,i,u,q,38,8u,1m,q,u,q,14,1q,10,w
14,a,a,6,8,a,q,s,16,3yq,29,1c,1a,12,9,1w,14,3g,8y,1k,u,s,o,16,1i,y,s
16,e,8,a,8,e,w,y,1o,3xo,1d,18,16,d,j,20,c,8,9s,18,u,m,w,o,1m,o,m
10,a,8,6,8,8,m,m,w,3zs,x,16,a,14,12,e,1i,4e,8s,1g,k,w,q,14,1w,16,u
12,a,8,8,8,8,o,o,12,3zs,1h,s,y,10,m,m,y,4a,8s,1k,q,u,q,16,1o,14,q
y,8,6,6,6,6,i,g,o,42c,n,t,3o,c,c,3,28,6k,8s,e,1e,e,w,s,2w,18,14
14,c,8,8,8,8,s,s,18,3yq,27,1a,1c,y,f,22,w,38,92,1i,u,s,o,14,1k,w,s
10,8,8,6,6,6,k,i,s,41i,f,9,2c,12,u,i,2w,4o,8u,16,m,q,s,u,2g,14,16
12,a,8,6,8,6,o,m,10,3z0,19,16,g,1a,y,g,1u,46,8s,1i,o,w,o,16,1q,14,u
10,8,8,6,6,6,k,i,q,41o,f,j,2q,14,o,i,30,4m,8u,10,u,m,s,u,2i,14,18
12,c,8,6,8,8,q,o,14,3ys,1l,w,w,16,c,1c,1e,40,8u,1m,s,s,o,16,1m,10,s
14,a,8,8,6,8,q,o,12,40w,29,1s,y,w,i,w,y,36,8u,1m,o,u,q,16,1o,12,w
y,6,8,6,6,6,g,i,m,428,p,v,3s,c,c,0,24,6u,8u,e,1e,c,u,u,2y,16,16
12,c,8,8,8,8,s,q,14,3yu,2j,1i,1e,10,0,1k,16,3s,8w,1k,u,s,q,16,1i,10,s
y,8,6,6,6,6,i,g,o,42a,n,r,3o,c,c,0,26,6k,8s,e,1g,e,u,s,2w,18,14
14,c,8,8,6,a,s,s,16,3yy,21,18,18,10,f,1u,s,3c,92,1g,u,s,o,14,1m,w,s
16,c,8,8,8,c,u,u,1e,3z2,1r,1e,16,g,v,1u,q,1u,9a,1e,u,q,w,s,1q,q,q
10,8,6,6,8,6,i,k,q,41i,j,9,2i,12,q,e,30,4s,8u,y,u,m,s,u,2m,12,18
12,c,8,8,6,a,q,o,14,3zs,1n,u,10,10,2,16,w,3y,8s,1m,s,u,o,16,1m,12,s
12,a,8,8,6,8,o,o,10,3z6,1n,1a,q,14,s,o,1i,46,8s,1k,o,w,o,16,1o,14,u
14,c,8,8,8,c,s,s,1a,3z6,1r,y,18,s,z,2a,s,2q,94,1g,w,s,q,10,1m,s,s
1a,e,a,8,c,e,14,16,20,3wc,n,1k,k,1b,i,i,f,j,a2,16,s,o,w,k,1g,k,i
12,a,8,6,8,6,o,k,y,418,1b,1e,s,14,10,q,2a,3y,8u,1i,k,w,o,16,1u,14,y
14,e,8,8,8,c,u,u,1e,3y8,1r,16,18,m,v,2i,q,1u,9c,1g,u,q,q,w,1m,s,o
12,c,8,6,8,8,q,m,12,40k,1v,1c,y,y,k,q,12,3i,8u,1k,o,w,o,16,1q,12,s
y,8,8,6,6,8,i,i,q,418,n,3,2e,y,s,c,2w,4w,8s,10,u,m,s,q,2q,12,16
16,e,8,8,a,c,w,y,1k,3xq,1d,16,16,3,r,2g,8,s,9s,18,s,o,u,q,1m,o,o
12,c,8,6,8,8,q,o,12,400,1p,10,y,10,i,q,w,3y,8s,1k,q,w,o,18,1m,12,u
10,8,8,6,6,8,k,k,u,40u,n,12,k,14,12,o,24,4u,8s,1a,k,u,q,s,2e,1a,y
10,8,6,6,6,8,i,i,q,41u,h,p,34,y,k,e,2y,4y,8u,s,10,m,s,s,2o,12,1a
18,c,a,a,a,c,10,10,1s,3xa,17,1e,14,x,1,1a,8,d,9u,18,u,m,y,k,1m,m,k
14,c,8,8,8,c,s,s,1a,3ye,1d,k,16,u,11,36,o,2s,92,1o,u,q,o,12,1k,s,s
14,a,8,8,8,8,q,o,14,40y,2l,24,y,w,c,10,q,38,8u,1m,q,u,q,14,1q,10,w
12,a,8,8,6,8,q,m,10,3z6,1l,18,q,16,q,o,1g,48,8s,1k,o,w,o,16,1o,14,u
18,e,a,8,a,e,y,12,1s,3x2,x,1k,12,x,3,18,4,h,a0,12,s,o,w,i,1q,m,m
16,c,a,8,a,c,u,y,1i,3xw,1f,12,18,0,t,2g,g,y,9m,1c,s,o,u,s,1m,o,o
14,a,a,8,6,a,s,q,16,3ye,19,i,10,14,b,2c,w,3k,90,1m,u,s,m,14,1k,w,s
16,c,8,8,8,a,s,u,1a,3zw,1x,1k,14,m,13,1s,i,2q,90,1m,u,s,q,10,1o,s,u
10,c,6,8,8,6,o,m,10,3yy,1f,1c,g,1a,10,g,1q,4c,8s,1k,o,u,o,14,1s,16,s
14,a,8,8,8,a,q,s,14,3z2,1z,14,18,10,5,1i,u,3q,90,1i,s,u,o,14,1k,10,q
16,c,a,8,8,c,w,w,1i,3xy,1p,1e,18,8,r,24,o,s,9m,1a,u,o,u,o,1q,o,m
14,c,a,8,8,a,s,u,1c,3z0,1r,14,16,s,11,2a,s,2e,98,1e,u,s,q,y,1o,s,s
y,8,8,6,6,8,i,k,q,40y,r,6,22,y,u,a,2s,4y,8s,u,10,m,s,s,2m,16,14
16,e,8,a,a,c,w,10,1m,3xo,1d,18,16,h,h,20,8,a,9s,18,u,m,w,o,1m,m,o
1a,e,a,a,c,e,14,18,20,3w8,l,1k,g,1f,m,e,j,h,a2,18,q,q,u,m,1e,k,g
10,8,8,6,6,6,i,i,q,420,d,7,2o,y,g,m,2w,4u,8u,k,18,o,s,q,2m,14,18
y,8,8,6,6,6,i,i,q,41q,l,n,34,y,k,a,30,54,8u,q,12,k,s,s,2q,14,18
12,a,8,6,8,6,o,m,y,412,1h,1g,q,12,10,o,1w,3s,8u,1i,k,w,q,16,1s,14,w
y,8,6,6,6,8,i,g,o,41q,n,n,3c,q,g,4,2u,5q,8u,i,1a,g,u,s,2u,14,18
12,c,8,8,6,a,q,q,14,3zu,1r,y,10,10,0,18,u,3u,8s,1m,s,u,q,16,1k,10,u
10,a,6,6,6,8,k,i,q,41w,b,a,1y,12,m,q,2u,4o,8u,10,s,s,q,q,2k,14,18
12,8,8,6,6,8,m,i,u,41g,n,q,16,14,y,s,2k,4g,8u,1e,i,u,q,10,24,14,14
1a,e,a,a,a,e,14,18,20,3wa,n,1k,k,1d,k,e,f,j,a2,18,q,q,u,k,1g,k,i
12,a,8,6,8,6,o,o,y,3z8,1b,18,i,16,w,k,1m,46,8s,1i,o,u,q,16,1q,14,u
14,e,8,8,8,c,s,w,1e,3y8,1v,1c,18,m,t,2g,o,1u,9e,1c,u,q,s,w,1m,s,o
12,a,8,8,8,8,q,m,12,3z4,1z,1c,y,14,i,y,1e,44,8s,1m,q,u,o,18,1k,14,s
12,c,8,8,8,8,q,q,14,3yq,1p,w,10,14,1,1u,18,3s,8u,1o,s,u,o,14,1k,y,u
y,8,6,8,6,6,g,i,o,426,j,n,3i,i,c,4,2c,66,8u,c,1g,g,u,s,2u,16,16
12,a,8,6,8,8,o,m,10,3zk,1b,10,o,12,w,i,1a,4c,8s,1i,o,w,o,18,1o,16,s
10,8,6,6,6,8,i,i,o,41u,j,p,38,w,k,c,2y,52,8u,q,12,k,u,q,2q,14,18
16,c,a,8,8,c,u,w,1g,3y4,1v,1k,18,q,1b,2s,e,16,9s,10,w,o,o,w,1q,o,o
12,a,8,8,8,8,o,o,12,3zy,1f,u,u,10,k,q,12,3y,8s,1k,q,u,q,16,1o,14,s
y,8,8,6,6,6,i,i,o,426,j,l,3e,m,c,8,2g,5w,8u,a,1i,i,u,q,2s,18,16
y,8,6,6,6,8,g,i,o,41k,n,p,3e,q,g,0,2w,5q,8s,k,1a,g,s,u,2u,14,18
14,c,a,8,8,a,s,u,1c,3z6,1v,16,16,s,11,28,q,2e,96,1g,u,s,q,10,1m,s,s
1a,e,a,8,c,e,12,18,1y,3wm,v,1i,q,1j,k,g,f,r,a0,1a,q,q,u,k,1i,i,k
12,c,8,8,8,8,s,q,14,3yo,1j,q,10,14,9,24,16,3q,8u,1o,u,s,o,16,1i,y,u
1k,c,a,c,a,e,12,12,1s,3vy,1s,18,1j,y,18,m,9,1c,au,14,m,1e,e,q,w,q,k
1m,c,a,a,c,c,12,14,1s,3wi,20,w,1x,1c,18,0,l,6,b0,12,q,1e,c,o,u,o,q
1e,e,8,a,c,c,y,10,1k,3us,2m,1c,2,r,1g,w,i,1o,as,y,m,12,y,g,16,o,k
1i,c,a,a,c,e,14,18,22,3wc,1s,w,1l,1,1c,p,19,15,aq,18,m,1i,g,g,u,k,e
1e,c,a,8,c,c,10,12,1q,3vo,22,12,g,1x,1k,6,j,f,aq,12,o,18,w,8,14,i,i
1k,c,a,a,c,e,14,18,20,3wm,1s,u,25,u,1a,n,1b,v,au,16,o,1i,c,k,u,k,i
1a,e,8,8,a,c,y,y,1o,3x6,2,14,u,1f,a,y,6,11,a6,1e,q,s,1g,c,12,e,k
1a,c,8,8,a,c,s,u,1e,3xc,5,18,w,h,2,2g,y,1c,a4,1c,q,m,1c,q,14,g,w
1m,e,8,c,c,e,14,18,20,3wo,22,m,25,1c,12,r,1d,r,b2,12,12,14,a,q,q,k,k
1q,e,c,c,c,e,16,1c,24,3x4,28,1f,k,12,1,z,13,h,bg,w,1i,g,m,o,o,k,m
1c,c,8,8,a,e,w,12,1q,3x2,6,14,u,1p,g,o,5,13,a8,1c,q,w,1c,c,12,c,i
1o,c,a,c,c,c,14,14,1u,3wm,2a,0,z,1c,16,b,h,0,b8,y,1c,q,e,s,q,q,u
1s,c,c,c,c,e,14,18,1y,3xc,24,1h,u,14,4,d,9,9,bi,10,1e,g,o,o,q,o,s
1m,e,8,c,c,c,12,16,1u,3wk,26,k,1p,1e,16,7,p,3,b4,10,12,12,c,q,s,o,q
1c,e,8,8,c,e,10,14,1u,3w8,1a,12,k,2b,1c,0,p,z,ae,1a,q,14,14,6,12,g,e
1k,c,a,c,a,e,10,14,1q,3w4,1u,14,1l,10,1a,g,d,16,au,16,k,1g,e,o,w,q,k
1c,c,a,8,a,c,w,w,1g,3w6,16,18,w,1d,1c,1i,10,1e,a8,1e,o,o,1c,g,18,k,o
1o,c,a,c,c,e,14,1a,20,3ws,26,0,1b,18,y,x,1d,n,b8,y,1e,q,e,q,o,m,k
1e,e,8,a,c,e,12,14,1u,3uu,1q,1g,0,x,1c,o,3,s,ak,12,m,w,s,s,14,m,g
1g,c,a,8,c,c,y,12,1m,3vi,2c,12,0,13,1m,g,b,g,as,12,m,1a,q,c,16,k,k
1q,c,a,c,c,c,16,18,1y,3x0,26,n,d,18,s,t,15,d,ba,y,1i,k,i,q,m,q,o
1q,c,c,c,a,e,14,16,1y,3x6,24,x,4,18,q,l,r,b,be,w,1i,i,k,q,o,s,q
1i,c,a,a,c,c,10,12,1o,3vq,24,16,11,c,1e,m,5,18,au,12,m,1c,i,m,10,o,m
1i,e,a,c,c,e,14,16,1w,3us,2a,16,1f,q,14,e,5,1g,b0,w,o,12,o,q,w,q,e
1o,c,a,e,a,e,18,1a,24,3ws,26,7,17,18,q,19,1j,n,b8,y,1g,o,e,q,m,m,i
1c,e,8,8,c,c,w,w,1i,3vu,1k,18,q,1b,1e,1e,y,1c,ac,1a,o,s,1a,e,18,m,m
1g,e,8,a,e,e,16,1c,26,3w2,1u,w,1b,r,1c,11,1d,15,ao,18,m,1k,g,e,u,g,8
1q,c,c,c,c,e,1c,1e,28,3wu,26,11,d,14,1,1n,1n,d,ba,y,1i,i,i,q,m,g,e
1k,c,a,a,e,c,16,1a,24,3wm,1q,u,2d,u,18,x,1n,t,au,18,m,1k,a,k,s,i,g
1g,e,8,a,c,c,12,14,1u,3vy,22,y,l,t,1i,3,t,j,as,14,m,1e,m,c,12,k,g
1c,e,8,8,c,c,y,y,1o,3w4,1g,12,s,1x,1c,m,6,d,ae,1a,q,10,16,8,16,g,k
1q,e,a,c,a,e,12,16,1u,3x6,24,z,c,18,q,7,3,1,be,w,1k,g,m,o,q,w,u
1i,e,8,a,c,e,10,14,1s,3we,1u,y,1h,k,1e,0,n,1,as,16,m,1i,e,k,w,o,m
1o,e,a,c,a,e,14,16,1w,3wy,26,d,l,18,10,l,t,9,ba,y,1g,m,g,q,o,s,q
1c,e,8,8,c,e,10,16,1y,3wc,14,10,k,2d,18,7,x,13,ae,1a,q,14,14,6,12,e,c
1g,c,a,a,c,c,12,14,1u,3vy,22,y,l,t,1i,3,t,h,as,14,m,1e,m,c,12,k,g
1s,c,c,c,a,e,16,18,1y,3xa,24,1d,q,14,6,h,f,d,bg,10,1g,e,o,o,q,o,q
1c,e,8,a,a,c,y,10,1k,3vw,y,1e,s,17,16,1e,g,1k,aa,1a,o,o,y,u,16,o,i
1e,e,8,8,c,c,y,y,1k,3v8,2c,16,e,15,1k,y,i,1a,am,14,m,12,10,e,16,m,m
1c,c,8,8,a,c,u,u,1e,3wu,a,1a,y,t,o,28,y,1u,a6,1c,q,m,18,o,18,i,s
1k,e,a,c,c,g,1a,1g,2a,3wk,1u,m,2t,16,u,1t,1x,3,ay,16,u,1c,a,o,m,g,6
1k,e,a,c,c,e,16,1c,26,3wo,1u,o,2l,18,12,17,1x,n,ay,16,q,1g,a,m,q,i,c
1i,c,a,a,c,c,12,14,1u,3wc,1u,y,1d,a,1e,1,r,d,as,16,m,1i,e,i,y,m,k
1e,e,8,a,c,c,10,14,1s,3vm,28,10,4,1n,1k,2,n,h,au,10,m,1a,u,8,14,i,i
1c,c,a,8,a,c,y,10,1o,3wg,12,12,s,1t,12,o,8,n,ac,1c,o,y,1a,8,16,e,m
1o,e,a,c,c,c,16,1a,22,3ww,24,b,x,18,s,15,1h,l,b8,10,1g,m,g,q,m,m,k
1g,e,8,a,c,c,10,12,1q,3w0,22,10,t,5,1g,a,j,c,as,14,m,1g,i,e,12,o,k
1q,e,a,e,c,e,1c,1g,2a,3wu,2a,1j,4,12,h,1l,1h,9,bc,y,1i,g,k,q,k,g,c
1e,e,8,a,c,c,14,18,22,3vu,1s,10,0,21,1g,h,15,z,ak,18,o,1c,u,6,10,g,a
1q,c,a,e,a,e,18,1a,24,3x0,26,x,9,18,g,19,1l,h,bc,w,1i,i,k,q,m,m,i
1k,c,a,a,c,c,12,16,1u,3wk,1u,w,1v,w,1c,5,t,f,au,16,m,1k,a,m,w,m,m
1m,c,a,c,c,e,1a,1c,28,3wm,1y,k,2j,18,w,1f,1v,h,b0,14,w,1a,a,o,o,i,a
1m,e,a,c,c,e,18,1c,26,3wo,22,8,21,1a,q,1f,1r,j,b4,12,16,y,c,q,m,i,e
1a,c,8,8,8,c,u,u,1e,3xi,b,16,w,b,d,2e,u,m,a4,1c,q,m,1c,q,14,e,w
1c,c,8,a,8,c,w,w,1g,3wm,s,16,u,19,y,1i,10,k,a8,1c,q,s,1a,g,16,i,q
1m,e,8,c,c,c,14,14,1w,3wm,26,k,1t,1c,18,d,t,b,b4,10,12,12,c,q,q,q,o
1k,e,8,c,a,e,10,14,1s,3wg,1w,10,1v,16,18,6,l,c,aw,14,m,1i,c,o,u,q,m
1c,c,a,8,c,c,14,16,20,3ws,g,12,m,2h,y,d,x,z,a8,1e,q,12,18,6,10,c,8
1g,e,8,a,c,c,14,16,1y,3w0,20,y,t,r,1g,b,z,z,as,14,m,1g,k,c,10,i,g
1c,e,8,8,c,c,w,y,1k,3vw,14,1c,u,1b,1g,1g,o,1m,a8,1c,o,o,10,s,18,o,i
1u,c,e,a,c,e,1c,1c,2e,3xi,10,2n,2u,12,1f,28,d,21,by,1u,y,g,m,m,a,a,c
1w,e,g,c,g,i,1o,1s,2w,3wq,1,1z,2q,7,10,12,3f,f,by,1w,s,k,c,2,4,e,6
1u,a,a,c,e,e,1a,1c,1s,3yk,1w,x,1a,1n,3,a,0,2e,c2,2c,12,k,a,i,m,2,o
1u,c,a,c,g,i,1e,1a,26,3z4,1q,15,z,1l,19,4k,14,n,cc,2g,u,i,g,e,3,e,10
1u,a,a,a,e,e,1a,18,1u,3y0,2g,x,1k,1v,1a,1n,1e,16,c2,26,16,i,g,i,q,1,m
1s,c,a,c,g,i,1c,1a,20,3yu,1s,z,f,2b,a,2a,1u,2,cc,2g,w,k,g,g,1,i,u
1u,c,a,a,a,c,12,1a,1w,3y0,10,1j,2e,1k,5,24,21,v,c0,1u,10,g,o,w,m,i,g
1u,c,c,a,a,e,18,18,26,3xu,12,2p,2e,22,a,1t,22,2h,by,1u,w,c,o,q,s,6,g
1y,i,g,e,k,m,20,20,3c,3vy,t,2,14,p,2i,2l,11,e,by,1w,o,i,4,1,6,k,6
1y,i,g,g,k,m,22,22,3i,3vu,n,e,1o,13,32,47,15,19,c2,20,k,e,0,0,e,e,a
1u,e,e,c,c,i,1e,1s,2i,3x6,u,39,2u,1m,29,4u,4z,i,bw,1w,s,i,m,a,0,8,c
1u,e,c,e,e,g,1i,1i,2g,3w8,2g,1v,q,1c,1q,2l,d,1n,c2,2e,w,a,a,2,q,4,k
20,i,g,i,m,o,24,26,3m,3vi,2,g,8,9,24,3p,x,t,c2,20,g,c,2,0,g,c,e
1w,g,e,e,g,i,1s,1u,30,3v4,24,x,2c,12,2i,55,15,2f,c8,2a,k,8,a,3,m,4,a
1u,c,c,a,e,i,18,1g,2c,3wy,1u,13,1k,d,f,2e,21,2d,c0,22,14,i,c,6,u,4,e
26,q,k,o,q,y,2g,2k,46,3zg,53,g,l,g,19,n,y,1n,c0,1u,e,4,0,8,o,8,m
24,q,i,o,o,w,2i,2g,48,3za,59,22,t,b,27,1b,1o,3n,cg,1k,a,4,4,8,o,2,o
20,i,g,i,k,m,24,26,3m,3vk,6,1,1a,a,1w,53,n,2h,c6,22,i,8,0,0,m,4,g
1u,c,a,a,a,a,y,10,1u,3ys,12,13,28,1o,1m,2s,j,4x,c4,20,w,e,o,w,16,q,m
1u,c,a,a,c,e,18,1a,1y,3xe,2o,z,1g,15,1c,1r,1k,f,c2,22,16,i,i,g,s,2,g
1w,e,c,a,a,c,1a,1c,20,3z0,k,1,20,1i,q,35,l,2e,c8,26,q,g,o,o,14,8,a
1w,e,c,g,c,i,1o,1o,2w,3wi,4,f,0,2y,1i,31,v,31,c0,22,10,e,4,4,a,8,c
1u,c,a,8,a,c,10,14,1w,3ya,14,h,1w,1i,e,28,27,2d,c2,1y,y,k,m,y,s,o,c
1y,i,g,i,g,m,22,22,3i,3vo,1,1e,7,1g,22,61,b,2z,cc,1y,i,c,0,1,o,2,e
1y,e,c,e,k,m,1u,1y,3e,3za,e,0,1,3r,a,3k,5,3l,ci,28,i,o,8,2,9,2,g
22,o,i,m,m,w,2c,2e,42,3xi,2x,18,d,g,2b,1x,1c,3d,cg,1q,8,6,2,6,o,2,m
1u,e,c,e,g,m,1i,1i,2k,3z4,1g,1p,1x,v,9,4k,2,1j,cm,2a,q,g,a,8,7,m,o
1u,e,c,c,e,i,1e,1k,2k,3wi,1q,15,16,c,16,9,1h,2v,c0,26,10,g,8,2,q,4,e
1u,c,a,a,a,e,16,1a,22,3xq,12,v,20,y,z,1w,23,11,c0,1w,10,m,k,o,i,i,a
1u,e,c,c,e,g,1g,1i,2o,3x4,o,1x,22,q,e,1k,2d,2t,c0,1w,y,i,g,8,8,c,a
1y,e,e,g,g,k,1u,1u,36,3ve,1e,2,18,12,2q,5l,x,2r,c8,26,k,c,4,1,m,2,c
22,m,k,m,m,u,2c,2e,40,3ww,25,18,v,q,17,2b,q,23,c6,1y,a,6,0,6,o,4,k
20,g,e,i,m,q,24,2e,40,3zk,x,x,2j,7,34,1c,19,49,cy,1w,i,e,4,f,0,4,k
26,s,m,o,s,w,2k,2k,4g,3ys,4t,2k,21,1z,1v,6,2k,51,cy,y,6,a,6,c,i,4,o
1u,c,a,a,c,g,1e,18,1y,3yk,2c,11,1s,n,11,2f,4c,1,c8,28,y,i,i,i,o,7,s
1s,c,a,a,e,e,1c,16,1s,3yk,1u,n,u,2d,w,2,1y,1e,c6,2g,10,k,k,g,e,6,s
1w,c,c,c,i,k,1m,1m,2s,3ze,1a,v,g,2r,1v,4a,1c,35,cc,2e,o,k,c,8,5,1,y
1w,g,e,e,i,i,1u,1u,38,3wa,b,9,1s,k,2s,3z,1h,2x,c2,22,s,c,4,0,e,8,c
1w,c,e,i,g,m,1o,1q,2y,3zc,y,1r,2f,7,s,3u,b,2r,cq,28,m,c,a,1,1,a,q
20,e,c,a,c,e,14,1e,24,3z2,2,6,1e,1a,3,1i,2x,a,ca,24,k,o,k,g,1a,k,e
2a,s,o,o,u,y,2k,2m,4m,3xc,2l,1u,2j,2l,1h,s,2q,5b,cy,s,4,g,8,c,i,4,o
1w,c,e,c,g,g,1k,1k,2q,3w0,26,23,1w,o,2g,2x,r,2n,c2,2a,w,c,8,1,q,2,e
1u,c,a,c,g,i,1c,1c,24,3z6,1s,13,z,13,1t,4m,18,x,cc,2e,u,i,g,g,1,6,14
1w,c,c,g,i,k,1m,1m,2u,3zi,16,1b,2h,13,8,5a,d,31,ci,2c,o,g,c,0,7,6,12
1u,c,c,c,c,g,1a,1g,2g,3xa,u,1h,20,12,19,1w,1j,2b,c0,1w,10,i,i,e,a,g,a
1u,c,a,a,a,a,w,10,1k,3ym,14,1v,1q,1w,1i,3q,1i,19,c0,1w,y,a,k,w,1g,k,y
1w,e,e,e,e,i,1o,1o,2y,3wa,o,5,0,26,20,3d,13,2v,c2,24,w,g,2,2,i,4,c
1u,c,a,c,e,g,1a,1c,1y,3xo,22,17,l,c,8,5,6,16,c0,2e,14,g,8,c,u,2,m
20,i,g,k,i,q,24,26,3o,3wa,x,2o,n,o,8,55,i,3d,ck,1s,a,c,2,0,o,0,g
1u,c,c,a,a,a,w,w,1k,3z0,16,1j,1o,1o,1c,4m,2q,2r,c4,20,u,c,k,q,1k,s,10
1u,c,a,c,e,i,1e,1c,24,3z8,1q,11,e,1r,15,2s,1c,n,c8,2g,u,i,a,m,0,6,y
1w,e,c,i,i,m,1q,1u,32,3zk,o,1l,2j,f,1a,3m,d,39,cq,26,m,c,c,5,1,4,s
1w,c,c,e,e,g,1i,1k,2g,3w4,2k,29,16,10,1w,2f,h,1l,c2,2c,y,a,8,2,m,8,e
1y,c,e,i,i,o,1q,1y,38,3zg,i,1t,2j,0,28,2c,f,3f,cu,24,k,c,8,9,2,6,k
1s,e,a,a,8,c,12,18,1s,3y4,10,x,24,1i,10,s,37,m,c2,1w,w,i,q,w,q,k,a
1w,c,c,i,g,m,1k,1o,2s,3zg,16,1j,2j,j,9,5c,b,2r,cm,28,q,c,e,2,7,6,12
1u,e,c,g,g,m,1k,1m,2s,3yu,1i,27,1h,h,a,2w,w,2p,cs,28,q,c,c,1,4,6,10
1s,c,a,a,e,g,1a,18,1s,3ym,1s,n,m,23,k,i,1q,1c,c6,2g,y,m,k,g,c,6,u
24,s,k,o,q,w,2i,2i,4a,3zw,67,1w,n,b,1x,11,1o,39,cc,1m,a,4,2,a,o,4,o
1s,c,a,c,e,g,1a,18,1w,3yw,1s,t,8,v,1f,1u,1o,w,c6,2g,y,k,c,m,a,6,u
1w,c,a,c,a,i,1g,1c,24,3ym,22,r,1s,i,2n,2p,4o,3,ca,2a,q,k,k,i,o,b,s
20,e,c,a,c,e,12,14,20,3yy,4,i,1a,y,b,4e,4,31,ca,22,i,q,k,g,18,u,y
1s,c,a,e,c,g,1a,1c,1y,3xk,1y,11,n,e,e,3,c,w,c0,2e,16,g,8,a,w,4,k
22,o,k,m,m,u,2e,2e,46,3yk,4v,20,h,8,2f,1n,1o,47,cw,18,a,8,2,8,k,2,m
y,8,6,6,8,6,g,i,m,3ww,17,3a,w,s,1a,1w,3w,6i,8w,1q,e,q,q,q,2q,10,16
y,8,6,8,6,6,g,i,m,3x4,1b,3e,s,u,1e,1u,3u,6g,8w,1o,e,q,q,s,2o,12,14
u,8,6,6,6,6,g,e,k,3xk,15,1v,52,m,u,24,1w,62,8u,i,1o,k,s,s,2i,1e,1g
y,8,8,6,6,8,g,i,q,3xu,1h,3a,e,1c,1o,16,30,5m,8u,1o,c,o,s,q,2o,14,16
w,8,6,6,6,8,e,g,m,3xc,x,b,3w,q,1g,22,2y,66,8w,q,1a,m,u,q,2w,12,1c
12,a,8,6,8,6,m,k,u,3xw,n,1q,e,12,1c,1s,2e,4y,8y,1m,u,q,m,18,1o,12,y
10,8,8,6,6,8,i,i,s,3xu,19,34,0,1e,1o,1k,2g,5s,8w,1k,i,s,o,y,2a,14,14
y,8,6,6,6,8,g,g,m,3xc,17,2g,1k,u,1i,1q,3m,66,8w,1g,k,o,s,q,2u,12,18
1c,g,a,c,c,g,1a,1e,2a,3um,o,1k,v,v,16,t,v,k,ae,16,m,w,i,10,w,i,g
14,a,8,8,6,8,q,m,10,3xy,k,f,w,10,i,18,1k,34,9c,1i,u,q,m,1g,18,u,w
1c,e,a,c,c,e,18,1c,24,3uy,o,1m,7,1b,16,d,n,1,ae,16,m,s,o,y,10,i,g
u,8,6,6,6,6,g,e,m,3wy,11,1t,58,m,12,2k,2c,60,8w,g,1q,m,s,q,2q,18,1e
10,a,6,8,6,8,k,i,u,3x4,1f,3e,2,10,y,1s,2o,5c,8y,1o,o,q,m,16,1w,10,10
12,c,6,8,8,8,o,m,10,3xu,6,8,o,y,o,10,1s,2w,96,1k,u,s,o,1c,1a,w,w
u,8,6,6,6,8,e,g,k,3wy,11,1r,56,o,14,2k,2e,62,8w,g,1q,m,s,q,2q,18,1e
10,8,8,6,6,8,i,i,s,3xw,1b,34,1,1g,1o,1i,2e,5s,8w,1k,i,q,q,y,2a,14,14
12,a,6,8,6,8,m,k,u,3y0,z,1w,a,1a,1o,1a,24,4k,8w,1k,s,s,m,14,1s,14,y
10,a,8,8,6,8,m,k,u,3xg,v,2e,2,10,y,1e,2e,4c,8y,1o,s,q,m,18,1o,10,y
10,a,8,6,8,6,k,k,s,3xs,11,2o,4,12,1k,1y,2g,5q,8w,1m,q,s,m,14,1w,14,y
u,8,4,6,6,8,e,g,k,3xk,1b,1v,50,k,m,1w,1k,62,8w,g,1q,k,s,s,2c,1g,1g
18,e,a,8,a,c,y,10,1o,3wq,n,1m,16,r,0,1w,g,6,a2,1a,q,m,10,o,1e,k,m
u,8,6,6,6,6,g,e,m,3xk,13,1t,52,m,y,22,20,64,8u,g,1o,k,u,q,2o,1a,1g
10,a,8,8,6,8,m,k,w,3xg,r,28,4,10,y,1a,2e,48,8y,1o,s,q,m,18,1o,10,y
1a,e,a,a,c,e,14,16,22,3w2,d,1o,s,1x,y,6,b,z,a4,1a,q,o,u,q,18,k,k
w,6,8,6,6,6,g,g,k,3xs,z,1l,4u,q,1a,1s,2e,60,8u,m,1g,i,u,s,2y,14,1c
16,c,8,8,8,a,s,u,18,3xo,b,m,y,q,1d,2u,e,y,a6,12,s,m,14,18,w,i,w
16,c,a,8,8,c,s,u,1e,3xe,t,14,14,i,p,2y,u,1o,9w,18,s,m,y,q,1k,m,q
12,c,8,6,8,8,o,o,10,3xu,e,5,q,10,k,s,1m,2k,98,1k,u,q,q,1c,18,w,u
w,6,6,8,6,6,e,g,m,3xe,x,1p,50,o,18,28,2a,64,8u,g,1o,k,u,q,2u,16,1e
1i,e,c,e,e,i,1i,1m,2i,3t2,1y,q,21,q,8,x,n,22,ay,s,m,y,i,y,m,g,e
18,c,a,8,8,c,u,y,1g,3x4,r,1g,14,0,d,2o,u,1c,a2,16,s,m,10,s,1e,k,o
w,8,8,6,6,6,g,g,m,3ww,11,22,1w,q,1c,20,3q,6c,8w,1e,q,o,q,q,2u,10,18
u,6,6,6,6,6,g,e,k,3xm,1f,17,4c,k,i,1o,1c,64,8w,g,1i,s,s,s,2a,1i,1e
16,c,a,8,8,a,s,u,1c,3xg,p,y,16,i,l,2w,y,28,9w,18,s,m,w,w,1g,m,s
1a,c,8,8,8,c,u,u,1e,3xi,d,16,w,9,j,2g,q,a,a6,18,s,m,1c,s,10,g,u
w,6,8,6,6,6,g,g,k,3xw,z,1l,4q,s,1a,1o,2e,60,8u,m,1g,i,u,q,30,14,1c
10,a,6,8,6,8,k,i,q,3xs,15,2y,0,16,1m,20,2i,60,8w,1m,o,q,m,16,1y,14,10
y,8,6,8,6,6,i,i,o,3y2,1b,2i,y,1e,1e,q,34,5c,8u,1m,c,o,q,s,2s,14,16
12,a,8,6,8,8,m,m,y,3y0,t,18,k,18,1g,14,26,3s,8w,1k,u,s,m,14,1q,14,u
y,8,8,6,6,6,i,g,o,3xc,1d,3g,q,u,1k,1q,3o,6c,8w,1o,e,q,q,q,2q,12,14
14,c,8,8,8,a,q,q,14,3xu,h,e,14,s,5,2m,1a,3g,9m,1a,u,o,o,14,1g,u,w
18,c,8,8,8,c,q,u,18,3xm,h,14,y,a,p,36,u,2c,a4,18,q,m,16,10,12,i,10
w,8,6,6,6,8,e,g,m,3x2,x,c,3c,o,1g,22,36,6a,8w,y,14,o,s,o,2y,10,1c
10,a,8,8,6,6,m,k,u,3y0,11,26,4,1c,1o,1c,24,4u,8w,1k,q,s,m,14,1u,16,y
y,8,6,8,6,6,g,i,m,3xi,19,2o,18,14,1i,1i,3i,62,8w,1m,c,q,q,q,2u,12,18
10,a,8,6,8,6,k,k,s,3xs,11,2o,4,14,1k,20,2g,5q,8w,1m,q,q,m,16,1w,12,12
u,8,6,6,6,6,e,g,k,3xa,15,1v,54,m,s,2a,22,5w,8w,g,1q,k,s,s,2i,1e,1g
y,a,6,8,6,6,i,i,o,3xi,1d,3i,a,y,1m,20,30,6g,8w,1o,g,q,q,y,2c,12,12
10,a,8,8,6,8,m,k,u,3y0,z,1w,a,1a,1o,18,24,4k,8w,1k,q,s,o,14,1s,14,y
u,8,6,6,6,6,g,e,m,3xs,17,1t,50,k,u,1y,1w,62,8u,g,1o,k,s,s,2m,1c,1g
1a,c,8,8,a,a,s,u,1c,3xg,b,18,y,9,3,2u,u,1y,a4,1c,q,m,18,u,14,g,y
10,8,8,6,6,8,i,i,o,3xi,1d,3i,4,10,1k,24,2u,6i,8w,1o,i,q,o,12,26,14,12
y,8,8,6,6,8,i,i,q,3xy,1d,32,c,1e,1k,12,2s,5i,8u,1m,e,o,s,u,2k,14,16
1c,e,a,a,e,e,16,1a,24,3va,c,1m,4,1l,16,9,j,h,ac,16,o,q,q,w,12,k,g
1a,c,a,a,a,c,10,12,1s,3wi,f,1k,16,1b,g,1a,8,9,a2,1c,o,m,10,q,1a,k,m
1g,g,c,e,e,i,1i,1m,2i,3t8,1i,w,25,i,e,15,j,2c,ao,10,m,10,e,10,m,g,c
y,a,6,8,6,8,i,i,o,3x8,1f,3o,6,y,18,24,2w,6g,8w,1o,k,q,o,12,26,12,10
u,8,6,6,6,6,g,e,m,3xa,11,1t,54,o,10,2c,28,62,8w,g,1o,k,u,q,2o,1a,1g
1e,e,a,c,e,e,1a,1e,28,3ua,16,1m,13,l,16,p,x,k,am,10,m,w,g,14,u,i,e
12,c,8,8,6,8,q,o,12,3xy,a,9,u,10,a,10,1c,2a,9g,1e,u,q,s,1c,16,s,u
w,8,8,6,6,6,i,g,o,3xw,17,u,2o,18,1e,16,32,5s,8u,1e,k,m,u,q,2w,14,18
u,8,6,6,6,8,e,g,k,3y0,15,1p,4u,k,12,1u,20,62,8u,g,1m,i,u,s,2w,16,1c
1a,e,8,a,a,c,y,10,1m,3wg,1,1k,14,15,i,1k,e,12,a4,1c,o,m,10,u,18,k,k
14,c,8,8,8,8,q,q,12,3xw,6,9,14,10,h,1w,y,2a,a4,w,w,m,m,1q,w,q,w
w,8,6,6,6,6,g,g,k,3x0,v,l,46,o,1c,2a,2w,68,8w,q,1c,o,s,q,2w,12,1c
u,6,6,6,6,8,e,g,k,3xu,1f,1v,4y,k,i,1i,16,6a,8u,i,1o,k,s,u,2a,1g,1c
w,6,8,6,6,6,e,g,m,3x4,x,13,4k,o,1a,2c,2o,66,8w,k,1k,m,s,q,2w,12,1e
1e,e,a,c,e,g,1a,1g,2a,3u8,12,1k,19,j,16,x,z,u,ak,10,o,w,g,12,u,i,e
18,c,8,8,8,c,q,s,18,3xm,h,12,y,e,r,3a,s,2k,a4,16,s,m,16,10,10,k,10
18,c,a,8,8,a,s,u,1c,3xk,f,14,w,4,t,2u,o,10,a4,18,s,m,1a,w,10,g,y
1c,e,8,a,c,c,12,14,1w,3vq,e,1m,m,1d,w,o,1,a,a8,1a,o,o,u,w,14,m,g
u,8,6,6,6,8,e,g,k,3x8,11,1r,54,o,14,2c,2a,62,8w,g,1o,m,s,q,2s,18,1e
12,a,8,6,8,8,m,k,u,3xy,5,w,m,14,1k,1k,26,4q,8w,1o,u,q,m,16,1o,12,y
y,a,6,8,6,6,k,i,o,3x0,1h,3q,a,y,14,26,2y,6g,8w,1q,k,q,m,14,24,12,10
10,a,8,8,6,8,o,k,y,3x4,h,1u,4,10,y,w,2g,3c,8y,1q,s,q,m,18,1m,w,y
w,8,6,6,6,8,e,i,m,3xo,x,t,48,y,1e,1i,30,60,8u,16,u,m,s,q,30,12,1c
u,6,6,6,6,6,g,e,k,3xm,1l,s,2a,k,i,18,k,6m,8w,i,w,1e,s,s,26,1i,1a
10,a,6,8,6,8,k,i,s,3xy,17,2q,0,1e,1o,1g,28,5e,8w,1k,m,q,o,12,22,16,10
10,a,8,6,6,8,k,k,s,3xk,17,32,0,10,16,1y,2i,5k,8y,1m,o,s,m,16,1u,14,y
14,a,8,8,8,8,o,o,10,3y0,8,7,12,10,a,1m,1k,3c,9i,1e,u,o,m,1i,16,w,w
w,6,8,6,6,6,e,g,m,3xc,x,1n,50,o,1a,28,2e,64,8w,e,1o,m,s,q,2u,16,1e
y,8,6,8,6,6,i,g,o,3wu,1d,3g,s,u,16,24,3m,6k,8w,1q,g,q,q,u,2i,10,14
10,a,8,6,6,8,m,k,u,3y2,15,2a,0,1i,1k,16,24,4m,8u,1m,m,s,o,12,1y,14,10
18,c,8,8,8,c,q,u,18,3xk,l,14,12,c,j,38,10,2w,a4,16,q,m,16,w,16,k,y
w,8,6,6,6,6,g,g,m,3xs,z,1l,4u,s,1c,1q,2i,60,8u,q,1c,k,s,s,2y,14,1c
1a,e,8,8,a,c,u,y,1g,3wq,4,1e,10,r,k,28,q,20,a4,1c,q,m,12,u,18,k,o
y,a,6,8,6,6,k,g,q,3xs,1d,3a,4,1c,1q,1o,2s,5y,8w,1m,g,q,o,u,2i,14,14
18,a,a,8,6,c,q,s,16,3xk,d,w,10,k,p,3a,y,36,a4,14,s,k,16,y,14,o,y
1a,c,a,8,a,e,y,12,1q,3wm,l,1m,16,11,6,1k,c,5,a2,1a,q,m,y,o,1g,i,m
14,c,8,8,6,a,q,o,12,3xw,e,f,14,y,7,1s,16,2k,9q,18,u,o,q,1i,12,q,w
16,c,a,8,8,a,s,s,1a,3xi,l,y,14,g,j,32,10,2q,a2,14,s,m,y,w,1e,m,u
w,8,8,6,6,6,g,g,o,3xm,11,6,3e,12,1e,1g,38,5y,8u,1g,k,m,s,q,2y,12,1c
1g,e,c,c,e,g,1g,1k,2e,3tk,1g,1a,1z,6,w,17,x,1s,aq,y,m,y,c,14,o,i,c
1e,e,a,a,c,e,16,18,24,3us,14,1m,7,13,16,0,l,2,ag,14,o,s,o,y,10,m,e
y,8,6,6,6,8,g,i,m,3xk,17,2a,1i,16,1i,1e,3g,5y,8w,1m,c,o,s,q,2u,12,18
w,6,6,8,6,6,e,g,m,3y8,13,1p,4q,o,12,1m,22,60,8u,g,1k,i,u,s,2y,16,1a
y,8,6,8,6,6,g,i,o,3xo,1b,2a,1g,18,1k,1e,3a,5w,8w,1m,c,o,s,o,2u,14,16
u,6,6,6,6,6,g,e,k,3xe,1f,5,3a,k,k,1y,1i,5y,8w,i,16,14,s,s,2a,1g,1i
w,8,6,8,6,6,g,g,k,3wu,x,14,2o,q,1c,26,3g,6c,8w,16,y,o,q,q,2u,12,1a
18,c,a,8,8,c,u,u,1e,3x8,l,1e,12,0,b,2u,w,2a,a2,18,s,m,12,u,1a,i,s
10,a,6,8,6,8,k,i,s,3xe,1b,3g,7,10,14,20,2g,5s,8y,1m,o,s,m,16,1w,12,10
14,a,8,6,8,8,o,k,y,3y0,4,6,w,10,s,1k,1y,3y,94,1m,w,o,m,1e,1c,y,y
1i,e,8,c,c,e,1a,1c,2a,3wc,1q,u,2d,c,18,1f,1t,h,aq,1a,m,1k,c,i,q,g,8
16,a,8,8,8,8,q,o,10,3xq,4,4,14,w,b,2q,1a,3u,9q,1a,u,m,y,16,14,u,10
u,8,6,6,6,6,g,g,k,3xs,13,1r,4w,m,10,1y,1y,64,8u,g,1o,i,u,s,2q,1a,1e
16,c,8,8,8,a,q,q,14,3xm,5,m,10,q,t,36,y,32,9y,18,s,m,12,16,10,o,10
w,6,8,6,6,6,g,g,k,3xw,11,1l,4s,s,18,1q,2c,60,8u,m,1g,i,u,s,2y,14,1c
1a,c,8,8,a,a,u,w,1e,3xg,9,16,u,f,b,28,u,4,a6,1a,q,m,1g,m,14,e,u
w,8,6,6,6,6,g,g,k,3x6,v,r,4a,q,1e,28,2u,68,8w,m,1g,m,s,q,2w,14,1c
w,8,8,6,6,6,g,i,m,3xa,13,1u,22,u,1i,1s,3i,64,8w,1a,q,o,s,q,2u,12,1a
16,c,8,8,8,a,s,q,18,3xm,h,o,14,o,l,32,10,38,a2,10,u,m,y,w,1e,o,w
y,a,6,8,6,6,k,i,q,3y0,1d,32,0,1i,1m,16,2g,5i,8u,1m,g,q,o,10,2c,14,14
12,c,8,8,6,8,q,m,10,3y0,l,m,w,12,o,1u,1q,3q,94,1i,w,q,m,14,1m,y,w
1a,c,a,8,a,c,u,y,1g,3wu,5,1i,10,n,c,2c,o,20,a4,1a,q,m,12,u,18,k,q
y,a,6,6,6,8,i,g,o,3xk,1b,3e,c,16,1m,1s,36,66,8w,1m,g,q,o,u,2k,12,16
y,8,8,6,6,6,i,i,q,3y6,1b,24,16,1e,1e,m,30,58,8u,1m,c,o,q,u,2q,12,16
1e,g,a,e,e,g,1e,1k,2e,3ts,18,1a,1p,1,u,13,t,1m,am,10,m,y,g,12,q,g,c
10,8,8,6,8,6,i,i,q,3xm,1b,3e,0,12,1k,22,2o,6c,8w,1m,m,q,o,12,24,14,10
1g,g,c,e,e,k,1i,1o,2k,3t6,1g,o,21,k,4,11,9,2g,ao,12,m,y,g,y,k,g,c
1a,c,8,8,a,c,s,u,1c,3xc,f,1c,10,7,0,2w,w,2c,a4,1a,q,m,14,w,16,i,w
1e,e,a,a,c,e,16,1a,22,3uk,1g,1k,f,t,16,4,p,8,ak,12,m,u,o,y,y,m,g
u,6,6,6,6,6,g,e,k,3xc,1h,16,1y,k,k,1u,18,66,8w,i,s,1k,q,s,28,1i,1g
u,6,6,6,6,6,g,e,k,3xw,1j,1r,4u,i,i,16,s,6g,8u,i,1q,k,s,s,28,1i,18
w,8,6,8,6,6,g,g,m,3xi,z,4,3g,10,1e,1m,38,60,8u,1a,q,o,s,q,2y,12,1a
1i,e,c,g,e,i,1m,1o,2k,3t2,1k,i,21,q,1,11,5,2i,au,w,m,y,i,w,k,e,e
y,a,6,8,6,6,i,i,o,3x8,1h,3o,a,y,1a,26,32,6k,8w,1o,k,q,o,10,28,12,12
12,a,8,6,8,8,m,m,w,3xk,b,1e,a,10,w,10,28,3m,8y,1q,s,s,m,18,1k,y,w
1a,c,a,8,a,c,w,y,1m,3ws,f,1k,14,p,6,22,m,16,a2,1c,o,m,12,s,1a,k,m
u,8,6,6,6,6,g,g,k,3xs,15,1t,50,k,y,1y,1y,62,8u,g,1o,i,u,s,2o,1a,1g
14,c,8,8,8,a,q,q,14,3xw,d,6,14,w,j,2y,14,3o,a0,y,u,o,m,14,1g,u,w
w,8,8,6,6,6,g,g,m,3xa,11,18,2k,u,1g,1w,3c,66,8w,14,w,o,s,q,2w,10,1a
1a,c,8,8,8,c,s,u,1c,3xk,f,16,y,5,h,2o,s,14,a4,1a,s,m,1a,s,14,e,y
12,a,8,6,8,6,m,k,w,3xs,h,1k,e,10,14,1k,2e,4i,8y,1o,s,s,m,18,1m,10,y
16,e,8,8,a,c,u,y,1k,3x4,v,1c,16,6,n,2k,k,o,9w,18,u,m,y,k,1o,k,o
18,e,8,a,a,e,y,12,1u,3x6,17,1g,16,15,0,1c,1,h,a0,16,s,m,y,m,1i,k,o
12,a,8,8,8,8,o,o,12,3yc,15,k,u,18,10,w,1g,4a,8u,1m,u,s,o,16,1k,12,s
14,c,a,8,6,c,s,s,1c,3xs,11,q,16,w,l,2o,o,2e,9i,1c,u,o,u,u,1m,q,s
y,8,8,6,8,6,i,k,q,3ye,1l,2y,4,1k,1e,u,2g,4s,8u,1k,c,q,q,q,2o,12,14
1a,e,a,8,c,e,14,16,20,3wo,11,1o,10,23,u,8,b,15,a0,1c,q,m,y,k,1g,i,k
18,e,8,a,a,c,10,12,1s,3x8,19,1g,16,z,3,1g,1,d,a0,16,s,m,y,k,1k,m,m
12,c,8,8,8,8,q,q,14,3yg,17,g,y,16,c,1g,12,44,90,1i,u,s,o,14,1k,10,s
12,c,8,8,8,8,s,o,16,3y2,t,c,12,10,e,1q,18,3i,92,1k,u,s,q,10,1k,y,s
10,8,8,6,8,6,k,k,s,3y4,1d,2u,7,1m,1m,14,24,4y,8u,1m,g,s,o,12,24,14,12
10,c,6,8,8,6,o,m,10,3yo,17,12,g,1c,18,e,1m,4k,8u,1i,q,u,o,16,1o,16,s
16,c,8,8,8,a,s,s,18,3xo,l,k,14,q,l,2w,10,2q,9k,1g,s,o,s,y,1i,q,s
18,e,8,a,a,c,y,12,1q,3ww,z,1k,16,n,7,1q,6,b,a2,16,s,m,10,i,1k,k,m
1c,g,a,c,e,e,1c,1g,2a,3ui,m,1k,15,p,14,t,13,10,a8,1a,o,y,e,10,y,g,e
14,c,8,8,8,a,u,s,1a,3xw,15,q,16,y,n,2q,m,2o,9k,18,u,q,q,y,1m,q,s
18,c,a,8,a,c,w,10,1m,3wy,x,1m,14,7,f,2a,g,8,a2,16,s,m,10,k,1k,i,o
10,c,6,8,8,6,o,m,10,3yg,19,1a,e,1e,18,m,1o,4a,8u,1k,q,s,o,16,1q,12,w
18,c,a,8,a,c,y,10,1o,3xc,19,1e,16,h,h,22,0,4,a0,14,s,m,y,m,1m,k,o
1a,c,a,a,a,e,10,14,1u,3wo,v,1o,14,1f,c,12,3,t,a2,1a,q,m,y,k,1g,k,m
y,8,8,6,6,6,i,i,q,3y8,1b,1s,1g,1g,1c,m,2w,58,8u,1m,c,o,q,s,2s,12,16
10,8,8,6,8,6,k,k,u,3y8,1d,2m,3,1m,1i,w,28,4k,8u,1k,i,s,o,10,26,14,10
y,8,6,8,6,6,i,i,o,3yc,1b,12,24,1e,1a,i,30,56,8u,1k,e,m,s,s,2s,12,16
16,c,8,8,8,c,s,u,1e,3xi,t,w,14,q,r,2u,q,1s,9m,1e,s,o,w,q,1m,o,q
1c,e,a,c,c,e,18,1e,26,3vc,1,1k,d,17,10,9,11,c,a4,1c,o,u,m,s,16,i,e
14,a,a,6,8,a,s,q,16,3ya,1d,m,14,10,1,22,w,3m,96,1g,u,s,o,12,1k,w,s
10,a,8,8,6,8,m,m,y,3y6,11,1k,8,1e,1e,w,24,3w,8u,1m,o,u,m,14,1u,12,w
10,a,6,8,6,8,m,k,w,3yo,1d,22,2,1g,1a,g,20,4c,8u,1i,k,s,q,12,20,14,y
y,8,6,6,6,8,i,i,o,3ym,1b,k,2m,1a,14,a,2y,54,8u,1a,m,m,q,s,2w,10,18
y,8,6,8,6,6,i,i,q,3ym,1f,1g,1q,1a,18,e,2s,50,8u,1e,i,m,s,q,2u,12,14
14,c,8,8,8,a,s,s,18,3xy,15,o,16,w,9,2c,s,2y,9e,1c,u,s,q,y,1k,u,s
y,a,6,8,6,6,k,i,q,3y0,1d,32,0,1k,1k,14,2g,5e,8u,1m,e,q,q,10,2c,14,14
12,a,8,8,6,8,q,m,10,3y6,t,q,m,18,10,18,1o,3w,8u,1o,s,s,o,14,1o,10,u
12,a,8,8,8,8,o,o,12,3y8,z,m,s,16,y,16,1i,40,8u,1o,s,u,m,16,1m,10,u
y,a,8,6,6,8,k,i,u,3yi,1h,2m,2,1i,1c,o,2c,4g,8u,1k,e,s,q,u,2e,14,10
1c,e,a,a,c,g,16,1a,26,3vq,b,1k,0,1f,y,7,r,3,a2,1c,q,s,o,q,18,i,i
y,8,6,6,6,8,i,i,o,3yk,1b,g,2q,1c,14,a,30,54,8u,1c,k,m,s,q,2w,12,16
12,c,8,8,6,a,o,o,12,3y2,h,8,w,12,e,1y,1i,3s,8w,1q,u,s,m,14,1m,y,u
12,a,8,8,8,8,o,m,10,3y2,p,s,o,16,y,1e,1u,3q,8u,1q,s,s,m,14,1o,10,w
10,a,8,8,6,8,k,k,w,3y2,13,20,4,1g,1k,10,26,48,8u,1m,o,s,m,14,1w,14,y
1a,e,a,a,c,e,12,18,20,3w8,l,1m,o,1j,o,c,d,l,a2,1a,q,o,u,q,1a,k,k
12,a,8,8,8,8,q,o,10,3yq,1b,o,s,18,o,w,1e,4c,8u,1k,s,u,o,16,1k,14,q
1c,e,c,a,e,e,1a,1c,28,3v0,a,1m,j,15,14,n,r,a,a8,1a,o,u,k,w,10,k,e
18,e,8,a,a,e,y,12,1u,3xc,1b,1g,16,19,2,16,0,p,a0,16,s,m,y,k,1m,k,k
10,a,8,6,8,8,m,k,w,3ya,19,1u,4,1i,1g,o,20,40,8u,1k,m,u,o,12,1w,14,w
y,a,6,8,6,6,k,i,s,3yc,1h,2e,g,1m,1e,s,2e,4u,8u,1k,e,s,o,u,2i,12,14
12,a,8,8,8,8,o,o,12,3y8,11,m,u,16,10,12,1i,42,8u,1m,u,s,o,16,1k,12,u
1a,e,a,8,c,e,14,16,1y,3wg,r,1k,o,1d,i,g,b,j,a2,1a,q,o,u,m,1e,k,k
12,c,8,6,8,8,q,o,12,3yc,19,g,12,12,12,w,1a,4c,8u,1m,w,s,o,14,1k,12,s
16,c,8,a,8,c,u,w,1g,3xq,1d,16,14,i,x,2w,a,1k,9u,14,s,o,u,u,1k,o,q
10,a,6,8,6,8,k,k,u,3y2,17,2i,5,1m,1k,14,24,4s,8u,1m,k,s,o,14,1y,14,10
12,c,8,8,8,8,q,q,14,3y4,9,f,16,y,6,24,16,3w,8u,1s,w,s,m,14,1i,y,u
10,a,8,8,6,8,o,k,y,3yu,17,1e,8,1c,18,c,1q,4k,8s,1k,m,u,o,14,1u,16,u
10,a,8,8,6,8,o,m,y,3yi,19,1e,a,1e,1a,k,1o,4c,8u,1i,q,u,o,14,1s,14,u
14,c,8,8,8,a,s,s,1a,3xq,j,a,16,u,h,2m,y,2m,9a,1k,u,q,s,w,1k,s,s
16,e,8,a,8,c,w,10,1k,3x8,17,1i,16,4,v,2m,8,e,a2,12,s,m,10,k,1m,k,o
12,a,a,6,8,a,q,q,16,3wg,i,5,m,10,2,14,16,w,94,1u,s,q,m,1c,14,o,m
w,8,6,6,6,6,g,g,k,3vk,t,1o,1q,w,1s,34,38,6c,8y,1i,6,18,q,o,2s,10,1a
1c,e,8,a,e,e,1a,1g,2c,3wc,i,10,0,2x,1c,1b,1l,2,ag,18,q,1a,w,6,s,c,2
1i,e,a,g,e,g,1m,1o,2m,3vm,1i,u,3p,s,7,2r,e,6,as,18,12,14,8,o,e,8,8
1k,e,a,e,e,g,1i,1k,2k,3w8,1q,k,3f,14,0,2p,n,8,au,18,16,10,a,o,g,c,6
1m,e,e,e,g,k,1m,1o,2o,3tw,1y,t,1b,12,17,13,g,0,ay,12,16,o,k,i,g,8,e
1e,g,a,c,e,i,1i,1m,2m,3us,1e,18,2p,h,m,2j,3,g,as,12,s,16,i,k,g,a,8
18,e,8,8,8,c,u,w,1i,3xo,n,16,u,5,z,28,a,j,a6,18,s,m,1e,q,10,c,s
1a,e,a,a,e,e,1a,1g,2e,3w6,p,18,c,2x,1a,13,15,6,a6,1e,s,12,y,a,q,a,4
12,c,8,8,6,a,q,o,16,3xk,a,9,q,10,4,w,12,1c,9g,1e,w,o,u,1a,14,q,q
1e,e,a,a,e,g,1e,1m,2i,3va,1s,y,1n,1h,16,29,1h,k,aw,y,o,1c,m,c,o,a,4
12,c,8,6,8,8,s,o,16,3xa,i,d,o,10,e,m,y,12,98,1o,u,q,o,1c,16,q,o
16,c,8,8,8,c,w,10,1m,3xa,b,6,y,c,21,26,d,1f,a8,10,u,m,18,12,s,a,g
1i,e,c,e,e,k,1k,1q,2m,3u6,1g,16,3h,q,9,23,s,6,au,14,10,w,e,m,e,8,c
10,a,8,8,6,8,o,m,y,3wm,0,1c,6,10,u,w,2i,2u,8y,1u,q,q,k,18,1k,u,y
1k,g,c,g,e,k,1m,1o,2o,3ug,1o,b,21,w,11,1f,o,0,ay,14,14,s,g,i,g,8,e
18,c,8,8,a,c,w,12,1q,3xe,l,u,w,7,1p,1q,d,1l,a6,16,s,q,18,w,u,a,g
1k,e,c,e,e,i,1m,1o,2o,3vk,1m,c,39,s,p,29,k,2,aw,16,18,u,c,m,e,8,a
1u,g,e,e,e,k,1k,1q,2k,3uu,2a,21,18,k,1b,11,1j,k,bc,y,18,k,m,e,m,c,c
y,8,8,6,8,6,k,i,q,3vo,1b,36,y,y,12,28,3i,5c,8y,1q,m,o,m,o,2g,w,12
1c,e,a,c,e,g,1e,1k,2g,3vo,4,16,t,25,1a,1v,v,g,ac,1c,q,18,q,c,o,8,6
1i,e,a,e,g,g,1m,1o,2m,3uw,1i,16,3p,i,7,2h,o,8,au,14,12,y,c,o,e,6,c
1i,e,c,e,e,k,1k,1q,2m,3u8,1g,16,3h,o,9,23,s,6,au,14,10,w,e,m,e,8,c
1e,g,a,c,e,i,1i,1m,2m,3uw,1k,14,2r,d,k,2p,3,e,au,12,q,18,i,k,g,8,8
w,8,6,8,6,6,g,g,m,3vi,r,1y,1k,w,1s,30,3o,6g,8y,1k,q,m,q,m,2q,w,16
1i,g,a,e,g,i,1m,1o,2o,3v4,1e,w,3n,q,l,27,u,2,au,16,16,w,a,m,e,8,c
16,c,a,8,8,e,y,12,1s,3ws,j,e,y,1,1t,1m,b,1d,a8,12,u,m,18,y,q,8,e
1e,e,c,c,e,g,1i,1o,2k,3uw,1c,16,2h,n,o,2j,7,g,as,12,q,18,k,i,i,8,8
1e,e,c,c,e,i,1i,1m,2k,3uo,10,1c,2j,h,q,2f,1,e,ao,16,o,16,k,k,g,a,8
18,e,a,8,e,e,16,1e,26,3vk,x,1a,10,2f,m,3,z,b,a8,1a,q,y,w,k,q,8,8
10,a,8,8,6,8,m,k,w,3xc,z,2g,2,10,y,16,2k,40,8y,1o,s,q,m,18,1o,10,w
w,8,6,8,6,6,g,g,k,3wc,v,1i,28,q,1e,2g,3k,6e,8w,1q,g,q,q,o,2s,10,1a
1k,e,a,g,e,i,1m,1q,2m,3v2,1g,q,3h,q,n,23,u,2,au,16,16,u,e,k,e,8,c
1e,g,a,e,e,g,1k,1o,2m,3ua,1a,1g,31,1,k,2h,8,c,au,10,q,14,k,k,g,8,a
1a,e,a,a,e,g,1c,1g,2e,3w0,j,18,0,2n,18,19,13,a,a8,1e,q,14,u,c,q,a,4
w,8,6,8,6,6,g,g,m,3ve,v,2i,16,y,1q,2u,3u,6e,8y,1q,e,s,q,m,2q,y,16
w,6,8,6,6,6,e,g,k,3ve,t,26,10,10,22,3e,2y,6e,8y,1e,7,1q,q,o,2s,10,1a
1a,c,a,a,c,e,18,1c,28,3wi,r,18,m,2n,m,n,11,7,a8,1a,s,y,12,e,u,8,4
1k,g,c,e,g,k,1m,1o,2o,3tu,1m,2,2d,14,z,1b,s,2,aw,12,16,q,g,k,e,a,e
18,c,a,8,8,e,y,10,1u,3xg,r,12,u,j,1b,14,9,1p,a6,18,s,o,1c,s,u,a,g
w,8,6,6,8,6,g,g,k,3vc,p,2a,16,w,1w,32,3o,6e,8y,1q,8,y,q,m,2s,y,16
y,a,6,8,6,8,i,k,q,3vu,1l,3k,o,10,12,26,3c,5a,8y,1q,o,m,m,10,24,w,14
16,c,8,8,8,c,u,w,1k,3xe,5,2,w,m,23,2m,h,19,a8,y,u,o,16,16,q,c,k
18,e,a,a,c,e,18,1c,28,3w2,x,16,y,2p,s,9,15,7,a8,1a,q,y,10,g,s,8,6
1k,c,a,e,c,g,1e,1i,2g,3wc,1q,q,37,y,m,2b,1j,a,au,18,w,1c,6,o,k,e,4
1i,e,a,g,e,i,1m,1o,2m,3uq,1g,14,3n,m,9,2b,q,6,au,14,12,y,c,m,e,8,c
w,8,6,6,8,6,g,g,k,3vu,v,1i,24,u,1m,2q,3m,6e,8y,1q,i,o,o,o,2s,10,18
1e,g,a,e,e,i,1k,1o,2m,3u8,y,1i,31,4,g,2b,e,a,as,12,o,14,k,m,e,a,a
1m,g,c,g,e,k,1m,1o,2o,3uc,28,1b,x,w,1d,13,8,0,b2,10,16,q,k,i,g,8,c
u,8,4,6,6,6,e,e,k,3vs,19,2q,0,w,1y,3c,2k,56,90,g,e,22,q,q,2k,1a,1g
1e,c,a,a,e,e,1a,1g,2c,3vw,1c,y,h,2f,1e,1h,1f,5,ai,1a,o,1c,s,8,s,c,4
18,c,a,a,a,g,14,1a,24,3vu,v,14,10,1v,0,c,t,p,a8,18,q,q,14,o,q,8,8
16,e,8,a,a,e,10,16,20,3vu,p,m,14,t,x,14,f,11,a8,14,s,o,14,u,q,8,a
1g,e,8,c,e,g,1e,1k,2g,3vo,1q,w,25,p,10,2b,1b,c,aq,16,q,1e,g,g,o,a,4
1g,g,a,e,g,i,1k,1o,2o,3ua,1i,1a,3j,i,0,2d,o,8,aw,12,w,10,e,m,e,8,c
1i,e,a,c,e,i,1i,1m,2k,3vs,1m,y,3l,m,c,2v,b,8,as,18,w,1a,8,o,g,a,6
w,8,6,8,6,6,g,g,m,3vg,t,2e,18,w,1q,32,3o,6e,8y,1q,c,w,o,m,2q,w,16
1a,e,8,a,a,g,14,1a,28,3x4,f,12,o,2p,m,j,15,n,a8,1c,s,14,14,8,u,a,4
14,c,8,8,8,a,u,w,1g,3wq,4,r,12,w,1h,2m,1,l,9y,16,u,m,12,16,q,g,i
12,c,8,8,8,a,s,s,1a,3w6,c,b,s,10,j,1w,k,6,9c,1o,s,q,q,1a,10,k,i
1e,e,a,a,c,g,1c,1i,2e,3vm,1k,y,z,1z,1c,1t,1j,6,aq,12,q,1a,q,a,q,c,4
1k,g,c,g,e,k,1m,1o,2o,3uc,1o,9,23,y,11,1d,o,0,aw,14,16,q,i,i,g,8,e
10,a,8,8,8,8,q,m,14,3vs,13,2i,4,12,s,k,1s,1o,8y,1o,w,q,m,14,1i,s,k
12,a,a,8,8,8,s,s,18,3vy,g,9,q,12,3,1c,m,i,90,20,q,q,m,1c,12,o,g
12,c,a,8,8,c,u,w,1k,3vw,6,j,10,u,15,28,0,r,94,1y,u,o,u,18,u,e,g
y,a,6,8,6,8,i,k,q,3wc,1l,3m,i,y,10,20,36,5m,8w,1s,m,o,m,12,22,y,12
12,a,8,8,8,8,q,q,14,3wo,k,3,i,10,c,w,1i,16,94,1s,s,q,o,18,18,q,s
1e,e,8,a,e,e,1a,1e,2c,3vq,1m,y,j,27,1e,1d,1h,9,ai,1a,q,1a,s,8,u,c,4
14,c,8,8,8,8,s,u,1a,3xq,8,j,y,w,x,1q,8,4,9y,12,u,q,10,1a,u,k,o
1i,g,a,g,e,k,1m,1q,2m,3te,1w,u,37,10,l,1p,y,4,ay,10,12,o,k,k,e,a,e
1m,g,c,g,e,k,1k,1q,2m,3uo,26,17,17,u,1d,19,c,0,b2,12,16,q,k,g,g,8,e
w,8,6,6,8,6,g,g,k,3vw,v,1c,2a,u,1k,2q,3k,6e,8w,1q,i,o,q,o,2s,10,18
14,c,8,8,8,c,s,w,1i,3ww,0,h,y,w,1f,22,4,n,9w,18,u,m,12,16,s,g,i
1g,c,a,a,e,e,1a,1e,2c,3vs,1u,w,19,19,1a,1h,1j,d,ak,1c,m,1g,k,c,s,e,6
16,c,a,6,8,c,s,s,1c,3xm,9,k,y,o,1j,2u,8,g,a6,12,u,m,14,16,w,g,w
u,8,8,6,6,6,e,g,k,3vk,t,1e,1u,w,22,3e,2w,6e,90,12,e,1e,q,o,2q,10,1a
18,e,8,8,a,e,y,14,1y,3xg,p,y,u,17,t,s,l,1h,a6,18,u,s,1c,k,u,a,a
1c,e,a,a,e,g,1c,1i,2g,3vu,w,10,p,2b,18,1t,1j,g,am,14,q,1a,s,8,q,a,4
1k,e,c,e,g,i,1m,1q,2m,3uw,1g,e,33,u,t,1t,u,0,aw,14,16,u,e,k,e,6,e
10,a,a,6,8,8,q,o,10,3vs,2,16,a,12,o,w,1u,1w,8y,1w,q,q,m,16,1e,s,s
1c,e,c,a,g,g,1e,1m,2i,3vc,2,1c,19,1n,14,1v,r,k,ac,1e,o,16,q,e,k,a,8
1c,e,8,8,c,e,16,18,26,3wi,q,10,i,2v,1a,n,13,t,aa,1e,q,14,16,4,y,c,6
14,c,8,8,6,a,s,u,1a,3xk,a,r,y,y,t,1i,8,0,9w,14,u,q,10,1a,u,k,i
1a,c,a,8,a,e,12,16,1y,3xe,h,12,s,21,6,3,n,17,a6,1c,s,u,1e,c,y,a,8
10,a,8,8,8,8,o,o,10,3vo,7,1q,4,14,u,s,20,1y,8y,1u,q,q,m,14,1i,s,w
1s,e,g,e,e,k,1k,1q,2k,3uo,2m,2d,10,u,1p,t,15,c,ba,w,1c,g,q,e,k,a,e
14,c,8,a,8,c,u,10,1k,3w2,1,h,14,q,1n,2m,5,13,9i,1o,s,o,10,14,s,a,g
18,e,a,8,c,e,16,1a,26,3w4,v,16,y,2b,c,1,z,h,a8,18,s,q,14,m,s,8,6
1c,e,a,a,e,g,1e,1k,2g,3vs,i,14,t,2b,18,1v,13,g,ag,1a,q,18,s,a,o,a,4
1i,c,a,c,e,e,1c,1i,2e,3w6,1q,u,2r,c,12,1z,1x,8,aq,1a,m,1m,8,k,m,g,4
1k,e,c,e,g,i,1m,1q,2m,3um,1g,k,35,w,r,1t,u,2,au,16,14,u,e,k,e,8,e
1e,g,a,e,e,i,1k,1o,2m,3u4,1c,1g,37,6,i,2h,c,c,aw,y,o,14,k,m,e,a,a
1m,e,c,g,e,k,1m,1o,2o,3us,1u,j,21,u,15,1h,m,0,b0,12,18,q,i,i,e,8,c
1k,e,a,g,e,k,1k,1q,2m,3v4,1g,o,3f,q,p,23,u,2,au,18,14,w,c,k,e,8,c
1k,e,c,g,e,k,1m,1o,2o,3u2,1i,g,2t,10,r,1l,u,2,au,14,16,q,g,k,e,8,e
1m,e,a,e,c,g,1i,1k,2i,3wa,1y,6,2t,18,5,2j,13,e,b0,14,1a,u,c,o,i,c,6
1k,e,a,g,e,k,1k,1q,2m,3v6,1g,m,3f,s,r,23,u,2,au,18,14,w,c,k,e,8,c
1i,e,c,e,e,i,1m,1o,2o,3ui,1g,14,3j,m,9,27,s,6,au,14,12,w,e,m,e,8,c
w,8,8,6,8,6,g,i,m,3vm,15,2y,16,w,1g,2g,40,6c,8y,1q,k,o,o,o,2m,y,14
w,6,8,6,6,6,e,g,k,3ve,t,28,12,y,20,3c,2y,6c,8y,1m,h,1s,q,o,2s,10,1a
w,a,6,8,6,6,i,i,m,3vi,17,30,16,w,1g,2i,3s,64,8y,1q,m,m,o,s,2g,y,12
1i,e,a,g,e,i,1m,1o,2o,3ue,1g,1a,3r,o,9,27,u,6,au,14,10,y,c,m,e,8,c
10,a,8,8,8,8,o,o,y,3w2,4,14,8,12,u,q,22,2e,8y,1w,q,q,k,18,1g,s,u
u,8,6,6,4,6,e,e,k,3vu,19,2m,6,y,22,3c,2o,58,90,g,e,22,q,q,2m,18,1i
1a,e,a,a,c,g,1a,1g,2c,3w4,r,18,g,2v,18,z,17,6,a6,1e,s,10,y,c,q,a,4
w,8,8,6,6,8,g,g,m,3vs,11,2s,14,w,1g,2i,3w,6e,8y,1q,k,m,q,o,2o,y,14
1e,g,a,c,g,g,1k,1m,2m,3uk,14,1c,2r,9,m,2f,2,e,aq,14,q,14,k,k,g,a,8
1i,e,c,e,e,i,1m,1o,2o,3uq,1e,14,3n,o,b,29,q,6,au,16,10,y,c,m,e,8,c
14,e,a,8,8,e,y,12,1s,3wc,h,4,12,2,1n,1y,9,19,a8,12,s,m,16,10,o,8,g
12,c,8,a,6,c,s,w,1g,3vy,8,h,y,10,13,2k,4,n,8y,24,s,o,w,18,u,g,k
1k,e,a,e,e,i,1i,1o,2m,3w0,1o,i,3h,12,b,2p,0,4,au,18,18,y,8,o,g,8,a
1e,e,c,c,e,g,1i,1o,2k,3uq,18,1a,2j,l,s,2h,7,g,aq,14,o,16,m,i,i,8,a
1c,e,a,c,e,g,1e,1k,2i,3vk,9,1c,p,27,1c,1p,x,k,aa,1e,q,16,q,c,m,c,6
1k,g,c,e,g,k,1m,1o,2o,3ty,1m,1,27,12,z,1b,q,0,aw,12,16,q,i,i,g,8,e
1g,g,a,c,g,g,1k,1o,2m,3va,1m,12,3p,k,6,2x,8,a,as,16,u,1a,a,o,e,8,8
1a,e,a,a,e,g,1a,1i,2e,3w8,9,14,3,2p,18,1d,17,a,a8,1e,q,16,w,a,q,a,4
1g,g,a,e,e,i,1m,1o,2m,3uk,1i,18,3l,g,0,2f,m,8,aw,12,y,10,e,m,e,8,a
1e,g,a,e,e,i,1k,1o,2m,3u4,w,1i,2z,4,i,2d,g,c,au,10,m,14,k,m,e,a,c
1e,g,a,e,e,i,1k,1o,2m,3u4,s,1i,2v,0,g,29,i,c,as,10,q,10,m,m,e,a,c
u,8,6,6,6,6,e,e,k,3vi,11,2c,o,14,22,3e,2q,5y,8y,k,c,22,q,o,2s,14,1c
1e,g,a,c,g,g,1k,1m,2m,3ui,18,1c,2t,7,m,2j,4,e,as,12,q,14,k,k,g,a,8
1m,e,c,g,e,k,1m,1o,2o,3uk,1u,l,1t,u,15,1d,m,1,ay,14,16,q,i,i,g,6,e
1i,e,c,e,g,i,1m,1o,2o,3tq,1m,14,3f,u,b,1x,w,4,aw,10,10,u,g,m,e,8,e
12,c,8,8,6,8,q,q,14,3xq,c,b,q,10,8,s,18,1m,9e,1g,u,q,s,1c,14,s,s
1s,e,e,e,e,m,1k,1o,2m,3uq,2q,2h,s,y,1t,t,x,8,ba,w,1c,g,q,e,k,8,e
1a,e,8,8,c,e,12,18,22,3x8,d,12,s,2d,c,7,t,13,a6,1c,s,10,18,a,y,a,6
u,8,6,6,6,6,e,g,k,3vk,z,2a,s,12,22,3i,2o,64,90,y,3,22,q,o,2q,12,1e
1m,e,c,e,g,i,1m,1q,2m,3v2,1w,j,23,q,15,1n,m,1,b0,14,16,s,g,i,g,6,e
1g,e,a,e,e,i,1k,1o,2m,3v0,1o,14,3h,a,a,2v,8,a,au,14,s,18,e,m,e,8,a
1k,g,a,g,e,k,1m,1o,2m,3va,1q,5,2l,q,11,1v,o,0,ay,14,18,s,g,i,g,6,c
18,c,8,a,8,e,y,12,1w,3xa,n,u,u,n,1h,1c,n,1f,a8,14,u,q,18,u,s,8,c
10,8,8,8,6,8,m,k,u,3vu,13,2y,c,10,10,1g,2y,3w,8y,1s,q,o,k,14,1q,w,12
18,e,8,a,a,e,14,18,24,3wu,t,10,w,1z,9,2,n,v,a8,18,s,u,16,k,s,8,8
1i,e,a,e,g,g,1k,1o,2m,3vm,1k,w,3t,u,5,2t,c,6,as,18,10,16,8,o,e,8,8
14,c,a,8,8,c,w,y,1k,3wa,3,f,14,m,1p,2k,3,13,a0,16,u,m,12,14,q,c,g
1a,c,a,8,a,e,14,18,22,3xa,p,12,u,25,1,3,v,11,a6,1c,s,10,16,e,u,a,6
w,8,6,6,6,6,g,g,k,3vc,r,28,12,10,1y,3c,38,6e,8y,1i,2,1c,q,o,2s,y,1a
1i,e,a,c,e,g,1e,1m,2i,3w2,1m,u,37,k,m,2j,15,c,aq,1a,u,1e,8,m,i,c,6
1g,g,a,e,e,i,1m,1o,2m,3ug,1k,18,3j,g,2,2h,k,a,aw,12,w,10,e,o,e,8,a
1s,e,g,e,e,k,1k,1q,2k,3ui,2s,2h,10,u,1n,t,15,c,ba,w,1a,i,q,c,m,a,c
1m,e,c,g,e,k,1k,1q,2o,3uu,1s,f,25,u,15,1j,o,1,ay,14,18,q,i,i,e,8,c
y,8,8,8,6,8,k,i,s,3vm,1f,3e,s,y,y,1w,3e,4s,8y,1q,o,m,o,q,2a,u,12
14,c,8,8,8,c,u,w,1i,3wg,0,h,10,s,1b,2a,2,p,9s,1c,u,m,10,16,s,e,i
y,a,8,8,6,8,m,k,u,3vm,19,38,e,12,y,1i,34,3s,8y,1q,q,o,m,10,1u,w,12
18,e,a,8,c,e,16,1c,26,3vm,v,18,10,27,e,2,x,h,a8,18,s,s,10,m,q,a,6
14,c,8,8,8,a,q,s,18,3xq,8,7,10,y,11,26,g,14,a0,12,u,o,y,1e,u,m,u
18,e,8,a,a,g,14,18,24,3wi,v,12,w,1z,3,0,t,n,a8,18,s,s,16,k,s,8,6
1i,e,a,e,e,i,1k,1o,2m,3vk,1k,w,3t,s,1,2v,c,6,as,18,y,16,a,m,g,8,8
1i,e,a,e,e,i,1k,1o,2m,3v4,1i,16,3p,g,1,2n,g,8,au,14,y,14,c,m,e,8,a
12,a,a,8,8,8,s,u,1a,3vu,4,4,m,12,7,1i,k,6,8y,1y,u,q,m,1c,12,m,g
1e,g,a,e,g,g,1k,1o,2m,3u8,12,1g,33,6,e,2b,e,a,as,12,q,14,i,m,e,a,a
10,a,8,8,6,8,q,m,10,3vo,t,2e,6,12,s,u,28,26,8y,1s,s,o,m,16,1i,u,q
1c,c,a,8,c,e,12,18,22,3wu,c,12,m,2j,y,h,x,z,a8,1e,q,12,18,6,10,a,8
14,c,8,8,8,c,u,w,1g,3x8,0,d,y,u,1p,24,4,15,a0,14,u,o,14,16,q,g,i
1g,e,a,c,e,g,1g,1m,2i,3vg,1q,y,2l,f,u,2n,x,g,au,12,q,1e,e,i,k,a,6
18,c,a,8,8,c,y,y,1o,3xm,n,10,w,j,11,1i,5,13,a6,18,s,m,1g,o,y,c,k
1g,g,a,e,g,i,1k,1o,2o,3uc,1i,1a,3l,i,0,2d,o,8,aw,12,w,10,e,m,e,8,c
1a,e,a,8,e,e,18,1g,2a,3wo,l,14,i,37,16,11,13,0,a8,1c,s,16,10,6,s,a,2
12,c,8,6,8,8,q,q,14,3xg,e,9,o,10,e,m,16,1c,98,1m,u,q,q,1c,16,s,o
10,a,8,6,8,8,m,k,w,3wa,h,26,6,12,y,14,2u,3o,8y,1s,q,q,k,14,1q,u,10
12,a,8,8,8,8,o,o,12,3xg,0,g,i,10,k,o,1m,20,96,1m,u,q,o,1c,1a,u,s
1m,e,e,e,e,k,1m,1q,2m,3u2,1y,p,1h,y,15,17,i,0,ay,12,16,q,i,i,g,8,e
10,c,8,8,6,8,q,o,12,3w6,8,o,e,12,k,u,1q,1s,8y,1y,q,q,k,1a,1c,q,q
y,8,8,6,8,6,k,i,q,3vi,1d,3c,y,y,14,2a,3k,5c,8y,1q,m,o,m,o,2g,w,12
1c,e,8,a,e,e,1a,1i,2e,3we,6,10,4,33,1e,1d,1p,c,ac,1c,q,1a,w,4,s,c,2
1o,g,c,g,e,k,1m,1o,2m,3tw,2m,1r,5,y,1j,t,5,4,b4,y,18,m,m,g,i,8,e
y,a,6,8,6,8,k,i,s,3vk,1h,3i,o,10,12,22,3e,4w,8y,1q,o,o,m,w,24,w,12
14,c,8,8,6,a,s,s,1a,3xo,a,n,y,y,t,1k,a,8,9w,14,u,o,12,1a,u,k,m
1c,g,a,c,e,g,1i,1k,2k,3v2,i,1a,1p,17,10,25,j,i,ai,18,o,18,o,g,i,a,8
1c,e,a,c,e,g,1e,1k,2i,3vo,g,14,11,23,18,1x,15,m,ac,1e,q,18,q,a,o,a,4
18,c,8,8,8,c,u,y,1k,3xi,f,s,w,4,1l,24,5,t,a6,14,u,m,1c,w,w,c,o
y,8,6,8,6,6,i,g,o,3we,1d,3c,y,u,16,26,3o,6i,8w,1s,i,o,o,u,2i,y,14
12,a,a,8,6,a,s,s,1a,3vu,e,1,o,12,5,1i,o,c,8y,20,s,q,o,18,14,m,k
16,c,a,8,8,c,y,10,1s,3wu,h,8,y,a,21,1w,7,1f,a8,12,s,m,18,10,q,8,g
1e,c,a,a,c,e,18,1c,28,3vu,1k,10,5,2f,1g,11,1d,l,ae,1e,o,1c,u,6,w,c,6
1c,e,8,a,c,e,18,1c,2a,3wi,k,10,m,3j,1k,x,1b,h,aa,1e,q,18,12,2,w,a,4
1c,e,c,a,g,g,1g,1m,2i,3va,b,1g,17,1l,16,1v,n,k,aa,1e,o,16,q,e,k,a,8
1o,e,c,g,e,k,1m,1o,2m,3uk,28,1d,z,u,1d,15,8,0,b2,12,16,o,m,g,g,8,e
1o,g,e,e,e,k,1m,1o,2m,3u8,2m,1z,0,y,1n,t,9,2,b6,y,16,o,o,e,i,8,e
18,c,8,8,a,c,w,10,1q,3xe,j,q,w,7,1p,1w,f,1h,a6,14,u,q,18,w,u,8,i
w,8,8,6,8,6,g,i,m,3ve,11,2s,18,y,1i,2o,3w,68,8y,1q,k,o,o,o,2m,w,14
1k,e,a,g,e,i,1m,1o,2o,3vc,1g,u,3p,q,n,29,s,4,au,18,16,u,c,m,e,6,c
1i,e,a,e,e,i,1i,1o,2k,3vw,1k,s,3n,u,0,2t,1,6,as,18,12,14,8,o,g,8,8
14,c,8,8,8,a,s,u,1e,3x4,6,p,y,y,13,1y,4,9,9w,16,u,o,10,18,s,i,k
16,e,8,8,a,e,10,14,1w,3wc,n,k,10,j,19,1c,f,15,a8,14,s,m,16,w,q,8,c
14,a,8,6,8,8,m,k,u,422,t,20,12,y,u,1q,2g,52,8s,1g,o,u,m,12,20,12,12
1e,e,a,a,e,e,18,1a,20,3z2,v,16,n,1d,g,b,g,26,9s,16,s,q,q,o,12,i,g
12,a,6,6,8,6,k,i,s,428,3,18,1e,s,i,1i,2i,52,8s,1c,k,s,o,q,2e,16,12
1g,c,a,a,c,c,10,10,1k,3y8,14,1e,2,r,10,o,w,2k,a2,16,s,10,g,w,16,q,q
16,c,8,8,8,a,o,o,12,42g,2n,2a,16,12,0,2y,1q,3s,90,1m,k,o,m,y,1u,y,u
14,a,8,6,6,8,m,k,u,422,n,1w,10,10,s,1q,2g,52,8s,1g,m,u,o,10,22,12,12
1a,e,a,8,a,e,w,10,1i,40y,2p,1m,y,t,i,1k,10,1g,9m,14,s,o,u,g,1k,q,o
1i,e,a,c,e,g,1a,1a,20,3x6,e,10,1v,4,y,9,k,2w,a0,1a,s,y,c,u,w,i,g
1q,k,g,g,i,m,1q,1o,2m,3qi,2e,1f,1c,8,0,1a,2y,3o,am,q,u,g,o,g,q,e,k
18,c,a,8,8,c,s,s,16,424,3p,2e,16,m,5,34,1i,28,9g,1e,g,o,k,u,1q,u,s
1c,e,8,a,c,e,12,14,1q,3zy,13,18,8,1d,g,i,a,1o,9q,14,u,m,w,m,1a,k,m
12,c,8,6,8,8,m,k,w,42c,z,x,20,1a,12,2g,2g,30,88,1g,12,e,o,k,1w,1g,y
1m,g,g,g,i,o,1q,1o,2k,3t8,18,1h,v,8,h,16,34,48,a8,12,s,m,g,g,o,g,i
1g,e,a,e,e,g,1c,1e,26,3xk,q,12,29,l,4,h,m,2s,9y,18,s,10,c,u,u,g,e
1e,e,a,a,e,e,16,18,1y,3z2,1p,1g,h,11,o,c,e,24,9q,16,s,o,s,k,16,i,k
1c,c,a,8,c,c,y,10,1k,40k,1p,1g,m,v,c,18,q,1o,9o,12,u,o,w,i,1i,o,m
12,a,8,6,6,8,k,k,s,428,0,10,1k,s,m,1s,2a,54,8q,1e,m,q,o,q,2c,1a,w
12,c,8,6,8,8,m,k,u,42q,v,11,28,18,u,2g,2a,3g,8a,1g,14,e,m,m,22,1c,s
1k,i,g,g,i,m,1o,1o,2k,3tc,16,19,11,e,j,12,30,4a,a8,12,q,o,e,i,q,e,i
1g,g,a,e,g,i,1g,1i,2a,3x2,c,1a,31,3,9,p,1a,3o,9y,1a,q,10,8,u,s,e,c
12,a,6,8,6,6,m,i,s,428,1,u,1s,q,k,1m,2c,56,8q,1e,o,o,o,q,2c,1a,y
14,a,8,8,6,8,m,k,u,42e,l,1g,18,y,m,24,2e,4w,8o,1i,o,s,m,w,24,14,w
1a,c,8,8,a,c,s,u,1a,41k,2l,22,14,0,0,2c,16,28,9g,1a,o,o,u,k,1q,s,s
12,a,8,6,8,6,m,i,u,426,3,1m,y,y,k,1s,2i,52,8s,1c,k,w,o,q,2c,14,12
16,a,8,8,6,a,o,m,w,42m,1n,1q,18,y,g,2i,2a,4e,8s,1i,q,q,m,y,1w,12,u
10,a,8,6,6,6,k,i,q,428,5,w,1q,q,i,18,2g,58,8s,16,q,q,q,q,2e,18,14
12,8,8,6,6,8,k,i,q,428,3,a,2c,o,i,1e,28,5a,8q,18,u,k,q,q,2e,1c,y
1g,e,a,c,g,g,1e,1g,28,3y0,9,18,21,v,2,t,q,34,9w,16,u,u,g,s,u,e,e
18,c,8,8,8,c,q,s,14,428,37,2e,16,q,3,2w,1i,2y,98,1k,i,o,m,u,1s,u,u
1a,e,8,a,a,e,w,10,1i,40q,1t,1k,o,r,a,1e,s,1o,9o,10,w,m,y,g,1k,o,o
14,a,8,6,6,8,m,k,w,41w,1d,28,w,12,w,1e,2i,4u,8s,1i,o,u,o,14,1w,14,14
10,a,6,8,6,6,k,i,s,438,3,t,20,o,k,1c,24,4c,8g,16,w,m,q,q,28,1e,u
1k,e,c,e,c,g,1a,1c,1y,3uo,1c,q,t,14,q,q,w,2i,ac,y,o,w,c,u,y,m,k
1i,e,a,c,c,g,14,16,1s,3w4,y,18,v,o,16,w,w,28,a8,12,o,y,c,w,12,o,m
1m,i,g,g,i,o,1q,1q,2k,3su,1i,1x,j,2,b,1c,38,46,ac,y,u,i,i,g,o,g,i
22,o,i,k,k,q,1u,1s,2u,3pe,9,26,8,m,e,34,3e,22,as,o,s,m,c,g,q,g,u
1g,e,a,a,c,e,12,12,1o,3xe,q,1c,p,2,16,14,y,1w,a0,1a,q,y,e,y,12,m,q
14,a,8,8,8,8,o,m,w,420,1j,v,24,1g,w,2y,2g,2g,86,1i,16,c,m,k,1u,1a,14
12,a,6,8,6,6,m,i,s,428,1,w,1o,s,k,1o,2c,54,8q,1e,o,o,o,q,2c,1a,y
1i,c,a,c,c,e,12,14,1o,3ww,s,18,l,c,18,1a,y,1u,a4,16,o,10,c,y,12,o,o
14,a,8,6,8,8,m,k,u,42c,f,1g,18,w,m,24,2c,4y,8q,1e,o,u,m,u,26,14,w
12,8,8,8,6,8,k,k,s,432,5,11,22,y,o,1u,26,3y,8e,1c,y,i,o,o,24,1g,s
1g,g,c,e,g,i,1k,1k,2e,3wy,d,14,2v,3,d,t,1u,42,9u,1a,s,w,c,q,q,e,c
14,a,8,6,8,8,m,k,u,42a,j,1k,16,y,o,20,2c,52,8q,1g,o,s,o,w,24,14,w
12,a,6,8,6,8,k,i,s,430,0,z,2o,u,m,1u,22,4s,8m,1c,10,e,o,o,28,1g,q
14,a,6,8,6,8,m,k,s,422,r,1y,y,10,u,1k,2k,50,8s,1g,m,u,o,12,20,14,12
10,8,8,6,6,8,i,i,q,428,9,c,2e,m,g,y,2a,5g,8s,u,10,o,s,q,2g,1a,12
1g,e,a,e,e,i,1g,1i,2a,3xo,3,18,2h,l,1,v,y,3g,9w,18,s,w,e,s,u,c,e
14,a,8,8,6,8,o,k,w,42q,1f,v,2a,18,y,2m,2e,3c,8a,1i,14,e,m,k,20,1a,u
1g,e,c,e,e,i,1i,1i,2c,3xg,9,1c,2t,b,9,v,1a,3q,9w,18,s,y,a,u,s,c,c
1g,g,a,g,e,i,1k,1i,2c,3wy,8,1c,3b,0,d,p,1i,3u,9y,1a,q,10,8,u,q,c,e
1c,e,8,a,c,c,12,12,1m,40a,1z,1g,g,11,i,10,m,1o,9o,12,u,o,w,i,1e,m,m
12,a,8,6,8,6,m,k,s,432,d,13,28,10,s,22,28,3w,8e,1e,10,g,o,m,24,1e,s
14,a,8,6,6,8,m,k,u,422,n,1w,10,y,u,1o,2i,52,8s,1g,m,u,o,10,22,12,14
12,a,6,8,6,6,m,i,q,426,0,14,1g,s,k,1k,2g,54,8s,1c,k,s,o,q,2e,16,12
10,a,8,6,6,8,k,i,s,432,5,r,2e,o,k,1k,24,4k,8k,1c,q,m,q,o,2a,1e,s
12,a,8,6,8,6,m,k,s,42u,b,17,26,12,w,1y,2c,3s,8c,1e,10,g,o,m,22,1g,s
1g,e,a,c,c,e,12,14,1q,3x6,w,1e,x,4,12,w,y,28,a0,1a,q,y,e,w,12,o,o
1i,e,c,c,c,e,16,16,1s,3vo,w,18,n,u,16,10,w,26,a6,12,o,y,c,w,12,o,m
1k,g,c,e,e,i,1c,1e,24,3tw,1q,4,h,16,a,q,14,30,ag,w,m,u,g,q,w,k,i
1i,g,c,g,g,k,1k,1m,2e,3vs,5,e,29,8,j,4,28,46,9s,1c,q,u,g,m,q,e,g
16,a,8,8,8,8,o,m,y,42c,1r,20,1a,10,e,2g,20,4i,8s,1o,m,q,m,12,1u,12,u
18,c,8,6,8,a,q,o,12,41i,1n,2i,1a,u,7,2o,1g,46,90,1o,m,q,o,w,1y,w,w
1g,g,a,c,e,g,1a,1c,22,3xc,k,1c,25,7,e,f,w,2w,a0,18,s,10,a,w,w,g,g
1g,e,a,c,c,e,14,14,1s,3x2,y,1e,13,4,12,s,u,2a,a0,1a,q,y,e,w,12,m,m
1k,g,e,i,g,m,1o,1o,2g,3u2,s,n,1p,i,n,q,2u,4c,a2,16,q,q,e,k,q,e,g
1g,e,a,c,c,e,12,12,1o,3xc,g,18,v,e,1a,1a,u,1e,a2,18,q,y,e,w,12,m,s
12,c,6,8,6,8,m,i,u,42a,7,14,1g,u,m,20,2c,50,8q,1e,o,q,o,q,2a,18,w
1g,g,a,e,g,i,1k,1k,2c,3x6,5,1e,37,5,f,t,1k,3y,9w,18,u,w,a,u,q,c,c
1a,c,a,8,a,c,u,u,1c,40g,3l,2i,1a,0,q,2w,1q,1g,98,1o,e,m,k,q,1k,u,u
1w,k,i,k,k,o,1u,1s,2q,3pm,10,1,14,f,s,28,3k,2y,aq,o,w,e,k,e,q,e,o
1o,g,g,e,g,k,1k,1k,2c,3ry,28,v,i,10,7,y,1u,3q,ak,q,q,o,k,m,s,g,i
1e,e,a,c,a,e,10,12,1k,3xa,x,1o,a,2,1k,1y,1e,26,9q,1e,q,s,m,u,1a,q,q
12,a,6,8,6,6,m,i,s,42u,9,l,2o,q,m,1o,24,4y,8m,1e,w,g,o,q,2a,1c,u
1i,e,a,a,c,e,14,14,1q,3ww,o,1a,z,g,18,12,w,1w,a2,18,q,y,c,y,10,o,o
16,c,a,8,8,a,q,o,12,41q,2z,10,1s,18,c,3q,22,24,8e,1y,m,m,i,q,1q,10,10
1i,e,c,c,e,g,1a,1c,20,3we,u,u,1r,e,m,8,y,2u,a0,1c,o,y,e,s,w,i,i
u,8,6,6,6,8,g,i,o,3zc,1z,1f,44,18,s,8,3k,7o,8m,i,1k,o,u,w,2q,16,18
s,8,6,6,6,6,g,g,k,3zm,1x,1p,4e,e,c,m,1k,6g,8s,i,1m,k,u,u,2a,1i,18
u,6,6,6,6,8,g,g,k,3ze,1j,1l,4o,u,u,1g,3c,7y,8s,i,1o,o,q,u,2a,1m,16
u,6,6,6,6,8,e,g,m,3zq,17,1l,48,g,a,i,20,70,8o,i,1k,g,y,y,2k,1a,18
s,8,6,6,6,6,g,e,k,3z6,1z,1l,4e,g,g,o,1g,6a,8s,k,1o,m,s,u,26,1c,18
s,8,6,6,6,6,g,g,k,408,1z,1r,48,c,a,k,1y,6u,8q,k,1k,k,w,w,2a,1i,18
u,6,6,8,6,6,g,g,m,3z8,11,1l,46,e,o,18,38,82,8q,i,1m,k,w,u,2e,1k,18
s,8,6,6,6,6,g,g,m,408,1n,1v,46,e,a,k,2u,7i,8o,k,1m,i,y,w,2c,1o,16
u,6,6,6,6,8,g,g,k,400,1p,1v,48,e,i,1c,38,7w,8q,k,1k,m,w,u,2c,1m,16
u,6,6,6,6,8,g,g,m,3z0,13,15,4a,k,e,o,2y,7o,8o,i,1k,k,w,y,2m,1c,18
u,6,6,6,6,8,g,g,k,3zk,1b,1h,4a,m,c,m,1u,6y,8o,i,1i,g,w,y,2q,18,18
s,8,6,6,6,6,g,e,m,3zc,1x,1n,4e,g,e,m,1m,6c,8s,i,1o,m,u,s,28,1g,1a
u,8,4,8,6,6,g,g,m,3zc,19,1n,4a,o,q,1i,3g,84,8s,i,1k,o,u,s,2e,1i,18
s,8,6,6,6,6,g,g,m,406,1t,1v,46,g,c,y,2y,7o,8q,k,1k,m,w,u,2c,1m,18
u,6,6,6,6,8,e,g,m,3zm,15,1j,48,g,c,k,1y,70,8o,i,1i,i,w,y,2o,18,18
s,8,6,6,6,6,g,g,k,40a,1z,1r,44,e,c,k,2g,7a,8q,k,1k,k,y,u,2c,1k,1a
u,6,6,6,6,8,g,g,k,3zm,1l,1r,4c,q,q,1g,3a,82,8s,i,1m,m,u,u,2a,1m,18
u,8,6,6,6,6,i,g,m,3yq,v,1h,4a,i,y,1m,3e,8a,8q,k,1k,m,w,s,2e,1g,1a
u,8,6,6,8,6,i,g,o,3yq,17,13,40,w,o,s,3u,7u,8o,i,1i,s,u,u,2o,14,18
u,6,6,6,6,8,g,e,m,3ze,z,1h,46,e,c,i,2u,7i,8o,k,1i,k,w,y,2i,1g,18
u,8,6,6,6,6,i,g,m,3ys,1b,v,48,q,e,14,3c,80,8o,i,1k,m,w,w,2q,16,1a
s,8,6,6,6,6,g,g,k,406,1z,1r,48,c,c,i,1w,6s,8q,k,1k,k,w,w,2a,1i,18
u,6,6,6,6,8,g,g,k,3zs,1v,1r,4k,w,s,u,2c,78,8o,i,1i,g,w,y,2q,18,18
s,8,6,6,6,6,g,g,k,408,1z,1r,48,c,c,i,1w,6s,8q,i,1m,k,w,w,2a,1i,18
u,8,4,8,4,8,e,g,k,3y4,1f,1v,4w,k,k,1g,1g,66,8u,g,1o,k,s,s,2g,1g,1c
u,6,6,8,6,6,g,g,m,3zi,1v,1j,4k,12,q,o,2o,7g,8o,i,1i,i,w,y,2s,18,16
u,6,6,6,6,8,e,g,m,3zq,1r,1t,4c,o,q,1a,36,7w,8s,i,1m,o,s,u,2c,1k,18
u,6,6,6,6,8,e,g,m,3yw,1j,1v,4o,g,g,10,1i,6a,8s,i,1m,i,s,u,2k,1c,1c
u,6,6,6,6,8,e,g,m,406,1h,1v,4c,a,a,g,1k,6u,8o,i,1m,g,w,w,2k,1c,18
u,8,6,6,6,6,i,g,m,3yw,z,1f,4c,q,u,1g,3m,88,8s,i,1k,o,u,s,2g,1e,18
s,8,6,6,6,6,g,e,m,3yk,1r,1n,4m,g,g,s,6,6w,8s,k,1o,k,u,s,22,1m,14
s,8,6,6,6,6,g,g,k,3zk,1v,1p,4e,e,c,m,1g,6g,8s,i,1m,k,u,u,2a,1i,18
y,8,6,6,6,8,g,i,m,3y0,19,1o,1w,18,1e,w,36,5k,8u,1g,i,m,s,q,2y,12,18
u,6,6,6,6,6,g,g,k,3zs,1v,1r,4e,e,c,i,1i,6g,8q,k,1m,i,u,w,2a,1i,18
s,8,6,6,6,6,g,g,k,404,21,1r,48,e,c,m,2c,78,8q,k,1m,m,u,u,2c,1i,1a
u,8,6,6,6,6,i,g,m,3yq,z,19,48,m,q,16,3i,84,8q,i,1k,o,u,u,2i,1e,18
s,8,6,6,6,6,g,g,m,40a,1p,1t,46,c,a,i,2g,78,8o,k,1k,k,w,w,2e,1k,18
s,8,6,6,6,6,g,e,k,3z2,1x,1l,4e,i,e,q,18,6e,8s,k,1o,m,s,s,26,1e,18
u,6,6,6,6,6,g,g,k,3yw,1p,1t,4m,g,g,q,16,6e,8s,i,1o,k,s,u,28,1i,1a
u,6,6,8,6,6,g,g,m,3yy,17,z,48,m,c,w,34,7s,8o,i,1k,k,w,y,2o,1a,18
u,8,6,6,6,6,g,e,m,3y8,1b,1v,4w,i,q,1m,1s,62,8u,g,1m,i,u,s,2q,1a,1c
u,6,6,6,6,8,g,g,m,3yw,h,1f,42,e,a,o,3c,7w,8q,i,1k,k,w,w,2g,1k,18
u,6,6,6,6,8,e,g,m,3ze,1n,1v,4k,e,c,q,1g,6g,8s,i,1k,i,u,w,2g,1e,1a
u,8,8,6,6,8,i,g,o,3ys,11,1d,3y,12,y,q,4a,7k,8o,i,1k,u,u,q,2o,12,16
u,6,6,6,6,6,g,g,m,400,1t,1t,4g,c,a,g,1m,6i,8q,i,1m,i,u,w,2g,1e,1a
s,8,6,6,6,6,g,g,k,3zw,1x,1r,4c,e,c,i,1m,6i,8q,k,1m,i,w,u,2a,1i,18
s,8,6,6,6,6,g,e,m,3z8,1v,1n,4e,g,e,o,1a,6i,8s,i,1o,k,u,u,26,1k,18
s,8,6,6,6,6,g,g,m,408,1x,1t,46,e,e,q,2s,7k,8q,k,1k,k,y,u,2c,1m,18
u,8,6,6,6,8,e,i,k,3yo,17,1r,4m,o,y,1a,1u,62,8u,g,1i,i,u,s,2y,16,1a
u,8,6,6,6,8,g,i,m,3yq,x,1b,48,s,y,18,3u,82,8q,i,1k,q,u,s,2i,1a,18
u,6,6,6,6,8,g,g,k,3zu,19,1n,46,e,c,k,1k,6s,8o,i,1i,g,w,w,2q,18,18
u,6,6,6,6,8,g,g,m,3zi,19,1p,48,e,k,1a,38,80,8q,k,1k,k,y,u,2c,1k,18
s,8,6,6,6,6,g,g,k,3zy,1z,1r,48,i,k,y,2u,7o,8s,i,1m,o,s,u,2c,1k,18
u,6,6,6,6,6,g,g,m,404,1l,1t,46,c,c,o,2w,7m,8q,i,1m,i,y,w,2c,1o,16
u,8,6,6,6,6,g,i,m,3za,1x,1j,4k,1a,u,g,2y,7q,8o,i,1i,m,u,y,2s,16,16
s,8,6,6,6,6,g,g,k,402,21,1p,48,c,c,k,22,6u,8q,k,1m,k,w,u,2a,1k,16
u,6,6,6,6,8,g,g,k,3zg,1j,1d,4e,s,i,m,2a,76,8o,i,1i,i,w,y,2q,18,18
u,6,6,6,8,6,g,g,m,3z6,1p,13,4i,y,i,i,2s,7i,8o,i,1i,k,w,y,2s,1a,16
u,6,6,6,8,6,g,g,m,3z2,1d,11,4c,q,e,o,2w,7m,8o,i,1k,i,w,y,2q,1c,16
u,8,6,6,6,8,g,g,o,408,2b,27,44,1k,18,8,32,7i,8m,i,1i,k,u,y,2u,16,18
u,8,6,6,6,6,g,i,m,3yq,v,19,46,m,m,12,3g,84,8q,i,1k,m,w,u,2i,1e,18
u,6,6,6,8,6,g,g,m,3z0,t,1f,46,e,g,y,3a,7y,8q,i,1k,k,y,u,2g,1k,16
s,8,6,6,6,6,g,g,k,408,21,1p,46,e,a,k,22,6w,8q,k,1k,k,w,w,2a,1k,18
u,8,6,6,6,8,g,i,m,3za,1x,1j,4c,18,u,a,3e,7q,8m,i,1k,o,u,w,2q,16,18
u,8,6,6,6,6,g,i,m,3ys,x,17,46,k,k,12,3g,82,8q,i,1k,m,w,u,2k,1c,18
u,6,6,6,6,8,e,g,m,3ys,1j,1v,4q,g,g,14,1k,66,8s,i,1m,i,s,u,2m,1c,1a
s,8,6,6,6,6,g,e,k,3ya,1r,1n,4m,k,g,u,c,6s,8u,i,1q,m,q,s,24,1k,14
u,6,6,8,6,6,g,g,m,3z0,19,11,4a,o,c,s,30,7q,8o,i,1k,k,w,y,2o,1c,16
s,8,6,6,6,6,g,e,m,3ys,1r,1p,4m,g,g,q,w,6i,8s,i,1o,k,u,s,28,1i,18
s,8,6,6,6,6,g,g,k,40a,21,1r,46,e,c,i,2c,74,8q,k,1k,k,w,w,2c,1k,18
u,6,6,6,6,8,g,g,k,3zy,1n,1t,48,e,i,16,34,7u,8q,k,1k,m,w,u,2c,1m,18
s,8,6,6,6,6,g,g,m,40a,1x,1t,4c,a,a,i,1q,6q,8q,i,1m,i,w,w,2c,1g,18
u,8,6,6,6,6,g,i,m,3yy,z,1h,4a,i,u,1g,3g,86,8q,k,1k,m,w,s,2e,1i,18
w,8,6,6,6,6,g,g,m,3yg,15,1j,4k,w,14,16,2a,5w,8u,m,1c,i,u,s,30,12,1c
u,8,6,6,8,6,i,g,o,3yw,1f,13,3y,w,m,o,3s,7s,8m,i,1k,q,w,u,2o,14,18
s,8,6,6,6,6,g,e,k,3zm,23,1n,4a,i,g,s,28,6o,8s,k,1o,o,q,u,2a,16,18
u,8,4,8,6,6,g,e,m,3yq,1h,1v,4q,i,g,16,1m,66,8s,i,1m,i,s,u,2o,1a,1c
u,8,6,6,6,6,g,e,m,3yi,1d,1v,4s,i,m,1g,1s,62,8u,g,1m,g,u,u,2s,18,1c
u,6,6,6,6,6,g,g,m,3zm,1r,1t,4i,e,c,m,1g,6g,8s,i,1k,i,u,w,2g,1e,1a
u,8,6,6,6,6,g,i,m,3yy,1j,11,4e,u,i,u,36,7w,8o,i,1k,k,w,w,2s,18,16
s,8,6,6,6,6,g,g,k,406,21,1r,46,e,e,m,2g,7a,8q,k,1m,k,w,u,2c,1k,18
u,6,6,6,6,6,g,g,m,400,1b,1p,46,c,a,g,24,72,8o,i,1k,i,w,y,2g,1g,18
u,8,6,6,6,6,g,g,m,3z4,15,1h,4i,y,y,1i,3i,88,8s,i,1m,o,s,s,2e,1g,18
s,8,6,6,6,6,g,g,k,3zi,1x,1p,4e,g,c,m,1k,6e,8s,i,1o,k,u,u,28,1i,18
u,6,6,6,8,6,g,g,m,3zo,1x,1p,4k,14,q,s,2k,7e,8o,i,1i,i,u,10,2q,18,16
u,8,8,6,6,8,g,i,m,3yw,v,13,42,w,u,y,4c,7s,8q,g,1k,s,u,s,2m,14,16
u,8,6,6,8,6,g,i,m,3yo,t,15,4c,y,w,16,40,84,8s,g,1k,q,u,s,2k,16,18
u,6,6,6,6,8,e,g,m,3zc,1n,1v,4m,e,c,s,1g,6e,8s,i,1k,i,u,u,2k,1c,1a
s,8,6,6,6,6,g,e,k,3y6,1p,1n,4m,k,g,w,a,6s,8u,i,1q,m,q,s,26,1i,14
u,6,6,6,6,6,g,g,m,404,1t,1v,4g,c,a,g,1m,6m,8q,i,1m,g,w,w,2g,1e,18
u,8,6,6,6,8,i,g,o,3yw,1n,13,42,12,o,m,3m,7u,8m,i,1k,q,w,u,2o,14,18
s,8,6,6,6,6,g,g,m,404,1j,1t,46,c,c,i,2s,7g,8o,k,1k,k,y,w,2c,1m,18
u,8,6,8,6,6,i,i,o,402,27,1t,3s,16,w,9,3w,7a,8k,i,1k,o,w,w,2q,16,18
u,6,6,6,6,8,e,g,m,406,1n,1v,4e,c,8,g,1k,6q,8q,i,1k,g,w,w,2m,1a,18
u,6,6,6,6,8,g,g,m,3za,1r,19,4m,w,m,i,2o,7g,8o,i,1i,i,w,10,2q,1a,16
u,8,6,6,6,6,g,e,m,3ya,1b,1v,4u,i,o,1k,1s,62,8u,g,1m,i,u,s,2q,1a,1c
u,6,6,6,6,8,g,g,k,3zg,1n,1d,4g,u,k,m,2e,7a,8o,i,1i,i,w,y,2q,1a,16
u,6,6,6,8,6,g,g,m,3yy,11,15,48,m,c,q,32,7q,8o,i,1k,k,w,y,2m,1e,16
s,8,6,6,6,6,g,g,k,3zy,21,1r,4a,i,g,y,2q,7k,8s,i,1o,m,u,u,2a,1k,18
u,8,6,6,6,6,i,g,m,3ys,1b,v,48,q,e,14,3a,82,8o,i,1k,m,w,w,2q,16,1a
u,8,6,6,6,6,i,g,m,3yy,1n,13,4e,y,m,s,34,7w,8o,i,1i,m,w,w,2s,16,18
u,8,6,6,6,6,i,g,m,3yy,11,1h,4e,u,u,1g,3m,88,8s,i,1k,o,u,s,2e,1g,18
s,8,6,6,6,6,g,g,k,404,1x,1r,4a,c,a,i,1s,6m,8q,i,1m,k,u,w,2a,1i,18
s,8,6,6,6,6,g,e,m,3zy,21,1p,48,e,c,m,20,6u,8q,k,1m,m,u,u,2a,1k,16
s,8,6,6,6,6,g,g,k,3ze,1t,1r,4g,g,c,m,1g,6g,8s,i,1o,i,u,u,2a,1i,18
u,8,6,6,6,6,g,e,m,3ya,1b,1v,4u,i,m,1k,1q,62,8u,g,1m,i,u,s,2o,1c,1c
w,8,8,6,6,6,g,i,m,3xy,13,a,34,14,1e,16,2y,5q,8u,14,u,m,s,q,2y,14,18
u,6,6,6,8,6,g,g,m,3za,11,1n,46,g,k,16,3a,7y,8q,i,1m,k,w,u,2e,1k,18
u,6,6,6,6,8,g,g,k,3zm,1h,1h,4c,o,e,o,1u,6y,8o,i,1i,g,w,y,2q,16,18
u,8,6,6,8,6,i,g,o,3yq,15,15,40,y,q,s,3y,7u,8o,i,1k,q,w,s,2o,14,18
u,6,6,6,8,6,g,g,m,3yy,11,13,46,m,c,q,32,7s,8o,i,1k,k,w,y,2m,1e,16
u,8,6,6,6,6,g,i,k,3z4,13,1f,4m,y,y,1g,3m,86,8s,i,1m,o,s,s,2e,1g,16
u,6,6,6,6,8,g,g,k,3zs,19,1n,48,g,c,k,1k,6u,8o,i,1i,g,w,w,2q,18,18
u,6,6,6,6,6,g,e,m,3yc,1n,1r,4q,i,g,y,k,6o,8u,i,1o,k,s,u,26,1i,18
u,8,6,6,6,8,g,g,m,3yu,z,1b,4c,u,u,1c,3q,88,8s,g,1m,o,u,s,2g,1c,1a
w,8,8,6,6,6,g,i,m,3ya,15,3,3c,16,18,s,2w,5k,8u,14,u,k,s,s,2y,12,1a
s,8,6,6,6,6,g,e,m,3zq,23,1n,4a,i,g,w,2i,76,8s,k,1m,o,s,u,2a,1e,18
s,8,6,6,6,6,g,g,k,3zk,1v,1r,4e,g,c,m,1i,6g,8s,i,1m,k,u,u,2a,1i,18
u,8,4,8,6,6,g,e,m,3yo,1f,1x,4s,g,i,18,1m,64,8s,i,1m,i,s,u,2o,1a,1c
s,8,6,6,6,6,g,e,k,3ze,21,1n,4c,i,e,q,1u,66,8s,k,1o,o,q,u,28,16,18
s,8,6,6,6,6,g,e,m,3zw,1z,1p,4a,c,c,k,1y,6m,8q,k,1m,m,u,u,2a,1i,18
u,6,6,6,6,8,e,g,m,3zm,1r,1p,4i,q,s,1e,38,7w,8s,i,1o,o,s,s,2a,1m,18
s,8,6,6,6,6,g,g,k,404,21,1r,48,g,g,u,2s,7o,8s,i,1m,m,u,u,2c,1k,1a
u,6,6,6,6,8,g,e,m,3zc,1l,1v,4m,e,c,u,1k,6e,8s,i,1k,i,s,w,2m,1c,1a
u,6,6,6,6,8,g,e,m,3zu,1r,1t,4c,k,m,1a,34,7y,8s,i,1m,m,u,u,2c,1k,18
u,8,8,6,8,8,i,i,q,402,1r,1f,2s,o,o,2,4g,6s,8i,i,1i,w,y,s,2m,14,16
w,8,6,6,6,6,g,i,m,3y6,13,v,42,y,1a,1c,2g,5w,8u,m,1e,i,u,q,30,14,1a
u,6,6,6,6,8,g,g,k,3z4,11,17,48,m,c,i,2k,7e,8o,i,1i,k,w,y,2q,1a,16
s,8,6,6,6,6,g,g,k,404,21,1r,46,g,c,q,2i,7g,8s,i,1m,m,u,u,2c,1k,18
u,6,6,6,6,6,g,g,m,406,1l,1v,46,c,c,m,2w,7k,8q,i,1m,i,y,w,2c,1o,16
u,6,6,6,6,8,e,g,m,404,1t,1v,4g,c,a,g,1m,6m,8q,i,1m,g,u,w,2k,1c,1a
u,6,6,8,6,6,g,g,k,3zk,1l,1v,4m,c,e,m,1o,6g,8s,g,1k,i,u,u,2o,1c,1a
u,6,6,6,6,8,g,e,m,3zo,1l,1x,4m,c,c,i,1q,6i,8q,i,1k,i,u,u,2q,1a,1a
s,8,6,6,6,6,g,e,m,3zu,23,1p,4a,g,e,q,28,70,8s,i,1o,m,u,u,2a,1e,18
u,6,6,6,6,8,g,g,k,3zs,19,1n,48,g,c,k,1k,6u,8o,i,1i,g,w,y,2o,18,18
s,8,6,6,4,8,e,g,k,3yi,1x,t,3q,k,g,u,12,6g,8u,k,1i,u,q,s,24,1g,18
2o,s,s,10,u,12,32,30,5e,3pm,8o,2l,51,2e,1,q,2m,1z,fm,1,8,4,2,4,i,u,m
2y,s,o,s,s,12,30,3a,58,3qs,n,1i,j,1a,l,o,d,e,f8,10,4,q,4,e,m,c,q
34,u,m,q,q,y,2q,2w,4i,3s8,v,10,z,3,1,8,1g,3g,f4,y,a,o,e,8,s,a,e
30,s,o,q,u,10,32,36,50,3r2,r,g,1,a,4,b,w,1o,f6,10,6,o,a,a,k,c,q
2u,u,o,s,y,12,34,3a,5i,3qa,2a,3g,23,23,k,18,a,1z,fe,i,0,i,g,2,c,k,s
2i,q,u,y,u,14,30,32,58,3qs,84,57,2p,1e,j,u,28,6,fi,3,e,3,6,2,k,q,k
2o,s,s,10,u,12,32,30,5e,3pk,8y,37,4j,2g,h,10,2m,1v,fm,3,a,4,0,6,i,s,m
30,s,o,s,s,10,2w,32,4u,3r2,v,c,l,a,c,1,18,2e,f6,10,a,o,a,8,q,i,m
2m,s,s,10,u,12,32,32,5c,3pe,94,33,4z,2e,2,m,2m,1n,fm,3,a,4,2,4,i,u,m
30,s,o,q,u,10,32,34,4y,3r4,r,e,9,c,6,b,14,20,f4,10,8,o,a,8,m,e,o
32,s,o,q,s,10,2s,2y,4k,3rg,13,e,t,0,2,g,18,4e,f6,y,a,q,c,a,q,e,g
3c,u,o,o,s,w,2o,2u,4k,3sw,b,c,e,9,8,1m,1y,1k,fe,o,e,m,a,e,k,c,m
2o,s,s,10,u,14,30,30,5e,3ps,8u,3n,45,2c,j,12,2k,1r,fk,1,a,2,2,4,k,s,m
2u,u,o,s,y,12,36,3a,5m,3qe,1u,3c,19,2r,1o,7,18,2r,fe,k,1,i,i,9,o,e,u
32,u,m,q,s,10,2s,2w,4i,3ri,11,k,v,1,0,e,1o,44,f6,y,a,q,c,a,q,e,g
3c,w,o,o,s,y,2o,2s,4k,3sm,j,a,k,1,a,1y,24,1k,fc,o,g,k,8,g,i,c,o
2k,s,u,w,u,16,30,30,5a,3qq,8o,5p,2d,1e,z,1c,2c,f,fg,1,c,0,4,2,k,s,k
2w,s,o,q,u,16,36,38,5a,3qa,c,3e,j,c,2z,2g,k,n,fe,s,5,u,e,8,4,c,w
36,u,m,o,s,w,2q,2u,4m,3so,7,y,l,7,0,a,1k,28,f8,u,c,o,e,a,o,6,k
2w,s,o,s,w,14,32,3c,5c,3qc,1a,3i,23,7,z,1q,9,r,fe,o,1,s,0,c,g,c,s
2k,s,s,10,u,12,32,32,5a,3pk,98,47,4b,2g,5,k,2k,x,fm,5,c,2,2,4,k,s,k
32,s,o,q,s,10,2s,2y,4m,3re,11,g,t,2,4,8,1k,3s,f6,y,c,o,c,8,s,e,i
34,s,o,q,q,y,2s,2u,4i,3rw,11,s,x,3,1,e,1g,44,f6,w,c,q,c,a,q,c,c
2s,u,o,w,w,14,32,36,5i,3py,4w,1u,47,9,16,k,1a,2d,fi,8,4,i,2,2,i,o,o
30,u,m,s,s,10,2w,30,4s,3r4,x,e,l,8,8,0,1e,2m,f6,10,a,o,c,6,s,g,k
2q,s,s,y,w,12,32,32,5g,3pq,7o,17,57,1u,w,c,2e,2p,fk,0,8,8,0,2,i,w,m
30,u,o,q,s,10,2u,32,4o,3rc,z,c,t,4,8,6,k,3y,f4,10,c,o,c,6,o,i,k
2m,s,u,y,u,14,30,30,5c,3qc,90,5d,2p,1q,15,1g,2g,x,fg,0,a,0,4,4,k,q,m
2s,u,o,w,y,12,34,34,5i,3pw,5u,12,4f,5,1k,c,1k,2r,fi,6,6,a,6,0,g,u,o
2g,s,q,10,s,14,32,30,56,3qy,7o,4j,39,1k,9,e,24,i,fi,5,i,5,6,4,i,q,k
2o,s,q,10,w,12,32,30,5e,3pg,90,2x,4x,2g,7,s,2m,1r,fm,3,a,4,0,6,i,u,k
2o,s,q,10,w,12,32,30,5e,3pa,90,2d,5h,2m,0,m,2o,1x,fm,1,8,6,0,4,k,s,m
2u,s,o,w,y,12,34,36,5i,3q0,4w,22,3v,13,1c,o,16,2j,fg,a,4,c,a,0,e,q,q
2y,s,o,q,w,12,34,3a,5g,3qq,7,1y,8,x,i,f,18,1b,fa,u,2,k,g,2,k,a,u
32,s,o,q,s,10,2u,30,4k,3re,z,c,t,2,4,c,w,46,f4,10,a,q,c,8,q,g,g
2q,s,q,y,w,12,32,32,5i,3pi,7q,h,5r,1c,1a,a,2g,2n,fm,0,6,a,4,1,m,s,m
32,s,o,q,s,10,2s,2w,4i,3rk,15,g,v,1,0,m,1c,4q,f6,y,a,q,c,a,q,e,e
2u,s,o,u,y,14,34,36,5i,3q2,4g,2c,3n,17,1c,q,w,2d,fi,8,4,i,6,0,c,s,q
30,u,m,s,s,10,2u,30,4s,3r4,x,o,p,e,1,a,1c,30,f6,10,a,o,a,a,q,g,k
2u,s,o,u,w,14,34,38,5g,3q0,3i,2s,3d,h,8,16,s,1p,fi,c,2,m,0,6,k,i,q
3a,s,o,o,s,w,2o,2u,4m,3su,j,s,4,b,6,y,1u,1o,fa,q,e,m,c,c,m,8,m
2k,s,u,y,u,14,30,32,5a,3qm,8w,5r,2b,1i,17,1g,2c,l,fe,0,a,2,2,4,k,q,m
30,s,o,s,s,10,2y,32,4u,3r6,v,e,j,a,4,0,u,2s,f6,y,a,o,a,a,m,g,m
34,u,m,q,q,y,2q,2w,4i,3s8,p,12,z,5,1,8,1i,3a,f6,y,a,o,e,8,s,a,e
2c,s,q,y,u,12,32,30,54,3rs,6e,3h,3r,1i,7,2,1w,12,fg,3,i,5,6,4,g,q,i
30,u,m,s,s,10,2u,2y,4o,3r6,x,o,r,8,1,a,1q,3o,f6,10,a,q,a,a,q,g,g
2q,u,q,y,w,12,32,32,5i,3pw,7o,1b,4z,1m,q,e,2g,2t,fi,0,a,6,2,2,i,u,o
3u,12,u,y,12,1a,3e,3k,5o,3q6,n,p,n,j,q,2s,1e,26,fc,q,e,i,k,c,10,w,12
2w,q,o,q,u,10,32,38,5a,3ss,d,l,7,0,6,k,16,p,f4,16,g,s,a,6,g,e,o
34,s,o,s,s,y,2s,2u,4i,3so,1h,4,l,d,4,1i,28,30,fc,y,g,u,e,g,q,c,i
44,18,12,14,16,1c,3s,3w,6c,3ou,1z,25,1f,19,1m,3e,26,38,fa,i,a,c,u,c,14,10,10
38,u,o,q,s,10,2s,2w,4m,3u0,1h,3,c,t,0,1g,28,2i,fi,s,u,i,e,i,q,e,q
2y,u,m,s,s,10,2w,36,4m,3su,x,6,19,a,d,1,k,3u,f4,14,c,y,a,a,q,g,k
32,u,o,q,s,10,2q,2w,4i,3se,1f,8,n,d,2,1m,26,34,fc,y,g,u,e,g,q,c,i
34,u,m,s,s,y,2q,2w,4k,3su,1j,6,p,d,4,1i,26,2w,fc,y,c,y,e,g,m,e,i
32,s,o,q,s,10,2s,2w,4m,3sc,13,g,13,7,3,12,1w,38,f8,12,a,10,c,e,q,g,g
32,s,o,q,u,10,2q,2u,4k,3s8,17,k,z,9,5,1e,24,3i,fa,10,c,y,e,e,q,c,k
3c,u,o,q,u,y,2u,2y,4o,3u2,11,b,u,j,2,1o,1s,1w,fi,q,s,g,e,k,o,k,s
30,s,o,q,u,10,2s,2w,4k,3so,z,o,1h,7,b,q,26,3u,f6,12,8,12,c,c,s,g,e
2q,q,o,o,y,16,34,36,5g,3rq,2,a,1w,31,0,1k,2a,23,fa,18,e,c,8,3,c,c,m
36,u,o,q,u,y,2s,2w,4k,3ti,1n,1,4,l,2,1o,2c,2k,fi,u,u,i,e,i,q,c,o
36,u,o,q,s,10,2q,2w,4m,3u0,1d,3,2,p,1,14,28,2g,fg,u,o,m,e,g,q,a,q
2q,q,o,o,y,16,34,36,5g,3ru,8,9,20,2f,j,1s,20,1v,fa,16,k,8,a,1,a,c,m
34,u,o,q,u,y,2q,2w,4i,3sy,1n,5,3,h,8,1m,2g,2k,fe,y,q,k,e,i,o,a,o
2s,q,o,s,w,14,36,34,5k,3rw,5,8,w,1h,a,m,2s,2n,f8,16,g,c,a,0,c,a,q
2y,s,o,q,s,10,2y,32,4s,3sg,h,4,11,0,7,c,18,2q,f6,12,c,10,c,a,o,e,k
32,u,o,q,s,10,2q,2u,4k,3so,1f,i,11,b,3,1a,24,3c,fa,y,a,10,e,e,q,c,i
34,u,m,s,s,y,2s,2u,4k,3t2,1j,8,n,f,2,1a,28,2u,fc,y,e,w,e,g,o,a,k
2w,q,o,s,s,12,36,36,5c,3sg,r,9,0,y,b,3,1u,11,f4,18,m,e,8,8,e,c,o
3s,10,u,w,y,16,3g,3k,5m,3qo,b,h,r,u,16,u,m,18,fc,q,e,m,g,c,w,u,14
3o,y,q,u,w,14,34,3a,56,3ro,5,l,i,q,2,24,16,1g,fe,q,i,k,e,g,s,s,12
2u,s,o,q,s,12,32,36,58,3sm,3,p,7,6,7,m,18,7,f6,14,e,u,a,8,g,e,k
32,s,o,s,s,10,2q,2u,4i,3s8,15,o,15,b,9,18,24,40,f8,10,a,10,e,e,s,e,g
3c,u,o,q,u,y,2u,2y,4o,3u0,15,b,s,l,4,1m,1y,24,fk,q,s,g,e,k,q,k,s
2w,s,o,q,u,10,30,38,54,3sw,r,9,f,3,e,2,y,i,f4,16,g,q,a,8,i,g,o
3e,u,q,q,u,10,2u,30,4s,3tk,x,l,w,5,a,24,1u,1u,fk,o,u,g,e,k,q,o,w
36,s,o,q,u,y,2q,2w,4k,3ti,1h,1,9,j,2,18,2c,2m,fe,w,k,q,e,i,o,a,m
2q,s,o,o,u,16,34,38,5i,3s6,c,17,1g,3,1l,2a,16,2d,f8,14,q,c,8,4,6,c,o
30,s,o,q,u,10,2s,2y,4k,3s6,t,o,19,3,f,w,1m,3s,f8,12,a,10,c,c,s,c,k
3a,u,q,q,s,10,2s,2y,4o,3u6,1b,7,q,t,4,1g,26,2a,fk,q,u,g,e,k,q,k,q
2q,q,o,s,10,14,34,34,5i,3r0,1a,22,h,3r,1y,q,2k,2j,fc,10,0,c,g,d,m,k,k
3e,u,o,q,s,10,2s,30,4q,3ty,v,f,10,h,8,1y,1m,1m,fi,o,s,g,e,k,m,m,u
32,u,m,q,s,10,2q,2u,4k,3su,1b,o,1d,b,7,10,1u,3q,f6,10,a,y,e,c,q,g,c
2q,q,o,q,12,12,36,32,5k,3r6,y,1m,9,3n,20,q,2u,2l,fa,14,2,i,a,d,m,g,m
34,s,o,q,s,y,2q,2u,4m,3ta,1f,i,13,d,4,u,20,2s,fa,y,a,y,c,e,o,c,i
2y,u,m,s,s,10,2u,30,4q,3so,x,8,15,6,3,g,1q,30,f4,14,c,u,c,c,o,i,i
30,s,m,s,s,10,2u,30,4o,3s6,v,e,z,a,4,2,12,2q,f6,12,c,s,c,8,s,g,m
30,s,o,q,u,y,2u,2y,4k,3sm,z,c,11,9,1,i,1e,40,f4,14,a,w,c,c,o,i,i
2q,q,q,q,10,14,34,34,5k,3r4,y,1w,4,3p,1g,10,2c,2p,fa,12,2,a,i,b,m,g,o
2y,u,m,s,s,y,2y,30,4q,3sa,v,c,p,1,a,1,1e,2q,f4,14,c,s,a,c,m,i,k
32,u,m,q,s,10,2q,2u,4i,3sw,1b,m,1b,b,0,s,1u,3i,f6,10,a,y,c,c,q,e,e
32,u,m,s,s,y,2q,2w,4i,3su,1b,i,19,9,7,12,20,3i,f8,10,8,10,c,e,q,e,e
32,u,m,s,s,y,2q,2u,4i,3t0,1d,k,19,b,0,u,1w,3c,f8,y,a,y,e,c,q,c,g
30,s,o,q,s,10,2u,2y,4o,3sq,z,c,19,6,7,g,1w,3a,f4,14,c,w,a,c,q,k,e
32,u,o,q,s,10,2q,2u,4k,3su,1h,g,13,b,0,16,24,3a,fa,y,a,10,c,e,q,c,i
2y,s,o,q,s,10,2y,32,4w,3rw,r,c,j,i,a,b,1e,1a,f6,14,c,o,a,a,q,g,m
2u,q,o,s,y,12,36,34,5k,3qo,1a,2u,1p,1l,m,q,22,2v,fc,w,1,u,4,4,g,c,q
30,u,m,s,s,10,2s,2w,4k,3ss,13,k,1f,3,d,k,1y,46,f6,12,8,10,c,c,q,g,i
32,u,m,s,s,y,2q,2u,4k,3sy,1d,m,1b,b,2,w,1u,3a,f8,y,a,10,c,c,q,e,e
36,u,o,q,s,y,2s,2w,4k,3u2,1b,1,0,n,1,10,26,2c,fg,s,q,m,c,i,o,a,o
3g,w,q,q,u,10,2u,32,4u,3t4,f,p,y,a,a,2m,1c,1c,fi,o,q,g,e,k,o,o,y
30,u,m,s,s,y,2s,2w,4k,3so,15,m,1d,7,3,m,1q,3q,f6,10,a,y,c,c,q,i,e
2u,s,o,q,w,16,34,3a,5e,3qk,w,3m,1f,1n,11,2g,8,1j,fc,w,7,w,c,6,8,e,s
34,s,o,q,s,10,2q,2u,4k,3t4,1f,e,13,b,0,12,22,30,fa,y,a,10,c,e,q,c,g
34,u,o,q,s,y,2q,2w,4k,3tc,1h,6,r,d,4,12,26,2q,fc,w,e,w,e,g,o,a,k
2w,s,o,q,s,14,32,38,5c,3s8,p,f,e,u,v,1a,c,11,f4,18,m,c,8,a,e,g,o
32,s,o,q,s,10,2s,2u,4i,3ss,19,m,1d,7,9,w,24,3u,f6,12,8,10,c,e,q,g,e
2u,s,o,q,u,14,34,38,5i,3ru,d,4,10,h,n,1o,u,29,f6,18,g,8,e,4,e,e,o
32,s,o,q,s,10,2s,2u,4i,3sq,1b,o,1d,7,7,w,26,3w,f8,10,8,10,c,e,q,e,i
2m,q,o,s,12,10,34,32,5e,3qu,1k,2c,1b,4d,3s,b,36,1h,fc,12,1,a,e,l,o,o,i
2y,u,m,s,s,10,2w,30,4u,3sq,z,8,1b,i,5,e,1c,2g,f4,14,e,u,a,a,q,g,i
2y,s,o,q,s,10,30,34,4y,3se,v,3,b,m,b,o,y,1k,f4,16,g,m,a,a,k,i,k
2o,q,o,u,10,12,34,32,5e,3q6,3y,22,3z,1n,2i,b,34,1v,fg,m,1,i,1,3,q,m,k
2m,s,o,10,w,12,32,32,5e,3pg,7k,b,7f,2g,1o,7,2s,1n,fm,2,8,q,f,0,i,w,k
2y,s,o,q,u,y,30,30,4u,3s4,t,a,f,4,e,d,1m,1u,f4,16,c,o,c,a,m,g,m
2s,q,o,u,10,12,34,34,5i,3py,46,2g,4x,n,24,0,2u,33,fi,g,0,12,h,0,u,e,o
36,s,o,q,s,y,2q,2w,4k,3to,1f,4,h,h,0,y,28,2i,fc,w,i,s,e,g,o,a,k
34,s,o,q,s,y,2q,2w,4k,3t8,1d,e,13,b,4,y,1y,2s,fa,y,a,10,c,e,o,c,g
36,u,o,q,s,y,2s,2w,4k,3u2,19,1,0,n,3,10,26,2c,fg,s,o,o,c,g,o,c,o
2w,s,o,q,u,10,34,34,58,3rk,l,o,8,c,a,p,2a,x,f6,16,c,g,c,a,i,a,q
34,s,o,q,q,y,2q,2w,4i,3rw,r,u,11,5,1,e,1k,3u,f6,10,8,q,e,8,s,c,c
30,s,o,q,s,10,2w,30,4o,3r0,p,s,v,m,1,c,10,3k,f6,12,8,q,a,a,q,i,m
38,u,o,o,s,y,2q,2u,4m,3ui,11,5,0,l,0,14,1y,20,fe,q,i,s,a,e,m,c,m
3o,w,s,s,u,12,2w,32,52,3rw,3,h,6,w,q,3q,1u,a,fc,o,i,k,e,g,m,q,10
3c,u,o,o,s,y,2q,2u,4m,3ug,z,b,g,n,6,1m,1y,1y,fg,o,i,q,a,g,k,g,o
3o,w,s,s,u,10,2u,2y,4i,3r6,a,b,0,i,o,3a,2q,22,fc,m,e,c,g,g,m,k,q
3a,s,o,q,s,y,2o,2w,4m,3ug,11,5,4,n,0,16,20,20,fe,q,k,q,a,g,m,c,o
3i,u,q,q,s,y,2q,2u,4g,3sa,0,d,e,a,e,2o,2i,1y,fg,k,g,k,a,g,k,g,q
48,16,16,12,12,1c,3q,3u,6k,3oo,z,3f,15,6,1i,3s,1u,1k,f6,i,a,a,g,o,y,y,10
3c,u,o,o,u,y,2q,2u,4m,3ua,x,b,g,l,6,1k,22,1w,fg,o,m,m,c,g,k,g,q
32,u,m,q,s,10,2q,2w,4i,3ri,v,q,z,3,5,e,1w,4c,f6,10,8,s,e,a,q,c,g
2m,q,q,10,u,12,34,32,5c,3pe,8a,1p,6r,30,10,3,2m,17,fm,0,8,e,7,4,g,w,k
34,u,m,q,s,y,2q,2u,4m,3to,1b,c,x,d,2,m,1y,2m,fa,w,c,y,c,c,o,a,i
2y,s,o,q,s,12,2w,32,4u,3ra,j,q,j,u,f,q,w,28,f6,14,c,k,c,a,m,i,o
3i,w,q,q,u,y,2q,2s,4i,3ro,3,h,g,m,m,2y,2q,22,fe,m,e,i,a,e,k,g,q
3s,y,s,u,w,12,34,3c,52,3r6,5,1,7,e,16,1w,g,1k,f8,o,e,e,k,c,q,o,s
3s,10,s,w,w,12,32,38,4u,3q4,f,j,3,u,k,3c,12,y,fc,k,c,e,a,e,i,k,m
3c,w,o,o,s,y,2o,2u,4i,3ta,9,1,e,d,a,20,20,1q,fg,m,g,k,c,g,i,g,o
3a,u,o,o,s,10,2q,2w,4m,3ua,11,7,g,n,0,1a,20,1y,fg,q,o,m,c,g,m,g,q
34,u,m,q,s,y,2q,2u,4k,3tc,1b,e,11,b,2,q,1w,2w,f8,y,a,y,c,e,o,a,g
3g,u,q,q,s,y,2q,2u,4k,3tk,d,l,c,5,c,2c,2c,20,fe,m,i,k,c,i,k,i,q
3g,u,q,q,s,y,2o,2s,4k,3sc,1,7,i,4,e,2g,2c,1s,fg,k,g,k,a,g,i,g,o
32,s,o,q,s,y,2s,2u,4g,3sk,11,m,1d,5,b,i,20,4a,f6,10,a,w,c,c,q,e,g
4a,14,10,10,12,1c,3o,3o,5k,3pi,z,11,3,9,9,1g,a,1q,f2,k,c,6,e,m,u,q,s
2u,s,o,s,u,14,32,3a,5a,3qm,m,2i,x,t,f,1s,4,11,fc,w,2,m,e,4,g,e,s
36,u,o,o,u,y,2q,2w,4m,3u2,19,1,3,l,1,u,26,2c,fe,u,m,o,e,g,o,a,o
2w,s,o,q,u,14,34,36,56,3q8,u,20,j,5,b,m,16,h,fc,u,2,s,a,4,e,e,w
2q,q,q,y,y,10,34,32,5i,3p6,7a,m,6z,12,2e,2,2k,2t,fm,4,4,q,5,5,k,u,m
2u,s,o,s,y,12,34,36,5i,3ps,3o,2i,2r,21,1u,g,1m,2t,fi,e,2,m,e,9,o,g,q
2s,s,o,u,10,12,32,36,5k,3pi,4k,2k,3f,2r,2s,8,1q,39,fi,e,0,k,g,d,q,k,q
2s,s,o,u,y,12,34,36,5i,3po,3s,2m,31,23,1q,q,1k,2l,fi,g,0,o,e,b,m,k,q
3y,10,u,y,y,16,3e,3c,56,3py,j,2,f,6,4,1i,16,u,f8,o,a,c,g,c,m,o,q
36,s,o,q,s,y,2q,2w,4k,3tu,1b,4,f,h,1,u,26,2e,fe,u,i,s,e,e,o,a,m
32,s,m,s,s,y,2s,2u,4i,3sa,z,k,17,1,7,g,20,44,f6,12,8,w,c,a,s,g,e
3y,12,w,y,y,18,3e,3g,5c,3py,n,2,t,2,a,1c,q,w,f8,m,c,a,g,c,o,q,o
3q,y,u,u,u,12,2y,32,4q,3qe,b,h,0,u,o,3g,1w,1k,fc,m,c,g,a,e,k,i,q
36,s,m,q,q,y,2q,2u,4k,3sk,d,w,z,5,1,6,1k,2u,f8,y,8,q,g,8,q,a,e
32,u,m,q,s,y,2q,2w,4i,3s6,x,o,17,5,5,m,1m,42,f6,10,8,w,c,a,s,e,c
2g,s,s,y,u,12,34,30,58,3qg,84,43,41,20,3,8,2c,4,fk,5,g,3,4,6,i,q,k
2q,q,q,y,y,10,34,32,5i,3p4,7w,f,63,1e,1m,6,2i,2p,fm,2,4,i,1,1,k,u,m
2q,s,o,10,w,12,32,32,5i,3pa,7e,g,5x,i,20,6,2i,37,fm,2,6,e,2,3,k,u,o
36,s,m,q,s,y,2q,2w,4k,3ts,19,a,t,f,0,o,22,2g,fa,w,c,y,c,c,o,a,k
32,u,m,q,s,10,2q,2w,4i,3ru,13,m,11,0,0,a,1i,46,f4,w,c,q,c,a,q,g,a
38,u,m,q,s,w,2q,2u,4k,3uc,t,0,5,j,2,10,1w,1u,fc,s,e,s,a,e,m,c,k
36,s,o,q,s,y,2q,2u,4m,3ty,19,8,j,j,0,q,24,2c,fc,w,c,y,c,e,m,a,m
3e,u,q,o,u,y,2s,2y,4o,3ts,n,l,w,9,8,22,1w,1i,fg,o,s,g,c,k,k,m,u
3w,10,w,w,y,16,3a,3c,52,3pk,l,x,2,e,2,2u,y,u,fc,k,a,c,c,c,k,m,m
3c,u,o,o,s,y,2o,2w,4k,3uc,t,b,8,h,4,1m,20,1w,fe,o,i,q,a,g,k,e,o
42,16,w,y,12,1a,3g,3k,5a,3oy,13,j,7,7,0,2c,k,w,fa,i,c,8,a,g,m,q,o
36,s,o,q,q,y,2q,2u,4m,3u2,13,6,n,h,0,k,1y,2c,fa,w,c,w,c,c,m,a,k
32,s,o,q,s,y,2s,2u,4k,3rs,t,q,15,3,5,i,1o,48,f6,10,8,u,e,8,s,e,e
34,s,o,q,s,y,2q,2u,4k,3t4,17,g,15,b,0,o,1s,38,f8,y,8,y,e,a,q,c,e
44,14,12,12,14,1a,3m,3q,66,3p2,f,1l,1v,f,w,38,1a,10,f6,m,8,a,e,m,u,u,w
3s,y,u,u,w,12,34,36,4y,3r4,3,3,l,m,u,20,1k,18,f8,o,c,g,i,c,o,o,q
48,16,14,10,14,1e,3q,3u,6c,3os,b,37,3,0,f,2s,16,16,f4,k,a,8,e,o,w,w,u
36,s,o,o,s,w,2q,2u,4m,3sq,19,12,h,b,0,a,1q,2q,f0,w,c,e,m,8,s,a,g
3o,y,w,w,y,12,36,38,4u,3qi,p,17,0,n,12,1s,2m,1s,ey,i,e,8,8,c,q,g,u
3c,w,o,q,s,10,2o,2u,4i,3t0,19,h,a,f,6,1u,30,2i,f0,m,g,a,i,c,o,a,u
3m,10,u,y,w,14,36,36,4w,3qu,v,r,19,2,10,s,3c,28,ew,i,c,8,8,c,s,c,10
3a,w,o,o,s,y,2o,2u,4k,3su,1b,a,o,9,c,1o,2e,1s,f6,m,g,c,i,c,m,a,q
3i,y,s,s,u,10,2u,2u,4k,3rc,f,x,c,1,14,2w,3a,2g,f6,k,e,c,a,g,m,c,s
3c,u,q,q,s,y,2o,2u,4i,3ta,1b,f,2,r,8,1c,2q,2u,ey,m,g,c,i,c,q,a,q
2u,u,o,s,y,14,34,38,5o,3qk,2s,2u,1x,3d,2m,b,1m,3h,fc,g,0,i,i,9,m,i,s
2u,u,o,s,y,14,36,36,5o,3qk,2g,30,1j,3d,2a,7,1k,3j,fc,g,0,k,i,b,o,g,u
2m,s,s,10,u,14,30,30,5a,3qs,8m,57,2r,1q,17,1g,2a,x,fc,2,a,0,4,4,k,q,m
34,u,m,q,s,w,2q,2w,4k,3sm,1b,s,p,f,0,8,1q,3g,ey,w,c,e,m,8,u,a,g
30,s,o,s,s,10,30,32,4s,3rg,r,e,19,o,0,6,1e,32,f2,10,6,s,8,8,o,i,o
30,u,o,q,s,10,2w,30,4m,3ri,z,8,11,c,6,8,y,42,f2,10,a,q,a,8,q,i,i
38,w,o,q,q,y,2o,2u,4k,3te,1f,7,6,p,8,y,2g,2m,ey,q,e,c,k,a,q,c,m
38,u,o,o,s,y,2o,2u,4k,3sw,15,u,a,b,6,12,20,1u,f6,q,e,e,k,a,o,8,m
2o,s,s,10,u,14,30,32,5c,3qo,80,2p,4l,26,l,u,2c,1t,fc,2,8,4,2,4,k,q,o
2o,u,q,10,u,14,30,32,5e,3qk,80,2f,4v,2a,h,s,2c,1x,fc,2,a,2,2,4,k,s,o
2y,u,o,s,s,10,32,36,4u,3rg,r,s,1b,10,9,a,o,2u,f2,10,6,o,6,c,m,i,s
38,u,o,q,q,y,2o,2u,4k,3ta,1f,0,c,n,6,y,2c,2c,f0,q,e,c,k,a,q,a,m
36,u,m,q,s,w,2q,2u,4k,3t8,1b,4,j,l,4,c,1w,36,ew,s,e,e,k,a,s,c,i
3k,y,s,w,u,12,30,2y,4m,3re,l,x,t,2,w,1w,3w,2q,ey,k,e,8,8,g,q,a,y
32,s,o,q,s,10,2s,2u,4k,3sq,15,a,1n,5,9,8,20,4g,eu,y,c,k,g,8,u,e,k
2u,u,m,u,10,12,34,38,5o,3qm,34,32,2f,39,2c,0,16,3b,fa,e,2,g,e,7,o,k,s
2w,u,o,q,w,12,38,36,5i,3qq,6,22,c,1n,y,p,20,23,fa,s,0,m,g,0,i,a,w
2o,s,s,y,w,12,32,30,5e,3qa,8i,3z,3p,24,p,14,2g,1l,fg,0,a,2,2,4,k,s,m
36,s,o,q,s,w,2q,2u,4k,3t6,1b,6,z,p,4,8,1u,3u,ew,s,e,e,k,a,u,c,g
32,u,m,s,s,y,2q,2w,4i,3ss,15,8,1h,d,3,6,1y,4i,eu,w,e,i,g,a,u,e,i
2s,u,o,w,y,12,34,36,5m,3q2,5e,1s,45,19,20,4,1m,39,fg,6,6,c,8,5,m,q,q
30,s,o,s,s,10,2w,30,4q,3s2,x,g,1v,m,5,e,16,3g,ew,10,a,o,a,a,s,g,o
2w,u,o,s,u,12,36,38,5c,3r0,a,1k,b,f,2,3,1m,z,f6,u,0,o,a,8,g,c,y
2u,s,o,w,y,12,34,38,5m,3qs,3g,2s,3f,15,12,g,u,2z,fa,c,4,e,1,8,c,w,s
38,u,o,o,s,y,2o,2u,4k,3t4,1f,a,a,l,6,y,2a,28,f0,q,e,c,m,a,q,a,k
34,u,m,q,q,y,2q,2w,4i,3sc,13,y,v,3,1,8,1i,3i,f2,w,c,k,i,8,s,a,e
2m,s,u,y,u,14,30,32,5a,3qy,8m,5f,2l,1k,19,1i,2a,x,fa,2,a,2,2,4,m,q,m
2y,s,o,s,u,10,32,3a,4y,3ri,r,16,1h,16,9,i,1,1k,f0,12,2,q,4,e,k,i,y
3a,w,o,q,s,y,2o,2u,4k,3t4,1d,b,g,j,8,1k,2u,2c,f0,o,e,c,i,c,o,a,s
38,u,o,q,q,y,2o,2u,4k,3te,1d,3,3,p,6,o,28,2s,ey,q,e,c,m,8,s,a,m
3e,w,s,q,s,10,2q,2u,4g,3sa,11,p,6,c,g,2o,3e,2e,f2,m,g,a,a,i,o,a,u
2o,s,s,10,u,12,32,30,5e,3qa,8g,3v,3r,26,p,12,2g,1l,fg,0,a,2,2,4,k,q,o
30,s,o,s,s,10,2y,34,4u,3r8,t,a,j,c,6,1,10,2q,f4,10,8,q,8,a,m,i,k
3e,w,s,q,u,10,2q,2s,4i,3se,z,v,4,2,c,2k,3q,2o,f0,m,g,a,c,e,q,a,u
2y,u,o,q,u,10,32,38,4u,3r8,p,i,13,y,e,3,e,2m,f2,12,6,q,6,8,m,g,w
32,s,o,q,s,10,2q,2w,4i,3sk,15,8,1f,b,3,8,1y,4i,eu,y,c,i,i,a,s,e,i
38,u,o,o,s,w,2o,2u,4m,3su,x,w,4,b,6,w,1y,1u,f6,s,e,g,i,a,o,8,m
30,s,o,s,s,10,2w,30,4s,3s4,v,i,21,s,9,g,16,36,ew,10,a,q,8,a,q,i,o
2s,s,q,y,w,12,34,32,5k,3qm,60,w,5n,u,1g,1,1s,2z,fa,8,6,a,0,2,i,w,o
2w,s,o,s,y,12,38,36,5o,3qi,1w,2s,x,2p,1s,j,22,3j,fc,k,0,i,i,5,k,e,u
38,u,o,q,q,y,2o,2u,4k,3t2,1f,c,e,j,6,12,2c,22,f2,q,e,c,k,a,q,8,o
34,s,o,q,s,y,2q,2u,4k,3si,19,g,z,f,4,6,1s,44,ey,w,c,g,k,8,s,e,g
2w,u,o,q,u,16,36,3c,5a,3r8,f,32,8,c,3r,2q,2,t,f4,w,3,g,4,s,b,w,10
2q,o,o,o,q,u,2g,2m,4e,3oc,3w,k,26,1m,10,m,29,17,gq,1i,c,a,8,c,u,1m,i
3m,14,y,12,14,1g,4o,4w,7o,3ng,19,6,1u,1k,o,1v,6,2a,fq,w,10,e,e,g,1k,16,1w
3o,16,y,14,1a,1m,52,5i,8a,3o4,1d,b,h,2,5,l,r,4m,fk,w,y,i,i,k,1o,1g,1w
38,12,u,w,y,16,3i,3o,60,3qg,2w,6,o,u,g,2d,15,j,fw,m,u,a,a,c,w,g,u
3k,14,y,10,14,1g,4g,4q,7i,3no,1f,s,1m,u,12,1l,1,18,fu,14,s,c,g,g,1c,14,2c
38,12,u,w,y,16,3i,3q,5w,3n2,4u,1e,o,12,i,2b,17,j,gg,g,i,c,8,c,w,i,w
3m,16,10,14,18,1o,52,5i,88,3os,1x,l,b,7,5,l,j,52,fk,w,y,k,i,i,1q,1e,1w
3a,12,w,y,10,1a,3o,3y,6g,3os,x,22,28,1k,c,1,15,39,g2,1g,k,8,c,g,w,u,2s
3c,12,w,y,10,18,3k,3s,66,3ng,22,3i,12,i,e,11,z,1j,g6,14,a,8,e,e,w,o,14
2e,m,i,k,k,q,1y,24,3i,3qk,1y,3a,g,2q,4,s,1l,17,g6,1w,g,6,g,e,14,1i,o
36,12,u,u,y,16,3g,3o,5w,3mm,4u,1y,u,w,g,2d,19,h,gk,g,e,c,8,c,u,g,u
3k,14,y,10,14,1g,4g,4q,7k,3ns,1j,s,1i,w,14,1p,3,16,fu,14,q,c,i,e,1c,14,2e
34,10,u,u,y,14,3e,3m,5q,3ne,1q,38,1w,o,m,1p,19,p,ga,16,8,a,8,e,u,g,u
3a,12,u,y,y,18,3i,3q,60,3ng,1y,36,16,10,k,1t,11,v,g4,16,a,8,c,c,w,k,y
2u,s,q,q,s,y,2q,30,4w,3og,28,1g,2i,1e,s,2,1d,1l,gg,1m,a,8,8,e,u,m,i
3g,12,y,12,12,1e,44,4g,76,3oc,1p,1e,q,1c,14,l,v,1n,fy,1e,i,a,g,g,y,12,2w
3c,10,u,y,10,1e,42,4e,78,3pc,1l,u,u,18,b,1l,19,16,fw,1e,s,i,i,i,1m,26,y
3i,12,w,10,14,1i,4g,4u,7s,3om,1j,2,1e,m,2,1h,1p,2o,fs,12,w,i,g,k,1g,28,y
32,w,u,s,w,14,36,3g,5k,3o8,g,30,2u,g,m,11,17,z,g8,1e,a,a,6,e,w,e,q
3o,16,y,12,16,1m,4w,58,88,3ns,1d,5,u,u,9,17,b,2u,fm,w,y,i,g,i,1m,1c,1w
38,12,u,w,y,16,3g,3o,5w,3qq,3i,d,i,m,i,21,19,j,fy,e,12,a,a,e,w,g,u
3i,12,w,y,14,1e,46,4k,7c,3pc,1t,a,1c,k,m,t,n,6,fu,1a,q,c,g,e,16,14,2g
3e,12,w,10,12,1e,40,4c,74,3oq,1l,1i,u,w,10,f,19,19,fy,1g,m,c,i,e,12,1c,2k
3e,10,y,10,12,1c,3y,48,6u,3pg,2b,y,1e,1a,18,7,z,2n,g0,1e,m,8,c,e,u,y,2s
32,u,s,q,u,10,2w,32,4y,3om,1w,28,2w,18,s,7,25,f,ge,1q,c,g,c,k,1k,1k,12
3a,10,w,y,10,18,3s,42,6u,3pa,1d,18,14,1o,o,7,1j,1n,fy,1k,q,c,i,e,10,1o,26
2y,y,q,u,u,12,34,3e,5g,3lu,4q,2w,1g,o,m,1l,1h,r,gu,o,4,g,6,e,s,e,m
3m,16,y,14,1a,1o,52,5g,86,3p8,25,t,b,7,7,n,l,5i,fm,u,y,k,i,k,1q,1e,1u
2k,o,m,m,o,s,2a,2g,44,3om,3q,1m,2i,16,w,2,21,17,gi,1k,8,6,i,c,u,18,u
36,y,u,w,w,16,3g,3m,62,3pa,h,1c,2k,1q,o,7,13,2b,g2,1o,o,c,e,e,18,14,2a
30,y,s,u,w,12,3c,3i,5o,3me,50,2k,10,k,c,29,1d,j,go,g,8,g,4,e,q,e,q
3c,10,y,y,12,18,3o,3y,6c,3os,1d,20,2y,1a,o,r,11,3b,g2,1e,e,4,c,e,u,s,2e
38,10,w,u,10,16,3g,3o,5y,3nw,m,3g,1s,y,k,1l,13,v,g6,1a,8,8,a,e,u,k,w
34,10,u,u,w,16,3c,3m,5q,3n2,2c,34,1s,o,o,1t,1b,n,ge,10,a,a,8,c,w,g,s
38,10,u,w,y,14,3c,3k,5u,3mo,4q,2u,w,e,3,n,17,1f,go,g,g,e,c,g,w,i,u
2e,k,e,g,m,o,1o,1o,2q,3sq,2a,2y,8,1v,y,3g,2a,12,gg,24,w,q,10,8,1y,16,1w
3c,12,y,y,10,1a,3o,3w,6c,3o6,1,30,28,y,6,h,15,2z,g2,1c,c,6,e,g,u,u,2a
3g,12,w,y,14,1e,44,4i,7c,3ok,1l,16,u,s,16,r,1f,3,fw,1c,q,c,k,g,14,1m,26
3i,14,w,y,16,1i,4a,4o,7c,3oe,1t,s,1o,6,t,7,6,1e,fu,1c,m,a,i,i,14,14,2q
3a,12,w,y,y,18,3i,3q,62,3n0,2q,38,1g,g,4,n,11,2d,gc,10,a,a,c,g,w,o,1m
3c,12,y,y,12,18,3q,3w,6c,3o6,b,2q,2e,10,i,t,13,2v,g2,1c,c,6,c,e,w,s,28
2k,o,k,o,m,s,28,2e,40,3ou,3y,1e,1o,1s,s,u,1p,29,gm,1k,e,6,e,c,w,1c,12
2o,m,k,m,q,u,26,2a,3s,3q6,3w,a,16,d,1y,1m,d,j,gm,1s,s,k,8,6,1u,1g,q
36,12,u,u,y,16,3g,3o,5y,3q4,2m,q,w,u,a,2f,15,j,g2,m,m,c,8,c,u,g,s
2k,m,i,k,o,s,1y,22,3e,3qy,3s,y,10,1f,1m,2o,k,3,gk,1y,s,o,k,4,1w,1c,18
22,g,c,c,c,i,1g,18,1u,3uq,1s,2w,32,n,a,1n,4y,5i,g4,2e,14,u,2a,14,1a,1q,2s
36,10,u,u,y,16,3g,3m,5y,3oy,34,1e,10,u,8,2f,17,j,g6,o,i,a,8,c,s,g,u
38,12,w,w,y,16,3i,3q,5y,3n6,3y,1w,w,10,m,27,15,n,g8,s,i,8,a,c,y,i,w
3g,14,w,12,14,1e,44,4i,76,3o2,1j,1k,12,u,16,p,r,17,fy,1c,k,a,g,g,10,12,2w
2k,o,k,o,m,s,26,2a,3w,3p4,42,m,1k,1u,14,s,1b,1z,gq,1m,g,8,c,a,18,1k,y
26,i,e,e,i,m,1g,1g,2a,3to,10,3s,1m,29,p,46,2y,1s,g8,28,w,q,14,u,1m,14,2s
3a,12,w,y,10,18,3k,3s,64,3nu,u,3c,1k,w,k,1h,x,1f,g4,18,a,8,a,e,w,o,16
38,10,u,u,y,16,3a,3k,5s,3mu,3a,3k,1m,c,3,9,1f,21,gk,w,a,c,c,g,y,q,1a
3c,12,w,y,14,1c,3w,48,6u,3py,2d,u,1c,1,1i,4,x,2h,fy,1i,m,8,g,c,u,y,2s
2m,q,o,m,o,w,2g,2q,4m,3nq,4u,1q,2o,g,i,r,r,2j,gg,18,8,a,g,a,s,g,s
38,12,u,w,y,16,3i,3q,5w,3n0,4u,1g,o,12,i,2b,17,h,gg,g,i,c,8,c,w,i,w
34,u,s,u,u,12,36,3g,5m,3pq,1,y,2c,1k,1k,1h,r,f,g2,1u,w,e,c,g,28,12,1i
3a,12,u,y,y,16,3i,3q,5y,3ni,4m,14,o,10,m,27,13,p,g6,k,q,a,a,c,y,i,y
3e,12,y,10,10,1c,3u,42,6o,3oa,t,20,24,1i,m,f,1b,37,g0,1e,g,6,e,g,u,y,2q
2s,o,m,o,q,w,2e,2k,46,3q2,30,g,y,8,1m,1w,1j,l,gm,1o,s,i,6,8,1c,1q,q
36,12,s,w,y,16,3g,3o,5y,3rc,1s,o,q,q,4,2f,15,j,fw,q,c,m,8,c,s,g,u
36,w,s,u,12,14,3e,3m,5w,3qq,1h,1c,g,8,24,o,h,37,g4,1m,s,a,8,a,q,k,2c
38,12,w,y,y,18,3m,3u,68,3pc,1r,1g,2e,1u,10,n,x,2z,g4,1e,k,4,a,a,u,q,24
2m,m,i,o,o,q,24,26,3o,3q0,3w,q,m,o,26,1w,1p,5,gm,1u,o,g,a,4,1e,1y,q
2c,m,g,m,k,o,1u,1w,38,3ra,1s,2y,h,2e,1a,g,2,13,ga,1y,m,8,g,e,1u,12,14
3c,12,w,y,10,1a,3m,3u,6a,3o8,3,36,2a,y,0,9,17,31,g2,1e,c,6,e,g,u,u,2a
3i,14,w,10,16,1g,4k,4y,7w,3oi,1j,0,1c,k,c,1h,1p,2q,fs,y,y,i,g,k,1i,24,10
38,12,w,w,y,14,3i,3q,5y,3r4,2y,d,i,q,m,2b,15,l,fw,g,10,a,a,c,w,i,u
2u,u,q,q,u,10,30,3a,5e,3lu,6c,2k,s,u,a,1x,1r,v,gs,g,4,k,6,c,q,e,i
3q,14,y,14,1a,1o,52,5i,86,3o2,r,9,v,l,9,f,3,5c,fi,w,y,i,k,k,1q,10,2a
3o,16,y,14,1a,1m,52,5i,8a,3oe,1l,f,f,7,5,j,j,4u,fk,w,y,i,i,k,1q,1c,1y
3a,10,y,y,10,18,3m,3w,6a,3p6,1p,1m,2m,1m,w,n,z,31,g4,1e,g,6,a,c,u,q,26
3a,10,w,w,y,18,3e,3o,5w,3mc,4s,2o,u,g,6,x,15,1l,gm,k,e,a,c,g,w,k,14
38,12,u,w,y,16,3g,3q,5y,3rq,2a,9,i,s,e,2d,13,l,fu,i,s,g,a,c,u,g,u
3k,16,y,14,1a,1m,50,5g,86,3po,2b,x,2,r,3,r,n,5a,fm,w,10,k,g,k,1q,1e,1s
3o,16,y,10,16,1k,4u,56,86,3nk,13,5,1e,14,5,1l,3,26,fo,u,10,e,g,i,1k,16,22
2i,m,i,m,m,q,20,22,3g,3qg,3e,1e,1,18,20,1c,h,f,gm,1u,o,g,a,8,1s,1k,s
28,k,e,i,i,m,1m,1m,2q,3sc,w,48,3,6,24,14,k,e,g4,22,u,g,c,g,24,1e,w
3g,12,w,10,14,1e,44,4e,76,3p0,23,10,14,s,o,7,p,1b,fy,1e,k,a,g,e,y,12,2u
3a,12,w,y,10,18,3k,3u,68,3og,d,30,2m,12,5,0,17,3h,g2,1g,e,6,e,g,u,u,2m
32,u,o,w,w,12,32,3c,5e,3qg,j,1m,8,16,1u,10,15,1v,g6,1q,u,a,0,c,q,q,18
2y,s,q,q,q,y,2o,2u,4i,3p6,2k,18,24,28,1e,f,p,0,gc,20,g,k,a,m,2g,12,w
30,10,s,s,w,16,3c,3k,5u,3ng,4g,2e,w,8,6,2f,19,j,gi,g,1,q,6,c,q,e,s
3k,16,w,12,14,1i,4m,4y,7w,3ns,1f,8,1k,s,k,19,l,26,fq,y,y,e,i,g,1i,1g,1s
46,1c,14,14,1a,1k,4k,4o,76,3nq,1x,8,1r,0,1,1d,a,3a,fc,m,8,12,e,k,1g,12,18
36,s,m,q,s,14,2y,34,52,3s4,f,20,w,o,2v,1j,d,1k,fy,1m,w,m,g,k,1w,1m,14
3c,10,u,w,12,1c,3u,46,6w,3qq,21,g,y,4,8,6,n,1z,fy,1i,o,c,g,c,y,y,2o
30,q,m,m,q,12,2q,2s,4i,3s6,u,1q,u,y,39,z,1w,1a,g0,1s,10,q,g,k,28,w,14
3q,14,y,14,1a,1m,52,5i,88,3o4,p,b,j,x,5,h,5,4w,fk,u,y,i,i,m,1o,12,2a
3k,y,u,w,10,18,3m,3m,5q,3q8,1b,1,c,8,h,25,t,24,fq,16,q,e,e,g,1a,1m,w
3q,10,w,y,10,1c,42,4c,6y,3pu,v,4,16,1g,b,1n,h,1m,fm,10,w,e,e,i,1g,18,1i
3s,14,y,10,16,1m,4u,58,86,3ok,p,5,1g,b,11,1h,3,30,fi,w,10,e,g,m,1k,12,2a
3m,10,u,y,12,1a,3q,3o,5s,3pq,13,f,5,5,b,1r,6,2i,fo,12,s,e,e,g,18,1a,u
4a,1e,12,16,18,1k,4e,4a,6q,3n8,v,v,1f,0,4,j,1y,3w,f8,g,g,s,k,k,1i,12,16
3s,14,y,12,1a,1m,4y,5e,86,3og,n,7,i,17,13,v,6,4i,fi,w,y,e,k,m,1o,10,2c
3s,14,y,14,1a,1m,4y,58,7s,3om,n,9,9,1f,d,13,1a,64,fg,w,y,e,k,o,1m,12,2e
3a,u,s,q,y,18,3g,3m,62,3rw,1b,18,12,4,2b,7,1h,g,fy,1k,s,g,i,g,14,1y,1i
48,1c,14,14,1a,1k,4k,4k,70,3nk,1d,b,1t,4,0,17,u,3i,fa,k,c,10,e,k,1g,12,18
3m,12,w,y,10,1c,4a,4o,7i,3pi,1b,1,y,1o,14,1d,v,12,fq,10,y,e,g,g,1a,14,1y
3k,12,w,10,10,1e,4a,4o,7i,3pg,1d,3,12,1o,u,1j,p,12,fs,12,u,e,g,g,1a,16,20
46,1a,14,16,18,1m,4m,4q,76,3ng,v,p,1f,f,2,1b,8,38,fa,k,a,s,o,k,1g,14,1e
3s,16,12,12,14,1g,46,46,6e,3o4,1v,b,1d,d,2,l,12,48,fc,m,8,s,o,g,1a,10,y
42,1c,12,16,18,1k,4g,4i,6w,3n2,t,h,19,v,6,t,w,3w,f8,g,c,g,12,i,1c,14,16
44,18,10,16,18,1i,4i,4m,76,3o2,1p,g,z,9,1,17,1,32,fe,q,g,s,g,g,1g,12,1e
44,1a,16,14,1a,1k,4o,4s,7a,3no,11,r,1f,9,2,1n,c,2u,fc,m,8,w,k,k,1g,14,1k
3o,12,y,10,14,1g,4m,4y,7w,3o6,1d,2,1e,1m,k,1x,f,1m,fq,y,w,e,g,g,1g,14,24
42,16,10,14,1a,1k,4i,4o,7a,3o8,13,8,9,h,0,l,c,3c,fe,s,m,m,g,i,1i,14,1u
3s,14,y,14,1a,1k,4s,4y,7k,3om,l,b,3,19,j,h,1w,5q,fg,u,y,c,k,m,1k,16,2e
40,16,12,16,18,1m,4q,4u,7i,3o4,j,11,13,d,3,19,y,34,fc,u,g,q,g,m,1g,16,2a
3q,14,y,12,18,1m,4y,5c,8g,3o4,v,9,m,5,f,x,b,2q,fk,w,y,g,i,k,1m,12,28
3y,14,y,10,16,1i,4g,4q,7k,3ok,j,2,w,a,3,h,8,2q,fg,w,w,e,e,k,1g,14,28
42,1a,14,16,18,1m,4m,4s,7a,3nk,v,r,1b,l,2,1f,2,30,fa,m,a,m,u,k,1c,16,1m
4c,1c,12,16,18,1k,4c,46,6o,3ng,1b,t,1b,6,6,7,28,3u,f8,g,i,s,i,k,1i,12,14
3u,16,10,14,1a,1k,4o,4s,7g,3oe,n,l,r,v,d,v,1a,3m,fe,u,i,o,i,k,1e,18,2a
3w,14,y,10,18,1m,4o,50,7u,3oi,h,1,y,5,13,z,g,3m,fg,w,y,e,e,o,1i,14,2a
3o,10,w,y,12,1a,48,4k,7i,3po,13,0,14,1m,1c,1j,11,16,fo,y,10,e,e,i,1e,18,1q
3a,w,q,q,s,14,36,36,52,3rm,1h,14,14,14,b,2v,29,16,fu,1i,k,k,e,i,1a,28,w
3u,16,w,12,1c,1m,4u,54,7o,3oi,h,5,u,1r,x,x,10,5c,fg,w,y,c,i,o,1m,12,2c
3a,y,q,u,w,1a,3k,3s,68,3rs,1f,w,16,e,25,v,1f,o,fw,1k,q,g,g,i,16,1y,1g
4c,1c,12,16,18,1k,4c,48,6o,3n8,x,x,17,3,6,9,26,3y,f8,g,g,o,o,k,1i,12,14
36,u,o,q,w,16,36,3c,5k,3s2,r,1q,s,7,2l,u,v,0,g0,1m,w,k,g,i,14,1u,1e
3u,1a,12,14,16,1i,4e,4k,6u,3ny,1l,f,1b,n,0,r,3,3q,fc,k,8,q,s,i,18,14,14
48,1c,12,16,18,1k,4g,4i,70,3nm,1x,2,1p,8,0,13,u,3e,fc,k,a,12,e,i,1g,10,16
3q,14,w,16,18,1o,50,5i,7y,3og,r,b,17,b,h,r,c,6g,fi,w,w,i,k,m,1o,12,2c
3y,16,y,12,18,1g,4e,4k,78,3ok,v,6,e,5,a,z,2,2i,fg,u,u,e,g,g,1i,12,1s
48,1c,14,14,1a,1k,4k,4i,6y,3na,v,l,1p,5,4,z,y,3m,fa,g,e,w,i,k,1g,14,18
40,18,12,14,14,1i,46,46,6m,3nw,1r,h,1f,a,8,z,16,3i,fg,o,i,s,e,g,1c,w,y
3w,14,y,16,1a,1m,4o,4w,7g,3oc,f,b,l,11,b,7,1q,4w,fe,u,u,e,k,m,1i,18,2c
3k,10,s,y,10,18,3k,3k,5k,3pw,13,3,0,3,2,1d,g,22,fq,10,u,e,e,g,16,16,u
3o,16,w,12,14,1k,4s,54,84,3nq,15,5,1g,1c,a,1z,9,20,fo,u,10,e,g,i,1i,16,22
3q,14,y,12,1a,1m,4y,5e,8e,3o8,t,7,a,h,p,p,7,3e,fk,u,10,g,i,k,1o,10,2a
48,1c,14,14,1a,1k,4g,4e,6s,3my,j,t,1d,d,4,p,1m,3y,f8,g,e,m,s,k,1g,12,16
3q,14,w,10,12,1e,4i,4w,80,3p0,x,3,16,1s,w,1r,r,12,fm,u,10,e,g,k,1e,12,24
36,u,s,s,10,14,3c,3q,5s,3rq,17,8,i,z,6,x,29,g,fk,16,10,i,e,i,1i,16,14
3q,12,w,10,12,1e,40,46,6m,3pg,l,j,c,8,l,1z,p,1u,fm,u,10,c,g,i,1g,w,1e
3o,16,10,10,16,1g,44,4a,6k,3ou,23,9,17,f,0,t,d,32,fe,q,a,u,k,g,18,12,14
10,c,8,8,8,a,u,w,1k,3za,1t,w,6,s,f,7,5,f,8w,1w,m,w,12,18,1g,o,i
1m,e,e,e,g,k,1m,1o,2o,3tu,1w,r,1b,10,17,11,e,4,aw,14,14,q,k,i,g,a,e
16,g,a,a,e,g,1g,1k,2i,3vm,1h,16,y,3f,w,1b,p,w,a8,16,u,12,o,k,m,8,8
w,6,8,6,4,8,e,e,k,40m,1d,1b,4w,e,4,1i,1u,5i,8y,g,1k,m,u,s,2o,16,1g
10,a,8,8,8,a,s,w,1g,3za,1f,1e,7,s,8,r,5,d,8w,1m,s,w,w,1a,1m,s,e
12,e,8,a,8,c,y,10,1q,3w2,5,p,16,q,21,2q,9,1n,9a,1u,u,o,12,14,o,8,g
w,8,6,8,6,6,i,g,m,3yo,15,1w,1q,q,o,1e,3e,6m,8y,1e,m,s,q,q,2s,u,16
16,c,a,a,a,e,12,16,20,3vo,n,k,14,t,v,16,h,13,a0,1c,s,s,10,u,o,8,c
1e,g,a,g,e,k,1m,1q,2o,3u8,c,1g,3b,6,0,21,14,g,ai,1a,u,12,i,m,c,a,c
1g,g,c,e,g,k,1m,1q,2o,3ss,1w,1e,3r,y,f,1n,16,8,b0,w,y,u,g,m,e,8,g
u,8,6,6,6,6,e,e,i,3x8,19,1y,10,i,1y,2u,2c,5e,90,g,g,1y,q,o,2i,1a,1i
16,e,c,a,e,g,1g,1m,2k,3w6,1r,w,y,3f,k,1j,j,12,a0,1c,u,16,o,k,k,6,a
12,c,8,8,8,c,s,w,1g,3vy,0,7,u,10,11,28,a,r,8y,20,u,q,s,18,y,g,g
14,e,c,8,c,g,18,1c,2a,3xi,1x,c,y,1v,v,b,v,e,9e,1s,y,14,12,m,m,8,4
10,a,8,8,8,8,s,s,1c,3ym,1t,2k,f,u,m,p,m,e,8y,1k,s,u,q,1a,1o,q,e
1e,g,a,e,e,i,1m,1o,2m,3ug,4,1i,2r,5,g,27,q,c,ak,18,q,10,m,k,e,a,c
10,a,8,8,6,8,q,o,12,40c,1x,22,d,q,m,t,3u,40,8w,1i,q,y,s,1g,20,12,k
1g,g,c,g,e,k,1o,1o,2o,3t0,1o,16,3j,w,j,1j,18,8,b0,u,14,s,g,k,e,a,e
w,8,8,6,6,8,i,g,o,3w6,11,2o,18,w,y,2k,3i,6c,8y,1o,k,o,o,m,2m,y,12
10,c,8,a,8,a,w,10,1s,3xe,l,2,c,y,13,12,n,1p,8y,1q,w,u,10,12,12,c,c
14,e,a,a,c,g,18,1g,2e,3wq,1f,3,16,1h,15,d,x,k,9q,1g,w,s,14,s,i,6,6
1a,e,c,c,e,i,1i,1o,2m,3w6,1p,1g,v,2f,12,27,6,s,a4,1g,s,18,s,c,i,a,8
y,8,8,8,6,8,k,k,s,3vw,1f,3c,o,w,w,1m,3a,4k,8y,1q,m,o,o,q,28,w,10
1k,e,c,g,g,k,1m,1o,2o,3tk,1q,8,2l,14,v,1b,w,0,aw,12,14,s,g,i,g,8,g
1m,g,e,e,e,k,1m,1q,2m,3ts,2e,1b,n,10,1d,z,0,4,ay,12,16,q,k,i,g,a,e
w,8,8,6,6,8,i,i,o,3xm,15,2q,10,u,q,1k,3k,6e,8y,1m,i,q,q,q,2k,w,12
u,8,6,6,6,6,c,e,k,3z6,1j,1z,4u,a,18,20,1y,4q,90,g,1m,m,s,q,2e,1e,1m
1k,g,c,g,e,k,1m,1q,2m,3tg,1y,5,23,12,11,15,q,2,ay,10,16,q,i,i,g,8,g
16,e,a,a,e,i,1g,1m,2k,3wk,21,y,10,3n,k,1p,f,12,9u,1g,w,14,u,i,k,6,8
1a,g,c,c,e,i,1k,1m,2m,3va,13,1k,19,1p,10,23,6,m,a4,1k,o,16,o,e,i,a,a
10,a,8,8,8,8,s,u,1c,3y8,1t,2i,d,w,m,p,a,4,8y,1k,u,u,o,1a,1m,o,c
1c,g,c,e,g,i,1m,1q,2m,3um,d,1k,2v,7,a,25,12,g,ai,1a,m,14,k,m,c,a,e
12,e,a,a,a,g,16,1c,2a,3xy,19,x,12,j,2j,4,x,a,8y,20,10,u,1a,y,i,6,4
1q,g,e,e,e,k,1m,1o,2m,3u4,2u,29,q,y,1n,v,11,e,b6,y,18,k,o,g,k,a,e
1c,e,c,c,g,i,1m,1o,2m,3vm,15,1g,21,19,q,2d,s,k,a4,1k,o,18,o,i,e,a,c
10,a,8,8,8,8,q,o,10,406,1v,1a,5,q,o,l,4g,56,8u,1k,u,w,s,1g,22,12,o
12,e,a,8,a,e,14,18,26,3ww,b,1d,16,w,3j,16,v,d,8y,20,y,q,1a,10,g,4,8
u,8,6,8,6,6,e,g,k,3wo,x,l,3u,s,1k,30,2e,6m,90,i,1e,w,q,o,2s,y,1a
16,g,a,a,e,g,1e,1m,2g,3wg,1z,1c,o,3n,16,1f,r,w,a6,18,u,1a,10,6,m,a,6
1e,e,c,e,e,i,1k,1o,2m,3ug,a,1i,2n,7,k,29,i,c,ao,14,o,12,m,k,g,a,a
1i,g,a,g,e,k,1m,1q,2o,3t6,1s,10,3d,10,l,1j,12,4,ay,y,14,q,g,m,e,8,e
1c,g,c,e,g,i,1o,1q,2o,3uu,n,1i,2x,9,6,23,18,i,ae,1e,o,12,m,k,c,a,e
10,c,a,8,a,c,y,16,20,3y2,h,t,m,w,29,m,13,17,8y,1q,y,u,16,16,s,a,6
12,c,a,8,8,c,w,10,1q,3ys,11,11,s,u,2b,1g,f,t,8w,20,s,w,1a,1a,s,i,o
1e,g,c,e,g,i,1o,1q,2o,3tq,o,1i,3j,k,5,1v,16,e,as,12,q,10,i,m,e,8,g
1c,g,a,c,g,g,1k,1m,2k,3v2,h,1k,1t,11,w,21,0,g,ag,1a,m,16,o,g,i,a,a
10,c,a,8,a,a,y,14,1u,3x0,d,h,m,y,1r,1i,l,1p,8y,1u,w,u,12,10,w,8,e
10,a,8,8,6,a,o,o,10,40c,1v,1q,9,o,m,j,4i,4u,8u,1k,s,w,s,1g,22,12,m
16,g,a,a,e,i,1g,1o,2k,3wa,1z,1a,u,43,14,1t,d,12,a2,1c,u,18,10,6,k,8,8
w,6,6,6,6,6,e,g,i,406,1f,1h,4w,e,a,1q,1q,5g,8y,g,1m,k,u,s,2o,16,1g
y,a,8,8,6,8,o,m,10,3wi,1l,38,1,y,w,c,2a,2o,8y,1m,q,s,m,12,1s,w,u
12,c,8,a,8,c,12,18,22,3xy,d,17,s,w,2z,u,17,p,8y,1s,y,u,18,16,o,6,6
16,g,a,8,c,g,18,1c,28,3ve,z,1a,10,2b,k,5,x,9,a8,18,s,10,s,m,o,a,6
12,c,a,a,8,e,12,18,24,3x8,d,15,y,k,2p,16,11,v,8y,1y,w,u,18,10,k,6,8
10,c,8,8,8,8,s,u,1a,3zq,1v,12,0,q,a,p,16,1y,8u,1q,s,w,w,1c,1q,y,m
w,6,8,6,6,6,g,e,m,3xu,11,19,4m,q,y,2k,2a,6m,8y,i,1k,q,s,o,2s,w,1c
w,6,6,6,6,8,e,e,k,3z0,1b,1l,4w,k,s,22,1y,5w,8y,g,1m,o,s,q,2q,12,1g
1e,g,c,e,e,k,1m,1o,2o,3ty,i,1k,39,8,2,1z,12,c,ak,18,s,10,i,m,e,a,c
u,8,6,6,6,6,e,c,k,404,1l,1t,4w,8,k,1o,1a,4u,8y,i,1k,m,s,q,2e,1e,1k
u,8,6,6,6,6,e,e,k,3xy,19,p,3q,g,1m,2g,26,5e,90,e,1a,12,s,o,2k,1a,1i
w,8,8,6,8,6,i,i,o,3xc,15,2q,14,s,s,1o,3k,6c,8y,1o,g,q,q,q,2k,w,y
1e,g,c,e,g,i,1o,1o,2o,3tq,q,1i,3f,g,1,1x,14,c,aq,14,q,10,i,m,e,a,e
12,e,8,a,8,e,12,18,20,3ya,t,1h,10,k,3f,1e,t,p,8y,1w,10,w,18,18,i,a,8
10,a,8,6,8,8,o,k,u,40u,1v,1s,b,o,m,6,6s,6c,8u,1k,o,y,u,1g,28,10,s
1k,g,c,g,e,k,1m,1q,2o,3tm,1u,b,1x,14,13,13,o,2,aw,12,16,q,i,i,g,8,g
1o,e,e,e,g,k,1k,1q,2m,3ts,2i,1l,b,10,1h,t,3,4,b2,10,14,o,o,g,g,a,e
y,8,8,8,8,8,m,m,y,3ya,1v,3e,3,s,s,6,2m,3m,8y,1m,k,u,o,18,1w,y,q
1c,g,a,c,g,g,1i,1m,2k,3v8,5,1e,25,r,w,23,3,i,aa,1g,q,18,m,i,k,a,8
18,e,c,a,g,i,1i,1q,2m,3vy,1x,1g,8,3p,18,29,8,y,a6,1a,u,18,o,e,i,8,a
10,c,8,a,8,a,w,12,1q,3xm,p,6,c,w,13,s,p,1n,8y,1q,u,w,10,14,12,c,a
w,8,6,6,8,6,g,g,m,3y6,11,1g,22,s,o,1s,3e,6m,8y,1a,s,q,s,m,2u,u,16
w,8,6,8,6,6,g,g,m,3vw,n,22,1c,w,1g,2y,3o,6k,90,1k,m,o,o,o,2o,w,16
14,g,a,a,e,g,1e,1k,2i,3wg,1v,o,14,33,a,17,v,12,a0,1a,w,10,u,m,i,8,8
14,e,a,a,c,e,1a,1e,2c,3wo,1j,2,16,1h,11,b,t,e,9u,1e,u,s,14,q,k,8,6
y,8,8,6,6,8,k,k,s,3xy,1j,34,o,q,s,u,38,5k,8y,1o,g,s,q,y,28,10,u
u,8,6,6,6,6,e,c,k,406,1n,1v,4u,6,m,1m,s,50,8y,i,1k,m,s,q,2c,1g,1g
10,c,a,8,8,a,y,12,1s,3xo,p,0,e,w,1d,s,r,1j,8y,1q,w,u,10,16,10,a,c
1i,g,c,g,e,k,1m,1q,2o,3sy,22,o,33,14,r,1d,10,4,b0,w,16,q,g,k,e,a,e
1a,g,c,c,g,i,1m,1o,2o,3vq,1d,1i,1v,1j,q,2d,u,m,a4,1k,o,18,o,g,g,8,c
y,8,8,6,8,8,i,k,s,3w4,1d,36,s,w,w,1o,3c,50,8y,1o,m,q,m,q,2c,w,10
1m,e,e,e,g,k,1m,1o,2o,3ts,20,t,1b,12,17,11,g,0,ay,12,16,o,k,i,g,8,g
10,c,a,8,8,a,u,y,1k,3z6,1r,c,e,s,v,8,d,b,8w,24,i,w,18,14,1a,m,o
1i,g,a,g,g,k,1m,1q,2o,3su,24,u,37,10,p,1j,16,4,b2,u,14,u,e,k,g,8,e
14,e,a,8,a,e,10,16,1w,3vu,n,a,16,f,19,1i,f,17,9w,1e,u,o,12,w,q,6,e
14,e,a,8,a,c,10,14,1w,3vu,h,2,18,7,1f,1w,f,19,9s,1i,s,q,12,y,o,8,c
16,g,a,a,e,g,1c,1k,2i,3wi,1x,18,u,3r,16,19,13,12,a4,18,w,18,12,8,m,8,6
y,8,8,6,6,8,k,i,s,3x8,1f,32,s,s,s,16,36,5m,8y,1o,i,q,q,w,28,10,u
10,a,8,8,8,8,q,m,y,40a,1t,y,1,q,o,2,60,6g,8u,1k,u,w,s,1i,24,14,s
y,8,8,8,8,6,o,k,10,3yw,1x,3e,5,s,q,4,2s,3o,8y,1m,k,u,o,1a,1w,10,q
10,a,8,8,8,8,s,o,16,402,1x,1o,7,o,o,z,2e,30,8w,1i,u,w,s,1g,1w,12,k
10,c,6,8,8,8,m,k,u,400,19,y,2,s,o,1y,6u,6e,8u,1i,u,w,u,1i,2a,14,12
y,8,8,6,8,6,k,i,s,3wg,1d,34,u,w,u,1k,38,5c,8y,1o,k,q,o,o,2e,y,y
12,e,a,8,a,e,12,16,20,3ya,17,11,10,4,2r,14,n,r,8y,1y,10,w,18,14,k,c,a
16,c,a,a,a,e,12,16,1y,3vq,p,k,14,r,x,16,f,13,a2,1a,s,s,10,u,q,6,c
u,8,6,6,6,6,e,c,k,3zs,1l,1t,4w,a,s,1s,1q,4s,8y,i,1k,m,s,q,2g,1c,1o
1i,g,a,g,g,i,1o,1o,2o,3te,1m,w,37,10,n,1j,12,4,aw,10,14,q,i,k,e,8,g
w,6,6,6,6,6,e,e,i,40m,1l,1t,4s,6,a,1i,4,5c,8y,i,1k,k,s,s,28,1k,18
1m,e,e,e,e,k,1m,1q,2m,3tu,26,p,1d,10,15,19,8,6,ay,10,16,s,i,i,i,8,e
1e,g,a,e,g,i,1m,1o,2m,3u0,q,1i,31,4,c,27,q,c,au,y,s,y,m,m,c,a,e
1m,g,c,g,e,k,1m,1o,2o,3ts,20,x,13,10,19,z,e,2,ay,12,16,o,k,i,g,a,e
18,e,c,a,e,i,1i,1m,2k,3w8,1x,1e,8,3j,18,1x,9,y,a6,1a,u,1a,w,6,k,8,a
12,e,a,8,a,e,16,1c,28,3y0,r,1f,10,a,3h,m,11,6,8y,1y,y,u,1c,14,e,8,2
12,c,a,8,8,c,w,10,1q,3w8,2,x,12,u,1r,2g,5,1l,94,1w,u,q,y,18,q,a,g
10,a,8,8,8,8,o,o,y,40c,1v,1g,7,q,m,d,5a,5k,8u,1k,s,w,u,1g,22,14,o
1g,e,c,e,g,i,1o,1o,2o,3tg,14,1i,3h,k,5,1v,12,c,au,10,w,u,k,m,c,a,e
1g,g,a,g,g,i,1m,1q,2m,3t8,1k,1e,3l,s,9,1t,12,8,ay,w,10,q,k,m,e,8,e
u,8,6,6,6,6,e,e,i,3xy,1d,0,30,g,1o,2g,28,5a,90,e,14,1a,q,o,2i,1c,1i
12,c,8,8,8,a,w,y,1i,3z2,1f,d,m,s,1d,q,5,i,8w,24,k,w,1a,1c,y,s,m
18,g,a,c,e,g,1i,1m,2k,3we,1v,1e,3,37,1a,1v,d,w,a4,1e,s,1a,w,8,k,a,8
12,c,a,8,8,e,10,16,1y,3yg,z,x,s,u,2z,y,x,t,8w,22,q,w,1a,16,q,a,a
w,8,6,6,4,8,e,e,i,40y,1b,1h,4u,a,1,1g,14,5c,8y,i,1i,m,u,s,2c,1e,1e
1a,g,a,c,g,i,1m,1q,2o,3vi,1h,1i,1b,21,u,2f,w,o,a4,1i,q,16,o,g,e,8,e
w,8,8,6,8,6,i,i,o,3wi,13,2s,12,w,w,24,3g,68,8y,1o,i,q,o,m,2m,y,10
w,6,6,6,6,6,e,e,k,40m,1h,1n,4w,8,4,1k,10,52,8y,i,1k,k,s,s,2c,1g,1g
w,6,6,6,6,6,e,g,i,40m,1f,1f,4w,e,0,1m,1o,58,8y,i,1i,m,u,s,2k,1a,1g
w,8,6,6,6,6,g,g,m,3yi,15,1,3i,q,o,20,2q,6k,8y,u,18,o,s,q,2s,w,1a
12,c,a,a,8,e,14,1c,26,3y2,p,1d,10,k,3l,q,13,3,8y,1y,w,u,1c,14,i,6,4
w,6,8,6,6,6,g,e,m,3yo,15,t,48,o,q,22,2c,6g,8y,m,1g,o,s,q,2s,y,1c
14,e,a,a,a,g,18,1e,2a,3xw,1z,3,10,1f,1f,9,z,k,90,24,y,16,w,u,i,8,4
y,a,a,8,6,a,q,q,1a,3xw,1r,2u,f,w,o,d,w,10,8y,1k,s,u,o,18,1o,q,i
u,8,6,6,6,8,e,e,k,3y6,17,1n,4w,k,16,2e,22,62,90,e,1m,q,s,o,2q,12,1e
1c,g,c,c,g,i,1m,1q,2m,3uu,l,1i,2j,j,i,27,w,i,ac,1e,m,16,m,k,c,a,e
10,c,6,8,8,8,o,k,u,40a,1n,s,0,q,o,1a,76,6q,8u,1i,u,y,s,1i,2a,12,y
18,g,a,c,e,i,1i,1o,2k,3w6,1v,1g,5,37,16,23,1,w,a4,1e,s,1a,u,a,i,8,a
18,e,c,a,g,i,1k,1o,2m,3vq,1p,1e,2,3d,14,27,8,w,a6,1a,u,18,m,g,g,8,c
10,c,8,8,8,a,s,u,1c,3w0,5,g,g,10,5,1a,i,0,8y,1w,s,s,o,18,16,k,i
12,c,8,8,8,c,u,y,1i,3w8,0,h,s,10,17,24,6,19,8y,1y,u,q,w,18,w,e,i
y,8,8,8,6,8,k,i,u,3w0,1d,3a,o,w,w,1k,3a,4s,8y,1o,o,o,m,s,28,y,y
1a,e,a,c,e,g,1c,1i,2g,3vi,x,1g,4,2n,1c,13,15,g,a6,1e,s,14,s,c,m,c,6
u,8,6,6,6,6,e,e,i,3yc,1f,13,44,g,1i,2a,24,5a,90,e,1g,w,s,o,2k,1a,1i
10,a,8,8,6,8,o,m,y,40k,1v,1y,d,o,m,h,5m,5q,8u,1k,q,w,u,1g,24,12,m
w,6,6,6,6,6,e,e,k,3zo,1f,1n,4y,e,k,1w,1s,5g,8y,g,1m,m,u,q,2o,16,1g
10,c,8,8,8,8,s,s,18,3zq,1v,q,4,s,4,f,24,34,8u,1u,q,u,10,1e,1m,10,o
1g,g,a,g,e,k,1m,1o,2o,3tc,1m,1a,3f,s,9,1v,y,6,ay,y,y,q,k,m,e,a,e
18,g,c,a,g,i,1k,1o,2m,3va,1b,1g,b,2v,12,29,e,u,a6,1e,s,16,o,e,g,8,c
12,c,a,8,8,e,y,16,1w,3yi,z,z,s,u,2z,16,v,x,8w,20,u,u,1a,18,o,c,c
14,e,c,a,c,g,1e,1k,2i,3wu,1r,a,12,2j,f,17,t,12,9i,1q,w,y,y,o,k,6,6
y,a,8,6,8,8,m,m,y,3x6,1p,3c,0,u,u,c,2e,3i,8y,1m,o,s,o,14,1u,y,q
y,8,8,6,8,6,m,k,s,3z2,1t,3c,a,q,q,g,3k,4w,8y,1m,i,s,q,16,24,y,u
10,c,8,8,8,a,w,y,1o,3yu,15,i,6,s,j,3,p,1p,8w,1q,s,w,12,14,1c,i,c
12,c,a,8,8,e,12,16,24,3y6,h,19,u,u,35,u,15,t,8y,1w,u,w,1a,14,o,6,6
12,c,a,8,8,c,w,10,1o,3w4,1,n,10,s,1l,2c,5,19,96,1w,u,o,y,16,s,c,e
14,g,a,a,c,g,1c,1i,2g,3xa,1z,c,10,2j,d,13,v,y,9c,1u,y,10,14,k,k,8,4
1k,g,c,e,g,k,1m,1q,2m,3te,24,1,27,12,z,1b,q,2,ay,10,16,s,g,i,g,a,e
1k,g,c,g,e,k,1m,1q,2o,3tq,1w,j,1n,12,15,13,k,2,aw,12,16,q,k,g,i,8,g
w,6,8,6,6,6,g,e,m,3xq,z,17,4k,q,y,2m,2a,6m,8y,i,1k,q,s,o,2s,w,1c
w,6,6,8,6,6,e,g,k,3ym,17,1j,4w,m,u,28,22,6a,8y,g,1m,o,u,o,2s,y,1e
14,e,a,8,a,e,12,16,20,3vu,r,8,18,h,1b,18,f,13,9y,1c,u,o,12,w,o,6,c
18,g,a,c,g,i,1k,1o,2m,3w0,1t,1g,f,2z,14,2d,e,u,a4,1e,s,1a,s,a,i,8,a
w,6,8,6,6,6,e,g,k,3x8,z,v,46,q,1a,2q,2e,6k,90,m,1g,q,q,o,2s,y,1a
y,a,8,8,6,8,o,k,y,3wc,1j,3a,2,y,w,i,2k,32,8y,1o,o,s,m,10,1u,y,s
1k,g,c,g,e,k,1m,1q,2o,3tu,1q,h,1p,14,15,13,m,0,aw,14,14,q,i,i,g,a,e
1e,g,c,e,g,i,1o,1q,2o,3tu,i,1i,3f,e,3,1x,1a,c,as,12,q,10,i,m,e,8,g
1e,g,a,g,g,i,1o,1q,2o,3u8,2,1i,3b,6,1,1x,1a,e,am,16,u,w,m,k,c,a,e
1c,g,c,c,g,i,1m,1o,2m,3uq,9,1k,2l,d,k,27,o,e,ae,1e,o,12,m,k,e,a,c
u,8,6,6,6,6,e,e,k,3zi,1h,1n,4w,g,o,1y,1u,5c,8y,g,1m,m,u,q,2m,18,1i
1e,g,a,e,g,k,1m,1q,2o,3uq,f,1i,35,3,0,21,1a,k,ac,1g,q,12,k,m,c,8,e
12,c,a,8,a,c,14,18,24,3xg,9,1d,y,q,35,1a,15,r,8y,1w,w,u,1a,14,i,4,8
16,e,a,a,c,i,1c,1k,2i,3wy,1z,o,10,33,4,19,r,y,9m,1m,y,12,12,g,k,8,6
y,a,8,8,8,8,q,q,14,3x8,1n,2y,b,y,q,1,1e,1k,8y,1m,q,u,o,16,1o,s,m
u,8,6,6,6,6,e,c,k,3za,1j,1v,4w,a,14,1y,1w,4u,90,g,1m,m,s,q,2g,1c,1m
10,a,8,8,6,8,o,k,u,40i,1n,16,3,q,m,14,76,6i,8u,1i,s,y,s,1i,2a,12,w
1c,g,a,e,g,g,1m,1q,2m,3v2,v,1k,27,v,o,2b,u,i,a8,1i,m,16,m,i,e,a,c
1e,g,a,e,g,k,1m,1q,2o,3uk,9,1i,37,1,0,1z,1a,i,ae,1e,s,10,k,m,c,8,e
12,c,8,a,8,c,10,16,1y,3ye,t,r,o,u,2j,s,13,1d,8y,1y,q,w,1a,14,s,8,8
14,e,a,a,c,g,18,1g,2c,3xk,1z,8,y,1v,v,j,x,m,98,1y,y,12,12,o,k,8,4
18,g,c,c,g,i,1k,1q,2o,3vq,1p,1i,x,2j,10,2j,u,q,a4,1g,s,16,o,g,e,8,e
w,8,6,6,6,8,g,g,m,3wy,v,w,2k,s,12,2k,2y,6w,8y,16,y,q,q,o,2q,w,16
10,a,8,8,8,8,u,s,1c,3zs,1x,1k,5,s,e,x,u,14,8w,1m,s,w,s,1e,1s,y,g
w,8,8,6,8,6,i,i,o,3vy,13,2s,16,w,12,2k,3k,62,8y,1q,i,q,m,o,2k,w,12
1a,e,a,c,e,g,1g,1k,2i,3ve,17,1i,0,2p,1a,1h,l,k,a6,1e,q,16,q,e,k,a,8
u,8,6,6,6,8,e,e,k,3ya,17,1l,4u,m,12,2e,20,62,90,e,1m,q,s,o,2q,10,1g
12,e,a,a,8,g,14,1c,28,3y2,13,13,10,3,2z,i,x,2,8y,20,y,w,18,12,i,6,4
10,a,8,8,8,8,q,o,14,3w0,z,26,2,12,o,k,1m,1e,8y,1q,u,q,m,16,1g,s,m
w,a,6,8,6,8,g,i,q,3vw,15,2w,14,w,12,2e,3k,5u,8y,1q,k,o,o,m,2k,w,12
w,8,6,8,6,6,i,g,m,3y4,13,1y,1o,s,o,1o,3i,6k,8y,1g,m,q,s,m,2s,u,16
1e,g,c,e,g,i,1o,1q,2o,3u6,8,1i,3f,8,5,1v,1a,g,ak,18,w,y,i,m,c,a,e
1c,e,c,c,e,i,1i,1m,2k,3ve,v,1i,1d,1f,12,21,7,m,a8,1g,o,16,o,g,g,c,a
w,8,6,6,4,8,e,e,i,40u,1b,1f,4w,c,1,1i,1m,56,8y,i,1i,m,u,s,2g,1c,1g
1c,g,a,e,g,i,1m,1o,2m,3v4,z,1k,27,v,m,2b,w,i,a8,1i,m,16,m,i,e,a,c
w,6,6,6,6,6,e,e,i,40m,1j,1p,4u,6,6,1i,k,56,8y,i,1k,k,s,s,28,1i,1c
u,8,6,6,6,6,e,g,k,3wa,z,w,26,q,20,38,2g,66,90,e,w,1k,q,o,2q,12,1c
1c,g,a,e,g,g,1m,1o,2m,3v0,p,1k,2b,p,m,27,o,i,a8,1i,m,16,m,i,e,a,e
10,c,a,8,8,a,y,10,1s,3yw,1l,8,g,u,1h,e,r,15,8w,22,k,w,18,14,14,g,g
1e,g,a,e,g,i,1k,1q,2m,3u2,o,1i,31,4,c,27,q,c,au,y,q,10,m,m,c,a,e
1e,e,c,e,e,i,1k,1o,2m,3ui,4,1k,2p,5,i,27,i,e,ao,14,o,12,m,k,e,a,c
1c,e,c,c,e,i,1i,1m,2k,3vu,11,1e,1h,1n,10,23,1,o,a2,1k,q,1a,q,e,k,a,8
y,8,8,6,6,8,i,i,q,41a,1n,38,c,g,c,y,5g,62,8y,1i,e,w,u,10,2m,w,w
10,8,6,6,6,6,e,g,k,41g,x,5,40,e,8,o,2k,5q,8y,1c,m,o,u,s,2e,10,y
y,8,8,6,6,6,g,g,m,41u,15,3c,i,c,c,16,4i,70,8y,1i,8,y,w,s,2u,w,12
y,8,6,6,6,6,e,g,k,41e,13,x,4m,c,4,q,22,58,8y,10,y,o,u,s,24,18,10
y,8,8,6,6,6,i,g,o,41i,1f,3a,i,c,c,16,50,6o,8y,1i,a,y,u,u,2u,w,10
y,8,6,6,8,6,i,g,o,40q,1l,3e,m,k,g,w,4m,62,8y,1k,a,w,u,s,2u,u,12
y,8,8,6,6,6,g,g,k,41w,11,3i,i,e,e,10,3w,6q,8y,1i,8,w,w,u,2q,w,16
y,8,8,8,6,8,k,k,u,408,1v,3c,4,q,m,e,4g,4s,8w,1m,g,w,s,16,28,10,o
10,8,6,6,6,6,g,e,k,41c,v,k,38,g,a,m,2q,5q,8y,1i,e,s,s,s,2a,14,w
10,a,8,8,8,8,s,s,1c,3yw,1x,2k,f,u,i,n,i,a,8y,1k,q,w,q,1a,1q,s,e
10,8,8,8,6,8,m,k,s,414,1r,2k,f,o,g,4,6e,62,8w,1i,k,y,u,1g,28,y,q
10,8,8,6,4,8,g,e,m,41m,p,2e,1k,g,c,10,2w,60,8y,1i,c,u,s,s,2g,w,w
y,a,8,6,8,8,m,k,w,40o,1v,30,b,q,i,1,50,4g,8w,1k,i,y,s,1e,24,10,m
w,8,6,6,6,6,e,g,k,40c,1b,f,42,i,e,1e,26,66,8y,g,1i,q,u,q,2w,y,1c
10,8,6,8,6,8,k,g,q,41g,1l,2y,4,g,a,m,62,6a,8w,1i,g,y,u,18,2e,y,u
y,8,6,6,6,6,e,g,k,41o,11,0,3y,e,6,w,2k,5y,8y,y,y,o,w,u,2q,w,1a
10,8,8,6,8,6,m,i,u,414,1p,2w,7,o,e,a,62,5o,8w,1i,i,y,u,1e,26,y,s
10,8,6,6,6,8,g,g,k,420,t,3e,k,e,e,1g,3i,76,8y,1i,a,w,u,u,2m,w,10
10,8,6,6,6,6,g,e,k,41s,v,26,1u,g,a,u,2u,62,8y,1i,a,u,u,u,2k,u,14
w,8,6,6,6,6,g,g,k,3z6,17,r,4a,m,m,1u,2i,6i,8y,k,1g,q,s,q,2u,w,1a
10,8,8,8,6,8,m,k,w,40w,1t,2s,f,o,i,3,5u,5c,8w,1i,k,y,u,1e,26,10,o
10,8,8,6,6,8,i,g,o,41u,1d,30,8,8,6,w,60,6w,8y,1g,e,y,u,12,2o,w,y
w,8,6,8,6,6,g,i,m,400,1b,2y,s,m,k,w,3q,6a,8y,1a,m,u,s,q,2w,u,16
10,a,8,6,8,8,q,o,16,404,1x,2i,h,q,m,r,2s,2i,8w,1k,o,w,s,1e,20,10,i
10,a,8,8,8,8,s,u,1c,3zm,1r,1w,b,s,g,x,k,g,8w,1k,s,w,u,1c,1q,w,g
w,8,6,6,6,6,g,e,k,40y,17,b,40,g,a,16,2e,62,8y,g,1i,o,u,s,2w,y,1c
y,8,6,6,6,8,e,g,k,41u,z,2u,18,e,c,y,3e,6i,8y,1c,e,w,w,s,2s,u,18
10,a,8,8,8,a,s,w,1g,3z4,1l,1q,9,s,8,n,5,h,8w,1m,s,w,u,1a,1m,q,e
y,8,6,8,6,6,i,i,q,406,1l,38,o,m,k,u,4e,5o,8y,1k,e,u,s,y,2k,w,y
y,a,8,8,6,8,q,m,14,3zk,1x,34,h,s,o,d,2c,2c,8w,1m,m,w,q,1a,1y,10,i
y,8,8,8,6,8,k,i,s,40s,1t,38,4,o,g,k,54,56,8w,1k,g,y,s,16,2a,10,q
10,8,6,6,6,6,g,g,k,41k,r,1k,2e,g,c,u,2w,5w,8y,1i,e,s,s,s,2i,u,w
12,a,6,6,6,8,g,g,m,41o,9,2y,s,i,g,2i,32,7m,8w,1k,i,u,q,s,2e,12,o
y,8,8,6,6,6,g,g,m,41u,15,3e,i,c,c,14,4g,70,8y,1i,8,y,w,s,2u,w,12
y,8,6,6,6,6,e,g,i,41i,15,v,4q,c,2,y,1y,5k,8y,s,16,m,w,s,2g,12,16
y,8,8,6,6,8,k,k,s,3zo,1t,3c,e,o,o,m,42,4w,8y,1m,g,u,q,16,26,y,u
10,a,6,8,6,8,m,i,q,41c,1p,2g,b,m,c,a,6u,6m,8w,1i,k,y,u,1g,2a,y,u
y,6,8,6,4,8,e,g,k,41m,11,b,46,e,8,y,2m,5w,8y,m,1a,o,w,u,2s,w,1c
y,8,6,6,6,6,e,g,k,41o,11,0,3y,e,8,w,2o,5y,8y,u,12,o,w,u,2s,u,1a
12,8,6,6,6,8,i,e,m,41w,p,34,i,e,c,1w,4a,7g,8y,1i,e,w,s,u,2m,u,y
10,8,8,6,6,8,i,i,o,41u,1b,2u,6,8,6,s,68,70,8y,1g,g,w,w,1a,2g,u,y
y,6,8,6,4,8,e,e,k,41c,17,13,4o,c,0,10,1e,58,8y,m,1e,k,w,s,22,1c,12
w,8,6,6,6,6,g,e,k,41e,13,15,4w,e,6,12,2a,5s,8y,g,1i,o,u,u,2u,w,1c
y,8,6,6,6,6,g,g,k,41u,z,2s,1a,g,a,u,32,6c,8y,1g,a,u,w,u,2q,u,16
y,6,6,6,6,8,e,e,k,41k,11,13,4u,e,2,10,26,5q,8y,s,16,m,w,s,2q,y,1c
y,8,6,6,6,6,e,g,k,41q,z,w,32,e,8,u,2q,64,8y,16,o,q,w,u,2q,u,1a
y,8,8,8,6,8,m,k,w,3zy,1x,3c,1,q,o,6,40,48,8w,1m,i,w,q,1a,22,10,o
w,8,6,6,6,6,g,e,k,41g,13,13,4u,e,6,12,2c,5s,8y,g,1i,o,u,u,2u,w,1c
y,a,6,6,6,8,i,g,m,41q,1d,36,c,c,8,14,5g,6u,8y,1g,e,w,w,u,2u,w,y
y,6,6,6,6,6,g,e,k,41g,11,15,4u,c,0,12,1u,5m,8y,o,1a,m,w,s,2k,12,1c
12,8,6,6,6,6,i,g,k,41w,j,3a,o,i,g,1s,30,6y,8y,1i,e,u,s,s,2g,y,w
10,a,6,6,6,6,i,e,m,420,r,3c,i,g,e,1q,3e,7i,8y,1i,c,w,s,u,2k,y,y
10,a,8,8,6,a,q,s,1c,3ze,1v,2e,d,s,k,t,s,m,8w,1k,s,w,q,1c,1s,w,e
w,8,6,6,6,6,e,g,k,41c,15,15,4u,g,4,14,28,5s,8y,g,1i,o,u,u,2u,y,1c
w,8,6,6,6,8,e,g,m,3zu,19,s,2u,m,i,1c,2s,6c,8y,q,18,q,u,q,2w,w,1a
w,6,8,6,6,6,e,g,k,3zi,19,1d,4u,k,i,1u,22,68,8y,g,1k,o,u,q,2u,y,1e
y,a,8,6,8,8,o,m,10,3zs,1x,36,b,s,o,7,32,34,8w,1m,k,w,q,1c,1y,10,k
y,8,8,6,6,6,i,g,m,41o,1b,3c,g,e,a,16,4s,6w,8y,1i,a,y,u,u,2u,w,12
10,8,8,8,6,8,k,k,s,41a,1r,2o,b,m,c,a,6m,6c,8w,1i,k,y,s,1g,2a,y,s
10,a,a,8,8,8,w,w,1m,3ys,1f,1a,3,u,3,f,f,15,8y,1k,u,w,w,18,1g,k,e
10,8,6,6,6,6,g,g,k,41w,t,3g,m,g,c,12,38,6m,8y,1i,8,w,u,u,2m,w,14
y,a,6,8,6,8,k,i,s,412,1p,32,4,m,c,k,5m,5k,8w,1k,g,w,u,1a,2a,w,s
10,a,8,8,6,8,o,k,u,40o,1r,1a,5,q,m,w,76,6i,8u,1i,s,w,u,1i,2a,10,w
10,a,8,8,8,a,s,w,1i,3z0,1n,1s,9,u,6,l,5,l,8w,1m,s,w,u,1a,1k,o,e
y,8,8,6,6,8,k,i,q,40q,1r,3c,e,k,g,s,50,5i,8w,1m,e,w,s,y,2k,y,u
y,8,6,6,6,6,g,g,k,41u,z,2o,1c,g,c,w,3c,6e,8y,1c,e,w,u,u,2s,u,18
10,a,6,8,8,8,m,k,w,40u,1v,2c,f,o,k,5,62,5s,8w,1i,m,y,u,1g,26,10,o
w,6,6,8,6,6,e,e,k,3zu,1d,1d,4u,i,g,1s,1w,5y,8y,g,1k,o,u,q,2s,12,1e
10,8,6,6,6,6,g,g,k,41e,t,1c,2i,i,a,s,2u,5u,8y,1i,e,s,s,s,2c,10,w
y,8,6,8,6,6,i,g,o,418,1j,3e,k,g,e,10,4s,6e,8y,1i,c,w,u,s,2w,u,12
10,a,8,8,6,8,s,o,16,402,1x,2c,f,q,m,v,2g,2c,8w,1k,q,w,s,1e,1y,10,i
10,6,8,6,6,6,e,g,k,41s,v,22,1y,g,a,u,2u,66,8y,1k,a,s,u,u,2k,w,14
y,8,8,6,6,6,i,i,o,412,1l,3c,k,g,e,10,4y,64,8y,1i,c,y,u,q,2u,w,y
y,6,6,6,6,6,g,e,k,41i,z,15,4u,e,2,12,26,5q,8y,k,1e,m,w,s,2u,w,1c
y,8,6,8,6,6,i,i,m,416,1j,3c,i,g,e,12,4w,6a,8y,1i,c,w,u,s,2w,u,10
w,8,6,8,6,6,g,g,m,3zg,17,24,1i,o,k,14,3g,6i,8y,18,q,s,s,q,2w,u,16
w,8,6,6,6,6,e,e,k,41a,13,1d,4u,c,3,18,1c,5k,8y,i,1i,k,w,s,2e,18,1e
y,6,8,6,4,8,e,e,k,41a,13,17,4o,8,2,y,1e,4s,8y,m,1e,k,w,s,1s,1g,y
y,a,8,6,8,8,m,m,y,408,1v,34,b,q,m,5,44,3s,8w,1k,k,y,q,1c,22,12,k
10,a,8,6,8,8,m,i,s,40w,1l,1s,3,o,k,u,6w,6m,8w,1i,m,y,u,1i,28,12,y
y,8,6,6,6,6,g,e,k,41k,11,7,44,e,4,u,2a,5o,8y,12,w,m,w,s,2i,y,14
w,6,8,6,6,6,e,g,k,408,1b,1b,4w,i,a,1k,1y,60,8y,g,1k,m,u,s,2u,10,1c
y,6,8,6,4,8,e,e,k,41i,11,11,4u,c,2,10,20,5o,8y,w,12,m,w,s,2m,10,1a
10,6,8,6,4,8,e,g,k,41i,x,7,44,e,8,k,2m,5q,8y,1c,m,o,u,s,2i,w,10
y,8,6,6,6,6,e,e,k,41g,13,x,4q,e,0,y,1y,5i,8y,q,18,m,w,s,2e,14,16
w,8,6,6,6,8,g,g,k,40k,19,2c,1c,k,i,w,38,6a,8y,s,12,u,u,q,2y,w,18
10,a,8,8,8,a,u,u,1k,3z0,1h,1i,5,s,2,j,9,p,8w,1m,u,u,w,18,1k,o,e
w,8,8,6,6,8,i,i,o,3zk,1d,30,s,m,m,s,46,5y,8y,1m,c,u,s,s,2q,u,12
y,8,8,6,6,6,g,g,m,41q,1b,3e,i,c,c,14,4m,6w,8y,1i,8,y,w,s,2w,u,14
10,8,6,6,6,6,g,g,k,41w,t,3g,m,g,c,12,36,6m,8y,1i,8,w,u,u,2m,w,12
w,8,6,6,6,6,e,e,i,412,1b,1h,4s,a,3,1e,m,5i,8y,i,1i,m,s,s,2a,1g,18
y,8,6,6,8,6,i,i,o,40m,1l,3a,o,i,i,w,4m,5w,8y,1k,c,w,s,u,2s,u,10
10,a,8,6,8,8,m,i,s,410,1n,1s,7,q,i,q,70,6k,8w,1i,m,y,u,1i,2a,10,w
10,8,8,8,6,8,o,k,y,40o,1t,2s,h,o,k,9,58,4m,8w,1i,m,y,s,1e,24,12,k
y,8,8,6,8,6,k,k,s,3zw,1v,3e,c,o,o,k,46,4y,8y,1k,g,w,q,16,28,y,s
10,8,8,6,6,6,i,g,k,420,x,36,g,c,c,1k,4m,78,8y,1i,c,w,u,u,2q,u,12
10,a,8,6,8,6,m,k,q,416,1n,1w,7,o,g,m,70,6m,8w,1i,m,y,u,1g,2c,y,y
10,8,6,6,6,8,g,g,m,420,13,38,g,a,c,1c,4q,74,8y,1g,c,w,w,u,2s,w,12
y,a,8,6,6,8,m,k,u,40u,1t,32,3,o,g,8,5c,50,8w,1k,i,w,u,1c,26,y,q
10,8,8,6,6,6,g,g,k,41w,n,3c,o,i,e,1c,30,6k,8y,1i,c,u,s,u,2g,w,y
w,8,6,6,6,8,e,i,k,40c,19,2g,18,k,i,10,34,6c,8y,s,14,s,u,q,2y,u,1a
10,a,8,8,6,8,m,k,s,40c,1f,1c,2,s,m,1g,6u,6g,8w,1i,q,y,u,1g,2a,14,10
10,a,6,8,8,8,o,k,y,40o,1v,2i,h,o,k,f,5e,52,8w,1i,o,w,u,1e,26,10,m
y,8,6,6,6,6,e,g,k,41o,13,3,44,e,8,y,2u,5y,8y,m,1a,o,w,u,2u,u,1a
y,8,6,6,6,6,e,g,k,41o,11,2,3w,e,6,w,2k,5y,8y,12,u,o,w,u,2q,w,18
10,6,8,6,6,6,g,g,k,41y,11,3g,i,e,e,14,3y,6u,8y,1i,8,y,u,u,2q,w,14
w,8,6,6,6,6,e,e,k,410,19,17,4w,e,2,1c,1w,5o,8y,g,1k,m,u,s,2s,14,1c
y,a,6,8,6,8,m,i,w,40g,1t,38,0,o,k,a,4o,4q,8w,1k,i,w,s,18,28,10,o
12,8,8,6,6,6,i,e,m,41u,f,34,q,g,i,24,32,7e,8y,1i,g,u,s,s,2g,10,s
10,a,6,8,6,8,m,i,q,41e,1p,2c,b,o,c,a,6w,6o,8w,1i,k,y,u,1g,2c,w,w
y,8,6,6,6,6,g,e,k,41o,z,e,3m,e,8,s,2k,5y,8y,1e,i,o,w,s,2q,u,18
w,8,6,8,6,6,g,i,m,3yw,17,22,1m,o,o,1a,3i,6k,8y,1e,m,q,s,q,2s,w,14
y,8,6,8,6,6,i,i,m,418,1l,3e,i,g,e,10,4w,6c,8y,1i,c,w,u,s,2w,u,10
w,8,6,6,6,6,g,e,k,41g,13,15,4w,e,4,12,2a,5q,8y,g,1i,m,w,u,2u,w,1c
10,8,6,6,6,6,g,g,k,41y,t,3e,k,g,e,16,3i,6w,8y,1i,8,w,w,s,2o,w,14
12,8,6,8,4,8,g,g,m,41u,h,38,q,i,i,1y,2w,6u,8y,1i,e,u,s,s,2e,10,s
y,8,8,8,6,8,k,k,s,40k,1v,3a,4,o,i,g,4s,4y,8w,1m,g,w,s,16,2a,y,q
y,8,6,8,6,6,i,i,m,3zi,v,2o,1o,12,w,1m,3y,7i,8w,1e,q,u,q,q,2g,12,10
10,8,6,6,6,8,g,i,m,406,11,3y,w,s,m,1i,34,7u,8y,1m,i,w,q,q,2a,16,y
w,8,6,6,6,6,g,g,k,402,z,3a,8,4,4,6,2a,6y,90,i,8,20,s,q,2m,14,1a
y,8,6,6,6,6,g,i,k,406,13,42,s,g,c,g,3a,7a,90,18,c,1a,q,q,2i,12,16
y,8,6,6,6,6,i,g,m,400,z,3g,1g,o,m,12,38,7w,8y,1c,i,10,s,o,2k,12,12
y,6,8,6,4,8,g,g,k,40a,x,3q,6,4,4,3,2y,6o,90,12,a,1g,s,q,2m,12,18
w,6,6,8,4,8,g,g,k,3ze,t,14,2s,k,g,s,2s,7i,8y,i,w,1e,q,s,2a,1g,16
y,a,6,6,6,8,i,g,o,402,z,3s,u,y,q,1k,3a,7s,8y,1m,i,w,q,s,28,16,y
10,8,6,6,6,8,i,g,o,402,z,3q,s,10,q,1o,3c,7o,8y,1m,i,w,q,s,28,16,y
10,8,6,6,6,6,g,i,m,40a,13,46,s,i,g,g,30,78,90,1m,g,u,q,q,2g,12,12
12,a,6,8,6,6,k,i,q,404,x,2u,10,y,10,2c,3s,6y,8w,1o,s,u,o,16,1q,12,10
w,8,6,6,6,8,e,i,k,404,x,3o,i,c,6,8,2y,74,90,s,8,1s,q,q,2m,12,18
y,8,6,8,6,6,i,i,m,3zi,v,2q,1q,10,w,1m,3u,7m,8w,1e,q,u,q,q,2g,12,10
w,8,6,6,6,8,g,g,m,3zs,r,k,40,s,k,u,3e,7u,8y,q,18,u,s,q,2i,14,14
u,8,6,6,6,6,g,e,m,3zi,1h,2u,6,6,8,c,1y,6m,90,i,8,22,q,s,28,1g,18
w,6,6,6,6,6,g,e,k,402,1d,2w,3,1,1,2,s,62,92,i,4,22,s,s,28,y,q
w,8,6,6,6,6,g,g,k,408,x,32,0,0,2,2,26,6c,90,o,8,1w,s,q,2o,12,16
y,a,6,6,8,6,i,i,o,3zo,v,3e,o,16,y,1u,3u,78,8w,1m,k,w,q,s,28,14,10
w,8,6,6,6,6,g,g,k,3zs,v,2o,1a,e,c,i,2q,7c,90,i,g,1s,s,q,2e,1c,16
y,8,6,6,6,8,i,g,o,3zg,r,1w,2i,10,w,1i,40,7m,8w,18,w,s,s,q,2g,12,12
w,8,6,6,6,8,g,g,m,3zk,p,3,4m,s,m,12,3g,7w,8y,o,1g,o,s,q,2g,16,14
10,a,6,6,6,6,i,g,o,40e,v,40,s,i,c,y,32,6g,90,1m,k,w,o,q,28,12,14
w,6,6,6,6,6,g,g,i,402,1d,2w,1,1,0,0,14,6g,90,k,6,20,s,s,2e,y,w
y,6,6,6,6,8,g,g,k,406,z,3s,i,a,8,6,2y,72,90,w,8,1o,s,q,2k,12,18
u,8,6,6,4,8,e,g,k,3za,1r,2q,0,6,4,e,1u,6a,90,k,6,22,q,s,28,14,18
10,8,6,6,6,8,g,i,m,40a,z,42,s,m,i,s,2w,7a,90,1m,g,w,q,o,2e,14,10
w,8,6,6,6,6,g,g,m,3zu,v,2g,1w,i,g,o,2y,7m,8y,q,k,1i,s,q,2e,1a,16
y,6,8,6,6,6,e,g,m,40e,x,3m,1,0,0,3,2w,6e,90,1m,c,w,s,q,2o,10,16
w,6,6,6,6,6,g,g,k,3zs,1b,2u,4,2,4,6,1s,6m,90,k,6,22,q,s,2e,16,18
w,6,8,6,6,6,g,g,k,3zi,t,14,2w,m,g,q,2w,7g,8y,i,y,1c,q,s,2c,1c,18
y,6,8,6,6,6,i,g,m,3zu,v,1a,3e,w,m,14,3i,7s,8y,10,14,q,s,o,2k,10,14
10,8,6,6,6,8,i,g,o,406,z,3w,u,s,o,1i,32,7u,8y,1m,i,w,q,q,2a,16,y
y,8,6,6,6,6,i,g,k,40a,13,46,k,c,8,4,36,72,90,1g,c,12,s,o,2k,10,16
u,8,6,6,6,6,g,e,m,3zo,1d,2s,4,4,6,8,1w,6i,90,k,6,22,q,s,2c,1a,18
10,8,6,6,6,6,i,g,m,40a,11,44,u,m,i,q,2u,7c,90,1m,g,w,q,o,2e,14,10
10,8,6,6,6,8,g,g,m,40c,z,44,s,e,e,m,2y,6y,90,1m,g,y,o,o,2e,12,14
y,8,6,6,6,8,g,g,m,40a,13,44,q,g,c,a,34,76,90,1g,e,12,q,o,2i,12,14
10,8,6,6,6,6,i,g,m,40a,11,44,u,o,i,s,2u,7e,90,1m,g,w,q,o,2e,14,10
y,8,6,6,6,8,g,g,m,404,13,40,y,o,k,10,36,7s,8y,1i,c,12,q,q,2g,14,12
y,6,8,6,6,6,g,g,m,400,z,3u,s,k,e,m,34,7k,8y,12,a,1i,q,q,2i,14,16
14,a,8,6,8,6,m,k,q,3zw,13,2i,12,12,w,2u,4g,6k,8u,1s,10,q,o,16,1s,u,12
10,8,6,6,6,8,g,i,m,408,11,42,w,o,i,12,30,7k,8y,1o,g,w,q,o,2e,14,10
y,8,6,6,6,6,g,i,k,408,11,42,m,c,a,4,36,74,90,1a,c,18,q,q,2k,10,16
y,8,8,6,6,8,i,i,m,3zq,x,3i,s,14,w,1q,3q,7g,8w,1m,k,u,q,s,2a,14,10
10,8,6,6,6,6,i,g,o,408,13,42,w,o,k,14,34,7m,8y,1o,g,w,q,o,2e,14,y
y,8,6,8,6,6,i,g,o,3zo,x,36,1g,10,s,1k,3m,7u,8w,1i,m,u,q,q,2g,14,y
w,8,6,6,6,8,e,i,k,3zw,t,30,1e,i,e,m,30,7k,8y,q,g,1m,s,q,2i,16,16
y,8,6,6,6,6,i,g,m,404,11,40,u,m,g,q,38,7m,8y,1c,c,18,q,q,2i,12,14
w,8,6,6,4,8,e,g,k,406,19,2y,3,0,1,0,s,68,92,i,4,22,s,s,2c,10,q
14,a,6,8,6,8,k,k,q,400,13,2k,14,10,10,2o,4a,6k,8u,1s,w,s,o,18,1q,w,12
10,8,8,6,6,8,i,g,o,406,x,3k,w,u,s,1w,34,7q,8y,1m,k,w,q,u,24,16,w
10,8,6,6,6,6,i,g,k,40e,z,44,m,8,6,4,3a,6o,90,1m,g,w,q,o,2g,10,16
w,6,6,6,6,6,g,g,k,402,1b,2u,1,0,0,4,1g,6e,90,k,6,20,s,s,2g,10,12
y,6,6,6,6,8,e,g,m,408,x,3s,a,6,4,0,2w,6y,90,q,8,1u,s,q,2m,12,16
10,8,8,6,6,6,i,i,m,40a,x,3w,w,o,m,1a,2s,7i,8y,1o,i,w,q,q,28,16,y
w,8,6,6,6,8,g,g,m,3zi,p,3,4m,s,q,16,3m,7w,8y,o,1g,o,s,q,2g,16,14
w,8,6,6,6,8,g,g,m,3zw,v,26,2e,o,i,u,36,7s,8y,w,s,14,s,q,2i,14,14
10,8,6,6,8,6,i,g,o,406,z,3u,u,u,q,1m,32,7y,8y,1m,i,w,q,s,28,16,w
y,8,6,6,6,6,i,g,m,404,11,3y,w,k,i,s,38,7o,8y,1c,a,1a,q,q,2i,12,14
10,8,6,6,6,6,i,g,o,408,13,42,w,o,k,14,34,7m,8y,1o,g,w,o,q,2e,14,10
y,8,8,6,6,6,g,g,m,40g,11,44,e,6,2,3,3c,6i,90,1m,e,w,q,q,2i,10,16
w,8,6,6,6,8,e,i,k,402,t,3q,o,c,a,e,32,7e,8y,m,8,1y,s,q,2k,14,16
y,a,6,6,6,8,i,i,m,3zw,x,3m,o,16,s,1q,3u,78,8w,1o,i,w,q,q,2a,14,10
w,8,6,8,6,6,g,g,m,3zy,x,2w,1q,m,k,s,36,7q,8y,12,i,1a,q,q,2i,14,14
y,8,6,6,6,6,i,g,m,400,z,3a,1k,q,m,10,38,7w,8y,1c,i,10,s,o,2k,12,12
y,a,6,6,6,8,i,g,o,402,z,3s,q,10,s,1k,3e,7o,8y,1m,g,y,q,s,28,16,y
w,8,6,6,6,6,g,e,m,404,11,36,4,4,2,4,26,6q,90,i,8,22,q,s,2k,16,18
y,6,6,6,6,8,g,g,k,404,z,3u,k,c,8,a,30,76,90,w,8,1o,s,o,2m,12,16
w,6,6,6,6,8,g,g,k,3zc,r,t,50,m,k,y,32,7o,8y,i,1k,q,q,q,2a,1i,14
u,8,6,6,6,6,g,e,k,3zo,1h,2u,2,2,2,6,1s,6e,90,k,6,22,q,s,2c,12,16
w,8,6,6,6,6,g,e,m,408,11,32,1,0,0,1,1i,6e,92,o,8,1s,u,s,2i,12,w
w,8,6,6,6,6,g,g,k,40a,z,36,1,0,0,0,20,6a,90,y,8,1m,s,s,2k,12,12
y,8,6,6,6,6,g,g,m,40e,11,40,c,4,4,3,36,6q,90,1g,c,12,s,q,2k,10,16
y,8,6,6,6,8,g,g,m,404,11,40,w,m,i,w,38,7o,8y,1g,c,14,q,q,2g,14,12
1y,o,i,i,m,q,20,22,38,3s6,z,q,7,h,9,0,o,2q,b4,16,o,q,i,e,w,q,s
24,s,m,o,q,u,2e,2i,3y,3ui,2z,2,j,a,m,1a,k,2m,b8,1c,o,i,i,e,12,y,u
12,a,8,6,6,8,k,g,o,40s,t,2a,y,e,a,18,62,7m,8y,1i,o,u,s,1c,28,s,y
14,a,6,8,6,8,o,i,q,40g,1h,1i,10,o,a,18,6m,7g,8w,1k,y,u,q,1g,24,y,12
1k,k,i,k,q,u,2c,2g,46,3uw,4s,1r,8,3d,25,2,1w,m,am,u,18,w,u,o,16,s,12
1s,i,i,g,k,q,22,26,3o,3si,2y,1d,10,13,d,11,8,18,ay,w,12,k,o,g,12,q,y
12,a,8,6,8,6,o,i,q,40c,1d,1i,s,q,a,12,6o,76,8w,1k,s,w,s,1g,26,12,12
1a,c,a,a,a,e,12,16,1q,3zo,l,22,16,1d,k,x,e,3m,9i,1c,10,w,12,12,1u,1c,y
28,q,i,m,m,s,1y,1y,2s,3sa,1b,y,n,i,4,1e,1i,2s,bg,16,e,i,c,c,m,i,o
16,a,6,8,6,6,k,i,o,40e,l,2g,1g,o,i,3c,5a,6y,90,1k,s,s,o,16,1w,o,y
10,a,6,8,6,6,k,g,m,41o,z,2s,c,6,6,10,60,7c,8y,1g,g,y,u,18,2g,u,y
28,q,k,m,m,q,20,1y,2s,3s4,15,s,f,g,a,1g,1g,2q,bg,16,i,g,c,a,o,i,o
16,c,6,8,8,8,m,k,s,400,1j,2a,1c,q,j,3c,62,7m,8u,26,m,s,o,1c,20,o,14
1c,c,a,8,a,a,u,s,12,3zo,j,1o,14,h,m,1q,5m,6a,9s,1a,w,q,14,q,24,u,1e
1a,e,8,a,a,e,10,12,1k,400,l,1u,1a,17,c,v,1e,4s,9g,1e,10,y,12,10,1w,18,12
1u,i,g,g,i,m,1k,1k,2a,3sw,22,h,18,4,f,u,24,46,b0,14,y,m,q,k,14,q,m
22,m,i,i,k,o,1w,1w,2u,3rk,h,1q,6,3,3,c,w,1i,ba,12,m,q,g,e,u,m,u
16,c,8,8,8,a,q,o,w,3zy,15,1q,1e,s,15,2i,5s,7c,9e,1g,u,u,s,18,24,10,1a
1c,c,a,8,a,a,q,q,y,3z2,1,1m,18,j,u,48,54,6o,9w,1a,u,o,12,m,20,m,1a
12,a,6,8,6,8,k,i,q,40o,17,22,k,m,8,i,6u,76,8y,1i,m,w,u,1e,2c,w,y
12,a,8,6,8,6,o,k,s,404,1h,1e,g,u,g,1g,6g,6u,8w,1i,u,w,u,1i,26,16,12
26,u,m,m,o,s,24,20,2w,3t8,3d,a,4,k,q,1s,26,3a,bc,1a,q,i,e,e,w,m,q
16,a,8,8,6,a,q,m,u,3zy,1n,20,1c,s,v,2c,5q,7w,8w,24,m,s,s,1e,20,14,1a
24,u,m,o,o,u,26,24,32,3u8,3h,f,9,k,m,1k,28,48,ba,1c,q,k,e,e,y,q,q
12,a,6,8,6,8,k,i,q,40q,17,22,g,o,6,g,6w,74,8y,1g,o,w,s,1g,2e,w,y
12,a,6,8,6,8,m,i,q,40m,1d,1w,e,s,8,m,6w,6y,8y,1g,o,y,s,1g,2c,y,10
14,a,8,8,6,8,q,k,u,408,1n,1k,18,s,3,1o,5y,7y,8u,1s,w,u,s,1g,1y,16,16
22,q,k,m,m,s,24,1y,2y,3tm,2v,10,p,m,6,18,26,4c,b6,1g,o,k,i,g,y,o,q
18,c,8,6,8,8,m,k,q,3zw,f,1k,16,y,4,4w,4u,76,9u,y,w,q,m,14,1s,k,10
1e,c,a,8,c,c,w,u,1a,3zg,3,1m,u,t,12,6,54,5u,9s,1e,u,w,y,u,20,w,1c
1s,o,i,k,o,q,26,2a,3u,3wa,k,v,9,1f,f,w,i,1e,aw,w,14,y,o,q,18,u,w
26,q,i,m,m,q,1y,1y,2s,3sc,1d,16,l,k,0,1i,1g,2c,be,18,i,k,c,c,o,i,o
28,s,k,m,m,q,20,1y,2s,3rk,z,m,5,k,m,1o,1i,2g,bg,16,o,g,c,e,o,k,o
24,o,i,k,m,q,1w,1w,2s,3sa,15,1c,d,e,4,1g,1c,26,ba,1c,g,o,e,e,q,i,o
14,a,8,8,8,8,s,q,10,402,1r,10,1a,y,n,1a,4a,6m,8s,1u,y,u,s,1g,28,1a,18
12,a,6,6,6,8,i,g,m,41k,j,2y,m,c,c,28,4s,7c,8y,1g,k,u,s,y,2g,s,y
22,q,i,m,o,s,24,26,38,3sq,1d,w,17,0,l,g,1e,3g,b8,1e,g,o,g,g,u,q,s
1m,i,k,i,o,s,28,2c,3y,3vc,42,1z,a,2t,1t,c,1i,o,am,w,16,w,w,o,18,u,10
28,s,m,m,o,q,24,1y,2w,3sw,31,g,2,k,s,1q,24,2w,bc,1a,q,i,e,e,u,m,s
1c,c,a,6,a,a,m,o,w,3ym,c,1q,1i,o,1s,4i,2q,5k,9y,18,s,m,q,o,1m,u,i
1y,k,g,i,i,o,1s,1s,2m,3tc,e,c,m,f,1,3,y,1i,b6,12,m,i,g,e,m,g,m
1c,c,a,8,a,c,s,o,12,3za,7,1m,12,f,s,2y,5g,6i,9u,1a,w,o,14,o,22,q,1e
2c,q,m,m,o,q,22,1y,2u,3qw,l,c,a,a,o,1q,1y,2k,be,18,q,i,c,e,s,m,s
1k,e,a,c,a,c,10,10,1i,3vc,2u,14,c,1a,1g,1q,y,10,au,u,o,k,m,k,16,m,g
12,a,6,6,6,8,i,i,m,41c,p,2o,o,a,8,1o,5i,7k,8y,1g,k,w,s,16,2c,u,y
26,o,i,m,k,s,1y,1w,2u,3s6,1p,1g,n,i,3,1i,1s,1y,be,1a,i,k,e,e,q,m,s
1u,i,e,g,g,k,1i,1g,28,3t2,24,9,18,7,a,10,1c,3i,b0,10,10,m,o,k,10,o,k
12,a,8,8,6,8,q,k,w,3zu,1f,w,s,w,k,12,60,74,8w,1i,10,w,s,1i,2a,14,16
12,8,8,6,6,6,i,g,m,41o,p,2w,i,a,a,1u,54,7e,8y,1g,i,w,s,10,2i,u,y
18,c,8,8,8,c,s,s,12,40c,1h,1s,1e,8,11,1s,52,68,9a,1k,w,u,14,u,28,12,1c
28,s,m,o,o,u,28,26,38,3sa,1t,i,1,2,0,12,1y,3w,bc,18,q,k,c,g,w,o,s
14,e,8,8,a,c,w,y,1e,41u,45,2c,1g,e,1r,16,1q,4e,8o,2e,k,u,1c,14,1y,16,14
14,c,6,8,6,8,o,i,q,40a,1f,1y,1a,m,0,2e,68,7i,8u,1s,w,s,q,1c,22,q,12
10,8,8,6,6,8,i,i,o,41q,1f,2w,6,a,6,s,66,6s,8y,1g,e,y,w,18,2g,w,y
12,8,8,6,8,6,m,i,q,40y,1d,22,4,q,6,k,6u,6y,8y,1g,m,y,s,1i,2c,y,y
1k,c,a,a,c,a,y,w,1c,3vu,2k,1k,f,1c,1s,2o,1e,2k,ao,w,q,s,i,k,1c,o,g
1i,e,8,a,a,c,u,u,1a,3wc,24,1k,1,u,22,38,1u,3i,ag,12,q,q,m,m,1e,q,g
28,s,k,m,o,s,24,20,2y,3ru,x,k,l,5,8,1i,28,2w,be,18,q,i,c,e,u,m,u
1w,k,g,g,g,m,1m,1m,2g,3t6,14,2,y,2,8,u,o,1k,b2,16,s,i,o,i,u,k,m
18,a,8,8,6,8,k,i,q,3zy,p,2q,1o,12,s,44,48,6m,9o,1a,m,o,o,y,1s,m,u
12,a,8,6,8,8,m,i,s,40c,1f,1g,u,q,c,14,6k,7a,8w,1k,u,w,s,1g,24,14,10
26,s,m,m,o,s,22,20,2y,3ta,3h,k,3,m,q,1o,26,3o,bc,1c,o,i,e,e,w,o,o
1c,c,a,8,8,a,q,m,w,3yu,6,1k,12,1,16,4y,4i,68,9w,1a,s,o,y,k,1u,i,12
16,e,8,8,8,c,u,u,16,414,23,1i,1e,4,17,1g,3m,5o,8s,1y,10,s,18,w,26,14,1a
1a,a,8,8,8,8,o,k,s,3zu,h,1i,1a,s,d,50,5a,72,9u,10,w,q,o,12,1u,k,16
1u,i,i,g,g,m,1o,1m,2e,3sy,28,r,12,c,5,2,22,4g,b0,14,w,o,q,m,18,q,o
1a,c,a,8,8,c,u,q,14,406,z,1o,1a,b,0,1e,5m,66,9q,18,y,q,16,q,26,w,1e
14,a,8,8,6,8,o,k,s,40e,1p,1o,18,q,0,1u,64,7u,8u,1o,10,u,s,1e,1w,14,14
1a,c,8,8,8,c,q,q,y,400,t,1k,1c,4,7,2q,5q,6s,9s,14,y,s,10,s,26,w,1c
14,a,6,8,6,8,k,g,o,40i,j,2c,1c,i,e,2k,5e,7g,8w,1k,s,u,o,18,22,q,y
12,8,8,8,6,8,m,i,s,40m,1f,1q,8,q,e,12,6o,6s,8w,1i,o,y,u,1g,28,14,10
10,a,6,8,6,8,k,i,o,41k,1j,2k,5,k,4,e,6q,6w,8w,1i,i,y,u,1g,2c,w,w
22,m,i,k,k,q,1w,1u,2q,3so,x,1e,7,2,2,10,18,1y,ba,18,g,q,g,c,o,i,m
12,a,8,6,8,6,o,i,q,40c,1f,1k,o,s,a,14,6m,74,8w,1k,s,w,s,1i,24,14,10
2a,q,k,k,m,s,20,1w,2s,3s4,t,k,b,k,c,12,1i,2s,bi,16,m,e,e,a,o,i,o
1y,m,g,i,k,m,1s,1q,2k,3sq,0,s,i,3,c,10,16,2a,b4,1a,m,o,k,g,w,i,o
26,u,o,o,q,u,2e,2e,3s,3v4,3f,11,1,2,s,1e,y,3e,ba,1a,s,i,g,c,12,w,u
14,a,6,8,6,8,k,i,q,40k,19,1q,16,k,e,16,6g,7g,8w,1k,u,u,q,1e,28,s,y
18,c,8,8,8,a,o,m,w,3zy,j,1c,1c,m,r,3c,5w,7k,9s,10,y,s,u,14,22,s,1a
2a,s,m,m,m,s,22,1y,2w,3ru,1v,e,8,i,u,1s,1y,2m,be,18,q,i,c,e,s,m,s
24,q,i,m,m,q,20,1y,2w,3se,1t,14,x,g,7,1m,1w,22,bc,1c,i,k,e,g,s,m,s
24,u,o,q,q,w,2e,2i,42,3wm,35,z,1b,f,u,1y,m,2u,b8,1a,u,k,i,c,14,y,u
26,q,k,k,m,s,1y,1y,2s,3se,1d,12,p,k,0,1e,1i,2m,bg,16,e,k,e,a,o,i,m
18,c,8,8,8,c,o,o,w,3zy,n,1g,1c,g,h,3e,5s,7c,9s,12,y,s,w,y,24,s,1a
1m,e,c,a,c,e,14,12,1q,3uk,3e,m,g,1c,18,12,c,g,ay,s,m,o,k,k,12,m,g
12,a,8,8,6,8,o,k,s,408,1h,16,y,s,e,16,6a,7g,8w,1k,y,w,s,1g,22,18,12
1i,e,a,c,a,c,y,u,1a,3vs,2o,1c,p,s,1q,3a,3s,4a,as,u,q,y,i,w,1c,i,10
1m,g,c,e,e,g,1c,1g,24,3um,36,a,6,1c,q,r,7,4u,ae,16,10,u,y,u,1e,14,u
1k,g,c,c,e,e,1a,1a,1s,3v4,30,s,3,1a,u,z,14,5k,ac,18,y,w,u,w,1g,14,w
2a,s,k,m,m,s,20,1w,2u,3r4,f,g,2,g,q,1g,1o,2k,bg,16,q,g,c,e,q,i,q
28,u,o,o,o,u,2a,26,38,3us,3v,1b,g,a,w,1e,24,4a,bc,1a,s,i,e,c,10,q,s
28,q,k,m,m,q,20,1y,2s,3s0,13,s,f,k,c,1i,1g,2m,bg,16,m,e,e,c,m,k,m
1g,i,c,g,e,m,1m,1u,32,3tm,3c,16,1l,10,15,5,k,2,9y,18,10,12,y,y,1g,s,12
1m,g,a,e,a,e,12,12,1o,3u4,3k,m,f,1k,1o,20,12,2m,ay,u,o,u,i,s,16,q,e
12,a,8,8,6,8,q,k,u,404,1j,12,w,w,e,y,66,7g,8w,1i,10,w,s,1i,26,16,14
12,c,8,6,8,8,q,o,w,3zu,1f,o,16,12,8,o,5a,7c,8u,1m,14,u,q,1i,2a,16,18
10,8,8,6,6,6,i,g,m,422,15,34,c,a,8,1e,56,76,8y,1g,e,w,w,u,2s,u,10
14,a,8,6,6,8,m,i,o,40i,19,1s,1c,k,e,1s,64,7i,8w,1k,y,s,q,1a,26,s,y
12,a,6,6,6,8,i,i,m,41a,p,2m,q,a,8,1k,5m,7k,8y,1g,k,w,s,18,2c,s,y
16,c,a,8,6,c,s,q,12,404,1l,20,1c,k,1d,20,54,6e,92,1u,u,s,12,10,26,14,1a
28,q,k,m,m,q,20,1w,2u,3s0,19,u,d,k,c,1o,1g,2e,bg,16,m,g,e,c,o,i,o
16,c,8,6,8,8,m,k,s,400,17,22,1a,q,d,3u,5o,7i,94,1m,w,s,o,18,1y,o,12
1u,k,e,g,g,m,1k,1m,2e,3ti,1m,9,1a,a,3,a,i,u,b2,y,y,i,k,g,q,i,m
12,a,8,6,8,6,m,i,s,40e,1d,1m,s,o,a,w,6s,76,8y,1i,s,w,s,1g,28,y,10
1a,c,8,8,8,a,o,o,u,3zy,t,1m,1a,i,9,46,5e,7c,9u,14,w,q,10,s,1y,q,18
18,e,8,8,8,e,u,s,16,40g,19,1o,1c,d,h,10,4w,5u,9k,1a,10,s,18,o,28,10,1e
12,a,8,8,6,8,o,k,s,406,1j,18,u,s,e,18,6c,7a,8w,1k,w,w,s,1i,24,16,14
20,q,k,q,o,u,2c,2g,3s,3we,27,6,1z,f,8,1e,k,34,b0,1i,q,q,i,i,14,u,u
1c,e,a,8,c,e,14,14,1q,3yi,s,1y,14,15,s,1b,k,4i,9o,1c,w,16,q,16,1q,1c,y
28,s,k,m,o,s,22,1y,2w,3rw,z,m,p,0,a,1q,26,2o,be,18,q,g,c,g,s,m,s
10,a,6,6,8,6,i,g,m,41y,11,2y,c,a,8,1e,5i,7a,8y,1g,g,w,u,w,2q,u,y
1g,e,a,a,a,c,w,u,16,3xe,1g,1m,3,0,18,2m,4o,5s,a6,1a,s,q,w,w,1q,o,18
10,a,6,8,6,6,k,i,o,41o,1d,2o,0,c,4,i,6k,70,8y,1g,g,y,u,1g,2c,w,w
20,m,i,k,k,o,1w,1s,2q,3sy,n,18,4,9,2,i,18,1s,b8,14,m,o,e,e,o,g,m
1g,e,8,a,a,c,s,q,16,3x2,1m,1g,a,a,22,3w,2y,52,a6,1a,q,m,w,g,1m,s,m
1q,g,e,e,e,k,1c,1a,1y,3t6,3k,l,14,o,n,1e,2g,46,b0,w,12,i,u,q,16,q,q
26,q,k,m,o,u,26,24,38,3sc,15,u,13,1,5,q,1s,3i,ba,1c,m,k,e,g,u,o,s
18,c,8,8,8,a,s,q,10,404,1b,1u,1e,i,15,24,5m,6m,9g,1e,w,s,12,y,26,10,1c
28,q,k,m,m,q,20,1w,2u,3s4,19,w,j,m,a,1i,1g,2m,bg,16,m,e,e,c,o,i,o
2a,s,k,m,m,q,20,1y,2s,3ra,n,i,0,i,o,1m,1i,2i,bg,16,q,e,e,c,q,i,q
26,w,o,o,q,u,2c,2a,3g,3v8,3p,1h,6,4,y,1c,1m,46,ba,1c,q,k,e,e,10,u,q
14,a,8,8,8,8,s,m,y,402,1n,1a,1a,y,j,14,5i,7a,8s,24,o,u,s,1g,26,16,1a
1a,e,8,8,a,e,w,y,1c,40a,t,1q,18,13,8,d,3c,5q,9k,1c,10,u,14,w,22,10,1a
1i,e,a,c,c,e,16,14,1m,3wi,26,1k,f,s,14,v,22,5q,a2,1e,s,14,o,10,1o,12,10
1c,e,a,a,a,e,12,12,1k,3yw,i,1s,u,r,s,15,1q,5a,9q,1c,w,14,q,14,1u,16,12
1o,e,c,e,c,e,16,16,1u,3uc,38,8,6,1m,o,1c,g,1c,aw,w,w,m,i,m,10,m,g
18,c,8,6,8,a,o,k,u,3zy,t,1m,1c,s,p,3q,5s,7q,9k,1a,w,q,s,16,20,o,18
1g,e,a,a,c,e,y,y,1c,3xe,1o,1i,b,a,14,e,4c,5k,a2,1c,u,12,k,12,1q,w,16
14,a,8,8,8,8,q,o,y,404,1p,14,1a,y,d,10,5a,78,8s,20,s,u,s,1g,28,16,18
16,a,8,8,6,8,o,k,s,406,17,1o,1a,q,d,2m,68,7s,94,1k,w,u,q,1c,20,u,14
12,a,6,8,6,6,i,g,m,41g,d,2u,u,g,g,2m,48,7e,8w,1i,m,u,q,y,2a,u,w
1g,e,a,c,c,e,14,16,1m,3xg,1i,1s,d,k,18,11,1c,5k,9u,1i,u,14,m,14,1o,16,10
14,a,8,6,6,8,k,g,o,40k,j,2k,1c,m,e,3a,58,6y,90,1i,s,s,o,16,1y,o,y
1q,i,e,e,e,i,1c,1a,20,3t4,38,d,w,e,8,1g,1a,3u,b0,w,14,g,s,m,14,q,i
12,c,6,8,6,8,m,g,q,40k,1b,1o,12,k,e,10,6o,7e,8w,1k,u,u,s,1e,28,s,10
2a,s,m,m,o,s,22,1y,2w,3s6,2b,c,a,k,s,1s,22,2m,be,18,q,i,c,e,u,m,s
2a,u,m,m,q,s,26,22,32,3sk,2l,2,i,8,g,1s,26,3q,be,18,q,i,e,e,u,q,q
14,a,6,6,8,6,k,g,m,40w,b,2m,12,i,e,2w,54,72,8w,1i,s,s,q,14,24,o,y
14,c,8,8,8,c,s,w,16,40s,2x,1o,1e,q,1p,1u,2o,5o,8q,26,o,w,14,1a,22,16,18
14,a,8,6,6,8,k,i,o,40e,n,28,1e,k,i,2m,5c,7k,8w,1k,u,u,o,18,20,q,y
1s,g,g,e,e,i,1i,1g,2a,3tg,2w,h,16,e,a,7,4,g,b2,w,q,k,i,i,q,k,i
1m,g,c,e,c,g,16,14,1o,3u2,3k,e,0,w,6,1o,2o,4o,aw,w,y,o,q,u,1c,s,s
16,c,8,6,8,a,q,o,y,3zu,1p,26,1c,u,19,22,5k,74,8w,26,k,u,u,1a,24,16,1a
1m,g,a,c,c,g,14,14,1s,3ua,3i,e,c,1g,w,14,c,s,ay,s,o,q,g,m,12,m,g
1a,c,8,6,8,a,o,m,u,3zy,v,1o,1c,o,b,4e,58,7k,9q,18,u,q,q,12,1y,k,18
10,a,6,6,8,6,i,i,m,41u,15,2u,8,8,4,10,60,78,8y,1g,g,w,w,16,2i,u,y
12,8,8,6,6,8,k,g,o,41e,11,2k,a,a,6,o,6e,7a,8y,1g,i,y,u,1e,2c,u,y
14,a,8,8,6,8,q,k,w,406,1j,1a,16,u,4,10,60,7w,8u,1q,y,w,q,1g,24,16,14
1a,e,8,8,a,c,u,s,14,406,z,1q,18,j,8,10,5k,60,9q,18,y,s,16,o,26,w,1e
18,a,8,8,8,a,m,m,s,3zy,11,1u,1c,s,p,3q,5s,7q,9e,1i,u,q,q,18,20,o,16
16,e,a,8,a,c,y,10,1i,41m,2z,26,1e,j,p,6,1m,4i,8u,22,u,u,1a,y,20,16,14
2a,q,k,m,m,q,20,1y,2s,3ry,l,g,9,i,g,16,1g,2q,bi,16,m,e,e,a,o,k,m
1m,i,i,i,k,q,20,28,3o,3vg,2w,27,u,1v,h,3,s,u,am,w,16,w,y,o,1c,s,y
2a,q,k,m,m,q,20,1y,2s,3ry,n,i,7,g,g,16,1e,2s,bi,14,o,e,e,a,o,i,o
2a,q,k,m,m,q,20,1y,2s,3rs,p,k,7,g,g,1g,1c,2s,bi,14,o,c,e,a,o,i,o
26,o,k,k,m,q,20,1w,2s,3sg,1f,1c,n,k,3,1e,1g,2a,be,18,g,k,e,c,o,i,m
1g,e,a,c,c,g,18,1a,20,3x4,1g,20,n,k,12,13,d,3i,9s,1g,u,16,o,18,1k,18,y
14,c,8,8,8,a,s,q,12,408,1v,1o,1c,q,1b,1u,4g,6i,8q,28,o,u,w,1a,26,18,1a
10,a,8,6,8,8,k,k,q,40u,1h,1w,2,q,c,u,6s,6u,8w,1i,m,y,u,1i,2a,10,y
16,e,a,8,a,c,y,12,1k,41w,3p,2m,1e,l,l,3,18,3w,8q,2a,q,u,1a,y,1y,18,12
1y,k,i,i,k,o,1w,1s,2m,3t2,7,i,e,0,0,m,20,4e,b0,1i,k,s,k,i,12,q,o
12,a,8,6,8,6,o,k,s,402,1f,1a,i,u,i,1e,6g,6u,8w,1i,u,w,u,1i,26,16,14
1o,g,e,c,e,i,1c,18,1u,3tq,3q,f,s,y,b,i,2u,4m,ay,u,10,o,s,u,1e,q,s
24,o,i,m,k,q,1y,1w,2s,3sg,17,1e,j,g,3,1c,1e,22,bc,18,g,o,e,c,o,i,o
1w,k,i,k,m,q,26,2e,3w,3sg,1e,c,o,z,b,9,6,1a,b0,14,q,q,k,i,10,w,w
14,a,6,8,6,6,k,i,m,40k,j,2e,18,i,e,2e,5e,7i,8w,1k,s,s,q,18,24,q,w
1a,c,8,8,8,a,q,m,y,3zy,r,1k,1a,c,9,3k,5q,74,9s,14,y,q,10,u,22,s,1c
16,c,6,8,6,8,m,i,q,404,p,20,1c,w,c,42,54,74,9g,18,w,q,o,14,1u,m,y
1m,e,a,c,c,e,12,12,1o,3uo,3c,o,e,1c,1a,18,e,m,aw,u,m,o,i,m,12,m,i
24,o,i,k,k,q,1w,1w,2s,3rs,17,22,b,e,9,10,1k,1m,bc,1a,g,m,g,c,s,k,u
2a,u,m,m,o,u,24,22,32,3s0,1x,8,c,8,a,1s,24,3k,be,18,q,i,c,e,w,o,q
22,q,i,m,m,q,24,20,34,3sk,1r,10,11,8,h,w,1i,2u,ba,1c,g,o,g,e,u,q,s
16,c,8,8,8,c,u,w,18,41i,3b,20,1e,g,1n,1m,2o,5g,8o,28,s,u,16,14,22,14,1a
12,8,6,8,6,6,i,g,m,41u,v,30,g,a,a,1o,54,7c,8y,1g,g,w,u,w,2m,u,10
1e,e,c,c,e,g,1c,1i,2g,3vs,22,1u,x,6,m,b,l,o,9s,18,10,18,o,18,1i,12,10
12,c,8,6,8,8,q,m,u,402,1j,y,14,y,6,u,5s,7i,8u,1o,10,w,q,1i,28,14,16
1g,e,8,a,a,c,s,u,16,3ws,1s,1k,g,y,1y,3e,1s,34,ae,12,q,o,o,i,1g,q,g
26,o,k,k,m,q,20,1w,2s,3si,1d,18,n,g,3,1c,1g,2i,be,18,g,k,c,c,m,i,o
28,q,k,m,m,q,20,1w,2u,3s2,13,s,f,i,c,1i,1e,2m,bg,16,k,g,e,a,o,i,o
28,u,o,o,o,u,28,26,36,3ue,3t,x,i,8,s,1m,22,44,bc,1a,q,k,e,c,y,q,s
24,u,o,q,q,u,2e,2e,3o,3w4,3d,13,17,1,u,1k,14,3w,b8,1c,s,m,e,e,12,w,s
16,c,a,6,8,c,s,s,14,40k,1p,1k,1e,g,1h,22,44,62,8s,1y,y,u,16,w,28,14,1c
24,s,m,o,o,u,28,26,36,3uo,3b,b,t,i,o,14,22,4g,b8,1e,q,k,g,e,10,s,q
1g,e,a,c,c,e,14,18,1s,3xq,16,1u,d,o,14,19,k,4w,9s,1i,u,16,k,1a,1k,18,y
18,c,a,8,8,a,s,q,12,406,17,1s,1c,a,t,20,5k,6g,9m,1a,w,s,16,q,28,10,1c
1i,e,a,c,c,e,10,w,1e,3w4,2g,1k,r,m,16,1k,3y,58,ai,12,q,10,k,12,1m,q,12
2a,s,m,m,o,q,22,1y,2u,3rc,19,e,8,i,s,1s,1w,2m,be,18,q,i,c,e,s,m,q
1o,i,c,e,g,i,1e,1e,22,3tk,3o,n,10,y,0,n,20,50,au,y,12,o,w,q,1e,w,s
26,u,o,m,q,s,28,22,34,3u8,3p,p,8,g,q,1o,28,42,bc,1a,q,k,e,c,y,q,q
24,u,o,m,q,s,28,26,36,3uk,3j,l,b,g,o,1g,24,4c,ba,1c,q,k,e,e,10,q,s
10,a,8,6,6,8,k,i,q,418,1d,2a,0,m,6,c,6w,6y,8y,1g,k,y,u,1g,2c,y,w
14,a,8,6,8,6,m,i,o,40c,11,20,1e,k,c,2c,5q,7m,8w,1k,y,s,q,18,24,q,y
14,c,6,8,6,8,o,k,s,40e,1f,1i,16,q,0,1y,66,7s,90,1g,12,u,q,1g,1w,12,14
12,a,8,8,6,8,k,i,q,40k,1b,1q,12,k,c,10,6m,7e,8w,1k,s,w,q,1e,2a,s,10
26,q,i,m,m,q,20,1w,2u,3s2,1d,18,j,m,4,1o,1g,2a,be,18,i,k,e,e,o,i,o
1o,e,c,e,c,e,16,18,1u,3u8,3e,6,c,1i,k,16,c,y,aw,w,s,o,i,k,10,m,g
16,c,8,8,8,c,u,u,16,416,2r,1s,1c,i,1j,1o,32,5q,8o,28,q,w,14,14,24,14,1a
16,c,8,8,8,c,s,w,16,414,2p,1s,1c,g,1h,1o,36,5q,8o,28,q,u,14,16,24,16,18
12,c,8,8,8,a,s,q,12,3zy,1n,q,18,12,p,1a,3s,64,8s,1m,16,u,s,1i,28,1a,18
1e,e,8,a,a,a,s,q,10,3xw,10,1k,k,b,1k,4g,4q,5k,a6,16,s,o,y,m,1q,i,16
20,k,i,i,k,o,1u,1s,2q,3t4,b,y,i,j,6,4,14,1m,b8,12,m,o,e,e,m,g,m
20,o,i,k,o,s,2a,2e,3y,3sq,n,1i,b,1,j,c,c,1w,b0,1c,k,s,i,g,10,w,y
2a,q,k,m,m,s,20,1w,2u,3ru,n,c,f,g,k,1e,1s,2m,bg,16,q,e,e,e,q,i,o
14,c,8,6,8,8,s,m,w,3zy,1n,1q,1a,u,l,1k,5o,7m,8s,24,o,u,s,1g,22,16,18
2c,q,m,m,o,q,22,1y,2w,3qu,h,e,6,a,m,1q,1y,2k,be,18,q,i,c,e,s,m,s
1c,c,a,8,c,c,y,w,1a,3zw,f,1o,12,11,q,b,48,5u,9q,1c,w,w,10,u,22,10,1a
1a,c,8,8,6,a,m,k,s,3zm,l,1y,1e,y,18,4o,3i,7e,9u,16,s,o,k,y,1s,q,o
28,s,i,m,m,s,1y,1y,2s,3ry,z,q,d,i,e,1i,1e,2m,bg,16,m,e,e,c,m,k,m
1k,e,c,c,a,e,10,y,1g,3v0,34,10,p,10,1c,2s,2w,4e,au,u,s,w,k,u,1c,o,u
16,a,8,6,6,8,m,g,q,406,v,22,1g,o,g,32,5a,7k,8y,1i,10,s,o,18,1y,q,w
10,8,8,6,8,6,k,i,q,41g,1n,2u,3,i,8,g,6g,6e,8w,1i,i,y,s,1g,2a,w,u
14,a,6,6,6,8,i,i,m,410,d,2o,12,g,e,2u,52,72,8w,1i,q,u,q,14,22,q,y
1m,e,a,c,c,c,12,10,1k,3ui,3c,16,r,1m,1w,1u,y,2u,aw,u,o,w,e,s,18,o,a
1a,e,8,a,a,e,y,12,1k,3zy,j,1u,1a,15,a,t,1q,52,9g,1g,y,y,12,10,1w,18,12
1s,i,e,e,g,k,1i,1g,2c,3tq,2a,l,1g,m,5,6,a,e,ay,10,10,c,o,e,s,i,m
14,a,8,8,6,8,m,k,q,40k,1j,1k,1a,m,8,1q,6i,7i,8u,1o,10,s,q,1e,24,s,12
14,a,8,6,8,6,o,k,s,40c,1j,1e,14,q,8,1c,6c,7m,8w,1k,10,u,s,1g,20,14,12
14,a,8,8,6,8,m,k,q,40i,1j,1m,16,m,8,1k,6m,7i,8u,1o,y,u,q,1e,24,u,12
16,a,8,8,8,8,o,m,u,404,1b,1q,1a,s,n,2i,5w,7y,96,1k,w,s,s,1c,1y,12,18
1a,c,8,8,8,8,o,k,u,3zc,b,1y,1g,y,1c,4i,3e,6u,9u,18,q,o,m,w,1q,q,m
16,a,8,6,8,8,k,i,o,406,r,2c,1e,u,g,3q,56,70,9a,1e,s,s,o,14,1w,m,y
26,q,k,m,o,s,22,20,2y,3sg,1h,o,z,0,1,1k,2a,2o,bc,1a,q,g,c,g,s,o,s
20,m,g,i,k,o,1s,1s,2o,3so,f,16,8,1,a,12,12,1y,b6,1e,e,q,k,e,s,i,o
12,a,8,6,8,6,o,i,s,408,1j,1g,k,u,c,1i,6e,6y,8w,1k,q,y,s,1i,24,18,12
1e,c,a,a,c,c,10,10,1e,3z8,e,1m,o,t,y,r,2y,5u,9q,1g,u,16,o,10,1u,16,14
1u,i,g,e,i,k,1m,1m,2g,3tg,1w,b,1c,b,c,j,i,q,b4,w,s,g,k,g,o,i,k
16,c,8,8,6,c,q,s,10,408,1t,1s,1a,s,1f,1y,4i,6g,8q,26,q,u,y,18,26,16,1a
12,8,8,6,6,8,k,g,o,41a,z,2i,e,c,6,o,6e,7c,8y,1g,k,w,u,1e,2a,w,w
12,8,8,6,6,8,k,g,o,41a,11,2i,c,c,6,m,6g,7a,8y,1g,k,w,u,1e,2c,u,y
1i,c,a,a,c,c,u,s,18,3ws,1w,1k,d,a,1i,3k,46,5c,ai,10,q,w,m,y,1k,k,16
26,u,o,q,q,u,2e,2e,3q,3vy,3h,1b,r,0,w,1m,y,3u,ba,1a,s,k,g,e,10,y,s
28,u,o,m,o,s,24,20,30,3t0,35,2,e,g,o,1w,26,38,be,18,q,i,e,e,u,o,s
28,u,o,o,q,s,2a,26,3a,3ui,3r,13,k,6,s,1i,20,46,bc,1a,q,k,e,c,y,s,s
26,q,k,m,m,q,20,1w,2u,3s0,1b,10,f,m,8,1o,1g,2c,be,18,k,i,e,e,o,i,o
16,a,8,8,6,8,m,i,q,404,15,22,1a,o,0,3c,5o,7e,8y,1k,10,s,q,18,1y,o,10
22,s,m,q,q,u,2c,2c,3k,3vu,33,d,1r,4,s,18,18,3y,b4,1g,s,m,g,e,12,u,s
22,m,i,i,m,o,1w,1u,2q,3su,t,1c,3,1,0,o,18,1u,ba,14,k,o,e,e,o,g,m
12,a,6,8,6,6,k,g,m,416,h,2q,s,c,a,26,56,7k,8y,1g,m,u,s,18,26,s,y
1e,e,a,a,a,e,y,y,1e,3ym,q,1m,a,d,12,9,42,5q,9u,1g,u,10,q,10,1u,10,16
26,s,m,m,o,s,24,20,30,3ti,3b,6,1,m,i,1q,28,3s,ba,1c,o,k,e,g,w,o,o
1y,m,g,i,i,o,1s,1q,2m,3sy,0,w,k,9,a,q,u,1m,b6,18,k,o,k,e,q,g,o
1m,e,c,e,c,g,18,16,1o,3uo,3c,k,8,12,c,t,32,5c,ao,10,s,10,s,w,1k,u,w
1q,g,c,e,c,i,1a,1c,22,3tk,3k,d,y,16,8,e,2,a,b0,u,o,q,g,k,y,i,k
2a,s,k,m,o,q,20,1y,2u,3rq,1n,g,4,k,u,1s,1s,2g,be,18,q,g,e,e,q,m,q
22,q,m,m,o,u,2e,2i,44,3ty,2f,w,p,i,6,12,g,20,b4,1e,k,m,i,g,12,y,w
1c,e,a,a,c,e,16,16,1u,3y2,10,1y,k,l,w,17,4,3y,9o,1c,y,16,o,18,1o,1a,y
1w,i,g,e,i,k,1m,1k,2g,3te,1k,9,16,a,0,o,e,14,b2,12,u,i,o,g,s,i,m
s,8,6,6,6,6,g,e,m,3z6,1x,1p,4k,m,k,12,2i,74,8u,i,1q,q,o,u,2a,1c,18
w,8,6,8,6,6,i,i,m,3z8,t,b,3w,10,w,18,3y,84,8s,o,1g,s,s,q,2k,14,12
w,8,8,6,6,8,i,g,o,3z6,v,6,3a,10,y,1a,3y,7w,8q,w,1a,s,s,q,2k,14,10
u,6,6,6,6,8,g,e,m,3ze,1j,1n,4o,w,u,1e,3c,7w,8s,i,1o,o,q,u,2a,1m,16
s,8,6,6,6,6,e,g,k,3y2,1x,3c,n,i,g,s,1m,64,8w,k,8,28,o,s,26,12,1a
s,8,6,6,6,6,e,g,k,3xo,1p,2m,a,m,i,y,o,6k,8w,i,g,20,o,q,24,1i,16
s,8,6,6,6,6,e,e,i,3wy,1v,3a,x,c,g,2k,2m,4m,90,i,a,26,m,s,1y,14,1k
u,8,6,6,6,6,g,g,k,3zc,17,z,4y,w,q,1a,36,7w,8w,i,1k,q,q,s,2a,1i,18
s,8,6,6,6,6,g,e,m,3z0,1v,1p,4o,m,m,12,2i,74,8u,k,1o,q,o,u,28,1g,18
u,8,8,6,6,6,g,i,m,3za,t,z,4u,10,u,1a,3q,8c,8u,g,1m,q,s,s,2i,18,14
s,8,6,6,6,6,g,e,k,3ze,21,1n,4c,k,i,w,2a,6w,8u,i,1o,q,q,u,2a,18,16
w,6,8,6,6,6,e,g,k,3vk,t,12,28,w,1u,38,30,68,8y,1e,8,1a,q,o,2s,12,1a
u,8,6,6,6,6,g,g,k,3z6,19,1d,58,w,u,1c,3a,7w,8w,i,1m,o,q,s,2c,1i,16
u,8,6,6,6,6,g,g,m,3z8,11,17,54,y,u,1e,3k,82,8u,i,1m,q,q,q,2g,1e,16
s,8,6,6,6,6,e,g,k,3y2,1z,38,n,g,e,o,1u,6e,8y,i,8,28,o,s,26,18,16
s,8,6,6,6,6,e,e,k,3ww,1n,3g,l,k,g,1i,1i,5s,8y,i,a,26,o,q,20,1c,1e
s,8,6,6,6,6,e,e,k,3ww,1j,3a,9,k,i,1y,m,6g,8y,g,a,26,o,s,24,1i,1c
s,8,6,6,6,6,g,e,k,3ya,21,32,l,e,c,i,1w,66,8y,i,8,28,o,s,28,10,16
s,8,6,6,6,6,e,e,i,3wu,1v,3c,z,c,k,2o,2o,4g,90,i,a,26,m,s,1y,12,1o
s,8,6,6,6,6,e,e,k,3x2,1t,3e,x,g,c,1w,2e,58,8y,i,a,28,m,s,1y,16,1i
u,8,6,6,6,6,e,e,k,3zu,1h,1p,4w,c,k,1u,1q,50,8y,i,1k,m,s,s,2i,1a,1k
s,8,6,6,6,6,e,c,k,3wi,1r,38,x,c,14,36,2o,3w,90,i,a,26,o,q,1u,1a,1q
s,8,6,6,6,6,e,e,k,3xk,1x,3a,t,e,e,16,26,5y,8y,i,a,26,o,s,22,18,1a
s,8,6,6,6,6,g,e,m,3zo,23,1n,4a,i,g,u,2c,6w,8s,k,1o,o,q,u,2a,1a,18
s,8,6,6,6,6,g,e,k,3yi,1x,14,1m,k,g,u,1s,6c,8w,i,w,1k,o,s,28,16,18
s,8,6,6,6,6,g,e,m,3yo,1x,y,1y,k,i,u,24,6q,8w,k,u,1k,o,s,28,1e,1a
u,8,6,6,6,6,g,g,k,3z8,17,f,4c,s,q,16,32,7q,8y,i,1a,y,s,q,2c,1i,16
u,8,6,6,6,6,g,e,k,3wc,z,7,3m,q,1c,2w,2i,60,8w,10,q,14,s,q,2q,16,1e
u,8,6,8,6,6,i,g,m,3z6,t,13,4o,y,w,18,3w,8c,8s,g,1m,s,s,q,2k,16,16
s,8,6,6,6,6,g,g,k,3zg,1v,1r,4k,q,m,16,2w,7g,8u,i,1o,q,o,u,2a,1i,16
s,8,6,6,6,6,g,e,m,3yw,1v,1p,4q,m,m,y,2i,70,8w,i,1q,o,q,s,28,1g,18
u,6,6,6,6,8,e,g,k,3z0,1l,z,4i,u,o,16,2y,7g,8w,i,1g,w,q,s,28,1m,18
s,8,6,6,6,6,g,e,k,3z6,21,1n,4e,k,g,w,22,6m,8u,i,1q,q,o,u,28,16,18
u,6,6,6,6,8,e,g,m,3zg,1r,1p,4o,w,s,1c,38,7q,8u,i,1o,o,q,s,2a,1m,18
s,8,6,6,6,6,g,e,k,3y8,1z,34,j,g,e,o,1s,6a,8y,i,8,28,o,s,26,16,18
u,6,6,6,6,6,g,e,m,3yu,1v,11,46,m,m,y,2i,72,8w,i,1i,w,q,s,28,1i,1a
u,6,6,6,6,6,g,e,k,3wq,1f,32,0,k,q,2k,1m,5s,8y,g,a,24,q,s,28,1i,1g
u,8,6,6,6,6,e,e,k,3vw,15,2o,c,o,1q,34,2g,5k,8y,g,e,22,q,q,2q,16,1g
u,8,6,8,6,6,g,g,m,3zc,v,11,52,y,u,1e,3m,88,8u,i,1m,q,q,s,2i,18,14
u,6,6,6,6,8,e,g,k,3z8,1n,1p,4w,u,q,1c,30,7o,8u,i,1o,q,q,s,2a,1k,16
u,6,6,6,6,6,g,e,m,3yo,1v,2w,2,i,i,s,1y,6q,8y,i,8,24,q,s,28,1g,18
u,8,6,8,6,6,g,i,m,3za,t,11,4y,y,w,1c,3o,8c,8u,i,1m,o,s,s,2i,18,14
u,6,6,6,6,6,e,e,k,3vw,1d,2w,3,k,1u,38,2e,52,8y,i,e,22,q,q,2g,1c,1k
w,6,8,6,6,6,g,g,m,3zc,p,z,5a,y,s,1a,3m,84,8w,i,1m,o,s,q,2k,14,16
u,8,6,6,6,6,g,i,k,3z8,11,1b,4y,10,u,1e,3m,86,8u,g,1o,o,s,q,2g,1e,16
s,8,6,6,6,6,e,e,k,3xy,21,32,r,c,a,1a,28,5w,90,g,a,26,o,s,24,10,1a
u,8,6,6,8,6,g,i,m,3yy,t,15,4g,10,w,18,42,86,8s,g,1m,q,s,s,2i,18,18
u,8,6,6,6,6,g,g,m,3zc,15,1b,5a,u,u,1a,3a,7y,8w,i,1m,o,q,s,2c,1g,18
s,8,6,6,6,6,e,e,k,3xk,1z,3a,v,e,c,1g,28,5s,8y,i,a,28,m,s,20,16,1e
s,8,6,6,4,8,e,g,k,3ys,1z,x,3q,k,g,u,1k,6i,8u,k,1i,w,o,u,26,1c,18
u,8,6,8,6,6,g,i,m,3yy,p,13,4g,10,u,18,3y,8c,8s,g,1m,q,s,s,2k,16,16
u,8,6,6,6,6,g,i,k,3z2,11,19,4u,10,w,1g,3o,86,8s,i,1m,q,q,s,2e,1g,16
s,8,6,6,4,8,e,g,k,3yi,1x,k,28,k,i,u,1i,6e,8w,i,12,1e,o,s,26,1a,18
w,8,6,8,6,6,i,i,o,3z6,t,0,3i,10,y,18,3y,80,8s,s,1c,s,s,q,2k,14,12
s,8,6,6,4,8,e,g,k,3y0,1v,3e,n,k,i,s,1k,66,8w,k,8,28,m,s,26,18,1a
u,8,6,8,6,6,g,i,m,3z4,v,13,4o,y,w,1a,3u,8a,8s,g,1m,q,s,s,2i,1a,14
u,8,6,6,6,6,e,e,k,3vu,15,2m,e,q,1s,36,2i,5k,8y,g,e,22,q,q,2q,16,1g
s,8,6,6,4,8,e,g,k,3y4,1v,2o,2,k,g,u,1m,66,8w,i,g,22,m,s,26,16,1a
s,8,6,6,4,8,e,g,k,3y4,1v,28,o,k,i,u,14,6e,8w,i,m,1u,o,s,22,1g,18
u,8,6,6,6,6,g,g,m,3za,19,1f,4w,10,u,1g,3i,82,8u,g,1o,o,s,q,2e,1i,16
w,8,8,6,6,8,i,g,o,3z6,v,o,2y,12,y,1c,42,7s,8s,10,14,u,s,q,2i,14,10
u,8,6,6,6,6,g,g,m,3z8,17,1d,4y,10,u,1g,3i,84,8u,g,1o,o,s,q,2e,1g,18
y,8,6,8,6,8,i,i,o,3z0,v,1k,1s,14,14,1k,44,7a,8s,1a,w,u,s,o,2g,16,10
w,a,6,8,6,8,i,i,o,3z0,x,1g,1s,12,16,1g,46,7a,8q,1a,y,u,q,q,2g,16,10
u,8,6,8,6,6,g,i,m,3z6,v,13,4o,10,u,1a,3u,8c,8s,g,1m,s,s,q,2k,18,14
w,8,6,8,6,6,i,g,o,3z8,p,0,3w,10,w,1c,3u,84,8u,q,1e,s,s,q,2i,14,12
s,8,6,6,4,8,e,g,k,3z0,21,1l,4e,i,i,s,1o,6i,8u,i,1q,q,o,u,26,18,18
u,8,6,6,6,6,g,g,k,3z6,17,b,46,s,o,16,30,7o,8y,i,1a,10,q,q,2c,1i,16
s,8,6,6,6,6,e,e,k,3xc,1z,38,x,c,c,26,2k,56,90,i,8,28,m,s,1y,14,1i
u,8,6,6,6,6,g,g,k,3z4,1b,8,3g,s,o,12,2w,7m,8y,i,12,18,q,q,2c,1i,18
u,8,6,6,6,6,e,e,k,3ws,19,1c,1u,m,w,2o,24,5o,8w,i,q,1m,s,q,2e,1i,1g
w,8,8,6,6,8,g,i,o,3z6,r,8,3k,10,w,1c,3w,80,8s,u,1a,u,s,q,2i,14,10
s,8,6,6,6,6,e,c,k,3x0,1x,38,z,a,g,2u,2s,4i,90,i,8,28,o,q,1w,16,1m
s,8,6,6,6,6,e,e,k,3wg,1n,3c,p,g,o,2m,2a,4k,8y,i,c,26,m,q,24,14,1o
s,8,6,6,6,6,e,e,k,3xw,21,36,r,e,c,y,24,5y,8y,i,a,26,o,s,24,12,1a
u,6,6,6,6,6,g,e,k,3yk,1x,2y,7,i,e,q,1u,6g,8y,i,8,26,o,s,28,1c,18
w,6,8,6,6,6,g,i,m,3zc,r,z,50,y,u,1c,3q,8a,8u,i,1m,q,s,q,2k,16,12
u,8,6,6,6,6,e,g,k,3w4,11,22,16,q,1i,32,2g,5u,8y,k,e,1w,s,o,2s,16,1e
s,8,6,6,6,6,g,e,k,3ys,1x,z,3q,m,i,w,20,6i,8w,i,1i,y,o,s,28,18,16
u,8,6,6,6,6,e,g,k,3vu,11,1o,1k,q,1q,36,2k,5w,8y,o,g,1s,q,q,2s,14,1c
u,8,6,6,6,6,g,e,k,3vs,x,m,2m,s,1q,38,2m,60,8y,10,e,1i,q,o,2u,12,1e
u,8,6,6,6,6,e,g,k,3we,13,1a,20,o,1a,2u,2c,5u,8y,m,k,1o,q,q,2k,1e,1e
s,8,6,6,6,6,e,g,k,3yg,1x,18,1i,k,i,u,1q,6c,8w,i,w,1k,o,s,28,18,16
s,8,6,6,6,6,e,g,k,3z8,23,1j,4a,k,g,s,1s,6e,8u,i,1q,o,q,s,28,16,18
u,8,6,6,6,6,e,g,k,3vq,11,1q,1g,u,1u,36,2m,5y,8y,o,g,1s,q,q,2s,14,1c
s,8,6,6,6,6,e,e,k,3x2,1v,3a,x,c,e,2c,2k,4y,90,i,8,28,m,s,1y,14,1k
u,6,6,6,6,6,e,e,i,3w2,1h,2w,9,s,20,3c,2k,4s,90,g,e,24,o,q,2e,1e,1k
12,a,6,6,6,8,i,g,m,40c,j,3a,g,e,c,18,3c,5m,90,1k,o,u,o,o,24,w,w
1s,g,e,e,e,i,1g,1g,2a,3u2,2u,r,y,w,l,4,6,e,ay,10,m,k,g,g,s,i,k
1s,i,g,g,g,m,1m,1m,2i,3ry,2k,1b,1i,a,3,9,o,1a,b6,w,u,a,k,c,k,g,k
y,8,6,6,6,6,g,g,k,40e,x,3k,2,2,2,d,2y,68,92,1e,a,14,s,q,2m,y,16
1s,g,g,e,g,k,1k,1k,2c,3ti,2u,17,y,g,h,2,8,s,b2,w,u,a,e,g,o,i,m
1a,a,8,8,6,a,k,k,s,3zo,e,1w,12,o,k,3y,2w,68,9y,12,o,o,i,10,1m,w,e
1k,c,a,a,c,c,y,y,1i,3v4,2o,1w,g,y,1m,1i,y,6,aq,w,m,k,m,e,16,m,q
12,a,6,6,6,6,i,g,m,41e,d,2y,w,k,e,20,2w,5y,8w,1i,i,w,o,q,26,14,o
12,8,6,6,6,8,g,i,m,40e,t,3y,s,i,e,12,36,6a,90,1m,k,w,o,s,26,12,12
28,s,i,m,m,s,20,1w,2s,3s2,15,o,h,g,c,1g,1g,2y,bg,14,c,m,a,c,m,i,o
12,8,6,6,6,6,i,g,k,41e,j,2m,1a,k,c,1g,2w,5y,8w,1k,e,u,s,q,2c,10,s
1u,g,g,g,g,m,1m,1m,2i,3ti,2m,1l,18,0,n,9,g,12,b4,w,u,8,c,i,m,i,m
y,8,6,6,6,6,e,g,m,40e,v,3a,0,2,2,7,2i,66,92,12,a,1e,s,s,2m,10,14
12,a,6,6,8,6,i,g,o,40c,h,38,g,e,e,18,3i,5k,8y,1m,o,w,m,o,24,w,w
1e,e,8,8,a,a,q,q,10,3x4,1a,1w,1a,k,1u,3m,20,20,a4,16,o,i,i,q,1e,q,s
14,8,8,6,6,6,i,i,m,40e,f,3c,g,c,c,1a,3q,5i,8y,1m,q,u,o,o,22,w,10
14,a,6,8,6,6,i,i,m,410,7,2q,14,k,i,2o,2q,5a,90,1e,o,u,o,o,1y,16,k
20,m,i,m,k,s,1y,1w,2s,3rm,x,w,b,b,3,12,1s,2u,be,10,i,g,e,a,k,e,o
10,a,6,6,6,6,i,g,m,40g,r,3y,c,6,6,0,3s,5m,90,1m,i,w,q,o,2e,u,18
18,c,8,6,6,a,k,k,q,3zy,3,2e,18,10,o,3i,2m,5u,9w,14,k,m,k,w,1k,y,8
1s,g,g,g,g,m,1q,1s,2s,3rc,3s,1l,1c,6,t,13,1,0,b2,s,10,8,q,g,o,i,s
10,8,6,6,6,6,g,g,m,40e,x,2s,y,8,8,1,2o,5g,92,1c,k,u,s,q,2a,y,s
1i,c,8,a,a,c,s,u,1a,3w8,20,2c,s,q,1o,2q,18,1s,ag,10,o,i,k,m,1a,o,i
28,s,i,m,m,s,1y,1y,2s,3s6,17,o,h,i,a,1g,1g,2w,bg,14,e,k,c,a,o,i,o
28,q,k,m,m,q,20,1y,2s,3sa,15,o,d,m,a,10,1g,2y,bi,14,i,i,e,a,m,i,o
y,8,6,6,6,8,g,g,k,40g,z,40,a,2,2,b,3e,6i,90,1m,e,w,q,q,2k,y,18
1a,a,8,8,6,a,m,i,s,3zy,h,2q,1g,s,q,3q,2s,58,9u,18,i,m,k,w,1g,y,8
16,a,6,8,6,6,k,i,o,406,k,2c,m,g,i,28,3e,5u,9k,16,q,s,m,y,1q,w,s
12,8,6,6,6,8,i,g,m,40e,r,3y,s,i,a,w,3e,5y,90,1m,k,y,m,q,26,10,16
y,6,6,6,6,8,e,g,k,40e,13,1w,1a,0,0,5,1c,5s,92,y,8,1i,u,q,22,y,g
24,o,i,m,m,s,1y,1y,2s,3sa,1t,1e,x,c,b,1e,1q,2w,be,16,8,k,8,c,m,g,q
14,8,6,8,6,6,i,g,o,40m,h,30,m,k,k,1q,2a,5a,8y,1k,m,u,o,o,20,18,i
12,8,6,6,6,8,g,g,k,410,n,30,o,k,i,o,2o,60,8y,1i,g,w,q,q,24,16,o
28,q,i,m,m,s,1y,1y,2s,3sg,1j,y,p,g,2,1g,1i,2w,bg,14,c,k,c,a,m,i,q
12,a,6,6,6,6,i,i,m,40e,l,3u,o,i,8,10,3i,5m,90,1m,m,w,o,o,26,y,16
w,8,6,6,6,6,e,e,k,414,19,1h,4o,8,1,18,k,52,8y,i,1i,k,u,s,1w,1m,10
1q,g,e,e,e,i,1g,1e,28,3tg,3g,j,s,k,8,d,0,c,b4,s,m,m,e,g,s,i,k
w,8,6,6,6,6,e,e,k,414,19,1j,4m,6,2,16,k,4u,8y,i,1i,k,u,s,1s,1m,10
24,q,k,k,m,s,20,1w,2s,3s0,1t,14,r,e,3,1m,1q,30,bg,14,8,m,6,e,k,i,o
20,k,i,i,k,o,1w,1s,2q,3t8,d,q,c,x,2,0,1a,24,b8,w,o,g,g,c,m,g,m
y,8,8,6,4,8,g,g,m,40g,11,44,c,2,4,7,3c,6i,90,1m,e,w,q,q,2k,y,18
16,a,8,6,8,6,m,i,o,400,1,2o,u,o,k,32,2o,6m,9s,16,i,q,m,y,1k,14,8
26,q,k,m,o,q,20,1y,2s,3ru,1t,u,n,k,8,1s,1m,34,bi,12,c,k,8,c,k,i,o
1q,g,e,e,g,i,1g,1g,28,3tk,38,n,s,g,6,b,2,i,b2,u,q,i,c,g,s,i,k
1g,c,8,a,8,c,q,q,14,3ws,1i,1y,16,i,1w,3a,1w,1q,a6,16,m,k,m,k,1e,o,s
10,8,6,6,6,6,g,g,k,40i,11,2m,12,8,6,5,2c,5i,92,1a,k,w,s,q,28,10,q
16,a,8,6,6,8,k,i,o,40m,d,30,1g,o,q,3k,2y,6e,9i,1c,g,q,o,k,20,14,a
26,q,k,k,m,s,20,1w,2s,3si,1j,14,t,g,0,1c,1k,2s,bg,14,c,m,a,c,m,i,o
10,8,6,6,6,6,g,g,k,40g,13,1w,1u,6,4,9,2i,5a,92,1c,k,u,s,q,26,y,o
1i,c,a,a,a,a,w,u,1a,3w8,24,2a,q,m,1q,2k,1c,1s,ae,14,o,k,k,k,1a,o,g
18,c,6,8,6,8,m,k,q,3zw,6,24,10,q,i,3o,2y,6k,9w,12,o,o,k,y,1m,w,c
24,o,i,m,k,s,1y,1w,2s,3sq,1l,1a,r,4,b,14,1o,2q,bc,16,a,k,a,c,m,g,q
1m,g,c,c,c,g,16,18,1w,3su,36,6,g,1m,1c,u,2,4,b0,u,m,o,e,i,w,k,i
1y,k,i,i,k,o,1u,1s,2o,3sy,4,6,e,t,1,8,1a,24,b8,u,q,c,g,c,m,g,m
y,8,6,6,6,6,g,e,m,40e,11,2e,y,2,2,b,24,5u,92,16,a,1a,s,q,2e,w,s
14,a,6,8,6,6,k,i,o,408,2,2m,18,o,k,2q,3q,68,8y,1o,w,s,o,16,1m,y,12
1e,c,8,8,a,a,o,o,10,3xy,y,2a,1k,q,1k,3o,2m,48,a0,18,q,k,m,o,1i,q,c
1c,c,8,8,8,a,o,m,y,3yk,i,20,1s,q,1o,4i,2c,5e,9y,18,q,m,i,u,1m,s,e
10,8,6,6,6,6,g,e,k,418,z,u,2w,e,a,g,2i,5u,8y,1i,e,s,u,q,26,1a,w
10,8,6,6,6,8,g,g,m,40g,v,42,g,8,4,0,3i,64,90,1m,g,y,o,q,2e,y,16
1m,e,a,a,c,e,10,12,1o,3ug,34,1k,6,14,1k,u,a,3,au,w,k,o,g,e,14,k,o
1a,a,8,8,6,a,m,k,s,3zs,7,2k,1i,q,s,3m,2s,56,9u,18,i,m,k,w,1g,y,6
10,8,8,6,4,8,g,e,k,414,t,2s,w,g,e,a,2u,60,8y,1i,a,10,q,s,22,1a,u
1m,e,a,c,c,e,12,14,1q,3uu,34,1a,0,1a,1g,w,4,3,au,w,o,q,e,k,10,k,m
14,a,8,6,6,8,i,i,o,408,7,2w,m,i,g,22,34,60,9a,1g,o,s,m,s,1u,12,m
16,8,8,6,6,8,k,g,o,40a,e,2m,i,e,g,22,3k,5o,9e,1a,q,u,m,u,1u,w,w
14,a,6,6,6,8,i,g,m,40w,3,2q,y,m,g,24,2u,5a,8u,1k,o,u,o,o,20,16,k
w,8,6,6,6,6,e,e,k,414,17,1l,4o,6,2,14,o,4s,8y,i,1i,k,u,s,1s,1m,y
24,q,i,k,m,q,20,1w,2s,3so,1f,1e,x,a,7,16,1k,2m,be,16,c,k,c,c,m,g,o
1q,g,e,e,e,i,1e,1e,28,3ty,3c,t,w,w,0,4,5,8,b0,w,u,e,k,e,s,k,k
1m,e,c,c,c,e,14,16,1q,3t8,32,i,e,1s,1c,y,8,6,ay,u,k,o,g,k,w,k,k
1s,i,g,g,i,m,1q,1q,2k,3si,20,1x,1c,d,a,1,m,1w,b2,12,w,8,i,e,o,e,k
1u,i,g,g,i,k,1s,1s,2q,3sg,28,r,1i,h,1,11,2,0,b0,y,w,c,o,g,q,i,s
1o,g,e,e,e,g,1e,1e,24,3sg,36,p,s,1g,o,k,5,c,b2,w,q,i,e,g,u,i,i
26,q,k,m,m,s,20,1w,2u,3ru,1t,u,n,i,8,1q,1o,34,bg,14,a,m,6,c,m,i,o
10,8,6,6,6,8,g,g,m,40g,v,42,k,8,6,4,3g,66,90,1m,g,y,o,q,2e,w,18
14,a,8,6,6,8,i,g,o,416,b,2q,18,k,k,32,2q,5u,96,1c,m,u,m,o,1w,18,g
16,a,6,8,6,6,k,i,o,404,o,2a,k,i,i,2a,3c,5w,9m,16,o,s,m,y,1q,w,s
26,q,k,m,m,q,20,1y,2s,3rc,1b,1a,j,s,4,12,1s,2y,bi,16,a,k,a,c,m,g,o
20,m,i,k,k,q,1w,1u,2s,3ss,t,u,5,n,1,k,1k,2g,ba,w,m,e,e,c,m,g,o
10,8,6,6,6,6,i,e,k,412,x,2i,12,g,e,4,2i,5u,8y,1c,i,y,s,q,28,12,s
12,a,6,6,6,8,i,g,m,40c,h,36,k,g,g,1i,2s,5q,90,1k,m,u,o,o,22,14,k
16,a,6,6,6,8,k,g,q,408,i,2g,k,g,g,24,3i,5q,9g,18,s,s,m,w,1s,w,u
10,8,6,6,6,6,g,g,k,41c,t,1g,2e,g,c,q,2s,5u,8y,1i,e,s,u,q,2c,12,u
1c,a,8,8,8,8,o,k,u,3ze,4,24,1i,10,y,3m,2k,4s,9u,16,o,m,i,s,1k,u,a
1s,g,g,g,g,m,1q,1s,2s,3s6,3e,1f,18,6,x,17,b,2,b0,u,10,a,q,g,q,i,q
1i,c,a,a,a,a,w,u,18,3vi,20,1s,e,14,1w,30,1g,w,am,w,m,g,m,k,1a,o,q
22,o,i,k,m,q,1y,1w,2s,3so,1b,16,l,7,7,10,1m,2m,bc,y,i,i,a,c,m,g,q
1g,e,8,8,a,a,u,s,16,3wg,1w,28,10,u,1m,2u,1k,1w,ae,10,o,i,k,m,1c,o,i
16,a,6,6,8,6,k,i,o,406,k,2e,k,g,i,26,3g,5s,9i,18,q,s,m,w,1s,w,u
12,8,6,6,6,8,g,i,m,40e,n,3q,c,a,6,g,3q,5i,90,1m,k,w,o,o,2a,u,16
20,m,i,i,m,q,1w,1u,2q,3ss,r,s,3,n,1,i,1k,2g,ba,y,k,e,e,c,m,g,o
1c,c,8,8,8,a,o,m,y,3yc,o,1s,1g,o,1g,4a,2g,3a,9y,18,o,k,e,w,1i,q,o
y,6,8,6,4,8,e,g,k,40c,z,28,y,0,2,5,1m,5u,92,y,8,1i,u,q,2a,y,o
12,8,6,6,6,6,i,g,m,40c,r,3i,c,a,a,e,3m,5e,90,1k,i,y,o,q,2a,q,12
1y,k,i,i,k,o,1u,1s,2o,3t8,4,8,c,l,3,1,18,1w,b8,y,m,g,e,a,o,g,m
16,a,8,6,8,6,m,g,q,3zw,c,2g,s,k,k,2s,2q,6i,9y,14,g,q,m,12,1i,16,c
1g,c,8,8,a,a,q,q,12,3x2,1k,24,18,u,1m,34,22,2c,a4,16,o,k,k,m,1e,q,g
24,o,i,m,k,s,1y,1w,2s,3rk,11,1s,j,k,5,12,1w,24,be,18,a,m,8,e,m,i,q
16,a,6,6,8,6,k,i,o,406,7,2w,q,k,m,2k,2k,6e,9i,1c,i,s,m,u,1q,18,8
14,a,6,6,6,6,k,g,o,40i,f,30,m,k,k,1y,2i,5k,8y,1k,q,s,o,o,1y,18,e
28,q,i,m,m,q,22,1w,2s,3rw,1d,16,h,o,2,y,1o,30,bi,16,a,m,a,a,m,i,o
1i,c,a,8,a,c,u,u,18,3vo,26,20,q,s,1w,2o,1g,s,ac,14,o,i,o,g,1a,m,u
12,8,6,6,6,8,i,g,m,40g,p,3y,i,c,6,m,3m,5m,90,1m,k,w,o,o,2a,w,18
12,8,6,6,6,6,i,g,k,40m,r,38,g,g,e,o,2a,5q,90,1i,g,w,q,q,28,12,o
1a,a,8,8,6,a,m,k,s,3zq,4,1w,16,y,k,46,30,5w,9w,12,q,m,i,w,1m,u,c
18,a,8,8,6,8,m,i,q,400,w,1o,u,m,g,3e,34,68,9y,w,s,o,m,10,1m,w,k
18,a,6,8,6,8,k,i,q,406,7,2o,10,u,m,36,2k,68,9s,16,i,o,k,y,1k,14,6
22,o,i,k,m,q,1y,1w,2s,3sw,1f,1c,n,1,d,w,1k,2k,bc,14,c,k,a,c,m,g,q
22,o,i,k,m,q,1y,1u,2s,3sw,17,1c,n,1,9,s,1i,2i,bc,12,g,k,c,c,m,e,q
12,8,8,6,6,6,i,g,k,410,f,30,q,m,g,16,2y,5u,8w,1i,i,w,q,q,22,16,o
1i,e,8,a,a,c,u,w,1a,3va,28,1w,a,14,1w,2o,16,u,ak,y,o,e,o,k,18,m,q
26,q,k,m,m,s,20,1w,2s,3re,1d,16,j,s,4,16,1o,34,bi,16,a,k,a,c,m,g,o
1y,k,i,g,k,o,1u,1s,2o,3t6,a,2,c,l,7,6,18,1w,b8,s,s,e,e,c,m,g,o
1k,c,a,a,c,c,y,y,1g,3uw,2o,1y,e,12,1u,1m,s,2,ak,12,m,m,k,e,16,k,s
26,q,k,m,m,s,20,1w,2s,3rg,1d,16,j,q,4,18,1o,32,bi,16,a,k,a,c,m,g,o
1w,k,i,i,k,o,1w,1u,2q,3rk,k,b,u,j,a,1,16,2i,b8,y,s,c,i,a,m,c,m
1e,a,8,8,8,a,q,m,y,3ya,q,1y,1i,o,1g,3q,2c,3a,9y,16,q,k,i,q,1i,q,m
1g,c,a,8,a,a,s,s,16,3wi,1u,2a,10,u,1m,2y,1i,20,ae,10,o,i,k,m,1c,o,i
1a,c,8,6,8,a,m,k,s,3zg,w,1s,14,k,o,3y,2q,6a,9y,14,q,m,m,w,1q,u,k
28,q,k,m,m,q,20,1y,2s,3sa,1j,s,n,g,6,1k,1i,30,bg,14,a,m,a,a,m,i,q
12,a,6,8,6,6,i,g,m,41k,3,2w,y,k,i,2o,2y,74,8w,1k,k,u,o,q,2c,12,m
14,8,8,6,6,6,i,i,m,40g,h,3i,g,c,a,18,3q,5i,8y,1m,q,u,o,o,24,w,12
10,8,6,6,6,8,g,g,m,40g,t,40,g,6,4,2,3m,5w,90,1m,g,y,o,q,2e,w,18
1i,e,8,a,a,c,w,u,1c,3ve,2e,1y,s,u,1u,2a,1a,c,ac,16,o,k,m,e,1a,m,s
1e,c,8,6,a,a,o,m,y,3y6,u,2c,1s,q,1g,3o,2m,4o,9y,18,q,k,k,q,1i,q,a
1i,c,a,a,a,c,u,u,1a,3vg,24,1u,g,10,1y,2q,1a,w,ai,10,m,g,m,k,1a,m,s
y,8,6,6,6,6,e,g,i,418,17,11,4g,a,6,k,1o,56,8y,12,y,m,u,s,20,1c,u
16,a,8,6,6,8,k,i,o,40q,f,2q,1a,m,g,2u,2s,56,9a,1g,k,q,m,s,1o,16,e
1w,k,i,g,k,o,1u,1s,2o,3sk,q,d,u,t,4,5,1a,1w,b8,y,o,e,g,8,m,g,m
12,8,8,6,6,6,i,g,o,40g,n,3u,m,e,8,s,3k,5m,90,1m,m,w,o,o,28,w,16
18,c,8,6,6,a,k,k,q,3zw,0,26,16,y,m,3o,2u,62,9w,12,o,m,i,y,1k,y,8
1g,c,8,8,a,a,s,q,12,3ws,1m,1m,w,k,1q,3w,2e,3q,aa,14,o,k,o,k,1g,s,c
14,a,6,8,6,6,k,g,o,40q,b,2s,s,o,m,28,28,4u,90,1i,o,u,m,o,1w,18,i
1i,e,8,a,a,c,u,u,1c,3wg,1y,1q,k,12,1o,2k,1u,1o,aq,u,o,i,s,e,1c,q,c
12,8,6,6,6,8,g,i,m,40e,n,3s,c,8,8,e,3q,5i,90,1m,k,w,o,o,2a,u,16
28,s,k,k,o,q,20,1w,2s,3s2,13,o,f,g,a,1i,1e,2w,bg,14,g,i,c,a,o,i,o
2a,q,k,k,o,q,20,1w,2s,3s2,13,o,f,g,c,1g,1g,2w,bg,16,e,i,c,a,o,i,o
12,a,6,6,6,8,i,g,m,40c,h,36,k,g,g,1i,2q,5q,90,1k,m,u,o,o,22,14,k
22,m,i,i,m,q,1w,1u,2s,3t2,x,14,9,f,5,i,1g,2c,ba,10,i,k,c,c,o,e,o
w,8,6,6,6,6,g,e,k,40e,19,1e,1k,1,1,2,s,4u,92,o,g,1i,u,s,1o,y,c
18,a,8,6,6,8,m,i,q,40o,l,2q,1c,q,e,3i,2w,5g,9i,1c,k,o,m,u,1k,12,c
12,8,8,6,6,6,i,e,m,41a,h,30,u,k,e,1k,2y,5w,8w,1i,g,w,q,q,26,14,q
28,o,k,m,m,q,20,1w,2s,3s0,1d,18,j,s,1,10,1o,2s,bi,16,c,k,a,a,o,g,o
1e,c,8,8,8,a,o,o,y,3xy,w,1y,1i,k,1k,3q,2a,2u,9y,18,o,k,k,o,1i,q,o
28,q,k,m,m,s,20,1w,2s,3ru,1n,o,h,k,c,1q,1i,34,bi,14,a,m,8,a,m,i,o
y,8,6,6,6,6,g,g,k,40k,19,b,3w,2,4,5,1m,52,92,q,18,q,s,q,1o,18,g
28,q,k,m,m,q,20,1y,2s,3ro,1j,w,h,m,8,1i,1k,34,bi,14,c,k,a,a,m,g,o
10,8,6,6,6,6,g,g,m,40e,11,32,s,6,6,d,3a,5c,92,1k,c,w,s,q,2e,q,y
1m,e,c,c,c,e,16,16,1s,3ta,32,c,i,1q,16,u,8,6,aw,w,k,m,i,k,w,k,i
10,a,6,6,6,6,g,g,k,41i,n,2e,1i,i,e,12,2y,5y,8y,1i,e,s,s,s,2e,y,u
26,s,k,m,m,q,20,1y,2s,3rm,1j,w,h,m,8,1i,1k,36,bi,14,c,k,a,a,m,g,o
1k,e,a,c,c,c,12,10,1m,3ty,2q,14,6,1m,1k,1o,q,a,aw,s,m,i,m,i,12,k,m
1c,a,8,8,8,8,o,k,u,3z0,g,28,1s,1e,w,3s,2w,5s,9w,12,s,m,g,w,1k,s,8
26,o,k,m,m,s,20,1w,2s,3qw,19,1i,p,q,4,1a,1s,30,bg,16,a,m,a,a,m,i,o
12,a,6,6,6,6,i,g,m,418,d,2w,w,k,e,1u,2w,5u,8w,1i,i,w,o,q,24,16,m
10,a,6,6,6,6,g,g,m,40c,r,3g,c,a,a,a,34,5g,90,1k,g,w,q,q,2c,u,w
12,8,6,6,6,6,i,i,m,40g,t,40,o,e,8,k,3i,5u,90,1m,k,w,o,o,2a,y,16
10,a,6,6,6,6,g,g,m,40w,v,34,k,g,e,i,28,5y,90,1g,e,y,s,o,2a,10,q
y,8,6,6,4,8,e,e,k,418,13,1b,4k,8,4,u,1c,4y,8y,u,16,m,u,q,1u,1i,w
10,8,8,6,4,8,g,e,m,41k,p,24,1s,i,c,10,2y,5y,8y,1i,e,s,s,s,2g,w,w
1e,c,8,8,8,c,o,o,10,3xo,16,2a,1u,e,1o,3q,28,40,a0,18,q,k,g,s,1g,q,a
1m,g,c,e,c,g,1a,1a,20,3tm,2w,9,5,1e,c,14,k,k,ay,y,w,i,c,k,w,i,i
1c,c,8,8,8,a,o,m,w,3yk,k,20,1i,s,1a,3q,2g,3m,9y,16,o,k,k,q,1i,s,i
12,a,6,6,6,6,k,g,o,40c,p,3u,u,k,i,1o,36,66,90,1m,o,w,m,y,1w,12,14
1m,e,a,c,c,e,14,14,1q,3us,32,1i,b,1g,12,u,0,3,aw,w,k,q,e,i,12,k,o
26,q,k,m,m,q,22,1w,2s,3r4,1f,18,h,s,4,1a,1q,34,bi,16,a,k,a,c,k,i,o
16,a,6,6,6,8,k,g,q,406,7,2w,q,k,k,2k,2k,6e,9g,1e,i,s,m,u,1q,18,8
1m,e,a,c,c,c,14,12,1o,3um,36,1e,2,18,w,10,c,6,ay,s,m,i,k,i,12,k,o
10,8,8,6,6,6,g,g,m,40c,t,3i,c,8,a,4,32,5g,92,1i,e,y,q,q,2c,u,w
26,o,k,k,m,s,1y,1y,2s,3sm,1l,1a,x,g,9,1a,1k,2u,be,16,a,m,a,c,m,g,o
1k,e,8,c,a,c,y,y,1i,3vm,2m,1k,g,12,1q,1s,14,u,am,10,o,m,m,i,16,o,g
1y,k,g,g,i,o,1s,1s,2q,3s2,w,k,16,p,e,h,m,i,b4,w,u,i,g,g,q,g,u
14,8,8,6,6,6,k,g,o,40c,f,34,k,g,g,1q,2s,5w,8y,1m,o,u,o,o,20,14,k
y,8,6,6,6,6,g,g,k,40e,x,3q,2,0,2,7,2y,6e,90,1g,c,12,s,q,2m,10,16
1m,e,c,c,a,e,14,14,1q,3ug,3i,s,a,1c,1g,s,2,0,ay,s,m,q,g,m,12,k,i
12,a,6,6,6,6,i,g,o,40e,j,3o,e,a,8,q,3q,5i,90,1m,m,w,o,o,26,w,14
12,a,6,6,6,6,i,g,m,416,b,2w,u,m,e,1w,2u,5u,8w,1i,i,w,o,q,24,14,o
12,8,6,6,6,6,i,g,m,40g,r,3u,a,6,6,4,3s,5k,90,1m,i,w,q,o,2e,s,16
18,a,8,6,8,8,k,i,q,402,3,2o,1k,w,s,3o,2w,5s,9u,y,o,o,m,o,1q,12,c
28,s,k,k,o,q,20,1w,2s,3s6,1d,m,h,g,c,1i,1g,30,bg,14,c,k,c,a,m,i,o
12,8,6,6,6,6,i,g,k,40c,p,3i,a,8,a,6,3q,5g,90,1m,g,y,o,q,2c,q,14
2a,q,k,k,o,q,20,1w,2s,3s2,11,m,f,i,a,1c,1c,32,bi,14,c,m,a,a,m,i,o
18,a,8,6,8,6,m,i,q,3zw,4,2e,w,o,k,3c,2u,6m,9y,12,k,o,k,10,1k,12,8
1m,e,c,c,c,e,14,16,1s,3te,30,e,i,1q,16,y,a,6,aw,w,k,m,i,i,y,k,k
1i,c,a,a,a,c,u,w,1a,3vs,2a,20,s,w,1k,26,1e,w,ai,10,o,i,m,g,1a,o,m
28,q,k,m,m,q,20,1w,2s,3sa,1j,s,n,g,6,1i,1k,30,bg,14,a,k,a,c,m,i,q
24,q,i,m,k,s,20,1w,2s,3so,1n,1c,x,e,d,1a,1k,2s,be,16,8,m,a,c,m,g,q
16,a,6,8,6,6,k,g,q,40s,9,2s,1c,k,o,38,2q,58,9i,10,q,s,m,m,1w,18,i
1i,e,8,a,a,c,u,u,1c,3w8,24,26,q,o,1s,2g,1i,1m,ac,16,o,o,m,g,1a,o,e
1s,g,e,e,e,i,1i,1g,2a,3te,3c,p,w,i,1,d,6,c,b4,u,k,m,e,g,s,i,k
1y,i,i,g,k,m,1s,1q,2m,3tg,i,4,s,r,1,7,10,1o,b6,w,o,e,i,e,m,g,m
y,6,8,6,4,8,e,g,m,40e,x,3g,1,0,2,5,2o,6a,90,1a,a,18,s,s,2o,10,16
16,a,6,6,6,8,k,g,o,40w,a,2k,1a,o,k,3g,2u,6w,90,1g,s,s,m,q,24,12,e
28,s,k,k,o,q,20,1w,2s,3s6,1d,m,h,i,a,1k,1e,30,bi,12,c,k,c,a,m,i,o
18,a,8,8,6,8,m,i,q,400,q,1q,w,o,e,3i,32,6c,9y,y,q,o,k,12,1m,w,i
28,q,k,m,m,q,20,1w,2u,3sa,1h,s,n,i,4,1i,1k,2y,bg,14,c,k,a,c,m,i,o
24,o,i,k,k,q,1y,1w,2s,3s0,z,1s,f,e,5,y,1o,1w,be,18,a,m,a,e,o,g,s
28,o,k,m,k,s,1y,1y,2s,3s8,1f,18,h,s,1,10,1m,2o,bi,16,c,m,a,a,o,i,o
14,a,6,6,6,8,i,g,o,414,1,2s,14,k,k,34,3g,7c,8w,1k,o,u,o,q,2a,y,o
28,q,k,m,m,s,20,1w,2s,3s4,19,o,j,i,a,1i,1g,2y,bg,14,c,k,c,a,m,i,o
28,q,k,m,m,q,20,1y,2s,3s8,1d,s,n,i,6,1i,1i,2y,bg,14,c,k,c,a,m,i,q
18,a,8,8,6,8,m,i,q,3zw,6,28,y,q,i,3i,2w,6k,9w,14,k,o,k,10,1k,10,a
1a,a,8,8,6,a,m,k,s,3zm,8,1y,14,u,o,42,2y,5s,9w,12,q,m,i,y,1k,u,e
12,a,6,6,6,8,i,g,m,40e,h,3i,e,c,a,12,3o,5i,90,1k,o,w,o,o,24,w,12
12,a,6,6,6,8,i,g,o,40e,p,3u,s,m,e,1k,3e,5u,8y,1o,o,w,o,u,1y,10,14
14,a,6,6,8,6,k,g,q,40a,7,30,12,o,m,2m,3k,64,8y,1o,u,u,o,14,1m,10,12
1s,g,e,e,e,k,1g,1i,2a,3tk,32,n,u,c,4,d,4,k,b2,u,q,i,e,e,s,i,k
1e,a,a,8,8,a,o,m,y,3y0,18,1o,18,i,1i,4a,2k,5a,a2,18,o,k,i,u,1k,u,c
14,8,8,6,6,6,i,g,m,41i,5,2u,y,k,g,2i,2s,6c,8w,1i,m,s,q,o,28,12,m
10,a,6,6,6,6,g,g,k,416,p,2m,16,i,e,s,2w,5w,8w,1k,c,w,s,q,28,14,u
1m,e,a,c,c,e,12,14,1q,3ui,3a,14,8,16,w,w,a,6,ay,s,m,k,i,k,10,k,m
18,a,8,8,6,8,m,i,q,3zw,0,2e,10,w,k,3i,2s,6c,9w,14,k,m,m,y,1i,12,6
14,a,8,6,6,8,i,i,o,408,9,2y,q,i,k,2e,2i,68,9a,1g,m,s,m,s,1s,18,a
14,a,8,6,6,8,i,i,o,40m,9,2q,w,o,m,2e,2c,52,94,1g,o,s,m,q,1u,16,i
1k,e,a,c,c,c,12,10,1m,3uo,34,1g,c,12,18,12,i,8,aw,s,m,k,k,g,14,k,o
1e,c,8,8,8,a,q,m,10,3xu,12,20,1g,o,1i,3i,2c,2w,9y,18,o,k,m,m,1g,s,k
1c,c,8,6,8,a,m,m,u,3yy,e,1u,1g,w,12,44,2o,4a,9y,14,q,k,g,u,1k,s,i
24,o,i,k,m,q,20,1w,2s,3ss,1l,1e,t,6,h,14,1o,2m,bc,16,a,k,a,c,m,g,q
10,8,6,6,6,6,i,g,k,40e,v,3k,8,6,8,9,3o,5k,92,1k,e,y,q,q,2g,q,14
1c,a,8,8,6,a,m,m,u,3z0,18,1y,1c,i,u,3o,2q,68,a0,16,q,m,q,q,1s,s,q
y,8,6,6,6,6,g,e,k,410,1f,n,3y,6,6,4,1e,4e,92,i,1e,o,u,q,1o,14,i
2a,q,k,m,m,q,20,1y,2s,3rw,r,m,b,g,c,1c,1e,2w,bi,14,i,g,e,a,m,i,o
1q,g,c,e,e,g,1c,1e,24,3tm,3g,3,i,q,i,7,3,6,b2,u,m,m,c,g,u,i,k
28,q,k,m,m,s,20,1w,2s,3s2,1d,q,h,i,a,1g,1g,32,bi,12,c,m,a,a,m,i,o
1s,i,g,e,g,k,1k,1m,2g,3tg,2u,1l,16,g,x,2,a,w,b2,y,s,a,e,i,m,g,m
14,8,8,6,6,6,k,g,o,40e,f,3o,m,g,a,1g,3m,5k,8y,1m,s,u,o,s,1y,y,12
16,a,6,6,6,8,k,g,q,404,5,2w,o,i,k,2c,2s,6c,9i,1c,k,s,m,u,1q,16,e
22,m,i,i,k,o,1w,1w,2q,3rs,f,1o,2,1,1,o,1i,1c,ba,12,i,m,a,e,q,g,u
10,a,6,6,6,6,i,g,m,40g,r,3y,c,8,6,4,3q,5m,90,1m,i,w,q,o,2e,u,18
28,o,k,m,k,s,20,1w,2s,3s4,1d,18,j,s,1,10,1o,2q,bi,16,c,k,c,a,m,i,o
10,a,6,6,6,6,i,g,m,40g,r,3y,c,8,6,4,3q,5m,90,1m,i,w,q,o,2c,w,16
14,8,6,8,6,6,i,g,m,40s,d,2u,q,m,k,1m,2a,56,8w,1k,m,u,o,q,20,16,m
1a,c,8,6,8,8,o,k,s,3zo,0,26,1g,14,s,3m,2m,56,9u,14,o,m,i,u,1k,w,8
18,a,8,6,8,8,k,k,q,3zw,6,2a,w,q,i,3g,2u,6k,9y,12,k,o,m,y,1m,10,a
24,q,i,m,m,s,20,1w,2s,3s2,1t,16,r,c,3,1k,1q,2y,be,16,8,m,6,c,m,i,o
18,c,8,6,6,a,k,k,q,402,9,2k,1e,u,o,3c,2o,5o,9u,16,i,m,k,y,1g,10,8
28,q,k,m,m,q,20,1y,2s,3s8,1f,s,n,i,6,1i,1i,30,bg,14,c,k,a,c,m,i,o
1i,c,8,a,a,a,u,s,1a,3w0,22,22,10,s,1q,2i,1w,m,a6,1a,o,i,q,e,1a,o,q
12,a,6,6,6,6,i,g,m,40a,j,3a,k,e,e,16,2y,5m,90,1k,k,w,o,o,24,10,q
26,q,k,m,m,q,20,1y,2s,3ry,1t,y,p,g,4,1q,1o,32,bg,14,a,k,6,e,m,g,q
12,8,6,8,4,8,g,i,k,40c,l,3c,g,e,e,w,2q,5m,90,1k,i,w,o,q,26,10,q
12,8,6,6,6,6,i,g,m,40c,p,3k,8,a,a,a,3q,5g,90,1m,i,w,o,q,2c,q,14
1a,a,8,8,6,a,k,k,s,3zu,3,2e,1g,u,s,3g,2q,5c,9u,16,k,m,k,w,1i,w,8
16,a,6,8,6,6,k,i,o,40s,0,2g,18,m,g,32,2o,4s,8u,1k,u,q,o,o,1u,16,k
24,q,i,m,m,q,20,1w,2s,3sm,1n,18,v,e,9,1a,1m,2u,be,16,a,k,a,c,m,g,q
16,a,8,6,6,8,k,i,o,3zy,0,2s,s,k,m,2s,2m,6o,9s,18,g,q,m,y,1k,18,6
12,8,8,6,6,6,g,g,m,412,j,30,q,k,i,10,2w,5w,8w,1i,g,y,q,q,22,18,o
y,8,6,6,6,6,g,e,k,418,17,h,40,a,8,e,1w,5g,8y,18,q,q,s,s,24,1a,u
26,q,k,k,m,s,20,1w,2s,3si,1l,12,t,g,1,1e,1k,2w,be,16,a,m,a,c,m,g,q
1s,g,e,e,g,i,1i,1g,2c,3tg,34,n,w,a,4,f,6,i,b4,u,m,k,e,g,q,i,k
1w,i,i,g,i,m,1q,1q,2k,3ta,1a,n,q,b,d,0,w,1k,b6,u,s,a,g,e,m,g,m
22,m,i,m,k,q,1y,1w,2s,3sk,19,16,l,5,7,y,1m,2m,bc,10,g,g,c,c,m,g,o
18,a,8,6,6,8,m,g,q,406,n,3e,1k,w,s,3u,38,6g,9u,18,e,o,m,k,1y,12,8
20,m,i,g,k,o,1u,1u,2s,3s2,5,1g,g,d,8,6,16,12,b8,10,m,m,c,e,q,g,u
1y,k,i,g,i,m,1u,1s,2q,3sg,i,u,12,t,i,h,o,k,b6,y,q,g,k,e,s,g,u
1s,g,e,g,e,k,1g,1i,2a,3tw,36,1d,16,u,p,4,2,c,b0,y,s,e,k,e,q,i,k
10,a,6,6,6,6,g,g,m,40c,r,3k,8,a,8,4,3o,5g,90,1m,g,w,q,q,2c,q,14
12,8,6,8,4,8,g,g,k,418,j,32,q,k,e,1c,2y,5w,8w,1i,g,w,q,q,28,12,s
20,m,g,i,k,o,1u,1s,2s,3sa,3,1g,i,h,8,4,12,y,b8,12,k,k,e,g,q,g,u
y,6,6,6,6,6,g,g,k,40c,t,30,0,2,0,3,20,62,92,u,6,1q,s,s,2i,10,y
14,a,6,8,6,6,k,g,m,40u,2,2k,10,m,g,2c,2s,4w,8s,1m,q,s,o,o,1y,16,m
12,a,6,8,6,6,i,i,o,40c,p,3o,y,m,k,20,3e,62,8y,1o,q,u,o,12,1q,12,12
1c,a,8,8,8,8,o,k,u,3z2,o,1q,16,m,y,4c,2s,5o,9y,14,s,k,i,w,1m,u,e
12,a,6,6,6,8,i,g,o,40e,h,3i,e,c,a,14,3q,5i,8y,1m,q,u,o,o,24,u,14
10,8,6,6,6,6,g,g,k,40e,z,2o,12,6,4,d,36,5i,92,1k,e,u,s,q,2e,s,w
28,q,k,m,m,q,20,1y,2s,3rm,1j,w,h,o,8,1g,1k,36,bi,14,c,k,a,a,m,g,o
12,a,6,6,8,6,i,i,o,40e,p,3s,u,m,i,1u,3c,5w,8y,1o,q,u,o,y,1u,12,12
1c,a,8,8,6,a,m,m,u,3z4,k,24,1k,10,10,3y,2o,56,9w,10,s,m,i,u,1i,u,8
y,8,6,6,6,6,e,e,k,414,1d,19,4e,6,6,e,18,3m,90,g,1i,o,s,s,1u,y,m
12,a,6,6,6,8,i,g,m,40c,h,3a,e,e,c,18,3i,5k,90,1k,o,w,m,o,24,w,y
2a,q,k,m,m,q,20,1w,2s,3rw,x,m,d,g,e,1g,1e,2w,bi,14,c,k,c,a,o,i,o
1s,g,g,e,g,k,1k,1i,2e,3tk,2q,t,10,2,0,f,a,q,b4,u,q,g,e,e,q,i,k
14,a,6,8,6,6,k,g,q,40a,2,36,w,m,g,22,3i,5o,8y,1o,u,s,o,10,1q,y,14
1m,e,c,a,c,e,14,14,1q,3tq,2w,s,c,1k,14,16,e,a,aw,u,m,i,k,i,10,i,m
1a,a,8,6,6,8,m,i,s,3zu,7,2u,1o,10,u,3s,30,6a,9u,16,i,o,k,q,1q,10,8
28,q,i,m,m,q,20,1y,2s,3ru,1d,16,h,q,0,10,1o,2y,bi,16,a,m,a,a,m,i,o
22,o,i,m,k,s,20,1w,2s,3ry,1l,16,p,0,5,1e,1u,2u,be,14,c,k,8,c,k,g,o
20,o,i,k,k,q,1y,1w,2s,3so,15,12,f,b,5,s,1m,2k,bc,y,i,g,c,c,m,g,o
1o,e,c,e,c,g,18,1a,20,3tq,3a,g,8,1c,k,e,7,1,b0,u,m,o,c,g,w,i,k
12,a,6,6,6,6,i,i,m,40g,l,3s,i,c,8,u,3o,5i,90,1m,m,w,o,o,26,w,16
14,a,6,8,6,6,k,i,o,40a,2,38,s,m,e,1w,3k,5o,8y,1m,w,s,o,y,1s,w,14
1y,m,g,k,k,o,1w,1s,2q,3t2,9,g,6,t,3,a,1g,24,b8,s,q,e,g,c,m,g,m
1i,e,8,a,a,c,w,u,1c,3va,2e,1y,g,y,1w,2e,14,o,ag,12,o,i,o,e,1a,k,s
16,a,8,6,8,6,k,i,q,3zy,4,2m,q,k,k,2s,2w,6i,9s,18,i,q,k,10,1m,12,e
1c,a,8,8,8,8,o,k,u,3yw,1a,22,1e,k,s,3m,2u,66,a0,16,q,m,q,s,1s,o,u
1i,e,a,a,a,c,w,w,1c,3v4,2g,20,a,12,1w,26,y,k,ai,12,m,k,m,g,18,k,s
1a,a,8,6,8,8,m,i,s,3zw,1,2a,1a,12,o,3k,2m,5q,9w,14,k,m,k,w,1k,y,8
1c,a,8,8,8,a,m,m,u,3yy,g,28,1i,w,16,3u,2i,4k,9w,14,o,m,g,w,1g,w,6
26,q,k,m,m,s,20,1w,2s,3rc,1d,18,j,q,4,18,1o,34,bi,16,a,k,a,c,k,i,o
y,8,6,6,6,6,e,e,k,416,19,17,4g,8,6,i,1g,48,90,o,1a,o,s,q,20,14,s
w,8,6,6,4,8,c,e,k,40y,1f,1p,4o,4,0,1e,4,4w,8y,i,1i,k,u,s,1q,1s,y
y,8,6,6,6,6,e,g,m,40e,13,1y,1g,0,2,9,1w,5s,92,16,c,18,s,q,2a,w,o
12,8,6,6,6,6,i,g,m,40c,p,3i,a,a,a,8,3o,5g,90,1m,g,y,o,q,2c,q,12
14,8,6,8,6,6,i,i,m,40g,h,3g,g,c,a,16,3q,5i,8y,1m,q,u,o,o,24,u,14
1u,g,g,e,g,k,1k,1k,2e,3tq,2m,11,14,e,f,1,6,q,b2,w,u,e,k,c,q,i,k
1u,i,g,g,g,m,1m,1m,2i,3tk,1u,j,18,7,0,d,g,y,b4,w,u,e,k,e,m,i,k
1e,c,8,8,8,a,o,o,y,3y2,u,1y,1i,m,1i,3o,2c,2y,9y,18,o,k,k,o,1i,q,m
1u,i,g,g,i,m,1o,1m,2k,3tc,22,15,y,1,l,2,k,1a,b4,w,u,8,e,g,m,i,m
24,q,k,k,m,s,20,1w,2s,3s6,1t,18,v,e,3,1i,1q,2y,be,16,8,k,8,e,k,i,o
2a,q,k,k,o,q,20,1w,2s,3s4,x,o,f,k,a,16,1e,2y,bi,14,g,k,c,a,m,i,o
28,q,k,m,m,q,20,1w,2s,3sa,1h,s,n,g,6,1i,1k,2y,bg,14,a,m,a,a,m,k,o
1o,g,c,c,e,g,1a,1a,20,3tq,3m,4,i,w,k,2,3,6,b2,s,m,m,e,i,w,i,k
y,8,6,6,6,6,e,g,k,40m,1d,1b,4o,0,0,0,1a,4a,92,m,1e,m,s,s,1i,14,i
1a,c,6,8,6,a,m,k,s,3zm,0,2k,1o,12,10,42,32,6a,9w,12,o,o,k,s,1q,u,c
1g,c,8,a,8,c,q,s,14,3x2,1k,2a,1e,e,1q,3a,22,30,a4,1a,o,k,o,i,1e,q,c
1k,e,c,a,c,c,10,12,1m,3v2,32,1a,8,18,1q,18,g,8,as,w,o,o,i,k,14,k,k
y,8,6,6,6,6,g,g,k,40g,19,8,3c,2,2,9,1o,56,92,12,q,w,s,s,1s,12,c
12,a,6,6,6,8,i,g,o,40e,h,3k,g,c,a,12,3q,5g,8y,1m,q,u,o,o,24,w,12
1q,g,c,e,e,i,1e,1e,24,3t2,2y,j,14,12,2,3,8,e,ay,y,m,k,g,i,q,i,i
14,8,8,6,6,6,i,i,m,40s,9,2s,s,m,k,1o,2a,56,8w,1k,m,u,o,o,22,16,k
1o,e,c,c,e,g,16,1a,1w,3uc,3c,e,8,1g,4,i,4,0,ay,w,i,o,g,i,y,i,o
1i,i,c,g,e,k,1g,1g,26,3um,18,j,1z,6,r,q,26,3o,ao,y,14,q,w,w,10,q,w
14,a,a,8,8,c,q,q,10,3xq,25,g,10,12,j,3w,3k,70,8o,22,10,u,s,14,1k,10,1c
w,8,8,6,8,8,i,k,o,3z2,1b,j,2i,12,14,16,4k,6o,8m,y,18,y,s,o,2i,16,14
y,8,8,6,6,8,k,i,o,3yw,v,1y,1a,14,16,1q,46,72,8q,1g,s,w,q,q,2e,16,10
w,a,8,6,8,8,k,k,q,3yu,x,v,1m,14,18,1m,4i,6e,8k,14,16,10,q,s,2a,18,14
1c,i,c,g,i,m,1s,20,2y,3t8,2m,28,2v,17,23,v,15,24,aa,10,y,10,m,s,18,g,u
1a,c,a,a,a,e,u,u,18,3uw,p,1c,12,11,12,3s,3s,5i,9q,1i,y,u,12,i,1q,s,1a
w,a,6,8,6,8,k,i,q,3yu,1r,1a,1m,12,18,1e,4k,6o,8o,1c,w,w,q,q,2e,18,12
w,8,8,8,6,8,k,i,q,3z0,1b,h,2c,12,16,18,4k,6m,8m,10,18,w,s,o,2i,16,14
u,8,6,8,6,6,i,g,m,3yu,r,13,4c,10,w,16,42,86,8s,g,1k,s,s,s,2k,16,16
18,e,a,a,c,e,y,y,1e,3t0,8,28,1g,1p,2,2q,3a,4m,9s,18,w,y,12,m,1o,q,16
1g,i,e,g,g,i,1m,1m,2e,3ue,1w,0,2n,f,t,g,6,3y,ai,10,12,u,s,w,12,m,u
1c,g,a,a,e,e,14,10,1k,3so,12,22,7,1j,s,2i,2w,4c,a2,18,u,16,q,y,18,u,12
u,8,8,6,6,8,g,i,m,3z0,r,11,4a,y,w,14,44,84,8q,i,1k,s,s,s,2m,14,14
1a,e,8,a,c,c,w,w,1a,3ty,2,1s,s,19,q,3i,3m,5a,9q,1g,w,w,12,m,1m,s,1a
w,8,8,8,6,8,k,i,s,3yy,1h,2,1q,14,18,1g,4i,6i,8k,1c,10,w,q,q,2e,18,14
u,8,8,6,8,6,i,i,q,3z4,1b,17,3a,y,s,e,4e,72,8k,i,1k,w,u,s,2o,12,18
u,a,6,8,6,8,i,k,o,3zg,1l,11,2o,q,u,i,4i,6o,8i,i,1k,y,w,q,2m,14,16
w,8,8,8,6,8,k,k,q,3yy,1b,f,1y,14,18,1g,4k,6g,8k,16,14,y,q,q,2e,18,14
y,a,8,8,6,8,m,k,s,3ye,1z,1o,m,16,18,28,4g,6o,8m,1o,u,y,o,y,20,1a,14
1g,e,c,c,c,g,14,12,1o,3sw,16,16,17,9,1e,2e,2u,3w,ac,1a,u,14,k,12,14,u,10
y,a,8,6,8,6,m,i,q,3yc,z,28,m,14,18,22,4c,6o,8q,1m,q,y,o,s,26,18,14
y,8,8,8,8,8,k,k,s,3ys,1v,u,m,1a,16,24,4g,6i,8k,1q,s,w,q,s,26,1a,14
12,c,a,8,8,c,u,s,16,3xi,3j,1g,1o,o,17,30,2q,5o,8e,24,10,u,u,18,1k,u,18
1c,g,c,e,e,g,1e,1c,20,3t0,1e,1o,1b,13,3,i,10,3w,a2,18,u,16,k,12,16,q,w
1a,i,c,e,e,k,1i,1m,2c,3sq,22,2e,21,1d,z,4,n,38,a0,18,s,16,m,y,18,m,s
1a,g,a,c,c,g,14,14,1o,3si,o,1y,i,1j,8,1q,2i,3y,9w,1a,u,16,q,y,1a,s,10
14,c,a,8,a,c,u,u,1a,3wc,37,26,1m,m,1v,3i,2o,5a,8m,2a,q,s,w,14,1o,q,18
14,i,a,c,e,i,1g,1m,2m,3u4,2b,32,1o,2p,2,1l,11,2,8u,20,u,u,16,m,1g,k,o
10,a,8,6,8,8,m,k,s,3xy,1j,1g,w,16,14,2e,4e,6s,8m,1o,y,w,q,y,1w,1a,14
u,8,8,6,8,6,k,i,o,3z0,x,1j,3q,10,y,o,4i,76,8m,i,1m,u,u,q,2o,12,16
w,8,6,8,6,8,i,g,o,3yw,t,3,3a,10,y,14,4a,7m,8q,q,1e,s,s,q,2m,14,12
y,a,8,8,6,8,m,k,s,3ym,23,1a,k,1a,16,28,4g,6q,8m,1o,u,y,o,y,20,1a,14
y,a,8,8,6,a,m,m,u,3yg,p,e,n,12,12,20,4e,6u,8g,1o,y,10,q,w,1y,1a,12
w,a,8,6,8,8,k,k,s,3yu,11,l,18,18,16,1s,4i,6e,8i,1a,14,10,q,s,28,18,14
10,a,8,8,8,8,m,k,u,3y4,1p,10,y,18,10,2i,4g,6w,8m,1o,12,w,o,12,1u,18,16
1k,g,c,e,e,i,1c,1c,20,3s4,20,1,1h,y,e,18,28,3c,as,12,w,y,u,u,10,s,w
y,8,8,6,6,8,i,i,o,3yy,v,26,1c,14,12,1o,48,74,8s,1g,q,w,q,q,2e,14,12
12,a,a,8,8,a,s,q,10,3y6,2r,i,1a,o,9,30,34,6i,8e,1w,14,w,s,1a,1i,10,1a
1a,e,c,a,c,g,16,16,1o,3sk,g,22,m,1b,1,12,20,3w,9w,18,u,12,s,y,1c,q,y
12,a,8,8,8,8,q,k,w,3yc,23,8,1a,1e,e,30,4c,7a,8k,1s,18,u,o,18,1m,16,18
10,a,8,8,6,8,o,k,u,3ym,1t,a,m,1e,w,2i,4e,6w,8k,1o,12,w,o,14,1s,1a,14
y,8,8,8,6,8,m,k,s,3ys,1h,4,u,1c,18,20,4i,6e,8k,1k,w,y,o,s,28,1a,14
1a,g,a,c,c,g,18,16,1s,3si,s,20,8,17,5,o,1o,3u,9w,18,u,12,s,y,1c,q,w
1c,g,a,a,e,e,16,12,1m,3sk,w,24,2,1r,m,28,2q,44,a0,1a,u,16,o,10,18,s,12
y,a,8,6,8,6,m,i,q,3ya,11,28,o,12,1a,20,4e,6m,8q,1m,q,y,o,s,26,18,14
w,8,6,8,6,8,k,i,o,3yy,t,19,2s,w,10,s,4m,6o,8k,o,1g,y,u,q,2k,14,16
y,a,8,6,8,6,m,i,s,3xy,1b,28,q,12,1a,24,4a,6m,8o,1o,q,w,q,q,28,1a,12
10,c,8,8,8,8,q,m,w,3yg,1n,f,w,1g,g,2w,46,78,8i,1s,16,y,m,18,1m,18,18
1a,c,a,a,c,c,y,y,1c,3tm,5,2c,12,1j,6,36,3g,4w,9s,1a,w,y,10,s,1i,s,16
18,e,a,8,c,c,y,u,1a,3u8,h,28,y,19,6,3i,3k,58,9q,1c,y,u,14,q,1k,q,1a
18,c,a,8,a,e,u,s,18,3v8,11,1w,y,l,7,3w,3m,5s,9i,1k,w,s,14,q,1m,u,1a
w,8,8,6,8,6,k,i,q,3z0,v,1n,34,10,16,10,4k,6k,8k,o,1i,y,s,q,2i,16,14
1a,i,c,g,i,m,1o,1y,2w,3t2,2g,2a,2d,19,1t,13,13,1w,a6,12,w,14,m,s,18,g,u
14,c,8,a,8,a,s,q,12,3xa,27,10,y,w,p,40,3i,6s,8w,1y,w,u,w,10,1m,y,1c
y,a,8,6,8,8,k,k,q,3xy,13,24,s,12,1a,24,4c,6o,8o,1o,s,w,q,s,24,1a,12
y,a,8,8,6,8,k,k,s,3y0,11,20,q,14,18,26,4e,6m,8o,1o,s,y,o,u,24,18,12
10,a,8,8,8,a,q,m,y,3yg,1n,2,k,o,m,2a,3o,6w,8e,1q,14,y,s,16,1k,18,14
10,a,8,8,8,8,o,k,w,3yi,19,f,g,1i,u,2i,4c,72,8k,1m,14,y,o,16,1o,1a,14
12,e,8,8,a,c,u,s,16,3x4,3f,1q,1m,q,1j,3e,2s,5m,8g,2a,u,s,u,18,1m,s,18
w,8,6,8,6,6,k,g,o,3z8,z,v,3u,y,u,y,4o,7a,8o,i,1k,w,s,q,2o,10,14
12,e,8,8,a,c,u,s,18,3x6,3n,1u,1m,q,1j,34,2o,5g,8g,28,u,u,w,16,1m,s,16
w,8,8,6,8,8,i,k,q,3z0,11,p,1w,w,10,10,4k,6g,8i,s,1e,y,u,q,2i,16,14
1i,i,i,k,k,q,1y,2c,36,3v8,2g,1r,1x,1x,25,12,27,3o,aq,u,16,s,m,m,16,g,w
18,e,a,a,c,e,10,10,1g,3t8,f,28,1e,13,d,2c,2w,4c,9m,1e,w,y,10,q,1k,q,14
y,a,8,6,8,8,o,k,w,3yg,p,a,b,u,12,1w,4e,6u,8g,1g,12,12,q,w,20,1a,12
14,c,8,8,8,a,q,o,10,3y6,33,u,1e,1e,l,4c,3q,78,8k,2e,w,s,o,16,1k,14,1c
1e,e,a,a,c,e,12,y,1g,3sw,10,1m,9,13,1k,2w,36,4i,a0,1e,w,14,m,y,1a,u,16
1e,i,e,i,i,m,1s,1y,2s,3u6,2k,e,2t,1b,1n,o,1n,3i,ag,10,10,y,m,s,16,i,u
w,a,6,8,6,8,k,i,q,3yy,23,1g,1a,14,18,1g,4i,6i,8m,1k,q,w,q,q,2e,18,14
w,8,8,8,6,8,k,i,q,3z0,1f,7,24,14,16,1a,4k,6k,8m,14,14,w,s,q,2g,16,14
1a,e,a,a,c,e,y,y,1g,3t4,4,2c,12,1p,c,2y,36,4m,9u,1a,w,10,w,s,1i,s,14
10,c,8,8,8,a,q,q,10,3yg,2d,6,14,k,8,2i,3a,6o,8e,1q,16,y,s,1a,1g,14,18
16,c,a,8,a,c,u,s,14,3vu,1l,1w,w,0,l,44,3k,62,9e,1o,u,s,14,q,1o,u,1a
1c,e,a,8,a,c,u,s,18,3tw,g,2c,1e,2,1y,2s,3m,56,a0,1m,q,s,y,o,1m,u,18
s,8,6,6,6,6,e,c,k,3xe,1z,32,z,8,c,2u,30,4s,90,i,8,28,o,q,1y,16,1k
w,6,6,6,6,6,g,e,k,408,1h,2c,c,5,3,e,s,42,92,i,8,1w,u,s,1m,w,o
1a,e,8,a,8,c,s,s,16,3v0,19,1u,1s,3,1i,3q,3s,5s,9w,1i,w,o,14,i,1q,w,1a
s,8,6,6,6,6,e,e,k,3y4,21,30,p,a,a,16,24,5y,90,i,8,26,o,s,26,y,18
u,6,6,6,6,6,e,c,k,3wu,1h,2s,7,k,24,34,2o,4i,90,g,c,24,o,q,2e,1e,1m
14,c,8,8,6,a,o,k,w,3xy,1f,1i,1i,1a,a,3c,4g,70,8w,1u,12,s,o,14,1q,10,18
u,6,6,6,6,6,g,e,i,3yy,23,2u,h,2,2,18,28,5u,90,i,8,24,q,s,26,s,1a
u,8,6,6,6,6,e,e,i,40g,1x,27,4i,5,0,1y,c,24,90,i,1k,c,w,u,1e,14,s
14,c,8,8,8,a,o,m,y,3xi,13,1a,1k,1c,0,3m,4a,72,92,1s,12,q,q,12,1q,y,1a
w,8,6,6,4,8,e,e,k,40e,1f,10,1o,3,3,g,s,3s,92,i,m,1i,s,s,1m,w,m
s,8,6,6,6,6,e,c,k,3we,1p,34,r,g,1q,3c,2m,42,90,i,c,24,o,q,20,1g,1q
y,8,6,6,8,6,i,i,m,3zc,v,2e,20,12,u,1m,46,7e,8u,1e,s,s,s,q,2g,10,12
u,8,4,6,6,8,e,e,k,3z2,1v,2s,5,6,4,k,1w,6a,90,i,8,22,q,u,26,10,18
16,c,8,8,8,a,o,o,w,3xe,7,1m,1g,w,0,3q,4a,6u,9o,1a,10,q,q,12,1q,u,1a
1c,e,a,a,a,e,w,u,1c,3u8,t,1u,q,n,22,3a,3g,52,9y,1m,s,q,10,q,1i,w,16
u,8,6,6,6,6,e,e,k,3w0,11,1m,1e,w,24,3a,2m,60,90,e,o,1s,q,q,2q,12,1e
s,8,6,6,6,6,e,c,i,3ww,1v,34,x,8,1g,3a,2c,42,90,i,a,26,o,q,1q,1k,1k
u,8,4,6,6,8,e,e,i,3zm,1z,2o,b,5,3,1g,1o,5a,90,k,4,24,q,u,20,u,10
u,6,6,6,6,6,e,e,i,3yq,25,2s,r,0,6,2i,2g,52,90,i,6,26,q,s,22,y,1a
y,8,8,6,6,8,i,i,o,3z4,v,2c,1g,18,10,1o,48,76,8u,1e,s,u,q,q,2e,14,12
16,c,a,8,8,a,q,q,10,3wy,2h,1c,1o,y,0,4c,3u,6s,9a,1u,y,q,w,u,1o,y,1a
w,8,6,6,6,6,e,g,i,40m,19,1x,4m,3,1,e,12,2g,92,g,1k,k,u,s,1g,u,u
10,8,8,6,8,6,m,i,q,3ym,p,28,k,14,18,26,4e,6m,8q,1o,q,y,o,u,24,16,12
u,6,6,6,6,6,e,c,k,3xo,21,30,x,4,i,30,30,4e,90,i,8,26,q,q,1y,14,1k
u,6,6,6,6,6,e,c,k,3xm,1n,2u,b,c,20,2w,2c,4e,90,g,a,24,o,q,2c,1i,1k
12,c,8,8,6,a,o,k,w,3xs,1x,1e,1m,1c,i,32,4g,70,8o,1y,12,s,o,16,1o,14,18
w,8,6,6,4,8,c,e,k,40y,1f,1t,4m,2,2,1c,a,3y,90,g,1i,m,s,s,1k,1o,w
u,8,4,6,6,6,e,e,i,3zo,25,1k,i,3,4,2m,1g,3c,90,i,g,1s,s,s,1i,1a,16
u,6,6,6,6,6,e,c,i,3xm,1p,2w,h,a,1y,30,24,4c,90,g,a,26,o,o,2a,1i,1k
16,c,a,8,8,c,q,q,10,3wu,2h,1g,1m,s,2,4g,3q,6q,9e,1s,w,q,10,q,1o,y,1a
u,8,6,6,6,6,c,e,i,402,1r,23,4q,2,s,1s,8,4y,8y,i,1m,k,s,s,20,1q,16
u,6,6,6,6,6,e,e,k,3yk,25,2u,r,2,4,28,2k,5k,90,i,8,26,o,s,26,w,1a
u,8,4,8,4,8,e,g,k,3yy,1l,12,2c,k,k,w,2e,78,8y,i,s,1i,q,s,28,1k,1a
u,6,6,6,6,6,g,e,k,3yi,23,2y,f,c,a,o,1w,68,90,i,6,26,q,s,28,10,18
s,8,6,6,6,6,e,e,k,3xk,21,32,v,8,a,2e,2o,58,90,i,8,28,m,s,20,14,1g
y,8,6,8,6,6,i,g,o,3zg,v,2g,1y,12,w,1k,42,7i,8w,1c,s,u,q,q,2g,12,10
w,6,6,6,6,6,e,e,i,40s,1n,1z,4k,0,2,1m,6,32,90,i,1i,6,10,s,1g,1i,u
u,6,6,6,6,6,g,e,m,3ym,1z,2w,5,e,c,m,1s,6i,8y,i,8,26,o,s,28,1a,18
u,6,6,6,6,6,e,c,i,3xm,1t,2y,p,6,1q,36,1m,4g,90,i,8,26,o,q,1w,1o,1g
w,6,6,6,6,6,g,e,i,40s,1n,21,4i,3,1,1c,g,1s,92,g,1k,3,14,u,1g,s,u
u,8,6,6,6,6,g,g,k,3z6,13,p,4m,o,k,10,2w,7i,8y,i,1g,u,q,q,2c,1g,18
18,c,8,8,8,a,o,o,10,3wq,b,1o,1k,s,8,3o,46,6q,9s,1a,y,q,s,y,1s,u,1a
y,6,6,6,6,6,g,g,k,40g,17,9,3e,1,0,0,12,4g,92,o,y,10,u,q,1o,y,g
u,8,6,8,6,6,e,g,k,3vu,v,0,36,u,20,3e,2o,6g,90,k,16,14,q,o,2q,10,1a
u,6,6,6,6,6,e,c,k,3xw,23,2y,v,2,u,36,2q,3y,90,i,8,26,o,s,1q,1a,1o
s,8,6,6,6,6,e,c,k,3x4,1z,34,z,8,q,34,30,42,90,i,8,28,o,q,1s,1a,1o
u,8,6,6,4,6,e,e,i,3zu,1p,23,4q,4,w,1w,o,4s,8y,i,1m,m,q,s,24,1k,1e
u,6,6,6,6,6,e,e,i,3wu,1h,2s,3,m,24,34,2m,4o,90,g,c,24,o,q,2g,1e,1k
u,8,6,6,4,6,e,e,k,3w6,1b,2m,6,w,24,3a,2o,58,90,g,e,22,q,o,2m,18,1i
14,c,6,8,8,8,o,k,w,3yo,z,1g,1c,12,k,36,4k,6u,8y,1q,12,s,m,16,1s,w,18
14,c,8,6,8,8,o,k,w,3z0,r,1a,18,10,i,3a,4m,6w,90,1q,10,s,o,16,1q,w,16
y,8,8,6,6,6,i,i,o,3zg,v,2s,1i,14,w,1o,42,7c,8w,1g,o,u,s,o,2g,12,10
u,6,6,6,6,6,e,e,i,3wa,1f,2s,5,u,22,3a,2o,4s,90,g,c,24,q,o,2i,1c,1k
y,a,6,8,6,8,i,i,q,3z2,v,2u,g,1c,16,20,4c,6q,8s,1o,m,w,q,q,2a,14,12
s,8,6,6,6,6,e,c,k,3x8,1z,34,11,6,y,38,2w,3u,90,i,8,28,o,q,1u,16,1o
16,c,8,8,6,a,o,m,w,3y6,d,1i,1a,w,4,3o,4g,6w,9i,1e,10,q,q,12,1s,u,18
16,c,a,8,8,c,q,q,12,3x6,2t,1e,1g,q,7,4s,3i,6s,9c,1s,y,q,10,q,1o,10,1a
u,8,6,6,6,6,c,e,k,3wu,19,2k,a,m,24,2y,2k,56,90,g,a,24,q,o,2k,1a,1k
u,8,6,6,6,6,g,g,k,3z6,11,8,3k,k,i,w,2q,7e,8y,i,14,16,q,q,2a,1k,16
u,6,6,6,6,6,e,c,k,3yw,1v,1o,m,2,1e,2m,o,4u,90,g,i,1u,q,q,1y,1q,18
14,c,8,8,8,a,o,m,y,3xk,1l,1c,1m,1e,2,3m,4a,72,8y,1w,10,s,q,12,1q,y,1a
u,8,4,6,6,8,e,g,k,3yw,1t,2s,4,c,c,i,1s,6s,90,i,8,22,q,s,28,1c,1a
u,6,6,6,6,6,e,c,k,3yu,25,2q,p,1,k,32,2g,3o,90,i,6,26,q,s,1i,1e,1k
u,6,6,6,6,6,c,e,i,3xq,1v,2w,r,6,1k,38,1o,48,90,i,8,26,o,q,1o,1q,1i
y,6,6,6,6,6,g,e,k,412,1b,1l,4g,4,2,o,w,2w,90,g,1k,k,u,s,1k,y,o
u,6,6,6,6,6,e,c,k,3yy,27,2s,r,1,e,30,24,42,90,i,6,26,q,s,1o,1a,1c
u,6,6,6,6,6,g,e,k,3ym,21,2u,d,8,8,u,1w,68,90,i,8,24,q,s,26,10,1a
u,8,6,6,4,8,e,c,k,3zw,1z,20,a,7,3,1q,w,3o,92,g,c,1w,s,s,1o,y,q
14,c,8,6,8,8,o,m,u,3ye,z,1e,1e,16,g,36,4k,6w,8w,1u,10,s,m,16,1s,y,16
14,a,8,8,8,8,m,m,u,3yo,13,1i,1c,14,m,34,4k,6s,8w,1s,12,s,m,16,1q,y,18
w,8,6,6,6,8,g,g,m,3zc,n,3,4g,y,u,1c,3w,7y,8w,q,1e,q,s,q,2k,10,14
u,8,6,6,6,4,e,e,k,3we,19,2k,a,s,26,36,2o,56,90,g,c,24,q,o,2q,14,1k
u,6,6,6,6,6,e,c,k,3y2,1r,2u,b,8,1u,2s,20,4e,90,g,8,26,o,q,2a,1i,1k
u,8,4,6,6,6,e,e,i,3z0,1p,t,3g,6,1i,28,1o,4k,90,g,18,12,q,q,2c,1g,1k
u,8,4,6,6,6,e,e,k,3zw,25,10,14,5,0,2c,10,30,90,i,m,1m,s,s,1i,14,y
w,8,8,6,6,6,i,g,o,3za,n,0,46,y,w,1c,3q,88,8u,s,1c,s,s,q,2k,12,10
10,a,8,8,6,8,m,i,s,3ya,n,1m,y,10,16,2g,4e,6o,8q,1m,10,u,o,12,1w,16,12
u,6,6,6,6,8,e,g,k,3yu,1t,2s,6,e,c,k,1u,6u,90,i,6,24,q,s,28,1c,1a
u,6,6,6,6,6,e,e,k,3y4,23,2w,r,6,4,28,2o,5g,90,i,8,26,o,s,24,10,1c
s,8,6,6,6,6,e,c,k,3wm,1t,36,x,8,1a,38,2u,3k,90,i,a,26,o,q,1m,1e,1s
12,c,8,8,6,8,o,m,u,3xy,1r,1q,1i,1a,k,2y,4i,70,8o,1y,12,s,m,18,1q,12,16
1k,e,a,c,a,c,12,12,1m,3qy,2e,1o,1,40,k,4y,1y,50,cm,36,24,1e,y,2q,1y,4i,1o
1g,e,a,8,a,c,12,y,1q,3p8,2e,1o,2i,1y,1m,28,62,42,cg,34,2a,1o,18,2s,4y,1q,1e
1c,e,a,a,a,a,q,q,1a,3o8,o,s,2y,18,4u,84,54,1b,ce,32,1y,1a,10,m,2q,16,20
1w,i,e,c,g,i,1i,1e,28,3ug,16,u,3o,w,2o,2x,4g,14,eo,1y,1w,u,s,10,34,1w,3g
1e,e,8,8,c,c,12,12,1s,3o6,2a,2c,42,x,12,42,4q,4q,c8,36,2c,1o,2a,22,5c,1k,s
1m,e,c,c,c,e,18,1o,2e,3ru,2e,10,1,2i,3q,52,3b,1w,d2,2u,2c,1i,1q,1k,22,4m,5
1k,e,a,c,c,e,12,1k,24,3qw,2a,1w,3,1d,74,5o,39,4e,ci,38,2c,1m,2w,k,2w,4a,3
1m,e,a,c,e,e,1a,1q,2o,3r2,2c,16,2,3i,2c,2q,p,0,ci,3a,2c,1k,18,24,36,34,d
1i,c,a,8,c,c,12,16,1s,3p8,2a,20,2q,r,1s,4m,2o,66,ce,36,2c,1o,2i,1k,5c,1k,q
1e,c,a,8,a,a,12,w,1o,3og,1o,22,4e,i,1i,u,8g,2m,c8,36,2c,1m,1s,2m,5m,12,1k
1g,e,a,8,a,c,10,w,1m,3p4,2g,1m,24,2c,2c,5,7u,2e,cg,34,2a,1k,18,2o,50,1m,1q
1o,e,a,c,a,e,12,1c,1o,3ru,2i,y,8,34,2,7y,2x,7o,cy,30,22,1c,1a,2a,22,4s,10
1a,e,a,8,8,a,o,m,1a,3oe,9,w,3m,2u,3o,92,4u,z,c8,36,26,1a,16,18,38,46,1e
1e,e,8,a,a,a,10,w,1m,3oq,1q,1s,3w,1c,1k,6,8o,2e,cc,34,2c,1m,1g,2s,5o,10,1o
1a,c,a,8,8,a,o,q,1c,3ok,n,14,48,2w,10,8m,5s,11,c2,3a,28,1c,1e,2m,50,24,12
20,m,q,s,q,y,3m,4c,86,3wo,7,4t,1p,o,s,u,33,61,em,1q,1i,g,e,a,i,c,1s
1m,g,c,c,c,e,14,12,1i,3re,1u,y,y,2o,1s,3c,3q,56,dk,2k,1o,16,s,y,2q,y,1o
1m,e,a,e,c,g,1i,1s,2y,3r0,2i,y,7,6i,d,c,w,1d,cm,36,2a,1k,a,2s,26,3g,9
20,k,e,e,i,i,1q,1k,2m,3vk,n,2y,36,j,2k,1n,3k,x,ew,2i,16,s,q,1i,2o,y,44
1m,e,c,c,c,e,18,1o,2e,3ru,2i,y,1,2k,3m,54,3d,22,d4,2s,2c,1i,1o,1m,22,4m,5
18,e,a,a,8,a,o,m,14,3oo,t,10,36,2g,44,8y,78,g,c8,34,1m,1o,10,u,2m,18,2i
1i,e,a,a,c,e,12,1g,1y,3ps,2c,26,1c,1n,40,5a,p,5s,cg,36,2c,1q,2u,u,4w,28,4
18,c,8,8,8,a,o,s,1c,3ok,1f,1c,4q,3o,j,7i,4y,1s,bm,3k,28,1g,1g,30,62,1g,o
1q,e,e,g,g,m,24,2a,4c,3sw,2i,x,42,1y,v,59,3w,4t,do,2e,26,u,k,12,30,5,8
14,c,a,8,8,8,m,i,q,3pq,3l,f,4c,3q,2s,7w,66,d0,b8,3s,26,1i,12,1c,3e,48,1k
1g,e,a,c,c,c,w,y,1g,3ow,1m,1k,s,16,46,4o,24,42,ck,34,1s,1a,u,m,2s,12,1k
1c,c,a,8,8,a,s,10,1c,3oe,g,1w,4k,22,i,7c,4,7k,c4,38,2a,1i,1k,2w,60,10,12
1m,e,a,c,e,e,1a,1o,2m,3rk,2a,14,2,32,2y,3s,1z,q,cu,30,2c,1k,1g,1u,2o,3q,7
24,m,i,m,m,q,2m,2w,5k,3x6,l,2u,2b,1y,1d,h,1n,55,f2,26,18,q,k,24,1k,e,i
16,c,8,8,8,a,m,k,s,3pi,3b,2,4k,3s,16,7m,84,be,be,3m,28,1i,14,24,4g,30,1q
1e,e,a,a,a,a,q,y,1a,3oa,1o,18,26,26,4s,7y,2z,3s,cg,34,20,1c,10,10,38,3a,1q
1c,c,a,8,8,a,s,10,1a,3og,c,1s,4i,24,q,7a,c,76,c4,38,2a,1i,1k,2s,5y,16,y
1w,i,k,o,m,u,30,3k,6y,3vk,s,f,2j,2,s,7,1z,6n,ea,24,1q,m,e,g,g,o,10
1i,c,a,c,8,c,y,y,1g,3pe,2c,1m,10,3c,2c,f,5m,4i,ci,34,24,1g,14,2i,5i,14,1a
1k,c,a,c,a,e,10,1a,1s,3q8,2k,1o,w,1o,w,6s,t,6u,ck,34,2c,1m,1q,24,30,3w,o
1s,e,e,c,e,g,18,18,1o,3se,2y,1,20,2q,d,2s,2g,2y,ec,20,1o,14,u,1a,2k,1g,48
1u,g,g,i,o,o,2m,30,5u,3uq,1y,j,y,1f,14,1f,1n,67,e2,28,1u,u,e,u,q,1c,k
1o,e,c,c,e,g,1c,1q,2q,3sk,2k,6,q,5e,7,3u,1v,7,dm,2g,2c,1c,q,2k,1y,3q,1
1e,c,a,8,a,a,10,w,1o,3oc,1o,26,4e,i,1e,u,8i,2k,c8,36,2c,1m,1u,2k,5o,12,1i
1e,e,a,8,a,a,s,10,1a,3oi,1u,1c,2m,2k,2a,6a,1d,7e,cg,32,26,1i,16,2g,4y,20,u
1e,e,8,a,a,a,12,w,1q,3om,1u,22,42,i,1u,1g,7s,36,ca,36,2c,1m,1s,2i,5k,16,1g
24,q,q,o,q,10,3k,48,82,3xm,19,57,8,e,10,1q,4n,6p,eu,1c,1i,k,i,k,6,m,2a
22,m,k,k,m,y,2k,34,5w,3vs,2g,1n,s,u,25,2q,51,5h,es,1k,1m,q,w,k,q,1k,10
1w,g,g,m,m,s,2q,34,66,3um,y,16,x,b,o,15,v,6l,e4,28,1q,o,g,i,18,0,q
2c,s,q,q,s,18,3g,4a,7w,3y0,2j,3n,k,w,11,34,6j,5h,fa,10,1k,u,o,m,0,1o,20
26,m,i,k,m,q,22,2c,44,3w4,24,1y,j,7,m,8,z,2l,fc,1c,1w,q,m,u,2m,m,k
2c,w,s,q,u,16,3w,4m,8e,3y0,2t,5l,y,1a,m,1s,6n,51,fe,o,1u,k,i,s,4,1m,2e
28,o,m,m,o,10,2q,38,62,3xs,0,x,a,2,17,g,3b,4h,fa,1c,1o,y,u,w,1c,w,o
2a,o,q,o,q,y,38,3s,72,3xq,w,4h,9,i,z,2c,55,4v,fg,y,1w,o,i,26,4,1c,u
1y,k,i,m,m,u,2u,3a,6c,3v6,w,c,p,6,a,e,2f,81,ee,1y,1o,i,m,k,m,a,1s
28,m,g,k,q,q,26,2g,4g,3ww,g,30,g,2p,j,2c,1d,37,fe,20,18,u,o,20,1a,k,i
y,a,a,8,6,a,k,g,o,3o2,d,29,2s,44,3m,7i,9c,bg,96,42,3g,1o,16,16,3k,12,3e
y,a,8,a,6,8,m,g,m,3oc,f,27,24,3u,3i,78,96,b6,8u,48,3c,1w,16,14,3k,12,3k
12,c,a,8,8,8,m,i,o,3q6,2z,b,4k,4a,3c,8s,8m,c8,bc,3g,2k,16,y,18,2u,12,1u
s,a,8,8,6,a,o,m,y,3mk,1r,1d,i,3s,2u,20,4y,7y,8k,34,36,34,1k,1i,4w,3k,1o
14,e,a,8,8,a,m,i,s,3q2,33,o,3m,3y,3g,8y,7w,9w,bw,3c,1k,1q,10,14,2m,10,1a
y,c,8,8,8,8,m,i,s,3lo,2,1b,4g,3s,3k,72,90,b4,9m,3y,3s,1i,18,1i,3m,26,20
14,e,a,8,8,8,m,k,q,3pi,2x,m,3c,3u,36,8o,80,9w,bk,3m,1g,20,10,16,2w,14,3y
12,c,8,8,8,8,m,g,o,3p8,29,9,58,48,3g,8y,98,by,b0,3q,2q,14,10,1a,32,16,3c
y,a,a,8,6,8,m,i,o,3n8,5,29,3m,3y,3m,7i,94,bo,8w,4c,3o,1k,18,1a,3o,1g,2q
u,a,a,8,8,8,k,k,q,3me,1i,37,1w,32,38,6c,78,bm,8s,3s,3k,22,1g,1a,40,32,1e
10,c,a,8,6,8,m,g,q,3o4,1j,j,5c,42,3m,8m,9o,by,ag,3i,3g,18,12,1e,36,18,32
10,a,a,8,8,8,k,i,o,3oi,19,1x,3o,40,3g,7k,8u,bo,a8,3i,3c,1c,12,16,38,14,34
u,8,a,8,6,a,o,m,w,3la,u,2v,24,36,32,2e,5m,8w,8o,3m,3k,2e,1k,1g,4m,3a,1m
10,a,a,8,8,8,k,i,m,3oi,17,1t,48,46,3i,82,94,bs,a2,3i,3o,18,10,18,3a,14,3e
w,a,8,8,8,8,q,k,u,3km,1e,2f,30,2q,3a,40,74,9u,8u,40,3m,26,1k,1g,46,30,1k
q,a,8,8,8,8,q,m,y,3n0,2p,r,0,3u,2w,1y,4w,7s,8i,2y,2w,3i,1k,1k,4w,3o,1o
w,a,a,8,8,8,k,g,o,3o2,2,2l,1y,40,3m,7a,9c,b2,8u,46,3a,20,16,16,3o,12,3e
q,8,8,8,8,a,o,o,y,3ou,4r,q,2p,40,3e,1o,4q,6w,8e,24,2w,3y,1q,1o,5i,3s,1m
y,a,a,8,8,8,m,i,q,3m6,0,1t,46,3u,3k,74,8u,bg,9c,42,3q,1k,1a,1e,3o,28,20
14,e,a,8,8,a,m,i,s,3qo,2z,w,48,44,3s,9a,8i,94,c4,3a,22,12,10,12,2i,10,14
2y,u,q,y,10,16,46,4m,8a,3pm,34,4a,2p,x,1a,6t,w,21,fq,20,a,q,m,k,3c,k,18
2q,o,o,k,o,s,26,28,3k,3os,5k,u,2i,e,s,2c,k,12,gw,1k,m,y,i,1k,20,1u,k
34,w,s,u,14,1a,4g,4y,8m,3og,18,4s,2a,2n,12,85,b,n,fu,1y,4,o,i,k,34,1c,1a
36,10,s,w,w,18,3i,3q,60,3ss,1m,o,m,b,v,2b,15,9,fk,k,8,6,m,m,m,k,w
2o,q,w,s,s,14,3o,4a,80,3s2,60,6f,w,y,11,g,3p,4f,g4,1i,s,i,q,22,w,m,16
2o,m,m,k,m,u,24,28,3m,3pk,5e,18,30,29,c,48,6,1s,gi,20,q,y,1y,18,1e,2w,d
2s,q,o,u,s,10,3i,3w,76,3pa,5o,2u,3d,14,7,39,4,3p,g0,1w,k,q,m,1g,2a,a,y
32,y,u,u,y,16,3k,3o,60,3sm,1y,12,k,b,1p,2d,13,3,fa,m,6,8,6,u,m,s,y
2s,s,u,w,w,14,3y,4i,88,3s4,5e,4x,13,1e,1u,2v,2r,3p,fu,1o,q,m,k,m,2m,q,18
2g,m,i,i,q,q,24,2c,46,3tq,4e,1y,18,5t,2o,30,l,3l,g2,22,16,w,2e,s,1a,e,2i
36,10,s,w,w,16,38,3i,5o,3ne,54,22,w,a,3,p,1b,19,gk,e,m,g,a,g,y,e,s
2y,10,s,u,y,16,3i,3o,60,3s4,2a,1c,i,f,17,2v,13,3,f6,o,4,8,8,8,16,s,y
36,10,s,w,w,16,3a,3k,5s,3qi,40,3,m,a,0,19,1b,p,g4,c,y,g,8,e,w,c,q
34,y,s,s,w,14,36,3e,5k,3oo,4y,1e,w,0,9,n,1b,z,gi,c,q,g,a,i,u,c,m
2m,m,m,o,q,y,2s,34,5w,3qe,7i,5,u,e,53,3w,1x,43,g4,1q,10,u,u,2w,c,m,g
2o,o,o,k,m,w,2k,2o,4m,3pq,4q,14,3c,d,1l,1k,u,6,g8,28,k,w,1i,1w,1c,18,5
32,10,q,u,w,12,34,3e,5i,3oc,56,1m,y,1,9,l,19,13,gk,e,m,g,c,i,u,c,m
34,y,q,s,w,12,32,3a,5a,3mc,4g,40,1k,a,1,3,23,17,gy,m,a,g,e,i,w,10,u
2q,m,o,m,m,w,2m,2u,4s,3ps,4g,u,3k,4,1r,u,u,0,g6,28,k,w,1a,22,1e,y,2
2e,q,q,o,q,16,34,3s,76,3wq,c,33,10,a,2r,2e,4j,4r,fi,12,1o,10,w,1o,4,16,u
2y,y,q,u,u,14,3c,3i,5s,3pk,2w,2i,e,6,3,29,1h,h,fy,w,5,u,6,c,m,g,q
2s,o,q,u,u,10,3k,40,7g,3q4,6o,5,2z,1a,4,2j,13,45,g0,1q,q,m,o,1c,26,c,12
2q,o,q,m,o,w,2u,30,5a,3ps,3u,8,44,14,21,r,o,5,g4,2a,i,s,u,2c,1k,m,i
2o,q,m,k,o,s,26,2a,3q,3qa,52,y,2c,w,n,3,h,z,gk,1g,k,o,i,16,1q,16,1m
2o,w,q,s,u,12,38,3e,5k,3rw,38,y,a,7,t,2b,1l,j,f2,u,6,8,a,a,10,o,u
2e,q,k,m,m,u,2c,2k,4e,3oy,46,2m,1m,s,a,j,1l,1t,fq,16,e,c,e,c,u,e,i
2y,10,q,u,w,16,3g,3k,5y,3s8,1q,1e,k,j,n,29,1b,b,fk,q,4,c,k,i,i,k,u
2q,q,m,m,o,s,28,2e,3u,3p2,5y,1c,1y,14,j,r,0,n,gq,1c,i,o,g,14,24,u,1i
30,y,s,u,w,16,3a,3k,5u,3ss,1q,u,j,t,0,11,1f,9,f8,u,6,c,m,o,m,k,q
30,12,u,u,y,18,3k,3o,62,3si,1k,q,e,2,v,2p,13,1,f2,m,8,a,8,c,16,s,y
32,12,u,u,y,18,3i,3q,62,3tc,12,g,a,2,17,27,11,3,f6,m,8,8,8,y,m,q,y
32,w,s,u,12,18,4a,4y,8i,3ok,14,4c,1m,1f,s,6h,1j,x,fu,20,8,m,g,m,2w,1c,18
2u,s,o,o,q,u,2i,2k,44,3n8,5e,2y,1k,1a,16,b,1z,4,h4,y,g,m,e,k,1s,1o,o
2i,m,m,m,o,y,2k,2y,5k,3t6,4w,v,20,1,4l,3y,25,49,fy,1w,y,18,10,2i,c,i,i
2s,o,m,q,s,w,34,3g,66,3p0,4e,3k,g,6,2,3n,y,2n,g2,22,i,u,o,1c,2m,a,o
2g,m,k,k,q,u,2e,2q,50,3ua,3y,8,1k,2v,z,38,1p,3v,fw,20,12,16,1g,1s,q,g,w
2w,s,o,u,y,12,3s,48,7o,3p0,3e,4u,29,l,12,6d,1c,2l,fu,24,a,s,k,m,3e,8,14
2g,u,o,o,s,10,2w,36,56,3qc,4o,u,18,n,n,1h,1z,x,f2,u,a,2,g,g,s,o,q
2y,w,o,q,u,y,2s,30,4s,3mu,60,30,18,c,4,3,2j,h,h0,m,c,m,c,k,w,1c,m
32,10,s,w,w,16,3i,3o,60,3si,1m,y,w,7,17,29,19,9,fk,o,6,4,a,y,k,m,u
36,10,s,w,w,16,3i,3m,60,3ts,10,0,1,f,o,2d,17,d,fi,m,a,6,y,c,q,i,u
2w,u,s,y,y,16,44,4m,86,3r2,4w,h,39,g,1y,4t,j,2p,fo,1s,k,q,m,k,30,o,1a
2k,u,o,q,s,10,2y,38,5a,3r0,46,u,u,j,j,1h,1v,z,f6,u,8,6,g,g,s,m,q
2q,o,o,m,m,w,2e,2k,48,3q4,4s,1t,4g,10,21,3a,1k,g,ga,2c,o,o,u,28,1i,s,m
36,10,u,u,y,16,3i,3o,60,3tq,14,0,0,5,3,29,17,7,fe,m,8,a,e,u,m,m,w
36,10,u,w,w,16,3k,3o,62,3tm,18,0,6,d,1,2b,15,9,fi,k,8,8,s,g,o,k,w
30,u,q,q,u,y,2u,2y,4q,3mu,40,3y,1u,q,k,0,2t,f,h2,w,8,k,c,k,18,1m,s
32,y,s,u,w,18,3i,3m,60,3se,20,14,m,9,1d,2d,17,7,fg,o,6,6,6,10,g,o,w
38,10,u,u,y,16,3i,3o,62,3t4,16,8,q,7,7,2b,13,f,fk,o,a,4,y,c,q,i,w
36,10,u,w,w,16,3g,3o,60,3te,18,1,5,4,m,2f,15,f,fk,o,a,k,k,c,q,i,u
2w,u,q,10,w,16,44,4i,86,3qg,4k,1o,3t,4,1m,5j,a,2t,fq,1w,g,q,m,k,36,i,18
32,10,s,u,w,16,3g,3m,5y,3s0,w,1q,s,3,d,29,19,f,fo,y,1,m,e,c,o,g,u
2g,o,o,m,q,12,2u,3e,6g,3ve,2w,2l,18,b,3h,2w,2x,4r,fo,1i,1e,14,u,2e,8,i,m
2i,q,s,o,s,16,38,3w,7g,3um,3o,51,14,k,39,24,3j,4v,fu,o,1u,u,w,1y,m,m,w
38,10,s,w,y,14,3g,3o,60,3t8,1c,3,7,c,s,2h,13,j,fk,o,c,q,e,e,q,g,u
2m,o,i,k,m,w,2a,2i,4a,3pm,5q,2k,2o,t,2j,3q,13,g,ga,1y,w,12,1u,1m,12,2i,1h
2q,w,s,s,w,14,3c,3i,5q,3s2,2u,12,8,5,z,2r,1d,b,f0,u,4,8,8,6,14,q,y
2i,y,s,s,w,14,3e,3i,5s,3qs,3g,k,8,o,x,3d,13,f,eu,s,6,6,6,4,10,u,10
2w,s,o,q,o,w,2k,2m,48,3ne,4e,34,1m,1m,18,7,1r,0,h4,12,e,m,e,i,20,1g,q
2u,q,o,m,o,u,2e,2g,3w,3nm,52,2o,1o,1m,1m,j,x,6,h4,12,k,m,g,g,2e,1c,o
2e,o,m,i,m,o,20,22,3e,3us,1q,7,2m,1i,8,1n,w,9,fo,20,o,g,o,s,2u,q,s
y,a,c,a,a,e,14,1o,2c,3oe,18,2m,4c,4e,2j,40,4b,1w,8s,3g,y,1k,12,2i,76,1c,2o
w,a,a,8,8,a,u,u,16,3o4,r,2j,1u,3q,2y,1,5i,74,90,3q,3c,22,1g,1y,74,1u,20
w,c,8,a,8,a,y,w,1e,3oo,17,1f,1q,3y,2k,y,3w,8g,90,3o,32,22,1e,2c,4w,2s,1q
w,c,c,a,a,c,18,1q,2i,3p4,1u,3e,5o,2e,2x,3u,7b,2o,8m,3c,8,1s,1a,2e,82,12,2g
u,a,a,8,a,8,w,w,1c,3p4,a,59,s,3i,3o,27,5u,7o,8w,38,36,24,1i,1k,70,2a,1o
s,8,8,a,8,8,s,q,12,3pe,15,41,1n,34,3q,x,5w,7s,8k,30,2e,3m,1k,1k,86,2g,1y
s,a,8,c,a,a,10,1e,20,3pq,22,21,10,3o,5k,b,1v,30,8m,22,1y,1s,14,1q,6e,3e,2e
u,a,a,a,a,c,10,1g,22,3ou,3m,31,20,58,32,1o,2j,2k,8s,2o,1i,28,s,24,64,2u,2i
q,a,8,8,a,a,y,12,1m,3s0,1v,x,2r,1,4i,18,38,7q,8k,m,2y,2u,24,1u,4c,32,40
s,a,8,8,8,8,q,o,y,3nu,19,3l,6,3q,3e,a,5y,7g,8k,3a,3g,2q,1k,1i,6s,2g,1s
s,a,8,c,8,c,10,1e,20,3pu,1s,1n,q,4g,52,p,1x,32,8m,20,1y,1m,16,1o,6m,3e,2e
q,a,8,a,8,a,w,y,1g,3rw,2f,1t,2d,g,4c,b,4e,88,8k,18,2o,34,24,1q,4w,3s,2w
s,a,8,8,a,8,q,o,10,3o6,p,4h,a,38,3i,5,6g,8a,8o,3c,38,2q,1m,1i,7k,2a,1s
s,8,8,a,a,c,10,1a,1w,3qe,s,15,13,2e,6c,r,c,40,8m,1i,2a,22,1c,1i,5s,3s,2o
w,a,a,8,a,a,y,12,1k,3p6,b,2t,q,46,4c,2k,2i,6e,8y,38,2y,1y,1q,1q,3o,2w,36
q,a,8,8,a,a,w,y,1g,3sa,2p,15,2z,2,4e,h,3y,94,8i,12,2q,32,28,1q,4w,48,2g
s,a,8,a,a,a,10,16,1s,3pu,y,2t,d,1c,6m,1m,12,4q,8q,1q,28,2q,1o,1k,4k,36,3e
y,c,c,a,a,g,16,1q,2k,3o2,q,54,52,2s,41,4a,4x,1s,8q,3m,i,1o,1c,2c,7g,y,2u
w,c,c,a,a,e,1a,1w,2m,3p8,3q,56,3m,26,3n,2s,8h,2y,8i,3a,1,1q,1g,20,8q,w,2m
q,a,8,8,a,8,u,s,1a,3rm,33,1h,3n,1i,40,1p,5q,6k,8i,1o,2q,3i,1u,1m,6k,46,1u
u,a,a,a,8,a,y,10,1i,3p4,g,47,m,3s,46,1k,2u,78,8y,32,32,1w,1q,1s,40,3s,2a
s,a,a,8,8,a,s,o,12,3oa,b,4z,16,2m,3g,j,78,8a,8s,3e,3m,28,1m,1e,80,26,1u
s,a,a,8,a,a,y,14,1m,3pu,k,45,5,1y,4q,2q,1y,72,8s,20,22,32,20,1q,3y,38,3a
y,c,c,a,a,g,18,1s,2k,3oi,1k,5q,4a,2k,4b,46,6f,2e,8o,3i,6,1u,1a,2k,7o,w,2o
s,a,a,8,a,a,10,1a,1s,3pk,1w,37,5,1k,74,12,m,44,8q,1y,22,2m,1g,1k,4w,3e,30
u,a,8,a,8,a,y,12,1i,3pg,10,57,8,30,4g,1c,2w,8k,8w,2o,24,2o,1w,20,48,3w,22
w,c,c,a,a,e,16,1u,2i,3pa,2w,42,4u,20,31,38,7z,2u,8k,3a,4,1o,1e,26,8g,10,2i
y,c,c,c,a,e,18,1u,2m,3oa,1e,5u,4e,2a,41,40,5r,26,8o,3k,a,1u,1c,28,7q,w,2e
u,8,a,a,8,a,w,w,1g,3po,8,5h,c,2s,40,17,4k,8s,8u,2w,2m,2g,1q,1s,62,32,1s
u,a,a,8,a,8,u,s,18,3og,1,4p,1o,36,3k,17,62,6u,8w,3i,3g,22,1i,1i,7u,1u,24
w,c,8,a,8,8,u,q,14,3ni,x,1n,2g,3m,24,28,66,7y,90,42,3e,22,1g,2g,6g,1o,20
u,a,8,c,a,a,12,1k,24,3pc,38,1r,3w,3a,2k,1k,4d,2g,8o,2i,1k,1k,10,22,72,2m,2c
u,a,a,c,a,c,14,1q,2e,3pk,2q,2m,4s,2k,6,1c,8b,2q,8g,2i,y,w,1o,1w,8o,1u,2a
u,a,8,8,8,8,q,m,y,3m4,7,3d,2a,38,3c,1s,5g,88,8o,3o,3o,2c,1k,1g,4y,3a,1w
s,a,a,8,a,a,y,12,1k,3pu,o,4n,1,26,4o,26,28,7w,8u,26,20,30,1y,1w,3y,3m,2s
q,a,8,a,8,a,s,s,16,3qc,1h,3r,27,2i,3y,1v,5u,70,8k,2o,2g,3g,1m,1k,7u,2s,1y
u,a,8,8,8,a,q,o,y,3nk,7,4t,1u,2q,36,o,7g,8u,8s,3o,3m,28,1m,1m,7a,22,1w
q,8,8,a,8,a,u,w,1c,3sm,3t,1,49,6,4a,17,4s,7o,8g,12,2w,38,22,1q,5i,4w,1u
u,8,a,a,a,a,10,1a,1w,3p0,36,45,k,2w,5y,1u,5,3k,8u,28,1u,2o,1c,1o,4w,3a,2w
s,8,a,8,a,a,10,14,1q,3qq,j,1r,1b,k,5y,1k,22,5q,8o,12,2o,2u,1s,1m,4g,2y,3w
q,a,8,a,a,a,w,10,1k,3ri,1t,25,1r,k,4k,10,3s,7o,8m,12,2q,32,26,1q,4e,2q,42
w,a,a,8,a,8,y,y,1i,3p4,d,37,u,40,3u,m,38,7s,8y,3c,32,20,1i,1o,4c,3o,24
q,8,8,a,8,c,y,14,1o,3sg,17,c,49,b,4e,1e,2a,6u,8g,m,2s,22,2a,1w,4m,3i,3u
w,a,a,a,8,a,y,y,1i,3p0,z,1x,18,44,3o,1c,36,86,90,3g,30,22,1g,1w,48,38,26
s,a,8,a,a,a,10,18,1s,3po,1e,35,9,1i,6i,1s,y,4k,8s,1s,24,2s,1m,1k,4k,38,3a
w,a,a,c,a,c,10,1g,24,3om,1q,x,1y,5e,2a,32,1v,1u,8w,34,1q,1s,14,1y,62,2c,2q
u,a,c,a,a,c,14,1q,2e,3pg,2y,2w,4k,2c,d,1w,83,2o,8i,2q,q,14,1i,1y,8m,1o,2c
s,8,a,8,8,a,q,s,12,3pk,11,47,1l,2w,3u,1d,60,7i,8m,2w,2i,3g,1m,1k,86,2g,1y
w,a,a,c,a,c,12,1i,26,3om,22,f,26,6g,c,34,35,1y,8u,34,1a,1u,y,2a,6o,20,2m
y,c,a,a,a,e,14,1i,2a,3oa,u,1g,3q,40,3,3y,2f,1c,8u,3g,1m,1g,1c,1w,6i,1q,2w
u,a,8,a,c,a,12,1g,22,3p8,3c,2l,2o,3q,42,w,35,2i,8q,2e,1q,1u,u,22,6k,2y,2c
u,a,a,8,8,a,s,q,14,3o2,3,4d,24,2u,3k,3,6y,7s,8w,3o,3k,22,1k,1g,7w,1u,1y
u,a,a,a,8,a,u,w,1a,3ou,0,4l,14,3m,3k,1f,5a,76,8y,3c,3c,20,1i,1i,6m,2q,1s
u,a,a,c,a,a,12,1g,24,3oq,34,21,1k,6o,1q,26,2z,2c,8s,2u,1e,28,o,28,6e,2i,2k
s,8,a,8,8,8,q,q,y,3ok,11,3z,r,3i,3k,9,60,7q,8k,36,2y,34,1m,1k,7i,2c,1u
u,c,a,c,a,c,16,1o,2e,3pc,2k,22,5g,2c,1l,38,6z,2i,8m,36,i,1i,1c,28,82,1g,2c
s,c,c,a,a,c,18,1s,2k,3pu,3m,4q,3s,1u,e,x,93,3e,8a,2c,m,e,28,1q,9c,1q,16
s,a,a,c,a,a,12,1i,26,3pq,28,19,3o,4m,20,16,4x,2i,8m,26,1u,14,16,20,7c,2u,28
w,a,a,8,a,8,y,y,1e,3p0,b,3d,w,40,3s,e,3i,7c,8y,3e,32,20,1i,1o,4q,3g,26
y,c,a,c,a,e,16,1m,2g,3o6,w,3k,4k,3k,2r,46,47,1o,8s,3i,w,1k,1c,28,78,16,2u
u,a,a,c,a,c,12,1m,28,3p8,32,d,4o,3g,a,2i,5d,2e,8o,2y,10,1m,12,2a,7e,20,2c
w,a,a,8,8,8,q,o,w,3m2,h,2v,3a,2q,2o,30,80,9u,8w,44,3u,20,1k,2a,56,20,1u
w,c,a,a,a,c,12,1c,22,3oq,u,h,1o,38,4w,38,d,1g,8w,36,2i,1i,1g,1e,5e,2k,30
w,c,8,a,8,a,y,w,1e,3oq,17,1h,1m,3y,2o,w,3u,8g,90,3o,32,20,1e,2c,4w,2u,1q
w,a,a,a,a,a,10,1a,1w,3os,1c,2h,s,38,74,2q,g,2a,8y,2u,2c,2a,1s,y,4i,36,34
s,a,a,8,8,8,q,m,y,3mm,d,3v,20,3a,3g,1a,5q,86,8o,3m,3o,2c,1m,1g,5g,34,1w
y,a,a,c,a,c,12,1g,26,3oi,q,8,2s,3y,2c,3m,13,18,8w,3c,24,1g,1c,1o,5y,24,2y
u,a,a,c,a,a,12,1k,26,3oy,3c,1l,32,52,1g,1y,41,2e,8q,2u,1a,1w,s,2a,6w,2c,2e
q,a,8,8,a,8,u,s,18,3r0,27,2p,33,24,42,1v,5m,6o,8i,28,2e,3m,1q,1m,7c,3c,1w
s,8,8,a,8,a,u,u,1c,3qu,1r,3h,1p,1q,44,1v,5e,7c,8m,2c,2e,38,1u,1k,6m,3m,1u
s,8,8,a,8,a,s,q,16,3pw,13,47,1x,2w,3u,1n,5w,76,8k,2w,2g,3g,1m,1k,86,2i,1w
u,a,8,c,a,a,12,1i,22,3p6,3g,2f,34,3u,3e,14,3j,2g,8q,2i,1k,1u,u,22,6q,2s,2e
w,a,8,a,8,a,u,u,18,3oe,h,3j,1o,3k,3i,15,5e,6m,8y,3m,3c,22,1g,1k,7k,1y,22
w,c,c,a,a,g,1a,1y,2q,3p6,52,68,26,1y,3b,1s,9d,3e,8g,34,5,1o,1i,1u,96,u,28
u,a,8,a,a,c,10,1a,1w,3ow,3e,47,u,3k,5g,1s,h,3c,8u,2c,1s,2o,16,1q,52,38,2u
u,a,a,8,a,a,u,u,1a,3os,0,4p,18,3i,3i,1n,5q,74,8w,3e,3e,20,1i,1i,78,28,1u
u,8,a,a,8,a,y,10,1i,3pm,u,5b,8,2q,4e,s,36,8s,8u,2o,22,2u,1u,1y,4i,3s,22
s,a,a,8,a,a,w,y,1i,3qc,0,55,3,20,4e,g,2y,9k,8s,2g,20,2w,1w,1w,4i,46,20
w,c,a,a,a,c,12,1i,26,3ok,1k,d,2s,5c,10,3c,27,1o,8w,38,1q,1i,12,24,6e,22,2q
w,a,a,c,a,c,14,1m,2c,3p2,28,10,4e,46,15,3c,5p,2e,8q,36,o,1s,12,2e,7i,1m,2g
q,a,8,8,8,8,q,o,10,3oi,3n,z,1d,3w,3g,i,52,7a,8i,2q,2s,3o,1m,1m,6a,34,1s
s,a,8,a,8,a,w,10,1i,3qw,t,3v,v,18,4g,m,3o,7s,8o,1w,2a,30,20,1q,4m,3i,30
w,a,a,8,a,c,y,18,1s,3os,1g,35,o,3s,62,38,u,38,8y,2s,2a,2e,1y,18,40,2w,3g
u,c,a,a,a,c,12,1i,28,3ou,2w,15,2i,64,o,2g,3x,2a,8s,2y,16,20,q,2c,6w,26,2g
o,a,8,8,8,a,q,q,12,3r2,4b,0,55,32,40,x,60,60,8c,1i,36,3y,1q,1o,7y,2u,20
w,a,a,a,a,a,10,18,1s,3p4,6,1z,o,3s,64,3s,14,2u,8y,32,2q,20,1u,1a,3q,2q,3m
w,c,8,a,8,a,w,y,1e,3oo,15,1l,1m,3y,2k,s,3w,8c,90,3o,32,22,1e,2e,52,2q,1q
y,c,a,c,8,e,16,1q,2g,3oi,12,46,4k,3i,3d,46,5j,2a,8q,3g,k,1q,16,2i,7i,14,2o
q,a,8,8,8,a,o,q,10,3p0,2l,23,23,3s,3m,3,5k,74,8i,2s,2o,3o,1m,1m,74,2o,1u
w,c,c,a,a,e,16,1q,2g,3oo,16,48,4u,34,3b,46,67,2g,8q,3g,c,1s,18,2i,7o,12,2m
w,a,8,a,8,a,w,w,1c,3oq,d,3j,18,3u,3m,r,4e,7a,8y,3i,38,22,1e,1m,62,30,1u
w,c,a,8,a,a,y,12,1o,3p6,11,1h,18,4o,3u,3c,2u,5o,90,3c,2y,20,1o,1o,3e,2y,32
u,a,c,a,a,c,16,1q,2g,3pe,2y,3q,4a,2a,7,1e,8n,2s,8g,2m,o,12,1o,1u,8w,1m,2a
u,a,8,8,8,a,q,o,w,3mu,5,4l,2e,2y,3a,18,6m,8y,8s,3q,3o,28,1m,1m,5u,2s,1w
o,a,6,a,a,a,w,12,1i,3tc,2j,g,4z,n,3q,u,2y,8q,8c,q,2o,2k,2e,20,4q,3w,3a
s,a,8,a,8,a,y,10,1k,3qs,z,3b,v,18,4o,1c,3a,7i,8o,1m,2e,32,22,1q,4c,30,3k
10,a,a,8,8,8,m,k,u,3ki,6,6,4k,3o,2m,7q,8m,b2,9y,3u,3u,1i,18,28,2u,24,22
18,e,a,a,8,a,m,m,y,3pc,b,1q,44,38,4s,8w,8g,52,cm,34,24,10,10,s,2a,10,18
1w,o,k,m,q,w,2i,2i,3q,3pi,38,4k,l,19,b,15,c,3,fc,14,0,10,2,4,0,3,i
1i,g,c,g,e,i,1k,1o,2g,3ow,m,1g,j,34,34,1q,z,2c,d2,36,1g,y,a,2,8,2,6
1a,e,a,8,8,a,o,k,y,3p4,4,1y,42,3a,50,8y,8i,5e,co,36,22,12,10,s,2a,10,1a
16,e,8,8,8,a,m,i,q,3q8,2j,26,54,46,3o,9s,92,bq,ca,3g,20,10,y,14,2m,14,22
24,s,m,o,u,w,2q,2k,3y,3r4,4g,6,5,1v,q,15,y,c,fw,2,1a,a,2,0,8,6,a
18,e,a,8,a,a,m,m,10,3pm,x,16,3s,2w,4k,8m,8e,46,cg,34,22,12,10,s,2a,u,1c
20,s,k,o,s,w,2m,2i,3w,3r2,4m,12,z,1r,w,17,q,4,fq,c,18,a,4,0,6,0,e
1c,e,a,8,a,a,o,m,12,3og,10,2g,3g,2u,5a,8e,86,4u,cu,36,20,14,10,o,28,10,1c
16,e,a,8,8,8,m,i,q,3pw,23,2q,56,46,3o,9y,96,bg,ce,3e,20,10,12,14,2k,16,30
12,c,8,8,8,8,m,i,o,3nc,1l,10,5q,3u,3i,8s,a0,bm,as,40,2u,18,14,1g,36,18,32
18,e,c,8,a,c,o,o,18,3ow,d,q,3a,1k,4a,8u,68,f,cc,32,1s,1e,10,o,22,14,1e
12,e,8,8,8,8,m,i,q,3oa,2l,34,5k,3w,2y,9q,9w,bw,bi,42,28,16,16,1k,2u,18,38
1o,k,k,i,o,u,2i,2m,3y,3om,1w,f,6c,5,n,1h,x,1n,e6,2o,w,p,1,i,5,l,y
18,e,a,a,a,a,o,m,12,3pa,3,1c,3s,2a,4w,88,8a,2g,cm,32,22,14,10,o,26,u,1c
1a,g,a,a,c,c,u,u,1e,3oo,s,1c,2a,o,4k,6u,64,9,co,32,1w,16,w,i,1g,m,14
18,e,a,8,a,8,o,k,w,3pi,p,1u,4c,3i,4o,94,8m,7a,ck,36,24,y,12,u,2c,10,1a
1s,k,k,k,o,w,2i,2k,3u,3oi,1k,3c,3w,t,1j,t,v,l,es,22,e,h,m,a,9,0,g
1w,o,k,o,s,w,2o,2m,3y,3p0,2g,5y,1d,n,b,1d,g,11,fc,1c,h,w,1,4,3,9,12
1s,o,k,o,q,y,2s,2q,42,3o0,12,5q,1u,8,1p,h,19,1l,f4,1y,h,9,6,6,h,c,16
1a,e,a,a,8,a,o,m,10,3ok,w,2c,3q,2y,58,8m,8c,62,cs,36,22,12,12,o,2a,10,1c
18,e,a,a,8,a,m,m,y,3pc,b,1u,44,3c,4u,8y,8i,5g,cm,36,22,10,12,s,2a,y,1a
1a,e,8,a,8,a,o,m,12,3p8,0,1m,3y,30,4y,8o,8a,30,cm,34,24,12,10,q,28,y,1a
14,e,a,8,8,8,m,i,o,3qk,37,1s,5a,4a,3e,9u,90,c8,c4,3i,22,10,y,18,2o,16,1w
12,e,8,8,8,8,m,i,o,3pm,37,20,5m,44,3a,9q,9o,bw,bq,3s,26,14,10,1e,2w,18,3e
1i,i,e,e,g,k,1o,1u,2q,3p6,g,12,0,3y,1u,1e,13,1k,d8,36,1g,s,4,2,6,3,6
1q,k,m,k,o,w,2o,2q,42,3o8,1y,1c,5i,p,17,z,1r,19,em,2e,i,r,4,g,h,2,s
16,c,a,8,6,a,k,k,o,3ps,2t,3g,5c,46,3a,a4,9g,bq,c6,3m,20,12,12,1a,2q,18,3o
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,k,1q,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1k,a,a,8,8,a,o,m,u,3xg,2k,q,i,w,1y,7e,60,74,be,1m,1o,q,k,m,1k,w,1a
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
14,8,6,6,6,6,g,g,k,40s,22,3a,i,e,c,2u,4e,60,9y,w,a,1u,u,18,24,1c,1e
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
12,8,6,4,6,6,e,g,k,41i,y,4k,g,e,c,1y,3s,5k,9o,12,f,20,u,1c,2a,1c,1i
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,m,1o,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,m,1o,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,m,1o,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
12,8,6,6,6,6,g,e,k,41a,s,4k,i,e,c,1w,48,5q,9o,12,0,1o,y,1c,22,1c,1g
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
14,8,6,6,6,6,g,g,k,40s,22,3a,i,e,c,2u,4e,60,9y,w,a,1u,u,18,24,1c,1e
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
q,a,6,8,8,8,o,m,y,3ym,13,1h,23,5,w,c,3k,4o,7w,k,28,w,u,24,2u,1k,1y
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
18,8,8,6,6,6,i,g,o,3zu,2s,20,g,a,w,4s,5a,68,ae,w,14,1i,u,w,26,1a,1s
q,a,6,8,8,8,o,m,y,3ym,13,1h,23,5,w,c,3k,4o,7w,k,28,w,u,24,2u,1k,1y
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,m,1o,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
1c,a,8,6,8,8,k,i,o,3yy,38,s,g,8,1e,6m,64,76,au,18,1m,18,u,o,1y,14,1q
1g,a,8,8,8,8,m,k,q,3ye,2s,u,e,m,1o,74,62,6y,b2,1c,1q,12,o,o,1s,12,1o
18,8,8,6,6,6,i,g,o,3zu,2s,20,g,a,w,4s,5a,68,ae,w,12,1k,u,w,26,1a,1s
18,8,8,6,6,6,i,i,m,3zu,2s,20,g,a,u,4u,5a,68,ae,y,12,1i,u,y,24,1a,1s
18,8,8,6,6,6,i,i,m,3zu,2s,20,g,a,u,4u,5a,68,ae,y,12,1i,u,y,24,1a,1s
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,c,16,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,a,18,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,a,18,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
u,8,6,6,6,8,i,i,q,41a,19,2m,17,4,e,14,2w,4o,8k,s,k,1k,u,1w,2m,1g,1u
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,a,18,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,a,18,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,6,4,6,6,c,g,i,426,9,5u,g,e,e,10,3c,58,9e,16,p,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,4,e,e,k,42y,8,2y,3a,i,g,1i,2c,48,9c,14,7,16,w,y,2a,16,18
10,8,6,4,6,6,e,e,k,422,9,5s,i,e,c,12,3g,5c,9e,16,n,1u,w,1g,2a,1a,1k
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
10,8,4,6,6,6,c,e,k,428,7,5s,i,e,c,12,38,58,9e,16,r,1w,s,1k,2a,1a,1i
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
u,8,6,6,6,8,i,i,q,41a,19,2m,17,4,c,14,2y,4o,8k,s,k,1k,u,1w,2m,1g,1u
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
s,8,6,8,6,8,k,m,s,406,17,o,1t,1,k,q,34,4i,88,k,1g,18,u,1y,2u,1i,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,a,6,8,8,8,o,m,y,3ym,13,1h,23,5,w,c,3k,4o,7w,k,28,w,u,24,2u,1k,1y
s,8,6,8,6,8,k,m,s,406,17,o,1t,1,k,q,34,4i,88,k,1g,18,u,1y,2u,1i,1y
18,8,8,6,6,6,i,i,m,3zu,2s,20,g,a,u,4u,5a,68,ae,y,12,1i,u,y,24,1a,1s
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
10,8,6,4,6,6,e,e,k,420,b,5s,i,e,c,12,3k,5a,9e,16,l,1s,y,1g,28,1c,1i
s,8,6,8,6,8,k,m,s,406,17,o,1r,3,k,q,34,4i,88,k,1g,18,u,1y,2s,1k,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
10,a,6,6,6,8,g,i,o,410,d,30,b,4,u,2y,40,5w,9e,10,e,1m,u,1g,2c,1c,1m
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
10,a,6,6,6,8,g,i,o,410,d,30,d,6,u,2y,40,5w,9e,10,e,1m,u,1g,2c,1c,1m
s,8,6,8,6,8,k,m,s,408,19,o,1r,3,m,o,34,4i,88,k,1g,18,u,1y,2s,1k,1y
s,8,6,8,6,8,k,m,s,406,17,o,1t,1,k,q,34,4i,88,k,1g,18,u,1y,2u,1i,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
14,8,6,6,6,6,g,e,m,41a,8,4o,g,a,u,2q,44,64,9s,16,9,1u,s,18,2a,18,1i
14,8,6,6,6,6,g,e,m,41a,8,4m,i,a,u,2q,44,64,9s,16,9,1u,s,18,2a,18,1i
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
w,a,6,8,8,8,m,m,w,3z4,r,j,1r,5,14,2m,40,5m,8m,u,22,y,s,1q,2m,1g,1u
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,a,6,8,8,8,o,m,y,3ym,13,1h,23,5,w,c,3k,4o,7w,k,28,w,u,24,2u,1k,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
s,8,6,8,6,8,k,m,s,406,17,o,1r,3,k,q,34,4i,88,k,1g,18,u,1y,2u,1i,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
18,8,8,6,6,6,i,g,o,3zu,2s,20,g,a,w,4s,5a,68,ae,w,14,1i,u,w,26,1a,1s
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
s,8,6,8,6,8,k,m,s,406,17,o,1t,1,k,q,34,4i,88,k,1g,18,u,1y,2u,1i,1y
w,8,6,6,6,6,g,g,m,41y,v,48,b,a,a,18,30,4w,8y,10,d,1y,u,1m,2k,1e,1o
18,8,8,6,6,6,i,g,o,3zu,2s,20,g,a,u,4u,5a,68,ae,w,14,1i,u,w,26,1a,1s
16,8,6,6,6,8,g,g,m,40i,1i,3a,i,8,w,3u,4q,5u,a4,12,e,1o,u,14,26,1a,1u
s,8,6,8,6,8,k,m,s,408,19,o,1r,3,m,o,34,4i,88,k,1g,18,u,1y,2s,1k,1y
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
w,8,6,6,6,6,g,g,m,41y,v,48,b,8,c,16,32,4w,8y,10,d,1y,u,1m,2k,1e,1o
w,8,6,6,6,6,g,g,m,41y,v,48,b,8,c,16,32,4w,8y,10,d,1y,u,1m,2k,1e,1o
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
u,8,6,6,6,8,i,i,q,41a,19,2m,17,4,e,14,2w,4o,8k,s,k,1k,u,1w,2m,1g,1u
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
18,8,8,6,6,6,i,g,o,3zu,2s,20,g,a,w,4s,5a,68,ae,w,12,1k,u,w,26,1a,1s
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
q,8,8,8,6,a,m,o,w,3z6,1f,13,2l,7,q,i,32,4g,7u,m,26,w,u,24,2w,1m,20
12,c,8,8,8,a,s,q,12,3w0,23,t,c,1a,1s,26,4e,78,a0,22,26,1m,1m,1o,3s,1y,48
1g,e,a,a,c,c,10,y,1e,3rk,m,1q,l,5,3o,46,2e,4s,b4,28,1m,14,18,10,1s,1y,1u
16,c,a,8,8,c,s,s,12,3wo,3p,8,6,1k,11,4a,4o,6s,as,1q,2g,1c,1k,1k,3g,1s,42
1k,e,a,c,c,c,12,12,1k,3qs,1w,1g,19,10,40,40,2i,2m,bc,2e,1c,y,18,u,1m,18,2c
1k,i,c,g,e,i,1k,1o,2q,3oi,2o,4,1,1s,k,2o,1s,1m,bc,28,1c,q,m,w,18,s,w
1e,g,a,c,e,e,18,1e,26,3pu,k,2a,v,o,2w,4a,1g,w,b2,1q,1s,14,o,18,1c,s,1a
1k,i,e,g,i,m,1s,22,3a,3nk,2i,7,4,1g,d,1e,26,2k,ba,22,1g,k,k,m,18,m,s
12,c,8,8,8,a,s,u,16,3wy,1r,j,u,2a,26,2a,16,80,9o,2a,2m,1i,1m,1u,40,48,2s
1k,e,a,c,c,c,10,w,1c,3rk,1e,w,1p,1i,2w,32,36,7c,bk,2i,1c,y,18,10,26,20,1g
16,c,8,8,8,c,u,w,1i,3xc,2x,o,10,2u,7,3w,1u,56,ao,1w,2c,1g,1o,1q,44,4y,2g
18,c,8,8,8,c,u,u,18,3x6,2d,q,i,2o,l,4s,1u,7a,au,28,26,1g,1m,1i,3s,3g,32
1g,c,a,a,a,c,w,u,14,3si,0,16,p,f,22,1w,72,7w,be,2k,1g,16,1c,16,3i,14,1m
16,c,8,8,8,c,s,s,16,3yg,3x,c,e,2i,b,4g,3e,68,ao,1u,2k,1e,1k,1o,3m,1s,4o
18,e,a,8,a,e,w,w,1c,3t6,1f,14,1e,x,1s,5i,2w,4e,aw,1c,2a,1a,16,18,24,2a,1q
1e,c,a,a,c,c,y,y,1g,3s8,j,1s,a,v,36,4m,28,4s,ay,1q,24,16,14,18,1u,28,1m
16,e,8,8,a,a,u,s,12,3wi,3d,8,4,1i,x,4a,4u,6s,au,1q,2i,1c,1m,1g,3g,1q,44
12,c,8,a,6,a,u,q,12,3xa,2t,p,e,1q,1g,2q,3w,72,9y,28,2e,1i,1k,1s,3o,1w,4k
18,c,8,8,a,c,s,s,12,3wo,2z,a,4,1w,x,4k,4k,6m,ay,1u,2e,1e,1m,1g,3e,1o,4c
14,e,8,8,a,a,u,s,10,3vk,2z,c,8,1c,z,40,4q,6m,b0,1e,2e,1a,1k,1i,3m,1u,3q
w,a,a,8,8,8,s,q,12,3u8,k,1p,r,s,3o,i,3a,84,8u,2a,1a,2o,1g,1s,4a,4w,2w
12,c,8,a,8,a,y,10,1q,3uc,1j,n,14,1w,s,2m,36,54,9i,2a,2i,1k,1s,1u,4i,52,38
1i,g,c,e,c,i,1c,1g,2c,3p0,24,1a,13,1y,1w,3e,1m,1e,ba,24,1g,w,o,14,18,u,y
1o,q,q,s,u,10,30,34,5a,3l2,a,1t,5,6,1k,42,3o,2a,ay,1y,u,e,a,8,q,y,1m
16,c,a,a,8,c,w,w,1c,3uk,2h,a,1m,e,i,6g,22,4e,aq,18,20,1m,16,1a,28,2c,1q
1u,u,q,s,u,12,30,36,5a,3ju,1,u,j,c,1i,48,2y,2k,b8,20,k,e,a,a,u,10,1o
14,c,8,8,8,a,u,s,16,3ym,3p,4,m,2i,u,3m,1w,76,ai,1q,2m,1e,1m,1s,3u,36,3k
10,c,8,8,8,8,u,s,16,3vs,h,15,o,1u,3q,i,1a,8g,90,2q,2c,1q,1q,1o,4c,4s,2g
10,c,a,8,8,a,s,q,10,3vs,23,x,g,w,28,1o,4q,74,98,2q,1w,1u,1m,1o,3s,20,42
18,c,8,a,8,c,s,s,14,3wi,2n,8,6,1w,x,4m,4i,6k,ay,1y,2c,1e,1k,1g,3g,1m,4e
16,c,a,8,8,c,u,s,12,3v0,2h,8,k,q,p,3w,58,6w,ay,1e,2g,18,1i,1i,3k,1q,2w
18,c,8,8,8,e,w,y,1q,3we,2h,16,10,2s,z,3g,40,3c,as,2c,20,1c,1y,1k,4c,4u,2w
10,c,a,8,8,a,s,q,12,3vo,2j,h,m,m,1q,22,4q,6m,8u,32,1s,1u,1i,1o,3u,1y,3y
1e,e,c,c,e,e,1a,1c,28,3q8,3,2c,n,g,2o,46,1k,q,b0,1k,1y,14,q,16,1c,s,1c
1u,q,o,q,s,y,2u,30,50,3kq,o,e,h,0,k,3o,36,32,be,24,m,e,a,c,q,w,1g
1c,e,8,a,a,c,w,u,16,3si,l,18,k,11,1s,3g,5i,70,b2,1y,20,18,1c,16,2u,1w,1o
1k,e,a,a,c,c,10,u,1a,3ru,12,16,1x,1c,2u,2o,3q,7s,bi,2k,1c,10,18,12,2i,1y,1a
1a,e,8,a,a,c,u,u,14,3sy,x,w,m,x,10,34,6i,7i,b0,1q,2a,18,1g,18,3e,1k,1o
18,c,a,8,a,c,s,s,14,3uw,2b,8,c,c,l,3w,5q,7q,au,1m,2g,1a,1i,1e,3k,1o,1w
1w,w,o,u,u,12,32,38,5e,3jy,j,10,d,e,1s,46,2u,2i,bc,20,i,e,a,8,w,10,1o
1k,e,a,c,c,c,12,10,1g,3r2,1u,1i,1l,18,3u,3u,2k,46,be,2g,1a,10,18,u,1q,1e,28
1g,k,g,k,m,s,2a,2g,42,3o8,b,5,j,k,j,w,2y,4c,aw,20,1g,i,e,e,u,o,u
12,c,a,8,8,a,u,q,14,3vm,31,4,w,m,o,3k,44,64,8y,30,1u,1o,1g,1m,3k,20,3i
1k,g,c,e,e,g,1g,1i,2i,3os,2a,w,n,1y,18,3a,1k,1g,ba,24,1g,u,o,10,18,u,w
1g,e,a,a,a,c,y,s,16,3sw,8,14,1b,8,28,1m,6k,7u,bi,2m,1g,16,16,16,3k,14,1i
1c,c,a,8,a,c,u,s,14,3ua,9,i,i,0,k,3i,5w,62,bc,2m,1k,1g,1g,16,3i,1e,4a
1e,c,a,a,e,e,1g,1y,3k,3s6,i,1m,w,c,6a,43,1n,4,be,2k,1q,20,1g,s,4w,4y,2e
1i,e,8,c,a,c,10,10,1i,3tw,1k,s,x,s,2u,w,38,78,bu,2q,1i,1k,14,10,4e,3q,2u
1e,c,a,8,a,c,w,s,14,3ta,5,u,3,j,18,2s,6o,76,be,2m,1g,1c,1e,14,3m,18,2s
18,e,8,8,8,c,w,u,1c,3w4,1b,w,o,24,7,46,1u,8c,b0,2k,1s,1g,1q,1e,3u,4s,26
1e,e,a,e,e,i,20,2i,46,3pw,1o,3i,2s,18,18,6j,1z,4o,be,2g,1s,y,1o,16,56,4e,1s
1m,e,a,c,c,c,10,10,1e,3t8,1o,c,1n,24,2q,16,36,6a,by,2q,18,10,10,10,2u,1s,1i
1i,c,a,a,e,e,1c,1u,3i,3ss,1g,16,17,1a,6e,1r,p,27,bq,2s,1k,26,1c,q,4o,52,2e
1c,c,a,8,a,c,u,s,14,3ty,d,k,c,3,i,3g,64,6a,bc,2k,1k,1e,1g,18,3i,1c,44
1c,c,a,8,a,c,u,s,16,3ug,2,k,q,2,q,3k,5e,5w,be,2m,1k,1g,1g,16,3m,1i,4a
1i,e,a,a,a,e,w,w,16,3t2,k,12,1r,s,2k,1e,5o,7k,bm,2m,1g,14,14,14,3c,1a,1g
1o,g,i,k,o,u,38,3m,64,3rk,5m,7,0,11,1l,6r,i,4k,by,32,u,w,1a,1a,3m,36,34
1u,g,e,c,e,g,1a,18,1u,3sm,16,1f,1e,26,q,2a,1g,2e,ck,2i,y,y,o,s,1k,1k,24
1c,c,8,a,a,e,10,1a,2i,3u0,f,1e,14,0,m,56,q,o,b8,2g,1u,1y,1q,1g,42,5e,2m
1i,c,8,a,c,c,y,y,1e,3tu,1k,q,5,8,2k,1g,4c,5e,bq,2q,1i,1i,16,y,46,24,44
1q,c,c,c,c,e,14,10,1k,3tg,22,13,d,2g,24,s,2g,4w,c6,2q,16,10,10,10,2u,22,2m
1c,e,8,8,c,c,u,u,1a,3u8,o,o,10,d,1i,30,4q,5y,bg,2q,1i,1i,1e,12,3u,26,3s
1a,c,a,8,8,e,w,y,1s,3v0,l,14,q,18,k,2w,5c,28,b6,2e,1w,1q,1m,1a,4g,4o,2i
1e,c,a,a,a,c,u,u,14,3ta,1,w,7,l,1g,2m,6o,7g,bg,2m,1g,1a,1e,14,3m,16,2c
1q,e,c,c,c,g,12,12,1i,3rs,24,19,e,2q,1s,36,26,3u,c2,2k,16,s,10,u,1q,1i,1y
1q,e,c,c,c,e,12,10,1i,3s0,20,x,3,2m,22,2y,1u,4o,c0,2m,16,u,12,u,1q,1u,1q
4w,14,u,u,w,10,2y,3m,66,3xm,1,b,c,k,e,j,1j,5b,d4,q,m,e,g,k,1u,1i,4u
40,w,s,q,w,12,34,44,6u,3y0,j,f,5,1z,3,6,3t,1h,de,u,o,i,o,m,26,2q,5a
4q,12,s,u,w,10,2w,3g,66,3xo,5,7,4,g,a,9,1l,5l,d6,q,m,g,g,k,1u,1k,4s
4y,14,u,w,u,12,2u,34,68,3xm,0,9,c,o,e,a,e,87,d4,o,m,g,g,k,1o,1m,4k
4o,10,u,u,u,10,2u,3c,64,3xs,7,5,0,g,a,0,19,6f,d6,s,k,g,i,i,1s,1o,4q
3k,q,k,m,m,q,26,2a,42,3zo,b,o,n,4,w,h,19,1h,dg,y,k,o,m,q,22,30,4y
4i,10,s,s,s,10,2w,3c,5y,3xy,7,1,2,c,4,j,25,3d,d6,s,k,i,i,k,1u,2i,4g
3q,s,m,o,m,s,2a,2g,4g,3z6,7,i,h,6,m,b,h,41,de,w,k,m,i,o,22,2g,4w
3g,q,i,m,k,q,1y,20,3e,3y8,8,y,d,9,4,1m,g,19,dw,u,o,k,k,m,1s,22,2u
46,10,u,u,w,12,30,36,56,3v2,n,3,5,5,a,28,32,1o,dw,w,m,i,i,k,1g,12,1m
1y,k,m,o,s,1a,3o,4c,7k,3tq,1y,y,13,1d,5h,37,2u,26,dc,22,k,u,1k,u,4g,1u,3u
4a,y,q,s,u,y,32,46,6a,3y0,f,5,2,l,4,j,5z,d,d8,u,m,i,k,m,22,2g,54
26,i,g,i,g,q,2g,32,4g,3yy,1z,1r,e,5c,m,85,1j,70,dq,1u,1a,12,s,12,5e,3o,1y
44,10,s,s,u,10,2q,2s,4a,3u6,h,6,7,l,4,2m,3o,2m,e2,u,i,i,i,g,1g,10,1c
3g,s,q,q,w,16,3s,4a,8u,3xw,d,1l,b,2d,3,l,1z,27,dm,12,q,i,i,12,2g,4q,4u
4k,10,u,u,u,10,2s,2q,4i,3w6,9,2,8,4,g,24,34,1o,dm,s,m,g,g,k,1g,12,20
4y,12,u,s,u,y,2i,2g,3s,3xi,4,1,q,o,q,1u,2k,3i,dc,o,m,g,e,i,1e,18,20
26,y,s,10,1a,1o,4s,5e,ao,3wk,1h,m,57,51,n,39,5s,1d,e4,s,u,16,s,u,4q,2i,4a
4e,10,s,u,u,12,36,4c,6i,3xo,9,9,1,n,2,r,4z,r,d8,s,o,g,i,o,24,1y,5e
2e,10,w,1a,1g,1u,58,5y,bq,3t6,2i,x,7t,3h,5,1r,5q,15,e2,s,q,y,w,y,4s,2s,4g
4k,12,u,u,u,12,2q,2s,48,3v2,9,a,a,1,k,2a,3c,2u,dq,s,k,g,i,g,1e,10,1i
3k,s,k,m,o,s,24,26,3m,3xg,i,0,4,z,e,1g,20,1f,dy,u,m,k,k,i,1g,16,2w
38,q,m,o,o,w,2e,2g,42,3wg,v,2,1d,p,6,1k,2e,g,e6,y,o,i,k,i,16,u,1i
4w,14,s,u,w,y,2m,2i,40,3wu,2,0,q,i,m,20,30,2u,de,q,m,g,e,k,1c,14,1y
4k,12,s,u,u,12,32,48,6c,3xq,9,7,0,3,4,v,4p,1b,d6,s,m,g,i,m,24,1q,5a
1y,w,o,u,18,1m,4k,4y,9y,3wa,1z,1o,1z,7n,29,5,2c,4,de,14,12,i,1k,q,26,4u,4c
2k,y,u,14,1g,1q,56,64,ba,3sm,2o,1,6v,3n,b,1x,3o,7,eg,k,s,1e,k,w,4w,2m,4e
5g,16,u,s,s,10,2i,2e,3s,3xs,8,e,1q,12,m,1c,2s,3e,d8,q,g,g,e,k,18,14,1u
2a,y,s,10,1a,1o,4u,5m,ag,3wk,v,5,59,4f,n,2p,3y,6,ec,o,u,1c,m,w,4s,2e,4a
1u,e,g,g,m,q,2u,3e,4y,3tq,20,10,34,v,1r,7j,n,78,cs,2s,u,1e,y,1a,4o,3a,24
3m,s,q,o,y,12,3a,44,8e,3y8,5,1b,a,4h,o,1k,2t,41,dk,w,s,k,m,w,2k,4a,4s
3a,q,o,o,10,y,3k,48,8m,3ys,b,1z,5,31,1k,1p,2j,2h,dk,16,s,k,e,1a,2o,4u,46
4y,14,u,u,u,10,2u,3c,62,3xs,1,9,i,m,e,4,x,6h,d4,o,m,g,g,k,1q,1q,4q
2e,s,s,s,1a,1k,4m,5e,9o,3ve,2k,31,13,5v,1l,2p,32,14,eg,k,w,1e,s,u,4q,26,46
3i,s,k,o,q,w,2y,38,66,3z6,7,4,1p,z,h,1n,1f,e,dg,10,o,q,m,u,2o,4i,3o
56,16,u,u,w,10,2s,2y,66,3xo,6,d,q,s,k,g,16,91,d4,o,k,e,g,k,1m,1q,4g
4w,14,u,w,u,12,2q,2o,42,3w2,3,8,m,k,o,1s,34,3o,di,q,k,g,e,i,1c,10,1m
20,e,e,g,e,k,28,2w,3y,3we,1x,r,3k,4c,1m,7x,3r,84,da,2g,1o,o,w,18,5g,3q,1q
4y,14,u,u,u,y,2m,2m,5a,3y2,0,3,c,u,c,10,2i,63,d2,o,m,e,g,i,1g,1q,3m
2a,m,k,q,q,1a,3m,4c,78,3wm,15,4u,3z,r,4t,2x,2s,2e,e0,1i,e,1a,1g,y,4i,1m,46
3s,u,q,o,q,y,2g,2i,3w,3vg,4,7,d,d,9,18,32,3k,e4,s,k,k,e,m,1c,s,18
4q,10,s,s,s,y,2i,2g,40,3xa,2,0,e,i,m,24,2k,1o,de,s,m,g,g,k,1g,18,2g
1o,e,e,e,g,q,2e,2y,4c,3sk,2e,1,4a,40,4r,7z,1r,8c,by,3a,18,1e,u,1a,50,3w,1k
4s,14,u,u,u,12,30,3w,66,3xo,7,9,8,k,c,11,2v,3l,d4,q,m,g,g,k,1y,1e,52
4c,y,u,s,w,12,36,48,6s,3xo,9,f,3,v,7,j,3l,1t,da,s,o,g,k,m,26,26,5e
1s,e,g,g,o,o,2y,3e,54,3sy,3a,14,3e,41,1m,97,11,7s,cm,2u,u,1g,u,18,4m,3k,1w
48,y,q,s,u,12,34,48,6s,3xy,b,d,0,1j,4,6,55,1f,da,u,o,i,k,o,26,2k,5a
48,10,s,s,u,14,36,4a,6w,3xm,5,f,5,1f,9,0,3l,1r,da,u,o,i,i,o,28,2i,5c
5a,14,u,u,u,y,2k,2g,3q,3xa,a,4,1e,y,o,1k,2w,40,da,q,i,g,e,i,1a,14,1q
3y,w,q,o,q,y,2m,2w,5g,3yk,5,2,7,2,r,3,j,5h,dc,s,k,k,i,m,1y,2i,4m
58,14,u,u,s,10,2k,2g,42,3xs,8,1,18,10,k,18,2q,1g,d8,o,k,g,e,i,1c,1c,2c
2m,10,s,14,1g,1s,56,64,be,3sk,2g,g,6j,3x,l,1x,3q,7,ei,e,w,1e,k,w,4y,2k,4e
52,14,u,w,u,10,2w,38,68,3xm,2,b,k,q,g,0,3,7d,d4,o,m,e,g,k,1q,1m,4m
4m,10,u,u,u,12,34,48,6c,3xo,7,9,0,1,6,11,4b,1j,d6,s,m,g,i,m,22,1o,5a
4k,12,u,u,u,12,34,48,66,3xo,d,9,0,e,a,1r,49,1j,d6,s,k,g,i,k,22,1a,5c
50,14,u,u,w,10,2w,3e,68,3xm,0,b,g,m,g,9,p,6f,d4,o,m,g,g,k,1s,1i,4q
2i,12,10,1a,1i,1w,5e,6c,by,3sw,2o,1z,7n,2t,5,z,4m,l,e2,s,o,10,y,y,4w,2u,4e
5g,16,u,s,s,10,2i,2g,40,3y0,6,e,1o,10,k,14,2s,22,d6,q,i,e,e,k,1a,1a,1y
26,i,e,c,g,i,1g,1m,2u,3xm,2f,14,16,9,1g,i,1c,10,ds,1m,y,1e,y,14,42,34,3s
1u,e,e,e,e,m,24,2w,3y,3v0,i,35,4s,5c,9,6v,4f,88,cu,2q,1u,s,u,18,5e,3u,1k
22,g,c,e,e,i,1a,1g,2k,3wg,2d,g,1s,i,i,16,2o,2i,di,22,s,1i,y,12,46,3o,30
1i,e,8,c,c,e,16,1k,36,3t0,1m,10,11,k,3q,3q,1,3l,bs,2u,1k,22,1i,y,46,5a,2m
1w,e,c,c,e,g,18,18,22,3us,w,2j,1g,2e,3,o,3e,4e,cu,2i,10,1c,y,12,42,3e,34
2e,i,c,g,m,k,1w,2k,3w,3zm,a,s,v,4x,5g,29,37,74,dq,1e,16,1e,10,q,56,4k,1o
2q,i,e,g,g,q,1q,22,3m,3zk,20,5,1,a,3f,o,18,3k,dg,1k,w,s,1a,y,4a,4o,2m
2o,o,m,m,o,u,2a,2c,3q,3r8,g,1r,p,2,o,2g,38,24,ea,18,k,m,k,g,w,m,1i
1s,e,c,c,c,g,16,1a,28,3uu,1e,2x,16,2o,e,10,34,4i,ck,2q,16,1e,12,14,4c,4e,2m
2c,i,e,i,g,k,1k,1o,2u,3we,1b,w,r,m,s,2,24,o,dy,1e,12,10,u,10,38,1y,30
22,g,e,e,e,i,1c,1c,2k,3w0,27,e,1i,c,y,m,3s,4,di,20,s,1e,w,10,3y,2c,3o
2c,m,i,k,k,o,1y,1y,30,3ta,1r,0,x,q,s,20,12,3c,e6,1a,s,m,o,k,16,18,1k
24,i,e,c,g,i,1e,1g,2m,3wo,2p,y,1c,1,1c,i,28,1y,do,1s,w,1c,y,12,40,32,3e
1q,e,a,g,c,g,1s,2k,3q,3ts,1k,25,14,6g,3q,5h,6b,70,c4,34,20,1c,i,14,5i,4c,18
1i,c,a,a,c,c,12,18,26,3tg,1g,w,j,8,2y,2c,3k,2y,bs,2s,1k,1u,1c,14,4i,4q,2s
2e,i,c,g,e,m,1i,1u,36,3z6,8,o,b,10,1t,w,14,2a,dq,1g,14,16,10,14,48,3i,3m
2c,o,i,k,m,q,22,22,38,3s8,1n,b,r,e,s,26,2k,32,e8,18,o,k,m,i,y,m,1i
2o,m,k,k,o,s,24,26,3m,3tm,w,1p,p,2,c,10,1o,1a,e4,1c,o,o,m,k,1c,1e,1i
1u,c,e,c,c,i,1c,1u,3e,3ve,u,37,28,22,16,46,1f,3,co,2q,1g,1q,w,14,4k,4y,1y
1s,e,c,c,c,e,16,14,1q,3ty,1w,27,k,2g,12,4,44,5e,ce,2q,16,18,y,12,3w,24,3w
2y,u,o,q,s,y,2q,2s,4c,3p2,p,9,l,4,s,30,2w,32,ei,10,g,e,g,e,k,c,s
36,w,q,s,u,y,2u,2u,4i,3py,1x,b,3,5,y,42,2s,36,eg,y,k,c,e,g,k,i,w
3e,14,y,12,16,1g,4c,4q,7u,3p8,29,17,i,16,y,2s,1k,1y,f8,y,q,k,c,g,1e,1e,2s
3g,16,y,12,18,1i,4g,4y,84,3p4,29,1b,8,18,10,2w,1k,1s,f4,y,m,o,c,g,1e,1g,2u
3k,y,u,w,y,16,38,3c,56,3rc,15,1b,3,g,12,3c,2w,2m,ew,y,k,k,g,g,1e,y,1i
36,w,q,q,s,10,2o,2m,42,3q8,13,e,u,4,m,3o,32,32,fa,12,k,m,g,m,14,w,1m
3k,10,u,w,w,12,2y,2w,4c,3pc,j,j,8,3,4,3e,4g,40,ew,q,i,8,a,u,14,k,12
38,w,q,s,s,10,2m,2k,40,3pi,h,1,n,1,q,2q,4m,4m,es,u,i,c,e,i,y,k,q
2s,s,o,s,q,y,2m,2o,48,3oy,7,1b,p,1,k,3w,3g,36,eg,y,g,g,e,g,m,g,s
2y,q,m,o,o,s,28,28,3k,3pq,e,3,7,10,12,2y,3g,3w,em,12,k,i,i,g,y,i,q
32,w,o,q,q,y,2i,2m,46,3ps,z,1,d,g,16,40,34,2q,ek,10,i,g,i,c,q,g,u
2u,u,q,s,s,10,2u,2u,4e,3no,d,9,f,4,1,2y,38,3q,ek,u,e,c,8,c,q,c,u
3i,12,w,10,12,1c,3w,48,70,3rc,21,1l,3,s,18,2m,22,s,ey,y,m,k,e,g,1e,16,2i
2s,w,s,s,u,12,2u,2w,4g,3mq,d,h,b,6,4,38,3a,46,eg,q,e,a,8,c,s,e,y
38,w,s,q,u,y,2m,2o,48,3q0,15,p,9,a,1c,40,3k,3m,eo,u,k,c,i,e,q,o,u
3q,y,u,u,y,16,3g,3u,6k,3uw,19,13,x,6,a,24,1q,m,ea,10,m,o,i,k,1c,14,1o
3a,y,s,s,w,10,2w,2u,4c,3qg,1f,b,o,c,m,3u,32,3a,f8,y,m,i,e,o,14,w,1e
30,q,m,o,o,w,2e,2g,40,3vc,l,p,1p,b,2,1w,2u,16,e2,16,m,m,k,i,12,o,1a
30,s,m,o,q,w,2m,2o,46,3q2,5,1y,a,o,s,2a,12,4c,fe,1e,m,o,m,o,1g,2g,1e
3g,10,u,w,w,12,2w,2w,4c,3oa,4,n,a,8,8,3s,4e,3k,f2,q,g,6,a,w,10,k,10
3a,y,q,u,s,10,2o,2o,46,3pu,v,n,j,4,14,3i,44,48,es,q,k,c,e,i,u,o,s
3e,12,y,10,14,1e,44,4i,7g,3py,29,1h,m,14,14,2q,1k,1o,f8,y,q,i,c,i,1c,1c,2s
3g,10,s,u,w,10,2u,2s,4a,3ry,17,1l,l,7,q,34,40,4k,eo,q,i,e,c,m,y,q,y
3m,y,u,w,w,14,32,32,4o,3r6,11,v,9,0,u,3a,3k,3c,eu,w,i,g,i,k,18,s,1a
3i,12,w,10,14,1e,42,4k,7o,3sk,2d,1t,n,k,12,2e,1i,u,es,10,m,o,c,g,1a,18,24
40,y,s,u,w,10,2w,2y,4m,3tk,j,0,f,l,a,2u,3i,2g,e8,w,i,i,i,k,1c,10,1c
36,w,o,q,u,y,2m,2o,4c,3pw,1h,3,7,4,1a,4g,32,2o,ek,y,k,c,k,c,m,k,u
3g,14,10,10,16,1i,4c,4u,82,3qm,2f,1p,9,14,10,2q,1k,16,f0,y,m,o,c,g,1c,1c,2w
3g,10,u,u,w,14,2w,2w,4c,3oy,l,l,e,8,e,3u,48,3c,f4,q,i,6,i,o,12,m,12
44,10,s,u,w,12,32,36,5a,3uk,p,3,9,7,8,2k,30,1g,e0,y,k,i,i,m,1e,12,1m
38,y,q,s,u,10,2o,2u,4g,3qi,1z,j,3,1,18,4u,2q,2y,ek,w,i,c,k,e,k,m,y
3m,10,w,w,10,1a,3q,44,6w,3tc,1t,1b,v,c,s,2g,1u,u,ek,10,m,o,g,g,1c,14,1s
3s,y,s,u,y,14,3c,3o,6c,3v8,13,z,p,0,a,20,1y,q,e6,10,m,o,i,k,1e,14,1m
3a,y,s,u,u,12,2s,2q,44,3p8,l,3,9,2,2,24,4w,54,ew,q,g,a,8,q,y,i,s
3u,10,s,u,y,14,36,3e,5g,3tg,x,d,l,5,e,2w,2u,1e,ec,y,k,k,k,i,1e,10,1m
30,s,o,o,q,w,2k,2o,44,3q4,7,1u,c,m,q,2i,1k,3u,fe,1c,m,q,k,o,1g,22,1s
3e,14,y,12,16,1g,4e,4s,7u,3p0,29,11,i,16,u,2u,1k,24,f8,y,s,i,c,i,1e,1e,2s
32,w,q,s,s,y,2m,2k,3u,3om,2,c,2,8,0,2c,44,52,ew,w,e,a,c,m,y,g,q
3m,10,u,y,10,1a,3u,48,76,3tu,1v,1l,z,c,o,2a,1g,w,ek,10,m,o,g,i,1a,14,1s
3i,y,u,w,w,12,2w,2u,48,3oo,2,n,5,2,4,2q,4u,52,ey,m,i,8,8,s,12,k,w
3k,10,w,y,10,18,3m,3u,6a,3s0,1l,1b,f,i,14,2u,2e,18,es,10,k,m,e,i,1c,12,1y
36,w,s,u,s,10,2q,2s,42,3oe,5,6,g,e,9,2q,3y,4q,f4,u,e,8,a,q,u,g,u
38,y,q,s,s,10,2q,2w,4g,3qc,1z,h,1,3,18,4u,2k,2w,ek,w,i,c,i,g,k,k,y
34,w,q,s,s,10,2o,2m,40,3og,3,i,8,a,1,2c,42,4q,f2,w,e,a,c,m,w,g,q
3w,y,q,u,u,12,2s,2u,4c,3tg,f,3,n,j,1,22,3k,3e,ea,w,g,k,c,o,1a,u,12
3g,10,u,y,y,18,3k,3q,60,3qy,1p,1f,i,m,1a,32,2g,1a,f2,y,o,i,e,i,1e,14,2e
3g,10,u,w,w,12,2y,2w,4e,3pc,r,p,c,a,g,3w,42,3c,f4,s,g,a,i,m,14,o,14
32,u,m,q,q,y,2i,2i,3s,3qa,j,16,k,c,m,3c,2s,2m,fa,18,m,o,k,m,18,10,28
3e,16,10,12,16,1i,4k,4w,88,3n6,2h,p,u,1c,10,2u,1y,2k,fe,y,y,g,e,g,1e,1e,2o
2y,q,m,o,o,u,2a,2c,3g,3q4,g,18,o,i,m,2s,2s,34,fc,1c,m,o,m,m,1a,16,1u
32,w,o,s,s,y,2s,2s,4g,3pm,1d,5,f,2,y,3q,2s,2y,eg,10,k,c,g,e,m,e,u
36,w,o,s,s,y,2m,2o,4a,3pw,1f,5,7,6,1a,4e,38,2q,ek,y,k,e,i,c,o,k,u
2w,w,s,s,w,10,2w,2y,4i,3n6,v,j,5,2,8,3a,3e,46,ee,s,e,c,8,a,s,g,y
40,y,q,s,u,10,2o,2q,46,3u4,b,2,f,j,3,1y,3e,3i,e4,u,i,k,e,k,1c,w,16
32,w,o,s,u,12,30,3a,5a,3po,13,1s,i,6,s,2o,o,3u,fg,1c,k,o,i,m,1e,24,1q
3o,10,u,w,y,18,3g,3q,62,3sy,1d,t,v,a,s,2q,2e,16,ek,10,k,m,g,i,1e,10,1q
3e,10,u,w,u,12,2w,2u,46,3om,2,n,5,6,2,2m,4s,56,ey,o,g,8,8,s,10,k,u
3k,10,u,w,w,12,2y,2w,4a,3qi,v,p,0,f,0,2o,48,58,es,q,i,a,a,s,14,m,y
40,y,s,u,w,12,2w,2y,4q,3tm,n,0,b,l,8,30,3e,2a,e8,w,i,k,g,k,1e,10,1e
2u,w,q,u,u,10,2u,2w,4i,3mm,f,2,d,3,6,2w,3e,46,eg,q,c,c,8,c,q,e,w
36,w,o,s,s,y,2i,2m,44,3pu,x,9,h,e,1a,3o,3s,3m,eo,w,i,e,i,e,s,m,s
3g,16,y,12,16,1i,4g,4y,84,3pe,2b,1f,6,18,10,2u,1k,1o,f4,w,o,o,a,i,1c,1g,2u
3m,10,u,u,y,12,30,30,4i,3qm,x,p,3,7,m,3i,40,3g,ew,u,g,c,k,k,16,q,16
3g,10,u,w,w,12,2w,2w,4c,3o8,6,n,a,8,6,3q,4e,3q,f2,o,g,8,8,w,10,k,10
2y,s,m,o,q,u,2g,2g,3u,3q6,6,1o,g,o,m,2g,1w,3o,fe,1c,o,o,m,o,1e,1y,1u
3g,12,y,10,12,1e,40,4g,7a,3qo,27,1n,a,y,16,2q,1u,10,f4,y,m,k,e,g,1c,1a,2u
3g,y,u,w,w,12,2w,2u,48,3oi,2,l,0,6,1,2w,4k,54,f0,m,g,8,8,u,10,i,u
2u,y,q,u,u,12,2u,2y,4g,3mg,j,2,d,1,6,30,3e,4c,ee,q,c,c,8,a,s,e,y
38,y,q,s,u,10,2o,2m,42,3pg,j,1,l,1,k,2i,4u,4o,eu,s,g,c,c,k,y,m,q
3g,10,s,w,u,12,2u,2s,4a,3q2,r,11,l,0,q,34,4m,4o,es,o,i,e,a,m,y,q,u
2u,y,q,u,u,12,2w,2w,4i,3my,t,5,7,3,6,36,3e,46,eg,q,e,a,a,a,q,g,w
3m,10,u,y,y,18,3g,3q,60,3se,1f,11,n,c,10,2u,2k,1e,eo,10,k,m,g,g,1e,10,1s
3g,14,y,12,16,1g,4e,4s,80,3qa,2d,1n,3,18,10,2q,1k,1c,f2,y,m,o,a,g,1c,1e,2u
30,s,m,q,s,y,2q,2y,4o,3po,0,1s,e,e,o,2a,i,4i,fg,1g,m,o,k,o,1g,2m,18
3q,y,s,s,u,10,2u,2u,48,3tk,j,j,n,l,7,1c,3g,4m,ee,u,g,k,a,q,18,u,w
3g,10,u,w,w,12,30,2y,4g,3q6,z,x,6,a,q,3o,3q,3i,f2,u,i,g,g,k,16,s,14
32,w,q,u,w,14,3a,3k,5y,3pi,15,1a,e,8,q,2i,g,3q,fi,1a,o,m,g,m,1e,22,1q
32,s,m,q,q,w,2g,2i,3s,3qc,d,1k,8,i,q,32,2k,2m,fc,18,m,q,k,m,1a,16,2g
32,w,q,s,s,10,2w,2w,4g,3p0,1j,9,f,6,c,3o,30,3m,eg,y,i,c,a,i,k,e,w
32,w,o,q,s,y,2m,2s,4c,3pq,15,1,l,6,14,42,2o,2m,ei,10,k,e,i,c,m,e,u
32,u,o,s,s,10,2w,32,4w,3pu,v,1s,g,e,s,2m,10,3s,fe,1c,m,o,i,m,1g,24,1q
3i,10,u,w,w,12,2w,2u,4a,3pq,h,t,5,7,4,2q,4s,52,eu,o,i,a,a,q,14,m,u
38,y,q,s,s,10,2o,2q,4c,3q6,1p,h,3,4,1a,4m,34,30,em,u,k,c,i,e,o,m,w
30,q,m,m,q,w,2e,2g,40,3v8,n,r,1p,d,4,20,2w,1e,e2,16,m,m,k,i,10,q,16
2y,u,o,q,s,y,2m,2s,4a,3pe,p,3,p,4,12,34,2u,2s,ei,10,i,g,g,c,m,c,s
3i,12,y,10,14,1e,48,4m,7s,3ru,2f,1t,h,q,14,2k,1k,w,ew,y,m,o,c,g,1c,1a,2e
3c,y,s,u,w,10,2s,2q,46,3pi,l,j,n,0,o,2w,4u,4m,eu,q,g,c,a,m,y,m,s
3q,w,s,q,u,y,2o,2o,44,3ue,9,r,h,j,7,1g,3e,4a,e8,u,k,i,c,o,18,s,14
34,u,o,q,q,y,2o,2o,46,3qa,r,1c,k,8,q,3c,26,2q,fc,16,o,m,k,o,1a,1e,2a
34,y,q,u,s,10,2q,2q,42,3oe,5,a,g,a,7,2k,3y,4s,f2,w,e,8,a,q,u,g,s
3i,12,y,10,16,1e,4a,4o,7s,3ri,2h,1t,d,w,12,2m,1k,y,ey,y,m,o,c,g,1a,1c,2m
2s,w,q,q,u,10,2s,2u,4e,3n0,1,9,7,3,2,2o,3c,3y,ei,s,e,a,8,c,q,c,w
32,w,q,u,w,16,3c,3q,64,3pg,15,12,c,8,o,2c,q,3c,fk,1a,q,m,g,i,1e,1u,1w
3i,12,w,12,12,1e,44,4i,7i,3rg,2b,1p,9,u,16,2k,1s,q,ey,y,m,m,e,g,1c,18,2m
3i,16,10,14,18,1k,4m,52,8a,3ng,1z,t,k,18,y,2y,1w,2a,f6,w,q,m,c,i,1g,1i,2w
3a,10,s,s,u,10,2q,2o,48,3pw,x,t,j,4,16,3k,44,48,es,q,i,c,e,i,u,o,u
2w,q,o,o,q,w,2m,2s,4k,3pi,y,1i,i,i,k,1w,k,4g,fk,1g,q,o,m,m,1i,2o,16
3q,y,s,w,y,16,3g,3w,6m,3v2,1b,17,v,4,c,20,1k,q,ea,10,o,m,i,k,1c,14,1o
3a,y,s,u,u,12,2s,2s,48,3pm,17,9,o,c,i,3q,3u,3a,f8,w,i,e,i,k,10,q,10
2y,s,m,q,q,w,2m,2s,4g,3ps,a,1q,g,k,m,24,s,4i,fg,1g,o,o,k,o,1i,2k,1a
2y,u,m,q,q,w,2i,2m,44,3po,d,3,l,g,12,3k,2q,2k,ei,12,k,g,i,c,o,e,q
3c,y,s,s,u,10,2q,2s,4c,3qw,21,z,5,4,1e,4i,3c,3g,eo,q,k,e,g,g,o,o,10
3k,10,u,w,w,14,2w,2y,4g,3po,p,l,6,3,c,3o,4a,3g,ey,s,g,a,e,q,14,o,14
2s,w,s,s,u,12,2u,2w,4g,3m2,4,4,h,3,4,2q,3e,4c,ee,q,c,a,8,c,q,g,w
3e,10,s,u,w,10,2s,2s,4a,3qm,19,17,l,2,16,3k,44,4c,eq,q,i,e,c,k,u,q,w
2y,s,m,q,q,y,2o,2w,4k,3po,8,1s,e,i,m,26,m,4i,fi,1e,o,o,k,o,1g,2m,18
2w,q,m,o,q,w,2m,2q,4e,3pk,10,1g,o,g,k,1u,q,4i,fk,1g,o,q,m,k,1k,2o,16
2w,s,o,q,u,10,30,38,5c,3p4,g,14,i,6,i,22,k,3u,fo,1g,o,o,k,k,1e,26,1i
1y,i,e,c,g,i,1c,1a,20,3q8,u,6,1o,e,1w,4c,2k,32,ci,28,s,q,q,o,18,w,1a
20,g,g,e,g,i,1g,1g,26,3so,v,4,1e,q,y,1y,1a,1u,dc,1y,s,u,m,q,1a,16,28
2a,q,k,m,o,s,2a,2c,3m,3p6,13,m,7,2,3,1o,2u,3s,dk,18,m,e,c,g,w,e,i
2m,y,s,u,u,12,2w,2y,4i,3lw,j,n,5,0,c,3a,2c,4u,dc,w,g,c,8,g,k,u,u
2i,10,u,w,y,12,2y,32,4o,3ki,e,3,f,7,12,36,3q,4c,co,16,i,c,a,i,u,s,14
2k,y,s,u,w,10,2w,2w,4i,3m2,f,x,3,0,c,38,2a,4w,d4,12,g,c,a,e,k,u,u
1u,m,k,m,o,s,2c,2c,3s,3ly,1m,f,e,n,0,8,32,3y,ba,1s,10,i,g,i,y,u,14
2c,s,m,m,o,w,2c,2c,3q,3o0,l,6,a,a,0,1q,3g,40,cw,1e,o,g,e,e,12,g,o
2k,y,u,u,w,12,2y,2y,4i,3l0,k,l,d,c,i,38,2y,4o,cu,16,g,e,a,g,o,u,y
1u,k,i,g,m,o,22,28,3o,3ns,28,f,1k,n,4,18,2y,2s,bo,2e,q,m,g,k,16,m,w
26,m,g,i,k,o,1u,1w,2y,3ro,27,12,f,c,1c,2c,28,3c,dq,1k,o,i,k,i,w,k,m
2g,u,q,q,s,y,2m,2o,44,3mo,c,v,a,2,i,1y,3s,48,cw,18,m,e,e,e,w,k,w
26,m,g,i,k,o,1u,1w,2y,3ro,27,12,j,e,1c,2c,26,3e,ds,1i,o,i,k,i,w,m,m
28,y,s,u,w,14,30,30,4s,3m8,1r,f,9,d,c,2k,3i,4e,c6,1g,k,c,e,c,w,w,18
2g,u,q,o,s,10,2m,2o,48,3ng,d,n,1,3,4,1o,30,4g,dg,12,i,c,c,c,q,k,o
28,o,i,k,k,q,22,24,3c,3q6,1j,w,7,8,q,22,2g,3e,dq,1a,m,g,i,c,w,g,i
20,m,g,g,i,k,1m,1k,2e,3pe,7,1c,k,3,14,3y,2o,3y,ci,1w,o,o,k,s,10,w,y
2o,y,s,u,u,12,2w,2y,4i,3l6,4,d,9,1,8,38,2i,4w,dy,s,c,a,8,c,g,u,s
1y,m,i,k,m,q,28,2e,3w,3o2,k,10,c,b,4,1c,3k,2s,bs,2c,k,m,g,g,18,m,y
26,u,q,s,u,10,2w,36,58,3ma,1f,c,0,6,k,3y,3o,2k,bw,1o,i,g,e,e,w,w,1c
22,u,o,s,s,y,2o,2q,46,3n2,1f,o,11,2,a,1g,2o,4q,by,1u,g,e,e,a,u,k,w
24,w,s,s,w,10,2u,2u,4g,3mq,1l,2,f,j,u,2m,2e,4q,c6,1g,i,e,c,c,s,q,10
2q,y,q,u,w,10,2w,2y,4i,3lc,4,2,d,1,6,34,2w,4o,e8,q,a,c,6,e,g,q,y
2k,y,s,u,w,12,2w,2w,4i,3m0,f,v,1,1,c,38,2c,4u,d8,10,e,c,a,g,k,u,s
28,o,i,k,m,q,22,24,3a,3r2,23,u,t,e,k,2o,2g,3a,dw,1a,m,i,g,g,s,g,k
1o,e,c,c,c,e,12,10,1g,3qu,2a,d,f,1s,2k,3s,38,4y,bo,2g,16,w,18,s,1q,1a,28
2a,s,k,o,o,s,2a,28,3k,3nw,l,i,2,6,8,2g,3g,3u,cs,1g,m,i,g,e,12,k,w
2m,u,s,s,s,10,2s,2u,4e,3m2,s,n,b,7,2,2c,2y,4k,e6,q,e,a,8,c,i,o,s
2i,u,o,q,u,y,2o,2q,4a,3n2,1,p,0,b,2,1u,34,4g,ds,y,g,c,a,c,q,g,q
1q,i,i,e,i,o,1u,22,3a,3nw,36,1b,1w,0,3,1u,2c,2e,bk,2c,12,i,k,o,16,o,w
28,s,i,m,m,s,24,26,3g,3ou,19,18,7,o,1,28,2w,3o,cw,1i,o,g,g,e,14,i,o
1w,s,m,o,s,w,2i,2i,40,3m0,1,c,f,d,6,20,2e,50,bk,1u,o,g,e,c,w,o,w
2o,w,q,u,u,12,2u,2w,4g,3lm,8,f,b,1,6,2w,2q,4q,e2,q,e,a,8,c,g,s,u
1y,s,k,q,q,y,2s,2w,4u,3mk,j,1w,11,2,1,2s,3o,32,bm,28,g,i,c,e,s,u,1a
22,o,i,m,m,s,28,2e,3w,3oe,r,1s,h,6,7,2e,3c,38,c8,1y,k,k,i,g,16,q,u
2i,w,s,u,u,12,2u,2w,4e,3me,h,11,0,1,c,2u,2g,4u,cw,16,i,c,a,g,m,s,u
2g,q,o,m,q,u,2g,2g,3y,3p8,l,15,7,7,m,2s,36,3m,ea,y,k,e,c,g,q,c,m
2m,w,q,q,u,10,2q,2u,4c,3mk,8,t,3,0,8,2i,34,4a,ea,s,e,c,8,c,m,i,u
26,y,u,u,w,14,2w,2y,4k,3mg,1t,9,7,l,k,3g,3a,4m,c8,1e,i,c,e,c,w,s,16
1s,k,i,i,k,q,26,2a,3q,3n8,2q,x,1s,n,0,10,2s,30,bk,2e,u,i,g,i,14,o,w
28,q,k,m,o,s,26,28,3g,3o8,x,10,f,c,2,2s,36,3m,cq,1i,o,g,i,e,12,o,10
2q,y,q,u,w,12,2w,2y,4i,3l0,a,3,f,0,6,3a,2m,4u,e4,o,c,a,8,c,g,u,u
2q,y,q,u,w,12,2w,2w,4k,3l2,a,1,f,1,8,38,2o,4s,e6,o,c,a,6,e,g,s,w
20,q,m,o,q,w,2g,2g,3w,3my,h,i,h,2,4,s,2g,4u,bu,1y,i,i,c,c,u,m,q
2o,y,s,s,w,12,2u,2y,4i,3l6,g,f,b,1,6,34,2m,4u,e4,o,c,a,8,c,g,s,u
1u,g,e,e,c,i,16,16,1q,3q6,24,17,1i,16,1m,4k,2y,3u,bw,2g,10,q,w,s,1e,10,1q
2g,u,o,s,s,y,2m,2m,46,3mg,a,j,4,7,k,20,3w,3y,cu,18,m,g,c,e,y,m,12
24,o,i,i,k,o,1w,1u,2w,3p0,x,1k,5,e,a,3e,2u,3m,cm,1q,m,m,i,m,10,s,y
2k,y,u,u,u,12,2w,2w,4g,3li,e,v,5,a,e,2y,2y,4o,d0,12,i,c,a,i,m,s,w
2g,10,u,w,w,12,2y,30,4k,3ku,6,9,j,6,y,38,2u,4s,co,14,i,c,c,g,q,u,10
28,y,u,u,y,12,2w,2y,4m,3mg,1r,b,b,j,w,3q,36,4m,ca,1c,i,c,c,g,u,s,14
28,q,i,m,m,q,26,28,3g,3pk,1d,14,d,a,1,1y,2o,3g,di,1c,k,g,e,i,u,g,i
1o,g,e,e,c,i,1c,1g,26,3pi,36,l,y,1k,y,3u,22,2g,bk,2e,14,q,q,12,16,y,10
2e,10,s,u,w,12,2w,2w,4g,3lu,0,x,h,c,i,2u,2k,4w,cq,14,i,e,a,g,o,u,w
26,w,q,s,w,12,30,30,4u,3m6,1p,5,d,7,6,1u,3a,44,c0,1m,i,g,e,a,y,u,1c
2c,q,m,m,o,u,2a,2e,3q,3pu,19,d,f,0,g,2m,2q,3o,e4,10,m,e,c,g,s,c,k
2e,q,m,m,o,w,2c,2g,3u,3pg,z,j,b,2,a,2g,2u,3q,e4,10,k,e,c,e,s,c,m
2i,y,s,w,y,12,32,34,4w,3ku,8,l,b,d,e,2o,3o,4c,co,18,i,c,c,g,w,u,18
2c,q,k,m,m,u,2a,2a,3q,3po,17,6,b,0,8,26,2w,3k,dy,14,m,e,e,c,u,c,m
2q,w,q,s,w,10,2u,2u,4g,3ls,m,3,h,5,6,2k,36,4g,ec,o,e,a,8,c,k,k,w
1s,g,e,c,e,i,1a,1c,22,3pq,38,15,1i,1c,u,40,22,3u,bq,2e,10,q,s,y,18,12,w
26,u,q,q,u,12,2y,34,5a,3mq,1f,a,6,1,m,48,3i,28,c6,1i,k,g,c,c,u,w,1e
20,k,g,e,g,k,1i,1i,2c,3pu,7,1c,12,4,1e,48,2u,34,ck,22,o,q,k,q,14,s,u
2c,y,s,u,u,14,32,36,56,3ls,x,v,3,2,k,2k,40,42,cc,1c,m,e,e,e,w,y,14
2c,y,s,u,w,14,32,36,56,3lo,p,13,3,4,m,2g,42,44,ce,1c,m,c,e,g,u,y,16
2s,y,q,u,w,10,2w,2y,4i,3ls,9,4,b,1,8,32,38,4i,ec,q,c,a,8,c,i,o,y
2i,w,s,u,w,12,2y,2y,4q,3l4,k,p,5,f,k,2c,3w,46,cq,18,k,c,c,g,u,u,16
2k,y,s,u,u,12,2w,2w,4i,3m4,h,x,3,0,c,36,2a,4w,d6,10,g,c,a,e,m,s,u
2g,u,q,o,s,10,2m,2q,46,3nc,7,r,2,9,6,1o,30,4g,dq,y,g,c,c,a,q,i,q
2e,s,m,o,q,u,2i,2i,3y,3oc,d,f,3,2,2,1i,2u,4c,dw,10,k,c,c,a,u,e,k
26,o,i,i,k,q,1y,20,36,3qc,1l,16,7,e,q,28,2a,3g,dk,1g,m,g,i,e,y,i,i
2m,w,s,s,w,10,2w,2w,4g,3m4,j,n,5,1,a,30,2e,4s,dk,y,e,a,a,e,i,s,s
1s,o,m,m,o,u,2a,2c,3m,3m0,14,f,0,h,8,1o,2i,4u,ba,1y,u,i,e,e,w,q,u
28,y,s,w,u,12,2w,2w,4i,3my,1j,d,n,3,y,2o,28,4w,cg,1a,g,c,e,e,o,s,10
2m,w,s,u,w,10,2w,2w,4i,3m2,n,n,5,0,a,36,2a,4u,di,w,g,a,a,e,i,s,u
1u,o,m,m,o,u,2g,2g,3w,3lu,10,4,9,f,3,g,3a,42,bc,1w,s,k,g,g,y,s,16
1q,i,g,g,g,m,1m,1q,2m,3na,34,19,1q,k,1,1k,1w,3m,ba,24,12,i,k,g,14,o,q
1y,i,e,e,g,i,1e,1e,26,3qw,a,a,16,s,1s,3i,2k,34,co,2a,s,q,o,m,16,u,s
2c,s,m,o,o,w,2e,2g,3s,3o4,n,2,8,4,0,1g,3c,42,d0,1c,o,e,g,c,12,c,o
20,k,g,g,g,k,1i,1i,2c,3po,d,1c,12,8,1c,44,36,3e,cg,22,o,o,k,s,14,s,w
2k,y,u,u,u,12,2w,2y,4i,3lm,0,x,1,4,c,38,2i,4s,cy,14,g,e,a,g,k,u,w
2a,q,m,m,o,s,26,2a,3g,3o2,p,m,2,c,4,2k,3e,3u,cs,1g,o,g,g,g,12,k,w
1q,m,o,m,u,1c,3s,4c,84,3t4,3a,1b,1g,45,51,j,5,14,c4,2k,s,o,1m,y,26,4g,3s
2a,y,w,18,1g,1s,56,5q,bi,3ta,26,h,6b,59,5,5,4a,15,do,y,s,i,1c,y,3g,42,4g
1s,q,o,q,12,1g,40,4m,8s,3vm,k,9,4,77,33,1f,2w,8,ce,20,10,o,1o,q,3i,3c,40
2g,10,10,1a,1i,1u,5e,66,by,3t2,2y,2p,6b,43,3,15,5a,v,do,10,q,k,1c,10,4u,2u,4g
1m,k,m,k,q,16,38,40,64,3vm,34,2h,1o,35,4d,29,1k,4o,bu,28,16,w,1u,18,4o,1s,44
1q,s,m,o,10,1e,3u,4k,80,3wc,q,t,0,6j,2v,21,2u,24,c2,26,14,s,1q,u,4m,22,40
2i,10,12,1a,1i,1w,5g,6a,c2,3sk,38,2p,6d,3v,5,x,52,r,ds,y,o,m,1c,10,4u,2u,4g
1e,i,e,k,i,q,2q,3a,4m,3ry,4o,4u,5n,1m,f,7b,11,7u,bi,20,1e,1a,1q,1o,54,3i,2a
1y,w,o,y,18,1k,4k,50,a2,3ua,1,2c,41,6p,z,9,2y,1d,cs,1m,12,k,1m,s,2o,4g,48
1g,g,e,i,k,q,2u,3a,4u,3qs,6a,28,2x,n,e,7x,v,8c,bg,2e,18,12,1o,1g,4w,3q,26
1i,k,i,m,m,10,34,3s,5q,3t2,44,w,1p,h,3d,4l,q,5w,bs,2a,12,y,1q,1i,4o,2a,3e
1o,m,m,m,s,18,3m,46,7e,3t6,3u,1b,1a,3x,4l,21,12,2s,by,2k,s,s,1o,12,3a,38,3u
2e,y,s,y,1c,1o,4w,5m,ao,3vo,5,b,41,4r,19,2z,4i,b,ec,m,u,1k,m,w,4o,2g,48
1w,u,o,u,14,1i,4a,4u,9g,3vu,r,1q,2l,6z,1p,2z,54,p,ci,1y,12,o,1o,q,4o,2a,42
2o,12,14,1c,1k,1y,5k,6s,ca,3qm,5a,43,61,2t,j,2,3a,2,dw,y,k,s,1a,12,50,2u,4c
2o,10,16,1c,1k,1y,5k,6s,ca,3qg,5k,4d,5x,2t,h,0,3c,2,dq,12,m,q,1c,10,50,2w,4c
2g,10,10,1a,1i,1u,5e,66,bw,3t2,30,2n,89,23,0,19,50,p,do,12,s,18,q,y,4w,2s,4e
2s,12,u,1c,1i,1y,5g,6g,c4,3se,26,m,8p,2f,x,v,44,d,ec,i,s,1i,k,w,4w,2s,4c
2q,10,16,1c,1k,1y,5m,6u,ca,3qc,5o,4j,5v,2p,f,6,2w,8,ds,12,k,s,1c,10,52,2u,4c
2q,12,14,1e,1k,1y,5m,6w,cc,3qg,5i,4h,5v,2p,f,a,2k,e,du,10,k,s,1c,12,50,32,46
2e,10,y,1a,1g,1s,5a,60,bq,3t6,2m,1f,8l,27,5,1r,5g,x,dw,y,s,1e,i,y,4u,2o,4g
2q,10,10,1c,1k,1w,5i,6o,c4,3si,2s,1r,87,1t,l,9,32,6,e6,s,m,1c,q,10,4y,2s,4e
2s,12,u,1a,1k,1w,5g,6g,c2,3s8,2a,w,8r,2j,z,v,44,d,ee,g,s,1i,k,w,4w,2s,4c
2k,12,y,1a,1i,1u,5e,6c,bw,3sc,38,1d,8l,1z,d,11,46,f,e4,u,o,1i,i,y,4w,2q,4e
1y,u,o,w,16,1i,4c,4y,9g,3v0,7,2c,3t,6l,11,2r,4g,1,cm,1s,14,m,1o,s,4o,2a,44
1c,g,c,g,k,m,2i,30,46,3r6,6a,3c,2b,1t,1u,87,1p,8i,bc,1q,1w,18,1u,1k,5c,42,1w
2m,10,14,1c,1i,1w,5k,6k,c6,3r0,52,43,5z,35,b,b,3s,3,do,12,o,o,1c,10,50,2u,4e
1a,g,c,c,i,m,28,2u,3w,3ri,3u,3y,1i,3p,g,5t,2j,8g,b2,1o,24,18,20,1g,5k,4e,1m
2e,y,u,16,1e,1q,54,5u,ba,3tg,24,3,7t,35,b,2b,4y,x,e6,u,q,1g,k,w,4s,2m,4c
1o,m,m,m,u,18,3g,44,6o,3we,2k,2l,1o,57,3z,1d,20,3q,by,26,16,u,1w,10,4o,1u,44
20,u,o,u,16,1k,4e,4w,9s,3wu,1h,s,25,6n,1z,2x,54,13,di,18,10,w,1i,q,4i,28,42
2g,10,10,1a,1g,1u,5c,64,bu,3t6,2k,1r,8j,21,5,1f,52,r,dw,y,s,1e,i,y,4u,2s,4e
2q,12,12,1e,1k,1w,5m,6s,ca,3rq,44,3r,6n,2b,j,2,30,6,dy,y,m,y,14,10,50,2u,4c
1s,m,i,k,k,s,26,2a,3o,3m8,2i,d,o,9,7,c,3o,3w,b4,1w,10,k,k,i,14,u,10
14,e,a,8,a,e,y,y,1i,3tk,29,k,2c,q,h,3i,3e,44,8i,2c,y,1c,1c,y,24,18,1a
1i,i,e,g,e,m,1m,1q,2s,3ok,20,s,4,1a,9,q,2i,2u,aq,18,1q,u,m,o,1e,u,12
16,i,e,c,g,i,1m,1s,2u,3ri,3p,2i,k,j,1w,z,1q,3g,9g,1u,1i,1a,o,m,1a,y,y
1i,i,e,e,g,k,1k,1m,2k,3om,1i,w,b,1c,6,q,2q,3g,aq,1a,1o,u,m,o,1e,u,14
1c,k,e,i,i,m,1y,22,3e,3os,11,24,1d,28,11,3,1w,3s,ae,18,1q,y,g,i,16,y,10
10,c,a,a,8,c,u,u,1a,3ui,1v,17,1m,1q,n,2y,2s,5w,86,2i,u,y,1o,16,28,1c,1e
w,a,a,8,a,8,u,q,14,3uy,5,2v,1,1i,18,3,3i,6a,86,1u,16,w,1e,1k,2m,1g,1k
q,8,8,8,8,8,o,o,y,3yc,x,1d,2n,b,10,h,3k,4c,7u,k,2e,q,u,22,32,1m,22
1m,m,i,i,k,q,28,2a,3s,3mg,2o,n,14,6,9,5,34,3o,aw,1u,14,i,k,i,16,u,12
s,8,8,a,8,8,q,q,12,3wu,7,2b,23,6,1q,x,4q,4k,7u,i,2e,u,w,22,2u,1o,1w
w,e,a,8,a,c,w,10,1k,3uy,1l,29,18,2a,c,2q,3e,26,7y,26,14,u,1w,16,2a,18,1e
w,a,a,8,a,8,u,s,16,3uw,9,37,2,1q,1k,5,3k,5w,82,22,10,w,1k,1c,2o,1g,1k
w,a,a,8,8,a,u,q,14,3us,4,3f,2,1k,1e,d,3s,5y,84,1s,18,w,1e,1i,2s,1e,1m
s,c,a,8,a,a,u,w,1i,3wm,1t,25,j,1e,w,1i,40,2q,7u,1k,1m,10,18,1o,2e,1g,1e
1m,o,m,m,o,w,2k,2o,4g,3le,1u,17,12,b,4,1m,34,42,aw,1w,10,g,e,g,w,w,1a
u,a,a,8,8,a,u,s,18,3vq,9,2x,n,1k,20,2,42,4e,7w,10,20,w,16,1u,2k,1i,1o
20,u,o,s,w,12,2y,38,5c,3kq,v,16,7,6,1a,44,2u,2q,be,20,i,e,c,a,w,y,1m
1y,u,q,s,u,10,30,36,5a,3kk,l,1a,f,8,14,44,2w,2s,be,20,i,e,a,c,u,10,1k
1e,i,c,e,g,i,1k,1o,2o,3pe,k,2i,l,1a,q,9,2i,28,ae,1c,1i,16,k,q,1g,u,14
10,e,8,a,8,c,u,w,18,3uy,29,11,1m,1k,p,30,2g,5s,88,2i,w,10,1m,14,26,1a,16
1m,m,k,k,m,s,2c,2i,42,3lw,2e,v,16,3,1,e,2y,42,aw,1u,14,g,i,g,12,w,14
u,a,8,8,8,a,q,q,12,3vi,q,35,15,o,1k,15,48,5k,82,12,1u,w,10,1w,2w,1i,1s
y,c,a,8,8,a,u,u,16,3um,13,25,w,1u,i,1e,2y,68,86,2c,u,w,1o,18,2g,1e,1g
1g,i,c,e,g,i,1k,1q,2o,3p6,16,1y,f,1e,m,1,2a,2i,ak,1a,1m,y,m,q,1g,s,16
3m,16,u,w,y,16,38,3a,54,3re,45,j,2,e,c,18,10,c,eq,8,a,6,4,8,i,u,10
2u,k,i,i,o,u,28,22,34,3ws,1s,p,w,2v,35,31,14,1i,ee,u,c,a,a,6,c,g,i
3o,10,u,w,y,14,3a,38,52,3qs,r,r,13,d,10,h,32,1u,eu,i,a,8,6,c,w,a,12
3a,w,q,s,s,y,2q,2o,46,3u2,1d,z,13,h,1,1j,4,2o,em,k,8,6,a,6,e,k,s
2k,g,e,g,g,q,24,1u,2u,3yq,22,1i,h,d,4d,71,12,e,ea,s,g,c,8,a,6,6,q
34,q,k,q,q,w,2i,2c,3i,3v4,d,g,1h,1l,t,21,k,36,ek,m,c,a,6,8,i,m,g
3m,14,u,w,y,14,34,36,4q,3rc,2p,t,b,2,4,15,1g,3o,eo,8,c,8,4,a,u,g,s
34,s,k,o,s,w,2c,2g,3o,3v2,f,8,11,1j,v,1f,b,2o,ek,o,c,a,6,8,i,g,m
34,q,m,o,s,w,2g,2i,3s,3v6,f,0,1h,11,1z,1d,8,1w,eg,q,a,a,6,a,g,c,s
2o,i,g,g,g,s,22,20,30,3xs,24,6,6,i,43,5b,1x,1i,ea,w,g,a,c,c,g,e,e
2u,k,k,g,o,u,28,22,34,3wq,1q,n,w,2f,37,3b,y,1m,ee,u,c,c,a,6,c,g,i
30,o,k,q,o,w,28,28,38,3vy,i,a,37,h,1x,10,a,1k,ei,m,a,8,6,4,8,e,s
2g,g,c,g,e,m,26,1u,2s,3zy,1g,1o,9,i,2d,bb,o,4,e6,o,m,c,8,c,4,2,s
3c,w,s,s,u,10,2q,2s,46,3tm,1p,1h,t,7,3,1f,s,30,eo,g,a,6,8,8,i,i,o
3m,12,s,w,w,14,32,30,4k,3ri,1n,v,n,a,n,p,16,3k,eo,a,a,a,4,a,e,i,y
2q,k,i,k,q,s,26,22,34,3xc,26,r,11,47,25,2,k,s,ec,q,a,8,6,2,4,8,o
30,o,k,o,o,w,28,26,38,3wa,k,c,2r,p,1z,w,1,1s,eg,m,c,8,6,6,8,c,q
2u,k,k,o,o,u,28,26,38,3x0,1o,17,21,2h,j,0,e,1a,e6,o,c,6,6,0,4,4,c
2m,k,e,g,k,s,24,1w,2w,3y0,22,g,4,1p,45,4n,12,s,ec,u,e,a,a,6,a,8,o
2o,k,g,g,g,q,24,20,2y,3xq,24,4,e,g,3v,5f,1l,1k,ec,w,e,a,c,c,i,c,e
2q,m,i,m,o,w,26,26,36,3xe,1y,1d,17,37,1h,4,e,16,e4,o,c,8,6,0,2,4,a
2s,m,k,o,o,u,28,26,38,3x0,1s,1b,2d,2h,n,6,q,y,e8,o,a,8,6,0,4,4,e
2q,k,i,m,o,u,26,24,36,3xi,20,13,1f,39,27,e,m,w,e8,o,a,8,6,2,2,6,c
30,q,k,o,o,w,2c,2c,3o,3vk,4,2,17,p,2r,1l,1,1e,ee,s,c,8,a,8,k,a,q
3m,16,u,y,y,14,36,38,54,3rg,43,15,e,8,g,2i,i,k,eq,a,8,6,4,a,g,y,w
2i,i,c,g,e,o,24,1u,2u,3z6,1y,1s,p,q,35,a1,i,o,e8,s,i,c,8,e,8,a,i
30,o,i,m,o,u,28,2a,3g,3vu,g,4,b,19,1z,1t,r,1k,eg,q,e,c,8,a,k,e,i
3q,14,u,y,10,16,3a,3a,4y,3qe,1f,11,j,2,1,1b,1w,3c,eo,8,c,8,4,a,y,e,w
3a,u,u,q,s,10,2q,2q,48,3u8,1f,2b,1,t,1,19,1,30,em,k,8,4,c,6,a,u,m
3i,10,u,s,y,10,32,30,4m,3s6,1f,7,f,z,i,17,1a,w,ek,c,a,8,6,8,a,4,1i
2i,g,e,e,g,m,24,1w,2u,3zc,1u,1q,n,o,3d,9v,a,k,e8,q,k,e,6,e,a,8,i
3s,18,y,y,10,18,3g,3g,5i,3py,2x,1l,o,i,8,18,1c,a,eq,8,8,8,4,a,k,w,10
3i,y,s,u,u,12,2w,2w,4k,3rs,l,z,j,0,e,1s,4c,38,ew,k,g,a,8,e,s,a,y
36,s,o,q,s,w,2k,2g,3o,3uq,r,7,1f,19,9,1x,q,3i,em,k,c,8,6,6,q,i,g
30,o,k,k,o,u,2c,28,3e,3vq,c,8,1,19,1v,2j,b,2k,ei,q,c,c,8,8,o,g,e
2k,i,c,g,g,o,24,1w,2u,3ys,20,1i,l,q,3j,8p,3,y,ea,s,i,c,8,c,e,a,g
2k,k,e,g,g,q,24,1y,2y,3y2,1w,u,f,q,4v,6t,n,1m,e4,s,i,c,a,e,g,e,e
3c,y,s,u,w,10,2w,30,4k,3t8,1b,p,z,z,8,9,w,4,ea,e,8,6,c,8,a,4,1m
2e,k,g,g,i,m,1s,1y,2u,3yw,k,w,17,e,r,2j,2j,17,ds,i,i,a,8,8,e,8,e
3e,y,s,s,y,10,2y,2y,4k,3t0,1h,d,d,1x,u,x,o,s,ee,e,8,8,a,6,c,a,1e
2u,k,k,i,m,s,24,28,3c,3w8,1g,t,g,p,21,37,1x,10,dy,y,e,a,a,c,k,m,i
2m,i,g,g,g,s,24,1y,30,3xg,22,g,9,s,53,5n,15,1k,dy,s,i,c,c,e,e,i,g
2g,i,e,e,g,o,24,1w,2u,3ym,1e,1q,v,o,3d,97,r,1g,dq,o,o,c,a,e,a,8,o
2i,o,i,m,m,q,22,1y,2s,3ww,2,11,n,13,h,a,1y,2q,dk,g,c,8,8,4,i,c,m
2g,i,e,g,g,k,24,1y,2u,3y6,18,1o,11,o,11,93,23,1e,dk,o,o,c,8,e,a,a,s
2i,o,k,k,m,s,20,1y,2u,3wm,a,19,j,17,d,e,1y,2s,dk,i,c,8,8,4,g,c,k
3g,y,s,u,y,y,30,2y,4k,3sq,1p,7,9,1n,s,15,c,1q,eg,e,8,8,a,6,a,i,16
2g,i,e,g,g,m,1w,22,2u,3yc,12,1q,11,i,1b,5h,4b,i,dm,o,m,c,8,c,c,a,o
2o,k,g,i,i,q,22,24,34,3x8,20,b,c,0,2v,4x,2d,14,e2,y,e,a,c,c,k,g,i
2i,i,e,e,g,q,22,1y,2w,3yy,1s,1k,t,q,4d,8n,2,10,e8,q,k,c,8,c,g,c,c
2m,i,e,g,i,q,24,1y,2y,3xu,20,o,d,q,4t,6f,x,1k,e2,s,i,c,a,e,g,g,g
3q,10,s,u,w,12,2y,2s,4c,3qw,f,5,7,a,c,g,1s,50,ec,g,c,a,6,a,m,s,q
2e,k,e,g,g,m,1w,22,2q,3y0,10,1k,1b,m,b,5n,49,10,dg,q,k,c,8,c,e,a,q
2k,o,m,k,o,q,22,1y,2s,3v8,g,1j,4,1d,5,u,28,32,dg,i,c,6,a,4,i,c,m
2e,g,e,e,g,k,24,1w,2s,404,10,1i,3,2,17,bp,4,1,e0,m,o,c,8,c,6,0,s
2g,i,c,e,g,m,26,1w,2u,3ze,1e,1o,n,i,35,ah,e,u,e0,o,m,e,8,c,a,a,g
2i,i,e,g,g,o,26,1w,2w,3yc,1s,1k,v,s,4t,7z,9,1e,dw,q,k,e,a,c,c,e,i
3e,y,s,s,y,y,2y,2w,4k,3t6,27,5,9,1l,u,z,b,22,ei,g,6,8,a,6,8,s,w
3y,12,u,u,y,12,2y,2w,4e,3pm,9,0,a,c,18,16,34,4q,ee,i,a,c,8,a,w,o,m
2m,o,k,k,o,s,20,1y,2s,3uq,g,1f,e,1b,3,w,28,36,dc,k,c,6,a,4,k,c,k
2g,i,c,g,g,m,22,1y,2u,3yq,18,1o,v,q,2b,8v,1j,o,ds,m,o,c,8,e,a,8,o
2e,k,e,g,g,m,1u,1y,2w,3yq,q,1e,13,g,t,45,2z,1l,ds,k,k,c,6,a,e,8,k
3w,12,u,w,w,12,2y,2w,4g,3ps,b,1,6,a,1c,18,2w,4q,ee,g,a,e,6,a,u,s,k
2w,o,i,k,o,w,26,2a,3g,3w2,u,d,1,1b,2p,1v,19,1e,e8,w,e,a,8,a,i,i,i
2u,m,k,i,m,u,26,2a,3c,3wa,16,l,6,v,2n,29,1v,y,e6,y,c,a,a,a,k,i,k
2q,i,g,i,g,s,22,24,32,3xg,1y,5,e,4,3l,4r,25,1g,e8,y,e,a,c,c,i,g,e
2q,i,i,g,i,s,22,24,34,3x8,1w,9,c,2,3t,43,27,16,e6,y,e,a,c,c,i,g,g
34,q,o,o,s,y,2g,2k,3w,3uy,l,d,17,1j,19,17,d,24,ec,q,a,8,6,a,e,g,u
2e,i,c,g,e,k,24,1w,2u,3zm,12,1i,f,c,17,af,t,1,dw,m,o,c,8,c,8,2,q
3q,12,s,w,y,14,34,30,4k,3qm,z,s,l,11,2,1d,1m,2y,eg,e,8,a,8,a,c,a,18
2i,i,c,g,g,o,24,1w,2w,3yq,1o,1o,v,q,4d,8v,4,16,e0,o,k,e,8,e,a,e,i
3k,10,s,u,10,12,32,34,4o,3rs,v,1,l,1r,s,1f,18,10,ec,a,a,a,8,8,e,2,1i
2o,k,g,g,i,q,24,24,32,3x8,20,7,8,6,3j,5d,1n,1g,e0,w,g,a,c,c,k,g,i
2i,i,e,g,g,q,24,1w,2w,3ya,1s,1g,t,s,4x,7p,b,1g,dw,q,k,e,a,c,c,e,i
32,s,m,q,s,w,2g,2k,3u,3uu,l,1,17,15,13,1b,j,1u,e6,q,c,8,6,c,g,a,10
3i,y,u,s,10,10,30,30,4m,3sc,17,9,f,1l,q,13,y,12,eg,c,8,8,8,8,a,a,1e
2g,k,c,i,e,q,24,1w,2w,3y4,1k,1m,z,s,3x,89,p,1k,do,q,k,e,a,g,a,e,m
2m,o,k,k,o,q,20,1y,2s,3u2,26,1t,u,15,a,i,1g,2w,dm,e,e,6,8,6,i,c,g
2a,m,i,m,k,s,20,1y,2w,3wq,27,k,1h,7,n,8,1e,3a,ca,1e,e,a,6,6,g,c,e
1k,a,8,8,8,a,m,m,w,40y,2a,12,k,w,1m,3m,4e,6c,au,16,1a,10,g,i,18,m,w
2o,m,m,k,m,q,20,1y,2s,3u6,26,1v,u,15,a,g,1i,2u,dm,e,e,6,8,6,i,c,g
2o,o,k,k,m,s,20,1w,2u,3u8,2a,1x,u,15,a,i,1g,2s,dq,i,c,6,8,4,i,c,g
1a,a,6,6,6,8,i,i,o,42k,3i,u,q,m,q,2u,26,62,a0,w,w,s,18,u,14,y,m
2i,o,m,k,o,q,22,1y,2u,3to,16,1f,c,v,a,m,1s,3a,d2,q,g,6,6,6,k,c,e
2k,o,m,k,m,s,20,1y,2u,3te,1s,1n,m,z,e,o,1q,34,d2,q,e,6,8,4,m,c,g
1g,a,8,6,8,a,m,k,s,41s,2q,12,q,q,1a,38,2u,4q,ac,y,s,s,14,g,1a,o,m
1o,c,8,a,a,c,u,u,16,402,26,m,e,10,16,2e,1m,38,b0,10,u,12,e,i,u,k,g
20,i,e,e,g,k,1g,1i,28,3y2,z,c,m,f,0,6,a,1w,by,1i,m,e,c,6,i,c,e
10,8,4,6,6,4,e,e,k,42a,9,5u,i,e,c,10,38,56,9e,14,p,1u,u,1i,2c,18,1k
1m,a,8,8,a,8,q,m,y,40q,2e,16,c,14,1k,3g,2s,64,aw,16,16,y,e,k,12,m,q
20,i,e,e,g,k,1g,1k,28,3xu,15,e,o,h,0,a,c,1u,c2,1s,e,e,c,8,i,a,e
1y,g,e,e,e,k,1e,1i,24,3y2,5,f,y,d,1,a,2,1s,bo,10,10,g,e,8,i,e,e
12,8,6,6,6,6,e,g,k,41q,d,5o,k,g,e,1i,3u,5o,9e,1k,0,16,12,1k,1o,1a,1c
24,m,g,i,k,q,1u,1w,2q,3xm,2n,12,11,d,v,2,w,2s,by,18,m,c,8,6,i,c,g
2e,o,k,k,o,s,22,1y,2u,3v0,f,7,r,t,9,m,1u,3g,ca,18,g,6,8,6,k,c,g
18,8,6,6,6,8,i,g,m,41a,2w,24,m,i,e,2g,48,5w,9s,16,y,u,1e,10,18,18,10
2i,o,k,m,m,s,20,1y,2u,3ti,18,1d,c,t,a,m,1q,3a,de,k,i,4,8,4,i,c,g
2g,o,k,m,m,q,22,1y,2u,3ue,e,11,5,l,4,g,1o,3c,d0,u,g,6,6,6,i,c,e
2e,o,k,k,o,s,22,1y,2u,3uy,f,9,p,v,3,i,1u,3i,cc,18,g,6,8,6,i,e,g
2m,o,k,k,m,q,22,1y,2s,3u0,24,1t,q,13,a,i,1g,2y,dk,g,e,6,8,6,i,c,e
1w,e,e,c,c,g,16,18,1q,3ye,s,b,12,k,i,u,i,1y,bs,1c,y,g,e,a,k,g,e
1i,a,8,8,8,a,m,m,w,41c,2o,16,m,s,1e,3e,2i,58,ag,12,s,u,w,i,18,m,i
2o,o,k,k,m,s,20,1w,2u,3uk,28,1x,u,17,8,g,1g,2q,do,g,c,6,8,6,i,c,g
2g,o,k,m,m,q,22,1y,2u,3ue,a,11,5,l,4,i,1o,3c,d4,q,i,4,8,4,i,c,e
2m,o,k,k,o,q,20,1y,2u,3te,26,1t,u,11,c,o,1m,2y,dm,i,e,4,8,6,i,c,g
1y,g,g,e,g,k,1i,1i,28,3xq,n,3,i,9,5,a,g,20,bg,12,12,e,c,a,k,e,i
1g,c,6,8,8,a,m,m,u,41g,2s,16,s,s,1i,3c,2q,3u,ag,u,o,o,16,g,1a,q,s
12,8,6,6,6,6,g,e,k,43e,g,3c,28,m,k,2a,1g,4k,98,1e,e,m,s,12,1q,y,s
12,8,6,6,4,6,e,g,k,43i,g,2c,3k,m,m,28,1g,4c,9a,1e,c,k,s,u,1w,10,w
1e,c,6,8,8,a,o,m,w,41c,2w,y,i,o,14,30,28,3g,ai,m,u,u,s,q,14,u,q
1o,c,a,a,c,c,w,w,1a,3zc,26,k,0,12,14,24,1o,2i,ay,y,u,y,i,i,u,i,g
1w,g,e,g,e,k,1g,1i,26,3y2,h,j,o,b,1,8,g,20,bc,12,14,g,c,a,k,g,g
28,s,k,m,o,s,24,20,2s,3xi,47,t,1l,7,7,18,2o,3k,ck,y,c,a,6,8,i,e,o
12,8,6,4,6,6,e,g,k,43g,i,2c,3i,m,k,2a,1g,4a,98,1e,c,k,u,u,1w,10,u
12,8,6,6,6,6,g,e,k,43e,g,3c,28,m,k,28,1i,4k,98,1e,e,m,s,12,1q,y,s
12,8,6,6,6,6,g,e,k,43e,g,3c,28,m,k,28,1i,4k,98,1e,e,m,s,12,1q,y,s
14,8,6,6,4,6,g,g,k,43g,e,3o,1u,m,k,2a,1i,4o,98,1e,e,o,s,12,1o,10,s
12,8,6,6,4,6,e,g,k,43i,g,2c,3k,m,m,28,1g,4a,9a,1e,c,k,s,u,1w,10,w
12,8,6,6,6,6,e,g,k,43e,g,38,2e,k,k,2a,1i,4g,98,1e,e,m,s,10,1s,y,s
12,8,6,6,6,6,e,g,k,43g,g,32,2k,m,k,28,1i,4e,98,1e,e,m,s,10,1q,y,s
22,m,i,k,k,s,1y,20,2s,3xi,3n,14,z,l,z,0,1a,3a,bo,1k,e,e,6,8,i,e,g
1e,c,8,6,8,8,m,m,s,42e,26,16,u,s,1e,38,2y,3s,a8,w,o,s,16,e,1e,o,w
1o,c,a,a,a,c,w,u,18,3zc,2e,m,6,10,16,2c,1o,2i,b0,w,s,u,o,i,u,k,i
1k,c,8,a,8,c,q,q,10,3zy,2y,16,8,10,1e,36,22,3o,aw,s,q,q,w,i,10,o,i
2g,i,e,g,g,q,26,1y,2y,3xm,18,1e,t,q,4h,7l,6,16,de,q,o,e,a,c,e,e,o
34,y,q,u,10,y,2s,2y,4i,3se,1p,1a,1v,2n,1a,1i,10,t,do,m,4,c,8,a,e,6,1w
3a,10,u,10,w,10,2y,34,4s,3r6,i,t,43,16,1a,1a,6,11,do,e,a,a,8,c,c,8,1y
2y,s,m,q,w,w,2k,2o,40,3t4,l,g,r,2x,4,1,i,a,di,u,8,a,6,c,k,6,1i
2g,m,i,i,k,w,22,26,38,3va,q,4,j,0,4b,1t,1j,u,d4,w,o,c,c,6,m,g,w
3e,12,u,y,y,10,32,38,4s,3qo,s,19,2d,8,18,k,2,l,dm,e,c,a,a,c,c,a,1w
2g,i,g,g,g,s,26,1y,2y,3x4,12,1e,11,q,4t,6f,2,1c,da,s,o,e,c,a,g,e,q
3a,10,s,10,w,10,2y,34,4q,3r2,k,t,3l,o,1a,1e,c,11,dk,g,c,a,a,a,c,a,1y
3e,10,u,10,w,10,30,38,4q,3qw,m,15,37,u,18,w,2,r,do,c,c,a,a,a,e,8,1y
3g,12,u,y,y,10,32,38,4s,3qi,12,1d,1v,9,18,a,0,b,dq,a,c,c,8,c,e,8,1w
3c,y,u,10,w,10,30,34,4s,3r8,q,13,3x,10,1c,14,6,z,dq,c,a,a,a,a,c,a,1w
2y,u,q,s,u,y,2m,2s,48,3sc,d,8,1t,z,9,w,i,b,dg,s,8,8,8,c,i,8,1e
2c,k,g,i,i,q,22,22,34,3wq,f,y,l,6,37,41,1f,o,cu,10,s,e,c,c,i,e,u
3g,10,u,10,w,12,30,38,4s,3qk,u,15,2l,e,18,i,0,j,dq,a,c,a,a,c,c,a,1w
36,10,q,10,w,10,2u,32,4o,3rm,9,6,45,c,1a,1u,i,17,dk,i,a,a,8,a,e,8,20
32,w,q,u,w,10,2q,2w,4e,3s6,9,0,2j,v,1,1o,u,l,dk,m,8,a,8,a,g,8,1s
2a,k,e,g,i,q,24,20,30,3y6,x,16,f,1,41,5x,k,6,cw,y,q,g,c,8,g,a,u
38,10,s,y,w,10,2w,32,4m,3r8,e,f,37,2,y,1i,c,t,dg,k,a,c,a,a,e,8,1y
38,y,s,10,w,10,2w,32,4o,3rg,3,1,3x,c,1a,1q,g,15,dk,i,a,a,8,a,e,8,20
2i,k,e,i,g,u,24,20,30,3ws,1g,u,n,o,4z,5d,v,1m,de,u,k,e,c,a,g,k,m
2q,q,m,m,q,y,2c,2g,3q,3te,y,z,0,1t,1r,n,f,k,dc,y,c,8,a,8,o,a,10
3g,10,u,10,w,12,30,38,4s,3qm,s,17,27,2,18,i,0,h,dm,e,c,a,a,c,c,a,1w
2g,m,g,k,k,s,24,26,36,3ve,i,a,l,5,3j,2d,1n,14,d2,w,q,c,c,8,m,e,w
3e,12,u,y,y,10,32,36,4s,3qo,s,17,2l,e,18,m,0,j,do,c,c,a,a,c,c,a,1w
2g,i,e,i,g,q,26,1y,2y,3xe,12,1c,v,q,4l,73,6,18,dc,q,q,c,c,a,g,e,q
3g,10,u,y,y,12,30,38,4s,3qm,u,19,23,1,18,g,2,h,dm,e,c,a,a,c,e,8,1w
2k,m,i,k,m,s,24,2a,3a,3u6,16,b,7,d,21,23,2f,10,d4,w,m,e,a,8,q,e,y
3i,10,u,w,10,12,32,36,4q,3qe,22,1n,h,21,o,0,8,a,do,e,c,c,a,a,g,8,1q
2u,q,o,o,s,w,2i,2k,3w,3sm,e,j,5,1r,h,r,b,m,dc,w,c,8,8,c,k,c,w
36,y,s,w,w,10,2u,30,4k,3re,4,0,3b,4,i,1u,g,x,dg,m,a,a,a,a,e,8,1y
26,i,e,g,g,q,22,1y,2y,3yu,1f,14,g,v,3l,6l,12,p,ci,1a,o,k,c,8,g,4,y
2s,o,k,o,o,w,2a,2e,3m,3ua,y,v,1b,2,1n,1t,1l,14,di,y,c,a,6,c,m,g,w
3i,10,u,y,y,12,32,38,4s,3qg,1a,1j,1h,n,18,6,0,7,do,c,e,a,a,a,e,a,1u
2y,u,o,q,u,y,2k,2q,42,3ri,8,8,9,19,v,3,9,g,d6,w,c,a,a,c,k,c,18
3e,10,u,10,w,10,32,36,4s,3qo,s,19,2t,k,1a,s,4,r,de,i,c,a,a,e,c,a,1w
2m,o,k,k,o,u,28,2a,3g,3t8,1e,n,3,3,25,21,1l,18,d4,y,i,e,a,a,o,e,10
3c,10,u,y,y,10,2y,36,4q,3qy,s,17,33,i,1a,14,6,t,di,g,c,a,c,a,e,8,1y
3g,12,u,y,10,10,32,38,4s,3qg,18,1f,1h,r,18,4,4,7,dk,e,c,c,a,c,e,a,1s
3c,10,u,10,w,10,2y,36,4s,3qs,8,r,3d,10,1a,16,4,v,da,k,c,a,c,c,e,8,1y
3c,10,u,10,w,10,30,36,4s,3qu,u,19,2z,i,1a,10,4,t,di,g,c,a,a,c,e,8,1y
2q,o,m,m,o,u,2c,2c,3k,3sg,1i,z,g,z,z,2f,v,1c,d4,y,i,a,c,a,o,e,u
3a,y,u,y,y,10,2w,34,4q,3qw,u,z,2z,2,1a,1c,e,x,dc,m,a,a,c,a,e,a,1y
2k,m,k,k,k,u,26,2a,3a,3tw,16,b,7,0,2f,23,23,14,d4,w,m,c,c,8,o,g,y
2k,o,i,k,m,s,26,2a,3a,3u0,14,d,3,d,25,29,1v,18,d4,w,m,e,a,8,o,g,y
36,y,s,y,w,10,2u,32,4k,3qq,o,5,37,2,s,1m,c,t,d8,o,a,a,c,a,g,a,1w
2w,s,o,q,s,y,2k,2m,3y,3rk,i,0,1,17,11,j,f,q,d6,w,c,a,a,c,m,c,16
3a,12,u,10,w,10,30,36,4s,3qm,e,x,3b,14,1a,12,4,x,da,i,c,a,c,c,e,a,1w
30,u,o,s,u,10,2m,2s,46,3r4,e,k,19,n,15,o,7,6,d6,w,8,c,8,c,k,c,1i
3a,y,u,y,w,10,2y,34,4o,3qs,q,r,3b,e,1a,1g,c,x,da,m,a,c,a,c,e,8,20
30,w,s,u,w,10,2q,2y,4e,3q8,w,3,1z,x,12,16,g,n,d4,s,8,a,a,c,i,a,1o
2a,k,e,g,i,k,1u,22,2u,3yi,3,10,f,6,h,4f,4f,d,d4,u,m,a,8,c,g,8,k
2a,k,e,g,g,k,1y,20,2u,3yo,7,14,7,4,n,6f,2x,d,d4,s,o,c,8,c,e,6,o
28,k,e,e,g,m,22,1y,2u,3z8,11,14,a,b,l,8z,l,d,cu,10,o,c,a,a,c,6,o
2c,k,g,i,i,o,1s,20,2u,3y2,p,m,z,6,d,j,3b,e,d2,u,g,a,8,8,k,a,g
2i,o,m,k,o,s,22,1y,2s,3uo,5,19,7,v,3,14,2e,34,cw,s,e,8,8,6,k,e,m
22,m,g,k,k,o,1w,1w,2q,3x8,2v,1c,13,b,v,4,y,2u,bq,1e,k,c,8,8,k,c,g
2g,q,k,m,o,s,22,1y,2s,3ui,13,r,0,t,1,14,2c,3k,cq,u,e,6,a,4,m,e,m
2a,k,g,i,k,m,1u,1y,2w,3yo,1p,o,z,4,b,1h,1x,n,cw,y,g,a,8,8,i,a,i
2e,k,e,i,i,m,1q,24,2q,3xu,k,10,1b,g,f,1j,53,s,dc,o,k,a,8,a,i,8,m
2a,q,i,m,o,s,22,1y,2w,3vk,1v,m,13,j,r,o,22,3k,c4,1a,c,8,8,8,k,e,i
2e,o,k,m,m,s,22,1y,2u,3us,5,b,n,x,5,m,1u,3g,c8,18,g,8,8,4,m,c,i
2g,q,m,k,o,s,20,1y,2u,3te,g,r,a,z,c,s,24,3a,co,u,e,6,a,6,m,g,i
2c,s,k,m,o,s,22,20,2s,3w2,2d,11,t,j,1,18,2i,3c,co,u,g,8,8,6,k,e,m
2a,q,m,m,o,s,24,1y,2u,3ww,3r,p,17,b,5,16,2m,3m,ck,y,e,8,6,8,i,e,o
26,o,i,k,m,q,20,1y,2u,3wm,2p,1c,1l,7,11,c,1g,38,bw,1e,e,c,6,a,i,c,i
2k,o,m,k,o,q,22,1w,2u,3sy,16,11,o,v,e,q,1y,34,cs,u,c,6,c,4,m,g,i
2i,q,k,m,m,s,20,1y,2u,3t2,1i,17,c,11,i,s,1w,36,cm,w,e,8,a,4,o,e,i
26,o,i,k,m,s,20,1y,2u,3wi,2l,1a,1n,5,13,g,1g,3c,by,1c,e,c,6,a,i,c,i
1y,g,e,g,e,k,1g,1i,28,3xw,f,3,m,9,3,8,e,1w,bk,12,y,g,e,8,k,e,g
2e,m,g,k,k,o,1u,20,2s,3xg,8,e,1b,2,l,0,25,1s,de,k,g,a,6,8,i,c,k
2a,q,k,o,o,s,22,1y,2u,3vs,2t,i,x,n,7,m,2g,3o,ce,10,e,8,8,6,m,e,k
2i,o,m,k,o,s,20,1y,2s,3vg,2,1d,b,11,7,10,2a,2y,d8,k,e,8,8,6,i,e,m
2k,o,m,k,o,s,20,1y,2s,3ts,i,z,e,11,1,y,26,38,cu,w,c,6,a,6,k,e,k
24,i,g,e,g,m,22,1y,2u,3z2,1f,12,m,19,1,8z,p,s,c8,1m,i,g,a,8,a,6,e
2i,q,k,m,m,s,20,1y,2u,3t8,1q,1h,c,13,e,u,1u,38,co,u,g,6,a,6,m,e,g
2e,o,k,k,m,s,20,1w,2u,3wg,15,l,11,9,b,m,26,2k,cw,s,e,a,8,6,i,e,m
2c,m,g,k,i,o,1u,20,2s,3xu,t,i,15,6,h,9,1l,18,d4,q,g,a,6,8,k,c,i
28,o,i,k,m,s,22,1y,2u,3wa,2d,14,1j,b,z,g,1o,3e,by,1e,c,c,6,8,k,e,g
26,k,e,e,g,k,28,1w,2u,3za,15,18,s,p,17,az,1e,k,co,16,o,e,a,a,a,4,m
2g,k,g,k,k,o,1u,22,2s,3xm,g,o,1j,0,r,3,2p,1w,di,k,g,a,8,6,g,c,m
2e,k,e,i,i,m,1q,24,2q,3xu,s,16,1j,i,j,1h,5f,w,dg,m,k,c,6,a,g,a,o
2c,q,k,m,o,s,22,20,2s,3uu,1b,4,j,x,3,q,2a,3i,cg,y,e,8,8,6,m,g,i
2e,k,g,i,i,m,1q,24,2s,3xs,4,o,17,a,h,4,4p,w,da,o,i,a,6,a,i,a,i
2k,o,m,k,o,q,22,1y,2s,3te,k,x,k,x,4,w,24,38,cs,u,e,6,a,6,m,e,k
26,i,e,e,g,m,28,1w,2w,3z8,1b,18,w,11,1r,a5,1g,8,cm,18,m,g,c,a,8,6,s
2a,k,e,g,i,k,1q,24,2w,3yk,j,q,j,2,3,1x,5z,11,d4,u,k,a,8,a,i,8,e
2m,o,k,m,m,q,20,1y,2u,3t2,1o,15,u,z,e,o,1q,2y,cu,u,c,8,a,4,m,e,i
3g,10,s,w,y,14,30,36,4o,3re,m,p,1n,t,1,6,c,6,dw,c,a,a,a,a,e,8,1o
2u,m,k,k,m,y,26,2a,3g,3vm,18,11,c,r,43,1j,17,18,du,w,c,c,a,6,m,m,m
2o,m,k,i,k,w,24,2a,3a,3vc,1o,r,0,f,3d,1r,2p,18,dk,w,g,a,c,6,o,m,m
2i,k,e,g,i,s,24,1y,30,3x0,1k,10,r,w,5b,5v,l,1s,dg,s,m,e,a,c,g,k,k
2i,k,e,g,g,s,24,1y,2y,3xi,1s,14,r,q,4n,6f,15,1m,dm,s,k,e,c,c,e,g,k
2i,i,e,i,g,q,24,1y,2y,3xm,1o,1e,x,s,4n,6x,r,1k,dk,s,m,c,c,e,c,e,m
2u,o,k,o,o,u,2c,2g,3m,3um,w,v,1b,2,1x,1j,1t,w,dm,w,e,6,a,a,k,g,y
2s,o,k,m,m,w,28,2e,3i,3uu,16,x,r,5,2f,1f,29,10,dm,w,e,8,a,a,k,k,s
2m,k,i,i,i,u,24,24,36,3w6,1w,9,0,2,4f,3f,21,1o,dm,u,i,c,c,8,k,m,i
2y,o,o,m,q,y,2e,2g,3s,3uy,6,19,1,l,2x,13,p,w,dw,u,c,6,a,8,i,i,w
2m,m,g,i,k,u,24,24,38,3vy,1u,d,2,1,4d,2x,27,1k,dk,w,g,c,c,8,k,o,k
2o,m,i,k,k,w,24,2a,3a,3va,1m,l,b,5,39,1z,2n,18,di,y,e,c,a,8,o,k,o
2o,k,g,i,k,u,24,26,36,3w2,1w,f,4,3,4b,33,25,1m,dm,w,g,c,c,8,k,m,k
32,u,o,s,u,y,2o,2q,48,3ts,1d,g,27,n,2,b,0,b,du,o,a,4,8,e,c,a,1i
2i,i,g,g,g,s,24,1y,30,3x8,1k,14,r,u,51,6b,l,1o,dg,u,k,e,c,c,e,g,m
2m,i,g,g,i,q,24,22,30,3x4,1w,i,b,g,45,55,25,1k,ds,s,i,c,c,c,e,m,i
3c,10,u,y,w,10,30,34,4q,3sk,n,j,3d,4,14,q,c,n,e4,8,a,8,8,a,c,6,1q
3e,10,s,10,y,10,2y,36,4s,3s2,a,x,3r,6,1a,s,8,r,dy,a,a,8,8,c,c,6,1u
2q,m,k,k,m,w,24,2c,3e,3vg,1o,13,6,n,3h,1p,27,14,do,w,e,a,c,6,m,m,o
2o,k,i,i,k,w,22,28,38,3vk,1s,n,a,h,3p,1x,2x,1a,di,y,g,c,a,8,m,o,m
3c,10,q,y,w,10,2y,32,4o,3t2,1j,1,31,7,1c,o,q,v,e6,a,a,6,a,8,a,6,1u
2k,k,e,g,g,s,24,1y,30,3xk,1w,s,h,o,4r,5x,1d,1i,du,s,i,c,c,c,e,k,g
2y,o,m,m,o,10,2c,2g,3o,3v2,c,19,2,l,39,13,n,10,dw,u,c,8,a,8,i,k,s
2k,i,e,g,g,s,24,1y,2y,3xs,1w,10,l,s,51,6h,x,1i,du,s,i,e,a,e,c,i,i
30,q,m,o,o,10,2e,2i,3s,3uy,5,1f,a,h,37,v,b,18,e0,s,a,8,a,8,i,i,u
34,u,s,q,s,10,2o,2s,48,3u6,1d,1v,6,d,1h,3,5,k,e4,m,8,6,a,8,e,8,1c
3e,10,s,w,10,y,30,34,4o,3sq,15,d,1v,17,1a,4,q,f,e8,a,a,6,a,a,a,4,1o
2q,m,k,k,m,q,20,1y,2u,3v6,24,1t,s,19,6,8,1c,2m,ds,i,c,6,8,6,g,a,g
2o,k,k,i,m,q,1y,20,30,3w4,1o,13,c,x,3,r,b,e,dw,m,a,8,a,2,e,8,e
2q,m,m,i,o,q,22,1y,2s,3v4,24,1z,o,1b,7,a,1s,2u,ds,g,a,6,a,2,g,c,i
2o,m,m,k,m,q,22,1y,2s,3tm,1w,1j,u,11,a,k,1q,2w,d6,o,c,6,a,4,k,e,i
2g,k,e,i,g,m,1q,1y,2s,3yw,m,12,19,i,15,19,2p,1b,dq,k,g,a,8,a,e,8,g
2i,k,e,g,g,m,1w,1y,2u,3yo,1m,16,p,s,z,5z,33,b,dw,m,g,a,a,a,c,6,e
2s,m,k,k,m,s,20,1y,2s,3va,1s,1l,k,11,1,8,1k,2q,ds,i,a,8,8,4,g,c,g
2u,m,k,i,o,q,26,22,34,3wc,1e,1f,i,13,x,1r,w,1,e4,m,a,a,6,6,a,6,g
2s,m,k,k,m,q,26,26,34,3w4,1m,1f,s,13,r,1r,r,a,e4,m,c,8,6,6,6,6,e
2k,o,k,m,m,s,20,1y,2u,3t8,1u,1l,c,x,e,q,1u,36,cs,u,g,6,a,4,k,e,g
2u,m,k,i,o,s,28,24,38,3wg,1k,z,c,1x,1l,15,f,k,e8,o,c,a,6,4,2,6,a
2m,m,m,k,m,s,22,1y,2s,3v2,14,1p,a,1h,7,o,20,32,di,i,e,6,a,2,g,c,k
2e,i,c,e,g,k,22,1w,2s,3zy,1g,1m,b,8,3,b9,1l,5,e0,m,k,c,8,c,8,2,o
2q,m,m,i,o,q,20,1y,2s,3uq,26,1x,s,15,8,a,1k,2o,do,g,c,6,a,4,i,c,g
2s,m,k,k,m,q,26,24,34,3w0,1o,1h,q,11,t,1h,b,9,e2,m,c,8,8,4,6,8,e
2k,o,i,m,m,q,22,1y,2s,3vy,u,1l,0,1f,d,e,1y,2s,dm,k,c,6,a,2,g,a,k
2o,m,k,k,o,q,22,1y,2s,3vc,1u,1v,i,1p,b,e,1w,2w,ds,g,c,6,8,2,g,a,k
2m,o,k,k,o,q,20,1y,2u,3tg,1y,1n,q,11,e,m,1q,30,d2,q,e,6,a,2,m,c,i
2o,o,k,k,m,s,20,1y,2s,3uo,26,1z,u,17,2,a,1m,2s,do,g,a,8,8,4,i,c,g
2q,m,k,k,m,q,20,1y,2s,3uw,26,1v,u,19,8,a,1e,2o,ds,i,a,8,8,4,i,a,g
2s,m,m,k,m,s,22,1y,30,3w0,1k,1r,6,1d,f,a,1y,1c,dy,k,a,6,8,4,c,a,i
2k,m,k,m,m,q,22,1y,2u,3wa,c,1n,9,1d,d,o,20,2q,dk,k,c,8,a,2,e,c,k
2e,i,c,g,e,k,20,1y,2s,400,12,1k,f,8,7,a3,25,d,dy,m,k,c,8,c,8,4,k
2s,m,k,k,m,s,20,1y,2s,3vk,1s,1j,k,15,5,6,1k,2q,dw,i,c,8,8,4,e,c,g
2m,o,k,k,o,q,20,1y,2u,3tc,1u,1h,q,z,e,o,1o,30,cy,s,c,8,a,4,k,e,i
2m,k,g,i,i,q,24,20,30,3xo,1s,2,3,5,29,4f,13,i,dy,o,c,8,c,2,8,8,8
2s,o,k,k,o,q,22,1y,2u,3w0,1i,1b,8,1b,l,c,1o,2o,dy,m,a,8,8,6,c,a,i
2m,m,m,k,o,q,22,1y,2s,3uw,10,1l,c,1f,7,q,22,32,dg,i,e,6,a,2,i,c,k
2s,m,k,k,o,q,22,1w,2s,3vg,1q,1j,g,17,9,a,1s,2u,du,i,a,6,8,4,g,c,g
2u,k,m,i,m,s,26,24,34,3wa,1i,1f,m,1f,h,1z,e,5,e4,m,c,8,6,6,8,6,e
2q,m,k,k,o,q,22,1y,2s,3v8,22,21,o,1h,9,c,1u,2w,ds,g,a,6,a,2,g,c,i
2s,k,k,k,m,u,26,26,38,3wo,1u,1d,2,2b,1l,x,7,o,e4,m,c,8,6,2,2,6,a
2o,m,m,k,m,q,22,1y,2s,3u6,22,1v,u,17,2,i,1o,2w,dk,g,c,6,a,2,i,e,g
2s,m,k,i,o,u,26,24,38,3we,1s,19,q,2n,1x,j,b,k,e4,o,a,a,6,2,2,6,a
2q,m,k,i,m,s,26,24,36,3w4,1u,1l,o,1f,r,2d,c,7,e0,m,c,8,6,4,8,4,e
18,a,8,8,6,a,q,q,10,3u0,4,f,q,y,1t,6q,4y,66,9y,y,y,1w,1i,1i,2u,1y,2u
10,a,8,6,8,8,m,k,s,3zk,1x,l,v,u,w,4w,68,5q,8m,1s,w,10,22,1s,2w,1q,26
1c,c,8,6,a,a,s,u,1a,3sg,1e,12,u,1b,p,8a,12,3o,ao,q,1i,1s,1i,1o,2a,1k,2k
12,a,8,8,6,8,o,k,w,3yi,1d,17,h,y,w,4g,60,60,8q,1o,12,18,22,1k,2u,1w,2e
y,8,8,6,6,8,k,i,q,3z4,2h,1w,t,1,16,5k,6k,6o,8g,1y,m,10,1k,26,32,1k,1q
18,c,8,8,8,c,u,w,18,3tg,u,12,14,i,2h,4q,2m,6a,ae,q,16,26,1g,22,2k,2k,28
12,a,8,8,6,8,q,m,y,3xc,2x,11,v,q,10,46,5s,76,8i,1w,10,16,2a,1m,34,1w,26
y,8,6,8,6,8,k,i,q,3z0,2f,1s,v,5,16,5m,6m,6s,8g,1w,m,10,1m,28,32,1m,1m
18,c,a,8,8,c,w,w,1a,3ti,1k,18,12,3,1x,46,28,6o,ao,m,1i,1y,1k,22,2k,2k,20
o,8,6,8,6,8,i,i,m,444,bf,r,1k,1a,7,80,5s,a0,5w,2g,1s,16,10,2e,3u,1w,10
10,8,a,6,8,8,q,m,w,3x2,4n,1,n,a,1i,42,6i,7e,88,2g,m,12,2a,1q,3e,1u,24
16,a,8,8,6,a,o,o,y,3vw,t,v,c,y,j,5g,5a,6k,98,1i,y,1s,1m,1g,2s,22,2w
10,a,8,8,8,8,s,o,y,3uq,2p,4,4,14,18,28,5m,7k,8e,28,s,1g,22,1s,38,1u,2e
w,8,8,6,6,8,i,i,q,3yo,1j,1k,7,5,1c,5e,6m,78,8g,1u,k,12,16,2a,2y,1m,1e
14,a,8,8,6,8,o,m,w,3xs,1b,11,0,y,u,46,5u,6c,8u,1q,10,1m,1q,1g,2q,22,2u
u,8,6,8,6,8,k,i,q,3ww,29,k,5,h,e,7e,6q,7e,7k,1i,1i,14,1i,2k,3i,1m,1i
w,8,8,8,8,8,m,m,u,3wo,39,t,11,4,1c,5w,7q,78,7o,2a,w,14,24,22,3s,1o,1s
10,a,8,8,6,8,m,k,s,3z8,v,18,1a,18,14,32,6u,6o,8m,1m,y,y,w,1u,1y,1a,1a
16,c,8,6,8,a,s,s,12,3tu,r,5,16,16,1z,52,44,6e,9k,18,10,20,1k,1w,2q,1u,30
y,8,8,6,6,8,k,i,q,3z8,27,28,p,c,18,58,6m,6s,8k,1s,q,y,1g,22,2u,1m,1k
y,a,8,8,8,8,q,m,y,3w2,49,4,9,k,1i,3g,6o,7i,86,2e,o,14,2a,1q,3i,1u,26
10,a,8,8,8,8,q,m,y,3x6,3r,j,r,o,18,3y,5w,7a,8g,24,s,18,28,1m,38,1w,26
14,a,8,8,8,8,q,o,y,3wi,1r,15,5,y,1,4y,5a,6u,90,1k,10,1s,1o,1i,2y,20,2q
18,c,6,8,6,a,q,m,10,3u0,y,9,u,10,1d,6k,5c,4o,aa,o,y,1s,1i,1e,2s,1w,2o
1a,a,8,8,6,a,q,o,14,3sw,1m,8,w,10,1v,7c,54,2s,ai,k,y,1w,1g,1m,2k,1o,2k
1a,c,a,8,8,e,w,y,1g,3tu,1m,1a,y,1f,n,40,2k,46,ao,w,1m,1u,1s,1o,2e,2k,1y
1c,k,c,i,g,m,1q,1o,2i,3ua,45,1u,1t,q,1,10,32,4o,9a,1o,k,y,e,o,o,e,g
18,c,8,8,8,c,w,w,18,3tq,12,16,12,g,27,4a,1s,7c,ag,q,1i,1u,1k,22,2m,2i,26
18,c,8,6,8,a,o,q,12,3te,12,1,s,10,1p,70,52,3s,aa,q,y,1w,1g,1i,2s,1s,2o
y,a,a,6,8,8,q,o,w,3vo,45,c,3,o,1g,34,6k,7m,86,2k,k,12,2c,1o,3i,1u,26
14,c,8,8,6,a,q,q,10,3vc,1n,z,i,12,v,56,4w,6m,8y,1o,12,1u,1o,1o,2u,1w,2w
w,8,8,8,8,8,m,k,u,3xc,31,9,17,f,16,5o,7c,70,7u,28,u,12,20,26,3k,1q,1s
12,c,8,8,6,a,s,o,12,3uy,21,p,i,1c,4,36,4w,6y,8m,1w,12,1y,1m,1w,2w,1s,2o
10,a,8,6,8,8,s,m,y,3v4,43,y,0,w,1e,2k,6a,7q,8a,2k,i,10,2e,1o,3i,1u,28
y,a,6,8,6,8,k,k,s,3zg,2t,1m,v,e,12,5e,6c,62,8k,20,k,y,1q,24,2y,1m,20
1k,i,i,i,k,o,1s,1s,2o,3ra,9,15,0,i,5,24,3s,48,9w,1a,u,m,k,g,m,e,k
16,c,8,8,8,c,u,u,16,3ts,8,o,18,u,2h,4s,2c,6u,a8,s,12,22,1m,1y,2o,2g,2e
12,a,a,6,8,a,o,m,y,3z8,l,s,1c,16,s,3a,5c,5w,8m,1k,12,w,16,1a,1s,12,12
w,8,8,6,8,6,k,i,o,3yu,1p,20,d,6,1c,50,6s,72,8i,1s,m,10,14,2a,2s,1m,1g
12,a,8,8,8,8,q,o,y,3wc,2d,1b,f,12,u,3u,5c,74,8m,1q,16,1i,1y,1o,32,1w,2e
16,a,8,8,6,a,o,o,y,3vy,v,z,a,y,h,5e,5c,6k,98,1g,10,1s,1m,1g,2s,24,2u
y,8,8,6,8,6,k,i,s,3ze,2f,22,p,e,14,5e,6g,6k,8k,1u,q,y,1k,20,2w,1k,1q
18,g,a,a,c,g,12,16,1u,3yw,53,1w,q,9,16,1o,10,20,96,1s,m,u,u,u,14,m,k
12,a,6,8,6,8,k,k,s,406,j,t,7,12,10,4y,64,4e,8s,1k,12,10,1q,1q,2o,1m,26
10,a,8,8,8,8,q,m,y,3vy,4l,i,7,w,1g,2y,64,7m,8c,2c,o,16,2a,1o,3c,1w,28
1a,a,8,6,8,a,o,o,12,3te,1e,0,y,10,1r,74,5a,3a,ae,m,y,1u,1g,1k,2m,1s,2k
1k,k,g,i,k,o,1u,1s,2o,3r4,7,1b,4,e,1,2a,3u,46,9w,1a,u,k,m,g,k,e,m
18,c,8,8,a,c,w,w,1a,3tg,1g,18,10,9,1r,48,2a,5y,ak,q,1i,1u,1q,1w,2i,2o,20
y,8,8,6,6,8,i,k,o,3z4,23,28,n,a,18,58,6o,6u,8i,1u,o,10,1e,22,2u,1m,1k
16,e,8,8,a,c,s,s,18,3zs,2h,1w,1g,c,4,3i,2s,4k,9o,1c,m,o,14,12,1i,u,y
y,a,8,6,8,6,m,k,s,3za,3b,1c,x,8,12,58,6e,62,8i,2a,c,10,1w,20,32,1o,24
w,a,6,8,6,6,k,i,q,3yy,1z,24,n,0,1a,5c,6o,70,8i,1u,m,10,1g,24,2w,1m,1i
14,c,8,8,6,a,u,u,12,3u6,b,3,18,12,1b,3y,2g,6w,94,1m,10,22,1k,22,2u,1s,2u
18,c,8,6,8,a,o,q,12,3ti,s,5,u,y,1r,6y,50,3w,a6,s,y,1w,1i,1k,2q,1w,2o
s,8,8,6,6,8,i,g,o,3xi,1b,1v,3w,5,q,5y,5s,8i,8a,m,1q,s,10,28,38,1s,18
10,a,6,8,6,8,k,k,s,3zo,2r,w,v,o,y,56,6a,5o,8m,22,k,10,1u,1y,2w,1o,26
y,a,8,6,6,8,k,i,q,3z6,1f,1w,6,18,18,3s,70,74,8m,1q,q,10,w,22,2a,1i,1e
y,a,8,6,8,8,o,m,w,3xc,49,7,t,9,1e,4u,76,74,80,2a,u,10,28,1w,3i,1u,20
1a,a,8,8,8,c,s,s,18,3s6,18,w,16,i,2t,6g,3w,4o,aa,u,12,24,1k,1w,2k,2c,2a
18,c,8,6,8,a,s,q,12,3t4,c,1,12,s,2j,76,4i,5c,a6,s,10,20,1i,1m,2s,20,2q
18,a,8,8,6,a,q,o,12,3ty,k,b,s,y,1l,6o,54,4q,a6,q,10,1u,1i,1g,2s,1y,2q
14,c,8,8,8,a,s,s,12,3tu,t,5,18,16,1n,4e,3w,6k,9i,18,10,22,1k,1w,2s,1u,2w
y,8,6,8,6,8,i,i,q,3yu,1l,24,7,q,1c,4c,6w,74,8k,1q,m,10,y,28,2k,1m,1e
12,a,8,8,6,a,s,q,10,3u6,1j,3,c,1a,s,26,4q,7g,8k,20,y,1o,1u,1w,34,1s,2i
y,8,8,6,8,6,k,i,q,3z2,1n,28,b,12,1c,46,6w,74,8m,1q,o,10,y,24,2g,1k,1g
12,a,8,8,6,8,q,o,y,3x0,2v,11,r,u,14,3y,5m,76,8i,1w,10,1c,24,1m,36,1w,28
y,a,6,8,6,8,k,i,s,3ze,2r,1s,t,a,14,5g,6e,66,8i,1y,o,y,1o,24,2y,1m,1w
1g,i,e,g,g,k,1k,1m,2e,3tm,1b,16,1l,1c,5,o,2c,46,9k,1k,o,y,i,k,s,e,e
u,8,6,8,8,8,k,k,u,3w8,2b,x,1b,m,12,80,7w,7a,74,28,1a,12,1k,2m,3w,1k,1m
12,a,6,6,8,6,m,i,s,40e,r,5,5,16,10,4w,64,4y,8s,1k,12,y,1g,1u,2k,1k,1w
s,8,6,6,8,8,i,i,m,3wu,1z,g,12,5,0,7q,5w,8s,7s,y,1q,12,10,2s,3c,1s,14
12,a,8,8,8,8,s,q,10,3tw,v,7,g,1a,u,22,4a,7e,8k,1y,10,1s,1q,20,32,1s,2i
12,a,8,8,6,8,q,m,w,3xo,2l,13,x,s,u,4e,5s,70,8k,1u,10,16,28,1m,32,1w,28
w,8,8,6,6,8,i,k,o,3yq,1p,1k,b,9,1a,5m,6m,78,8g,1u,k,12,1a,2a,30,1m,1e
w,a,8,6,8,8,m,k,w,3xa,33,f,17,d,16,5o,7c,72,7u,28,u,12,22,24,3m,1q,1s
y,8,8,8,8,8,m,m,w,3x8,3t,f,x,9,1c,54,7a,74,7y,26,y,10,26,1y,3m,1s,1w
1c,a,8,8,8,a,o,s,14,3se,1o,s,w,c,1j,8k,36,4e,ao,m,18,1q,1e,1q,28,1i,28
18,c,a,8,8,c,y,w,1c,3ti,1w,1a,y,d,1j,3u,1q,6i,aq,o,1m,1s,1q,1y,2k,2k,1y
12,a,6,8,6,8,o,m,u,3ym,2j,n,z,s,w,4i,66,6c,8k,1w,w,16,24,1o,30,1u,24
u,8,6,6,6,8,i,i,o,3xo,1l,13,34,h,o,6m,5y,8i,88,o,1s,w,y,2o,34,1q,16
10,a,8,8,8,8,s,o,y,3uu,2h,7,1,18,18,26,5o,7m,8e,24,w,1k,1y,1q,3a,1u,2c
16,a,8,8,8,a,u,s,14,3tu,b,4,18,14,1z,4i,34,6i,9o,16,y,24,1k,1y,2s,1w,2u
u,8,6,8,6,8,i,g,o,3xw,1f,9,24,h,y,64,68,84,8c,m,1o,10,10,2k,30,1q,18
10,8,8,6,6,8,k,i,q,3zo,21,1y,j,10,16,4y,6g,6m,8m,1s,s,10,1e,1w,2m,1k,1o
18,a,8,8,8,c,u,s,16,3t4,g,k,1a,u,2l,56,30,6m,a8,s,12,20,1k,1y,2o,2e,2e
w,a,8,6,8,8,k,k,s,3yg,31,q,11,l,16,5g,6s,6m,88,22,m,12,1y,24,3e,1m,1y
w,8,8,6,8,6,k,i,o,3ys,1t,1o,h,7,1a,5k,6o,76,8g,1u,m,10,1e,28,30,1m,1g
w,8,8,8,6,a,m,k,u,3wq,2v,v,19,4,18,68,7o,78,7m,2a,w,14,22,26,3q,1o,1q
18,c,8,8,8,c,w,u,1a,3t2,14,12,16,6,29,4s,2y,5o,ai,o,1c,1y,1m,1w,2k,2m,22
10,a,8,6,8,6,m,i,u,3zy,2h,w,p,10,y,52,64,5a,8o,26,i,y,1s,1u,2s,1o,22
10,c,8,8,8,a,o,m,10,3ys,n,d,1e,1g,q,3c,4k,66,8g,1i,12,u,w,1i,1s,10,w
1c,e,8,8,c,e,10,10,1q,3ve,20,y,m,2r,1u,2i,1q,28,au,16,1w,1o,20,12,2k,2m,1k
14,e,8,a,8,c,u,u,1c,3zs,2n,c,1g,e,6,3c,2c,3q,8o,1s,y,q,16,10,1i,q,s
1g,k,e,i,i,m,1q,1o,2k,3su,1j,6,r,w,b,1e,34,4i,9m,1i,o,w,i,k,m,e,i
1g,g,c,e,e,i,1c,1e,24,3um,v,1e,v,10,u,o,14,32,9s,1a,q,w,i,s,w,i,i
w,a,8,8,6,8,o,k,w,3wy,3f,n,11,5,1e,5i,7i,74,7s,2a,w,12,24,22,3o,1q,1u
10,a,8,6,8,6,o,k,s,3zi,25,d,x,s,y,4w,68,5s,8m,1w,s,10,22,1s,2y,1o,26
16,c,8,6,8,8,o,o,y,3v8,a,l,o,10,p,5q,5k,5k,9w,y,y,1o,1m,1c,2s,20,2q
10,8,8,6,6,8,k,k,s,3zo,2l,1g,r,k,12,5e,68,5q,8k,1u,s,y,1k,26,2w,1m,24
18,a,8,8,8,a,s,q,14,3t6,3,2,18,12,2t,6e,4c,5y,a0,w,y,22,1k,1q,2o,1w,30
w,8,8,6,6,8,i,i,o,3yk,1f,1k,1,5,1e,58,6o,78,8g,1o,o,12,12,2c,2w,1o,1c
1a,a,8,8,8,a,q,q,18,3sa,1c,e,w,m,27,7o,4o,24,ae,q,12,1y,1g,1q,2o,1q,2m
1q,m,k,i,m,q,1u,1u,2s,3o2,1m,1d,1a,5,w,36,3u,36,ak,s,10,e,g,e,o,e,o
12,a,8,6,8,6,m,i,u,40a,1t,22,12,y,y,3q,6m,5i,8s,1q,w,y,18,1m,1y,1g,1i
14,c,8,6,8,8,q,m,y,3wg,11,z,8,y,5,52,5g,6m,90,1o,y,1q,1o,1g,2s,24,2u
q,8,8,6,8,6,k,i,o,3vg,25,1,14,1,3,8s,6g,8q,6y,1g,22,12,12,2y,3i,1o,1a
14,a,8,8,8,a,s,s,12,3u2,h,b,12,14,n,3c,38,6u,90,1m,12,22,1k,20,2w,1o,2s
s,a,6,8,8,8,k,k,s,3vy,1z,t,13,i,q,8c,7o,7g,74,22,1e,16,1e,2s,3s,1k,1m
14,a,a,6,8,a,u,u,12,3ty,1,3,16,14,x,3m,2c,70,90,1o,14,1y,1m,22,2w,1s,2q
u,a,6,8,8,8,k,m,s,3wi,29,h,1f,4,w,6q,7i,78,7k,2c,u,14,1q,2i,3o,1m,1m
y,a,8,6,8,8,o,m,w,3xi,4d,1,v,7,1c,4s,72,72,82,2c,q,10,2a,1u,3i,1s,20
10,a,8,8,8,8,o,o,w,3x6,5b,o,r,g,1i,3w,62,7k,8c,2o,c,12,2c,1o,38,1y,24
y,a,6,8,6,8,k,k,q,3zc,2v,1o,v,a,12,5g,6e,62,8i,20,m,y,1o,26,30,1m,20
16,c,8,8,8,c,u,w,16,3tw,e,10,14,w,2h,4k,1q,7c,a0,10,1a,1y,1k,22,2m,2i,2c
10,a,8,8,8,8,o,o,w,3xa,3f,t,v,m,16,42,5y,7c,8g,20,w,16,2a,1m,36,1y,24
s,8,6,6,8,6,k,i,o,3wk,25,7,1k,1,3,80,5y,8u,7i,16,1u,10,10,2u,3e,1q,16
2a,s,o,o,s,y,2k,2k,4k,3u6,e,5,1d,29,l,y,2a,55,cu,m,k,g,8,e,c,6,m
1g,c,8,8,a,c,s,u,1e,3us,q,w,4,2b,2a,7q,46,1p,am,1a,1m,1i,14,18,1o,1c,2a
1q,c,e,e,e,g,1i,1m,2m,3x2,2c,2h,1i,24,1y,37,1t,2d,be,20,14,m,c,4,18,6,s
1e,a,8,8,8,c,q,u,14,3tu,4,12,s,x,9,96,14,64,ae,12,1m,1q,16,1q,20,1e,2o
1q,e,e,c,e,g,1g,1k,2g,3xw,22,2z,1a,2s,2c,3h,2p,3,bm,20,12,m,e,6,1k,e,i
14,a,8,6,8,8,m,k,w,3y6,l,x,6,10,s,4i,64,52,8u,1q,10,1c,1u,1g,2q,1w,2q
1c,c,8,6,8,a,o,q,10,3t8,22,12,w,c,t,8a,54,3q,ai,q,14,1k,1c,18,28,16,1g
1s,g,i,g,i,m,1y,20,3e,3x2,2g,2b,2,q,1i,4r,2,37,bk,1y,s,c,8,6,q,2,i
2c,s,o,o,u,y,2m,2m,4m,3u0,1i,v,1n,2j,h,14,28,4z,cw,g,m,g,a,c,e,6,k
1i,c,8,a,a,c,u,y,1m,3ve,16,s,p,2b,2y,7e,1w,2t,au,1e,1k,1e,18,w,1o,1e,1q
1g,c,8,a,a,c,w,u,1g,3v8,w,s,2,37,38,54,46,b,aq,1e,1m,1g,1c,12,1s,1e,2i
1s,g,i,e,g,k,1s,1u,34,3xw,1q,45,38,o,w,5f,1c,3b,bs,1w,y,8,e,6,12,7,s
1s,e,g,g,e,m,1k,1k,2k,3zq,1a,37,6,m,1r,a,16,2m,c0,1w,14,s,m,g,1m,12,o
1m,c,a,a,c,e,16,1e,28,3v2,26,u,25,1g,4i,30,4z,31,ao,18,1k,1c,o,8,k,o,s
1e,c,8,8,8,a,q,u,1g,3uw,24,16,w,r,w,6g,34,2v,ac,10,y,1c,1g,s,1w,u,18
20,m,k,o,m,s,2e,2g,3u,3wy,6,2b,2f,1e,u,29,e,14,bs,1g,q,g,2,2,e,g,e
1s,g,g,g,g,o,1s,22,34,3w2,2q,2h,k,0,9,7,1z,o,b8,1k,14,i,g,4,6,e,g
12,a,6,8,6,8,m,k,u,3zo,1d,n,h,10,w,4w,62,52,8q,1q,y,10,20,1o,2s,1o,2a
1m,e,8,c,e,e,18,1i,2e,3v8,2a,k,1v,14,38,34,51,2p,aq,1a,1g,1a,k,c,e,k,s
1q,e,e,e,c,i,1i,1e,28,3z6,1g,27,15,3i,e,2p,m,30,bs,20,16,q,i,i,1i,12,o
22,s,k,m,o,u,2g,2g,4a,3xg,5h,26,c,v,1f,v,26,4z,ca,1k,a,c,8,8,m,2,m
12,a,8,6,8,6,m,i,s,40u,t,0,q,14,12,4m,62,4o,8u,1m,10,y,1c,1s,2a,1i,1w
1e,a,8,8,8,a,q,s,18,3ug,2m,16,w,5,g,5u,40,o,ae,w,y,1a,1g,w,1y,w,1c
1q,c,e,e,c,g,1c,1k,2a,3xw,1u,23,b,3w,2u,1n,4v,o,bk,20,16,o,c,8,1k,i,k
14,a,8,6,6,8,k,k,s,40g,f,j,s,14,10,4u,68,42,8w,1o,10,y,1m,1k,2g,1m,20
1q,e,c,e,c,g,1c,1k,2a,3y2,1u,27,9,42,2o,23,4h,12,bm,20,16,m,e,8,1k,k,k
1s,g,i,g,g,k,1u,1w,36,3xy,1k,41,30,m,w,53,10,3d,bu,1u,w,a,c,6,y,3,q
1c,c,8,8,a,c,y,y,1i,3uq,s,18,y,29,2,42,2c,2w,ag,16,1o,1u,1u,1g,2e,2a,24
18,a,8,8,6,8,o,m,10,3v6,1i,1,14,14,l,60,62,2w,a8,o,10,1g,1k,18,2o,1i,1w
1w,k,k,i,k,m,1s,28,3c,3zu,2b,2f,6,1l,q,22,1d,3w,ci,1q,y,q,m,q,1o,y,m
1c,c,8,8,c,c,y,y,1m,3v2,u,16,y,2n,o,3y,24,2w,ai,18,1o,1u,1w,1a,2e,2a,22
1c,c,8,8,8,a,s,u,18,3s2,1s,12,u,19,f,7w,s,5a,au,m,1i,1s,1c,1s,28,1i,2k
1c,a,8,6,8,a,o,o,y,3uo,38,16,w,10,d,4i,50,6w,ae,q,y,1a,1c,12,22,10,1i
1q,e,e,e,e,i,1i,1e,2a,3za,1k,2b,15,2u,5,2d,1e,2o,bu,1w,1a,q,k,i,1m,14,q
1s,e,e,g,g,k,1q,1w,2y,3w0,2y,2b,4,8,w,d,1r,u,b4,1m,14,o,c,6,a,c,c
1s,g,g,g,g,m,1k,1m,2k,3zw,14,3n,1a,h,25,12,m,32,c4,1u,10,u,k,g,1k,y,o
1w,m,g,i,k,o,1q,22,34,40e,3v,6,5,2r,5,2i,v,2u,ci,1o,w,q,k,i,1m,s,m
1e,c,8,8,a,c,s,y,1c,3u8,o,12,s,3f,1a,72,6,58,ak,16,1o,1q,1k,1c,20,1g,2s
1s,g,e,g,e,k,1k,1i,2g,3zm,w,2p,6,1o,1j,1h,1u,2w,c0,1y,10,q,k,e,1m,y,o
1e,c,8,6,a,c,u,y,1g,3u4,o,14,w,33,u,6c,1s,2a,ai,16,1o,1q,1o,1c,26,1e,2u
14,a,8,6,6,8,o,k,u,3yo,p,x,2,10,u,4m,68,4q,8u,1o,10,18,1y,1g,2q,1w,2o
1c,c,6,8,8,a,o,o,12,3tg,1u,10,y,g,z,82,4q,50,ae,u,12,1i,1g,18,2a,16,1i
1o,c,c,c,e,g,1a,1m,2i,3w4,28,9,1d,3o,2u,5,5n,1r,b0,20,1a,u,a,2,u,w,e
14,a,8,6,6,8,k,k,u,406,f,j,o,14,y,4u,6c,3w,8w,1o,10,y,1q,1i,2i,1o,24
1e,c,8,8,8,c,q,s,16,3tm,w,10,s,1j,o,80,44,36,am,10,1k,1m,16,1k,1u,1c,2g
1o,e,e,e,e,g,1g,1o,2m,3x0,2a,27,18,26,20,2t,27,2h,be,1y,16,m,a,4,14,a,u
1w,m,g,i,k,m,1s,20,34,40g,3t,0,7,2h,b,2g,n,2u,cg,1q,w,q,k,g,1m,q,o
14,a,6,8,6,6,m,k,s,406,h,p,e,12,10,4y,66,3u,8u,1o,10,y,1s,1k,2m,1m,2c
1i,e,8,a,a,c,y,y,1u,3vg,18,s,1h,1d,30,72,2e,61,au,1e,1m,1c,12,s,1e,12,14
14,a,8,6,6,8,m,k,u,3zi,j,p,g,10,y,4q,6k,3i,8w,1o,10,10,1w,1i,2k,1s,2o
1m,e,a,c,c,g,1a,1i,2g,3vm,28,a,23,30,3i,14,5b,2j,au,1q,1c,12,i,4,o,u,g
16,a,8,6,6,8,m,i,u,3zc,k,8,12,16,12,4c,6s,3e,92,1o,10,u,1i,1i,26,1k,20
16,c,6,8,6,8,m,k,y,3wy,u,b,10,12,g,4w,6m,3k,9w,w,y,1e,1m,1c,2g,1o,2a
14,a,8,6,8,6,m,i,u,3zu,1,f,10,16,10,4o,6i,3y,8w,1q,10,w,1o,1i,2e,1m,22
14,a,6,8,6,8,m,i,w,3zc,n,v,0,10,w,4s,6a,48,8u,1o,y,14,1w,1k,2q,1s,2m
18,a,8,6,8,8,m,m,y,3vo,16,9,12,12,b,5m,68,3a,a6,o,10,1g,1m,18,2m,1o,2a
16,a,8,6,6,8,m,i,u,3ys,m,7,16,14,10,4i,6y,3i,92,1o,10,w,1q,1e,28,1o,22
1o,e,c,g,e,i,1i,1o,2s,3we,2g,z,2,26,1i,25,2h,2j,b6,20,16,k,a,6,q,i,e
1q,e,e,g,g,i,1o,1s,30,3wk,2m,15,g,18,q,3f,j,31,ba,22,12,c,a,8,o,8,e
1c,c,8,8,6,c,o,q,12,3tg,16,12,y,4,15,90,4g,4o,ae,w,1a,1i,1e,1g,20,1a,1g
1m,e,a,c,c,g,18,1k,2e,3vg,2c,e,2f,2k,52,1a,6f,2h,as,1g,1k,16,i,0,m,o,o
1k,e,8,c,c,c,10,16,1w,3x2,18,k,2h,j,3s,5c,1f,1h,b6,1k,1o,1a,w,u,1g,1a,10
1u,k,i,g,g,m,1y,20,38,3z4,r,3h,3c,c,8,4d,s,2p,c4,1s,o,e,a,6,u,4,e
1i,c,8,a,a,c,u,w,1m,3v6,14,s,p,23,2u,82,2c,47,aq,1e,1m,1e,16,w,1k,1a,1m
1k,e,8,c,c,c,10,16,1w,3wy,1a,o,2h,13,44,68,z,2p,b6,1m,1k,1c,u,q,1c,14,12
1o,e,c,c,e,g,1c,1m,2i,3wu,24,1f,8,2w,2o,1n,41,1r,ba,20,16,s,a,4,18,g,o
1e,a,8,8,8,a,q,s,12,3sw,1e,12,u,f,l,8s,40,4q,ao,q,1g,1m,18,1m,1w,1e,1q
1c,c,6,8,8,a,o,o,12,3ui,32,16,w,k,3,4w,52,4m,ae,s,w,1c,1e,10,22,y,1g
20,q,i,m,k,o,1y,2a,3i,40c,61,o,2d,11,m,2k,d,20,cu,1c,u,q,i,i,1c,m,o
22,q,k,k,k,s,24,2k,3w,3y2,3f,11,15,d,w,1u,7,4c,cs,1i,w,q,m,s,1e,w,s
1w,m,k,i,i,q,26,28,3s,3z2,33,17,1c,i,7,3j,16,43,c4,1q,g,e,8,4,q,0,k
1u,i,g,e,g,k,1u,1q,2s,3ze,d,39,3o,w,j,59,1e,r,c4,1y,m,i,c,8,14,a,s
1g,c,8,6,a,a,s,s,1e,3u8,k,12,k,15,y,96,40,1t,ae,16,1m,1g,16,1c,1k,1c,1e
1w,i,i,k,i,q,22,24,3m,3xc,w,2v,9,y,1g,2f,9,1f,bm,1s,s,c,2,4,k,g,e
1q,e,e,e,c,g,1e,1k,28,3ye,1w,2j,7,4e,20,2v,3l,1o,bo,20,16,k,e,a,1m,k,m
12,a,8,6,8,6,m,i,u,406,h,p,6,12,y,50,66,3u,8u,1m,10,10,1q,1m,2o,1k,2g
1g,c,8,8,c,a,u,s,1g,3uu,s,w,2,2f,2e,7g,48,1p,am,1c,1m,1g,16,16,1o,1e,28
1w,k,i,g,g,o,1y,1w,30,3zu,35,z,3i,5,1x,3n,1m,11,ce,1q,k,g,a,c,s,6,y
1k,e,a,a,c,e,14,1c,28,3vk,1u,i,1v,2i,22,32,2f,3x,au,1o,1i,16,m,c,s,10,i
14,a,8,6,8,6,m,i,u,3zu,3,d,10,14,10,4o,6i,3y,8w,1q,10,w,1o,1i,2e,1m,22
1e,a,8,8,8,a,o,s,16,3ua,2u,16,w,4,8,5i,4k,22,ae,u,10,1a,1g,w,20,w,1e
1a,c,8,8,a,c,w,w,1g,3tk,16,1a,10,15,13,4i,2u,38,am,u,1k,1u,1o,1u,2c,2g,1y
16,a,8,6,6,8,m,i,u,3zw,c,k,12,14,14,46,6m,46,94,1m,10,u,1c,1k,22,1i,1y
22,q,k,k,m,q,26,2k,3w,3xy,3f,z,19,7,w,1q,0,4a,cs,1i,w,q,m,s,1e,w,s
1c,a,8,8,6,c,o,q,10,3se,26,u,10,m,1r,8g,34,78,au,e,12,1q,1e,1k,28,1g,1e
1c,a,8,8,6,a,q,o,10,3ss,20,u,y,q,1n,84,3i,72,ao,k,y,1q,1e,1i,2a,1g,1g
1e,c,8,8,c,c,10,y,1q,3uy,1a,12,o,3f,26,44,3i,4,ao,1a,1s,1m,1u,12,2a,1m,2k
1e,c,8,6,8,a,q,q,18,3su,2g,18,w,9,2,8c,4y,5,ao,m,16,1o,18,14,20,12,1c
1y,q,k,k,m,u,2c,2c,44,3zq,6x,10,14,6,1l,1v,1w,4r,c8,1o,a,e,6,6,o,2,m
20,q,k,m,m,u,2e,2e,44,3z4,6x,1i,q,6,1l,1h,1u,49,c4,1m,e,a,8,6,q,2,m
1u,i,k,g,i,m,22,22,3i,3y6,1,2p,1s,i,q,4d,q,3r,by,1u,o,a,a,4,s,0,m
1s,g,i,g,g,k,1w,1w,38,3xw,1i,3x,2u,k,y,4z,w,3f,bu,1u,w,a,c,4,y,3,q
1q,c,c,e,c,g,1c,1g,22,3yk,1e,1b,1f,3k,2o,1b,2r,28,bo,1y,1a,o,i,g,1k,w,q
12,a,8,6,6,8,m,i,u,402,l,v,5,12,y,4y,66,48,8s,1k,12,10,1u,1o,2o,1m,2c
1u,k,i,g,i,o,1m,1u,2u,408,25,15,s,2d,1d,2a,c,2u,cc,1s,w,q,k,e,1i,u,o
16,c,6,6,8,6,m,k,u,3zo,o,y,10,16,12,3y,6o,3q,9y,u,10,s,1a,1i,1y,1i,1w
1k,c,8,a,c,c,10,10,1s,3we,18,q,1p,1j,3k,52,18,2f,b2,1e,1q,1e,16,s,1o,1c,1o
1g,c,a,8,c,c,10,12,1o,3vi,18,y,5,2v,2y,50,20,u,au,1e,1s,1g,1o,u,20,1e,2o
18,a,8,6,8,8,o,k,w,406,q,1c,10,w,e,3o,5g,5k,9s,w,10,s,16,1c,1q,16,1o
16,a,8,6,8,6,m,i,w,3zm,m,u,10,16,12,40,6q,3e,9o,14,10,s,1c,1i,20,1i,1w
1g,c,8,8,a,a,w,w,1q,3uw,1y,1a,g,p,14,4o,3g,57,ae,12,12,1e,18,q,1i,u,y
1m,c,a,a,c,e,10,16,1y,3ws,1a,g,2n,4,3k,56,1,3t,b6,1i,1m,1a,u,i,1e,w,w
1q,e,e,g,e,k,1q,1q,30,3w0,30,2f,a,1q,s,1b,1l,z,b0,1s,14,q,c,2,e,g,c
1w,i,i,g,k,m,1o,1y,2y,408,2l,j,q,31,r,2e,h,2y,ce,1q,y,q,k,g,1o,s,o
1c,c,6,8,6,a,q,o,10,3uk,34,14,w,q,5,4q,54,58,ae,s,w,1c,1e,10,22,y,1i
1s,g,i,g,i,k,1w,1y,3a,3xm,1k,3b,24,m,y,4l,k,3l,bs,1w,u,a,a,6,s,0,m
1i,a,8,8,a,c,u,y,1o,3v0,26,1a,c,j,1k,36,32,35,ae,14,12,18,16,q,1k,s,u
16,a,8,6,6,8,o,k,u,3xs,1,n,o,10,m,4o,6g,4g,9i,16,y,1g,1o,1e,2i,1w,2q
1g,c,a,8,c,e,10,14,1u,3vq,1g,y,f,2j,36,4y,1w,r,aw,1e,1w,1e,1o,s,24,18,2o
1w,k,i,k,i,m,1w,24,38,3zu,29,2z,3,15,1g,k,1e,4m,ce,1w,10,s,q,q,1s,10,q
14,a,8,6,8,6,m,i,u,416,v,s,10,10,12,3y,6a,4q,8u,1q,10,w,18,1m,1y,1g,1s
1w,k,k,i,k,q,26,28,3s,3yi,1l,1p,8,s,k,3t,o,3f,bu,1s,k,c,6,6,s,0,k
14,a,8,6,6,8,o,k,u,3yo,n,v,4,y,w,4k,6a,4m,8u,1q,y,1a,1u,1i,2q,1u,2o
1o,e,c,e,c,e,1c,1e,22,3ym,1a,t,2h,2k,3c,b,23,2c,bm,1w,1e,u,k,k,1e,14,q
1o,e,a,g,c,e,1c,1c,24,3ym,1a,t,2n,2y,2s,z,p,1w,bm,1u,1g,u,m,k,1i,14,s
1w,k,k,i,k,q,26,28,3s,3yq,2f,17,s,k,4,3r,12,3x,c0,1q,i,e,6,6,s,1,k
12,a,8,6,8,6,o,k,u,3za,v,z,9,10,w,4q,68,4s,8s,1m,12,10,22,1k,2s,1s,2i
1a,c,8,6,8,a,o,o,12,3so,20,i,y,w,1x,7y,4c,3w,ai,m,12,1q,1e,1m,2g,1k,20
1w,m,k,i,i,q,26,28,3q,3z2,2z,1d,1i,g,5,3j,14,41,c4,1q,i,c,8,4,s,0,i
14,a,6,8,6,8,o,k,u,3yo,13,13,7,y,w,4i,62,5i,8s,1o,10,16,22,1k,2s,1w,2k
12,a,6,8,6,6,m,i,s,40g,p,2,4,16,12,4q,66,52,8s,1k,12,y,1c,1u,2e,1k,1u
2a,s,m,o,o,w,2m,2q,4q,3ve,1z,1b,p,e,p,1u,2s,1i,cy,1a,10,k,m,10,14,u,y
1q,e,e,e,e,i,1i,1g,2c,3zc,1o,2h,15,1w,9,t,y,2a,bu,1w,18,w,m,k,1i,16,q
1e,c,a,8,a,e,10,12,1q,3vm,10,12,k,31,20,3u,1m,1k,am,1e,1q,1q,1u,10,2a,1u,2c
1g,c,8,6,a,a,s,w,1i,3ts,1s,1a,12,19,1e,7y,w,2z,aa,14,1g,1e,14,y,1o,14,10
1e,a,8,8,8,a,q,q,12,3sq,1k,12,w,7,v,8s,3q,5e,aq,o,1c,1m,1a,1m,1y,1e,1m
1k,c,a,a,c,c,y,12,1u,3wi,1a,o,23,17,3g,6m,j,2r,b2,1k,1k,1c,10,s,1g,1a,14
14,a,8,6,6,8,m,k,u,3za,j,t,a,10,y,4o,6e,3w,8u,1q,y,16,1u,1i,2m,1s,2o
12,a,8,6,8,6,o,i,w,3zc,t,11,7,10,w,4q,68,4q,8s,1m,12,10,22,1k,2s,1s,2i
1s,e,g,g,e,k,1k,1i,2g,3zm,12,2v,6,1i,1j,19,1q,2y,c0,1y,10,q,k,g,1k,10,o
1g,a,8,8,a,c,u,s,1a,3um,g,y,m,2l,1s,6c,3w,2k,ak,18,1o,1k,18,1g,1u,1e,2q
1k,c,a,a,a,e,12,1a,1y,3uu,26,14,17,q,1k,3i,1z,17,am,12,1c,16,y,k,u,k,u
16,a,8,6,8,8,m,m,w,3wy,2,r,o,10,a,4u,68,4y,9i,18,y,1k,1m,1e,2k,20,2o
1e,c,8,8,a,e,y,y,1o,3us,18,14,q,39,1o,4a,2y,14,am,18,1s,1o,1w,14,2a,1s,2g
1e,c,8,8,a,a,u,u,18,3u4,m,10,o,29,10,5s,3g,4o,am,14,1o,1m,18,1k,20,1e,2s
1g,a,8,8,a,a,s,u,1k,3uy,22,16,q,r,u,62,3c,45,ac,12,w,1e,1e,s,1q,u,16
1a,c,6,8,6,a,o,o,y,3te,28,o,10,12,19,6s,3s,84,ae,q,w,1i,1k,1a,2i,1e,1i
1a,a,8,6,8,8,o,m,y,3um,1u,c,12,12,p,64,50,5m,aa,q,y,1e,1k,18,2k,1g,1q
1k,c,a,a,a,e,14,16,26,3uu,1w,y,1b,18,1w,3g,b,4t,am,1a,1k,1a,s,m,m,u,m
1w,k,i,k,k,q,26,28,3k,3wg,1k,2b,t,n,1o,2d,0,y,bk,1m,s,g,8,0,c,g,e
1e,c,8,8,8,a,q,u,1e,3uw,24,16,w,p,w,6c,3a,2t,ac,10,y,1a,1g,s,1w,w,18
1q,e,c,e,c,i,1i,1c,28,3z6,1c,1d,2p,4c,o,3r,1u,2o,bq,1y,1c,q,k,g,1o,16,q
1q,e,c,e,c,i,1i,1c,28,3z6,1a,1b,2p,4a,s,3p,1q,2o,bq,1y,1c,q,k,g,1o,16,q
1o,e,a,g,a,g,16,1k,24,3xw,1k,13,27,54,3e,q,7b,1o,bg,20,1c,q,e,8,1m,o,m
1u,e,i,i,i,m,20,22,3g,3x0,34,3f,5,u,26,4v,9,31,bk,1y,o,g,6,4,q,4,i
1y,q,i,i,m,s,26,28,3s,3zg,55,e,1s,f,1h,1z,1m,3r,ci,1k,e,e,8,6,m,2,i
1e,a,8,8,8,a,o,q,12,3tm,y,12,y,1,11,9c,4q,3s,ac,y,1c,1k,1a,1g,20,1a,1g
14,a,8,6,6,8,k,k,s,40k,h,h,u,16,10,4s,68,44,8w,1o,10,y,1i,1m,2g,1k,20
1e,c,a,8,a,c,y,y,1m,3v2,u,12,g,3f,2a,50,2i,1k,am,1e,1q,1k,1q,12,22,1e,2s
1k,c,a,a,a,e,12,1a,22,3um,28,16,17,i,2a,36,1j,2t,am,14,1e,1e,s,k,o,q,o
18,c,8,6,6,a,o,m,10,3ua,1i,2,10,12,13,6k,5m,2w,aa,o,y,1k,1k,1a,2s,1k,2a
1o,e,a,e,c,g,1c,1c,22,3ym,16,f,2z,2g,2y,r,f,1m,bk,1u,1k,u,o,m,1i,16,s
22,q,k,k,o,u,2c,2c,42,3xo,4t,1m,w,13,19,15,22,4l,cm,1c,g,c,8,6,k,4,k
1i,c,8,8,a,c,w,y,1s,3ui,1w,1a,8,j,o,50,2s,4l,ae,14,1a,1c,16,s,18,u,u
1k,c,a,a,c,c,10,14,20,3vq,1g,m,23,7,2s,5a,1w,63,aw,1g,1k,1e,w,m,16,s,w
1k,c,8,a,a,c,y,12,1w,3va,28,1a,n,e,1m,k,2i,2z,ai,14,16,y,y,q,1e,o,s
1e,c,8,6,a,c,s,w,1a,3te,14,14,s,2j,u,6m,1c,5i,aq,y,1m,1o,1g,1i,22,1g,2o
26,u,k,m,q,w,2i,2i,4e,3uq,1x,1c,3,21,r,4,2c,59,co,12,i,c,8,c,e,6,k
1i,c,8,8,a,c,w,y,1s,3ug,18,10,5,r,1g,7e,1y,5j,ai,18,1m,1e,14,10,14,16,w
18,a,8,6,6,8,m,k,u,3zm,12,16,y,12,o,3w,6i,42,a0,s,10,s,1a,1e,1u,1c,1w
1m,e,a,c,c,e,14,1a,20,3xi,1c,0,2n,1c,3w,3k,25,1v,bc,1q,1i,14,m,i,1e,u,s
1u,i,i,i,i,m,20,22,3i,3y4,6,2x,1w,i,s,4f,q,3r,by,1u,o,a,a,4,s,0,m
16,c,6,8,6,8,m,k,w,3xe,12,5,16,12,i,4y,6w,2g,9o,16,y,16,1m,1c,2c,1m,22
1c,c,8,8,8,c,u,w,1e,3te,w,16,w,23,9,6s,20,1m,ag,12,1k,1u,1m,1m,28,1m,2k
1u,i,k,g,i,m,20,22,3g,3y8,8,39,28,i,q,4d,m,3n,by,1u,q,a,a,4,s,0,k
1g,c,a,8,c,e,10,12,1s,3vo,1c,y,f,2h,2y,58,1k,8,aw,1e,1u,1e,1m,w,20,1c,2k
40,18,y,y,10,18,3g,3i,5e,3pg,3p,0,3,o,w,2a,10,j,f2,c,8,8,8,a,w,m,s
3u,18,w,y,y,16,3a,3e,5c,3qi,3r,j,2,u,s,1w,1a,p,f0,e,8,8,4,a,w,k,s
3k,y,u,u,w,12,2w,2w,4e,3ss,15,1r,z,b,a,1o,1m,10,f0,g,e,6,4,8,w,i,o
3s,14,u,y,w,16,34,38,52,3re,2v,r,t,c,o,2e,1a,d,f2,g,a,8,4,a,w,k,s
44,12,y,10,10,18,3k,3m,5i,3pq,11,j,f,4,3,12,4,o,f0,k,8,8,a,e,12,q,w
3s,16,u,y,y,14,38,3a,56,3r2,35,t,f,k,s,20,1e,l,f2,e,8,8,4,a,y,i,s
4c,1e,12,12,16,1c,3u,42,6m,3p0,45,13,n,m,18,2y,1w,2u,f2,e,e,c,c,s,1c,12,14
3s,12,s,y,y,14,34,36,4y,3ri,1j,l,z,3,5,24,16,2,f2,k,a,a,6,c,u,o,w
2y,o,i,o,o,w,26,26,36,3ww,10,1,25,21,z,e,a,e,ec,q,c,a,4,4,4,8,u
4c,16,12,12,14,1c,3q,3w,64,3p6,l,2v,3,p,e,2g,k,1e,f0,i,c,8,e,q,10,w,u
3o,18,w,y,y,16,3a,3e,5a,3qs,47,p,g,e,e,2a,q,8,eq,8,8,8,4,a,i,w,y
2u,m,k,i,o,u,26,26,36,3wk,1g,v,i,25,21,l,h,o,e8,q,c,a,6,4,2,6,a
3s,1a,w,10,y,18,3e,3g,5g,3ps,3t,p,e,i,o,1s,1k,f,es,8,6,a,4,a,s,q,u
38,s,k,q,q,w,2g,2a,3c,3vk,l,o,29,a,1l,8,16,1w,ey,m,c,8,a,4,s,i,o
3a,s,m,q,q,y,2i,2c,3i,3v4,l,s,2f,4,19,b,1g,20,eu,k,c,6,c,4,o,i,m
3o,10,s,u,y,12,32,34,4s,3qe,b,z,a,7,10,2i,28,1o,f6,k,c,a,a,e,m,g,o
3c,s,m,q,q,y,2k,2e,3g,3v0,p,6,1r,2,11,0,1g,2a,ey,k,e,6,a,4,s,k,k
42,18,w,10,10,18,3g,3i,5e,3pi,39,2,9,8,k,2w,q,5,f4,e,a,8,8,a,w,o,u
3q,10,u,u,y,14,36,36,4y,3q4,d,15,e,1,i,2c,1s,18,f8,i,c,c,8,c,m,i,q
32,q,i,o,o,y,28,26,36,3wc,4,s,2d,t,21,y,e,1w,eq,o,c,a,a,4,g,m,i
3i,w,q,s,u,10,2s,2m,3y,3ti,b,1f,r,v,l,1q,1y,1w,f2,k,e,a,8,e,m,m,q
3m,w,s,w,u,12,2u,2w,4c,3sw,t,1l,19,h,1,24,1m,16,f2,i,c,a,6,6,w,k,q
3w,14,w,w,10,18,3e,3e,56,3pc,15,1f,e,2,5,2i,14,s,f6,i,a,a,8,e,m,m,o
3w,16,y,y,12,18,3g,3g,5c,3p2,23,1h,a,c,4,2c,18,k,f0,c,a,a,6,a,u,m,q
38,s,k,s,q,y,2g,2c,3e,3v6,f,s,2p,2,1f,b,18,26,eq,k,a,8,a,4,k,k,m
36,q,k,s,m,y,2e,28,3e,3vo,7,14,2r,8,1z,7,12,1m,es,k,c,8,a,4,i,k,k
34,o,k,o,o,y,28,26,3a,3w8,2,y,2j,b,2b,o,o,1s,eq,o,c,a,a,4,k,k,g
38,s,m,q,o,y,2g,2a,3c,3ve,r,k,27,a,1h,o,16,24,f0,m,e,a,c,a,m,o,o
32,o,k,o,o,w,28,26,36,3we,6,q,2b,11,1t,w,8,1y,eo,o,e,a,8,4,g,m,i
3o,14,w,y,y,14,36,3a,56,3ra,43,z,4,e,m,2i,q,0,eq,a,a,6,4,8,i,y,u
3u,14,u,y,y,16,36,3a,54,3r2,2f,x,h,2,c,2k,18,j,f4,g,8,a,6,a,u,o,s
48,1e,10,14,14,1e,3u,40,6o,3p8,4h,p,t,6,y,34,26,2g,f0,e,e,a,e,s,1a,12,14
3u,1a,y,w,10,18,3e,3g,5i,3ps,45,l,k,o,w,1k,1k,l,es,a,8,8,6,8,w,o,s
44,18,y,10,12,18,3g,3k,5a,3ok,2l,5,9,4,s,3a,i,o,f6,c,c,8,8,a,w,m,s
4c,1e,12,14,14,1c,3u,42,6m,3oq,3r,1f,n,s,1g,2w,1s,2u,f4,e,e,c,c,s,1a,12,14
2w,m,k,m,q,u,26,26,38,3x4,16,j,1j,2f,t,6,3,c,ea,s,c,a,4,2,4,6,o
2y,m,k,m,q,u,26,26,38,3x2,10,9,1r,2b,v,a,2,g,ec,s,c,a,4,2,6,6,u
34,q,k,q,o,y,2a,28,3a,3vw,0,12,2x,2,29,8,m,1w,eo,k,c,a,8,4,g,k,i
3k,y,u,u,w,12,2w,2y,4g,3so,17,1t,11,9,e,1i,1k,y,ey,g,c,6,4,8,u,k,o
2w,m,k,k,o,w,26,26,38,3wy,1a,p,x,2f,1b,c,f,w,ea,s,c,a,6,2,2,6,i
3w,16,y,y,12,18,3g,3i,5e,3p4,29,1r,g,a,c,20,1g,i,ew,c,a,8,8,8,u,m,u
4a,1c,10,12,14,1e,3s,3y,6m,3p2,3r,1j,3,c,k,34,1s,1y,f0,g,c,a,c,s,14,12,10
4c,18,16,10,14,1e,3q,3y,6i,3oo,v,3h,c,4,8,3q,10,12,f2,g,e,a,c,s,10,w,y
3s,18,y,y,10,18,3g,3i,5i,3ps,2x,1d,g,i,a,1k,1a,2,eq,8,8,8,4,a,m,w,y
44,16,10,10,12,1c,3q,3u,60,3pm,1x,1z,j,d,d,1k,q,1y,ey,k,a,8,c,g,18,w,u
3i,y,s,q,u,10,2s,2w,4i,3rc,d,t,c,a,y,2y,32,2a,f8,k,e,c,c,g,k,c,s
44,16,10,10,14,1c,3q,3u,66,3pm,29,23,p,b,3,1u,14,1u,ey,k,a,8,c,i,18,w,w
4c,14,12,10,12,1c,3q,3q,5q,3pc,h,1z,3,1,9,1m,e,1i,f0,k,c,8,e,m,10,q,u
3o,10,s,u,y,12,32,36,4u,3qc,d,l,e,n,1i,26,1w,16,f6,k,c,c,a,e,k,g,q
40,18,y,y,10,18,3e,3i,5g,3pk,3h,0,5,e,q,2o,w,j,f4,c,a,8,8,8,y,m,u
3u,y,w,w,y,16,38,3c,56,3r6,5,27,r,0,1,12,q,a,ey,k,c,a,6,e,16,q,w
32,o,k,o,o,w,28,26,36,3wc,4,k,2b,11,1t,w,o,20,eo,q,c,c,8,6,m,i,i
3i,w,q,u,u,12,2q,2q,44,3ti,r,1j,11,j,3,18,1o,1o,f0,g,e,6,6,8,s,k,m
42,18,w,10,10,1a,3g,3k,5e,3pk,2f,5,7,4,2,2m,g,3,f6,g,8,8,a,a,w,q,w
32,o,k,o,o,y,28,26,38,3wa,e,q,2t,f,27,m,2,1u,ek,m,a,a,8,4,a,k,k
3s,18,y,w,10,16,3c,3e,5e,3qc,49,p,k,u,u,1m,1e,l,eu,a,8,8,4,a,s,q,s
42,1a,w,10,10,1a,3g,3i,5e,3os,3f,8,b,m,10,2k,s,8,f2,a,a,8,8,a,w,m,s
46,18,10,12,12,1c,3u,3u,62,3pe,1z,1p,h,f,9,1u,q,1y,ey,k,a,8,c,o,12,w,u
46,18,y,10,12,18,3i,3i,5a,3om,2r,6,d,0,u,3a,c,w,f6,c,c,6,c,8,w,o,s
36,q,k,o,q,y,2a,2a,3a,3vq,d,u,2j,d,25,1o,y,1s,ey,o,c,c,c,a,o,k,q
2u,m,k,k,o,q,22,20,30,3w4,1i,1f,6,1b,d,9,1w,u,e2,m,a,a,6,6,c,a,g
2u,m,k,k,m,u,28,24,38,3w4,1a,15,4,1v,21,3,b,u,ea,q,e,c,8,6,6,c,q
2w,m,k,m,q,w,26,28,36,3w4,s,p,1h,2h,v,u,1,28,ei,s,c,c,6,6,a,q,k
3i,u,s,s,w,14,2w,2u,4c,3ta,h,1j,1n,r,19,18,1y,1s,f2,k,g,c,8,o,w,s,y
2q,m,i,k,m,q,26,26,34,3w0,1q,1d,g,1l,17,1j,l,o,ea,o,e,c,a,6,a,c,o
30,o,i,o,q,u,28,26,38,3wa,c,2,21,21,13,14,a,22,em,q,c,c,8,6,e,o,k
2u,m,k,i,o,s,28,24,36,3wc,16,19,g,1h,1r,11,7,4,e8,s,c,a,8,8,4,8,q
2w,m,m,m,s,u,28,28,38,3vm,e,x,1p,2n,0,e,1a,2i,eq,o,e,c,6,6,s,g,o
2s,o,k,k,m,s,20,1y,2s,3vc,1i,15,e,11,b,6,1y,2u,dy,m,c,8,8,6,e,a,i
2u,m,k,k,o,q,22,20,30,3vy,1i,19,6,19,f,1,1g,12,e2,m,c,8,8,6,c,a,i
2u,m,k,i,m,s,26,24,36,3wg,1c,1d,o,1d,r,1x,b,d,e6,o,c,8,8,6,8,6,e
3o,y,u,w,y,1a,36,36,52,3ry,b,2v,p,x,1x,1u,1w,1s,f0,m,c,c,6,q,y,u,y
2u,m,k,i,m,s,28,24,36,3wi,1a,17,s,1h,1l,1r,4,9,e6,q,c,a,6,8,4,8,c
44,1a,y,10,12,18,3g,3k,5a,3ok,2l,1,9,0,s,3c,e,u,f6,e,a,8,a,a,u,m,u
2u,m,k,k,o,u,26,26,36,3w8,18,11,5,25,1v,a,j,14,ec,q,e,c,8,4,4,g,o
2u,m,k,k,o,u,28,24,38,3w4,1a,11,3,2b,2f,10,d,16,ec,q,e,c,a,4,4,c,s
32,o,k,o,q,w,28,28,38,3w4,1,i,2d,19,1t,1g,s,24,es,q,c,c,8,8,o,k,m
2w,m,k,k,o,u,26,26,38,3wg,14,v,n,29,1t,w,p,1a,ec,s,c,c,8,4,4,g,m
3m,u,u,u,w,16,32,30,4o,3sm,d,23,1b,p,1n,1i,1w,1u,f2,k,e,c,6,q,y,s,y
2w,k,k,k,o,w,26,26,36,3wu,1a,t,7,2h,1p,a,l,s,ea,s,c,a,6,2,2,6,g
2u,m,k,i,o,w,26,24,38,3wo,1e,v,o,2l,2b,6,n,s,ea,q,c,c,4,4,0,8,a
2y,o,k,m,q,w,28,28,36,3vy,c,h,1t,2d,j,u,o,2e,eo,q,c,c,6,6,k,o,o
2u,m,k,k,m,q,26,22,34,3w0,1i,1d,e,z,v,1l,u,6,e4,q,c,8,8,6,c,6,m
2s,m,k,k,q,u,28,26,38,3vy,18,1f,x,2z,r,i,4,2g,ek,s,c,a,8,4,c,q,k
2u,m,k,i,o,u,28,24,38,3wq,18,x,0,1x,1z,7,h,s,ea,q,e,a,6,4,0,8,a
2q,m,k,i,o,s,28,24,36,3w4,1m,17,0,27,1l,j,f,1i,ee,q,e,a,a,8,4,m,k
3u,14,y,w,10,1c,3i,3k,5u,3qw,21,2v,5,g,1t,1i,20,1u,f0,i,c,c,8,m,16,w,10
2u,m,k,k,m,s,22,24,32,3vw,1g,19,8,11,f,x,q,m,e4,o,c,a,6,6,c,8,m
34,o,k,q,o,y,2a,28,3c,3vu,b,o,2l,v,1x,1s,10,1y,ew,o,c,c,c,6,s,m,q
30,m,k,m,q,u,26,26,38,3ws,k,2,1t,1z,11,k,b,1m,eg,s,c,c,6,2,8,k,i
34,q,k,q,q,w,2c,2a,3e,3vc,j,m,39,h,1n,28,1e,1y,ew,o,e,c,e,c,q,m,w
30,o,k,m,q,w,26,26,38,3w8,c,2,21,1z,13,12,c,24,em,q,c,e,6,6,i,m,k
1w,i,g,g,g,m,1q,1u,2s,3re,1q,d,1i,b,q,2,9,1o,bg,1e,q,q,k,e,o,e,k
1o,e,c,c,c,e,14,16,1q,3s6,3o,m,k,1u,16,1w,2u,1y,b2,12,10,o,o,s,18,i,w
1q,g,e,e,e,i,1m,1m,2o,3se,3i,11,1i,18,n,1l,1d,s,b8,14,16,e,q,i,s,i,g
18,c,8,6,8,8,m,m,s,3z6,o,1s,1i,10,i,3y,4q,62,9y,14,w,o,w,s,1u,i,1a
1s,g,e,g,e,m,1q,1s,2q,3s8,3k,1p,1o,g,1l,1t,5,q,ba,14,12,c,q,g,o,g,k
1s,g,g,e,g,m,1q,1s,2s,3s4,3g,1p,1o,c,1h,1t,2,s,ba,14,12,e,o,g,q,e,k
1o,g,c,c,e,g,18,18,1y,3qu,3o,6,w,1s,16,14,1i,34,b2,10,y,q,u,q,14,q,s
1q,g,e,e,e,k,1m,1q,2o,3sk,3o,19,1i,14,1l,1r,11,g,b6,16,14,a,s,i,q,g,i
16,c,6,8,6,8,m,i,s,3zq,16,1a,1i,w,g,3o,4k,5w,9k,1a,y,q,o,14,1o,o,18
20,m,i,i,k,o,1w,1u,2w,3qs,b,1o,e,8,e,14,1w,3s,bk,1i,k,s,g,k,u,k,m
1o,e,a,e,c,e,18,1e,26,3t8,3i,k,c,1o,1c,13,1f,g,ay,18,12,k,o,o,10,m,g
1g,c,8,a,a,a,u,s,14,3v6,1y,24,10,c,1u,32,4i,4o,ae,1a,q,s,u,s,1m,m,16
1a,c,8,8,8,a,o,m,w,3xo,u,20,1a,q,w,44,48,6e,a0,1a,u,o,12,m,1w,g,1a
1a,a,8,8,8,a,m,m,u,3ye,s,1w,1c,10,q,40,4g,6g,a0,16,w,o,y,o,1w,g,1a
1g,e,8,a,a,a,u,u,1a,3v0,2s,20,o,w,1w,38,2q,2i,ak,18,q,s,q,q,1m,i,12
1q,e,e,c,e,g,18,18,1y,3ri,3o,5,1c,1k,o,1e,2i,34,b2,12,16,i,u,q,14,k,u
1c,c,8,8,8,a,o,m,y,3xk,18,22,1g,m,1g,3u,4c,56,a2,1c,s,o,10,k,1w,e,1c
1c,a,8,8,8,8,o,m,u,3ye,16,24,1k,w,16,3k,3w,50,a2,18,s,m,y,m,1y,c,1e
1o,g,c,e,c,i,1g,1k,2i,3su,3m,h,12,1m,b,1p,1l,a,b2,18,12,g,s,k,s,k,g
1i,c,a,a,a,c,u,u,18,3ua,2w,1y,g,14,1w,38,40,2w,au,10,q,10,i,y,1e,k,14
1k,c,a,a,c,c,10,10,1m,3u6,3m,1y,5,1a,1a,w,a,18,b0,w,o,u,i,s,18,m,k
1u,k,g,g,k,m,1w,20,2y,3qg,1o,11,20,f,1e,1d,v,1m,bg,1c,q,o,g,6,o,g,s
16,a,8,6,6,8,k,i,q,3zw,1g,16,1g,q,k,3a,48,62,94,1k,10,q,m,18,1m,u,14
1w,i,g,g,i,k,1q,1u,2s,3re,1o,b,1o,f,q,4,5,1o,bg,1g,o,q,k,e,q,c,m
1e,c,a,8,8,c,q,o,12,3w4,1q,24,1a,c,1u,3o,4g,4q,a8,1e,q,q,y,o,1q,g,18
1y,k,g,g,k,m,1s,1w,2u,3qy,o,c,y,d,18,w,2,1w,bk,1g,k,s,i,c,m,e,o
1k,e,a,c,c,c,12,12,1s,3t8,3q,1i,3,1o,1o,1m,u,o,b2,10,q,y,g,w,18,i,s
14,c,6,8,6,8,m,i,s,3zu,6,1o,1e,12,o,32,4a,6o,90,1o,10,q,m,18,1q,s,12
16,c,6,8,8,8,m,m,s,3z2,6,1o,12,10,c,3m,4k,6q,98,1m,10,q,m,16,1s,o,18
1u,i,g,e,i,k,1q,1s,2q,3rm,2e,v,1o,2,a,l,h,1m,bg,18,w,o,k,e,q,g,i
1q,e,e,e,c,k,1i,1o,2k,3sq,3o,v,1a,1i,11,1p,1j,e,b4,16,12,e,s,k,s,g,i
1i,c,a,a,c,c,y,w,1e,3t8,2a,24,c,12,20,1q,34,4c,am,16,o,y,o,y,1e,s,12
1e,c,8,a,8,c,s,q,14,3vq,1a,28,1c,0,1i,30,4g,5a,a6,1c,s,q,10,o,1q,m,18
18,a,8,6,6,8,m,i,s,3zq,12,1e,1i,w,e,3q,4m,5w,9o,16,y,q,o,14,1o,o,18
1s,g,e,e,g,m,1o,1s,2q,3sc,3m,1l,1m,k,1r,1r,b,k,b8,16,12,a,q,i,o,g,k
16,a,8,8,6,8,o,i,u,3zc,a,1m,14,14,i,3i,4g,6s,9a,1i,10,s,k,16,1s,o,16
1a,c,8,8,6,a,o,k,w,3yg,s,20,1i,w,10,3u,4k,5m,a0,1a,s,o,12,k,1w,e,1c
1q,e,e,e,e,i,1k,1m,2m,3sq,3m,x,1c,1i,15,1p,1j,g,b6,16,12,c,s,k,q,i,i
1m,e,c,c,c,e,12,14,1m,3s4,3q,y,c,1u,1c,1q,36,34,b2,10,w,s,m,w,18,m,y
1u,i,g,e,i,m,1s,1u,2u,3rc,2i,1b,1o,b,8,x,c,16,be,16,y,m,i,c,o,c,q
1s,g,g,g,g,m,1u,1w,2u,3r2,3c,25,1o,6,3,f,3,1a,ba,12,14,g,k,e,i,g,q
14,c,6,8,6,8,k,i,s,3zy,2,1w,1g,10,m,32,4a,6m,92,1m,10,q,m,18,1o,u,12
1e,c,8,a,8,c,q,o,12,3w4,1u,22,1a,e,1u,3q,4c,4i,aa,1c,q,q,y,m,1s,g,18
1u,i,g,g,i,m,1u,1u,2w,3qy,22,15,1m,f,14,7,2,1c,bg,12,10,o,i,8,m,e,s
1m,e,a,c,c,c,14,16,1u,3t0,3s,1e,7,1s,1o,12,a,e,b2,12,s,w,g,w,16,i,o
1y,m,g,i,i,o,1u,1u,2w,3qs,8,w,s,b,w,1e,o,22,bk,1k,k,q,i,e,m,g,k
1y,k,g,g,i,m,1s,1s,2s,3r0,u,m,16,9,m,10,10,1u,bi,1e,o,s,k,g,q,g,i
16,a,6,8,6,8,k,i,q,3zw,s,1i,1i,u,k,36,4a,6g,94,1k,10,q,m,18,1m,u,14
1o,g,c,e,c,i,1g,1i,2i,3su,3k,f,12,1g,2,1n,1j,c,b2,18,12,i,q,k,u,k,g
14,c,6,6,8,6,m,g,q,400,s,1s,1c,s,k,32,44,6a,92,1m,y,q,o,18,1k,w,14
18,c,8,6,6,a,m,i,u,3zg,w,1s,1k,w,i,3w,4g,5e,9y,12,w,o,s,y,1q,k,1a
1g,e,8,a,a,a,u,s,18,3v2,2m,20,s,u,1y,3e,3c,2u,ak,18,q,u,o,q,1m,k,12
1u,i,g,g,i,m,1u,1w,2w,3qq,2e,1j,1s,9,12,5,h,1c,be,14,10,m,i,8,m,e,s
1o,e,c,c,c,g,1a,1e,2a,3t0,3k,a,k,1o,y,v,1b,i,b0,18,12,i,q,o,y,m,g
1s,g,g,e,i,m,1q,1s,2q,3su,3i,2l,10,7,13,p,g,s,b6,12,10,8,o,g,m,g,o
1q,g,e,g,e,m,1m,1s,2q,3sm,3q,1j,18,g,1r,1f,f,a,b6,14,12,a,q,i,m,i,m
1u,i,e,g,g,k,1q,1s,2q,3ro,2k,z,1q,6,8,n,l,1k,be,18,y,o,k,g,q,e,k
1q,g,c,e,e,g,1i,1k,2i,3sk,3k,r,1a,1g,3,19,1f,q,b6,16,14,g,q,k,u,k,e
1k,e,a,a,c,c,y,10,1i,3te,3a,1m,4,1m,1s,2m,2w,1s,ay,12,u,w,i,w,1a,k,y
1m,e,a,c,c,e,14,18,1y,3t8,3m,12,4,1o,1i,i,1,i,b0,14,w,s,k,s,14,i,o
1s,g,e,g,e,m,1q,1s,2s,3s6,3k,1r,1q,g,1j,1v,3,s,ba,14,12,c,q,g,q,e,m
1y,k,g,i,i,o,1u,1w,2y,3qu,i,c,10,d,1c,g,5,1u,bk,1g,k,s,g,c,m,e,q
1q,g,e,e,e,m,1m,1q,2o,3si,3o,1f,1k,12,1n,1r,x,k,b8,14,14,a,s,i,q,e,k
1y,k,g,i,k,m,1u,1w,2w,3qq,c,e,u,h,1s,1a,5,1u,bk,1k,g,u,g,a,k,e,s
1i,c,a,a,a,c,u,u,18,3ua,2q,20,i,10,1w,30,4a,3i,aq,14,q,y,k,y,1e,m,14
1q,g,e,e,e,k,1m,1q,2m,3si,3o,1d,1m,1e,1l,1p,1b,o,b8,14,16,8,u,i,q,g,i
1o,g,c,e,c,g,16,18,1y,3ry,3o,3,16,1q,q,1q,2m,1c,b4,12,14,k,s,o,14,i,u
1s,g,e,g,g,m,1q,1s,2s,3sg,3o,21,14,3,1d,1d,8,g,b6,14,12,6,s,g,o,g,m
1w,i,i,e,i,m,1q,1s,2s,3ra,1e,0,1o,n,y,g,2,1m,bi,1i,k,s,i,g,q,c,k
1o,g,c,g,e,i,1i,1k,2e,3tg,3g,11,6,w,z,d,1,i,ay,16,u,i,k,k,o,i,k
1w,k,g,i,i,m,1q,1s,2q,3qy,18,6,14,8,g,16,u,3i,bc,1a,u,s,k,i,10,m,q
1m,e,c,c,a,e,12,16,1s,3sy,3k,y,a,1q,1i,1s,1e,k,b0,14,y,q,m,s,16,i,s
1c,a,8,8,8,a,m,m,w,3y4,w,22,1i,q,16,3w,4g,5k,a0,1a,u,o,10,k,1w,e,1c
1q,g,e,e,g,k,1m,1s,2o,3sg,3o,1f,1k,10,1p,1t,r,k,b8,14,14,a,s,i,q,e,k
26,q,k,m,m,s,2e,2g,44,3y2,4p,f,19,k,1a,8,34,34,cy,1c,10,q,w,u,16,u,u
32,12,y,y,12,1a,3o,46,72,3u6,1d,1v,1t,3,d,a,1e,0,d2,u,k,k,g,k,1q,18,1a
1q,e,e,g,g,i,1k,1m,2g,3zi,1a,2d,27,1c,g,8,y,3a,bu,20,1g,12,w,u,20,1e,18
3k,1a,10,12,18,1g,4a,4w,80,3uk,2n,h,1b,n,11,1i,9,g,d6,y,q,o,i,y,18,14,1k
2k,s,q,q,s,y,2u,38,54,3tw,0,21,1j,h,2,14,b,20,cy,16,o,m,i,m,18,s,w
1s,g,g,g,i,k,1k,1q,2k,3zy,k,3p,r,m,k,q,q,4m,c2,22,1c,10,10,u,20,18,10
28,q,k,m,m,s,2g,2i,44,3x6,3n,p,13,q,q,k,38,2u,d0,1c,10,q,s,w,16,s,w
1u,i,i,g,i,m,1o,1w,2q,40g,1n,2x,f,1,q,w,y,5e,cc,1y,16,y,w,w,1y,12,s
3a,16,10,12,14,1e,40,4o,7q,3u8,1j,z,1n,j,r,18,2,d,d4,u,m,q,g,o,1k,14,1m
22,o,i,k,k,o,24,26,3e,3zu,59,d,1d,4,2a,9,2u,4s,cs,1o,10,s,14,s,1i,s,q
1q,e,e,g,g,k,1k,1m,2k,3zg,14,2f,1p,u,y,9,1i,2o,bu,20,1c,10,10,q,1y,1e,16
1q,g,g,g,g,m,1m,1q,2o,3zq,m,37,n,c,7,10,o,44,c0,20,18,10,w,q,20,1a,w
2y,10,w,w,10,18,3m,44,70,3sw,7,r,11,n,j,12,4,17,d4,y,k,m,k,m,1i,10,1k
3i,1a,y,14,16,1g,46,4u,7y,3ue,29,f,1f,n,11,1g,b,a,d8,y,o,o,k,y,16,14,1k
24,s,i,m,k,q,2a,2c,3o,3ya,4d,d,1p,w,1e,2,3g,3w,cw,1k,y,s,12,s,1c,q,s
1m,c,c,e,e,g,1e,1g,26,3yo,16,i,3p,18,20,g,a,2c,bi,1u,1q,18,16,w,1w,1e,1y
1m,e,a,e,e,i,1g,1e,26,3yy,16,c,2p,n,1q,k,1e,20,bk,1y,1o,1a,18,y,1y,1c,1s
1i,e,8,c,e,e,18,1c,20,3xu,1c,s,21,7,22,34,z,1o,ba,1g,22,1g,1e,10,22,1k,20
1u,g,i,i,i,m,1m,1u,2o,406,b,4l,2,m,3,1e,i,5k,c8,20,1a,y,y,u,20,12,s
24,q,i,m,k,o,28,28,3k,3z2,53,5,1t,o,1w,b,3g,4c,cu,1m,y,s,14,s,1g,q,s
1o,e,c,e,e,i,1i,1i,2g,3yw,1k,17,2n,1a,1g,h,1a,1q,bm,1u,1o,12,10,s,1w,1g,1g
2y,y,u,w,10,16,3i,3y,6q,3t2,l,l,1h,j,h,u,u,5,d0,12,i,k,k,k,1m,14,1a
2m,s,q,s,u,y,2y,3c,5e,3tc,2,1j,1f,n,6,10,o,1w,cy,18,m,m,i,m,1c,w,y
2c,q,m,o,o,y,2k,2s,4q,3vk,29,19,p,i,r,20,2u,1c,d0,1a,y,m,m,y,14,u,y
26,s,k,k,m,u,2e,2g,44,3xg,3z,j,17,q,w,g,36,30,d0,1c,10,q,u,u,18,s,u
28,s,k,o,o,s,2c,2u,4c,3x4,39,15,1p,5,o,20,z,2o,d0,14,y,m,k,q,18,q,q
3c,18,y,12,14,1g,42,4o,7u,3ua,1j,t,1l,n,r,w,3,0,d4,w,o,q,g,o,1i,14,1m
1g,c,8,a,c,e,12,14,1u,3wc,1g,w,3,2b,2m,3a,m,1c,au,1e,1y,1i,1s,10,2c,20,22
2y,y,s,w,y,16,3i,3q,6m,3sm,5,4,l,15,9,i,1s,1l,d4,1a,i,m,o,10,12,u,1e
22,o,k,k,k,o,26,26,3e,3zg,51,b,1j,c,1s,5,36,4y,co,1s,y,u,12,u,1i,q,q
1y,m,k,i,i,o,1y,20,32,40e,4b,1j,p,7,1q,0,2g,5i,ck,1u,12,u,12,u,1q,s,q
2m,s,q,q,s,10,30,30,5g,3t6,2,1n,e,9,f,w,3s,l,cy,1i,s,m,k,14,10,q,16
2s,u,s,u,w,14,36,3m,64,3sy,9,p,15,z,7,14,1c,e,cy,18,k,i,k,k,1k,10,18
2a,q,m,m,m,u,2g,2i,46,3wg,2t,11,11,w,g,y,3g,2o,cy,1i,y,q,y,s,18,q,w
26,q,k,m,k,q,2c,2c,3s,3yk,4r,h,1f,u,1o,7,3e,3s,cw,1k,y,s,10,u,1c,q,s
3e,18,y,12,14,1e,44,4q,7s,3uc,1j,r,1j,n,v,12,9,3,d6,y,o,q,i,s,1c,12,1k
30,12,w,10,10,18,3o,44,70,3tu,x,1p,1t,3,d,i,14,3,d2,w,i,m,i,k,1o,16,1a
28,s,m,m,m,w,2i,2m,4g,3wo,3b,z,v,i,8,14,34,22,cy,1c,10,o,q,w,16,s,w
1m,c,a,e,e,g,1g,1e,28,3yk,18,k,3d,4,3i,1b,1i,1u,bi,1s,1s,16,18,u,1y,1e,1y
30,10,u,y,y,1a,3m,46,72,3t0,7,t,13,n,h,u,8,15,d4,y,k,m,k,m,1k,10,1k
1s,g,g,g,i,m,1m,1q,2k,3zw,18,5f,8,o,5,18,i,4y,c4,1y,1g,y,10,s,22,16,w
1w,k,i,i,k,m,1s,1y,2y,40a,2d,3d,i,13,1c,12,1c,5k,cg,1w,16,s,12,u,1u,w,o
1o,g,e,e,g,k,1i,1m,2i,3zg,1i,33,1d,1c,7,q,o,3a,bs,22,1e,12,y,q,22,1e,14
1s,i,g,g,i,m,1m,1s,2m,406,7,3n,p,18,f,1a,e,56,c6,20,1a,10,w,u,20,16,w
34,14,w,10,12,1c,3u,4e,7g,3to,d,1h,1h,d,l,u,2,v,d4,w,k,q,g,m,1m,12,1m
1w,k,i,i,k,m,1s,20,2w,40a,2d,3h,c,n,16,u,1i,5m,cg,1w,16,s,12,u,1u,w,o
36,12,y,10,12,1c,3w,4e,7g,3to,h,17,1f,h,l,k,2,p,d4,w,m,o,i,o,1i,12,1m
1m,e,a,e,e,g,1e,1g,28,3yo,18,g,3r,1c,1s,o,6,2e,bi,1w,1o,18,16,w,1w,1g,1w
2u,y,s,u,y,14,3c,3o,6a,3t2,n,h,1t,l,3,e,1i,y,d0,12,k,k,i,k,1k,12,16
2y,10,u,w,10,18,3m,40,6y,3ss,3,d,x,v,f,k,e,13,d4,12,i,m,m,m,1i,y,1i
38,12,y,y,12,1c,3w,4c,7g,3ts,j,v,1d,j,r,q,4,z,d6,10,m,q,k,w,16,10,1i
2g,s,m,q,q,w,2o,34,4w,3uy,1d,1r,13,d,e,1i,i,2g,d0,16,u,k,k,o,1a,u,y
20,o,k,i,k,q,24,28,3i,3zo,53,h,13,b,22,n,32,4k,cq,1m,y,u,w,s,1i,u,q
3m,1c,10,14,16,1i,4a,4y,84,3um,2v,j,19,l,z,1o,d,k,da,w,q,m,k,y,18,14,1k
24,o,k,m,k,q,28,2a,3o,3yk,4f,f,1p,q,1i,3,3a,44,cw,1i,10,s,y,s,1e,s,s
1o,g,c,g,g,k,1k,1k,2i,3ze,u,1l,2d,16,14,p,1k,2s,bs,20,1e,12,10,q,20,1e,18
24,q,i,m,k,o,28,2a,3k,3z2,53,b,1n,u,1m,9,3i,4i,cs,1o,y,u,12,s,1e,q,s
20,o,i,k,k,o,22,24,38,400,53,f,1b,5,28,5,2o,56,co,1s,10,s,14,u,1k,q,q
1y,o,i,k,i,m,20,22,34,40i,57,h,1j,1,2e,v,34,58,co,1s,10,s,10,w,1o,s,q
26,q,m,m,k,u,2e,2i,48,3xo,49,n,15,k,u,g,36,2w,cw,1g,y,q,s,u,18,u,u
1w,o,i,i,k,m,1y,20,34,40k,4h,1j,p,3,1q,7,2i,5c,ck,1u,10,w,y,u,1q,u,q
1o,e,8,c,c,c,12,1a,20,3uq,1u,7,3d,3c,4w,5e,3r,11,bo,28,1g,1g,g,e,w,s,q
1i,a,8,6,8,a,o,o,y,3v2,3,1i,14,5,2q,8m,3w,3u,bo,28,1c,10,1a,s,1m,1a,2a
1m,c,a,a,a,c,y,10,1u,3tq,1o,w,2r,20,3s,5k,20,3t,bm,1w,1m,1e,14,i,14,10,1c
1i,a,8,8,8,a,o,o,10,3ve,c,1e,12,n,2w,84,4a,3c,bq,28,1e,y,16,q,1m,1c,28
1m,c,8,a,8,c,u,y,1m,3ts,20,14,15,w,3u,6g,7,39,bq,22,1c,16,1e,k,10,14,w
1i,a,8,8,8,a,o,q,12,3ui,6,1k,18,z,2y,8c,30,3e,bm,22,1e,12,18,s,1s,18,2e
1i,a,8,6,a,a,q,s,18,3ts,g,1m,1c,19,2g,84,2i,1s,bg,1e,1y,16,18,y,1y,1c,2e
1i,c,6,8,8,a,o,q,10,3uy,a,1g,12,z,30,8c,42,38,bo,26,1c,10,18,s,1o,18,2c
1m,c,8,8,a,c,s,w,1m,3ua,1u,18,z,u,3c,66,1k,3h,bq,26,1a,12,1c,o,12,10,y
1m,a,8,8,a,a,s,u,1i,3w0,1k,1a,j,18,3g,4i,1m,3x,c0,2e,16,y,w,u,1g,10,w
1m,c,8,8,a,a,u,w,1k,3to,1o,16,19,s,3i,78,2e,3l,bo,24,1a,18,1c,q,12,12,1e
1k,a,8,8,a,a,o,s,1a,3vu,16,1c,4,e,3a,6k,2m,1h,bw,2g,16,y,y,q,1q,14,1m
1o,a,8,a,a,c,w,12,1o,3v6,1y,12,19,1k,2s,46,1f,1d,bw,26,1c,y,14,k,y,q,g
1w,k,i,i,k,q,1u,24,3m,3za,z,2v,e,23,15,20,h,u,ce,24,q,m,e,i,18,u,m
1k,a,8,8,8,c,q,s,16,3tk,q,1m,q,13,3g,78,30,38,bk,1u,1i,14,1a,14,1m,10,2m
1k,a,8,8,a,a,s,u,1a,3t8,14,1i,k,z,36,60,36,24,bi,1s,1k,14,1g,10,1m,12,2i
1s,c,e,e,c,k,1k,1i,2k,3x8,26,2b,h,3m,1p,p,y,1c,bu,2c,1a,o,g,k,12,12,k
20,o,i,k,o,q,22,26,3q,3zu,5d,s,p,2t,f,12,q,m,cu,1u,e,g,g,g,s,i,m
1u,g,i,e,i,o,1q,1s,30,3ya,18,39,18,b,2n,s,w,1k,c6,2c,u,i,c,k,14,s,k
1w,i,k,g,i,q,1w,1w,36,3yq,n,2r,2m,z,2n,1p,1y,a,cc,2c,i,c,c,c,y,c,m
20,q,i,m,m,s,24,2i,46,404,6f,o,1z,1f,8,1u,z,a,cq,1s,m,m,g,i,y,s,o
1i,a,8,6,8,a,o,q,14,3ua,4,1q,1c,r,2k,8i,3g,28,bk,1m,1u,14,16,w,1s,1a,2c
1m,c,8,8,a,a,s,u,1o,3v2,1u,18,t,12,38,5i,2e,5l,bu,26,1c,y,18,q,14,y,w
2c,u,m,o,s,10,2k,2q,4g,3sq,1i,19,2b,1f,13,1m,g,11,de,o,k,k,g,e,c,6,16
1k,c,8,8,8,c,q,s,18,3ts,12,1c,5,d,3i,6q,4u,q,bm,24,1a,12,1e,12,1a,12,2e
1o,c,8,a,a,e,y,16,1q,3vm,1w,w,17,1c,2e,2g,1n,4,by,28,1a,y,y,e,y,g,e
1k,c,8,8,a,c,u,u,1g,3t4,1c,1c,h,2,3k,5s,44,1n,bk,1u,1k,16,1i,u,18,12,2a
1o,c,8,a,8,e,y,16,1m,3wu,1s,10,13,1u,w,24,3d,32,c4,2c,16,w,w,i,12,a,g
1u,e,e,e,e,i,1o,1q,2k,3wq,2k,33,1u,24,y,4d,15,w,c0,2g,10,c,c,6,18,a,i
1o,c,8,a,c,c,y,14,1u,3tw,1y,m,1t,1w,40,4o,1h,1j,bq,24,1e,1c,y,c,w,q,o
2e,u,o,q,s,y,2q,2y,4w,3uc,f,1j,29,1b,3,i,e,5,dc,q,m,m,e,g,q,o,u
1g,a,8,8,8,a,o,q,16,3u6,2,1m,1e,n,1y,8g,3k,s,be,18,26,18,16,y,1w,1g,2g
1k,a,8,8,8,a,q,q,14,3ua,k,1i,m,11,3e,7m,3o,3k,bm,24,1c,10,1a,y,1m,14,2g
1g,a,8,8,8,a,o,o,14,3uc,1,1q,1c,b,20,8k,3o,1o,bg,1c,22,18,18,w,1s,1i,2e
1w,k,k,g,m,q,1u,20,3e,3z6,2b,13,10,2t,1l,1a,w,1a,cg,2a,g,g,e,g,y,m,k
1m,c,8,8,a,a,u,w,1i,3ti,1i,16,17,o,3q,6w,2m,27,bm,22,1e,18,1c,q,14,14,1k
1m,c,8,8,a,a,u,s,1m,3um,1u,18,t,w,36,5y,2y,5d,bs,26,1a,10,1c,q,12,10,10
2e,u,m,q,s,y,2o,2u,4k,3tm,c,1b,2b,1d,j,y,e,9,de,o,k,m,e,g,i,e,10
2g,u,o,q,u,y,2s,30,56,3ug,n,1p,25,15,0,c,g,l,da,s,m,k,g,g,u,q,u
1o,c,8,a,a,a,y,10,1u,3to,1s,w,23,26,3u,56,10,3t,bo,24,1e,1g,y,g,10,u,y
26,s,k,o,o,w,2c,2k,42,3xo,5j,26,33,13,t,1a,k,z,de,12,e,g,g,e,c,4,12
28,s,m,o,q,w,2g,2k,4a,3vw,2t,m,2p,1f,h,16,g,h,dg,u,i,i,g,g,e,a,y
1k,c,8,8,8,a,q,q,1e,3vm,1c,1e,7,g,3e,5w,50,4j,bw,28,1a,w,12,y,1g,12,1o
1m,a,a,8,a,c,y,y,1o,3tg,1m,16,23,10,3k,4m,32,1x,bi,1q,1q,1e,1c,o,18,12,1y
1u,e,g,e,e,o,1o,1m,2o,3xq,2e,47,26,1y,35,15,1a,2i,c0,2g,10,e,e,c,16,s,i
22,s,k,o,m,u,2a,2q,4i,3zi,63,6,29,z,c,1s,11,u,d0,1e,q,k,g,k,y,u,q
1q,c,8,a,c,c,12,1a,1y,3u4,1y,4,1j,28,3y,3y,2z,t,bq,28,1e,18,q,8,u,k,i
1m,c,8,a,a,a,w,w,1o,3ta,1k,12,1h,y,3y,5u,2s,2p,bm,1u,1k,1a,1e,m,14,14,1k
1o,c,8,8,a,c,y,16,1m,3ww,1s,10,15,22,24,18,31,1w,c4,2e,14,y,u,m,10,e,e
1w,k,i,i,k,q,1u,28,3o,3ze,1v,1p,n,1d,l,20,11,1g,ce,20,w,m,e,m,1a,y,k
1o,a,8,a,a,c,w,10,1q,3vc,1y,12,19,1m,2u,40,15,1v,bw,28,1a,10,12,k,10,q,g
1o,c,8,a,a,e,y,16,1m,3w4,1u,10,17,1g,1e,2c,2l,2o,c0,2a,18,w,y,e,12,c,e
1u,e,e,g,e,k,1o,1o,2k,3x6,2i,3v,2c,2m,t,4d,e,1q,c0,2g,y,i,6,8,1c,g,i
1m,a,8,8,a,a,s,s,1g,3ua,1i,1a,h,6,3e,6u,4e,3h,bq,24,1a,12,1c,w,14,14,1m
1q,c,a,a,c,e,14,1a,22,3uu,22,1,j,u,3c,24,25,1f,bw,28,1c,u,s,6,q,g,e
1o,c,8,a,a,c,y,16,1s,3tk,1u,s,2x,2i,4g,6a,2l,l,bm,22,1i,1m,u,c,10,w,u
1o,c,a,c,a,e,16,1a,24,3va,1s,6,3d,2i,46,34,2l,1,bm,1y,1m,1g,q,i,14,12,y
1o,a,8,a,a,c,w,y,1q,3ui,20,10,1d,1m,30,58,l,2f,bs,26,1c,14,14,k,y,u,m
1o,e,a,c,c,c,1a,1c,22,3vk,1w,h,2h,28,4s,y,1b,6,bo,26,1i,16,o,c,14,y,s
1m,a,8,8,a,a,s,s,1g,3ty,1i,18,j,4,3a,74,4q,3r,bo,24,1c,10,1g,w,14,14,1o
1m,c,8,8,a,a,u,10,1o,3w4,1s,18,11,1u,32,3w,1l,3f,c0,2a,18,y,12,o,16,s,g
20,o,i,k,m,s,20,28,3m,3zq,57,s,h,2x,j,w,u,k,cs,1w,e,g,g,e,s,i,m
22,s,k,m,o,s,2a,2s,4g,3zc,59,r,1l,z,m,1u,1f,2w,d4,18,u,k,i,m,12,y,o
1i,c,8,6,a,a,s,u,1a,3tc,y,1k,w,1b,2u,6q,2c,2i,bg,1k,1s,16,1c,12,1s,14,2i
1k,c,6,8,a,c,s,u,1e,3t6,16,1g,8,n,3a,5m,3u,q,bi,1s,1m,14,1g,10,1g,12,2i
1m,c,8,8,a,a,u,w,1m,3v4,1u,18,x,16,38,5g,1a,4v,bu,28,1a,y,18,q,12,y,s
1m,c,8,a,a,c,y,12,1u,3tu,1o,12,37,1y,3c,5i,1q,2j,bk,1o,1u,1i,12,k,18,10,1k
1i,a,8,6,8,c,o,q,16,3u0,a,1q,1e,z,2e,8e,32,1u,bi,1e,1y,16,18,y,1u,1c,2c
1k,a,8,8,8,a,q,o,12,3v2,q,1g,i,t,38,7c,4w,38,bq,28,1a,y,16,u,1m,16,2c
1s,e,c,e,c,g,1g,1k,2g,3vq,2a,2l,1o,48,1e,1r,3p,0,bu,2g,1a,k,2,4,1c,e,c
1s,e,c,e,c,i,1k,1i,2c,3wo,2c,2t,10,3o,1q,4t,z,30,bu,2g,16,q,6,6,1g,m,g
1s,c,c,e,c,e,1e,1k,28,3w0,26,1t,f,3i,38,13,3f,1k,bs,2e,1c,s,a,8,18,i,i
1y,k,k,i,m,q,1w,28,3o,3zo,33,1d,6,2z,l,1u,5,w,ck,22,k,k,e,g,12,q,m
26,s,k,o,q,u,2e,2k,4c,3y2,53,s,2r,1h,a,y,c,1,de,y,k,k,g,g,o,i,q
2e,u,o,q,s,y,2s,30,5c,3vc,1h,1t,1z,13,4,m,c,d,da,u,o,k,e,i,y,s,u
2a,s,m,o,s,w,2k,2m,48,3u4,d,3,2p,1b,1b,1s,i,1n,de,s,i,i,g,c,c,2,16
1k,c,8,8,8,a,s,q,18,3u8,12,1c,1,f,3c,74,54,3,bo,26,1a,10,1c,y,1c,14,26
1k,c,8,8,a,c,w,y,1k,3ta,1k,18,15,1,3q,3y,2s,q,bg,1o,1q,1c,1g,u,1e,12,2a
1k,c,8,8,8,a,q,q,1a,3va,18,1e,1,2,3c,6i,5i,2x,bs,28,1a,y,14,y,1e,12,1w
1m,a,8,8,a,a,s,s,1o,3vs,1o,1e,n,18,3e,3y,3w,75,by,2a,18,y,12,w,18,10,u
1y,q,i,k,m,q,22,2c,3w,408,5r,c,17,23,7,1u,f,g,co,1y,i,k,g,i,y,o,o
1m,c,8,a,c,a,10,14,1u,3u0,1s,w,3j,1y,48,46,q,1l,bk,1o,1s,1m,12,i,1a,14,1g
1q,c,a,e,a,e,18,1e,24,3ve,20,11,2d,3q,4a,2i,35,b,bo,2c,1g,16,c,a,12,q,m
1k,c,6,8,a,a,q,o,16,3v8,12,1g,2,7,3c,6s,5a,n,bs,28,1a,w,16,w,1i,14,22
1k,a,8,8,a,a,o,q,16,3vo,12,1e,8,1,3c,6s,48,p,bu,2c,18,w,12,s,1o,14,22
1q,e,a,c,c,e,16,1g,26,3uw,20,19,t,3k,40,2e,51,b,bq,2c,1e,10,e,6,10,k,i
1i,c,8,8,8,a,q,s,14,3u4,k,1i,o,z,3e,7i,3i,3a,bm,22,1c,12,1a,y,1m,14,2i
2e,u,o,q,s,y,2q,2y,52,3uo,r,1n,25,1b,0,k,g,l,dc,q,o,k,e,i,s,o,u
1o,e,8,c,a,c,12,18,20,3u6,1u,2,2l,38,4e,48,27,1t,bo,26,1i,1e,m,a,y,o,o
20,q,k,k,m,q,26,2m,46,3zi,4r,v,11,1n,10,22,1x,3g,cw,1i,u,k,i,o,16,w,o
1k,a,8,8,8,a,q,o,12,3uw,o,1g,m,z,3a,7i,4o,3c,bo,28,1a,10,16,w,1m,14,2e
1o,c,8,a,c,c,10,16,1s,3vm,1w,q,13,16,2e,26,1h,2,by,28,1a,w,10,c,w,e,e
1u,g,i,e,g,m,1u,1s,2u,3xk,24,3r,2s,16,21,3z,14,e,c6,2e,q,c,8,a,14,a,m
1k,c,8,8,8,a,q,q,1e,3vq,1c,1e,7,m,3e,5s,4o,59,bw,2c,16,y,10,w,1i,12,1k
1m,c,6,a,8,c,s,s,1m,3v8,1q,1a,p,10,38,5e,3k,6d,bu,28,1a,y,16,u,14,10,y
1s,c,e,e,c,k,1i,1k,2g,3x2,2a,2j,1,2w,b,13,5,2e,bu,2c,1a,o,g,g,14,y,k
1q,c,c,c,c,g,1e,1g,2e,3wg,1w,z,2d,3a,1w,1a,1z,c,bo,26,1i,14,k,k,12,14,q
24,s,k,m,o,u,2c,2k,4e,3zo,6r,w,2p,15,4,1k,1,3,d8,18,k,k,g,i,s,o,q
1m,a,8,8,a,a,s,o,1i,3ve,1i,1e,f,o,3e,5c,5g,6f,bw,26,1a,y,14,w,1c,y,1c
1w,g,i,g,i,q,1s,1u,30,3ye,a,3f,2q,r,2x,h,1m,1c,c8,2e,o,c,e,e,10,m,i
1s,e,g,e,g,m,1o,1m,2y,3xy,20,3b,i,y,1l,b,1s,e,c0,2a,14,o,g,i,18,10,k
1q,c,a,c,c,c,16,1c,24,3u8,1w,p,1d,2w,4u,34,3l,1t,bq,2a,1g,18,c,6,u,k,g
1i,a,8,6,a,a,m,o,w,3v8,4,1e,12,8,32,7o,3y,4e,bs,2g,1a,w,16,q,1i,16,2c
1k,a,8,8,a,a,o,s,1c,3uw,1c,1a,8,16,3q,6q,2k,27,c0,2k,14,y,y,k,1g,12,y
1o,a,a,8,a,c,10,14,1q,3wm,1s,u,11,2c,2u,a,11,c,c8,2k,14,u,o,m,18,a,e
1o,c,8,a,8,c,12,14,1m,3x0,1u,y,15,24,1w,4,29,2e,c4,2g,14,w,u,k,y,e,e
1q,c,a,a,c,g,1c,18,1w,3xw,1q,b,0,1k,x,1z,2e,1g,ce,2k,10,k,q,a,m,0,o
1s,c,a,a,e,g,1c,16,1w,3yk,1s,l,a,2f,1m,p,30,o,ca,2i,y,k,m,e,a,8,u
1o,a,a,8,a,c,10,16,1o,3wm,1s,s,v,2a,2q,g,1l,y,c8,2k,14,u,o,m,16,a,e
1u,c,c,e,g,k,1i,1i,2i,3ym,1o,21,b,1b,5,28,1q,1t,cm,2e,s,g,e,2,8,2,12
1s,c,c,c,c,k,1g,1e,28,3xi,1q,1f,b,y,h,14,14,4,cy,28,w,g,k,a,2,2,u
1q,c,8,a,c,e,1a,18,1s,3xq,1s,0,5,1u,b,25,1k,1y,cc,2k,10,o,q,e,m,4,m
1q,a,a,a,a,e,16,18,1q,3xi,1s,a,h,2c,m,25,i,20,ca,2k,12,o,u,e,q,4,m
1s,c,c,c,g,g,1g,1c,26,3yo,1q,17,4,2j,4,1q,24,d,cg,2i,u,k,e,c,8,6,w
1s,c,a,c,g,g,1c,1a,20,3yo,1s,v,2,2n,s,10,2a,8,ce,2g,w,m,g,e,8,a,s
1s,c,a,c,e,k,1c,1c,22,3yi,1s,15,k,25,1f,1q,2u,1,cg,2i,w,k,g,c,c,4,s
1k,a,8,8,a,a,o,q,1a,3vo,12,1a,8,o,3e,6g,2u,1b,by,2i,16,y,y,o,1o,14,1k
1i,a,8,8,8,a,o,o,10,3v4,i,1c,y,c,3c,7a,48,28,bw,2i,16,w,14,m,1k,16,1y
1m,c,8,8,a,a,y,12,1m,3wg,1q,12,11,24,3i,1i,25,i,c6,2i,14,w,s,o,1c,i,e
1m,c,8,8,a,a,u,y,1o,3vw,1q,10,t,22,3k,3g,n,1v,c4,2k,14,y,s,i,1i,q,k
1u,e,c,g,i,k,1o,1q,2w,3yc,18,2r,v,1,a,3a,g,2r,cy,28,o,c,a,2,5,2,14
1s,c,c,c,e,k,1i,1g,2a,3xs,1q,1p,2,0,9,w,1a,6,cu,2c,u,g,k,4,6,4,m
1o,c,8,c,a,c,16,18,1s,3x4,1s,c,l,22,1w,1j,8,16,cc,2k,12,q,o,k,u,4,g
1o,c,8,a,8,c,14,16,1o,3wy,1s,w,15,2c,38,1t,1t,2e,c8,2k,14,s,q,o,y,a,c
1w,c,e,g,i,m,1q,1s,30,3y6,w,2l,n,4,10,2g,8,2d,d6,20,m,e,8,0,3,4,q
1k,c,6,8,a,a,q,q,1e,3vk,1a,16,0,12,3i,5y,2s,31,c0,2i,16,y,y,k,1m,14,1c
1q,a,a,a,a,e,18,16,1o,3xq,1s,e,j,1q,d,1j,12,2o,c8,2i,14,o,s,g,m,8,m
1o,c,8,a,a,a,12,18,1q,3wm,1u,w,x,2a,34,4,2x,20,ca,2k,14,u,m,k,14,6,e
1m,c,8,8,a,a,w,10,1o,3wg,1q,14,11,20,3c,1a,r,1n,c2,2g,16,w,y,q,18,m,g
1e,c,6,8,8,8,o,k,u,3uy,f,1i,16,1a,24,82,42,5q,bm,2e,1e,10,12,y,1g,18,20
1m,a,8,8,a,a,s,w,1k,3w0,1m,1a,l,1c,3i,40,y,3h,c0,2e,16,y,y,u,1e,w,s
1s,c,a,a,e,g,1a,16,1w,3yi,1u,l,i,2n,1k,h,32,m,ca,2g,y,k,m,e,c,8,u
1w,c,e,g,g,o,1q,1s,30,3xq,1a,2r,0,m,s,18,y,27,dm,1o,m,a,8,5,0,0,g
1k,a,8,8,8,a,q,o,1a,3v6,14,18,c,y,3i,6q,3k,1j,c0,2i,16,w,10,m,1i,14,1i
1g,c,8,6,8,a,o,m,y,3v6,5,1g,14,2,2o,8k,3s,4o,bo,28,1e,10,1a,s,1k,18,2e
1q,c,a,a,c,e,1c,1a,1w,3xk,1s,b,7,12,u,z,0,20,ce,2m,10,k,q,e,i,6,i
1q,c,a,a,a,g,1a,1a,1w,3x6,1u,7,3,1i,16,z,0,1m,cg,2m,10,m,m,g,m,2,i
1m,c,6,a,a,a,u,10,1k,3w0,1q,10,p,1w,3e,3g,1r,5,c4,2i,16,y,s,k,1i,s,i
1g,a,8,6,8,a,m,k,s,3ui,7,1i,18,18,2m,88,4k,72,bq,2m,14,w,10,s,1a,y,16
1e,c,8,6,8,8,o,k,u,3uw,f,1i,18,1a,26,82,48,6e,bm,2g,1c,10,10,y,1e,18,1a
1e,a,8,6,8,8,o,k,s,3v6,l,1a,18,1y,1k,7y,40,6g,bi,24,1q,10,12,10,1g,16,1s
1g,a,8,6,8,a,m,m,u,3uq,9,1i,18,12,2m,82,4c,6e,bq,2k,16,y,10,u,1c,14,16
1u,c,a,e,g,k,1i,1i,2g,3ye,1o,1z,n,l,17,2w,1s,1l,cm,2g,u,e,e,8,2,0,14
1o,c,8,a,a,e,16,18,1q,3xe,1s,g,p,2i,14,1b,13,2c,ca,2k,12,q,s,i,q,6,i
1i,c,6,8,8,a,q,m,10,3vk,k,1c,10,n,30,7k,4c,3i,bs,2a,1c,w,16,o,1o,1c,26
1u,e,c,g,g,k,1o,1q,2s,3xy,1a,29,f,2,m,1i,y,1d,d8,20,o,e,a,0,0,4,k
1g,a,8,8,8,a,m,m,u,3us,3,1i,16,q,2u,80,48,5m,bq,2k,16,y,12,u,1e,16,1g
1q,c,8,c,a,g,1a,18,1s,3xu,1s,3,1,12,13,1d,28,1u,cc,2k,10,m,q,c,m,4,o
1k,a,8,8,8,a,q,o,18,3vo,y,1a,g,c,3e,6k,3i,l,by,2g,16,y,10,o,1o,14,1u
1o,c,8,a,a,c,16,16,1q,3x0,1s,k,v,26,2i,1h,5,12,ca,2m,12,q,q,k,y,6,e
1o,a,8,a,a,a,10,14,1o,3wq,1s,y,13,28,34,6,2v,1q,c6,2i,14,w,s,o,16,c,e
1k,c,8,8,8,a,s,s,1g,3vg,1e,16,5,1c,3k,5k,2e,3p,c2,2i,16,y,w,k,1k,12,14
2u,10,u,w,10,16,3k,3s,6s,3tc,19,z,1p,d,h,j,22,i,d2,w,k,i,g,i,1m,14,18
2w,y,u,w,y,16,3k,3y,6w,3sk,7,3,x,11,f,w,i,17,d4,12,i,k,k,m,1k,y,1i
30,10,w,y,10,1a,3m,3y,6y,3ty,1f,23,19,3,p,d,1u,g,d2,u,i,i,i,k,1m,16,18
2s,y,s,u,y,14,38,3e,5y,3ty,1t,11,1d,17,b,8,q,d,d2,w,m,i,g,g,1a,s,12
2c,q,m,m,o,y,2k,2q,4m,3vw,2p,13,n,m,r,1y,36,1g,d6,1a,w,m,m,y,14,s,y
30,12,y,y,12,1a,3q,46,7a,3tm,v,1x,1p,3,d,6,26,v,d4,u,g,k,i,i,1s,16,1e
3e,1a,y,12,16,1e,46,4q,7w,3ua,1v,n,1b,n,x,16,9,1,d6,y,o,q,i,s,1c,14,1k
24,q,k,m,m,q,2e,2i,44,3xy,3l,15,1f,m,16,0,2m,3g,d8,18,w,o,m,y,18,u,u
1u,e,g,i,g,m,1o,1s,2w,3yu,e,2n,7,2,5,w,q,3o,c8,26,18,m,s,o,1u,18,q
1y,k,k,i,k,m,20,26,3g,3zk,3h,19,v,11,1w,0,1e,4w,cm,1w,y,m,q,s,1m,w,q
30,10,w,y,10,18,3m,3w,6q,3u2,1h,25,13,7,t,5,1q,k,d2,u,i,k,g,k,1m,14,16
2i,u,q,s,u,10,32,3a,5o,3ua,n,21,1l,v,2,2,i,1k,d6,y,m,i,g,i,18,y,w
2m,w,q,s,w,12,34,3c,5u,3to,r,1b,1x,x,1,3,o,i,d4,y,m,g,g,i,16,w,10
32,12,y,y,12,1a,3o,44,72,3u8,1f,27,1n,4,l,1,1s,2,d2,u,i,k,g,k,1q,18,18
1u,g,i,g,i,k,1m,1s,2m,3ze,7,2v,6,g,c,k,14,4i,ce,28,16,y,s,u,22,14,10
1y,k,i,i,i,m,1s,1y,2w,406,2v,1f,b,j,y,c,1u,5a,co,20,12,s,w,w,1w,w,s
2o,s,q,q,u,12,30,38,5q,3sw,6,1f,e,p,v,1k,32,v,d4,1c,o,k,k,12,12,s,16
3i,1c,y,14,18,1i,48,4w,86,3uo,3h,3,1d,p,v,1m,6,6,d0,y,o,o,g,o,1k,18,1k
2w,w,q,u,w,14,3e,3k,6a,3se,2,5,b,11,9,o,2g,1h,d6,1c,i,m,k,14,10,u,1c
3c,18,y,12,14,1g,44,4o,7s,3ua,1f,x,1f,n,r,w,9,3,d6,w,o,q,i,o,1g,14,1k
38,16,y,10,14,1c,40,4k,7m,3u0,t,19,1f,h,n,o,5,h,d6,w,m,q,i,m,1i,12,1m
3a,16,w,10,14,1c,40,4i,7k,3tu,r,11,19,j,t,w,0,v,d8,y,m,q,k,w,18,10,1k
36,14,10,10,14,1c,3w,4e,7m,3ua,1j,1t,1v,b,7,s,1k,13,d2,u,i,m,g,k,1s,18,1g
36,16,y,12,14,1c,3w,4g,7o,3u8,1j,1p,1t,d,d,14,1a,13,d2,u,k,k,g,k,1s,18,1i
3e,1a,10,12,16,1i,46,4s,82,3ui,39,2,1j,p,x,1s,8,7,d2,w,m,m,g,o,1m,18,1m
3g,1a,10,14,16,1i,44,4w,82,3um,3l,4,1f,r,v,1y,4,5,d0,y,m,m,i,o,1k,18,1m
2m,u,o,q,s,10,2y,32,5e,3tc,b,1f,a,l,0,10,3o,h,d6,1e,q,m,k,12,12,q,14
2w,10,u,w,10,16,3i,3q,6k,3ty,1p,1n,11,l,t,9,1i,i,d2,w,i,i,g,k,1i,10,14
3k,1a,10,14,16,1g,4a,4w,82,3uk,2t,d,19,p,v,1c,9,g,d6,y,o,o,i,q,1g,16,1k
2w,w,s,u,w,14,3c,3k,6a,3s4,0,6,5,1f,4,i,2m,1n,d6,1e,i,i,q,12,y,u,1c
30,10,w,y,10,18,3q,46,78,3t2,f,r,17,n,j,u,c,15,d4,y,i,m,i,m,1k,12,1k
38,14,y,10,12,1c,3y,4g,7i,3ts,j,13,1b,j,r,s,4,11,d8,y,m,q,k,w,16,10,1k
26,q,k,k,m,q,2a,2a,3o,3z8,4r,t,1h,18,16,b,3o,3y,dc,1a,y,s,y,u,1c,q,s
2i,u,o,s,s,12,2y,3e,5s,3t2,4,1p,r,9,17,1m,1s,4,d8,10,o,i,g,q,1c,w,18
2q,w,s,u,w,14,38,3g,62,3ta,13,r,1v,13,9,0,u,4,d4,w,k,i,g,g,1a,w,10
2e,s,m,m,o,w,2k,2m,4i,3vc,1r,1j,f,q,1,14,40,1i,d8,1e,w,q,u,s,14,q,y
2y,y,s,w,y,16,3i,3s,6q,3sg,1,1,l,15,h,u,1c,1f,d6,16,g,m,m,y,16,w,1g
30,14,w,10,10,1a,3q,44,78,3ty,17,23,1p,1,b,9,26,b,d2,u,i,k,g,k,1q,18,1a
2i,s,o,q,q,10,2u,32,5c,3tu,r,1b,7,1,11,24,2s,0,d6,18,s,k,k,y,14,u,14
28,q,k,k,k,s,2a,2a,3q,3yw,4z,d,1h,1a,1e,j,40,3m,d8,1g,y,s,y,u,1c,q,s
3e,1c,y,14,16,1i,44,4u,82,3um,3l,6,1h,r,x,22,4,9,d0,y,m,m,g,o,1m,18,1m
1s,e,e,g,g,k,1m,1o,2w,3ya,1u,3b,2,8,4,e,1i,1e,c0,28,1a,s,m,m,1s,1a,q
1u,g,i,g,i,m,1m,1u,2s,3z4,0,2x,b,g,9,1a,s,4k,cc,28,10,w,s,q,20,14,u
30,14,w,y,10,1a,3q,40,72,3u4,1b,2b,1n,8,p,9,1y,2,d2,u,i,i,i,k,1o,16,1a
2y,12,w,y,10,1a,3o,44,78,3t8,n,1f,1l,7,l,o,1m,z,d4,u,i,k,i,i,1q,14,1g
28,s,m,m,o,w,2i,2y,4s,3xc,3h,19,1l,8,3,1m,g,2u,de,y,s,m,g,u,16,u,y
3a,16,10,12,14,1e,40,4m,7w,3ua,23,t,1p,j,t,1o,i,r,d2,u,m,m,g,m,1o,16,1m
22,o,i,k,k,o,22,22,38,40i,59,p,15,q,1m,t,3k,4q,d0,1o,10,u,y,u,1m,s,q
32,14,w,y,12,1a,3u,4c,7e,3te,b,17,1f,d,j,k,4,x,d6,w,i,o,i,m,1k,12,1m
2a,s,k,m,m,s,2g,2k,44,3xi,4d,j,13,12,q,q,3g,2k,da,1a,y,q,w,u,14,q,w
2g,s,m,o,o,10,2o,2s,4w,3to,9,1r,8,g,15,20,3e,q,d6,1c,w,k,m,10,12,s,10
38,14,w,10,12,1a,3w,4e,7g,3tm,d,13,19,f,r,q,8,19,d8,y,o,q,i,10,14,y,1k
2c,s,o,o,q,y,2o,32,56,3v0,1p,1d,v,a,1l,2c,1s,18,dc,y,u,k,g,w,16,u,12
36,16,y,12,12,1c,3w,4c,7m,3uc,1n,1v,1t,9,7,k,1k,x,d2,u,k,k,g,k,1s,18,1g
2w,10,u,w,y,18,3e,3q,6c,3u0,1t,1t,f,x,v,0,1a,u,d0,w,k,i,i,i,1g,y,14
3e,18,10,12,16,1g,46,4s,7y,3ue,2l,b,1j,p,v,1g,6,1,d2,w,m,o,g,o,1k,18,1k
20,m,k,k,i,o,20,24,3c,402,4f,15,13,2,1y,n,2y,4u,cu,1q,10,s,u,u,1m,u,q
2k,u,q,s,u,10,32,3e,5w,3tg,7,1l,3,x,n,1g,1y,l,d4,16,o,g,k,m,1g,y,18
2o,s,o,q,s,10,2y,2y,5c,3t0,3,1b,a,f,7,16,3w,j,d6,1g,q,m,o,10,10,q,14
30,10,w,y,10,18,3m,3w,6q,3u2,1h,25,13,7,r,5,1q,m,d2,u,i,k,i,i,1m,14,16
2q,u,s,s,w,14,38,3k,6a,3ss,5,n,d,z,n,1g,1c,z,d2,18,k,i,k,o,1g,y,1c
1w,i,i,i,k,m,1u,24,38,3zk,1p,2l,1,z,k,y,u,44,cg,22,y,m,m,s,1o,10,q
24,s,k,m,o,s,2e,2s,4e,3y8,4r,h,1j,c,3,1k,10,3c,da,14,u,m,i,w,16,u,w
3g,1a,10,12,18,1g,48,4u,82,3ug,2x,5,1d,r,v,1a,4,a,d2,y,m,o,g,q,1i,18,1k
2a,w,m,q,q,y,2q,32,5a,3wq,2z,1r,1j,t,4,y,3,1g,dc,u,q,k,g,k,12,y,u
3k,1c,10,14,18,1i,4a,50,86,3uu,3f,d,1b,l,v,1g,1,k,d2,10,o,m,i,o,1k,16,1k
2s,y,q,w,y,16,3c,3o,6a,3t6,1d,b,1v,x,d,d,18,1m,d4,w,k,i,g,g,1i,10,14
1u,i,g,g,k,m,1o,1w,2y,3z8,4,3n,q,x,4,16,u,4k,cc,26,12,q,s,q,1w,14,q
1y,i,i,i,k,m,1s,1y,2y,402,2h,2b,2,l,12,k,1i,5c,ck,22,10,w,q,w,1w,y,q
22,o,k,k,k,q,26,2a,3o,3zi,57,b,1t,m,1k,h,38,4a,cy,1m,w,q,s,u,1i,q,s
38,14,y,10,12,1c,3y,4i,7i,3tu,n,z,1d,l,n,o,0,r,d8,w,m,q,k,q,1e,10,1k
3c,18,10,12,14,1g,46,4q,7w,3ua,1x,n,1h,n,t,10,1,2,d4,w,m,q,g,o,1i,16,1m
2m,s,q,q,s,12,2y,34,5i,3su,6,1b,c,h,z,1q,34,h,d4,1e,o,k,k,12,12,s,16
36,12,y,y,12,1a,3w,4c,7g,3tk,b,13,1b,f,j,g,6,13,d8,w,m,q,i,o,1g,10,1k
2u,y,u,u,y,18,3c,3k,6a,3ty,1v,19,13,11,n,1,16,4,d2,w,k,i,g,i,1c,w,14
32,12,w,w,10,1a,3s,46,78,3t4,5,v,z,n,j,i,c,15,d6,10,i,o,k,q,1e,10,1i
3g,18,10,12,14,1g,46,4s,7u,3u8,1x,l,19,n,11,1c,b,0,d8,w,o,q,i,w,1a,12,1k
36,14,y,12,12,1c,3u,4c,7g,3uc,1l,1v,1r,7,d,q,18,r,d2,u,k,k,g,k,1s,1a,1c
3c,18,y,14,14,1g,44,4o,7y,3ue,2j,b,1n,n,x,1m,a,d,d2,u,m,o,g,o,1m,16,1m
20,m,k,i,k,o,24,28,3k,3zc,3d,1n,v,p,1y,9,1y,4u,cu,1o,y,o,o,u,1k,u,s
2s,u,s,u,w,14,3a,3m,6e,3sa,4,9,h,15,h,1g,16,z,d4,18,i,k,k,o,1g,w,1c
34,14,y,y,12,1c,3w,4e,7i,3tk,f,19,19,h,l,k,2,t,d6,w,k,o,i,o,1i,12,1k
3i,1c,10,14,16,1i,4a,4w,84,3uo,37,9,1b,p,v,1e,1,g,d2,y,o,o,g,q,1i,18,1k
2u,w,s,w,y,16,3g,3u,6s,3se,3,h,19,z,d,o,1g,z,d4,10,i,i,k,g,1o,10,1g
2y,10,u,y,y,18,3o,42,72,3su,5,l,11,t,f,q,e,15,d4,12,g,m,k,m,1k,10,1i
3a,16,10,12,14,1e,3y,4m,7s,3ug,2d,11,1n,l,j,1i,u,11,d2,u,k,m,g,k,1s,18,1k
2w,y,s,u,y,16,3g,3o,6k,3sg,1,0,h,19,d,u,1u,1h,d6,18,i,m,m,y,14,w,1c
2m,u,q,s,w,12,34,3i,62,3ta,5,1h,1d,l,5,2,1m,m,d4,12,m,g,g,i,1k,y,16
38,16,y,12,14,1e,3y,4i,7s,3ua,1x,1b,1r,h,h,1e,12,13,d2,u,k,m,e,m,1q,18,1k
2i,q,q,o,s,y,2u,32,5e,3ts,b,1t,3,2,13,1y,2g,2,d4,1a,s,i,i,w,18,u,14
3k,1c,10,14,16,1i,4a,4y,86,3uq,35,f,19,l,v,1e,7,k,d4,y,q,m,i,o,1k,16,1k
2y,10,u,y,10,18,3o,44,76,3su,b,x,13,h,n,s,o,15,d4,w,i,k,k,k,1m,12,1k
1w,g,i,g,k,k,1m,1y,2s,3za,6,43,1k,21,1a,1o,7,5k,ce,28,12,w,m,w,20,10,s
1y,m,i,k,k,o,1y,2e,3m,3zs,2z,1z,j,z,i,1a,7,4i,cq,1s,u,m,i,u,1g,y,o
22,o,i,k,k,m,20,22,34,40u,63,m,1l,e,1y,h,2w,54,d0,1s,10,u,u,y,1o,s,q
1k,a,8,8,a,c,w,y,1i,3tg,16,16,3,z,38,4e,1i,3a,be,1k,1s,1a,1i,10,1m,12,2i
1k,a,8,8,a,a,u,w,1e,3t8,18,1c,c,13,34,5o,1w,2o,bg,1k,1q,18,1g,12,1o,10,2k
1k,c,8,a,c,a,12,10,1q,3ty,1i,y,25,g,40,2u,2c,o,bg,1q,1q,1e,1g,m,1e,14,22
1i,c,8,a,c,c,12,10,1k,3ua,1u,12,1j,n,26,20,2c,5g,b4,1k,22,1s,1o,y,2u,2g,1g
1i,c,8,8,c,c,y,10,1o,3ty,16,18,d,1n,1y,4w,1q,2g,b4,1c,1y,1s,1q,10,24,24,1y
1g,a,8,6,8,a,m,m,w,3uy,d,1k,18,w,22,8a,3s,56,bk,26,1k,10,1a,y,1k,18,2a
1s,e,e,g,e,k,1i,1k,2e,3yk,18,1v,2j,3s,w,1f,1u,2w,c4,2e,1a,14,u,q,20,1g,1e
1c,a,8,8,6,a,o,m,y,3v6,f,u,1g,1y,q,7q,3o,5c,b6,18,2c,1a,18,16,1o,1i,2e
22,o,g,k,k,m,1y,1y,2y,41a,5v,c,1h,g,1q,t,3e,5a,d8,1m,12,s,y,y,1q,s,s
1g,a,8,6,8,a,m,o,w,3uq,9,1o,1a,m,1u,8e,3u,4s,bi,1g,24,14,1a,y,1q,1a,2c
1s,e,e,e,g,i,1i,1i,28,3z0,1m,3f,b,20,o,1,14,42,c6,2a,1g,1a,w,u,1y,1u,12
1m,c,a,c,c,c,16,18,24,3v8,1o,q,45,2c,46,3c,5,1l,bi,1w,1u,1g,s,i,1c,14,1i
1k,c,8,a,c,c,12,12,1u,3ui,1u,14,2j,k,2u,2s,1w,1m,b8,1s,1u,1y,1a,w,2g,24,1w
1m,c,a,a,c,c,14,18,20,3uu,1q,q,41,2i,4a,3w,1j,t,bk,1s,1s,1k,u,k,18,16,1c
1s,e,e,g,g,k,1k,1m,2i,3yg,1c,2p,19,2c,8,5,o,3w,c6,26,1e,10,q,q,22,1c,1a
1u,g,g,g,e,k,1k,1k,2c,3zu,1k,4z,i,38,j,5,q,4o,cg,28,18,1a,s,u,24,1o,w
1m,c,8,a,c,c,12,14,1u,3uc,1m,u,31,1e,48,3e,i,5,bi,1q,1s,1i,16,k,1a,14,1q
20,m,g,k,i,m,1s,1w,2o,41q,71,2y,29,f,22,8,1k,6a,cs,26,14,w,q,10,1w,u,q
1i,c,8,a,c,c,10,12,1o,3u2,1o,16,1x,9,2a,3k,18,4c,b4,1g,24,22,1i,q,2c,2g,1q
1q,e,c,e,e,k,1i,1i,2m,3xo,1k,1h,1d,1o,18,d,14,u,bu,28,1g,10,q,m,1s,1c,12
1s,g,g,g,i,k,1m,1u,34,3yk,14,2t,d,1,3,s,10,18,c2,28,18,m,m,k,1o,16,m
1q,e,e,e,e,k,1i,1k,2i,3y0,1o,29,15,2c,1,e,o,2k,c0,28,1c,14,s,o,1y,1e,1e
1m,c,a,a,c,c,14,1a,22,3v2,1o,q,3z,1w,4i,44,8,2f,bi,1s,1w,1k,w,g,1g,12,20
1q,e,c,e,e,i,1i,1k,2s,3xc,1u,1b,1f,18,1w,h,i,13,bq,28,1g,y,o,g,1g,1a,o
1o,e,a,e,e,g,1g,1e,28,3x8,1g,n,2f,2i,2m,l,o,2s,bw,20,1q,1c,u,q,1y,1e,1w
1u,i,g,i,k,m,1q,22,3e,3yy,b,1v,b,1d,5,1k,j,1o,c8,26,10,m,g,o,1g,12,k
1s,e,g,g,g,k,1k,1o,2i,3yk,1i,3f,d,1e,6,c,q,40,c6,28,1a,12,q,s,22,1a,18
1o,e,a,e,c,g,1c,1e,2g,3we,1s,p,2d,2i,2k,m,1,v,bm,24,1m,18,m,g,18,16,u
1u,g,g,e,g,m,1k,1m,2c,3zo,1s,5x,2e,1i,1l,1g,8,54,cg,26,1c,16,s,w,22,1g,14
20,o,i,i,k,m,1w,1y,2y,414,59,h,j,2,1c,7,2u,5a,d4,1o,14,s,w,y,1q,u,u
1q,e,c,g,e,k,1i,1k,2m,3xw,1c,1n,1d,2e,c,l,16,1y,by,26,1k,u,s,m,1u,1e,16
1s,c,e,g,e,k,1k,1m,2y,3xs,1w,1p,1b,u,s,0,y,x,bu,28,1e,s,m,g,1k,16,o
1q,c,c,e,e,g,1i,1e,2a,3xg,1g,17,1r,1i,3s,37,3c,1q,bw,22,1u,18,q,q,20,1e,1s
1s,c,e,e,e,i,1e,1g,24,3ym,1s,35,11,3k,14,c,b,4g,c4,28,1i,1e,y,u,20,28,s
1u,g,g,g,g,m,1k,1q,2g,3zg,w,49,u,y,n,1c,e,4y,cc,26,1c,12,s,w,20,18,14
1s,e,e,g,g,k,1m,1m,30,3xy,26,2v,4,a,r,5,1k,d,by,28,18,s,i,g,1e,12,m
1w,g,i,i,k,m,1s,24,3g,3z4,r,1z,5,1x,e,1o,z,2q,ca,26,y,k,k,m,1i,12,k
1o,e,c,c,e,g,1g,1g,2m,3wy,1m,p,2d,2c,2m,5,1,z,bo,24,1m,14,o,k,1g,1c,w
1o,c,a,c,a,e,14,1e,24,3ve,1s,a,45,36,48,3y,41,f,bm,1w,1q,1i,m,k,14,16,10
1y,i,g,g,i,m,1m,1q,2i,40u,2n,1f,e,8,c,16,6,5u,ck,2a,16,12,q,y,22,16,y
1q,e,c,e,g,g,1i,1g,28,3y6,1m,21,19,1g,1u,n,1q,34,c0,26,1o,18,w,s,1y,1k,1k
1i,c,a,8,c,c,10,14,1q,3ua,1a,14,1n,p,2y,5m,m,1g,ba,1q,1u,1g,1k,u,1q,16,2q
1m,c,8,c,a,c,12,14,1y,3uc,1q,s,3p,22,4g,3s,7,13,bk,1q,1q,1o,y,i,1a,16,1e
2g,s,s,q,s,10,2o,2m,4q,3us,2o,2p,1z,2d,9,12,1u,43,cw,i,e,i,a,e,e,a,g
1w,i,g,i,g,m,20,22,3g,3vm,1e,5,6,18,1c,5d,e,3n,ca,2a,i,a,0,2,o,3,i
1s,c,c,c,e,g,1a,1e,2e,3vk,24,13,4,10,2i,m,1h,35,by,2i,14,k,8,0,10,8,i
1y,m,i,k,k,q,28,28,3w,3xq,2t,9,18,s,1r,2n,1e,49,cg,22,e,6,2,4,k,1,k
2g,s,q,q,u,y,2o,2m,4o,3to,3c,3f,1t,1z,2,1g,1k,3x,d2,i,k,i,8,a,a,a,g
2g,s,q,q,u,y,2o,2m,4q,3uu,20,19,2l,21,l,q,2e,4t,cy,g,a,i,c,e,g,8,k
22,o,k,k,m,u,2e,2e,44,3yi,4l,8,u,0,21,1h,1y,4p,cs,1m,a,6,4,6,i,0,m
1s,c,a,c,c,g,18,1c,2a,3uy,26,p,4,12,2m,1e,1v,2l,by,2a,1a,o,i,6,o,e,e
1u,e,e,e,e,i,1m,1o,2y,3uk,2i,v,1o,14,2m,45,9,3t,c0,2i,u,c,8,3,q,0,q
1s,c,a,a,e,e,16,1e,26,3vg,20,l,2,5,2a,2y,25,27,by,2c,18,o,m,6,q,8,g
2g,u,q,q,u,y,2o,2o,4m,3u2,3g,3t,23,1p,2,1i,16,3f,d0,e,m,i,8,e,a,a,g
1w,e,e,g,e,k,1q,1s,32,3uw,24,e,v,3k,1i,51,f,33,c6,2e,m,e,1,2,o,4,e
2e,u,q,o,u,10,2m,2m,4q,3u6,2q,29,1z,2d,9,16,1y,4f,cw,e,k,i,a,e,c,a,i
2c,u,o,q,s,y,2m,2m,4q,3ug,q,a,1z,1z,t,k,2q,5p,cw,i,c,g,c,c,k,4,o
2e,s,q,q,u,y,2m,2m,4o,3vc,1w,27,25,2p,d,1a,1y,4d,d0,g,g,g,a,e,c,8,i
22,q,k,m,m,u,2e,2g,46,3yi,57,1w,8,t,1v,1d,26,4r,d2,18,a,6,2,a,e,4,k
28,s,m,m,q,y,2i,2k,4i,3vs,2p,1k,r,2b,z,g,2g,59,d8,w,8,a,6,c,c,4,m
2g,s,s,o,u,10,2m,2o,4o,3ui,2s,2f,21,2b,9,10,1y,4b,cw,e,i,i,a,e,e,8,k
1s,c,a,c,e,g,1a,1a,28,3wi,20,t,b,k,1a,w,1,21,c0,2g,14,k,a,6,q,e,i
1o,c,8,a,a,e,10,16,1m,3ws,1u,s,x,1k,u,1k,29,2e,c2,2c,16,w,u,g,10,a,g
24,q,k,m,q,u,2g,2g,4a,3xy,4n,2c,l,1f,1l,t,2a,4v,d2,14,8,8,4,a,g,2,m
2a,u,o,o,u,y,2m,2m,4m,3wm,l,8,2v,2p,x,12,2g,4z,cy,o,8,i,8,e,e,6,k
2g,s,q,q,u,10,2m,2o,4o,3uw,2e,29,27,2b,d,10,20,4b,cy,g,c,i,c,c,g,8,i
2a,s,o,o,s,y,2k,2m,4k,3u8,g,9,1h,2h,l,16,2c,55,d8,o,c,e,6,c,c,6,k
1s,c,c,c,e,g,1a,1c,2c,3w8,22,z,l,1s,10,g,7,2l,c0,2g,14,g,8,8,k,k,g
2g,u,q,q,u,y,2o,2o,4o,3u8,3e,3z,27,1l,4,1i,14,39,d0,g,k,i,8,e,a,a,e
1u,e,e,e,e,i,1o,1o,2y,3uu,2o,1h,14,24,1w,3z,d,39,c0,2g,y,c,4,0,w,0,g
28,s,m,o,q,y,2i,2k,4i,3vk,27,18,x,2b,x,m,2e,57,d8,u,a,a,6,c,c,4,m
1u,c,c,g,c,i,1i,1i,2k,3vq,2a,j,17,3c,w,23,9,33,c0,2k,w,a,6,6,g,k,g
2g,s,q,q,u,10,2m,2m,4q,3um,2a,1f,2h,21,j,s,2a,4r,cw,g,e,g,c,e,g,8,k
2g,u,q,q,u,y,2o,2o,4o,3uo,32,3j,1z,21,3,1a,1i,3p,cw,e,k,i,a,e,c,a,g
1y,e,e,g,g,k,1s,1w,32,3v4,1q,m,y,1c,22,5l,p,2n,c8,2c,g,a,6,0,m,2,e
2g,u,q,q,u,y,2o,2m,4o,3u4,3c,3p,23,1r,2,1g,1a,3h,cy,g,m,i,8,e,a,a,g
2e,u,q,o,u,y,2o,2m,4o,3u6,2m,21,21,2d,9,12,22,4j,cw,e,k,i,a,c,e,8,k
2g,u,q,q,s,10,2o,2m,4o,3uu,30,3t,21,1z,3,1g,1e,3j,cy,g,i,i,a,c,c,a,g
2g,s,q,q,u,y,2o,2m,4q,3us,1q,r,2j,1v,p,k,2i,55,cw,i,a,i,a,e,i,6,m
1s,c,a,a,e,g,18,1a,1w,3xo,1w,p,3,l,1a,7,2,1g,c2,2e,14,k,c,c,s,4,m
2g,s,q,q,u,y,2o,2m,4o,3uc,36,3j,23,1z,1,1i,1g,3r,d0,i,i,i,8,c,a,a,g
2c,s,o,o,u,y,2k,2m,4m,3u4,14,v,1j,2j,h,1a,26,4x,d6,m,e,e,8,c,c,6,k
1y,i,g,g,i,m,20,22,3g,3vg,g,1o,6,1k,w,61,a,31,cg,24,c,a,0,2,o,0,c
1y,g,g,g,i,m,1w,22,3e,3v4,1u,n,10,1m,a,4j,8,3l,ca,28,m,8,0,2,o,3,e
2a,s,o,o,s,y,2k,2k,4m,3uq,4,7,1d,2n,n,14,2a,53,d6,o,e,c,8,c,a,6,m
2e,u,q,o,u,10,2m,2o,4o,3va,1m,1b,2j,2f,l,y,2a,4p,cy,i,a,i,a,e,g,8,i
1s,a,a,c,c,e,18,1a,22,3w0,1y,f,5,7,2y,y,v,1p,c0,2c,18,o,k,6,u,a,g
26,u,m,o,q,w,2k,2k,4g,3wc,3p,24,r,17,1d,1,2i,4x,cg,16,a,c,8,c,m,2,q
2g,u,q,q,u,y,2o,2o,4o,3uq,36,3z,27,1r,0,1g,16,39,cy,g,i,k,8,e,a,a,g
2c,u,o,o,u,y,2m,2m,4o,3u8,1u,1p,1l,2n,b,1c,20,4n,d4,k,g,g,8,c,a,8,i
1s,e,a,e,c,i,1c,1g,2k,3uw,2a,z,q,1k,20,f,15,3d,by,2g,14,g,c,2,q,c,i
2a,u,m,o,s,y,2k,2m,4k,3tu,q,f,13,2n,j,16,2a,53,d8,o,e,c,8,c,c,4,m
22,s,k,m,o,u,2g,2g,4a,3z4,6j,1o,6,x,1j,t,28,4t,cq,1q,4,8,4,8,g,4,k
22,o,k,k,m,u,2c,2e,44,3yk,41,1e,7,7,27,1t,1w,4h,cy,18,a,a,2,8,g,2,m
2g,s,s,o,u,10,2m,2o,4o,3uo,34,3t,23,1z,0,1g,1e,3l,d0,g,i,i,a,c,a,a,g
1u,c,c,e,c,i,1e,1g,2c,3wi,28,11,l,1s,s,1f,8,1r,c0,2i,y,c,a,0,w,4,o
20,k,i,i,k,q,26,26,3s,3x2,1d,y,k,k,x,43,12,3n,cq,1m,c,a,2,4,k,0,g
1y,g,i,g,i,m,20,22,3i,3vc,1k,n,y,1i,7,49,e,3p,cc,26,k,6,0,4,m,1,g
20,k,i,k,k,q,28,28,3u,3x0,z,r,14,s,1x,2r,1a,47,cm,1u,e,6,0,6,k,0,i
2e,s,q,q,u,y,2o,2m,4o,3v8,24,2d,23,2p,b,18,1w,47,cy,i,e,i,a,c,e,8,i
2e,s,q,q,u,y,2m,2o,4o,3v4,28,2n,1z,2n,9,1e,1s,47,d0,g,g,i,a,c,c,8,i
2a,s,o,q,u,y,2m,2k,4o,3ww,1p,1c,2n,2n,1b,u,2q,5b,cy,q,6,e,a,c,i,4,o
24,o,k,m,o,u,2e,2g,48,3y6,4n,2c,d,15,1v,15,26,4r,d0,16,a,8,2,a,g,2,m
1u,c,e,c,g,g,1i,1m,2s,3uo,2g,1d,18,24,2a,2n,13,3b,c0,2e,12,e,6,0,y,4,g
2g,u,q,q,u,y,2o,2m,4q,3uq,2u,35,1x,27,5,16,1o,3x,cw,g,g,i,c,c,e,a,g
28,u,m,m,s,y,2i,2k,4k,3um,13,o,t,2h,r,u,2g,59,da,s,a,c,6,c,c,4,m
1y,k,i,k,i,q,24,28,3s,3x0,z,d,o,u,l,3r,12,41,ce,24,e,8,0,4,m,1,i
1w,e,g,e,g,k,1u,1u,36,3uo,2i,t,1i,18,1u,51,1,3p,c6,2c,q,8,4,0,o,1,i
2g,u,q,q,u,y,2o,2o,4o,3u0,3i,3x,25,1l,4,1i,14,3b,d0,i,i,k,8,c,a,a,g
1s,c,c,a,e,g,1a,1g,2e,3uk,28,z,u,o,3w,s,39,2d,bw,2c,1a,m,e,0,10,8,g
1w,e,e,g,e,k,1q,1s,30,3v2,20,m,z,3c,1m,51,d,33,c6,2e,m,c,2,2,k,6,g
22,o,m,k,m,u,2e,2e,46,3ym,5h,12,o,f,1t,17,20,4p,co,1s,8,6,4,6,i,2,k
2e,s,q,o,u,y,2m,2m,4o,3t4,2s,25,17,2p,7,1g,1y,4l,d6,k,g,g,8,c,c,6,k
26,q,m,m,q,w,2g,2i,4e,3xk,3p,1g,x,1p,1d,5,2c,51,d4,10,a,8,4,c,c,6,k
28,u,m,o,s,w,2k,2k,4k,3v8,p,e,19,2n,t,w,2c,55,d6,q,e,a,6,c,c,6,k
2g,u,q,o,u,10,2m,2o,4o,3tu,3c,3l,1v,1v,0,1i,1g,3r,d2,i,i,i,8,c,a,a,g
1u,e,c,e,e,i,1i,1m,2s,3uu,2i,1b,14,22,1q,2f,p,3n,c0,2i,y,c,4,2,m,8,i
2g,s,s,o,u,10,2m,2o,4o,3uw,2w,3p,1z,23,3,1e,1i,3n,cy,g,i,i,a,c,c,a,g
2g,s,q,q,s,y,2o,2m,4s,3ui,1o,j,27,1x,p,i,2m,5j,cw,g,c,i,a,e,i,6,m
1s,c,c,c,e,g,1a,1c,28,3wm,20,r,t,1c,m,c,c,1x,c0,2g,14,g,a,2,w,c,i
2g,s,s,o,u,10,2m,2o,4o,3uy,2q,39,1z,2b,5,1a,1o,3v,cy,g,g,i,a,e,c,a,g
2g,u,q,q,u,y,2o,2m,4o,3uc,3c,3t,23,1t,0,1g,1a,3h,cy,e,m,i,8,e,a,c,e
2g,u,q,q,u,y,2o,2m,4o,3to,3g,3p,1x,1p,4,1i,1c,3n,d0,k,i,k,8,c,a,a,e
26,s,k,m,q,w,2i,2i,4e,3x2,4h,1s,d,1p,17,0,2c,53,d2,18,6,8,6,a,e,4,m
28,s,o,o,s,y,2k,2k,4k,3wq,19,q,2p,2h,z,y,2e,51,d0,q,c,e,6,e,c,6,k
2a,u,o,o,u,y,2k,2m,4m,3w8,2,d,2p,2p,r,18,2a,4v,d0,m,c,g,8,e,c,6,k
1w,e,e,g,e,i,1q,1s,2y,3va,26,l,1i,1e,2e,4v,13,2n,c6,2e,k,a,8,1,m,4,g
1q,c,8,a,c,c,14,16,1u,3vy,1w,e,p,m,22,1c,p,7,c0,2a,18,u,u,a,u,c,g
2e,u,o,s,s,y,2m,2m,4s,3um,s,6,1r,1h,r,4,2s,5x,cw,i,c,c,c,c,m,4,q
2e,s,q,o,u,y,2m,2o,4m,3to,2m,2b,1h,2j,5,1e,1u,4h,d4,i,i,g,8,c,c,8,i
1y,i,g,i,i,m,20,24,3g,3ve,c,2a,a,u,s,5z,c,31,cg,24,a,8,2,2,o,0,c
24,q,m,m,q,w,2g,2i,4c,3zc,5r,2q,1f,1b,1z,l,2a,4v,cy,12,8,a,4,a,i,4,m
2g,u,q,q,u,y,2o,2m,4o,3tw,3g,3t,21,1n,2,1i,1a,3h,d0,i,k,i,8,c,a,a,g
2a,u,o,o,s,y,2m,2k,4m,3vc,o,r,1z,2t,l,1a,24,4t,d2,m,e,g,8,c,a,8,k
1w,i,g,i,i,m,20,22,3i,3w0,12,t,a,1o,i,4p,i,3l,ca,2a,k,6,2,2,o,3,g
2g,s,q,q,u,y,2m,2o,4o,3ta,3a,35,1f,2b,0,1g,1q,45,d4,i,k,i,8,a,c,8,i
22,m,k,i,m,s,2a,2c,40,3ya,37,1,16,8,27,2h,1s,47,cy,1a,e,8,2,6,g,2,k
1q,c,8,a,c,e,14,16,1u,3wg,1w,6,h,c,2a,1,k,v,c0,2c,18,s,s,a,s,c,g
24,q,m,m,o,w,2g,2i,4c,3y2,4l,2o,13,1p,1p,j,2c,4x,d0,12,a,8,4,a,g,4,m
22,q,k,m,m,w,2e,2g,48,3yk,4b,i,0,b,21,11,24,4x,d2,18,a,8,2,a,e,4,k
1y,g,i,i,i,m,20,24,3k,3w8,s,13,k,1k,1,49,o,3n,ca,28,m,6,0,4,o,3,g
1s,c,a,c,c,g,18,1g,2a,3ui,20,17,0,2c,4k,1k,4f,1j,bs,2c,1c,w,c,0,12,e,g
1s,e,c,c,e,g,1g,1k,2i,3va,26,1l,1g,26,2u,2p,27,v,bu,2i,16,k,6,2,18,c,c
1w,g,i,g,i,k,20,20,3e,3w8,2a,1j,a,y,1m,5r,g,35,c8,2e,i,8,4,4,o,3,g
24,s,k,m,q,w,2e,2g,48,3xm,5n,2c,d,1p,1j,5,28,45,d6,1a,6,a,8,8,g,4,k
2g,s,q,q,u,y,2o,2m,4o,3t6,3a,35,1b,2f,0,1i,1s,45,d4,k,i,i,8,c,a,a,g
22,q,k,k,o,u,2c,2c,3w,3yy,6p,2c,e,1t,1n,z,1o,2v,d0,1k,8,a,8,6,i,6,g
28,s,k,m,o,w,2g,2e,44,3uu,1l,1s,1t,1b,1f,e,1y,37,d6,q,k,i,8,e,e,4,g
2a,u,m,o,s,y,2k,2k,4k,3tq,c,5,1b,2d,l,18,2e,51,da,o,e,e,6,c,c,6,k
26,s,i,m,o,s,28,2c,3q,3x6,3j,1a,31,t,5,u,u,11,d4,y,q,k,g,e,g,8,10
1s,e,c,c,e,i,1g,1k,2m,3uo,2c,1f,28,14,2i,2h,19,2n,by,2e,14,i,8,2,14,3,m
1w,e,i,g,g,k,1w,1y,32,3ww,2a,2v,1m,16,a,5l,g,1f,c6,2e,o,a,6,4,u,2,q
2o,w,q,s,w,12,2y,34,56,3ti,11,t,25,15,3,k,4,9,d6,s,m,i,e,g,s,o,10
2a,s,o,o,q,10,2i,2o,48,3ts,c,d,2t,19,1p,20,m,2b,de,s,e,k,c,e,a,4,w
2g,w,o,q,s,10,2q,2s,4g,3tu,4,1d,2f,1j,h,1e,u,29,d6,s,i,k,e,e,c,2,12
22,q,m,k,m,w,2c,2e,44,3ys,67,1o,e,17,1l,v,24,49,d0,1i,6,a,4,8,i,4,i
2g,s,o,q,s,10,2o,2s,4g,3ss,1g,1f,2h,1h,t,1i,m,1x,da,q,i,k,e,e,c,4,14
2e,s,q,o,u,y,2m,2m,4m,3si,32,29,19,2x,3,1i,26,4d,da,k,g,i,8,a,e,6,i
2c,u,o,o,s,10,2m,2m,4e,3se,2m,1p,25,27,13,1s,1q,33,dc,o,g,i,c,a,c,4,i
2e,s,o,o,s,y,2k,2m,4a,3sg,2u,29,25,1l,13,20,10,2f,d8,o,k,m,e,c,a,2,m
2e,u,o,o,s,y,2m,2o,4a,3t0,1y,27,29,1j,r,1w,q,27,d8,o,k,m,e,e,a,2,u
1y,o,k,i,m,s,22,26,3k,404,5x,1o,8,37,p,z,1q,t,ck,24,a,e,e,a,s,1,y
1s,e,e,c,e,g,1i,1m,2m,3v4,2e,2b,1s,2s,26,2n,21,1t,bw,2g,16,g,4,2,1a,4,g
2e,s,q,o,s,10,2k,2o,4c,3sk,2w,2h,1x,29,n,1w,1a,2n,d8,o,i,m,c,c,c,2,i
2k,w,o,s,u,10,2w,2y,4s,3u8,1d,r,2f,13,7,s,e,t,d6,u,i,k,e,g,i,8,1c
2q,w,q,u,w,14,34,3a,5o,3t8,11,r,1z,13,7,4,k,5,d6,s,m,i,g,e,14,q,10
1w,i,i,i,g,o,20,20,38,3xw,6,2j,26,a,x,4f,y,1h,ca,2c,i,a,6,6,q,4,q
2e,s,q,o,u,y,2m,2m,4k,3sg,2y,23,1b,2v,7,1k,26,4d,da,k,g,i,a,a,c,6,i
2e,s,q,o,u,y,2m,2m,4o,3t0,2s,27,15,2r,5,1g,20,4j,d6,k,i,g,8,a,c,8,i
2e,s,o,q,s,y,2m,2m,4c,3si,2y,2h,21,23,p,1y,14,2j,d8,o,k,k,e,c,a,4,i
2c,s,m,o,q,y,2i,2k,46,3ss,22,13,2h,11,1r,20,16,2l,d8,o,m,k,c,c,c,2,m
2i,u,o,s,s,12,2q,2u,4i,3ug,13,v,2j,19,d,14,q,1t,d6,s,i,m,c,g,c,6,18
2g,s,q,o,u,10,2m,2m,4m,3ss,3a,2v,15,2v,1,1i,22,49,d8,k,g,k,8,a,c,8,g
2c,s,o,o,s,y,2k,2m,4e,3sy,1g,t,25,1z,19,1o,22,3n,dc,q,c,k,a,a,c,6,g
28,s,m,o,q,y,2i,2i,4c,3v8,2n,1g,17,23,1d,u,2c,47,dc,w,a,e,8,a,e,6,i
2g,u,q,o,u,y,2o,2m,4o,3ta,3a,39,1f,29,2,1i,1o,41,d2,k,k,i,8,c,a,a,g
2e,u,o,o,u,10,2m,2q,4e,3s6,32,29,27,29,p,1u,12,2f,dc,m,i,k,c,c,c,2,k
2i,s,o,q,u,w,2q,32,4u,3um,l,1z,1v,z,e,18,h,18,d0,10,s,m,g,k,14,q,s
2g,u,o,q,s,10,2o,2q,4e,3tk,q,1n,2d,1p,f,1k,u,2b,d6,q,k,m,c,e,c,2,10
2e,u,m,o,s,w,2k,2q,4e,3uo,f,1t,29,17,a,16,c,m,d4,u,s,o,g,i,o,m,s
26,s,i,o,m,s,2a,2a,3s,3x2,35,i,2r,v,0,w,u,n,d4,w,s,m,g,e,i,8,y
26,s,m,m,o,w,2i,2i,4e,3wm,4d,1s,3,1x,15,4,2e,55,d8,12,8,a,4,a,e,4,m
2c,u,o,o,u,y,2m,2k,4i,3se,2m,1n,1p,2n,l,1m,2a,43,dc,m,e,k,8,c,c,6,g
1w,g,k,e,i,o,1y,1u,34,3y6,y,31,2k,5,2j,3d,1q,9,ca,2c,i,e,8,c,y,8,o
20,q,i,k,m,s,28,2a,3o,3zu,6f,28,9,2d,r,11,16,1x,co,20,8,c,c,a,i,4,10
2e,u,o,q,u,y,2o,2m,4k,3se,3c,2r,1j,2v,b,1m,22,3l,da,m,g,k,a,a,c,6,i
24,s,k,k,o,u,2e,2c,42,3w8,3l,2g,3,1v,x,r,26,3t,d2,y,g,e,8,a,i,4,i
1w,k,i,e,i,m,1w,1y,34,3zu,2p,23,40,2,x,43,y,1p,ca,1u,k,g,a,6,s,6,y
1y,o,i,i,m,q,26,26,3k,3z2,3x,2,1o,1h,1v,25,1c,1x,cm,22,a,a,8,8,k,4,s
1y,k,k,i,k,q,24,28,3s,3xs,1r,t,w,q,x,3b,16,3t,ce,26,e,6,4,4,m,1,i
2g,u,o,o,u,y,2o,2q,4c,3te,16,1v,2b,1t,f,1o,s,29,d6,q,k,m,e,c,c,2,w
2g,u,o,q,s,y,2o,2s,4e,3u6,b,1b,2j,13,h,1g,m,1f,d6,q,m,o,c,g,c,6,18
2g,s,q,o,u,10,2m,2m,4k,3sq,38,2z,1d,31,3,1m,24,3r,d6,o,i,k,8,c,c,6,g
2g,s,q,o,u,10,2m,2o,4g,3su,2y,2x,1n,2t,7,1o,1s,37,d6,q,g,m,a,c,a,6,i
2k,w,o,s,u,10,2u,2w,4o,3ui,1j,t,2f,13,9,y,e,x,d4,u,k,k,e,g,e,c,18
2g,s,q,o,u,10,2m,2m,4k,3su,36,2x,17,2x,0,1k,20,41,d6,m,i,k,8,a,c,8,g
22,q,m,k,o,u,2e,2e,46,3yi,65,1q,m,13,1n,t,26,4p,d0,1i,6,8,4,8,i,2,k
28,s,m,m,q,w,2i,2i,4e,3vu,33,1a,p,23,11,i,2i,4x,dc,w,8,c,6,c,e,4,k
2g,u,o,q,s,10,2o,2s,4g,3sy,1o,1v,2d,1r,n,1i,w,23,d8,q,i,k,e,c,c,2,m
1u,g,i,e,g,m,1u,1u,2w,3xe,22,3h,2o,y,z,4r,u,5,c6,2e,q,a,a,6,14,6,o
1u,g,g,g,g,k,1u,1u,2u,3xa,28,3j,2m,16,r,4z,q,3,c6,2e,s,8,a,6,14,8,m
2a,s,o,m,s,y,2k,2i,4g,3tw,b,8,1j,29,11,18,2c,49,dc,s,c,g,8,a,e,4,i
2a,s,o,o,q,10,2i,2o,48,3tu,a,d,2t,15,1v,20,o,29,de,s,e,i,e,c,c,2,q
26,u,m,m,q,w,2i,2i,4e,3vy,3f,1i,j,25,z,g,2g,4z,da,y,8,c,6,a,e,4,m
2s,y,s,u,y,16,36,3g,5y,3tw,1p,15,1f,15,d,4,s,5,d2,w,k,i,g,i,18,s,12
1w,g,i,g,i,m,1y,1y,34,3xk,18,35,2c,m,h,51,u,17,c8,2c,m,a,8,4,u,4,q
2e,u,o,o,u,10,2m,2m,4m,3sw,32,2h,11,2t,3,1g,1y,4h,d6,k,i,g,8,c,a,8,i
2k,w,o,s,u,10,2u,2y,4q,3u8,1b,t,2d,13,9,w,c,v,d6,u,i,k,e,g,g,a,1a
1q,e,a,a,c,g,16,1e,28,3ue,22,z,0,2s,2w,1q,2n,2h,bu,2a,1c,w,g,4,w,e,g
1u,e,g,g,e,k,1q,1s,32,3vm,2m,2d,1u,1w,1a,5f,k,31,c2,2g,u,a,2,4,14,9,m
2g,s,q,q,u,y,2o,2m,4o,3uu,2w,3j,1z,29,3,1g,1k,3t,d0,g,i,i,8,e,a,a,g
2i,w,o,s,s,10,2s,2w,4k,3uq,1j,v,2h,15,b,12,m,1h,d4,u,k,k,c,g,e,6,1a
2e,s,o,o,s,10,2k,2m,4a,3sa,32,25,1z,23,z,1y,1i,2v,d8,o,k,k,c,c,a,4,i
1s,c,a,c,c,g,18,1e,2c,3ug,24,15,k,2e,2i,1a,2n,2b,bu,2c,1a,s,e,6,w,e,c
28,s,k,o,q,u,2g,2i,48,3u8,x,1a,v,1z,p,a,28,4h,d4,q,i,g,8,c,e,4,i
2q,u,q,u,w,12,30,34,5a,3tg,13,r,23,15,1,e,a,5,d4,u,m,k,e,g,w,q,y
2e,u,o,q,s,y,2o,2s,4g,3sw,16,19,2f,1d,v,1g,i,1f,dc,o,k,k,e,g,c,4,18
26,u,k,o,o,y,2e,2k,42,3ww,49,1y,33,v,1x,1k,q,2d,dc,12,a,i,c,c,e,2,10
2e,u,o,o,u,y,2m,2m,4m,3sw,34,2h,17,2n,2,1g,1u,4f,d4,k,i,k,8,a,c,8,i
22,s,g,m,m,s,26,28,3k,3ye,57,2y,25,1j,d,4,y,1h,cw,1a,k,k,e,c,i,8,y
2q,w,q,u,w,12,32,36,5g,3tk,17,v,1z,17,1,c,e,d,d4,u,m,k,e,g,y,q,y
2o,u,q,s,w,12,2y,36,58,3tc,t,v,27,13,3,a,a,3,d8,q,m,k,e,e,w,m,10
2m,u,q,u,u,12,30,36,5g,3tg,n,13,27,z,3,2,i,9,d8,s,m,i,e,g,10,q,y
22,q,i,k,o,s,26,28,3m,3y4,4x,36,z,21,l,r,18,1x,cq,1g,g,i,c,a,k,6,u
2g,u,o,q,u,y,2o,2s,4e,3ty,3,19,2j,1h,b,1g,q,23,d6,s,i,m,e,e,c,4,14
2g,s,q,o,w,y,2m,2o,4o,3t8,38,33,1b,2f,0,1g,1s,47,d4,k,i,i,8,a,c,8,i
2g,s,q,o,u,y,2m,2m,4k,3sq,38,2v,1d,2z,1,1m,22,3v,d6,o,g,m,8,a,c,8,g
2g,s,o,q,s,10,2o,2s,4e,3sm,1s,1j,2h,1n,r,1m,o,23,da,q,i,k,e,c,c,4,w
2e,s,o,o,s,10,2k,2k,4e,3sa,32,1v,1v,2f,l,1q,20,3n,d8,m,i,m,a,a,c,6,g
2g,u,o,q,u,10,2o,2q,4g,3tc,1c,1z,27,1x,h,1i,12,2f,d6,q,i,m,c,e,a,4,q
1y,m,k,i,k,q,26,28,3u,3ya,2z,j,16,o,1j,2t,1e,3v,ce,26,c,8,2,6,k,0,i
2i,u,o,s,u,10,2q,2w,4m,3u4,v,t,2j,17,f,14,k,1f,d6,u,i,k,e,e,e,6,1a
2e,s,q,o,u,y,2k,2m,4i,3si,34,27,1l,2r,3,1k,22,41,d6,m,i,m,8,a,c,8,g
2o,u,q,s,w,12,2w,34,52,3te,t,t,29,z,0,c,e,4,d4,u,m,m,e,g,u,q,w
1u,e,g,e,g,i,1q,1s,30,3vs,2y,2z,1s,2c,12,5t,s,2n,c2,2e,y,8,4,4,18,b,o
28,u,m,m,s,w,2k,2i,4e,3uq,1p,w,17,29,13,y,2e,4f,dc,u,a,e,8,c,c,6,i
2i,u,o,s,s,12,2s,2w,4o,3tu,f,11,2h,15,d,u,g,v,d8,s,k,k,c,g,g,a,18
2g,s,q,q,s,10,2o,2o,4g,3t4,24,2h,1x,2d,d,1m,1a,2l,d6,q,i,m,c,c,a,4,m
22,q,i,i,o,s,28,26,3o,3yg,4t,2i,8,25,t,1r,1q,25,cq,1g,e,g,c,8,k,4,i
2e,s,o,o,s,y,2k,2k,4i,3si,2s,1j,1n,2h,7,1g,22,49,d6,k,k,k,8,c,a,8,g
20,q,k,k,o,s,2a,2c,3q,3zo,6z,2q,f,2b,t,t,10,27,cu,1u,6,c,c,8,i,4,w
2e,s,o,o,u,y,2m,2k,4m,3sm,2i,1l,1d,2p,9,1g,28,4l,da,k,g,i,8,a,c,6,k
1y,i,i,i,i,o,20,24,3i,3xc,3,1v,1i,y,t,41,12,33,cc,28,k,6,4,4,q,3,i
2k,u,o,s,u,y,2u,2y,4w,3u6,n,1j,29,x,a,i,c,i,cy,12,o,m,e,i,w,q,s
2g,s,q,o,w,y,2m,2m,4o,3t0,38,31,15,2r,2,1i,1w,45,d4,m,i,k,8,a,c,8,g
20,o,i,i,m,q,24,24,3g,3z6,4z,1w,1a,1r,17,29,1q,1v,cm,1m,e,e,c,a,m,4,o
2c,s,o,o,s,y,2k,2k,4i,3sq,2c,13,1j,29,b,1a,24,4l,d6,i,k,i,a,a,c,6,i
2a,s,k,o,q,y,2g,2g,46,3tk,i,i,2b,19,1l,18,1u,37,d8,o,k,k,8,e,c,4,g
2g,s,q,o,u,10,2m,2m,4m,3sm,3c,2x,19,31,1,1m,22,3z,d8,m,g,k,a,a,c,6,i
1w,g,g,g,g,m,1w,1w,36,3wo,20,2l,1o,18,8,5f,q,21,c6,2e,o,8,6,4,w,1,o
2g,s,q,o,u,10,2o,2o,4g,3sm,2w,2l,1x,2h,j,1m,1q,2z,d8,o,i,k,c,a,c,6,g
2g,s,o,q,u,y,2o,2o,4e,3sw,2m,2p,1v,2h,d,1q,1c,2n,d6,q,i,m,c,c,a,4,i
26,s,m,m,q,w,2i,2g,4e,3wk,4h,1y,9,23,11,8,2e,4z,da,12,6,a,6,a,e,4,m
1y,k,i,i,i,q,24,26,3k,3xy,15,1h,1g,i,1d,3f,12,2v,ce,26,g,8,4,4,m,2,i
2g,u,o,q,s,10,2m,2s,4c,3tg,14,1v,2b,1t,f,1m,u,2b,d6,q,k,m,c,e,a,4,w`;

let righe: string[] | null = null;

function decodifica(riga: string): ParametriSito {
  const t = riga.split(',');
  const gruppo = (base: number, scala: number) => {
    const out: number[] = [];
    let v = 0;
    for (let i = 0; i < 9; i++) {
      const z = parseInt(t[base * 9 + i], 36);
      v += z % 2 === 0 ? z / 2 : -(z + 1) / 2;
      out.push(v / scala);
    }
    return out;
  };
  return { ag: gruppo(0, 1000), F0: gruppo(1, 1000), TCstar: gruppo(2, 1000) };
}

/**
 * Parametri del comune di indice dato; `undefined` se l'indice non esiste.
 * La stringa è divisa in righe alla prima chiamata, poi si decodifica solo la
 * riga che serve.
 */
export function parametriSito(indice: number): ParametriSito | undefined {
  righe ??= DATI.split('\n').filter(Boolean);
  const riga = righe[indice];
  return riga ? decodifica(riga) : undefined;
}
